import { useState, useEffect, useCallback, useRef, lazy, Suspense } from "react";
import { flushSync } from 'react-dom';
import axios from "axios";
import PaystackPop from "@paystack/inline-js";
import './App.css';
import Tasbeeh from "./components/Tasbeeh";
import TakbeerCounter from "./components/TakbeerCounter";
import RadioHeroCard from "./components/RadioHeroCard";
import SignupFlow from "./components/SignupFlow";
import TabBar from "./components/TabBar";
import PermissionFlow from "./components/PermissionFlow";
import { DebugFAB, DebugScreen, isDebugHost, setDebugState } from "./components/DebugScreen";
import { installTabBarObserver } from "./tabBarObserver";
import DUA_CATEGORIES from "./duaData";
// Egzon/Dossari kept eager: their named `egzonPlayer`/`dossariPlayer` singletons
// share module-level audio state with the React component (see EgzonRecitations.js:51-80).
// Splitting that state across two files for lazy-loading is risky enough to defer to a follow-up.
import EgzonRecitations, { egzonPlayer } from "./components/EgzonRecitations";
import DossariRecitations, { dossariPlayer } from "./components/DossariRecitations";
import CalorieCounter from "./components/CalorieCounter";
import HayahHub from "./components/HayahHub";
import { generateShareCardBlob } from "./components/ShareCard";
import QRCode from "qrcode";
import { isNative } from "./capacitor";
import { Preferences } from '@capacitor/preferences';
import { isBiometricAvailable, authenticateBiometric } from "./biometrics";
import { setupNativePushListeners, registerNativePush, getNativePushDebug } from "./pushNative";
import { hapticPrayerMarked, hapticPrayerComplete, hapticUiSelection, hapticPlayPause, hapticToggle, hapticSuccess, hapticSwipeCommit, hapticCeremony, hapticImpact } from "./haptics";
import { useToast } from "./ToastContext";
import Toast from "./components/Toast";
import HayahFastInsightToast from "./components/HayahFastInsightToast";
import { useHayahFastToastScheduler } from "./components/hayahFastToast";
import { startActivity as startLiveActivity, endActivity as endLiveActivity, computeNextPrayer as computeLiveNextPrayer, computeJumuahTime as computeLiveJumuah, computeTahajjudWindow as computeLiveTahajjud, computeAllPrayers as computeLiveAllPrayers, formatTimeShort as formatLiveTime, todayAt, getActivityDebug, installLiveActivityPushTokenListener } from './liveActivity';
import { syncPrayerTimesFromAladhan } from './watchSync';
import { writePrayerTimes as siriWritePrayerTimes, writeUserName as siriWriteUserName, consumePendingAction as siriConsumePendingAction, addOpenURLListener as siriAddOpenURLListener, getDebugSnapshot as siriGetDebugSnapshot, writePrayerCompletion as siriWritePrayerCompletion, writeWeeklyLog as siriWriteWeeklyLog, readPrayerCompletion as siriReadPrayerCompletion, writeCalorieData as siriWriteCalorieData } from './siratIntents';
import { SYNCED_KEYS as ICLOUD_SYNCED_KEYS, pushOne as icloudPushOne, pullAll as icloudPullAll, attachRemoteListener as icloudAttachListener, mergeIfNeeded as icloudMergeIfNeeded, getICloudDebugSnapshot } from './icloudKV';
import { initOtaUpdater } from './otaUpdater';

const BACKEND = "https://azaan-backend.onrender.com";

// Code-split heavy feature components — loaded on first access so they don't
// bloat the initial bundle. Hayah / Salaah / Radio / Settings keep their
// eager imports above. See PR for the perf rationale.
const ZakatCalculator = lazy(() => import("./components/ZakatCalculator"));
const Azkar = lazy(() => import("./components/Azkar"));
const NamesOfAllah = lazy(() => import("./components/NamesOfAllah"));
const Qibla = lazy(() => import("./components/Qibla"));
const Khatm = lazy(() => import("./components/Khatm"));
const MoodGuide = lazy(() => import("./components/MoodGuide"));
const TajweedHelper = lazy(() => import("./components/TajweedHelper"));
const HajjCompanion = lazy(() => import("./components/HajjCompanion"));
const JanazahGuide = lazy(() => import("./components/JanazahGuide"));
const IslamicCalendar = lazy(() => import("./components/IslamicCalendar"));
const FaraidCalculator = lazy(() => import("./components/FaraidCalculator"));
const IslamicWill = lazy(() => import("./components/IslamicWill"));
const FamilyInfoPack = lazy(() => import("./components/FamilyInfoPack"));
const Muraqabah = lazy(() => import("./components/Muraqabah"));
const FastingTracker = lazy(() => import("./components/FastingTracker"));
const DuaJournal = lazy(() => import("./components/DuaJournal"));
const MaintainerDashboard = lazy(() => import("./components/MaintainerDashboard"));
const CardGenerator = lazy(() => import("./components/CardGenerator"));

// Wrapper for code-split (React.lazy) components. Fallback is null so the
// chunk swap looks like a brief blank rather than a flash of loader UI —
// most chunks load in under a frame on a warm cache.
const Lazy = ({ children }) => <Suspense fallback={null}>{children}</Suspense>;

// Lightweight event tracking — fire and forget, never blocks UI
const trackEvent = (type, data) => {
  try {
    const uid = localStorage.getItem("azaan-userId") || null;
    fetch(`${BACKEND}/api/track`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ userId: uid, type, data }) }).catch(() => {});
  } catch {}
};

// For any livemasjid URL (regardless of host/port/relay), extract the mount
// name and construct the known-working https://relay.livemasjid.com:8443/ URL.
// Non-livemasjid URLs (radio stations) are left unchanged.
function normalizeGlobalStation(gs) {
  if (!gs.url) return gs;
  if (!gs.url.includes("livemasjid.com")) return gs;
  // Extract bare mount name (strip .mp3/.ogg extension — iPhone needs bare mount, iPad adds .mp3 at play time)
  const mount = gs.url.split("/").pop().replace(/\.(mp3|ogg)$/i, "");
  if (!mount) return gs;
  return { ...gs, url: `https://relay.livemasjid.com:8443/${mount}` };
}

// ── Mirath encryption helpers (AES-256-GCM + PBKDF2) ──
// crypto.subtle is only available in secure contexts (HTTPS). On HTTP (local dev),
// fall back to a simple hash for PIN verification. Encryption is skipped on HTTP.
const MIRATH_PBKDF2_ITERATIONS = 100000;
const HAS_SUBTLE = !!(typeof crypto !== "undefined" && crypto.subtle);
async function mirathDeriveKey(pin, salt, extractable = false) {
  if (!HAS_SUBTLE) return null; // no encryption on HTTP
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey("raw", enc.encode(pin), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey({ name: "PBKDF2", salt, iterations: MIRATH_PBKDF2_ITERATIONS, hash: "SHA-256" }, keyMaterial, { name: "AES-GCM", length: 256 }, extractable, ["encrypt", "decrypt"]);
}
async function mirathHashPin(pin, salt) {
  if (!HAS_SUBTLE) {
    // Simple fallback hash for HTTP dev: just combine pin + salt hex (NOT secure, but PIN gate still works on local dev)
    const saltHex = Array.from(salt).map(b => b.toString(16).padStart(2, "0")).join("");
    let h = 0; const s = pin + saltHex; for (let i = 0; i < s.length; i++) { h = ((h << 5) - h + s.charCodeAt(i)) | 0; }
    return "dev_" + Math.abs(h).toString(16);
  }
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey("raw", enc.encode(pin), "PBKDF2", false, ["deriveBits"]);
  const bits = await crypto.subtle.deriveBits({ name: "PBKDF2", salt, iterations: MIRATH_PBKDF2_ITERATIONS, hash: "SHA-256" }, keyMaterial, 256);
  return Array.from(new Uint8Array(bits)).map(b => b.toString(16).padStart(2, "0")).join("");
}
async function mirathEncrypt(key, plaintext) {
  if (!key || !HAS_SUBTLE) return null; // skip encryption on HTTP
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const enc = new TextEncoder();
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, enc.encode(plaintext));
  const buf = new Uint8Array(iv.length + ct.byteLength);
  buf.set(iv); buf.set(new Uint8Array(ct), iv.length);
  return btoa(String.fromCharCode(...buf));
}
async function mirathDecrypt(key, ciphertext) {
  if (!key || !HAS_SUBTLE) return null; // skip decryption on HTTP
  const raw = Uint8Array.from(atob(ciphertext), c => c.charCodeAt(0));
  const iv = raw.slice(0, 12);
  const ct = raw.slice(12);
  const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ct);
  return new TextDecoder().decode(pt);
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) { outputArray[i] = rawData.charCodeAt(i); }
  return outputArray;
}

async function registerPush() {
  try {
    // Native iOS/Android: push handled by Capacitor plugin, not web push
    if (isNative()) return false;
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      alert("Push notifications are not supported on this browser/device.");
      return false;
    }
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      alert("Please allow notifications when prompted.");
      return false;
    }
    const reg = await navigator.serviceWorker.register("/sw.js");
    await navigator.serviceWorker.ready;
    const VAPID_KEY = "BEdA2-S2vGqjhhGkYTjl8xMFeSDbJnrHCwWjdWsS9xgzAWRyOKHwViX3uTbaF4-ak9J6onMxldX5QIJDz4i7dxY";
    await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_KEY)
    });
    // Don't call /api/subscribe here — callers use syncFollowsToBackend()
    // which sends the full payload (subscription + mounts + userId) in one call,
    // avoiding duplicate subscription records on the backend.
    return true;
  } catch(e) {
    console.error("Push error:", e);
    alert("Error: " + e.message);
    return false;
  }
}

const STATIONS = {
  masjids: [
    { name: "Abu Bakr Siddique", url: "https://relay.livemasjid.com:8443/westwoodabs", salaahUrl: "https://www.abubakrsiddique.co.za", arabicInitial: "أ" },
    { name: "Darul Ihsan", url: "https://relay.livemasjid.com:8443/darulihsan", arabicInitial: "د" },
    { name: "Hilaal", url: "https://relay.livemasjid.com:8443/ihilal", salaahUrl: "https://www.alhilal.co.za", arabicInitial: "ه" },
    { name: "Musjid E Noor", url: "https://relay.livemasjid.com:8443/noor", salaahUrl: "https://www.musjidnoor.za.net", arabicInitial: "ن" },
    { name: "Saliheen", url: "https://relay.livemasjid.com:8443/saliheendbn", salaahUrl: "https://www.saliheen.org.za", arabicInitial: "ص" },
    { name: "Mu'aadh Bin Jabal", url: "https://relay.livemasjid.com:8443/rockdale", salaahUrl: "https://www.muaadhbinjabal.org.za", arabicInitial: "م" },
    { name: "Umhlanga Islamic", url: "https://relay.livemasjid.com:8443/umhlanga", websiteUrl: "https://www.umhlangaislamic.co.za", arabicInitial: "م" },
    { name: "Westville Jaame'", url: "https://relay.livemasjid.com:8443/westville", salaahUrl: "https://salaahboard.co.za/westville/", arabicInitial: "م" },
  ],
  radio: [
    // Moved off simplestreaming (that host is dead) to iono.fm, channel 467 —
    // the same CDN Channel Islam streams from. Plain AAC first: HLS only plays
    // natively in Safari, so it can't be the primary for Chrome/Android.
    { name: "Al Ansaar Radio",   url: "https://edge.iono.fm/xice/467_medium.aac", arabicInitial: "أ" },
    { name: "Channel Islam International", url: "https://edge.iono.fm/xice/109_medium.aac", arabicInitial: "ج" },
    { name: "Radio Islam", url: "https://cast1.my-control-panel.com/proxy/netmoham/radioislam.mp3", arabicInitial: "ر" },
    { name: "BBC World Service", url: "https://stream.live.vc.bbcmedia.co.uk/bbc_world_service", arabicInitial: "ب" },
  ],
};

// Maps LiveMasjid mount name → prayer times / website URL
const TIMES_MAP = {
  // Original curated masjids
  westwoodabs:    "https://www.abubakrsiddique.co.za",
  ihilal:         "https://www.alhilal.co.za",
  noor:           "https://www.musjidnoor.za.net",
  saliheendbn:    "https://www.saliheen.org.za",
  rockdale:       "https://www.muaadhbinjabal.org.za",
  umhlanga:       "https://www.umhlangaislamic.co.za",
  westville:      "https://salaahboard.co.za/westville/",
  // Additional Durban masjids
  darulihsan:     "https://darulihsan.com/",
  northbeachdbn:  "https://salaahboard.co.za/northbeach/",
  musjidurrahman: "https://musjidurrahman.co.za/",
  taqwadbn:       "https://www.taqwa.org.za/",
  mahmood:        "https://masjidboardlive.com/boards/?id=MSJ11341EXT",
  lalucia:        "https://premium.masjidboardlive.com/v2/?mid=la-lucia-fatah",
  may:            "https://salaahboard.co.za/maystreet/",
  nurulhuda:      "https://www.nurulhuda.co.za/",
  shallcross:     "https://www.quwwatulislam.org.za/",
  // KZN — Pietermaritzburg
  sawliheenpmb:   "https://www.sawliheen.co.za/",
  mountainrise:   "https://mountainrisemusjid.co.za/",
  // KZN — Newcastle
  nmcc:           "https://www.nmc.org.za/",
  // Eastern Cape
  taqwape:        "https://masjidtaqwa.org.za/",
  mansoorpe:      "https://www.mansoor.co.za/",
  // Western Cape
  tuba:           "https://panoramamasjid.co.za/",
  // Gauteng — Johannesburg / Lenasia
  rmsc:           "https://www.rmsc.org.za/",
  nurulislam:     "https://nurulislam.org.za/",
  hmjamaat:       "https://www.hmjamaat.com/",
  rynsoord:       "https://masjidetawheed.com/",
  lakefield:      "https://masjidulansaar.co.za/",
  abubakrmackenzie: "https://www.mpit.co.za/",
  // Gauteng — Pretoria / Laudium
  salaamlaudium:  "https://darussalaamcenter.co.za/",
  // Gauteng — other (no dedicated site, salaahtimes per-masjid pages)
  crosby:         "https://salaahtimes.co.za/crosby-masjid-muaadh-bin-jabal",
  noorroshnee:    "https://salaahtimes.co.za/roshnee-masjid-an-noor",
  // KZN — Newcastle Jaamia (no dedicated site)
  newcastle:      "https://salaahtimes.co.za/newcastle-jaami-musjid",
  // KZN — Ladysmith (no dedicated site)
  ladysmith:      "https://salaahtimes.co.za/ladysmith",
  // North West
  potchefstroom:  "https://salaahtimes.co.za/potchefstroom-town-masjid",
  rustenburg:     "https://salaahtimes.co.za/rustenburg",
  abraarrustenburg: "https://salaahtimes.co.za/rustenburg",
  // Mpumalanga
  nelspruit:      "https://salaahtimes.co.za/nelspruit-masjid",
  middelburg:     "https://salaahtimes.co.za/middelburg",
  // Eastern Cape (city-level, no per-masjid page found)
  eastlondon:     "https://salaahtimes.co.za/east-london",
  // Free State
  bloemfontein:   "https://salaahtimes.co.za/bloemfontein",
  // Gauteng — Springs
  jamisprings:    "https://salaahtimes.co.za/springs",
};

const BUILD_TIME = process.env.REACT_APP_BUILD_TIME || null;

// Common Arabic-origin name transliterations → Arabic script.
// Used to render `Yusuf` as `يوسف` beneath the Jumuʿah card's Dancing Script name.
// Keys are lowercased; multiple transliterations can map to the same Arabic form.
const NAMES_AR = {
  // Men / prophets / companions
  muhammad:"محمد", mohamed:"محمد", mohammed:"محمد", mohammad:"محمد", mahomed:"محمد",
  ahmed:"أحمد", ahmad:"أحمد",
  yusuf:"يوسف", yousef:"يوسف", yusef:"يوسف", yousuf:"يوسف", yousif:"يوسف",
  ibrahim:"إبراهيم", ebrahim:"إبراهيم",
  ismail:"إسماعيل", ismaeel:"إسماعيل", ismaael:"إسماعيل",
  ishaq:"إسحاق", ishaaq:"إسحاق",
  yaqoub:"يعقوب", yaqub:"يعقوب", yacoub:"يعقوب",
  dawud:"داود", dawood:"داود", daud:"داود",
  sulaiman:"سليمان", suleiman:"سليمان", sulaymaan:"سليمان",
  musa:"موسى", moosa:"موسى",
  isa:"عيسى", esa:"عيسى",
  nuh:"نوح", nooh:"نوح",
  adam:"آدم",
  ali:"علي", aly:"علي",
  hassan:"حسن", hasan:"حسن",
  hussein:"حسين", husain:"حسين", hussain:"حسين",
  omar:"عمر", umar:"عمر",
  uthman:"عثمان", othman:"عثمان",
  abdullah:"عبدالله", abdallah:"عبدالله",
  abdulrahman:"عبدالرحمن", abdurrahman:"عبدالرحمن", abdurahman:"عبدالرحمن",
  abdulaziz:"عبدالعزيز",
  abdulmalik:"عبدالملك",
  khalid:"خالد", khaled:"خالد",
  hamza:"حمزة", hamzah:"حمزة",
  bilal:"بلال",
  zaid:"زيد", zayd:"زيد",
  zain:"زين", zayn:"زين",
  salman:"سلمان",
  saad:"سعد",
  saif:"سيف", sayf:"سيف",
  tariq:"طارق", tarek:"طارق",
  amir:"أمير", ameer:"أمير",
  rashid:"راشد", rasheed:"رشيد",
  rayyan:"ريان", rayan:"ريان",
  faisal:"فيصل", faysal:"فيصل",
  kamal:"كمال",
  karim:"كريم", kareem:"كريم",
  imran:"عمران",
  yahya:"يحيى",
  zakariya:"زكريا", zakaria:"زكريا", zakariyya:"زكريا",
  yunus:"يونس", younus:"يونس",
  harun:"هارون", haroon:"هارون",
  nasir:"ناصر", naseer:"ناصر",
  yaseen:"ياسين", yasin:"ياسين",
  yasir:"ياسر",
  mustafa:"مصطفى",
  murad:"مراد",
  hashim:"هاشم",
  habib:"حبيب", habeeb:"حبيب",
  jamal:"جمال",
  mahmoud:"محمود", mahmud:"محمود",
  ridwan:"رضوان",
  rizwan:"رضوان",
  shareef:"شريف", sharif:"شريف",
  anas:"أنس",
  usama:"أسامة", osama:"أسامة",
  abdalla:"عبدالله",
  sami:"سامي",
  zahir:"زاهر",
  taha:"طه",
  haniff:"حنيف", hanif:"حنيف",
  aqeel:"عقيل",
  // Women
  aisha:"عائشة", ayesha:"عائشة", ayisha:"عائشة", aysha:"عائشة",
  khadija:"خديجة", khadijah:"خديجة", khadeeja:"خديجة",
  fatima:"فاطمة", fatimah:"فاطمة",
  maryam:"مريم", mariam:"مريم", marium:"مريم", meryem:"مريم",
  amina:"آمنة", aminah:"آمنة", amineh:"آمنة",
  noor:"نور", nur:"نور",
  asiya:"آسية", asya:"آسية",
  ruqayyah:"رقية", ruqayya:"رقية", ruqaiya:"رقية", ruqaya:"رقية",
  hafsa:"حفصة", hafsah:"حفصة",
  safiya:"صفية", safiyyah:"صفية",
  summayyah:"سمية", sumayyah:"سمية", sumayya:"سمية",
  zaynab:"زينب", zainab:"زينب",
  yasmin:"ياسمين", yasmine:"ياسمين", yasmeen:"ياسمين",
  zahra:"زهراء", zahraa:"زهراء",
  layla:"ليلى", laila:"ليلى", leila:"ليلى",
  sara:"سارة", sarah:"سارة",
  salma:"سلمى",
  huda:"هدى",
  bushra:"بشرى",
  sanaa:"سناء", sana:"سناء",
  samira:"سميرة", sameera:"سميرة",
  nadia:"نادية",
  rania:"رانيا",
  amal:"أمل",
  amani:"أماني",
  ikram:"إكرام",
  iman:"إيمان", eman:"إيمان",
  rahma:"رحمة", rahmah:"رحمة",
  nabila:"نبيلة",
  munira:"منيرة", muneera:"منيرة",
  nadira:"نادرة",
  hadiya:"هدية",
  afra:"عفراء",
  maha:"مها",
  lina:"لينا",
  dina:"دينا",
  naila:"نائلة",
  raheema:"رحيمة", rahima:"رحيمة",
  hibah:"هبة", hiba:"هبة",
  rawaan:"روان", rawan:"روان",
  shahida:"شهيدة",
  thuraya:"ثريا",
};
// Look up a name's Arabic form. Handles common spelling variants by stripping spaces + lowercasing.
function lookupNameAr(s){
  if (!s || typeof s !== 'string') return '';
  const key = s.trim().toLowerCase().replace(/\s+/g, '');
  return NAMES_AR[key] || '';
}
const DEV_PUSH_TIME = "26 Apr 2026 22:50 SAST";
// Feature flag — Azaan Alerts (per-prayer Makkah azaan clip).
// Disabled on main while we fix reliability. Keep enabled on dev/staging.
const SHOW_AZAAN_ALERTS = true;
const ARABIC_GREETINGS = [
  { arabic: "مرحباً",         english: "welcome" },
  { arabic: "أهلاً بك",       english: "glad you're here" },
  { arabic: "نوّرت",           english: "you've brought light" },
  { arabic: "حيّاك الله",      english: "may Allah keep you" },
  { arabic: "بارك الله فيك",   english: "may Allah bless you" },
  { arabic: "ما شاء الله",     english: "what Allah has willed" },
  { arabic: "طاب يومك",        english: "may your day be good" },
];
const PRAYER_NAMES = ["Fajr", "Sunrise", "Dhuhr", "Asr", "Maghrib", "Isha"];
const TRACKABLE_PRAYERS = ["Fajr", "Dhuhr", "Asr", "Maghrib", "Isha"];
const PRAYER_ARABIC = { Fajr: "الفَجْر", Sunrise: "الشُّرُوق", Dhuhr: "الظُّهْر", Asr: "العَصْر", Maghrib: "المَغْرِب", Isha: "العِشَاء" };
// Mount names that should never surface anywhere (test/developer streams).
// Scrubbed from localStorage on load AND filtered from every render path.
const BLOCKED_MOUNTS = new Set(["yusuftest", "yusuf-test", "yusuftest.mp3"]);
const isBlockedMount = (m) => { if (!m) return false; const clean = String(m).replace(/\.mp3$/, "").toLowerCase(); return BLOCKED_MOUNTS.has(clean); };

// Prayer detail info — shown in a bottom sheet when the user taps the dial glyph
// on the Salaah hero. Rakats follow the common Hanafi/Shafi'i tradition; virtues
// are authenticated hadith paraphrased from Bukhari, Muslim, Abu Dawud, etc.
const PRAYER_INFO = {
  Fajr: {
    rakats: [["Sunnah", "2"], ["Fard", "2"]],
    window: "Dawn until sunrise",
    virtues: [
      { text: "The two rak'ahs of Fajr are better than the world and everything in it.", source: "Sahih Muslim 725" },
      { text: "Whoever prays Fajr in congregation, it is as though he has stood the whole night in prayer.", source: "Sahih Muslim 656" },
      { text: "The angels of the night and the angels of the day meet at Fajr prayer.", source: "Bukhari · Muslim" },
    ],
  },
  Sunrise: {
    rakats: null,
    window: "Ishraq — about 15 min after sunrise",
    virtues: [
      { text: "Whoever prays Fajr in congregation, then sits remembering Allah until the sun rises, then prays two rak'ahs, receives the reward of a complete Hajj and Umrah.", source: "Tirmidhi 586" },
    ],
  },
  Dhuhr: {
    rakats: [["Sunnah before", "4"], ["Fard", "4"], ["Sunnah after", "2"]],
    window: "After zenith until Asr",
    virtues: [
      { text: "There are four rak'ahs before Dhuhr; whoever prays them, the gates of heaven are opened for him.", source: "An-Nasa'i · graded sahih" },
      { text: "When the sun reaches its zenith, the gates of the heavens are opened until the sun begins its descent.", source: "Abu Dawud" },
    ],
  },
  Asr: {
    rakats: [["Sunnah (optional)", "4"], ["Fard", "4"]],
    window: "Afternoon until sunset",
    virtues: [
      { text: "Whoever misses Asr prayer, it is as though he has lost his family and wealth.", source: "Sahih al-Bukhari 552" },
      { text: "Guard strictly the prayers, especially the middle prayer — and it is Al-Asr.", source: "Qur'an 2:238 · hadith Muslim 627" },
    ],
  },
  Maghrib: {
    rakats: [["Fard", "3"], ["Sunnah after", "2"]],
    window: "After sunset until the red twilight fades",
    virtues: [
      { text: "Between every two calls (adhan & iqamah) there is a prayer answered — especially at Maghrib.", source: "Abu Dawud 1283" },
      { text: "Hasten to pray Maghrib — my Ummah will remain upon good as long as they hasten it.", source: "Ahmad" },
    ],
  },
  Isha: {
    rakats: [["Fard", "4"], ["Sunnah after", "2"], ["Witr", "3"]],
    window: "After twilight fades until pre-dawn",
    virtues: [
      { text: "Whoever prays Isha in congregation, it is as if he stood half the night; and whoever prays Fajr in congregation, as if he stood the whole night.", source: "Sahih Muslim 656" },
      { text: "Make Witr the last of your night prayers.", source: "Sahih al-Bukhari 998" },
    ],
  },
};
const localDateStr = () => { const d = new Date(); return d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0") + "-" + String(d.getDate()).padStart(2,"0"); };
const formatTimeRemaining = (expiryDate) => {
  const ms = new Date(expiryDate) - new Date();
  if (ms <= 0) return "Expired";
  const mins = Math.ceil(ms / 60000);
  if (mins < 60) return `${mins} min${mins !== 1 ? "s" : ""} left`;
  const hrs = Math.floor(ms / 3600000);
  const remMins = Math.ceil((ms % 3600000) / 60000);
  if (hrs < 24) return remMins > 0 ? `${hrs}h ${remMins}m left` : `${hrs} hour${hrs !== 1 ? "s" : ""} left`;
  const days = Math.ceil(ms / 86400000);
  if (days < 365) return `${days} day${days !== 1 ? "s" : ""} left`;
  const years = Math.round(days / 365);
  return `${years} year${years !== 1 ? "s" : ""} left`;
};

/* CSS moved to App.css */

let audioEl = null;
// Double-buffer for seamless ayah transitions
let yasinBufs = [null, null];
let yasinBufActive = 0;
function setupYasinBuf(i) {
  if (!yasinBufs[i]) { yasinBufs[i] = new Audio(); yasinBufs[i].preload = "auto"; yasinBufs[i]._url = ""; }
}
function preloadYasinBuf(i, url) {
  setupYasinBuf(i);
  if (yasinBufs[i]._url !== url) { yasinBufs[i]._url = url; yasinBufs[i].src = url; yasinBufs[i].load(); }
}

function getAudio() {
  if (!audioEl) {
    audioEl = new Audio();
    audioEl.preload = "none";
  }
  return audioEl;
}

// iPad sends a desktop Safari UA → the LiveMasjid relay serves OGG, which
// Safari can't play. Appending .mp3 to the mount makes the relay serve
// audio/mpeg instead. iPhone gets MP3 by default (mobile UA), so no extension
// is needed there.
// Only extensionless mounts get the suffix: the radio stations already end in
// .mp3 / .aac, and "…/radio.mp3.mp3" is a 404 that leaves the player stuck.
function withIpadMp3(url) {
  const isIPad = navigator.userAgent.includes("iPad") ||
    (navigator.userAgent.includes("Macintosh") && navigator.maxTouchPoints > 1);
  if (!isIPad) return url;
  const lastSegment = url.split("?")[0].split("/").pop() || "";
  if (/\.[a-z0-9]{2,4}$/i.test(lastSegment)) return url;
  return url.replace(/\/?$/, ".mp3");
}

// Ordered list of URLs to try for a station.
// AzuraCast (which hosts Al Ansaar) serves the same programme on several
// mounts. When a station swaps or retires its primary mount the browser does
// not raise an error — the connection just hangs — so the player would sit on
// "Connecting" indefinitely. Trying the host's other standard paths recovers
// from that, and failing all of them at least produces an honest "Offline".
function streamCandidates(url) {
  const out = [withIpadMp3(url)];

  const azuracast = url.match(/^(https?:\/\/[^/]+)\/listen\/([^/]+)\/[^/?#]+$/);
  if (azuracast) {
    const [, origin, shortName] = azuracast;
    for (const alt of [
      `${origin}/listen/${shortName}/radio.mp3`,
      `${origin}/radio/8000/radio.mp3`,
      `${origin}/listen/${shortName}/aac`,
    ]) if (!out.includes(alt)) out.push(alt);
  }

  // iono.fm publishes each channel as plain AAC over Icecast (/xice/) at a few
  // bitrates, and as HLS (/xhls/). HLS goes last on purpose: only Safari plays
  // .m3u8 natively in an <audio> element, so it's a fallback, never the primary.
  const iono = url.match(/^(https?:\/\/[^/]+)\/x(?:ice|hls)\/(\d+)_/);
  if (iono) {
    const [, origin, channelId] = iono;
    for (const alt of [
      `${origin}/xice/${channelId}_medium.aac`,
      `${origin}/xice/${channelId}_high.aac`,
      `${origin}/xhls/${channelId}_high.m3u8`,
    ]) if (!out.includes(alt)) out.push(alt);
  }

  return out;
}

// Wake Lock — prevents iOS from suspending audio when screen locks
let _wakeLock = null;
async function requestWakeLock() {
  if (isNative()) {
    try { const { KeepAwake } = await import('@capacitor-community/keep-awake'); await KeepAwake.keepAwake(); } catch {}
    return;
  }
  if (!("wakeLock" in navigator)) return;
  try { _wakeLock = await navigator.wakeLock.request("screen"); _wakeLock.addEventListener("release", () => { _wakeLock = null; }); } catch {}
}
async function releaseWakeLock() {
  if (isNative()) {
    try { const { KeepAwake } = await import('@capacitor-community/keep-awake'); await KeepAwake.allowSleep(); } catch {}
    return;
  }
  if (_wakeLock) { _wakeLock.release().catch(() => {}); _wakeLock = null; }
}

function formatTime(secs) {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}`;
}

function to24min(t) {
  if (!t) return 0;
  const parts = t.replace(/ \(.*\)/, "").split(":");
  return parseInt(parts[0]) * 60 + parseInt(parts[1]);
}

// iOS shares cookies between Safari and same-domain PWA; localStorage is siloed.
// Storing userId in a cookie lets the PWA auto-restore accounts registered in Safari.
function getCookie(name) {
  const m = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return m ? m[2] : null;
}
function setUserCookie(id) {
  document.cookie = `azaan-userId=${id}; max-age=${365*24*60*60}; path=/; SameSite=Lax`;
}
function clearUserCookie() {
  document.cookie = "azaan-userId=; max-age=0; path=/; SameSite=Lax";
}
function setProCookie() {
  document.cookie = `sirat-isPro=1; max-age=${3*365*24*60*60}; path=/; SameSite=Lax`;
}
function clearProCookie() {
  document.cookie = `sirat-isPro=; max-age=0; path=/; SameSite=Lax`;
}

const RECITERS = [
  { id: "ar.husary",               name: "Mahmoud Al-Husary",          short: "Husary" },
  { id: "ar.abdulbasitmujawwad",   name: "Abdul Basit (Mujawwad)",     short: "Abdul Basit" },
  { id: "ar.minshawi",             name: "Mohamed Al-Minshawi",        short: "Minshawi" },
  { id: "ar.alafasy",              name: "Mishary Alafasy",            short: "Alafasy" },
  { id: "ar.abdurrahmaansudais",   name: "Abdurrahman As-Sudais",      short: "Sudais" },
  { id: "ar.saoodshuraym",         name: "Saud Ash-Shuraim",           short: "Shuraim" },
  { id: "ar.mahermuaiqly",         name: "Maher Al Muaiqly",           short: "Muaiqly" },
  { id: "ar.abdulbasitmurattal",   name: "Abdul Basit (Murattal)",     short: "Murattal" },
  { id: "ar.ahmedajamy",           name: "Ahmed Al-Ajmi",              short: "Al-Ajmi" },
  { id: "ar.shaatree",             name: "Abu Bakr Ash-Shatri",        short: "Shatri" },
  { id: "ar.hanirifai",            name: "Hani Ar-Rifai",              short: "Ar-Rifai" },
];

const SURAH_INFO = {
  // ── Free ──────────────────────────────────────────────────────────────────
  yaseen:   { num: 36, name: "Surah Yaseen",       arabic: "سورة يس",         count: 83,  icon: "📖", reciter: "ar.alafasy", totalSecs: 840  },
  kahf:     { num: 18, name: "Surah Al-Kahf",      arabic: "سورة الكهف",       count: 110, icon: "🌿", reciter: "ar.alafasy", totalSecs: 1440 },
  rahman:   { num: 55, name: "Surah Ar-Rahman",    arabic: "سورة الرحمن",      count: 78,  icon: "✨", reciter: "ar.alafasy", totalSecs: 600  },
  mulk:     { num: 67, name: "Surah Al-Mulk",      arabic: "سورة الملك",       count: 30,  icon: "👑", reciter: "ar.alafasy", totalSecs: 480  },
  // ── Pro ───────────────────────────────────────────────────────────────────
  fatiha:   { num:  1, name: "Surah Al-Fatiha",    arabic: "سورة الفاتحة",     count:  7,  icon: "🌟", reciter: "ar.alafasy", totalSecs:  40,  pro: true },
  ikhlas:   { num:112, name: "Surah Al-Ikhlas",    arabic: "سورة الإخلاص",     count:  4,  icon: "☝️", reciter: "ar.alafasy", totalSecs:  25,  pro: true },
  falaq:    { num:113, name: "Surah Al-Falaq",     arabic: "سورة الفلق",       count:  5,  icon: "🌄", reciter: "ar.alafasy", totalSecs:  30,  pro: true },
  nas:      { num:114, name: "Surah An-Nas",       arabic: "سورة الناس",       count:  6,  icon: "👥", reciter: "ar.alafasy", totalSecs:  35,  pro: true },
  asr:      { num:103, name: "Surah Al-Asr",       arabic: "سورة العصر",       count:  3,  icon: "⏳", reciter: "ar.alafasy", totalSecs:  20,  pro: true },
  kawthar:  { num:108, name: "Surah Al-Kawthar",   arabic: "سورة الكوثر",      count:  3,  icon: "💧", reciter: "ar.alafasy", totalSecs:  20,  pro: true },
  nasr:     { num:110, name: "Surah An-Nasr",      arabic: "سورة النصر",       count:  3,  icon: "🏆", reciter: "ar.alafasy", totalSecs:  25,  pro: true },
  qadr:     { num: 97, name: "Surah Al-Qadr",      arabic: "سورة القدر",       count:  5,  icon: "⭐", reciter: "ar.alafasy", totalSecs:  35,  pro: true },
  tin:      { num: 95, name: "Surah At-Tin",       arabic: "سورة التين",       count:  8,  icon: "🍇", reciter: "ar.alafasy", totalSecs:  50,  pro: true },
  zalzalah: { num: 99, name: "Surah Az-Zalzalah",  arabic: "سورة الزلزلة",     count:  8,  icon: "🌍", reciter: "ar.alafasy", totalSecs:  45,  pro: true },
  alaq:     { num: 96, name: "Surah Al-Alaq",      arabic: "سورة العلق",       count: 19,  icon: "📜", reciter: "ar.alafasy", totalSecs:  90,  pro: true },
  waqiah:   { num: 56, name: "Surah Al-Waqi'ah",   arabic: "سورة الواقعة",     count: 96,  icon: "💎", reciter: "ar.alafasy", totalSecs: 720,  pro: true },
  duha:     { num: 93, name: "Surah Ad-Duha",      arabic: "سورة الضحى",       count: 11,  icon: "🌅", reciter: "ar.alafasy", totalSecs:  90,  pro: true },
  inshirah: { num: 94, name: "Surah Al-Inshirah",  arabic: "سورة الشرح",       count:  8,  icon: "🕊", reciter: "ar.alafasy", totalSecs:  60,  pro: true },
  ahzab:    { num: 33, name: "Surah Al-Ahzab",     arabic: "سورة الأحزاب",     count: 73,  icon: "⚔️", reciter: "ar.alafasy", totalSecs: 1800, pro: true },
  // ── Para 30 / Juz Amma ───────────────────────────────────────────────────
  naba:       { num: 78,  name: "Surah An-Naba",       arabic: "سورة النبأ",       count: 40, icon: "🌌", reciter: "ar.alafasy", totalSecs: 420,  pro: true },
  naziat:     { num: 79,  name: "Surah An-Naziat",     arabic: "سورة النازعات",    count: 46, icon: "⚡", reciter: "ar.alafasy", totalSecs: 480,  pro: true },
  abasa:      { num: 80,  name: "Surah Abasa",         arabic: "سورة عبس",         count: 42, icon: "🌹", reciter: "ar.alafasy", totalSecs: 360,  pro: true },
  takwir:     { num: 81,  name: "Surah At-Takwir",     arabic: "سورة التكوير",     count: 29, icon: "🌀", reciter: "ar.alafasy", totalSecs: 90,   pro: true },
  infitar:    { num: 82,  name: "Surah Al-Infitar",    arabic: "سورة الانفطار",    count: 19, icon: "💫", reciter: "ar.alafasy", totalSecs: 60,   pro: true },
  mutaffifin: { num: 83,  name: "Surah Al-Mutaffifin", arabic: "سورة المطففين",    count: 36, icon: "⚖️", reciter: "ar.alafasy", totalSecs: 420,  pro: true },
  inshiqaq:   { num: 84,  name: "Surah Al-Inshiqaq",   arabic: "سورة الانشقاق",   count: 25, icon: "🌒", reciter: "ar.alafasy", totalSecs: 240,  pro: true },
  buruj:      { num: 85,  name: "Surah Al-Buruj",      arabic: "سورة البروج",      count: 22, icon: "🌃", reciter: "ar.alafasy", totalSecs: 150,  pro: true },
  tariq:      { num: 86,  name: "Surah At-Tariq",      arabic: "سورة الطارق",      count: 17, icon: "🌠", reciter: "ar.alafasy", totalSecs: 60,   pro: true },
  ala:        { num: 87,  name: "Surah Al-Ala",        arabic: "سورة الأعلى",      count: 19, icon: "🏔", reciter: "ar.alafasy", totalSecs: 60,   pro: true },
  ghashiyah:  { num: 88,  name: "Surah Al-Ghashiyah",  arabic: "سورة الغاشية",     count: 26, icon: "☁️", reciter: "ar.alafasy", totalSecs: 240,  pro: true },
  fajr:       { num: 89,  name: "Surah Al-Fajr",       arabic: "سورة الفجر",       count: 30, icon: "🌇", reciter: "ar.alafasy", totalSecs: 300,  pro: true },
  balad:      { num: 90,  name: "Surah Al-Balad",      arabic: "سورة البلد",       count: 20, icon: "🏙", reciter: "ar.alafasy", totalSecs: 180,  pro: true },
  shams:      { num: 91,  name: "Surah Ash-Shams",     arabic: "سورة الشمس",       count: 15, icon: "☀️", reciter: "ar.alafasy", totalSecs: 90,   pro: true },
  layl:       { num: 92,  name: "Surah Al-Layl",       arabic: "سورة الليل",       count: 21, icon: "🌑", reciter: "ar.alafasy", totalSecs: 120,  pro: true },
  bayyinah:   { num: 98,  name: "Surah Al-Bayyinah",   arabic: "سورة البينة",      count: 8,  icon: "📋", reciter: "ar.alafasy", totalSecs: 120,  pro: true },
  adiyat:     { num: 100, name: "Surah Al-Adiyat",     arabic: "سورة العاديات",    count: 11, icon: "🐎", reciter: "ar.alafasy", totalSecs: 120,  pro: true },
  qariah:     { num: 101, name: "Surah Al-Qariah",     arabic: "سورة القارعة",     count: 11, icon: "🔔", reciter: "ar.alafasy", totalSecs: 90,   pro: true },
  takathur:   { num: 102, name: "Surah At-Takathur",   arabic: "سورة التكاثر",     count: 8,  icon: "💰", reciter: "ar.alafasy", totalSecs: 60,   pro: true },
  humazah:    { num: 104, name: "Surah Al-Humazah",    arabic: "سورة الهمزة",      count: 9,  icon: "😤", reciter: "ar.alafasy", totalSecs: 60,   pro: true },
  fil:        { num: 105, name: "Surah Al-Fil",        arabic: "سورة الفيل",       count: 5,  icon: "🐘", reciter: "ar.alafasy", totalSecs: 60,   pro: true },
  quraysh:    { num: 106, name: "Surah Quraysh",       arabic: "سورة قريش",        count: 4,  icon: "🕌", reciter: "ar.alafasy", totalSecs: 60,   pro: true },
  maun:       { num: 107, name: "Surah Al-Maun",       arabic: "سورة الماعون",     count: 7,  icon: "🤲", reciter: "ar.alafasy", totalSecs: 90,   pro: true },
  kafirun:    { num: 109, name: "Surah Al-Kafirun",    arabic: "سورة الكافرون",    count: 6,  icon: "🚫", reciter: "ar.alafasy", totalSecs: 60,   pro: true },
  masad:      { num: 111, name: "Surah Al-Masad",      arabic: "سورة المسد",       count: 5,  icon: "🔥", reciter: "ar.alafasy", totalSecs: 30,   pro: true },
};

const QURAN_SURAHS = [
  { num:  1, arabic: "الفاتحة",    translit: "Al-Fatiha",       english: "The Opening",          ayahs:  7 },
  { num:  2, arabic: "البقرة",     translit: "Al-Baqarah",      english: "The Cow",              ayahs: 286 },
  { num:  3, arabic: "آل عمران",   translit: "Ali 'Imran",      english: "Family of Imran",      ayahs: 200 },
  { num:  4, arabic: "النساء",     translit: "An-Nisa",         english: "The Women",            ayahs: 176 },
  { num:  5, arabic: "المائدة",    translit: "Al-Ma'ida",       english: "The Table Spread",     ayahs: 120 },
  { num:  6, arabic: "الأنعام",    translit: "Al-An'am",        english: "The Cattle",           ayahs: 165 },
  { num:  7, arabic: "الأعراف",    translit: "Al-A'raf",        english: "The Heights",          ayahs: 206 },
  { num:  8, arabic: "الأنفال",    translit: "Al-Anfal",        english: "The Spoils of War",    ayahs:  75 },
  { num:  9, arabic: "التوبة",     translit: "At-Tawbah",       english: "The Repentance",       ayahs: 129 },
  { num: 10, arabic: "يونس",       translit: "Yunus",           english: "Jonah",                ayahs: 109 },
  { num: 11, arabic: "هود",        translit: "Hud",             english: "Hud",                  ayahs: 123 },
  { num: 12, arabic: "يوسف",       translit: "Yusuf",           english: "Joseph",               ayahs: 111 },
  { num: 13, arabic: "الرعد",      translit: "Ar-Ra'd",         english: "The Thunder",          ayahs:  43 },
  { num: 14, arabic: "إبراهيم",    translit: "Ibrahim",         english: "Abraham",              ayahs:  52 },
  { num: 15, arabic: "الحجر",      translit: "Al-Hijr",         english: "The Rocky Tract",      ayahs:  99 },
  { num: 16, arabic: "النحل",      translit: "An-Nahl",         english: "The Bee",              ayahs: 128 },
  { num: 17, arabic: "الإسراء",    translit: "Al-Isra",         english: "The Night Journey",    ayahs: 111 },
  { num: 18, arabic: "الكهف",      translit: "Al-Kahf",         english: "The Cave",             ayahs: 110 },
  { num: 19, arabic: "مريم",       translit: "Maryam",          english: "Mary",                 ayahs:  98 },
  { num: 20, arabic: "طه",         translit: "Ta-Ha",           english: "Ta-Ha",                ayahs: 135 },
  { num: 21, arabic: "الأنبياء",   translit: "Al-Anbiya",       english: "The Prophets",         ayahs: 112 },
  { num: 22, arabic: "الحج",       translit: "Al-Hajj",         english: "The Pilgrimage",       ayahs:  78 },
  { num: 23, arabic: "المؤمنون",   translit: "Al-Mu'minun",     english: "The Believers",        ayahs: 118 },
  { num: 24, arabic: "النور",      translit: "An-Nur",          english: "The Light",            ayahs:  64 },
  { num: 25, arabic: "الفرقان",    translit: "Al-Furqan",       english: "The Criterion",        ayahs:  77 },
  { num: 26, arabic: "الشعراء",    translit: "Ash-Shu'ara",     english: "The Poets",            ayahs: 227 },
  { num: 27, arabic: "النمل",      translit: "An-Naml",         english: "The Ant",              ayahs:  93 },
  { num: 28, arabic: "القصص",      translit: "Al-Qasas",        english: "The Stories",          ayahs:  88 },
  { num: 29, arabic: "العنكبوت",   translit: "Al-'Ankabut",     english: "The Spider",           ayahs:  69 },
  { num: 30, arabic: "الروم",      translit: "Ar-Rum",          english: "The Romans",           ayahs:  60 },
  { num: 31, arabic: "لقمان",      translit: "Luqman",          english: "Luqman",               ayahs:  34 },
  { num: 32, arabic: "السجدة",     translit: "As-Sajda",        english: "The Prostration",      ayahs:  30 },
  { num: 33, arabic: "الأحزاب",    translit: "Al-Ahzab",        english: "The Combined Forces",  ayahs:  73 },
  { num: 34, arabic: "سبأ",        translit: "Saba",            english: "Sheba",                ayahs:  54 },
  { num: 35, arabic: "فاطر",       translit: "Fatir",           english: "Originator",           ayahs:  45 },
  { num: 36, arabic: "يس",         translit: "Ya-Sin",          english: "Ya Sin",               ayahs:  83 },
  { num: 37, arabic: "الصافات",    translit: "As-Saffat",       english: "Those Who Set the Ranks", ayahs: 182 },
  { num: 38, arabic: "ص",          translit: "Sad",             english: "The Letter Sad",       ayahs:  88 },
  { num: 39, arabic: "الزمر",      translit: "Az-Zumar",        english: "The Troops",           ayahs:  75 },
  { num: 40, arabic: "غافر",       translit: "Ghafir",          english: "The Forgiver",         ayahs:  85 },
  { num: 41, arabic: "فصلت",       translit: "Fussilat",        english: "Explained in Detail",  ayahs:  54 },
  { num: 42, arabic: "الشورى",     translit: "Ash-Shura",       english: "The Consultation",     ayahs:  53 },
  { num: 43, arabic: "الزخرف",     translit: "Az-Zukhruf",      english: "The Ornaments of Gold",ayahs:  89 },
  { num: 44, arabic: "الدخان",     translit: "Ad-Dukhan",       english: "The Smoke",            ayahs:  59 },
  { num: 45, arabic: "الجاثية",    translit: "Al-Jathiya",      english: "The Crouching",        ayahs:  37 },
  { num: 46, arabic: "الأحقاف",    translit: "Al-Ahqaf",        english: "The Wind-Curved Sandhills", ayahs: 35 },
  { num: 47, arabic: "محمد",       translit: "Muhammad",        english: "Muhammad",             ayahs:  38 },
  { num: 48, arabic: "الفتح",      translit: "Al-Fath",         english: "The Victory",          ayahs:  29 },
  { num: 49, arabic: "الحجرات",    translit: "Al-Hujurat",      english: "The Rooms",            ayahs:  18 },
  { num: 50, arabic: "ق",          translit: "Qaf",             english: "The Letter Qaf",       ayahs:  45 },
  { num: 51, arabic: "الذاريات",   translit: "Adh-Dhariyat",    english: "The Winnowing Winds",  ayahs:  60 },
  { num: 52, arabic: "الطور",      translit: "At-Tur",          english: "The Mount",            ayahs:  49 },
  { num: 53, arabic: "النجم",      translit: "An-Najm",         english: "The Star",             ayahs:  62 },
  { num: 54, arabic: "القمر",      translit: "Al-Qamar",        english: "The Moon",             ayahs:  55 },
  { num: 55, arabic: "الرحمن",     translit: "Ar-Rahman",       english: "The Beneficent",       ayahs:  78 },
  { num: 56, arabic: "الواقعة",    translit: "Al-Waqi'a",       english: "The Inevitable",       ayahs:  96 },
  { num: 57, arabic: "الحديد",     translit: "Al-Hadid",        english: "The Iron",             ayahs:  29 },
  { num: 58, arabic: "المجادلة",   translit: "Al-Mujadila",     english: "The Pleading Woman",   ayahs:  22 },
  { num: 59, arabic: "الحشر",      translit: "Al-Hashr",        english: "The Exile",            ayahs:  24 },
  { num: 60, arabic: "الممتحنة",   translit: "Al-Mumtahana",    english: "She That is to be Examined", ayahs: 13 },
  { num: 61, arabic: "الصف",       translit: "As-Saf",          english: "The Ranks",            ayahs:  14 },
  { num: 62, arabic: "الجمعة",     translit: "Al-Jumu'a",       english: "Friday",               ayahs:  11 },
  { num: 63, arabic: "المنافقون",  translit: "Al-Munafiqun",    english: "The Hypocrites",       ayahs:  11 },
  { num: 64, arabic: "التغابن",    translit: "At-Taghabun",     english: "Mutual Disillusion",   ayahs:  18 },
  { num: 65, arabic: "الطلاق",     translit: "At-Talaq",        english: "Divorce",              ayahs:  12 },
  { num: 66, arabic: "التحريم",    translit: "At-Tahrim",       english: "The Prohibition",      ayahs:  12 },
  { num: 67, arabic: "الملك",      translit: "Al-Mulk",         english: "The Sovereignty",      ayahs:  30 },
  { num: 68, arabic: "القلم",      translit: "Al-Qalam",        english: "The Pen",              ayahs:  52 },
  { num: 69, arabic: "الحاقة",     translit: "Al-Haqqah",       english: "The Reality",          ayahs:  52 },
  { num: 70, arabic: "المعارج",    translit: "Al-Ma'arij",      english: "The Ascending Stairways", ayahs: 44 },
  { num: 71, arabic: "نوح",        translit: "Nuh",             english: "Noah",                 ayahs:  28 },
  { num: 72, arabic: "الجن",       translit: "Al-Jinn",         english: "The Jinn",             ayahs:  28 },
  { num: 73, arabic: "المزمل",     translit: "Al-Muzzammil",    english: "The Enshrouded One",   ayahs:  20 },
  { num: 74, arabic: "المدثر",     translit: "Al-Muddaththir",  english: "The Cloaked One",      ayahs:  56 },
  { num: 75, arabic: "القيامة",    translit: "Al-Qiyama",       english: "The Resurrection",     ayahs:  40 },
  { num: 76, arabic: "الإنسان",    translit: "Al-Insan",        english: "The Human",            ayahs:  31 },
  { num: 77, arabic: "المرسلات",   translit: "Al-Mursalat",     english: "The Emissaries",       ayahs:  50 },
  { num: 78, arabic: "النبأ",      translit: "An-Naba",         english: "The Tidings",          ayahs:  40 },
  { num: 79, arabic: "النازعات",   translit: "An-Nazi'at",      english: "Those Who Drag Forth", ayahs:  46 },
  { num: 80, arabic: "عبس",        translit: "'Abasa",          english: "He Frowned",           ayahs:  42 },
  { num: 81, arabic: "التكوير",    translit: "At-Takwir",       english: "The Overthrowing",     ayahs:  29 },
  { num: 82, arabic: "الانفطار",   translit: "Al-Infitar",      english: "The Cleaving",         ayahs:  19 },
  { num: 83, arabic: "المطففين",   translit: "Al-Mutaffifin",   english: "The Defrauding",       ayahs:  36 },
  { num: 84, arabic: "الانشقاق",   translit: "Al-Inshiqaq",     english: "The Sundering",        ayahs:  25 },
  { num: 85, arabic: "البروج",     translit: "Al-Buruj",        english: "The Mansions of the Stars", ayahs: 22 },
  { num: 86, arabic: "الطارق",     translit: "At-Tariq",        english: "The Morning Star",     ayahs:  17 },
  { num: 87, arabic: "الأعلى",     translit: "Al-A'la",         english: "The Most High",        ayahs:  19 },
  { num: 88, arabic: "الغاشية",    translit: "Al-Ghashiya",     english: "The Overwhelming",     ayahs:  26 },
  { num: 89, arabic: "الفجر",      translit: "Al-Fajr",         english: "The Dawn",             ayahs:  30 },
  { num: 90, arabic: "البلد",      translit: "Al-Balad",        english: "The City",             ayahs:  20 },
  { num: 91, arabic: "الشمس",      translit: "Ash-Shams",       english: "The Sun",              ayahs:  15 },
  { num: 92, arabic: "الليل",      translit: "Al-Layl",         english: "The Night",            ayahs:  21 },
  { num: 93, arabic: "الضحى",      translit: "Ad-Duha",         english: "The Morning Hours",    ayahs:  11 },
  { num: 94, arabic: "الشرح",      translit: "Ash-Sharh",       english: "The Relief",           ayahs:   8 },
  { num: 95, arabic: "التين",      translit: "At-Tin",          english: "The Fig",              ayahs:   8 },
  { num: 96, arabic: "العلق",      translit: "Al-'Alaq",        english: "The Clot",             ayahs:  19 },
  { num: 97, arabic: "القدر",      translit: "Al-Qadr",         english: "The Power",            ayahs:   5 },
  { num: 98, arabic: "البينة",     translit: "Al-Bayyina",      english: "The Clear Proof",      ayahs:   8 },
  { num: 99, arabic: "الزلزلة",    translit: "Az-Zalzala",      english: "The Earthquake",       ayahs:   8 },
  { num: 100, arabic: "العاديات",  translit: "Al-'Adiyat",      english: "The Courser",          ayahs:  11 },
  { num: 101, arabic: "القارعة",   translit: "Al-Qari'a",       english: "The Calamity",         ayahs:  11 },
  { num: 102, arabic: "التكاثر",   translit: "At-Takathur",     english: "The Rivalry in World Increase", ayahs: 8 },
  { num: 103, arabic: "العصر",     translit: "Al-'Asr",         english: "The Declining Day",    ayahs:   3 },
  { num: 104, arabic: "الهمزة",    translit: "Al-Humaza",       english: "The Traducer",         ayahs:   9 },
  { num: 105, arabic: "الفيل",     translit: "Al-Fil",          english: "The Elephant",         ayahs:   5 },
  { num: 106, arabic: "قريش",      translit: "Quraysh",         english: "Quraysh",              ayahs:   4 },
  { num: 107, arabic: "الماعون",   translit: "Al-Ma'un",        english: "The Small Kindnesses",  ayahs:  7 },
  { num: 108, arabic: "الكوثر",    translit: "Al-Kawthar",      english: "The Abundance",        ayahs:   3 },
  { num: 109, arabic: "الكافرون",  translit: "Al-Kafirun",      english: "The Disbelievers",     ayahs:   6 },
  { num: 110, arabic: "النصر",     translit: "An-Nasr",         english: "The Divine Support",   ayahs:   3 },
  { num: 111, arabic: "المسد",     translit: "Al-Masad",        english: "The Palm Fibre",       ayahs:   5 },
  { num: 112, arabic: "الإخلاص",   translit: "Al-Ikhlas",       english: "Sincerity",            ayahs:   4 },
  { num: 113, arabic: "الفلق",     translit: "Al-Falaq",        english: "The Daybreak",         ayahs:   5 },
  { num: 114, arabic: "الناس",     translit: "An-Nas",          english: "Mankind",              ayahs:   6 },
];

// ── "Right Now" — curated verse pool ──
const RIGHTNOW_FALLBACK = [
  { arabic: "فَإِنَّ مَعَ الْعُسْرِ يُسْرًا", translation: "For indeed, with hardship comes ease.", ref: "94:5", surahName: "Ash-Sharh", surahArabic: "الشرح" },
  { arabic: "وَمَن يَتَوَكَّلْ عَلَى اللَّهِ فَهُوَ حَسْبُهُ", translation: "And whoever puts their trust in Allah, He is sufficient for them.", ref: "65:3", surahName: "At-Talaq", surahArabic: "الطلاق" },
  { arabic: "وَاذْكُرُوا اللَّهَ كَثِيرًا لَّعَلَّكُمْ تُفْلِحُونَ", translation: "And remember Allah often, so that you may be successful.", ref: "62:10", surahName: "Al-Jumu'ah", surahArabic: "الجمعة" },
  { arabic: "إِنَّ اللَّهَ مَعَ الصَّابِرِينَ", translation: "Indeed, Allah is with the patient.", ref: "2:153", surahName: "Al-Baqarah", surahArabic: "البقرة" },
  { arabic: "وَلَسَوْفَ يُعْطِيكَ رَبُّكَ فَتَرْضَىٰ", translation: "And your Lord will give you, and you will be satisfied.", ref: "93:5", surahName: "Ad-Duha", surahArabic: "الضحى" },
  { arabic: "أَلَا بِذِكْرِ اللَّهِ تَطْمَئِنُّ الْقُلُوبُ", translation: "Verily, in the remembrance of Allah do hearts find rest.", ref: "13:28", surahName: "Ar-Ra'd", surahArabic: "الرعد" },
  { arabic: "ادْعُونِي أَسْتَجِبْ لَكُمْ", translation: "Call upon Me, I will respond to you.", ref: "40:60", surahName: "Ghafir", surahArabic: "غافر" },
  { arabic: "وَنَحْنُ أَقْرَبُ إِلَيْهِ مِنْ حَبْلِ الْوَرِيدِ", translation: "And We are closer to him than his jugular vein.", ref: "50:16", surahName: "Qaf", surahArabic: "ق" },
  { arabic: "رَبِّ اشْرَحْ لِي صَدْرِي وَيَسِّرْ لِي أَمْرِي", translation: "My Lord, expand my chest and ease my task for me.", ref: "20:25-26", surahName: "Ta-Ha", surahArabic: "طه" },
  { arabic: "حَسْبُنَا اللَّهُ وَنِعْمَ الْوَكِيلُ", translation: "Allah is sufficient for us, and He is the best Disposer of affairs.", ref: "3:173", surahName: "Ali 'Imran", surahArabic: "آل عمران" },
  { arabic: "إِنَّ مَعَ الْعُسْرِ يُسْرًا", translation: "Indeed, with hardship comes ease.", ref: "94:6", surahName: "Ash-Sharh", surahArabic: "الشرح" },
  { arabic: "وَلَا تَيْأَسُوا مِن رَّوْحِ اللَّهِ", translation: "And do not despair of the mercy of Allah.", ref: "12:87", surahName: "Yusuf", surahArabic: "يوسف" },
  { arabic: "قُلْ يَا عِبَادِيَ الَّذِينَ أَسْرَفُوا عَلَىٰ أَنفُسِهِمْ لَا تَقْنَطُوا مِن رَّحْمَةِ اللَّهِ", translation: "Say, 'O My servants who have transgressed against themselves, do not despair of the mercy of Allah.'", ref: "39:53", surahName: "Az-Zumar", surahArabic: "الزمر" },
  { arabic: "وَهُوَ مَعَكُمْ أَيْنَ مَا كُنتُمْ", translation: "And He is with you wherever you are.", ref: "57:4", surahName: "Al-Hadid", surahArabic: "الحديد" },
  { arabic: "فَاذْكُرُونِي أَذْكُرْكُمْ", translation: "So remember Me; I will remember you.", ref: "2:152", surahName: "Al-Baqarah", surahArabic: "البقرة" },
  { arabic: "وَمَن يَتَّقِ اللَّهَ يَجْعَل لَّهُ مَخْرَجًا", translation: "And whoever fears Allah — He will make for him a way out.", ref: "65:2", surahName: "At-Talaq", surahArabic: "الطلاق" },
  { arabic: "لَا يُكَلِّفُ اللَّهُ نَفْسًا إِلَّا وُسْعَهَا", translation: "Allah does not burden a soul beyond that it can bear.", ref: "2:286", surahName: "Al-Baqarah", surahArabic: "البقرة" },
  { arabic: "وَإِذَا سَأَلَكَ عِبَادِي عَنِّي فَإِنِّي قَرِيبٌ", translation: "And when My servants ask you about Me — indeed I am near.", ref: "2:186", surahName: "Al-Baqarah", surahArabic: "البقرة" },
  { arabic: "إِنَّ اللَّهَ لَا يُضِيعُ أَجْرَ الْمُحْسِنِينَ", translation: "Indeed, Allah does not allow to be lost the reward of those who do good.", ref: "9:120", surahName: "At-Tawbah", surahArabic: "التوبة" },
  { arabic: "وَلَا تَهِنُوا وَلَا تَحْزَنُوا وَأَنتُمُ الْأَعْلَوْنَ", translation: "So do not weaken and do not grieve, for you are superior.", ref: "3:139", surahName: "Ali 'Imran", surahArabic: "آل عمران" },
  { arabic: "وَعَسَىٰ أَن تَكْرَهُوا شَيْئًا وَهُوَ خَيْرٌ لَّكُمْ", translation: "Perhaps you dislike a thing and it is good for you.", ref: "2:216", surahName: "Al-Baqarah", surahArabic: "البقرة" },
  { arabic: "إِنَّ اللَّهَ يُحِبُّ الْمُتَوَكِّلِينَ", translation: "Indeed, Allah loves those who rely upon Him.", ref: "3:159", surahName: "Ali 'Imran", surahArabic: "آل عمران" },
  { arabic: "رَبَّنَا لَا تُزِغْ قُلُوبَنَا بَعْدَ إِذْ هَدَيْتَنَا", translation: "Our Lord, let not our hearts deviate after You have guided us.", ref: "3:8", surahName: "Ali 'Imran", surahArabic: "آل عمران" },
  { arabic: "وَلَئِن شَكَرْتُمْ لَأَزِيدَنَّكُمْ", translation: "If you are grateful, I will surely increase you.", ref: "14:7", surahName: "Ibrahim", surahArabic: "إبراهيم" },
  { arabic: "مَن عَمِلَ صَالِحًا مِّن ذَكَرٍ أَوْ أُنثَىٰ وَهُوَ مُؤْمِنٌ فَلَنُحْيِيَنَّهُ حَيَاةً طَيِّبَةً", translation: "Whoever does righteousness, male or female, while being a believer — We will surely cause them to live a good life.", ref: "16:97", surahName: "An-Nahl", surahArabic: "النحل" },
  { arabic: "وَقُل رَّبِّ زِدْنِي عِلْمًا", translation: "And say, 'My Lord, increase me in knowledge.'", ref: "20:114", surahName: "Ta-Ha", surahArabic: "طه" },
  { arabic: "إِنَّ رَحْمَتَ اللَّهِ قَرِيبٌ مِّنَ الْمُحْسِنِينَ", translation: "Indeed, the mercy of Allah is near to those who do good.", ref: "7:56", surahName: "Al-A'raf", surahArabic: "الأعراف" },
  { arabic: "وَمَا تَوْفِيقِي إِلَّا بِاللَّهِ", translation: "And my success is not but through Allah.", ref: "11:88", surahName: "Hud", surahArabic: "هود" },
  { arabic: "كُلُّ نَفْسٍ ذَائِقَةُ الْمَوْتِ", translation: "Every soul will taste death.", ref: "3:185", surahName: "Ali 'Imran", surahArabic: "آل عمران" },
  { arabic: "وَمَا خَلَقْتُ الْجِنَّ وَالْإِنسَ إِلَّا لِيَعْبُدُونِ", translation: "And I did not create the jinn and mankind except to worship Me.", ref: "51:56", surahName: "Adh-Dhariyat", surahArabic: "الذاريات" },
];
const RIGHTNOW_REFLECTIONS = [
  "Reflect on this verse.",
  "How can you apply this in your day?",
  "Take a moment. Read it again slowly.",
  "SubhanAllah — every verse has wisdom for us.",
  "Sit with this. Let it settle in your heart.",
  "What lesson is hidden in these words for you today?",
];
const RIGHTNOW_DHIKR = [
  "Say SubhanAllah 10 times right now",
  "Say Alhamdulillah 10 times with gratitude",
  "Send 10 salawat upon the Prophet ﷺ",
  "Say Astaghfirullah 10 times sincerely",
  "Say La ilaha illallah 10 times with conviction",
  "Say SubhanAllahi wa bihamdihi 10 times — a tree is planted in Jannah for each",
];
// Juz (Para) boundaries — surah number where each Juz starts
const JUZ_START = [
  { juz: 1,  surah: 1 },  { juz: 2,  surah: 2  }, { juz: 3,  surah: 2 },
  { juz: 4,  surah: 3 },  { juz: 5,  surah: 4  }, { juz: 6,  surah: 4 },
  { juz: 7,  surah: 5 },  { juz: 8,  surah: 6  }, { juz: 9,  surah: 7 },
  { juz: 10, surah: 8 },  { juz: 11, surah: 9  }, { juz: 12, surah: 11 },
  { juz: 13, surah: 12 }, { juz: 14, surah: 15 }, { juz: 15, surah: 17 },
  { juz: 16, surah: 18 }, { juz: 17, surah: 21 }, { juz: 18, surah: 23 },
  { juz: 19, surah: 25 }, { juz: 20, surah: 27 }, { juz: 21, surah: 29 },
  { juz: 22, surah: 33 }, { juz: 23, surah: 36 }, { juz: 24, surah: 39 },
  { juz: 25, surah: 41 }, { juz: 26, surah: 46 }, { juz: 27, surah: 51 },
  { juz: 28, surah: 58 }, { juz: 29, surah: 67 }, { juz: 30, surah: 78 },
];

// MORNING_AZKAR, EVENING_AZKAR moved to src/components/Azkar.js

// ASMA_UL_HUSNA moved to src/components/NamesOfAllah.js

// Wrap navigation state changes in View Transitions API for smooth cross-fade
// Skipped in native (Capacitor/WKWebView) — can stall visually on tab tap
let _vtActive = false;
let _vtResetTimer = null;
const navTransition = (fn) => {
  if (document.startViewTransition && !_vtActive && !isNative()) {
    _vtActive = true;
    try {
      const t = document.startViewTransition(() => flushSync(fn));
      t.finished.finally(() => { _vtActive = false; if (_vtResetTimer) { clearTimeout(_vtResetTimer); _vtResetTimer = null; } });
    } catch {
      // startViewTransition or flushSync threw — still navigate so we never get stuck
      _vtActive = false;
      try { fn(); } catch {}
    }
    // Safety net — if the transition promise never settles (known Safari quirk)
    // force-release the lock after 1s so subsequent taps keep working.
    if (_vtResetTimer) clearTimeout(_vtResetTimer);
    _vtResetTimer = setTimeout(() => { _vtActive = false; _vtResetTimer = null; }, 1000);
  } else {
    fn();
  }
};

// ─── TEACHING MODE DATA ─────────────────────────────────────────

const TEACHING_TILES = [
  { id:"salahguide",    icon:"🕌", label:"Salah Guide",       desc:"How to pray, step by step",     color:"#00c9a7" },
  { id:"surahs",        icon:"📿", label:"Short Surahs",      desc:"Learn & memorise Surahs",        color:"#ffb300" },
  { id:"duas",          icon:"📖", label:"Basic Duas",         desc:"Essential daily supplications",  color:"#a78bfa" },
  { id:"tajweed",       icon:"📚", label:"Tajweed",            desc:"Quran recitation rules",         color:"#60a5fa" },
  { id:"learnarabic",   icon:"ا",  label:"Learn Arabic",       desc:"Arabic alphabet basics",         color:"#fb923c" },
  { id:"fivepillars",   icon:"🕋", label:"Five Pillars",       desc:"Foundations of Islam",           color:"#c9a75e" },
  { id:"prophets",      icon:"🌟", label:"Prophets",           desc:"Stories of the Anbiyaa",         color:"#818cf8" },
  { id:"wudu",          icon:"💧", label:"Wudu",               desc:"Purification step by step",      color:"#38bdf8" },
  { id:"adab",          icon:"🤲", label:"Islamic Manners",    desc:"Adab for everyday life",         color:"#f472b6" },
  { id:"names",         icon:"✨", label:"99 Names",           desc:"Asma ul-Husna",                  color:"#fbbf24", hidden:true },
  { id:"islamichistory",icon:"📜", label:"Islamic History",    desc:"",                               color:"#94a3b8", hidden:true },
  { id:"seerah",        icon:"🌙", label:"Seerah",             desc:"Life of the Prophet ﷺ",           color:"#2dd4bf", hidden:true },
];

const TEACHING_SALAH_RAKAATS = [
  { name:"Fajr",    arabic:"الفجر",  rows:[
    { label:"Sunnah (before)", r:2, color:"var(--accent)" },
    { label:"Fard",            r:2, color:"var(--gold)" },
  ]},
  { name:"Dhuhr",   arabic:"الظهر",  rows:[
    { label:"Sunnah (before)", r:4, color:"var(--accent)" },
    { label:"Fard",            r:4, color:"var(--gold)" },
    { label:"Sunnah (after)",  r:2, color:"var(--accent)" },
    { label:"Nafl",            r:2, color:"var(--muted)" },
  ]},
  { name:"Asr",     arabic:"العصر",  rows:[
    { label:"Sunnah (before)", r:4, color:"var(--accent)" },
    { label:"Fard",            r:4, color:"var(--gold)" },
  ]},
  { name:"Maghrib", arabic:"المغرب", rows:[
    { label:"Fard",            r:3, color:"var(--gold)" },
    { label:"Sunnah (after)",  r:2, color:"var(--accent)" },
    { label:"Nafl",            r:2, color:"var(--muted)" },
  ]},
  { name:"Isha",    arabic:"العشاء", rows:[
    { label:"Sunnah (before)", r:4, color:"var(--accent)" },
    { label:"Fard",            r:4, color:"var(--gold)" },
    { label:"Sunnah (after)",  r:2, color:"var(--accent)" },
    { label:"Nafl",            r:2, color:"var(--muted)" },
    { label:"Witr (Wajib)",    r:3, color:"#ff9800" },
  ]},
];

const TEACHING_SALAH_STEPS = [
  { n:1,  pose:"standing", title:"Niyyah",                      arabic:"النية",                                trans:"",                                                 transl:"",                                                             note:"Intention in the heart — no words needed" },
  { n:2,  pose:"takbir",   title:"Takbiratul Ihram",            arabic:"اللَّهُ أَكْبَرُ",                     trans:"Allahu Akbar",                                     transl:"Allah is the Greatest",                                         note:"Raise hands to earlobes, cross arms on chest" },
  { n:3,  pose:"qiyam",    title:"Qiyam — Recite Al-Fatiha",   arabic:"بِسْمِ اللَّهِ الرَّحْمَنِ الرَّحِيمِ",  trans:"Bismillahir Rahmanir Raheem...",                   transl:"Recite Surah Al-Fatiha, then a short surah",                   note:"Stand upright, eyes on place of sajdah" },
  { n:4,  pose:"ruku",     title:"Ruku",                        arabic:"سُبْحَانَ رَبِّيَ الْعَظِيمِ",          trans:"SubhanAllah Rabbiyal Adheem",                      transl:"Glory to my Lord, the Magnificent",                             note:"Bow with back flat, hands on knees — recite 3×" },
  { n:5,  pose:"standing", title:"I'tidal — Rising from Ruku",  arabic:"سَمِعَ اللهُ لِمَنْ حَمِدَهُ",          trans:"Sami Allahu liman Hamidah / Rabbana lakal Hamd",   transl:"Allah hears those who praise Him — Our Lord, praise be to You", note:"Rise fully upright" },
  { n:6,  pose:"sujood",   title:"First Sajdah",                arabic:"سُبْحَانَ رَبِّيَ الْأَعْلَى",          trans:"SubhanAllah Rabbiyal A'la",                        transl:"Glory to my Lord, the Most High",                               note:"Forehead, nose, palms, knees & toes on ground — recite 3×" },
  { n:7,  pose:"sitting",  title:"Juloos — Sitting",            arabic:"رَبِّ اغْفِرْ لِي",                    trans:"Rabbigh fir li",                                   transl:"My Lord, forgive me",                                          note:"Sit briefly between the two prostrations" },
  { n:8,  pose:"sujood",   title:"Second Sajdah",               arabic:"سُبْحَانَ رَبِّيَ الْأَعْلَى",          trans:"SubhanAllah Rabbiyal A'la",                        transl:"Glory to my Lord, the Most High",                               note:"Same as first sajdah — recite 3×" },
  { n:9,  pose:"sitting",  title:"Tashahhud",                   arabic:"اَلتَّحِيَّاتُ لِلّٰهِ وَالصَّلَوَاتُ",   trans:"At-tahiyyatu lillahi was-salawatu wat-tayyibat…",  transl:"All greetings, prayers and good things are for Allah…",         note:"Recited sitting in 2nd rakaat and last rakaat" },
  { n:10, pose:"sitting",  title:"Tasleem",                     arabic:"اَلسَّلاَمُ عَلَيْكُمْ وَرَحْمَةُ اللهِ",  trans:"Assalamu Alaykum wa Rahmatullah",                  transl:"Peace and mercy of Allah be upon you",                          note:"Turn head right then left — prayer complete" },
];


const TEACHING_WUDU_STEPS = [
  { icon:"🤲", step:"Make niyyah (intention)",                   note:"In your heart, intend to purify for prayer" },
  { icon:"💬", step:"Say Bismillah",                             note:"Bismillahir Rahmanir Raheem" },
  { icon:"🖐️", step:"Wash both hands 3×",                        note:"Up to and including the wrists" },
  { icon:"💧", step:"Rinse mouth 3×",                             note:"Swirl water in mouth and spit out" },
  { icon:"👃", step:"Rinse nose 3×",                              note:"Sniff water in, then blow out" },
  { icon:"😊", step:"Wash face 3×",                               note:"Hairline to chin, ear to ear" },
  { icon:"💪", step:"Wash right arm to elbow 3×, then left 3×",  note:"Include the elbows" },
  { icon:"👆", step:"Wipe head once",                             note:"Wet hands, wipe front to back once" },
  { icon:"👂", step:"Wipe both ears once",                        note:"Index fingers inside, thumbs outside" },
  { icon:"🦶", step:"Wash right foot 3×, then left foot 3×",     note:"Up to and including the ankles" },
  { icon:"🤲", step:"Recite the Wudu Dua",                       note:"أَشْهَدُ أَنْ لَا إِلَهَ إِلَّا اللهُ… — Ashhadu alla ilaha illallahu wahdahu la sharika lah, wa ashhadu anna Muhammadan abduhu wa rasuluh" },
];

const TEACHING_SURAHS = [
  { id:"fatiha",  name:"Al-Fatiha",  arabic:"الفاتحة",  number:1,   ayahs:7,  meaning:"The Opening",
    text:"بِسْمِ اللَّهِ الرَّحْمَنِ الرَّحِيمِ ﴿١﴾ الْحَمْدُ لِلَّهِ رَبِّ الْعَالَمِينَ ﴿٢﴾ الرَّحْمَنِ الرَّحِيمِ ﴿٣﴾ مَالِكِ يَوْمِ الدِّينِ ﴿٤﴾ إِيَّاكَ نَعْبُدُ وَإِيَّاكَ نَسْتَعِينُ ﴿٥﴾ اهْدِنَا الصِّرَاطَ الْمُسْتَقِيمَ ﴿٦﴾ صِرَاطَ الَّذِينَ أَنْعَمْتَ عَلَيْهِمْ غَيْرِ الْمَغْضُوبِ عَلَيْهِمْ وَلَا الضَّالِّينَ ﴿٧﴾",
    translation:"In the name of Allah, the Most Gracious, the Most Merciful. All praise is for Allah, Lord of all worlds. The Most Gracious, the Most Merciful. Master of the Day of Judgment. You alone we worship, and You alone we ask for help. Guide us to the straight path — the path of those You have blessed, not of those You are angry with or those who have gone astray." },
  { id:"ikhlas",  name:"Al-Ikhlas",  arabic:"الإخلاص",  number:112, ayahs:4,  meaning:"Sincerity",
    text:"قُلْ هُوَ اللَّهُ أَحَدٌ ﴿١﴾ اللَّهُ الصَّمَدُ ﴿٢﴾ لَمْ يَلِدْ وَلَمْ يُولَدْ ﴿٣﴾ وَلَمْ يَكُن لَّهُ كُفُوًا أَحَدٌ ﴿٤﴾",
    translation:"Say: He is Allah, the One. Allah, the Eternal. He did not give birth and was not born. And no one is equal to Him." },
  { id:"falaq",   name:"Al-Falaq",   arabic:"الفلق",    number:113, ayahs:5,  meaning:"The Daybreak",
    text:"قُلْ أَعُوذُ بِرَبِّ الْفَلَقِ ﴿١﴾ مِن شَرِّ مَا خَلَقَ ﴿٢﴾ وَمِن شَرِّ غَاسِقٍ إِذَا وَقَبَ ﴿٣﴾ وَمِن شَرِّ النَّفَّاثَاتِ فِي الْعُقَدِ ﴿٤﴾ وَمِن شَرِّ حَاسِدٍ إِذَا حَسَدَ ﴿٥﴾",
    translation:"Say: I seek refuge in the Lord of the daybreak, from the evil of what He has created, from the evil of darkness when it settles, from the evil of those who blow on knots, and from the evil of the envier when he envies." },
  { id:"nas",     name:"An-Nas",     arabic:"الناس",    number:114, ayahs:6,  meaning:"Mankind",
    text:"قُلْ أَعُوذُ بِرَبِّ النَّاسِ ﴿١﴾ مَلِكِ النَّاسِ ﴿٢﴾ إِلَهِ النَّاسِ ﴿٣﴾ مِن شَرِّ الْوَسْوَاسِ الْخَنَّاسِ ﴿٤﴾ الَّذِي يُوَسْوِسُ فِي صُدُورِ النَّاسِ ﴿٥﴾ مِنَ الْجِنَّةِ وَالنَّاسِ ﴿٦﴾",
    translation:"Say: I seek refuge in the Lord of mankind, the King of mankind, the God of mankind, from the evil of the whispering tempter who whispers in the chests of people — from among jinn and people." },
  { id:"asr",     name:"Al-Asr",     arabic:"العصر",    number:103, ayahs:3,  meaning:"Time",
    text:"وَالْعَصْرِ ﴿١﴾ إِنَّ الْإِنسَانَ لَفِي خُسْرٍ ﴿٢﴾ إِلَّا الَّذِينَ آمَنُوا وَعَمِلُوا الصَّالِحَاتِ وَتَوَاصَوْا بِالْحَقِّ وَتَوَاصَوْا بِالصَّبْرِ ﴿٣﴾",
    translation:"By time, indeed people are in loss — except those who believe and do good deeds and encourage each other to truth and encourage each other to patience." },
  { id:"kawthar", name:"Al-Kawthar", arabic:"الكوثر",   number:108, ayahs:3,  meaning:"Abundance",
    text:"إِنَّا أَعْطَيْنَاكَ الْكَوْثَرَ ﴿١﴾ فَصَلِّ لِرَبِّكَ وَانْحَرْ ﴿٢﴾ إِنَّ شَانِئَكَ هُوَ الْأَبْتَرُ ﴿٣﴾",
    translation:"Indeed, We have given you Al-Kawthar. So pray to your Lord and offer sacrifice. Indeed, your enemy is the one cut off." },
  { id:"nasr",    name:"An-Nasr",    arabic:"النصر",    number:110, ayahs:3,  meaning:"The Help",
    text:"إِذَا جَاءَ نَصْرُ اللَّهِ وَالْفَتْحُ ﴿١﴾ وَرَأَيْتَ النَّاسَ يَدْخُلُونَ فِي دِينِ اللَّهِ أَفْوَاجًا ﴿٢﴾ فَسَبِّحْ بِحَمْدِ رَبِّكَ وَاسْتَغْفِرْهُ إِنَّهُ كَانَ تَوَّابًا ﴿٣﴾",
    translation:"When Allah's help comes and the victory, and you see people entering Allah's religion in crowds, then glorify your Lord and ask His forgiveness. He is ever Accepting of repentance." },
  { id:"fil",     name:"Al-Fil",     arabic:"الفيل",    number:105, ayahs:5,  meaning:"The Elephant",
    text:"أَلَمْ تَرَ كَيْفَ فَعَلَ رَبُّكَ بِأَصْحَابِ الْفِيلِ ﴿١﴾ أَلَمْ يَجْعَلْ كَيْدَهُمْ فِي تَضْلِيلٍ ﴿٢﴾ وَأَرْسَلَ عَلَيْهِمْ طَيْرًا أَبَابِيلَ ﴿٣﴾ تَرْمِيهِم بِحِجَارَةٍ مِّن سِجِّيلٍ ﴿٤﴾ فَجَعَلَهُمْ كَعَصْفٍ مَّأْكُولٍ ﴿٥﴾",
    translation:"Have you not seen how your Lord dealt with the companions of the Elephant? Did He not make their plan go wrong? He sent against them flocks of birds, throwing stones of baked clay at them, making them like chewed-up leaves." },
  { id:"quraish", name:"Al-Quraish", arabic:"قريش",    number:106, ayahs:4,  meaning:"The Quraysh",
    text:"لِإِيلَافِ قُرَيْشٍ ﴿١﴾ إِيلَافِهِمْ رِحْلَةَ الشِّتَاءِ وَالصَّيْفِ ﴿٢﴾ فَلْيَعْبُدُوا رَبَّ هَذَا الْبَيْتِ ﴿٣﴾ الَّذِي أَطْعَمَهُم مِّن جُوعٍ وَآمَنَهُم مِّنْ خَوْفٍ ﴿٤﴾",
    translation:"For the safety of the Quraysh — their safety in their winter and summer journeys — let them worship the Lord of this House, who fed them from hunger and gave them safety from fear." },
  { id:"maun",    name:"Al-Ma'un",   arabic:"الماعون",  number:107, ayahs:7,  meaning:"Small Kindnesses",
    text:"أَرَأَيْتَ الَّذِي يُكَذِّبُ بِالدِّينِ ﴿١﴾ فَذَلِكَ الَّذِي يَدُعُّ الْيَتِيمَ ﴿٢﴾ وَلَا يَحُضُّ عَلَى طَعَامِ الْمِسْكِينِ ﴿٣﴾ فَوَيْلٌ لِّلْمُصَلِّينَ ﴿٤﴾ الَّذِينَ هُمْ عَن صَلَاتِهِمْ سَاهُونَ ﴿٥﴾ الَّذِينَ هُمْ يُرَاءُونَ ﴿٦﴾ وَيَمْنَعُونَ الْمَاعُونَ ﴿٧﴾",
    translation:"Have you seen the one who denies the Day of Judgment? That is the one who pushes away the orphan and does not encourage feeding the poor. So woe to those who pray but are heedless of their prayer — those who show off and refuse to give even small help." },
];

const TEACHING_PROPHETS = [
  { name:"Adam ﷺ",    arabic:"آدَم",      icon:"🌿",
    story:"Allah created Adam from clay and breathed life into him — making him the very first human being on Earth. Allah taught Adam the names of all things and honoured him greatly. Adam ﷺ is the father of all people, and from him all of humanity began.",
    lesson:"Allah created us and gave us knowledge — always be grateful to Him." },
  { name:"Nuh ﷺ",     arabic:"نُوح",      icon:"⛵",
    story:"Nuh ﷺ called his people to worship only Allah for 950 long years, but most of them refused to listen. Allah told Nuh to build a great ark and he put two of every animal on board. Allah then sent a great flood, but kept Nuh and all those who believed completely safe.",
    lesson:"Keep trying to do good, even when things are hard. Allah always protects those who believe." },
  { name:"Ibrahim ﷺ", arabic:"إِبْرَاهِيم", icon:"🔥",
    story:"Ibrahim ﷺ was brave and told his people to stop worshipping idols and worship only Allah. When they threw him into a huge fire, Allah made the fire cool and peaceful for him. Ibrahim later built the Ka'bah in Makkah with his son Ismail ﷺ — the house Muslims face when they pray.",
    lesson:"Be brave and always stand up for the truth. Allah will protect you." },
  { name:"Yusuf ﷺ",   arabic:"يُوسُف",    icon:"⭐",
    story:"Yusuf ﷺ was thrown into a deep well by his jealous brothers, but Allah kept him safe. He was taken to Egypt and put in prison, but he never stopped trusting Allah. Years later, Yusuf became a great leader of Egypt and forgave his brothers with a kind heart.",
    lesson:"Never lose hope — Allah can turn the hardest time into a beautiful blessing." },
  { name:"Musa ﷺ",    arabic:"مُوسَى",    icon:"🌊",
    story:"Musa ﷺ was chosen by Allah to speak to Pharaoh, the cruel king of Egypt who made the Children of Israel suffer. Allah gave Musa a staff that turned into a snake and helped him part the sea so his people could escape. Musa ﷺ was given the Torah on Mount Sinai and spoke directly to Allah.",
    lesson:"Trust in Allah — He is always with you, even in the most difficult moments." },
  { name:"Isa ﷺ",     arabic:"عِيسَى",    icon:"🕊️",
    story:"Isa ﷺ was born miraculously to Maryam — without a father — as a special sign from Allah. He spoke as a baby in the cradle and Allah gave him the ability to heal the sick. Isa ﷺ is a beloved messenger of Allah and is deeply honoured in Islam.",
    lesson:"Allah can do anything — nothing in the whole universe is impossible for Him." },
  { name:"Muhammad ﷺ",arabic:"مُحَمَّد",  icon:"🌙",
    story:"Muhammad ﷺ is the last and final prophet of Allah, born in Makkah over 1,400 years ago. Even before he became a prophet, people called him Al-Ameen — the trustworthy — because he was always honest and kind. Allah revealed the Quran to him, and his beautiful way of life guides all Muslims today.",
    lesson:"Be honest and kind in everything you do, just like our beloved Prophet ﷺ." },
];

const TEACHING_ADAB = [
  { icon:"🍽️", title:"Before Eating",     arabic:"بِسْمِ اللَّهِ",                                              phrase:"Bismillah",                            desc:"Always say 'Bismillah' before you eat or drink. It means 'In the name of Allah'. If you forget, say Bismillahi awwalahu wa akhirah when you remember." },
  { icon:"😊", title:"After Eating",      arabic:"الْحَمْدُ لِلَّهِ",                                             phrase:"Alhamdulillah",                        desc:"Say 'Alhamdulillah' — All praise is for Allah — after every meal. Allah gave us this food, so we thank Him for it." },
  { icon:"👋", title:"Greeting Others",   arabic:"اَلسَّلَامُ عَلَيْكُمْ",                                          phrase:"Assalamu Alaykum",                     desc:"Greet every Muslim with 'Assalamu Alaykum' — Peace be upon you. Reply with 'Wa Alaykum Assalam'. The one who greets first gets more reward!" },
  { icon:"✋", title:"Right Hand",        arabic:"",                                                              phrase:"Use your right hand",                  desc:"Always eat, drink, give and receive things with your right hand. The Prophet ﷺ loved to start with the right side in everything." },
  { icon:"🛏️", title:"Before Sleeping",   arabic:"بِاسْمِكَ اللَّهُمَّ أَمُوتُ وَأَحْيَا",                          phrase:"Bismika Allahumma amutu wa ahya",      desc:"Say this dua before you sleep: 'In Your name, O Allah, I die and I live'. Lie on your right side like the Prophet ﷺ." },
  { icon:"🌅", title:"When Waking Up",    arabic:"الْحَمْدُ لِلَّهِ الَّذِي أَحْيَانَا بَعْدَ مَا أَمَاتَنَا",          phrase:"Alhamdulillahil-ladhi ahyana ba'da ma amatana", desc:"When you open your eyes, thank Allah. It means: 'All praise to Allah who gave us life after death (sleep), and to Him is the return'." },
  { icon:"🤧", title:"When Sneezing",     arabic:"الْحَمْدُ لِلَّهِ",                                             phrase:"Say Alhamdulillah — reply: Yarhamukallah", desc:"When you sneeze, cover your mouth and say 'Alhamdulillah'. When someone else sneezes and says it, reply 'Yarhamukallah' — May Allah have mercy on you." },
  { icon:"👨‍👩‍👧", title:"Kind to Parents", arabic:"",                                                              phrase:"Be gentle and helpful",                desc:"Allah says to be very kind to your parents. Never be rude or say 'ugh' to them. Help around the house, speak softly, and make dua for them every day." },
];

const TEACHING_FIVE_PILLARS = [
  { icon:"☝️", name:"Shahada",  arabic:"الشَّهَادَة", color:"#c8a02a", desc:"The Declaration of Faith. 'There is no god but Allah, and Muhammad ﷺ is His messenger.' Saying this sincerely makes one a Muslim." },
  { icon:"🕌", name:"Salah",    arabic:"الصَّلَاة",   color:"#2a9d8f", desc:"The 5 daily prayers — Fajr, Dhuhr, Asr, Maghrib, and Isha. A direct conversation with Allah, five times every day." },
  { icon:"💰", name:"Zakat",    arabic:"الزَّكَاة",   color:"#c8a02a", desc:"Giving 2.5% of accumulated wealth (above nisab) to those in need. It purifies wealth and strengthens the community." },
  { icon:"🌙", name:"Sawm",     arabic:"الصَّوْم",    color:"#6c63ff", desc:"Fasting during Ramadan — no food, drink, or intimate relations from Fajr to Maghrib. A month of spiritual renewal." },
  { icon:"🕋", name:"Hajj",     arabic:"الْحَجّ",     color:"#2a7d4f", desc:"Pilgrimage to Mecca at least once in a lifetime for those able. Over 2 million Muslims gather each year — the largest annual gathering on Earth." },
];

const TEACHING_HISTORY = [
  { year:"570 CE",     title:"Birth of the Prophet ﷺ",   arabic:"مولد النبي ﷺ",          desc:"Muhammad ﷺ is born in Mecca. His father passed before his birth; his mother Aminah passed when he was 6." },
  { year:"610 CE",     title:"First Revelation",           arabic:"الوحي الأول",           desc:"Angel Jibreel appears in Cave Hira. 'Iqra' — Read! The first verses of Surah Al-Alaq are revealed." },
  { year:"613 CE",     title:"Public Preaching Begins",    arabic:"الدعوة العلنية",        desc:"After 3 years of private da'wah, the Prophet ﷺ preaches openly. Opposition from Quraysh intensifies." },
  { year:"619 CE",     title:"Year of Sorrow",             arabic:"عام الحزن",             desc:"Khadijah RA and Abu Talib both pass away in the same year. Two irreplaceable losses." },
  { year:"621 CE",     title:"Isra wal Mi'raj",            arabic:"الإسراء والمعراج",      desc:"The Night Journey — from Mecca to Jerusalem, then through the seven heavens. The 5 prayers are prescribed." },
  { year:"622 CE",     title:"Hijra to Madinah",           arabic:"الهجرة",               desc:"Migration to Madinah. This marks year 1 of the Islamic calendar (1 AH)." },
  { year:"624 CE",     title:"Battle of Badr",             arabic:"غزوة بدر",              desc:"313 Muslims defeat over 1,000 Quraysh — a decisive turning point in Islamic history." },
  { year:"625 CE",     title:"Battle of Uhud",             arabic:"غزوة أحد",              desc:"A test of faith. Muslims face a setback when archers leave their posts. Many companions are martyred." },
  { year:"630 CE",     title:"Opening of Mecca",           arabic:"فتح مكة",               desc:"10,000 companions. The Prophet ﷺ forgives his enemies and removes all idols from the Ka'bah." },
  { year:"632 CE",     title:"Farewell Sermon & Passing",  arabic:"حجة الوداع",            desc:"'I leave you two things — the Quran and my Sunnah.' The Prophet ﷺ passes away completing his mission." },
  { year:"634–661 CE", title:"Khulafa Rashidun",           arabic:"الخلفاء الراشدون",      desc:"The four Rightly Guided Caliphs. The Quran is compiled into a single book." },
  { year:"661–750 CE", title:"Umayyad Caliphate",          arabic:"الخلافة الأموية",       desc:"Islam spreads across Spain, North Africa, Persia, and Central Asia — three continents in under a century." },
];

const TEACHING_SEERAH = [
  { year:"570 CE",  title:"Birth in Mecca",             arabic:"الميلاد",              desc:"Born into the noble Banu Hashim clan. Orphaned early — father before birth, mother at age 6." },
  { year:"583 CE",  title:"Journey to Syria",           arabic:"السفر إلى الشام",       desc:"Travels with uncle Abu Talib for trade. The monk Bahira recognizes signs of prophethood." },
  { year:"595 CE",  title:"Marriage to Khadijah RA",    arabic:"الزواج من خديجة",       desc:"Marries the noble businesswoman at age 25. She is his greatest supporter for 25 years." },
  { year:"610 CE",  title:"First Revelation",           arabic:"نزول الوحي",            desc:"At 40, in Cave Hira, Angel Jibreel appears. 'Iqra' — the first words of the Quran are revealed." },
  { year:"615 CE",  title:"Migration to Abyssinia",     arabic:"الهجرة إلى الحبشة",    desc:"First Muslims flee persecution to Christian Abyssinia. King Negus grants them asylum." },
  { year:"619 CE",  title:"Year of Sorrow",             arabic:"عام الحزن",             desc:"Khadijah RA and Abu Talib both pass away. Two irreplaceable losses in one year." },
  { year:"621 CE",  title:"Isra wal Mi'raj",            arabic:"الإسراء والمعراج",      desc:"Night Journey from Al-Haram to Al-Aqsa to the seven heavens. The 5 prayers are prescribed." },
  { year:"622 CE",  title:"Hijra to Madinah",           arabic:"الهجرة إلى المدينة",    desc:"After years of persecution, migrates to Madinah. The Islamic calendar begins." },
  { year:"624 CE",  title:"Battle of Badr",             arabic:"غزوة بدر",              desc:"313 Muslims defeat over 1,000 Quraysh. Angels are sent to assist." },
  { year:"628 CE",  title:"Treaty of Hudaybiyyah",      arabic:"صلح الحديبية",          desc:"A peace treaty that opened the door for Islam to spread freely across Arabia." },
  { year:"630 CE",  title:"Opening of Mecca",           arabic:"فتح مكة",               desc:"Enters Mecca with 10,000. Forgives all enemies. Every idol removed from the Ka'bah." },
  { year:"632 CE",  title:"Farewell & Return to Allah", arabic:"الوفاة",               desc:"Farewell Sermon at Arafah. On 12 Rabi al-Awwal 11 AH, the Prophet ﷺ returns to his Lord at age 63." },
];

const TEACHING_ARABIC_LETTERS = [
  { letter:"ا", name:"Alif",  sound:"aa",   iso:"ا",  ini:"ا",   mid:"ـا",   fin:"ـا"   },
  { letter:"ب", name:"Ba",    sound:"b",    iso:"ب",  ini:"بـ",  mid:"ـبـ",  fin:"ـب"   },
  { letter:"ت", name:"Ta",    sound:"t",    iso:"ت",  ini:"تـ",  mid:"ـتـ",  fin:"ـت"   },
  { letter:"ث", name:"Tha",   sound:"th",   iso:"ث",  ini:"ثـ",  mid:"ـثـ",  fin:"ـث"   },
  { letter:"ج", name:"Jeem",  sound:"j",    iso:"ج",  ini:"جـ",  mid:"ـجـ",  fin:"ـج"   },
  { letter:"ح", name:"Ha",    sound:"ħ",    iso:"ح",  ini:"حـ",  mid:"ـحـ",  fin:"ـح"   },
  { letter:"خ", name:"Kha",   sound:"kh",   iso:"خ",  ini:"خـ",  mid:"ـخـ",  fin:"ـخ"   },
  { letter:"د", name:"Dal",   sound:"d",    iso:"د",  ini:"د",   mid:"ـد",   fin:"ـد"   },
  { letter:"ذ", name:"Thal",  sound:"dh",   iso:"ذ",  ini:"ذ",   mid:"ـذ",   fin:"ـذ"   },
  { letter:"ر", name:"Ra",    sound:"r",    iso:"ر",  ini:"ر",   mid:"ـر",   fin:"ـر"   },
  { letter:"ز", name:"Zay",   sound:"z",    iso:"ز",  ini:"ز",   mid:"ـز",   fin:"ـز"   },
  { letter:"س", name:"Seen",  sound:"s",    iso:"س",  ini:"سـ",  mid:"ـسـ",  fin:"ـس"   },
  { letter:"ش", name:"Sheen", sound:"sh",   iso:"ش",  ini:"شـ",  mid:"ـشـ",  fin:"ـش"   },
  { letter:"ص", name:"Sad",   sound:"S",    iso:"ص",  ini:"صـ",  mid:"ـصـ",  fin:"ـص"   },
  { letter:"ض", name:"Dad",   sound:"D",    iso:"ض",  ini:"ضـ",  mid:"ـضـ",  fin:"ـض"   },
  { letter:"ط", name:"Ta",    sound:"Ṭ",    iso:"ط",  ini:"طـ",  mid:"ـطـ",  fin:"ـط"   },
  { letter:"ظ", name:"Dha",   sound:"Ẓ",    iso:"ظ",  ini:"ظـ",  mid:"ـظـ",  fin:"ـظ"   },
  { letter:"ع", name:"Ain",   sound:"ʿ",    iso:"ع",  ini:"عـ",  mid:"ـعـ",  fin:"ـع"   },
  { letter:"غ", name:"Ghain", sound:"gh",   iso:"غ",  ini:"غـ",  mid:"ـغـ",  fin:"ـغ"   },
  { letter:"ف", name:"Fa",    sound:"f",    iso:"ف",  ini:"فـ",  mid:"ـفـ",  fin:"ـف"   },
  { letter:"ق", name:"Qaf",   sound:"q",    iso:"ق",  ini:"قـ",  mid:"ـقـ",  fin:"ـق"   },
  { letter:"ك", name:"Kaf",   sound:"k",    iso:"ك",  ini:"كـ",  mid:"ـكـ",  fin:"ـك"   },
  { letter:"ل", name:"Lam",   sound:"l",    iso:"ل",  ini:"لـ",  mid:"ـلـ",  fin:"ـل"   },
  { letter:"م", name:"Meem",  sound:"m",    iso:"م",  ini:"مـ",  mid:"ـمـ",  fin:"ـم"   },
  { letter:"ن", name:"Noon",  sound:"n",    iso:"ن",  ini:"نـ",  mid:"ـنـ",  fin:"ـن"   },
  { letter:"ه", name:"Ha",    sound:"h",    iso:"ه",  ini:"هـ",  mid:"ـهـ",  fin:"ـه"   },
  { letter:"و", name:"Waw",   sound:"w/oo", iso:"و",  ini:"و",   mid:"ـو",   fin:"ـو"   },
  { letter:"ي", name:"Ya",    sound:"y/ee", iso:"ي",  ini:"يـ",  mid:"ـيـ",  fin:"ـي"   },
];

function TeachingScreen({ teachingView, setTeachingView, setTeachingMode, darkMode, showIntro, setShowIntro }) {
  const [expandedLetter, setExpandedLetter] = useState(null);
  const [selDuaCat, setSelDuaCat] = useState(null);
  const swipeRef = useRef(null);
  const exitTapCountRef = useRef(0);
  const exitTapTimerRef = useRef(null);
  const [duaPlayingId, setDuaPlayingId] = useState(null);
  const [ttsId, setTtsId] = useState(null);
  const [selSurah, setSelSurah] = useState(null);
  const [selProphet, setSelProphet] = useState(null);
  const [selAdab, setSelAdab] = useState(null);
  const [learnedSurahs, setLearnedSurahs] = useState(() => {
    try { return JSON.parse(localStorage.getItem("teachingLearnedSurahs") || "[]"); } catch { return []; }
  });

  useEffect(() => {
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    setDuaPlayingId(null);
    setTtsId(null);
    setSelSurah(null);
    setSelProphet(null);
    setSelAdab(null);
  }, [teachingView]);

  useEffect(() => {
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    setDuaPlayingId(null);
  }, [selDuaCat]);

  // Arabic voice detection — iOS/Android need the Arabic TTS pack installed
  // or audio playback will silently no-op. voiceschanged fires async on iOS.
  const [arabicVoiceAvailable, setArabicVoiceAvailable] = useState(true);
  useEffect(() => {
    if (!window.speechSynthesis) { setArabicVoiceAvailable(false); return; }
    const check = () => {
      const voices = window.speechSynthesis.getVoices() || [];
      const hasArabic = voices.some(v => (v.lang || "").toLowerCase().startsWith("ar"));
      setArabicVoiceAvailable(hasArabic);
    };
    check();
    window.speechSynthesis.addEventListener("voiceschanged", check);
    return () => window.speechSynthesis.removeEventListener("voiceschanged", check);
  }, []);

  const playDua = (id, arabic) => {
    if (!window.speechSynthesis) return;
    if (duaPlayingId === id) {
      window.speechSynthesis.cancel();
      setDuaPlayingId(null);
      return;
    }
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(arabic);
    u.lang = "ar-SA";
    u.rate = 0.85;
    u.onend = () => setDuaPlayingId(null);
    u.onerror = () => setDuaPlayingId(null);
    setDuaPlayingId(id);
    window.speechSynthesis.speak(u);
  };

  const playTts = (id, text, lang = "ar-SA") => {
    if (!window.speechSynthesis) return;
    if (ttsId === id) { window.speechSynthesis.cancel(); setTtsId(null); return; }
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = lang; u.rate = 0.85;
    u.onend = () => setTtsId(null);
    u.onerror = () => setTtsId(null);
    setTtsId(id);
    window.speechSynthesis.speak(u);
  };

  const toggleSurahLearned = (id) => {
    const next = learnedSurahs.includes(id) ? learnedSurahs.filter(s => s !== id) : [...learnedSurahs, id];
    setLearnedSurahs(next);
    localStorage.setItem("teachingLearnedSurahs", JSON.stringify(next));
  };

  const handleExitTap = () => {
    exitTapCountRef.current += 1;
    if (exitTapTimerRef.current) clearTimeout(exitTapTimerRef.current);
    if (exitTapCountRef.current >= 5) {
      exitTapCountRef.current = 0;
      setTeachingMode(false);
      localStorage.setItem("teachingMode", "false");
      setTeachingView("grid");
    } else {
      exitTapTimerRef.current = setTimeout(() => { exitTapCountRef.current = 0; }, 5000);
    }
  };

  const handleSwipeStart = (e) => { swipeRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
  const handleSwipeEnd = (e) => {
    if (!swipeRef.current) return;
    const dx = e.changedTouches[0].clientX - swipeRef.current.x;
    const dy = Math.abs(e.changedTouches[0].clientY - swipeRef.current.y);
    swipeRef.current = null;
    if (dx > 60 && dy < 100) { setTeachingView("grid"); setExpandedLetter(null); setSelDuaCat(null); }
  };

  const playLetterAudio = (letter) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(letter);
    u.lang = "ar-SA";
    u.rate = 0.8;
    window.speechSynthesis.speak(u);
  };

  const shell = (title, children) => (
    <div style={{position:"fixed",inset:0,background:"var(--bg)",zIndex:9999,display:"flex",flexDirection:"column"}} onTouchStart={handleSwipeStart} onTouchEnd={handleSwipeEnd}>
      <div style={{display:"flex",alignItems:"center",gap:"12px",padding:"16px",borderBottom:"1px solid var(--border)",flexShrink:0,paddingTop:"calc(16px + env(safe-area-inset-top))"}}>
        <button onClick={() => { setTeachingView("grid"); setExpandedLetter(null); setSelDuaCat(null); }}
          style={{background:"transparent",border:"1px solid var(--border)",borderRadius:"8px",color:"var(--muted)",fontSize:"22px",width:"38px",height:"38px",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontFamily:"inherit"}}>‹</button>
        <div style={{fontSize:"16px",fontWeight:"800",color:"var(--gold)"}}>{title}</div>
      </div>
      <div style={{flex:1,overflowY:"auto",WebkitOverflowScrolling:"touch",padding:"0 0 40px"}}>
        {children}
      </div>
    </div>
  );

  if (teachingView === "names") return (
    <div style={{position:"fixed",inset:0,background:"var(--bg)",zIndex:9999,overflowY:"auto",paddingTop:"env(safe-area-inset-top)"}} onTouchStart={handleSwipeStart} onTouchEnd={handleSwipeEnd}>
      <Lazy><NamesOfAllah onBack={() => setTeachingView("grid")} /></Lazy>
    </div>
  );

  if (teachingView === "tajweed") return (
    <div style={{position:"fixed",inset:0,background:"var(--bg)",zIndex:9999,overflowY:"auto",paddingTop:"env(safe-area-inset-top)"}} onTouchStart={handleSwipeStart} onTouchEnd={handleSwipeEnd}>
      <Lazy><TajweedHelper darkMode={darkMode} onBack={() => setTeachingView("grid")} /></Lazy>
    </div>
  );

  if (teachingView === "duas") {
    const cat = DUA_CATEGORIES.find(c => c.id === selDuaCat);
    return shell("Basic Duas", (
      <div style={{padding:"16px"}}>
        {!selDuaCat ? (
          DUA_CATEGORIES.map(c => (
            <div key={c.id} onClick={() => setSelDuaCat(c.id)}
              style={{padding:"16px",marginBottom:"10px",background:"var(--s2)",border:"1px solid var(--border)",borderRadius:"14px",cursor:"pointer",display:"flex",alignItems:"center",gap:"14px"}}>
              <span style={{fontSize:"28px"}}>{c.icon || "📖"}</span>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:"14px",fontWeight:"700",color:"var(--text)"}}>{c.name}</div>
                <div style={{fontSize:"12px",color:"var(--muted)"}}>{c.duas.length} dua{c.duas.length!==1?"s":""}</div>
              </div>
              <span style={{color:"var(--muted)",opacity:0.5,fontSize:"18px"}}>›</span>
            </div>
          ))
        ) : cat ? (
          <div>
            <button onClick={() => setSelDuaCat(null)} style={{background:"transparent",border:"1px solid var(--border)",borderRadius:"8px",color:"var(--muted)",fontSize:"13px",padding:"8px 16px",cursor:"pointer",fontFamily:"inherit",marginBottom:"16px"}}>← All categories</button>
            <div style={{fontSize:"16px",fontWeight:"800",color:"var(--text)",marginBottom:"16px"}}>{cat.name}</div>
            {cat.duas.map((dua,i) => {
              const pid = `${selDuaCat}-${i}`;
              const isPlaying = duaPlayingId === pid;
              return (
                <div key={i} style={{marginBottom:"12px",background:"var(--s2)",border:`1px solid ${isPlaying?"var(--accent)":"var(--border)"}`,borderRadius:"14px",overflow:"hidden",transition:"border-color 0.2s"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 12px 12px 16px",borderBottom:"1px solid var(--border)"}}>
                    <div style={{fontSize:"12px",fontWeight:"700",color:"var(--muted)",letterSpacing:"1px",textTransform:"uppercase"}}>{cat.name} {i+1}</div>
                    {dua.arabic && (
                      <button onClick={() => playDua(pid, dua.arabic)}
                        style={{width:"34px",height:"34px",borderRadius:"50%",border:`1px solid ${isPlaying?"var(--accent)":"var(--border)"}`,background:isPlaying?"rgba(0,201,167,0.12)":"transparent",color:isPlaying?"var(--accent)":"var(--muted)",fontSize:"14px",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all 0.2s",fontFamily:"inherit"}}>
                        {isPlaying ? "■" : "▶"}
                      </button>
                    )}
                  </div>
                  {dua.arabic && <div style={{padding:"12px 16px 8px",fontFamily:"'Scheherazade New',serif",fontSize:"22px",color:"var(--gold)",direction:"rtl",textAlign:"right",lineHeight:1.8}}>{dua.arabic}</div>}
                  {dua.translit && <div style={{padding:"0 16px 8px",fontSize:"13px",color:"var(--accent)",fontStyle:"italic"}}>{dua.translit}</div>}
                  {dua.translation && <div style={{padding:"0 16px 12px",fontSize:"13px",color:"var(--muted)",lineHeight:1.6}}>{dua.translation}</div>}
                  {dua.source && <div style={{padding:"0 16px 12px",fontSize:"11px",color:"var(--muted)",opacity:0.6,fontStyle:"italic"}}>{dua.source}</div>}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    ));
  }

  if (teachingView === "salahguide") return shell("Salah Guide", (
    <div style={{padding:"16px"}}>
      <div style={{fontSize:"15px",fontWeight:"800",color:"var(--text)",marginBottom:"12px"}}>Daily Prayer Rakaats</div>
      {TEACHING_SALAH_RAKAATS.map(p => (
        <div key={p.name} style={{background:"var(--s2)",border:"1px solid var(--border)",borderRadius:"14px",overflow:"hidden",marginBottom:"12px"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 16px",borderBottom:"1px solid var(--border)"}}>
            <span style={{fontWeight:"800",fontSize:"15px",color:"var(--text)"}}>{p.name}</span>
            <span style={{fontFamily:"'Scheherazade New',serif",fontSize:"18px",color:"var(--muted)"}}>{p.arabic}</span>
          </div>
          {p.rows.map((row,j) => (
            <div key={j} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"9px 16px",borderBottom:j<p.rows.length-1?"1px solid var(--border)":"none",paddingLeft:"24px"}}>
              <span style={{fontSize:"13px",color:row.color,fontWeight:"600"}}>{row.label}</span>
              <span style={{fontSize:"13px",fontWeight:"800",color:row.color}}>{row.r} rakaats</span>
            </div>
          ))}
        </div>
      ))}
      <div style={{fontSize:"15px",fontWeight:"800",color:"var(--text)",marginBottom:"14px"}}>How to Pray — Step by Step</div>
      {TEACHING_SALAH_STEPS.map(s => (
        <div key={s.n} style={{marginBottom:"12px",background:"var(--s2)",border:"1px solid var(--border)",borderRadius:"14px",padding:"14px 16px"}}>
          <div style={{display:"flex",alignItems:"center",gap:"10px",marginBottom:"8px"}}>
            <div style={{width:"26px",height:"26px",borderRadius:"50%",background:"var(--gold)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"12px",fontWeight:"800",color:"#07090c",flexShrink:0}}>{s.n}</div>
            <div style={{fontSize:"14px",fontWeight:"800",color:"var(--text)"}}>{s.title}</div>
          </div>
          {s.arabic && <div style={{fontFamily:"'Scheherazade New',serif",fontSize:"20px",color:"var(--gold)",direction:"rtl",textAlign:"right",lineHeight:1.8,marginBottom:"6px"}}>{s.arabic}</div>}
          {s.trans && <div style={{fontSize:"13px",color:"var(--accent)",fontStyle:"italic",marginBottom:"4px"}}>{s.trans}</div>}
          {s.transl && <div style={{fontSize:"13px",color:"var(--muted)",marginBottom:"6px"}}>{s.transl}</div>}
          <div style={{fontSize:"12px",color:"var(--muted)",opacity:0.75,borderTop:"1px solid var(--border)",paddingTop:"8px",marginTop:"4px"}}>{s.note}</div>
        </div>
      ))}
    </div>
  ));

  if (teachingView === "wudu") return shell("How to Make Wudu", (
    <div style={{padding:"16px"}}>
      {TEACHING_WUDU_STEPS.map((s,i) => (
        <div key={i} style={{display:"flex",gap:"14px",alignItems:"flex-start",marginBottom:"14px",background:"var(--s2)",border:"1px solid var(--border)",borderRadius:"14px",padding:"14px 16px"}}>
          <span style={{fontSize:"28px",flexShrink:0,lineHeight:1}}>{s.icon}</span>
          <div style={{flex:1}}>
            <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"4px"}}>
              <div style={{width:"22px",height:"22px",borderRadius:"50%",background:"var(--gold)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"11px",fontWeight:"800",color:"#07090c",flexShrink:0}}>{i+1}</div>
              <div style={{fontSize:"14px",fontWeight:"700",color:"var(--text)"}}>{s.step}</div>
            </div>
            <div style={{fontSize:"12px",color:"var(--muted)",lineHeight:1.6,paddingLeft:"30px"}}>{s.note}</div>
          </div>
        </div>
      ))}
    </div>
  ));

  if (teachingView === "fivepillars") return shell("Five Pillars of Islam", (
    <div style={{padding:"16px"}}>
      {TEACHING_FIVE_PILLARS.map((p,i) => (
        <div key={i} style={{marginBottom:"14px",background:"var(--s2)",border:`1px solid ${p.color}44`,borderRadius:"16px",padding:"18px 16px",borderLeft:`4px solid ${p.color}`}}>
          <div style={{display:"flex",alignItems:"center",gap:"12px",marginBottom:"10px"}}>
            <span style={{fontSize:"32px"}}>{p.icon}</span>
            <div>
              <div style={{fontSize:"16px",fontWeight:"800",color:"var(--text)"}}>{p.name}</div>
              <div style={{fontFamily:"'Scheherazade New',serif",fontSize:"18px",color:p.color}}>{p.arabic}</div>
            </div>
          </div>
          <div style={{fontSize:"13px",color:"var(--muted)",lineHeight:1.7}}>{p.desc}</div>
        </div>
      ))}
    </div>
  ));

  const renderTimeline = (data) => (
    <div style={{padding:"16px"}}>
      {data.map((e,i) => (
        <div key={i} style={{display:"flex",gap:"14px",marginBottom:"14px"}}>
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:"4px",flexShrink:0}}>
            <div style={{width:"12px",height:"12px",borderRadius:"50%",background:"var(--gold)",flexShrink:0,marginTop:"6px"}} />
            {i < data.length-1 && <div style={{width:"2px",flex:1,background:"var(--border)",minHeight:"20px"}} />}
          </div>
          <div style={{flex:1,background:"var(--s2)",border:"1px solid var(--border)",borderRadius:"14px",padding:"12px 14px"}}>
            <div style={{fontSize:"11px",fontWeight:"800",color:"var(--gold)",letterSpacing:"0.5px",marginBottom:"4px"}}>{e.year}</div>
            <div style={{fontSize:"14px",fontWeight:"800",color:"var(--text)",marginBottom:"2px"}}>{e.title}</div>
            <div style={{fontFamily:"'Scheherazade New',serif",fontSize:"15px",color:"var(--muted)",marginBottom:"6px",direction:"rtl",textAlign:"right"}}>{e.arabic}</div>
            <div style={{fontSize:"13px",color:"var(--muted)",lineHeight:1.6}}>{e.desc}</div>
          </div>
        </div>
      ))}
    </div>
  );

  if (teachingView === "islamichistory") return shell("Islamic History", renderTimeline(TEACHING_HISTORY));
  if (teachingView === "seerah") return shell("Seerah — Life of the Prophet ﷺ", renderTimeline(TEACHING_SEERAH));

  if (teachingView === "surahs") {
    const surah = TEACHING_SURAHS.find(s => s.id === selSurah);
    return shell("Short Surahs", (
      <div style={{padding:"16px"}}>
        {surah ? (
          <div>
            <button onClick={() => { setSelSurah(null); setTtsId(null); if (window.speechSynthesis) window.speechSynthesis.cancel(); }}
              style={{background:"transparent",border:"1px solid var(--border)",borderRadius:"8px",color:"var(--muted)",fontSize:"13px",padding:"8px 16px",cursor:"pointer",fontFamily:"inherit",marginBottom:"16px"}}>← All surahs</button>
            <div style={{background:"var(--s2)",border:"1px solid var(--border)",borderRadius:"16px",overflow:"hidden"}}>
              <div style={{padding:"16px",borderBottom:"1px solid var(--border)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div>
                  <div style={{fontSize:"18px",fontWeight:"800",color:"var(--text)"}}>{surah.name}</div>
                  <div style={{fontSize:"12px",color:"var(--muted)",marginTop:"2px"}}>{surah.meaning} · {surah.ayahs} ayahs · Surah {surah.number}</div>
                </div>
                <div style={{display:"flex",gap:"10px",alignItems:"center"}}>
                  <button onClick={() => playTts("surah-"+surah.id, surah.text.replace(/﴿\d+﴾/g," "))}
                    style={{width:"40px",height:"40px",borderRadius:"50%",border:`1px solid ${ttsId==="surah-"+surah.id?"var(--accent)":"var(--border)"}`,background:ttsId==="surah-"+surah.id?"rgba(0,201,167,0.12)":"transparent",color:ttsId==="surah-"+surah.id?"var(--accent)":"var(--muted)",fontSize:"16px",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.2s",fontFamily:"inherit"}}>
                    {ttsId==="surah-"+surah.id?"■":"▶"}
                  </button>
                  <button onClick={() => toggleSurahLearned(surah.id)}
                    style={{width:"40px",height:"40px",borderRadius:"50%",border:`1px solid ${learnedSurahs.includes(surah.id)?"var(--gold)":"var(--border)"}`,background:learnedSurahs.includes(surah.id)?"rgba(255,179,0,0.12)":"transparent",fontSize:"20px",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.2s"}}>
                    {learnedSurahs.includes(surah.id)?"⭐":"☆"}
                  </button>
                </div>
              </div>
              <div style={{padding:"16px",fontFamily:"'Scheherazade New',serif",fontSize:"26px",color:"var(--gold)",direction:"rtl",textAlign:"right",lineHeight:2}}>{surah.text}</div>
              <div style={{padding:"0 16px 16px",fontSize:"13px",color:"var(--muted)",lineHeight:1.7,borderTop:"1px solid var(--border)",paddingTop:"12px"}}>{surah.translation}</div>
            </div>
          </div>
        ) : (
          <div>
            <div style={{fontSize:"12px",color:"var(--muted)",marginBottom:"14px",textAlign:"center"}}>Tap a surah to read it · ⭐ when you know it by heart</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"12px"}}>
              {TEACHING_SURAHS.map(s => (
                <div key={s.id} onClick={() => setSelSurah(s.id)}
                  style={{background:"var(--s2)",border:`1px solid ${learnedSurahs.includes(s.id)?"var(--gold)":"var(--border)"}`,borderRadius:"14px",padding:"14px 12px",cursor:"pointer",position:"relative",transition:"border-color 0.2s"}}>
                  {learnedSurahs.includes(s.id) && <span style={{position:"absolute",top:"8px",right:"10px",fontSize:"14px"}}>⭐</span>}
                  <div style={{fontFamily:"'Scheherazade New',serif",fontSize:"18px",color:"var(--gold)",direction:"rtl",marginBottom:"6px"}}>{s.arabic}</div>
                  <div style={{fontSize:"13px",fontWeight:"700",color:"var(--text)"}}>{s.name}</div>
                  <div style={{fontSize:"11px",color:"var(--muted)",marginTop:"2px"}}>{s.ayahs} ayahs · #{s.number}</div>
                </div>
              ))}
            </div>
            {learnedSurahs.length > 0 && (
              <div style={{textAlign:"center",marginTop:"16px",fontSize:"13px",color:"var(--gold)",fontWeight:"700"}}>⭐ {learnedSurahs.length} / {TEACHING_SURAHS.length} surahs learned</div>
            )}
          </div>
        )}
      </div>
    ));
  }

  if (teachingView === "prophets") return shell("Prophets", (
    <div style={{padding:"16px"}}>
      <div style={{fontSize:"12px",color:"var(--muted)",marginBottom:"14px",textAlign:"center"}}>Tap a prophet to read their story · ▶ to listen</div>
      {TEACHING_PROPHETS.map((p, i) => (
        <div key={i} style={{marginBottom:"10px",background:"var(--s2)",border:`1px solid ${selProphet===i?"var(--gold)":"var(--border)"}`,borderRadius:"14px",overflow:"hidden",transition:"border-color 0.2s"}}>
          <div onClick={() => setSelProphet(selProphet===i?null:i)}
            style={{display:"flex",alignItems:"center",gap:"14px",padding:"14px 16px",cursor:"pointer"}}>
            <span style={{fontSize:"28px",flexShrink:0}}>{p.icon}</span>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:"15px",fontWeight:"800",color:"var(--text)"}}>{p.name}</div>
              <div style={{fontFamily:"'Scheherazade New',serif",fontSize:"16px",color:"var(--muted)"}}>{p.arabic}</div>
            </div>
            <span style={{color:"var(--muted)",fontSize:"18px",flexShrink:0,transition:"transform 0.2s",transform:selProphet===i?"rotate(90deg)":"none"}}>›</span>
          </div>
          {selProphet===i && (
            <div style={{borderTop:"1px solid var(--border)",padding:"14px 16px"}}>
              <div style={{fontSize:"13px",color:"var(--text)",lineHeight:1.8,marginBottom:"12px"}}>{p.story}</div>
              <div style={{background:"rgba(255,179,0,0.08)",border:"1px solid rgba(255,179,0,0.2)",borderRadius:"10px",padding:"10px 14px",marginBottom:"12px"}}>
                <div style={{fontSize:"10px",fontWeight:"700",color:"var(--gold)",letterSpacing:"2px",textTransform:"uppercase",marginBottom:"4px"}}>Lesson</div>
                <div style={{fontSize:"13px",color:"var(--gold)",fontStyle:"italic"}}>{p.lesson}</div>
              </div>
              <button onClick={() => playTts("prophet-"+i, p.story, "en-US")}
                style={{display:"flex",alignItems:"center",gap:"8px",background:"transparent",border:`1px solid ${ttsId==="prophet-"+i?"var(--accent)":"var(--border)"}`,borderRadius:"20px",padding:"8px 16px",color:ttsId==="prophet-"+i?"var(--accent)":"var(--muted)",fontSize:"13px",cursor:"pointer",fontFamily:"inherit",transition:"all 0.2s"}}>
                <span>{ttsId==="prophet-"+i?"■":"▶"}</span>
                <span>{ttsId==="prophet-"+i?"Stop":"Listen to story"}</span>
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  ));

  if (teachingView === "adab") {
    const manner = selAdab !== null ? TEACHING_ADAB[selAdab] : null;
    return shell("Islamic Manners", (
      <div style={{padding:"16px"}}>
        {manner ? (
          <div>
            <button onClick={() => { setSelAdab(null); setTtsId(null); if (window.speechSynthesis) window.speechSynthesis.cancel(); }}
              style={{background:"transparent",border:"1px solid var(--border)",borderRadius:"8px",color:"var(--muted)",fontSize:"13px",padding:"8px 16px",cursor:"pointer",fontFamily:"inherit",marginBottom:"16px"}}>← All manners</button>
            <div style={{background:"var(--s2)",border:"1px solid var(--border)",borderRadius:"16px",padding:"20px 16px",textAlign:"center"}}>
              <div style={{fontSize:"52px",marginBottom:"12px"}}>{manner.icon}</div>
              <div style={{fontSize:"18px",fontWeight:"800",color:"var(--text)",marginBottom:"8px"}}>{manner.title}</div>
              {manner.arabic ? (
                <>
                  <div style={{fontFamily:"'Scheherazade New',serif",fontSize:"24px",color:"var(--gold)",direction:"rtl",lineHeight:1.8,marginBottom:"8px"}}>{manner.arabic}</div>
                  <button onClick={() => playTts("adab-"+selAdab, manner.arabic)}
                    style={{width:"38px",height:"38px",borderRadius:"50%",border:`1px solid ${ttsId==="adab-"+selAdab?"var(--accent)":"var(--border)"}`,background:ttsId==="adab-"+selAdab?"rgba(0,201,167,0.12)":"transparent",color:ttsId==="adab-"+selAdab?"var(--accent)":"var(--muted)",fontSize:"15px",cursor:"pointer",display:"inline-flex",alignItems:"center",justifyContent:"center",marginBottom:"8px",transition:"all 0.2s",fontFamily:"inherit"}}>
                    {ttsId==="adab-"+selAdab?"■":"▶"}
                  </button>
                </>
              ) : null}
              <div style={{fontSize:"14px",fontWeight:"700",color:"var(--accent)",marginBottom:"14px",fontStyle:"italic"}}>{manner.phrase}</div>
              <div style={{fontSize:"13px",color:"var(--muted)",lineHeight:1.8,textAlign:"left"}}>{manner.desc}</div>
            </div>
          </div>
        ) : (
          <div>
            <div style={{fontSize:"12px",color:"var(--muted)",marginBottom:"14px",textAlign:"center"}}>Tap to learn an Islamic manner</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"12px"}}>
              {TEACHING_ADAB.map((a, i) => (
                <div key={i} onClick={() => setSelAdab(i)}
                  style={{background:"var(--s2)",border:"1px solid var(--border)",borderRadius:"14px",padding:"16px 12px",cursor:"pointer",textAlign:"center"}}>
                  <div style={{fontSize:"36px",marginBottom:"8px"}}>{a.icon}</div>
                  <div style={{fontSize:"12px",fontWeight:"700",color:"var(--text)",lineHeight:1.3}}>{a.title}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    ));
  }

  if (teachingView === "learnarabic") return shell("Learn Arabic Letters", (
    <div style={{padding:"16px"}}>
      <div style={{fontSize:"13px",color:"var(--muted)",marginBottom:"16px",lineHeight:1.6,textAlign:"center"}}>Tap any letter to hear it · see all 4 forms below</div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"10px",direction:"rtl"}}>
        {TEACHING_ARABIC_LETTERS.map((l,i) => (
          <div key={i} onClick={() => { setExpandedLetter(expandedLetter===i?null:i); playLetterAudio(l.letter); }}
            style={{background:expandedLetter===i?"rgba(255,179,0,0.12)":"var(--s2)",border:`1px solid ${expandedLetter===i?"var(--gold)":"var(--border)"}`,borderRadius:"12px",padding:"12px 6px",cursor:"pointer",transition:"all 0.2s",direction:"ltr"}}>
            <div style={{fontFamily:"'Scheherazade New',serif",fontSize:"34px",color:"var(--gold)",direction:"rtl",lineHeight:1.2,textAlign:"right"}}>{l.letter}</div>
            <div style={{fontSize:"11px",fontWeight:"700",color:"var(--text)",marginTop:"4px",textAlign:"center"}}>{l.name}</div>
            <div style={{fontSize:"10px",color:"var(--muted)",textAlign:"center"}}>/{l.sound}/</div>
            {expandedLetter===i && (
              <div style={{marginTop:"10px",paddingTop:"10px",borderTop:"1px solid var(--border)"}}>
                {[["iso","Isol."],["ini","Init."],["mid","Mid."],["fin","Final"]].map(([k,label]) => (
                  <div key={k} style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"4px",gap:"6px"}}>
                    <span style={{fontSize:"10px",color:"var(--muted)"}}>{label}</span>
                    <span style={{fontFamily:"'Scheherazade New',serif",fontSize:"18px",color:"var(--text)",direction:"rtl"}}>{l[k]}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  ));

  return (
    <div style={{position:"fixed",inset:0,background:"var(--bg)",zIndex:9000,display:"flex",flexDirection:"column"}}>
      <div style={{position:"relative",flexShrink:0}}>
        <div onClick={handleExitTap} style={{padding:"calc(16px + env(safe-area-inset-top)) 20px 14px",textAlign:"center",borderBottom:"1px solid var(--border)",userSelect:"none",WebkitUserSelect:"none",cursor:"pointer"}}>
          <div style={{fontFamily:"'Scheherazade New',serif",fontSize:"16px",color:"var(--gold)",opacity:0.7,marginBottom:"4px",lineHeight:1}}>بِسْمِ اللَّهِ الرَّحْمَنِ الرَّحِيمِ</div>
          <div style={{fontSize:"26px",fontWeight:"900",color:"var(--gold)",letterSpacing:"0.5px",lineHeight:1.1}}>Sirat Learn</div>
          <div style={{fontSize:"12px",color:"var(--muted)",marginTop:"4px"}}>Islamic learning for young Muslims</div>
          <div style={{display:"inline-flex",alignItems:"center",gap:"4px",marginTop:"8px",padding:"3px 9px",background:"rgba(0,201,167,0.12)",border:"1px solid rgba(0,201,167,0.35)",borderRadius:"999px",fontSize:"10px",fontWeight:"700",color:"var(--accent)",letterSpacing:"0.5px"}}>
            <span>✓</span><span>Works offline</span>
          </div>
        </div>
        <button
          onClick={() => setShowIntro && setShowIntro(true)}
          aria-label="Help"
          style={{position:"absolute",top:"calc(20px + env(safe-area-inset-top))",right:"16px",width:"32px",height:"32px",borderRadius:"50%",background:"var(--s2)",border:"1px solid var(--border)",color:"var(--gold)",fontSize:"16px",fontWeight:"800",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"inherit",padding:0,lineHeight:1}}
        >?</button>
      </div>
      {!arabicVoiceAvailable && (
        <div style={{flexShrink:0,padding:"10px 14px",background:"rgba(255,179,0,0.10)",borderBottom:"1px solid rgba(255,179,0,0.35)",fontSize:"11px",color:"var(--text)",lineHeight:1.5,textAlign:"center"}}>
          <strong style={{color:"var(--gold)"}}>⚠ Arabic voice not installed.</strong> Duas &amp; Surahs audio may be silent. On iPhone: <em>Settings → Accessibility → Spoken Content → Voices → Arabic → download</em>.
        </div>
      )}
      <div style={{flex:1,overflowY:"auto",WebkitOverflowScrolling:"touch",padding:"14px",paddingBottom:"calc(20px + env(safe-area-inset-bottom))"}}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"12px"}}>
          {TEACHING_TILES.filter(t => !t.hidden).map(tile => (
            <div key={tile.id} onClick={() => setTeachingView(tile.id)} style={{
              background:"var(--s1)",
              border:`1px solid ${tile.color}44`,
              borderRadius:"20px",
              overflow:"hidden",
              cursor:"pointer",
              WebkitTapHighlightColor:"transparent",
              display:"flex",
              flexDirection:"column",
              transition:"transform 0.12s"
            }}>
              <div style={{
                background:`linear-gradient(145deg, ${tile.color}22, ${tile.color}08)`,
                padding:"20px 16px 16px",
                display:"flex",
                flexDirection:"column",
                alignItems:"center",
                gap:"12px",
                borderBottom:`1px solid ${tile.color}22`
              }}>
                <div style={{
                  width:"60px",
                  height:"60px",
                  borderRadius:"18px",
                  background:`${tile.color}1a`,
                  border:`1.5px solid ${tile.color}44`,
                  display:"flex",
                  alignItems:"center",
                  justifyContent:"center",
                  fontSize: tile.id === "learnarabic" ? "30px" : "32px",
                  fontFamily: tile.id === "learnarabic" ? "'Scheherazade New',serif" : "inherit",
                  color: tile.id === "learnarabic" ? tile.color : "inherit"
                }}>
                  {tile.icon}
                </div>
              </div>
              <div style={{padding:"12px 14px 16px", flex:1, display:"flex", flexDirection:"column"}}>
                <div style={{fontSize:"14px",fontWeight:"800",color:"var(--text)",lineHeight:1.2}}>{tile.label}</div>
                {tile.desc && <div style={{fontSize:"11px",color:"var(--muted)",marginTop:"5px",lineHeight:1.5,flex:1}}>{tile.desc}</div>}
                <div style={{marginTop:"10px",display:"inline-flex",alignItems:"center",gap:"3px",padding:"2px 8px",background:`${tile.color}18`,borderRadius:"999px",fontSize:"10px",fontWeight:"700",color:tile.color}}>
                  Open →
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {showIntro && (
        <div style={{position:"fixed",inset:0,background:"rgba(7,9,12,0.82)",backdropFilter:"blur(6px)",WebkitBackdropFilter:"blur(6px)",zIndex:9100,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px",animation:"fadeIn 0.4s ease"}}>
          <div style={{background:"var(--s1)",border:"1px solid var(--border)",borderRadius:"20px",padding:"28px 22px",maxWidth:"460px",width:"100%",maxHeight:"90vh",overflowY:"auto",boxShadow:"0 10px 40px rgba(0,0,0,0.4)"}}>
            <div style={{fontSize:"22px",fontWeight:"900",color:"var(--gold)",textAlign:"center",letterSpacing:"0.5px"}}>Welcome to Sirat Learn</div>
            <div style={{fontFamily:"'Scheherazade New',serif",fontSize:"26px",color:"var(--gold)",textAlign:"center",marginTop:"6px"}}>بِسْمِ اللَّهِ</div>

            <div style={{marginTop:"22px"}}>
              <div style={{fontSize:"13px",fontWeight:"800",color:"var(--accent)",textTransform:"uppercase",letterSpacing:"1.5px",marginBottom:"6px"}}>What is this?</div>
              <div style={{fontSize:"14px",color:"var(--text)",lineHeight:1.6}}>A simple learning space for children and beginners — Salah step-by-step, short Surahs, daily duas, Wudu, the Five Pillars and more. Clean, colourful, no ads, no distractions.</div>
            </div>

            <div style={{marginTop:"18px"}}>
              <div style={{fontSize:"13px",fontWeight:"800",color:"var(--accent)",textTransform:"uppercase",letterSpacing:"1.5px",marginBottom:"6px"}}>How to use it</div>
              <div style={{fontSize:"14px",color:"var(--text)",lineHeight:1.6}}>Tap any tile to open a lesson. Swipe left or tap back to return to the grid. Let your child explore freely — nothing here can break the app.</div>
            </div>

            <div style={{marginTop:"18px",padding:"14px",background:"var(--s2)",border:"1px solid var(--border)",borderRadius:"12px"}}>
              <div style={{fontSize:"12px",fontWeight:"800",color:"var(--muted)",textTransform:"uppercase",letterSpacing:"1.5px",marginBottom:"6px"}}>For parents</div>
              <div style={{fontSize:"13px",color:"var(--text)",lineHeight:1.6}}>To exit Teaching Mode, tap the <strong style={{color:"var(--gold)"}}>Sirat Learn</strong> title at the top 5 times.</div>
            </div>

            <div style={{marginTop:"18px",padding:"14px",background:"rgba(255,179,0,0.08)",border:"1px solid rgba(255,179,0,0.25)",borderRadius:"12px"}}>
              <div style={{fontSize:"13px",color:"var(--text)",lineHeight:1.7,textAlign:"center",fontStyle:"italic"}}>May Allah accept this effort and let every Muslim — young and old — benefit from it. Ameen.</div>
            </div>

            <button
              onClick={() => { localStorage.setItem("teachingIntroSeen","true"); setShowIntro(false); }}
              style={{marginTop:"22px",width:"100%",padding:"14px",background:"var(--gold)",border:"none",borderRadius:"12px",color:"#07090c",fontSize:"15px",fontWeight:"800",cursor:"pointer",fontFamily:"inherit",letterSpacing:"0.5px"}}
            >Bismillah — Let's begin</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared inspiration pool — verses + hadiths used in the Ibadah card and share
// modal. ONE source of truth so the displayed entry always matches the shared one.
// ─────────────────────────────────────────────────────────────────────────────
const IB_INSPIRATION_POOL = [
  // ── Quranic verses ───────────────────────────────────────────────────────
  { kind:"verse", ar:"إِنَّ مَعَ الْعُسْرِ يُسْرًا", en:"Indeed, with hardship comes ease.", ref:"Ash-Sharḥ · 94:6" },
  { kind:"verse", ar:"فَاذْكُرُونِي أَذْكُرْكُمْ", en:"Remember Me — I will remember you.", ref:"Al-Baqarah · 2:152" },
  { kind:"verse", ar:"أَلَا بِذِكْرِ اللَّهِ تَطْمَئِنُّ الْقُلُوبُ", en:"In the remembrance of Allah do hearts find rest.", ref:"Ar-Raʿd · 13:28" },
  { kind:"verse", ar:"وَمَن يَتَوَكَّلْ عَلَى اللَّهِ فَهُوَ حَسْبُهُ", en:"Whoever relies upon Allah — He is sufficient for him.", ref:"Aṭ-Ṭalāq · 65:3" },
  { kind:"verse", ar:"إِنَّ اللَّهَ لَا يُغَيِّرُ مَا بِقَوْمٍ حَتَّىٰ يُغَيِّرُوا مَا بِأَنفُسِهِمْ", en:"Allah does not change a people's condition until they change what is within themselves.", ref:"Ar-Raʿd · 13:11" },
  { kind:"verse", ar:"وَمَن يَتَّقِ اللَّهَ يَجْعَل لَّهُ مَخْرَجًا", en:"Whoever fears Allah — He will make a way out for him.", ref:"Aṭ-Ṭalāq · 65:2" },
  { kind:"verse", ar:"وَاللَّهُ يُحِبُّ الصَّابِرِينَ", en:"And Allah loves the patient.", ref:"Āl ʿImrān · 3:146" },
  { kind:"verse", ar:"وَمَا خَلَقْتُ الْجِنَّ وَالْإِنسَ إِلَّا لِيَعْبُدُونِ", en:"I did not create jinn and mankind except to worship Me.", ref:"Adh-Dhāriyāt · 51:56" },
  { kind:"verse", ar:"رَبَّنَا آتِنَا فِي الدُّنْيَا حَسَنَةً وَفِي الْآخِرَةِ حَسَنَةً", en:"Our Lord, grant us good in this world and good in the Hereafter.", ref:"Al-Baqarah · 2:201" },
  { kind:"verse", ar:"إِنَّ الَّذِينَ قَالُوا رَبُّنَا اللَّهُ ثُمَّ اسْتَقَامُوا تَتَنَزَّلُ عَلَيْهِمُ الْمَلَائِكَةُ", en:"Those who say 'Our Lord is Allah' and remain steadfast — the angels descend upon them.", ref:"Fuṣṣilat · 41:30" },
  { kind:"verse", ar:"وَعَسَىٰ أَن تَكْرَهُوا شَيْئًا وَهُوَ خَيْرٌ لَّكُمْ", en:"Perhaps you dislike something while it is good for you.", ref:"Al-Baqarah · 2:216" },
  { kind:"verse", ar:"حَسْبُنَا اللَّهُ وَنِعْمَ الْوَكِيلُ", en:"Allah is sufficient for us, and what an excellent Guardian He is.", ref:"Āl ʿImrān · 3:173" },
  { kind:"verse", ar:"وَلَسَوْفَ يُعْطِيكَ رَبُّكَ فَتَرْضَىٰ", en:"Your Lord will give you, and you will be satisfied.", ref:"Aḍ-Ḍuḥā · 93:5" },
  { kind:"verse", ar:"وَبَشِّرِ الصَّابِرِينَ", en:"And give good news to the patient.", ref:"Al-Baqarah · 2:155" },
  { kind:"verse", ar:"وَإِذَا سَأَلَكَ عِبَادِي عَنِّي فَإِنِّي قَرِيبٌ أُجِيبُ دَعْوَةَ الدَّاعِ", en:"When My servants ask you about Me, I am near — I respond to the call of the one who calls.", ref:"Al-Baqarah · 2:186" },
  { kind:"verse", ar:"إِنَّ اللَّهَ مَعَ الصَّابِرِينَ", en:"Indeed, Allah is with the patient.", ref:"Al-Baqarah · 2:153" },
  { kind:"verse", ar:"وَنَحْنُ أَقْرَبُ إِلَيْهِ مِنْ حَبْلِ الْوَرِيدِ", en:"We are closer to him than his jugular vein.", ref:"Qāf · 50:16" },
  { kind:"verse", ar:"وَفِي السَّمَاءِ رِزْقُكُمْ وَمَا تُوعَدُونَ", en:"In the heavens is your provision and what you are promised.", ref:"Adh-Dhāriyāt · 51:22" },
  { kind:"verse", ar:"لَا تَقْنَطُوا مِن رَّحْمَةِ اللَّهِ إِنَّ اللَّهَ يَغْفِرُ الذُّنُوبَ جَمِيعًا", en:"Do not despair of the mercy of Allah — Allah forgives all sins.", ref:"Az-Zumar · 39:53" },
  { kind:"verse", ar:"وَمَا تَوْفِيقِي إِلَّا بِاللَّهِ", en:"My success is only through Allah.", ref:"Hūd · 11:88" },
  { kind:"verse", ar:"رَبِّ زِدْنِي عِلْمًا", en:"My Lord, increase me in knowledge.", ref:"Ṭāhā · 20:114" },
  { kind:"verse", ar:"يَا أَيَّتُهَا النَّفْسُ الْمُطْمَئِنَّةُ ارْجِعِي إِلَىٰ رَبِّكِ رَاضِيَةً مَّرْضِيَّةً", en:"O tranquil soul, return to your Lord well-pleased and pleasing to Him.", ref:"Al-Fajr · 89:27–28" },
  { kind:"verse", ar:"فَتَبَارَكَ اللَّهُ أَحْسَنُ الْخَالِقِينَ", en:"Blessed is Allah, the best of creators.", ref:"Al-Muʾminūn · 23:14" },
  { kind:"verse", ar:"وَاللَّهُ خَيْرٌ حَافِظًا وَهُوَ أَرْحَمُ الرَّاحِمِينَ", en:"Allah is the best guardian, and He is the most merciful of the merciful.", ref:"Yūsuf · 12:64" },
  { kind:"verse", ar:"وَاللَّهُ يُحِبُّ الْمُحْسِنِينَ", en:"And Allah loves the doers of good.", ref:"Āl ʿImrān · 3:134" },
  { kind:"verse", ar:"إِنَّمَا يُوَفَّى الصَّابِرُونَ أَجْرَهُم بِغَيْرِ حِسَابٍ", en:"Indeed, the patient will be given their reward without account.", ref:"Az-Zumar · 39:10" },
  { kind:"verse", ar:"سَنُرِيهِمْ آيَاتِنَا فِي الْآفَاقِ وَفِي أَنفُسِهِمْ", en:"We will show them Our signs in the horizons and within themselves.", ref:"Fuṣṣilat · 41:53" },
  { kind:"verse", ar:"قُلْ إِنَّ صَلَاتِي وَنُسُكِي وَمَحْيَايَ وَمَمَاتِي لِلَّهِ رَبِّ الْعَالَمِينَ", en:"Say: My prayer, my rites, my living and my dying are for Allah, Lord of all worlds.", ref:"Al-Anʿām · 6:162" },
  { kind:"verse", ar:"وَهُوَ مَعَكُمْ أَيْنَ مَا كُنتُمْ", en:"He is with you wherever you are.", ref:"Al-Ḥadīd · 57:4" },
  { kind:"verse", ar:"فَإِنَّ مَعَ الْعُسْرِ يُسْرًا", en:"For indeed, with hardship will be ease.", ref:"Ash-Sharḥ · 94:5" },
  // ── Hadiths ──────────────────────────────────────────────────────────────
  { kind:"hadith", ar:"إِنَّمَا الْأَعْمَالُ بِالنِّيَّاتِ", en:"Actions are judged only by intentions.", ref:"Bukhārī · 1", narrator:"ʿUmar ibn al-Khaṭṭāb (raḍiyallāhu ʿanhu)" },
  { kind:"hadith", ar:"مَنْ كَانَ يُؤْمِنُ بِاللَّهِ وَالْيَوْمِ الْآخِرِ فَلْيَقُلْ خَيْرًا أَوْ لِيَصْمُتْ", en:"Whoever believes in Allah and the Last Day should speak good or remain silent.", ref:"Bukhārī · 6018", narrator:"Abū Hurayrah (raḍiyallāhu ʿanhu)" },
  { kind:"hadith", ar:"الدِّينُ النَّصِيحَةُ", en:"The religion is sincerity.", ref:"Muslim · 55", narrator:"Tamīm al-Dārī (raḍiyallāhu ʿanhu)" },
  { kind:"hadith", ar:"لَا يُؤْمِنُ أَحَدُكُمْ حَتَّى يُحِبَّ لِأَخِيهِ مَا يُحِبُّ لِنَفْسِهِ", en:"None of you truly believes until he loves for his brother what he loves for himself.", ref:"Bukhārī · 13", narrator:"Anas ibn Mālik (raḍiyallāhu ʿanhu)" },
  { kind:"hadith", ar:"تَبَسُّمُكَ فِي وَجْهِ أَخِيكَ صَدَقَةٌ", en:"Your smile in the face of your brother is charity.", ref:"Tirmidhī · 1956", narrator:"Abū Dharr (raḍiyallāhu ʿanhu)" },
  { kind:"hadith", ar:"خَيْرُكُمْ مَنْ تَعَلَّمَ الْقُرْآنَ وَعَلَّمَهُ", en:"The best of you are those who learn the Quran and teach it.", ref:"Bukhārī · 5027", narrator:"ʿUthmān ibn ʿAffān (raḍiyallāhu ʿanhu)" },
  { kind:"hadith", ar:"الْمُسْلِمُ مَنْ سَلِمَ الْمُسْلِمُونَ مِنْ لِسَانِهِ وَيَدِهِ", en:"A Muslim is one from whose tongue and hand other Muslims are safe.", ref:"Bukhārī · 10", narrator:"ʿAbdullāh ibn ʿAmr (raḍiyallāhu ʿanhu)" },
  { kind:"hadith", ar:"مَنْ سَلَكَ طَرِيقًا يَلْتَمِسُ فِيهِ عِلْمًا سَهَّلَ اللَّهُ لَهُ طَرِيقًا إِلَى الْجَنَّةِ", en:"Whoever takes a path seeking knowledge, Allah eases for him a path to Paradise.", ref:"Muslim · 2699", narrator:"Abū Hurayrah (raḍiyallāhu ʿanhu)" },
  { kind:"hadith", ar:"لَا يَشْكُرُ اللَّهَ مَنْ لَا يَشْكُرُ النَّاسَ", en:"Whoever does not thank people has not thanked Allah.", ref:"Abū Dāwūd · 4811", narrator:"Abū Hurayrah (raḍiyallāhu ʿanhu)" },
  { kind:"hadith", ar:"أَحَبُّ الْأَعْمَالِ إِلَى اللَّهِ أَدْوَمُهَا وَإِنْ قَلَّ", en:"The most beloved deeds to Allah are those done consistently, even if they are small.", ref:"Bukhārī · 6464", narrator:"ʿĀʾishah (raḍiyallāhu ʿanhā)" },
  { kind:"hadith", ar:"اتَّقِ اللَّهَ حَيْثُمَا كُنتَ وَأَتْبِعِ السَّيِّئَةَ الْحَسَنَةَ تَمْحُهَا", en:"Fear Allah wherever you are, and follow a bad deed with a good one — it will wipe it out.", ref:"Tirmidhī · 1987", narrator:"Muʿādh ibn Jabal (raḍiyallāhu ʿanhu)" },
  { kind:"hadith", ar:"لَيْسَ الشَّدِيدُ بِالصُّرَعَةِ إِنَّمَا الشَّدِيدُ الَّذِي يَمْلِكُ نَفْسَهُ عِنْدَ الْغَضَبِ", en:"The strong man is not the wrestler — the strong man is the one who controls himself in anger.", ref:"Bukhārī · 6114", narrator:"Abū Hurayrah (raḍiyallāhu ʿanhu)" },
  { kind:"hadith", ar:"كُلُّ بَنِي آدَمَ خَطَّاءٌ وَخَيْرُ الْخَطَّائِينَ التَّوَّابُونَ", en:"Every son of Adam makes mistakes, and the best of those who err are those who repent.", ref:"Tirmidhī · 2499", narrator:"Anas ibn Mālik (raḍiyallāhu ʿanhu)" },
  { kind:"hadith", ar:"خَيْرُ النَّاسِ أَنفَعُهُمْ لِلنَّاسِ", en:"The best of people are those most beneficial to people.", ref:"Al-Muʿjam al-Awsaṭ · 6026", narrator:"Jābir ibn ʿAbdillāh (raḍiyallāhu ʿanhu)" },
  { kind:"hadith", ar:"إِنَّ اللَّهَ كَتَبَ الْإِحْسَانَ عَلَىٰ كُلِّ شَيْءٍ", en:"Allah has prescribed excellence in all things.", ref:"Muslim · 1955", narrator:"Shaddād ibn Aws (raḍiyallāhu ʿanhu)" },
  { kind:"hadith", ar:"بُعِثْتُ لِأُتَمِّمَ مَكَارِمَ الْأَخْلَاقِ", en:"I was sent to perfect noble character.", ref:"Al-Muwattaʾ · 1613", narrator:"Abū Hurayrah (raḍiyallāhu ʿanhu)" },
  { kind:"hadith", ar:"الطَّهُورُ شَطْرُ الْإِيمَانِ", en:"Purity is half of faith.", ref:"Muslim · 223", narrator:"Abū Mālik al-Ashʿarī (raḍiyallāhu ʿanhu)" },
  { kind:"hadith", ar:"أَقْرَبُ مَا يَكُونُ الْعَبْدُ مِنْ رَبِّهِ وَهُوَ سَاجِدٌ فَأَكْثِرُوا الدُّعَاءَ", en:"The closest a servant is to his Lord is when he is prostrating — so make much duʿāʾ.", ref:"Muslim · 482", narrator:"Abū Hurayrah (raḍiyallāhu ʿanhu)" },
  { kind:"hadith", ar:"مَنْ قَرَأَ حَرْفًا مِن كِتَابِ اللَّهِ فَلَهُ حَسَنَةٌ وَالْحَسَنَةُ بِعَشْرِ أَمْثَالِهَا", en:"Whoever reads a letter from the Book of Allah has a good deed, and that deed is multiplied by ten.", ref:"Tirmidhī · 2910", narrator:"ʿAbdullāh ibn Masʿūd (raḍiyallāhu ʿanhu)" },
  { kind:"hadith", ar:"الدُّعَاءُ هُوَ الْعِبَادَةُ", en:"Supplication is worship.", ref:"Tirmidhī · 3247", narrator:"Nuʿmān ibn Bashīr (raḍiyallāhu ʿanhu)" },
  { kind:"hadith", ar:"لَا تَحْقِرَنَّ مِنَ الْمَعْرُوفِ شَيْئًا وَلَوْ أَنْ تَلْقَى أَخَاكَ بِوَجْهٍ طَلْقٍ", en:"Do not belittle any good deed, even meeting your brother with a cheerful face.", ref:"Muslim · 2626", narrator:"Abū Dharr (raḍiyallāhu ʿanhu)" },
  { kind:"hadith", ar:"مَنْ نَفَّسَ عَنْ مُؤْمِنٍ كُرْبَةً مِنْ كُرَبِ الدُّنْيَا نَفَّسَ اللَّهُ عَنْهُ كُرْبَةً مِنْ كُرَبِ يَوْمِ الْقِيَامَةِ", en:"Whoever relieves a believer of distress in this world, Allah will relieve him of distress on the Day of Resurrection.", ref:"Muslim · 2699", narrator:"Abū Hurayrah (raḍiyallāhu ʿanhu)" },
  { kind:"hadith", ar:"الْيَدُ الْعُلْيَا خَيْرٌ مِنَ الْيَدِ السُّفْلَى", en:"The upper hand is better than the lower hand.", ref:"Bukhārī · 1427", narrator:"ʿAbdullāh ibn ʿUmar (raḍiyallāhu ʿanhumā)" },
  { kind:"hadith", ar:"إِنَّ اللَّهَ جَمِيلٌ يُحِبُّ الْجَمَالَ", en:"Allah is beautiful and loves beauty.", ref:"Muslim · 91", narrator:"ʿAbdullāh ibn Masʿūd (raḍiyallāhu ʿanhu)" },
  { kind:"hadith", ar:"مَنْ صَامَ رَمَضَانَ إِيمَانًا وَاحْتِسَابًا غُفِرَ لَهُ مَا تَقَدَّمَ مِنْ ذَنبِهِ", en:"Whoever fasts Ramadan with faith and sincere hope, his past sins are forgiven.", ref:"Bukhārī · 38", narrator:"Abū Hurayrah (raḍiyallāhu ʿanhu)" },
  { kind:"hadith", ar:"مَنْ أَصْبَحَ مِنْكُمْ آمِنًا فِي سِرْبِهِ مُعَافًى فِي جَسَدِهِ عِنْدَهُ قُوتُ يَوْمِهِ فَكَأَنَّمَا حِيزَتْ لَهُ الدُّنْيَا", en:"Whoever wakes safe in his household, healthy in his body, with food for the day — it is as if the whole world has been gathered for him.", ref:"Tirmidhī · 2346", narrator:"ʿUbayd ibn Miḥṣan (raḍiyallāhu ʿanhu)" },
  { kind:"hadith", ar:"مَنْ كَانَ آخِرُ كَلَامِهِ لَا إِلَهَ إِلَّا اللَّهُ دَخَلَ الْجَنَّةَ", en:"Whoever's last words are 'There is no god but Allah' will enter Paradise.", ref:"Abū Dāwūd · 3116", narrator:"Muʿādh ibn Jabal (raḍiyallāhu ʿanhu)" },
  { kind:"hadith", ar:"الصَّلَوَاتُ الْخَمْسُ وَالْجُمُعَةُ إِلَى الْجُمُعَةِ كَفَّارَةٌ لِمَا بَيْنَهُنَّ", en:"The five prayers and one Friday to the next are expiation for what lies between them.", ref:"Muslim · 233", narrator:"Abū Hurayrah (raḍiyallāhu ʿanhu)" },
  { kind:"hadith", ar:"مَنْ أَحَبَّ لِقَاءَ اللَّهِ أَحَبَّ اللَّهُ لِقَاءَهُ", en:"Whoever loves to meet Allah, Allah loves to meet him.", ref:"Bukhārī · 6507", narrator:"ʿUbādah ibn al-Ṣāmit (raḍiyallāhu ʿanhu)" },
  { kind:"hadith", ar:"الصِّدْقُ يَهْدِي إِلَى الْبِرِّ وَالْبِرُّ يَهْدِي إِلَى الْجَنَّةِ", en:"Truthfulness leads to righteousness, and righteousness leads to Paradise.", ref:"Bukhārī · 6094", narrator:"ʿAbdullāh ibn Masʿūd (raḍiyallāhu ʿanhu)" },
  // ── Jumuah ───────────────────────────────────────────────────────────────
  { kind:"jumuah", ar:"جُمُعَةٌ مُبَارَكَةٌ", en:"May Allah accept your duas and bless your day.", ref:"Friday · Blessed Day" },
];

export default function App() {
  // TEMPORARY: Force clear to show registration
  if (window.location.search.includes('reset')) {
    localStorage.clear();
    window.location.replace('/');
  }

  // Register service worker for offline caching (separate from push registration)
  // Skip on native — Capacitor bundles assets locally, no SW needed
  useEffect(() => {
    if ('serviceWorker' in navigator && !isNative()) {
      navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' }).then(reg => {
        // Force update check on every app open
        reg.update().catch(() => {});
      }).catch(() => {});
    }
    // Capture PWA install prompt for Android
    const handleBIP = (e) => { e.preventDefault(); deferredInstallPrompt.current = e; };
    window.addEventListener("beforeinstallprompt", handleBIP);
    return () => window.removeEventListener("beforeinstallprompt", handleBIP);
  }, []);

  // OTA self-update (native iOS only): pull the latest JS bundle from the
  // Cloudflare deploy and apply it on next launch. Fully fail-safe with
  // auto-rollback — see src/otaUpdater.js.
  useEffect(() => { initOtaUpdater(); }, []);

  // iOS WKWebView keyboard-dismiss layout fix.
  //
  // When the iOS keyboard opens and a focused input would be hidden behind
  // it, WKWebView scrolls window.scrollY > 0 so the field stays visible.
  // On keyboard close iOS sometimes fails to scroll back to 0 — the whole
  // app appears "shifted down" with a black gap below the status bar. App
  // switcher trip forces a reflow that fixes it.
  //
  // Hook the global keyboardDidHide event, scroll body back to top, and
  // force a reflow. Was previously only handled inside CalorieCounter for
  // food search; promoting to App level so AI Coach, Riyadah, Settings,
  // etc. all behave correctly too.
  useEffect(() => {
    if (!isNative()) return;
    let remove = () => {};
    (async () => {
      try {
        const { Keyboard } = await import("@capacitor/keyboard");
        const reset = () => {
          try { window.scrollTo(0, 0); } catch {}
          try { document.documentElement.scrollTop = 0; } catch {}
          try { document.body.scrollTop = 0; } catch {}
          // Trigger a resize-style recompute so any stale
          // env(safe-area-inset-top) consumers refresh.
          try { window.dispatchEvent(new Event("resize")); } catch {}
        };
        const handle = await Keyboard.addListener("keyboardDidHide", reset);
        remove = () => { try { handle.remove(); } catch {} };
      } catch {}
    })();
    return () => remove();
  }, []);

  /* ─── Viewport height fix (iOS 100vh bug + resume shift) ───────────────────
     iOS Safari's `100vh` includes the dynamic URL bar and doesn't update when
     the bar shows/hides, leaving a blank strip below the tab bar (and cut-off
     content near the Dynamic Island) after the app returned to foreground.
     Publish the real inner height as --vh on every event that can change it,
     and use calc(var(--vh)*100) for all viewport-sized rules in App.css.

     Also dispatch synthetic pointerup/pointercancel on resume so the TabBar
     (and other gesture handlers) release any captured pointer / drag refs
     that got stuck when the app was backgrounded mid-gesture. */
  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };

    const clearStuckPointerState = () => {
      try {
        window.dispatchEvent(new PointerEvent("pointercancel", { bubbles: true }));
        window.dispatchEvent(new PointerEvent("pointerup",     { bubbles: true }));
      } catch (_) {
        try {
          window.dispatchEvent(new Event("pointercancel"));
          window.dispatchEvent(new Event("pointerup"));
        } catch (__) {}
      }
    };

    const onResume = () => {
      setVh();
      clearStuckPointerState();
      // Refresh the last-tab timestamp on every resume so the persisted-tab
      // window (used by the cold-remount restore in useState) doesn't expire
      // while the app is sitting in the background.
      try { localStorage.setItem("lastTabAt", String(Date.now())); } catch {}
      // iOS WKWebView occasionally leaves the page scrolled (status-bar tap,
      // keyboard cycle, app-switcher gesture). Native code resets the
      // underlying UIScrollView; this resets the document/window in case
      // anything bubbled up to the JS side.
      try { window.scrollTo(0, 0); } catch (_) {}
      try { if (document.scrollingElement) document.scrollingElement.scrollTop = 0; } catch (_) {}
      // Force a layout read so iOS commits the new viewport immediately
      // instead of waiting for the next paint.
      // eslint-disable-next-line no-unused-expressions
      void document.documentElement.offsetHeight;
    };

    setVh();
    window.addEventListener("resize",             setVh);
    window.addEventListener("orientationchange",  setVh);
    window.addEventListener("pageshow",           onResume);
    window.addEventListener("focus",              onResume);
    const onVis = () => {
      if (document.visibilityState === "visible") {
        onResume();
      } else {
        // Stamp the timestamp on background so the freshness check survives
        // a cold WKWebView reload on iOS resume regardless of how long since
        // the tab last changed.
        try { localStorage.setItem("lastTabAt", String(Date.now())); } catch {}
      }
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", () => {
      try { localStorage.setItem("lastTabAt", String(Date.now())); } catch {}
    });
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", setVh);
    }

    return () => {
      window.removeEventListener("resize",            setVh);
      window.removeEventListener("orientationchange", setVh);
      window.removeEventListener("pageshow",          onResume);
      window.removeEventListener("focus",             onResume);
      document.removeEventListener("visibilitychange", onVis);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", setVh);
      }
    };
  }, []);

  const [userId, setUserId] = useState(() => {
    const fromStorage = localStorage.getItem("azaan-userId");
    const fromCookie = getCookie("azaan-userId");
    const existingId = fromStorage || fromCookie || null;
    if (existingId) {
      if (!fromStorage) localStorage.setItem("azaan-userId", existingId);
      // Existing user — ignore ?claim=1 / ?code= params, just enter the app
      const params = new URLSearchParams(window.location.search);
      if (params.get("claim") || params.get("code")) window.history.replaceState({}, "", "/");
      return existingId;
    }
    return null;
  });
  const [userName, setUserName] = useState(() => localStorage.getItem("userName") || "");
  const [shareCardStyle, setShareCardStyle] = useState(() => localStorage.getItem("shareCardStyle") || "paper");
  const [userSurname, setUserSurname] = useState(() => localStorage.getItem("userSurname") || "");
  const [userWhatsapp, setUserWhatsapp] = useState(() => localStorage.getItem("userWhatsapp") || "27");
  const [isSuspended, setIsSuspended] = useState(false);
  const [suspendReason, setSuspendReason] = useState("");
  const [userEmail, setUserEmail] = useState(() => localStorage.getItem("userEmail") || "");
  const [editingEmail, setEditingEmail] = useState(false);
  const [editingMobile, setEditingMobile] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(() => localStorage.getItem("profilePhoto") || "");
  const [khatmLogPrompt, setKhatmLogPrompt] = useState(null); // { surahKey, khatms: [...] } — shown after surah finishes
  const [khatmGuestName, setKhatmGuestName] = useState("");
  const [khatmGuestSurname, setKhatmGuestSurname] = useState("");
  const [khatmGuestPhone, setKhatmGuestPhone] = useState("");
  const [khatmGuestLoading, setKhatmGuestLoading] = useState(false);
  const [khatmGuestError, setKhatmGuestError] = useState("");
  const [khatmGuestDetail, setKhatmGuestDetail] = useState(null); // khatm object passed to <Khatm> after guest join
  const [khatmShowFullSignup, setKhatmShowFullSignup] = useState(false); // "already have account" escape
  const [khatmShowA2HS, setKhatmShowA2HS] = useState(false);
  const [khatmJoinCode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const k = params.get("khatm");
    if (k) window.history.replaceState({}, "", window.location.pathname);
    return k ? k.toUpperCase() : "";
  });
  const _witnessParams = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const w = params.get("witness");
    const vo = params.get("viewonly") === "1";
    if (w) window.history.replaceState({}, "", window.location.pathname);
    return { w: w || "", vo };
  })[0];
  const [witnessWillId, setWitnessWillId] = useState(_witnessParams.w);
  const [witnessViewOnly, setWitnessViewOnly] = useState(_witnessParams.vo);
  const [debugWitnessUserId, setDebugWitnessUserId] = useState(null);
  const _fipshareParam = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const f = params.get("fipshare");
    if (f) window.history.replaceState({}, "", window.location.pathname);
    return f || "";
  })[0];
  const [fipShareOwnerId, setFipShareOwnerId] = useState(_fipshareParam);
  const [sharedFipInbox, setSharedFipInbox] = useState([]);
  const _fipUnlockParam = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const c = params.get("fipunlock");
    if (c) window.history.replaceState({}, "", window.location.pathname);
    return c || "";
  })[0];
  const [inviteCode, setInviteCode] = useState(() => {
    // Pre-fill invite code from deep link ?code=SIRAT-XXXX
    const params = new URLSearchParams(window.location.search);
    const c = params.get("code");
    return c ? c.toUpperCase() : "";
  });
  const [claimingCode, setClaimingCode] = useState(() => {
    // Only claim if new user (no existing session)
    if (localStorage.getItem("azaan-userId") || getCookie("azaan-userId")) return false;
    return new URLSearchParams(window.location.search).get("claim") === "1";
  });
  const [claimError, setClaimError] = useState(null);
  useEffect(() => {
    if (!claimingCode) return;
    fetch(BACKEND + "/api/invite/claim")
      .then(r => r.json())
      .then(data => {
        if (data.code) {
          setInviteCode(data.code.toUpperCase());
          window.history.replaceState({}, "", `?code=${data.code}`);
          setClaimingCode(false);
        } else {
          setClaimError(data.error || "No invite codes available.");
          setClaimingCode(false);
        }
      })
      .catch(() => {
        setClaimError("Could not connect. Please try again.");
        setClaimingCode(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [registering, setRegistering] = useState(false);
  const [darkMode, setDarkMode] = useState(() => { const s = localStorage.getItem("darkMode"); if (s !== null) return s !== "false"; return true; });
  const theme = darkMode ? "dark" : "light";
  const [showThemeHint, setShowThemeHint] = useState(false);
  useEffect(() => {
    if (localStorage.getItem("themeHintShown")) return;
    const t = setTimeout(() => setShowThemeHint(true), 4000);
    return () => clearTimeout(t);
  }, []);
  const [tab, setTab] = useState(() => {
    // App-switcher resumes that keep the WKWebView alive preserve React state
    // automatically. But iOS reaps WebViews under memory pressure — coming
    // back from the app switcher then triggers a cold remount, which used to
    // snap us back to the default tab. Persist the last tab + timestamp to
    // localStorage and restore it if the gap was short (≤4h), so quick
    // resumes feel continuous. The "Default tab on launch" preference still
    // wins on first ever launch and on long-gap returns. Live Activity taps
    // override via pendingAction (handlePending).
    const pref = localStorage.getItem("defaultTab");
    const valid = (t) => t === "masjids" || t === "radio" || t === "tools" || t === "hayah" || t === "settings" || t === "maintainer";
    try {
      const lastTab = localStorage.getItem("lastTab");
      const lastTabAt = parseInt(localStorage.getItem("lastTabAt") || "0", 10);
      const fresh = lastTab && lastTabAt && (Date.now() - lastTabAt) < 24 * 60 * 60 * 1000;
      if (fresh && valid(lastTab)) return lastTab;
    } catch {}
    return valid(pref) ? pref : "masjids";
  });
  // Stamp the active tab whenever it changes so a cold remount on resume
  // can land us back where we left off.
  useEffect(() => {
    try {
      localStorage.setItem("lastTab", tab);
      localStorage.setItem("lastTabAt", String(Date.now()));
    } catch {}
    setDebugState("tab", tab);
  }, [tab]);
  const [playing, setPlaying] = useState(null);
  const playingRef = useRef(null);
  playingRef.current = playing; // keep ref in sync for timer callbacks
  const lastLiveActivitySigRef = useRef({ nextPrayer: null, jumuah: null, tahajjud: null });
  const { showSuccess, showError, showInfo } = useToast();
  const hayahFastToast = useHayahFastToastScheduler();
  const [playStatus, setPlayStatus] = useState("stopped"); // stopped buffering playing error
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const bufferingTimeoutRef = useRef(null);
  // Monotonic token identifying the live stream-connection attempt. Bumped for
  // every attempt (and by every stop path), so a superseded attempt's late
  // error/timeout/play() callbacks can't clobber the attempt that replaced it.
  const playAttemptRef = useRef(0);
  const playStatusRef = useRef("stopped");
  const [prayerTimes, setPrayerTimes] = useState(null);
  const [countdown, setCountdown] = useState("--:--:--");
  const [nextPrayer, setNextPrayer] = useState("--");
  const [activeIdx, setActiveIdx] = useState(-1);
  const [hijri, setHijri] = useState("");
  const [headerNow, setHeaderNow] = useState(new Date());
  const [locationMode, setLocationMode] = useState(() => localStorage.getItem("locationMode") || "auto");
  const [userLat, setUserLat] = useState(() => parseFloat(localStorage.getItem("userLat")) || null);
  const [userLng, setUserLng] = useState(() => parseFloat(localStorage.getItem("userLng")) || null);
  const [detectedCity, setDetectedCity] = useState(() => localStorage.getItem("detectedCity") || "");
  const [locationLoading, setLocationLoading] = useState(false);
  const [manualCityInput, setManualCityInput] = useState("");
  const [citySuggestions, setCitySuggestions] = useState([]);
  const citySearchTimerRef = useRef(null);
  const locationCooldownRef = useRef(parseInt(localStorage.getItem("userLatTime") || "0", 10));
  // tasbeeh state moved to src/components/Tasbeeh.js
  // Render instantly from the last good backend list (or the bundled list) —
  // the backend can take 20s+ to cold-start, which left Radio on "Tuning in…".
  const [stations, setStations] = useState(() => {
    try {
      const c = JSON.parse(localStorage.getItem("stationsCache") || "null");
      if (c && Array.isArray(c.radio) && Array.isArray(c.masjids)) return c;
    } catch {}
    return STATIONS;
  });
  const [radioMeta, setRadioMeta] = useState({}); // { [url]: { show, artist, isLive } }
  const [masjidNames, setMasjidNames] = useState({});
  const [followedMounts, setFollowedMounts] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("followedMounts") || "[]");
      const cleaned = raw.filter(m => !BLOCKED_MOUNTS.has(m));
      if (cleaned.length !== raw.length) localStorage.setItem("followedMounts", JSON.stringify(cleaned));
      return cleaned;
    } catch { return []; }
  });
  const [notifyMounts, setNotifyMounts] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("notifyMounts") || "null");
      if (!Array.isArray(raw)) return raw;
      const cleaned = raw.filter(m => !BLOCKED_MOUNTS.has(m));
      if (cleaned.length !== raw.length) localStorage.setItem("notifyMounts", JSON.stringify(cleaned));
      return cleaned;
    } catch { return null; }
  });
  // Effective notify list — null means legacy (all followed get notified)
  const effectiveNotify = notifyMounts !== null ? notifyMounts : followedMounts;

  // qibla state moved to src/components/Qibla.js
  const [followedNames, setFollowedNames] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("followedNames") || "{}");
      let changed = false;
      for (const m of Object.keys(raw)) if (BLOCKED_MOUNTS.has(m)) { delete raw[m]; changed = true; }
      if (changed) localStorage.setItem("followedNames", JSON.stringify(raw));
      return raw;
    } catch { return {}; }
  });
  const [followedUrls, setFollowedUrls] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("followedUrls") || "{}");
      // Drop blocked test mounts
      let changed = false;
      for (const m of Object.keys(raw)) if (BLOCKED_MOUNTS.has(m)) { delete raw[m]; changed = true; }
      // Migrate any non-relay livemasjid URLs to relay format
      for (const mount of Object.keys(raw)) {
        if (raw[mount] && raw[mount].includes("livemasjid.com") && !raw[mount].startsWith("https://relay.livemasjid.com:8443/")) {
          const urlMount = (raw[mount].split("/").pop() || mount).replace(/\.mp3$/i, "");
          raw[mount] = `https://relay.livemasjid.com:8443/${urlMount}`;
          changed = true;
        }
      }
      if (changed) localStorage.setItem("followedUrls", JSON.stringify(raw));
      return raw;
    } catch { return {}; }
  });
  const [allStreams, setAllStreams] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [isLandscape, setIsLandscape] = useState(() => window.matchMedia('(orientation: landscape)').matches);
  const [landscapeWallDismissed, setLandscapeWallDismissed] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
    const [streamStatus, setStreamStatus] = useState({});
  const [lmBanned, setLmBanned] = useState(false);
  const prevStreamStatusRef = useRef({});
  const autoPlayInitRef = useRef(false);
  const liveMountsRef = useRef(new Set()); // all currently live Icecast mounts (updated every 15s)

  // Check if a livemasjid mount is currently live (uses Icecast status, not stale catalog)
  const isMountLive = (mount) => {
    if (!mount) return false;
    const clean = mount.replace(/\.mp3$/, "");
    return liveMountsRef.current.has(clean) || liveMountsRef.current.has(clean + ".mp3");
  };

  // Normalize livemasjid URL for status lookup (extract mount name)
  const getStreamStatus = (url) => {
    if (!url.includes("livemasjid.com")) return streamStatus[url];
    const mount = url.split("/").pop().replace(".mp3", "");
    for (const [statusUrl, live] of Object.entries(streamStatus)) {
      const statusMount = statusUrl.split("/").pop().replace(".mp3", "");
      if (statusMount === mount) return live;
    }
    return null;
  };
  const [showWelcome, setShowWelcome] = useState(false);
  const [welcomeName, setWelcomeName] = useState("");
  const [welcomeProLabel, setWelcomeProLabel] = useState(null);
  const [showSplash, setShowSplash] = useState(() => {
    // User opt-out: Settings → Skip splash on launch. When set, skip the
    // ~3s intro animation entirely and land straight on the configured
    // `defaultTab` (resolved at the top of App() above).
    try { if (localStorage.getItem("skipSplash") === "1") return false; } catch {}
    return !sessionStorage.getItem("siratSplashShown");
  });
  const [skipSplash, setSkipSplash] = useState(() => {
    try { return localStorage.getItem("skipSplash") === "1"; } catch { return false; }
  });
  const [splashFading, setSplashFading] = useState(false);
  const [showOnboardingCeremony, setShowOnboardingCeremony] = useState(false);
  const [ceremonyFading, setCeremonyFading] = useState(false);
  const ceremonyNameRef = useRef("");
  const welcomeTimerRef = useRef(null);

  // Splash screen auto-dismiss
  useEffect(() => {
    if (!showSplash) return;
    const fadeTimer = setTimeout(() => setSplashFading(true), 2000);
    const hideTimer = setTimeout(() => {
      setShowSplash(false);
      setSplashFading(false);
      sessionStorage.setItem("siratSplashShown", "1");
    }, 2800);
    return () => { clearTimeout(fadeTimer); clearTimeout(hideTimer); };
  }, [showSplash]);


  const [showVerseOverlay, setShowVerseOverlay] = useState(false);
  const [showFanMenu, setShowFanMenu] = useState(false);
  const [fanStage, setFanStage] = useState('idle'); // 'idle'|'flying'|'burst'|'unfold'|'open'|'switching'
  const [fanPage, setFanPage] = useState(0);
  const fanPageRef = useRef(0);
  const [fanPinnedIds, setFanPinnedIds] = useState(() => {
    try { const s = localStorage.getItem("fanPinnedIds"); if (!s) return null; const p = JSON.parse(s); return Array.isArray(p) && p.length === 6 ? p : null; } catch { return null; }
  });
  const [fanEditingSlot, setFanEditingSlot] = useState(null); // 0-5 = open picker for that slot, null = closed
  const fanFlyRafRef = useRef(null);
  const [fanFlyT, setFanFlyT] = useState(0);
  const [fanTappedId, setFanTappedId] = useState(null);
  const fanStageRef = useRef('idle');
  const fanLogoSrcRef = useRef({ x: 22, y: 50 });
  const fanParticlesRef = useRef([]);
  const fanParticleRafRef = useRef(null);
  const [fanParticleTick, setFanParticleTick] = useState(0);
  const fanRingsRef = useRef([0, 180, 360].map(d => ({ delay: d })));
  const logoRef = useRef(null);
  // Refs for every animation timer in openFanMenu so they can be cancelled on background.
  const fanOpenTimer1Ref = useRef(null); // 340ms → burst
  const fanOpenTimer2Ref = useRef(null); // 60ms  → unfold
  const fanOpenTimer3Ref = useRef(null); // 180ms → open
  const fanCloseTimerRef = useRef(null); // 320ms → idle

  // Hard-reset: cancel every in-flight animation and leave the menu fully closed.
  // Called on visibilitychange→hidden so stale timers can't revive the backdrop.
  const abortFanMenu = () => {
    clearTimeout(fanOpenTimer1Ref.current);
    clearTimeout(fanOpenTimer2Ref.current);
    clearTimeout(fanOpenTimer3Ref.current);
    clearTimeout(fanCloseTimerRef.current);
    if (fanParticleRafRef.current) { cancelAnimationFrame(fanParticleRafRef.current); fanParticleRafRef.current = null; }
    if (fanFlyRafRef.current) { cancelAnimationFrame(fanFlyRafRef.current); fanFlyRafRef.current = null; }
    fanStageRef.current = 'idle';
    setFanStage('idle');
    setShowFanMenu(false);
    setFanFlyT(0);
    setFanParticleTick(0);
    fanPageRef.current = 0;
    setFanPage(0);
  };

  const closeFanMenu = () => {
    if (fanStageRef.current === 'closing' || fanStageRef.current === 'idle') return;
    if (fanParticleRafRef.current) { cancelAnimationFrame(fanParticleRafRef.current); fanParticleRafRef.current = null; }
    if (fanFlyRafRef.current) { cancelAnimationFrame(fanFlyRafRef.current); fanFlyRafRef.current = null; }
    setFanTappedId(null);
    hapticImpact("medium");
    playFanClose();
    fanStageRef.current = 'closing';
    setFanStage('closing');
    setFanFlyT(0);
    const closeStart = performance.now();
    const closeTick = (now) => {
      const ft = Math.min((now - closeStart) / 320, 1);
      setFanFlyT(ft);
      if (ft < 1) fanFlyRafRef.current = requestAnimationFrame(closeTick);
      else { fanFlyRafRef.current = null; setFanFlyT(0); }
    };
    fanFlyRafRef.current = requestAnimationFrame(closeTick);
    fanCloseTimerRef.current = setTimeout(() => {
      fanStageRef.current = 'idle';
      setFanStage('idle');
      setTimeout(() => { setShowFanMenu(false); fanPageRef.current = 0; setFanPage(0); }, 20);
    }, 320);
  };
  const playFanSwoosh = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const dur = 0.38;
      const src = fanNoise(ctx, dur);
      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.Q.value = 1.2;
      filter.frequency.setValueAtTime(2400, ctx.currentTime);
      filter.frequency.exponentialRampToValueAtTime(280, ctx.currentTime + dur);
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.28, ctx.currentTime + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur);
      src.connect(filter); filter.connect(gain); gain.connect(ctx.destination);
      src.start(); src.stop(ctx.currentTime + dur);
      src.onended = () => ctx.close();
    } catch (_) {}
  };
  const fanNoise = (ctx, dur) => {
    const bufLen = Math.ceil(ctx.sampleRate * dur);
    const buf = ctx.createBuffer(1, bufLen, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < bufLen; i++) data[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    return src;
  };
  const playFanBurst = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const dur = 0.18;
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(95, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(38, ctx.currentTime + dur);
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.32, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(); osc.stop(ctx.currentTime + dur);
      osc.onended = () => ctx.close();
    } catch (_) {}
  };
  const playFanSwitch = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const dur = 0.22;
      const src = fanNoise(ctx, dur);
      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.Q.value = 1.6;
      filter.frequency.setValueAtTime(1600, ctx.currentTime);
      filter.frequency.exponentialRampToValueAtTime(380, ctx.currentTime + dur);
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.18, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur);
      src.connect(filter); filter.connect(gain); gain.connect(ctx.destination);
      src.start(); src.stop(ctx.currentTime + dur);
      src.onended = () => ctx.close();
    } catch (_) {}
  };
  const playFanClose = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const dur = 0.32;
      const src = fanNoise(ctx, dur);
      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.Q.value = 1.2;
      filter.frequency.setValueAtTime(280, ctx.currentTime);
      filter.frequency.exponentialRampToValueAtTime(2400, ctx.currentTime + dur);
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.22, ctx.currentTime + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur);
      src.connect(filter); filter.connect(gain); gain.connect(ctx.destination);
      src.start(); src.stop(ctx.currentTime + dur);
      src.onended = () => ctx.close();
    } catch (_) {}
  };

  const openFanMenu = (sourceEl) => {
    if (showFanMenu) return;
    hapticImpact("medium");
    const src = sourceEl || logoRef.current;
    const lr = src?.getBoundingClientRect();
    fanLogoSrcRef.current = { x: lr ? lr.left + lr.width / 2 : 22, y: lr ? lr.top + lr.height / 2 : 50 };
    fanPageRef.current = 0; setFanPage(0);
    // 'init': render logo at src position first (no motion yet)
    setShowFanMenu(true);
    fanStageRef.current = 'init';
    setFanStage('init');
    // Double rAF: wait for 'init' to paint before triggering CSS transition
    requestAnimationFrame(() => requestAnimationFrame(() => {
      fanStageRef.current = 'flying';
      setFanStage('flying');
      playFanSwoosh();
      // Drive comet trail progress 0→1 over the flight duration
      const flyStart = performance.now();
      const flyDur = 340;
      const flyTick = (now) => {
        const ft = Math.min((now - flyStart) / flyDur, 1);
        setFanFlyT(ft);
        if (ft < 1) fanFlyRafRef.current = requestAnimationFrame(flyTick);
        else { fanFlyRafRef.current = null; }
      };
      fanFlyRafRef.current = requestAnimationFrame(flyTick);
      // After transition completes (~320ms), burst at center
      fanOpenTimer1Ref.current = setTimeout(() => {
        fanParticlesRef.current = Array.from({ length: 56 }, () => {
          const angle = Math.random() * Math.PI * 2;
          const speed = 55 + Math.random() * 95;
          const size = 1 + Math.random() * 2.4;
          return { vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed, size, color: Math.random() > 0.5 ? '#F4C56A' : '#FFE4B0' };
        });
        hapticImpact("heavy"); // logo arrives + explodes
        playFanBurst();
        fanStageRef.current = 'burst';
        setFanStage('burst');
        const start = performance.now();
        const tick = (now) => {
          const t = Math.min((now - start) / 500, 1);
          setFanParticleTick(t);
          if (t < 1) fanParticleRafRef.current = requestAnimationFrame(tick);
          else { fanParticleRafRef.current = null; setFanParticleTick(0); }
        };
        fanParticleRafRef.current = requestAnimationFrame(tick);
        fanOpenTimer2Ref.current = setTimeout(() => {
          fanStageRef.current = 'unfold'; setFanStage('unfold');
          // Staggered light taps as icons spring in (matches 25ms item stagger × 6)
          hapticImpact("light");
          setTimeout(() => hapticImpact("light"), 40);
          setTimeout(() => hapticImpact("light"), 90);
          fanOpenTimer3Ref.current = setTimeout(() => { fanStageRef.current = 'open'; setFanStage('open'); }, 180);
        }, 60);
      }, 340);
    }));
  };
  const nextFanPage = (totalPages) => {
    hapticUiSelection();
    playFanSwitch();
    fanStageRef.current = 'switching';
    setFanStage('switching');
    setTimeout(() => {
      const next = (fanPageRef.current + 1) % totalPages;
      fanPageRef.current = next;
      setFanPage(next);
      fanStageRef.current = 'open';
      setFanStage('open');
    }, 250);
  };
  // ── Ahzab 35 "Gem Verse" ──────────────────────────────────────────────────
  const [showAhzab35, setShowAhzab35]               = useState(false);
  const [ahzab35Playing, setAhzab35Playing]         = useState(false);
  const [ahzab35Loop, setAhzab35Loop]               = useState(true);
  const [ahzab35ReciterId, setAhzab35ReciterId]     = useState("ar.alafasy");
  const [ahzab35Loading, setAhzab35Loading]         = useState(false);
  const ahzab35Ref      = useRef(null);
  const ahzab35CacheRef = useRef({});
  const ahzab35TextRef  = useRef(null);
  const ahzab35LoopRef  = useRef(true);
  const [ahzab35TextLoaded, setAhzab35TextLoaded]   = useState(false);
  const [ahzab35ShowControls, setAhzab35ShowControls] = useState(true);
  const [ahzab35Duration, setAhzab35Duration]       = useState(null);
  const [ahzab35SecondsLeft, setAhzab35SecondsLeft] = useState(null);
  const [ahzab35SessionDone, setAhzab35SessionDone] = useState(false);
  const ahzab35FadeTimerRef  = useRef(null);
  const ahzab35CountdownRef  = useRef(null);
  const ahzab35DurationRef   = useRef(null);
  const ahzab35SwipeRef      = useRef({x:0,y:0});
  const [showLogoHint, setShowLogoHint] = useState(() => !localStorage.getItem("logoHintShown"));
  const [showOnboardingTips, setShowOnboardingTips] = useState(() => !localStorage.getItem("onboardingDone"));
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const [showLegalPage, setShowLegalPage] = useState(null); // null | "privacy" | "terms"
  const [whatsNewData, setWhatsNewData] = useState(null);
  const [notifEnabled, setNotifEnabled] = useState(localStorage.getItem("notifEnabled") === "true");
  const [showNotifPrompt, setShowNotifPrompt] = useState(false);
  const [showPermFlow, setShowPermFlow] = useState(false);
  const [permFlowStep, setPermFlowStep] = useState(0);
  const deferredInstallPrompt = useRef(null);
  const [setupBannerDismissed, setSetupBannerDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("setupBannerDismissed") || "{}"); } catch { return {}; }
  });
  const [school, setSchool] = useState(() => {
    const rc = (() => { try { return JSON.parse(localStorage.getItem("remoteConfig") || "{}"); } catch { return {}; } })();
    return localStorage.getItem("school") || (rc.defaultSchool !== undefined ? String(rc.defaultSchool) : "1");
  });
  const [autoPlayMount] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('play');
  });
  const [autoOpenFeedback] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('view') === 'feedback';
  });
  const [autoPlayAzaan] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('azaan');
  });

  const [isOffline, setIsOffline] = useState(() => !navigator.onLine);
  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline = () => setIsOffline(false);
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => { window.removeEventListener("offline", goOffline); window.removeEventListener("online", goOnline); };
  }, []);

  const [donationsEnabled, setDonationsEnabled] = useState(true);
  const [proPromptEnabled, setProPromptEnabled] = useState(true);
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const [appBanner, setAppBanner] = useState("");
  const [tabsConfig, setTabsConfig] = useState(() => {
    try { return JSON.parse(localStorage.getItem("tabsConfig") || "null") || {}; } catch { return {}; }
  });
  const [remoteConfig, setRemoteConfig] = useState(() => {
    try {
      const rc = JSON.parse(localStorage.getItem("remoteConfig") || "null") || {};
      if (rc.globalStations) rc.globalStations = rc.globalStations.map(normalizeGlobalStation);
      return rc;
    } catch { return {}; }
  });
  const settingsLoadedRef = useRef(!!localStorage.getItem("remoteConfig"));
  const userFetchDoneRef = useRef(false);
  const [userFeatures, setUserFeatures] = useState({});
  // Distinct local-dates (YYYY-MM-DD) the user has opened the app — sourced from
  // user.activeDays on the backend (populated via /api/users/:id/heartbeat).
  // Used by the Settings "Days Active" stat so it survives device changes.
  const [daysActive, setDaysActive] = useState(() => {
    try { return JSON.parse(localStorage.getItem("daysActive") || "[]"); }
    catch { return []; }
  });
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [feedbackPhone, setFeedbackPhone] = useState(() => localStorage.getItem("feedbackPhone") || "");
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const [feedbackHistoryLoading, setFeedbackHistoryLoading] = useState(false);
  const [ratingStars, setRatingStars] = useState(0);
  const [ratingComment, setRatingComment] = useState("");
  const [ratingSending, setRatingSending] = useState(false);
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [ratingLoaded, setRatingLoaded] = useState(false);
  const [showFeedbackPhone, setShowFeedbackPhone] = useState(false);
  const [feedbackRefreshing, setFeedbackRefreshing] = useState(false);
  const [feedbackPullY, setFeedbackPullY] = useState(0);
  const [devMode, setDevMode] = useState(() => localStorage.getItem("devMode") === "true");
  // Focus Mode: per-tab hide list. The user picks which non-essential tabs
  // to hide for a calmer app. Salaah / Settings / Admin are never hidden.
  // Storage: localStorage.focusHiddenTabs = JSON array of tab ids.
  // Migration: old binary `focusMode === "1"` maps to hiding radio + tools,
  // matching the prior fixed behaviour. Cleared after migration.
  const [focusHiddenTabs, setFocusHiddenTabs] = useState(() => {
    try {
      const raw = localStorage.getItem("focusHiddenTabs");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed.filter(t => typeof t === "string");
      }
      if (localStorage.getItem("focusMode") === "1") {
        const migrated = ["radio", "tools"];
        localStorage.setItem("focusHiddenTabs", JSON.stringify(migrated));
        localStorage.removeItem("focusMode");
        return migrated;
      }
    } catch {}
    return [];
  });
  const toggleFocusHiddenTab = (tabId) => {
    setFocusHiddenTabs(prev => {
      const next = prev.includes(tabId) ? prev.filter(t => t !== tabId) : [...prev, tabId];
      try { localStorage.setItem("focusHiddenTabs", JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const [maintainerUnreadCount, setMaintainerUnreadCount] = useState(0);
  const [showDevJourney, setShowDevJourney] = useState(false);
  const [devUnlockAnim, setDevUnlockAnim] = useState(false);
  // Push debug panel polls getNativePushDebug() every 1s while super-user
  // mode is on so the user can watch register-called → timeout / registration
  // happen in real time after tapping Force re-sync.
  const [pushDebugTick, setPushDebugTick] = useState(0);
  useEffect(() => {
    if (!devMode || !isNative()) return;
    const id = setInterval(() => setPushDebugTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [devMode]);
  const [devDeniedAnim, setDevDeniedAnim] = useState(false);
  const [generatedCodes, setGeneratedCodes] = useState([]);
  const [codeGenLoading, setCodeGenLoading] = useState(false);
  const [qrDataUrls, setQrDataUrls] = useState({});
  const [copiedCodeIdx, setCopiedCodeIdx] = useState(null);
  const [redeemCode, setRedeemCode] = useState("");
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [suCodesRemaining, setSuCodesRemaining] = useState(null);
  const [suCodeLabel, setSuCodeLabel] = useState(null);
  // User invite codes (for all users)
  const [userInviteCodes, setUserInviteCodes] = useState([]);
  const [userInviteRemaining, setUserInviteRemaining] = useState(null);
  // inviteQrUrls removed — QR generated via URL
  const [userInviteLoading, setUserInviteLoading] = useState(false);
  const [showInviteSheet, setShowInviteSheet] = useState(false);
  const [copiedInviteIdx, setCopiedInviteIdx] = useState(null);
  // Fetch invite remaining every time settings tab opens (respects admin changing invitesPerUser live)
  useEffect(() => {
    if (tab === "settings" && userId && userId !== "guest") {
      fetch(`${BACKEND}/api/user/invite-remaining`, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({userId}) })
        .then(r => r.json()).then(d => { if (d.remaining !== undefined) setUserInviteRemaining(d.remaining); if (d.codes) setUserInviteCodes(d.codes); if (!d.ok && d.remaining === undefined) setUserInviteRemaining(0); }).catch(() => setUserInviteRemaining(0));
    }
  }, [tab, userId]);
  const devTapCountRef = useRef(0);
  const devTapTimerRef = useRef(null);
  const feedbackPullStartRef = useRef(null);
  const feedbackScrollRef = useRef(null);
  const feedbackPanelRef = useRef(null);
  const fbSwipeRef = useRef({ startX: null, startY: null, startT: null, moving: false });
  const [vvHeight, setVvHeight] = useState(null); // visualViewport height for keyboard avoidance
  const [vvOffsetTop, setVvOffsetTop] = useState(0); // visualViewport offsetTop for iOS keyboard repositioning
  const [feedbackImage, setFeedbackImage] = useState(null); // base64 image attachment for feedback
  // Prayer + daily notifications
  const [dailyNotifEnabled, setDailyNotifEnabled] = useState(() => localStorage.getItem("dailyNotifEnabled") !== "false");
  const [prayerReminderEnabled, setPrayerReminderEnabled] = useState(() => localStorage.getItem("prayerReminderEnabled") === "true");
  // Azaan Alerts — per-prayer azaan clip at prayer time
  const [azaanPrayers, setAzaanPrayers] = useState(() => { try { return JSON.parse(localStorage.getItem("azaanPrayers") || "[]"); } catch { return []; } });
  const azaanPlayedRef = useRef({}); // keyed by "YYYY-MM-DD:Prayer" to prevent double-play
  const azaanAudioRef = useRef(null); // currently playing azaan Audio element
  const [debugPlayLog, setDebugPlayLog] = useState(null); // debug: last play attempt
  const [debugScreenOpen, setDebugScreenOpen] = useState(false);
  const isStaging = typeof window !== "undefined" && window.location.hostname.includes("pages.dev");
  const debugMode = !BUILD_TIME || isStaging || localStorage.getItem("sirat_debug") === "1";
  const showDebugFab = debugMode || isDebugHost();
  const [azaanPlaying, setAzaanPlaying] = useState(false);
  const [showHadithOverlay, setShowHadithOverlay] = useState(false);
  const [showHijriCalendar, setShowHijriCalendar] = useState(false);
  const [hijriCalClosing, setHijriCalClosing] = useState(false);
  const hijriRef = useRef(null);
  const toggleHijriCalendar = () => {
    hapticUiSelection();
    if (showHijriCalendar) { setHijriCalClosing(true); setTimeout(() => { setShowHijriCalendar(false); setHijriCalClosing(false); }, 500); }
    else setShowHijriCalendar(true);
  };
  const feedbackImageRef = useRef(null); // hidden file input for image picking
  const [localSentMsgs, setLocalSentMsgs] = useState([]); // messages sent this session (immune to server overwrites)
  const [feedbackLightbox, setFeedbackLightbox] = useState(null); // full-size image overlay src
  const [showQiblaOverlay, setShowQiblaOverlay] = useState(false);
  const [isPro, setIsPro] = useState(() => {
    // Check expiry FIRST — if expired, revoke Pro regardless of localStorage/cookie
    const expiry = localStorage.getItem("proExpiry");
    if (expiry && new Date(expiry) <= new Date()) {
      localStorage.removeItem("proExpiry");
      localStorage.removeItem("isPro");
      clearProCookie();
      return false;
    }
    if (localStorage.getItem("isPro") === "true") return true;
    if (getCookie("sirat-isPro") === "1") { localStorage.setItem("isPro", "true"); return true; }
    if (expiry && new Date(expiry) > new Date()) return true;
    return false;
  });
  const [showProPrompt, setShowProPrompt] = useState(false);
  const [proPromptContext, setProPromptContext] = useState("default");
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [paystackLoading, setPaystackLoading] = useState(false);
  const [proExpiry, setProExpiry] = useState(() => localStorage.getItem("proExpiry") || null);
  const [selectedProTier, setSelectedProTier] = useState(0);
  const [trialStart, setTrialStart] = useState(() => localStorage.getItem("trialStart") || null);
  const TRIAL_MS = (remoteConfig.trialDays || 7) * 24 * 60 * 60 * 1000;
  // Monetization mode: "support" = everything free + voluntary contributions, "pro" = existing paywall
  // isSupportMode controls the monetization model (free for all, no paywall)
  // supportPageVisible controls whether the donation page/card is shown
  const isSupportMode = remoteConfig.monetizationMode === "support" || (!remoteConfig.monetizationMode && remoteConfig.support?.enabled);
  const supportPageVisible = isSupportMode && (remoteConfig.support?.enabled !== false || devMode);
  const MAINTAINER_IDS = ["69ee43f6d46333a536d49f18","69f249e20407dff7e03737ce","69f0d8ae6e3dcef797f1cca2","69efb44d0e4f86b7150ecda9","69f3416ece17a5ac660926e8"];
  const SUPER_USER_IDS = ["69ee43f6d46333a536d49f18"];
  const isMaintainer = devMode ||
    MAINTAINER_IDS.includes(userId) ||
    (Array.isArray(remoteConfig.maintainerIds) && remoteConfig.maintainerIds.includes(userId));
  // Trial is irrelevant when app is fully free — suppress it so trial UI never shows in support/allFree mode
  const isTrial = settingsLoadedRef.current && !isPro && !isSupportMode && !remoteConfig.allFree && !!trialStart && (Date.now() - new Date(trialStart).getTime()) < TRIAL_MS;
  const trialDaysLeft = isTrial ? Math.max(1, Math.ceil((TRIAL_MS - (Date.now() - new Date(trialStart).getTime())) / (24 * 60 * 60 * 1000))) : 0;
  // zakatInputs state moved to src/components/ZakatCalculator.js
  const [proGrantedByAdmin, setProGrantedByAdmin] = useState(false);
  const proRevokedRef = useRef(false); // guard: prevents checkUserStatus from restoring Pro after revoke
  const [showDonation, setShowDonation] = useState(false);
  const [greetingIdx, setGreetingIdx] = useState(0);
  const [longPressMount, setLongPressMount] = useState(null);
  const longPressTimer = useRef(null);
  const [ptrY, setPtrY] = useState(0);
  const [ptrRefreshing, setPtrRefreshing] = useState(false);
  const ptrStartY = useRef(null);
  const [donationAmt, setDonationAmt] = useState(50);
  const [donationCustom, setDonationCustom] = useState("");
  // Support model state
  const [supportTotal] = useState(() => parseInt(localStorage.getItem("supportTotal") || "0"));
  const [supportCount] = useState(() => parseInt(localStorage.getItem("supportCount") || "0"));
  const [hasSupported] = useState(() => localStorage.getItem("hasSupported") === "true");
  const [showSupportToast, setShowSupportToast] = useState(false);
  const [supportToastTier, setSupportToastTier] = useState(null);
  const [supportToastUpgraded, setSupportToastUpgraded] = useState(false);
  const supportTotalRef = useRef(parseInt(localStorage.getItem("supportTotal") || "0"));
  const [toolView, setToolView] = useState(() => {
    // Restore the last sub-screen within Tools alongside the tab itself, so
    // a cold remount on iOS resume lands the user back where they were
    // (e.g. mid-Quran reading) rather than the grid root.
    try {
      const lastView = localStorage.getItem("lastToolView");
      const lastTabAt = parseInt(localStorage.getItem("lastTabAt") || "0", 10);
      if (lastView && lastTabAt && (Date.now() - lastTabAt) < 24 * 60 * 60 * 1000) return lastView;
    } catch {}
    return "grid";
  }); // "grid" | "tasbeeh" | "zakat" | "quran" | "surah" | "azkar" | "names" | "fullquran" | "fullquranreading" | "istikharah" | "duas" | "duadetail" | "islamiccalendar" | "rightnow" | "duajournal" | "tawbah" | "cardgenerator"
  useEffect(() => {
    try { localStorage.setItem("lastToolView", toolView); } catch {}
  }, [toolView]);
  const [hayahFeature, setHayahFeature] = useState(() => {
    // Mirror the lastTab/lastToolView pattern: persist the active Hayah
    // sub-feature so an iOS WKWebView reap on resume doesn't snap the user
    // back to Ghidha when they were last on Exercise.
    try {
      // One-time migration: with the v3 Today landing, force existing users
      // (whose persisted lastHayahFeature is "ghidha"/"exercise") to see the
      // new Today screen on next load. Clears once via the v3-seen flag.
      const seenV3 = localStorage.getItem("hayahTodayV3Seen");
      if (!seenV3) {
        localStorage.setItem("hayahTodayV3Seen", "1");
        return "today";
      }
      const saved = localStorage.getItem("lastHayahFeature");
      const lastTabAt = parseInt(localStorage.getItem("lastTabAt") || "0", 10);
      const fresh = saved && lastTabAt && (Date.now() - lastTabAt) < 24 * 60 * 60 * 1000;
      if (fresh && (saved === "today" || saved === "ghidha" || saved === "exercise" || saved === "ai")) return saved;
    } catch {}
    return "today";
  }); // "today" | "ghidha" | "exercise"  (fasting/muraqabah moved to Ibadah)
  useEffect(() => {
    try { localStorage.setItem("lastHayahFeature", hayahFeature); } catch {}
  }, [hayahFeature]);
  // Bumped each time the user taps the Hayah bottom-nav tab. Passed as a key
  // through HayahHub to CalorieCounter so the inner view resets to Today.
  const [hayahResetKey, setHayahResetKey] = useState(0);
  const [siriDebugOpen, setSiriDebugOpen] = useState(false);
  const [siriDebugData, setSiriDebugData] = useState(null);
  const [tawbahText, setTawbahText] = useState("");
  const [tawbahPhase, setTawbahPhase] = useState("idle");
  // Teaching Mode state
  const [teachingMode, setTeachingMode] = useState(() => localStorage.getItem("teachingMode") === "true");
  const [teachingView, setTeachingView] = useState("grid");

  // ── Settings v2 — Tier-1 user preferences (added with editorial Settings redesign) ──
  // Theme preference: 'dark' | 'light' | 'auto'
  const [darkModePref, setDarkModePref] = useState(() => localStorage.getItem("darkModePref") || (localStorage.getItem("darkMode") === "false" ? "light" : "dark"));
  // Default tab on app launch — 'masjids' | 'radio' | 'tools'
  const [defaultTab, setDefaultTab] = useState(() => localStorage.getItem("defaultTab") || "masjids");
  // Default reciter for Surah / Khatm players (RECITERS id; '' = no preference)
  const [defaultReciterPref, setDefaultReciterPref] = useState(() => localStorage.getItem("defaultReciterPref") || "");
  // Daily Name of Allah push toggle + hour
  const [dailyNameEnabled, setDailyNameEnabled] = useState(() => localStorage.getItem("dailyNameEnabled") === "true");
  const [dailyNameHour, setDailyNameHour] = useState(() => parseInt(localStorage.getItem("dailyNameHour") || "8"));
  // Live Activity window — controls how far before a prayer the lock-screen card appears
  // "always" | "60min" | "30min" | "10min" | "5min" | "off". Always-on Fajr→Dhuhr regardless (morning override).
  const [liveActivityWindow, setLiveActivityWindow] = useState(() => localStorage.getItem("liveActivityWindow") || "60min");
  useEffect(() => { try { localStorage.setItem("liveActivityWindow", liveActivityWindow); } catch {} }, [liveActivityWindow]);
  // FaceID app lock toggle
  const [faceIdLock, setFaceIdLock] = useState(() => localStorage.getItem("faceIdLock") === "true");
  // Advanced (destructive) settings drawer — collapsed by default
  const [advancedExpanded, setAdvancedExpanded] = useState(false);
  // Qibla haptic alignment toggle
  const [qiblaHaptic, setQiblaHaptic] = useState(() => localStorage.getItem("qiblaHaptic") !== "false");
  // Haptics master + per-event toggles (all default ON when on native; web no-op anyway)
  const [hapticsEnabled, setHapticsEnabled] = useState(() => localStorage.getItem("hapticsEnabled") !== "false");
  const [hapticsTasbeeh, setHapticsTasbeeh] = useState(() => localStorage.getItem("hapticsTasbeeh") !== "false");
  const [hapticsPrayerMark, setHapticsPrayerMark] = useState(() => localStorage.getItem("hapticsPrayerMark") !== "false");
  const [hapticsTabSelection, setHapticsTabSelection] = useState(() => localStorage.getItem("hapticsTabSelection") !== "false");
  // Tier-2 "Notify me" intent flags (set when user taps Notify on Coming Soon row)
  const [comingSoonIntent, setComingSoonIntent] = useState(() => { try { return JSON.parse(localStorage.getItem("comingSoonIntent") || "{}"); } catch { return {}; } });
  const [showTeachingIntro, setShowTeachingIntro] = useState(false);
  const headerTapCountRef = useRef(0);
  const headerTapTimerRef = useRef(null);
  const handleSiratTap = () => {
    headerTapCountRef.current += 1;
    if (headerTapTimerRef.current) clearTimeout(headerTapTimerRef.current);
    if (headerTapCountRef.current >= 5) {
      headerTapCountRef.current = 0;
      setTeachingMode(true);
      localStorage.setItem("teachingMode", "true");
      setTeachingView("grid");
      if (!localStorage.getItem("teachingIntroSeen")) {
        setShowTeachingIntro(true);
      }
    } else {
      headerTapTimerRef.current = setTimeout(() => { headerTapCountRef.current = 0; }, 5000);
    }
  };
  const [expandedCats, setExpandedCats] = useState(() => { try { const v = JSON.parse(localStorage.getItem("ibadahExpandedCats") || "null"); return Array.isArray(v) ? v : []; } catch { return []; } });
  // single-open category accordion for Ibadah v2 — all collapsed on first open
  const [openCat, setOpenCatRaw] = useState(() => { try { const v = localStorage.getItem("sirat.ibadah.openCat"); return v || null; } catch { return null; } });
  const setOpenCat = (v) => { setOpenCatRaw(v); try { v == null ? localStorage.removeItem("sirat.ibadah.openCat") : localStorage.setItem("sirat.ibadah.openCat", v); } catch {} };
  // Salaah Discover accordion — always starts collapsed on app entry (by user request).
  // State is session-scoped, not persisted.
  const [openMasjidCat, setOpenMasjidCat] = useState(null);
  useEffect(() => { try { localStorage.removeItem("sirat.masjid.openCat"); } catch {} }, []);
  // Prayer detail sheet — opened by tapping the dial glyph on the Salaah hero.
  const [prayerDetailFor, setPrayerDetailFor] = useState(null);
  // Virtue rotation — deterministic per day per prayer so the hadith feels considered, not random.
  const virtueIdxFor = (prayerName) => {
    const info = PRAYER_INFO[prayerName];
    if (!info || !info.virtues || info.virtues.length === 0) return 0;
    const daySeed = Math.floor(Date.now() / 86400000);
    return (daySeed + prayerName.length) % info.virtues.length;
  };
  // Inspiration card (verse/hadith rotation) + share sheet
  // On Fridays, default to the Jumuah card (index 19 in IB_INSPIRATIONS_RAW — 10 verses + 9 hadiths + jumuah).
  const [ibadahInspIdx, setIbadahInspIdx] = useState(() => {
    if (new Date().getDay() === 5) return 19;
    return Math.floor(Date.now() / 86400000) % 10;
  });
  const [ibadahShareOpen, setIbadahShareOpen] = useState(false);
  // Personal Jumu'ah Mubarak message (editable on the Jumuah inspiration card)
  const [jumuahMessage, setJumuahMessage] = useState(() => localStorage.getItem("jumuahMessage") || "");
  // Week the message was saved — used to hint when a message is stale (last week's)
  const [jumuahMessageWeek, setJumuahMessageWeek] = useState(() => parseInt(localStorage.getItem("jumuahMessageWeek") || "0"));
  // Optional recipient name ("For X")
  const [jumuahRecipient, setJumuahRecipient] = useState(() => localStorage.getItem("jumuahRecipient") || "");
  // Total Jumu'ah cards the user has shared via Sirat
  const [jumuahsSentCount, setJumuahsSentCount] = useState(() => parseInt(localStorage.getItem("jumuahsSentCount") || "0"));
  const [greetingStep, setGreetingStep] = useState(0);
  const greetingPlayedRef = useRef(false);
  // Mirath PIN/biometric auth
  const [mirathUnlocked, setMirathUnlocked] = useState(false);
  const mirathKeyRef = useRef(null); // AES-256 key for FIP encryption
  const [mirathPin, setMirathPin] = useState("");
  const [mirathPinConfirm, setMirathPinConfirm] = useState("");
  const [mirathPinError, setMirathPinError] = useState("");
  const [mirathAuthPhase, setMirathAuthPhase] = useState(""); // "" | "setup" | "confirm" | "enter" | "bio"
  const mirathHasPin = !!localStorage.getItem("mirath_pin_hash");
  const mirathHasBio = !!localStorage.getItem("mirath_bio_cred");
  const mirathSaltRef = useRef(() => {
    const stored = localStorage.getItem("mirath_salt");
    if (stored) return Uint8Array.from(atob(stored), c => c.charCodeAt(0));
    const salt = crypto.getRandomValues(new Uint8Array(16));
    localStorage.setItem("mirath_salt", btoa(String.fromCharCode(...salt)));
    return salt;
  });
  const getMirathSalt = () => {
    if (typeof mirathSaltRef.current === "function") mirathSaltRef.current = mirathSaltRef.current();
    return mirathSaltRef.current;
  };
  // Biometric helpers — native uses Capacitor plugin (real FaceID), web uses WebAuthn
  const [mirathBioReady, setMirathBioReady] = useState(false);
  useEffect(() => { isBiometricAvailable().then(setMirathBioReady).catch(() => setMirathBioReady(false)); }, []);
  const mirathBioAvailable = mirathBioReady;
  const mirathRegisterBio = useCallback(async () => {
    if (isNative()) {
      // Native: FaceID/TouchID is device-level, no registration needed — just store a flag
      localStorage.setItem("mirath_bio_cred", "native");
      return true;
    }
    // Web: WebAuthn registration
    try {
      const uid = userId || "sirat-user";
      const challenge = crypto.getRandomValues(new Uint8Array(32));
      const cred = await navigator.credentials.create({ publicKey: {
        challenge, rp: { name: "Sirat" }, user: { id: new TextEncoder().encode(uid), name: uid, displayName: userName || "Sirat User" },
        pubKeyCredParams: [{ alg: -7, type: "public-key" }],
        authenticatorSelection: { authenticatorAttachment: "platform", userVerification: "required" },
        timeout: 60000
      }});
      if (cred) { localStorage.setItem("mirath_bio_cred", cred.id); return true; }
    } catch {}
    return false;
  }, [userId, userName]);
  const mirathAuthBio = useCallback(async () => {
    if (isNative()) {
      // Native: use Capacitor biometric plugin — real FaceID, no passkey dialog
      const res = await authenticateBiometric("Unlock Wasiyyah & Mirath");
      return res?.ok === true;
    }
    // Web: WebAuthn assertion
    try {
      const credId = localStorage.getItem("mirath_bio_cred");
      if (!credId) return false;
      const challenge = crypto.getRandomValues(new Uint8Array(32));
      const assertion = await navigator.credentials.get({ publicKey: {
        challenge, allowCredentials: [{ id: Uint8Array.from(atob(credId.replace(/-/g,'+').replace(/_/g,'/')), c => c.charCodeAt(0)), type: "public-key" }],
        userVerification: "required", timeout: 60000
      }});
      return !!assertion;
    } catch { return false; }
  }, []);
  const mirathUnlock = useCallback(async (pin) => {
    const salt = getMirathSalt();
    const key = await mirathDeriveKey(pin, salt);
    mirathKeyRef.current = key;
    setMirathUnlocked(true);
    setMirathPin(""); setMirathPinConfirm(""); setMirathPinError("");
    // Migrate plaintext FIP data to encrypted (HTTPS only)
    const plain = localStorage.getItem("familyInfoPack");
    if (plain && !localStorage.getItem("familyInfoPack_enc") && key) {
      try { const enc = await mirathEncrypt(key, plain); if (enc) { localStorage.setItem("familyInfoPack_enc", enc); localStorage.removeItem("familyInfoPack"); } } catch {}
    }
  }, []);
  const mirathLock = useCallback(() => {
    setMirathUnlocked(false); mirathKeyRef.current = null;
    setMirathPin(""); setMirathPinConfirm(""); setMirathPinError(""); setMirathAuthPhase("");
  }, []);
  const MIRATH_VIEWS = ["mirath","faraid","islamicwill","witnesswill","familyinfo","sharedfip"];
  // Auto-lock mirath when navigating away
  useEffect(() => {
    if (mirathUnlocked && !MIRATH_VIEWS.includes(toolView)) mirathLock();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolView]);
  const [seenNewFeatures, setSeenNewFeatures] = useState(() => { try { return JSON.parse(localStorage.getItem("seenNewFeatures") || "[]"); } catch { return []; } });
  // Dhikr after salah
  const [showDhikr, setShowDhikr] = useState(false);
  const [dhikrStep, setDhikrStep] = useState(0); // 0=SubhanAllah, 1=Alhamdulillah, 2=Allahu Akbar, 3=Ayatul Kursi
  const [dhikrCount, setDhikrCount] = useState(0);
  const [dhikrPrayer, setDhikrPrayer] = useState("");
  const dhikrTimerRef = useRef(null);
  // Friday salawat counter
  const [fridaySalawat, setFridaySalawat] = useState(() => {
    try { const d = JSON.parse(localStorage.getItem("fridaySalawat") || "{}"); return d.date === localDateStr() ? d.count : 0; } catch { return 0; }
  });
  const [selectedDuaCat, setSelectedDuaCat] = useState(null); // selected dua category id
  const [quranReadNum, setQuranReadNum] = useState(1);
  const [quranReadCache, setQuranReadCache] = useState({});
  const [quranReadLoading, setQuranReadLoading] = useState(false);
  const [quranPaperMode, setQuranPaperMode] = useState(true);
  const [quranCurrentPage, setQuranCurrentPage] = useState(0);
  const [bookFlipDir, setBookFlipDir] = useState(null);      // 'forward' | 'back' | null
  const [bookFlipSnapshot, setBookFlipSnapshot] = useState(null); // page index being turned
  const [bookmarks, setBookmarks] = useState(() => { try { return JSON.parse(localStorage.getItem('quranBookmarks') || '[]'); } catch { return []; } });
  const [quranProgress, setQuranProgress] = useState(() => { try { return JSON.parse(localStorage.getItem('quranProgress') || '{}'); } catch { return {}; } });
  const [quranSearchQuery, setQuranSearchQuery] = useState("");
  const [quranSearchResults, setQuranSearchResults] = useState(null); // null = no search, [] = no results
  const [quranSearchLoading, setQuranSearchLoading] = useState(false);
  const [selectedJuz, setSelectedJuz] = useState(0); // 0 = all, 1-30 = specific juz
  const quranSearchTimer = useRef(null);
  const [quranScrollTarget, setQuranScrollTarget] = useState(null); // ayah number to scroll to after load
  const quranReadCacheRef = useRef({});
  // azkarTab, showTranslit, azkarCounts moved to src/components/Azkar.js
  // namesExpanded moved to src/components/NamesOfAllah.js
  const [surahKey, setSurahKey] = useState("yaseen");
  const [selectedReciter, setSelectedReciter] = useState(() => localStorage.getItem("selectedReciter") || localStorage.getItem("defaultReciterPref") || "ar.husary");
  const [showReciterPicker, setShowReciterPicker] = useState(false);
  const [surahBookmark, setSurahBookmark] = useState(() => {
    try { return JSON.parse(localStorage.getItem("surahBookmark") || "null"); } catch { return null; }
  }); // { key, ayahIdx }
  const [surahCache, setSurahCache] = useState({});       // key → ayahs array
  const [surahLoading, setSurahLoading] = useState(false);
  const [reciterSwitching, setReciterSwitching] = useState(false); // overlay spinner during reciter switch
  const [surahCurrentAyah, setSurahCurrentAyah] = useState(null);
  const surahCurrentAyahRef = useRef(null); // ref mirror for async access
  const [surahIsPlaying, setSurahIsPlaying] = useState(false);
  const [sleepTimerLabel, setSleepTimerLabel] = useState(null); // null | "15m" | "30m" | "45m" | "60m"
  const [showSleepMenu, setShowSleepMenu] = useState(false);
  const sleepTimerRef = useRef(null);
  const sleepTimerEndRef = useRef(null);
  const [sleepTimerRemaining, setSleepTimerRemaining] = useState(null);
  const surahTouchStartX = useRef(null);
  const surahTouchStartY = useRef(null);
  const [surahSearch, setSurahSearch] = useState("");
  const [surahFilter, setSurahFilter] = useState("all"); // "all" | "para30" | "long"
  const [recentSurahs, setRecentSurahs] = useState(() => {
    try { return JSON.parse(localStorage.getItem("recentSurahs") || "[]"); } catch { return []; }
  });
  const playAllQueueRef = useRef(null); // array of surah keys for sequential play, or null

  // Interactive swipe-back for all detail views (tasbeeh / zakat / surah)
  const toolViewPanelRef  = useRef(null);
  const backSwipeStartX   = useRef(null);
  const backSwipeStartY   = useRef(null);
  const backSwipeStartT   = useRef(null); // timestamp for velocity
  const backSwipeMoving   = useRef(false);
  const livemasjidCacheRef = useRef(null); // cached full masjid catalog from livemasjid.com
  const [masjidCatalogTick, setMasjidCatalogTick] = useState(0); // bumped when catalog (re)loads to trigger Live Now re-render
  const siratProSourceRef = useRef(null); // tracks where siratpro was opened from ("settings" or null for ibadah)

  const onBackSwipeStart = (e) => {
    const x = e.touches[0].clientX;
    // Always edge-only: swipe-back only from left 40px so scroll works on all sub-tool views
    if (x > 40) { backSwipeStartX.current = null; return; }
    backSwipeStartX.current = x;
    backSwipeStartY.current = e.touches[0].clientY;
    backSwipeStartT.current = Date.now();
    backSwipeMoving.current = false;
  };

  const onBackSwipeMove = (e) => {
    if (backSwipeStartX.current === null) return;
    const dx = e.touches[0].clientX - backSwipeStartX.current;
    const dy = Math.abs(e.touches[0].clientY - backSwipeStartY.current);
    if (!backSwipeMoving.current) {
      if (dy > 8) { backSwipeStartX.current = null; return; } // vertical — cancel
      if (dx < 6) return;
      backSwipeMoving.current = true;
    }
    if (dx > 0) {
      e.preventDefault();
      const el = toolViewPanelRef.current;
      if (el) {
        el.style.transform = `translateX(${dx}px)`;
        el.style.transition = 'none';
        el.style.boxShadow = `-${Math.min(dx * 0.08, 18)}px 0 28px rgba(0,0,0,${Math.min(dx / 400, 0.35)})`;
      }
    }
  };

  const onBackSwipeEnd = (e) => {
    if (backSwipeStartX.current === null || !backSwipeMoving.current) {
      backSwipeStartX.current = null;
      backSwipeMoving.current = false;
      return;
    }
    const dx       = e.changedTouches[0].clientX - backSwipeStartX.current;
    const dt       = Date.now() - backSwipeStartT.current;
    const velocity = dx / dt; // px per ms
    backSwipeStartX.current = null;
    backSwipeMoving.current = false;
    const el = toolViewPanelRef.current;
    if (!el) return;
    // Commit if dragged past 35% OR flicked fast enough (≥0.4 px/ms with at least 40px travel)
    const commit = dx > window.innerWidth * 0.35 || (velocity >= 0.4 && dx > 40);
    if (commit) {
      hapticSwipeCommit();
      // Determine destination
      let destView = "grid", destTab = null;
      if (toolView === "feedback") { destView = "grid"; destTab = "settings"; }
      else if (toolView === "siratpro" && siratProSourceRef.current === "settings") { destView = "grid"; destTab = "settings"; siratProSourceRef.current = null; }
      else if (toolView === "surah") { destView = "quran"; }
      else if (toolView === "fullquranreading") { destView = "fullquran"; }
      else if (toolView === "duadetail") { destView = "duas"; }
      else if (toolView === "faraid" || toolView === "islamicwill" || toolView === "witnesswill" || toolView === "familyinfo" || toolView === "sharedfip") { destView = "mirath"; }
      else if (toolView === "cardgenerator") { destView = "grid"; }

      // Reset debug witness override when leaving witnesswill
      if (toolView === "witnesswill") setDebugWitnessUserId(null);

      // Sub-view to sub-view: instant switch (no slide — avoids grid flash)
      if (destView !== "grid") {
        el.style.transform = ''; el.style.transition = ''; el.style.boxShadow = 'none';
        setToolView(destView);
        if (destTab) setTab(destTab);
      } else {
        // Sliding back to grid: animate out
        el.style.transform = `translateX(110%)`;
        el.style.transition = 'transform 0.22s ease-out';
        el.style.boxShadow = 'none';
        setTimeout(() => {
          if (destTab) setTab(destTab);
          setToolView("grid");
          if (el) { el.style.transform = ''; el.style.transition = ''; }
        }, 220);
      }
    } else {
      el.style.transform = 'translateX(0)';
      el.style.transition = 'transform 0.32s cubic-bezier(0.34, 1.4, 0.64, 1)';
      el.style.boxShadow = 'none';
    }
  };

  // handleAzkarDecrement, getAzkarList, azkarList, allDone moved to src/components/Azkar.js

  const surahVolumeRef = useRef(1.0);
  const surahSpeedRef = useRef((() => { const s = parseFloat(localStorage.getItem("surahSpeed")); return (s && s > 0) ? s : 1.0; })());
  const [surahSpeed, setSurahSpeed] = useState(() => { const s = parseFloat(localStorage.getItem("surahSpeed")); return (s && s > 0) ? s : 1.0; });
  const [quranFontSize, setQuranFontSize] = useState(() => { const s = parseInt(localStorage.getItem("quranFontSize")); return (s && s > 0) ? s : 28; });
  const [readingStreak, setReadingStreak] = useState(() => { try { return JSON.parse(localStorage.getItem("readingStreak") || '{"count":0,"lastDate":null}'); } catch { return { count: 0, lastDate: null }; } });
  const [salahLog, setSalahLog] = useState(() => { try { return JSON.parse(localStorage.getItem("salahLog") || "{}"); } catch { return {}; } });
  const [salahStreak, setSalahStreak] = useState(() => { try { return JSON.parse(localStorage.getItem("salahStreak") || '{"count":0,"lastDate":null}'); } catch { return { count: 0, lastDate: null }; } });
  const [showSalahToast, setShowSalahToast] = useState(false);
  const [salahToastData, setSalahToastData] = useState(null);
  const [profileCardOpen, setProfileCardOpen] = useState(() => localStorage.getItem("profileCardOpen") !== "false");
  const [comingSoonToast, setComingSoonToast] = useState(null);
  const salahAnimRef = useRef(null); // track which cell is animating
  const surahPausedRef = useRef(false);
  const surahPositionRef = useRef(0);    // last known playback position in seconds
  const surahTotalSecsRef = useRef(900); // current surah known total, for pauseSurah
  const surahKeyRef = useRef("yaseen");
  surahKeyRef.current = surahKey; // keep in sync with state on every render
  const surahCacheRef = useRef({});
  surahCacheRef.current = surahCache; // keep in sync with state on every render
  const scrollContentRef = useRef(null);
  // Reset scroll whenever the visible top-level tab/view changes
  useEffect(() => {
    if (scrollContentRef.current) scrollContentRef.current.scrollTop = 0;
  }, [tab, toolView]);

  /* ─── Scroll-reactive header progress ──────────────────────────────────
     Drives the top sticky headers' background opacity, blur, and hairline
     so the header reads as transparent when content is at the top and
     gains depth once content scrolls behind it (iOS Settings / Mail /
     Music pattern). Values: 0 at scrollTop=0, 1 at scrollTop ≥ 24.     */
  const [headerScroll, setHeaderScroll] = useState(0);
  useEffect(() => {
    const el = scrollContentRef.current;
    if (!el) return;
    const onScroll = () => {
      const p = Math.min(1, Math.max(0, el.scrollTop / 24));
      setHeaderScroll(p);
    };
    onScroll(); // initial
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [tab, toolView]);
  // Reset progress on tab change so a switch doesn't flash the scrolled state
  useEffect(() => { setHeaderScroll(0); }, [tab, toolView]);
  const [surahVolume, setSurahVolume] = useState(1.0);
  const [masjidOrder, setMasjidOrder] = useState(() => {
    const saved = localStorage.getItem("masjidOrder");
    return saved ? JSON.parse(saved) : STATIONS.masjids.map((_, i) => i);
  });
  const [radioOrder, setRadioOrder] = useState(() => {
    const saved = localStorage.getItem("radioOrder");
    return saved ? JSON.parse(saved) : STATIONS.radio.map((_, i) => i);
  });

  const moveStation = (type, index, direction) => {
    const order = type === "masjids" ? [...masjidOrder] : [...radioOrder];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= order.length) return;
    [order[index], order[newIndex]] = [order[newIndex], order[index]];
    if (type === "masjids") { setMasjidOrder(order); localStorage.setItem("masjidOrder", JSON.stringify(order)); }
    else { setRadioOrder(order); localStorage.setItem("radioOrder", JSON.stringify(order)); }
  };

  // Apply/remove light-mode and apple-mode classes on html element and persist preference
  useEffect(() => {
    document.documentElement.classList.toggle("light-mode", !darkMode);
    document.documentElement.classList.remove("apple-mode");
    localStorage.setItem("darkMode", darkMode);
  }, [darkMode]);

  // Settings v2 — Theme Auto: follow OS prefers-color-scheme when user picks Auto
  useEffect(() => {
    if (darkModePref !== "auto") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    setDarkMode(mq.matches);
    const handler = (e) => setDarkMode(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, [darkModePref]);

  // Settings v2 — FaceID app lock.
  //   - On mount and on every return from background, prompt for biometric.
  //   - Success → unlocked (React state), app renders as usual.
  //   - Cancel / fail → gate stays up. User sees an overlay with a Retry button.
  //   - Biometrics genuinely unavailable on this device → auto-disable the
  //     preference (otherwise the user would be permanently locked out).
  const [faceLocked, setFaceLocked] = useState(faceIdLock);
  const [faceAsking, setFaceAsking] = useState(false);
  useEffect(() => {
    // When the pref flips, re-lock (or unlock if user turned it off in Settings)
    setFaceLocked(faceIdLock);
  }, [faceIdLock]);
  const tryFaceUnlock = useCallback(async () => {
    if (faceAsking) return;
    setFaceAsking(true);
    try {
      const avail = await isBiometricAvailable();
      if (!avail) {
        // Device has no biometrics enrolled — can't ever unlock this way.
        // Disable the pref so the user isn't permanently locked out.
        try { localStorage.setItem("faceIdLock", "false"); } catch {}
        setFaceIdLock(false);
        setFaceLocked(false);
        return;
      }
      const res = await authenticateBiometric("Unlock Sirat");
      if (res?.ok) { setFaceLocked(false); return; }
      if (res?.reason === "unavailable") {
        try { localStorage.setItem("faceIdLock", "false"); } catch {}
        setFaceIdLock(false);
        setFaceLocked(false);
        return;
      }
      // fail / cancel → overlay stays up, user taps Retry to try again
    } finally { setFaceAsking(false); }
  }, [faceAsking]);
  useEffect(() => {
    if (!faceIdLock) return;
    tryFaceUnlock();
    const onVis = () => {
      if (document.visibilityState === "visible") {
        setFaceLocked(true); // re-lock on resume
        tryFaceUnlock();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faceIdLock]);

  // QR code generation — client-side via qrcode package (no external API)
  const inviteQrUrl = useCallback((code) => {
    // Returns a promise that resolves to a data URL
    return QRCode.toDataURL(`${window.location.origin}?code=${code}`, {
      width: 200, margin: 2,
      color: { dark: "#ffb300", light: darkMode ? "#07090c" : "#ffffff" },
    });
  }, [darkMode]);

  // Generate QR data URL when a code is created (ref avoids render loops)
  const qrPendingRef = useRef({});
  const ensureQrDataUrl = useCallback((code) => {
    if (!code || qrPendingRef.current[code]) return;
    qrPendingRef.current[code] = true;
    inviteQrUrl(code).then(url => setQrDataUrls(p => ({ ...p, [code]: url }))).catch(() => {});
  }, [inviteQrUrl]);



  // Auto-start trial disabled — app is currently free for all users
  // Re-enable when paywall goes live by uncommenting below:
  // useEffect(() => {
  //   if (userId && !localStorage.getItem("trialStart") && !isPro && !localStorage.getItem("trialExpired")) {
  //     const now = new Date().toISOString();
  //     localStorage.setItem("trialStart", now);
  //     setTrialStart(now);
  //   }
  // }, [userId]);

  // Prevent pinch-zoom on Android (Chrome ignores user-scalable=no since v56)
  useEffect(() => {
    const block = (e) => { if (e.touches && e.touches.length > 1) e.preventDefault(); };
    document.addEventListener("touchstart", block, { passive: false });
    document.addEventListener("touchmove", block, { passive: false });
    return () => {
      document.removeEventListener("touchstart", block);
      document.removeEventListener("touchmove", block);
    };
  }, []);


  // Check user status — runs on mount and whenever app comes back to foreground
  const checkUserStatus = useCallback(async () => {
    const id = localStorage.getItem("azaan-userId");
    if (!id) return;
    try {
      const r = await fetch(`${BACKEND}/api/users/${id}`);
      if (r.status === 404) { localStorage.removeItem("azaan-userId"); localStorage.removeItem("trialStart"); localStorage.removeItem("isPro"); localStorage.removeItem("proExpiry"); clearUserCookie(); clearProCookie(); setUserId(null); setIsPro(false); setTrialStart(null); return; }
      const d = await r.json();
      if (!d) return;
      // Suspended user — show alert and clear session
      if (d?.user?.suspended) {
        setIsSuspended(true);
        setSuspendReason(d.user.suspendReason || "");
        return;
      }
      // Clear suspension if previously suspended but now unsuspended
      setIsSuspended(false);
      if (d?.user?.pendingReset) {
        const keysToKeep = ["azaan-userId", "darkMode"];
        Object.keys(localStorage).forEach(k => { if (!keysToKeep.includes(k)) localStorage.removeItem(k); });
        await fetch(`${BACKEND}/api/users/${id}/confirm-reset`, { method: "POST" }).catch(() => {});
        window.location.reload();
        return;
      }
      // Admin reset to trial — clear pro/trial state, restart fresh trial, keep everything else
      if (d?.user?.pendingResetToTrial) {
        localStorage.removeItem("isPro");
        localStorage.removeItem("proExpiry");
        localStorage.removeItem("trialExpired");
        clearProCookie();
        const now = new Date().toISOString();
        localStorage.setItem("trialStart", now);
        setIsPro(false); setProExpiry(null); setTrialStart(now);
        await fetch(`${BACKEND}/api/users/${id}/confirm-reset`, { method: "POST" }).catch(() => {});
        userFetchDoneRef.current = true;
        return;
      }
      if (proRevokedRef.current) {
        // User just revoked Pro locally — skip restoration, wait for backend to confirm
        if (d?.user?.isPro === false) {
          proRevokedRef.current = false; // backend confirmed revoke, clear guard
        }
        // Either way, don't restore Pro during this cycle
      } else if (d?.user?.isPro) {
        // Check if pro has expired
        if (d.user.proExpiresAt && new Date(d.user.proExpiresAt) <= new Date()) {
          // Expired — revoke local pro state
          localStorage.removeItem("isPro");
          localStorage.removeItem("proExpiry");
          clearProCookie();
          setIsPro(false);
          setProExpiry(null);
        } else {
          const alreadyKnew = localStorage.getItem("isPro") === "true";
          setIsPro(true); localStorage.setItem("isPro", "true"); setProCookie();
          // Sync expiry from backend so admin-granted time-limited pro shows days remaining
          if (d.user.proExpiresAt) {
            const expiryStr = new Date(d.user.proExpiresAt).toISOString();
            localStorage.setItem("proExpiry", expiryStr);
            setProExpiry(expiryStr);
          }
          if (!alreadyKnew) { setProGrantedByAdmin(true); setShowPaymentSuccess(true); setTimeout(() => setShowPaymentSuccess(false), 5000); }
        }
      } else if (d?.user?.isPro === false) {
        // Admin revoked pro — clear local pro state
        localStorage.removeItem("isPro");
        localStorage.removeItem("proExpiry");
        clearProCookie();
        setIsPro(false);
        setProExpiry(null);
      }
      // Backfill name for existing users who registered before localStorage persistence was added
      if (d?.user?.name && !localStorage.getItem("userName")) {
        localStorage.setItem("userName", d.user.name);
        setUserName(d.user.name);
      }
      if (d?.user?.surname && !localStorage.getItem("userSurname")) {
        localStorage.setItem("userSurname", d.user.surname);
        setUserSurname(d.user.surname);
      }
      // Restore support data from backend (source of truth)
      if (d?.user?.supportTotal != null && d.user.supportTotal > parseInt(localStorage.getItem("supportTotal") || "0")) {
        localStorage.setItem("supportTotal", String(d.user.supportTotal));
        supportTotalRef.current = d.user.supportTotal;
      }
      if (d?.user?.supportCount != null && d.user.supportCount > parseInt(localStorage.getItem("supportCount") || "0")) {
        localStorage.setItem("supportCount", String(d.user.supportCount));
      }
      // Sync per-user feature gates from backend
      if (d?.user?.features) setUserFeatures(d.user.features);
      if (d?.user?.hasSupported) localStorage.setItem("hasSupported", "true");
      // Clear any existing trial — trials no longer granted via invite codes
      if (!d?.user?.isPro && localStorage.getItem("trialStart") && !d?.user?.pendingResetToTrial) {
        localStorage.removeItem("trialStart");
        localStorage.removeItem("trialExpired");
        setTrialStart(null);
      }
      // Sync registeredAt from backend (source of truth — fixes unreliable client-side date)
      if (d?.user?.createdAt) localStorage.setItem("registeredAt", d.user.createdAt);
      if (d?.user?.email && !localStorage.getItem("userEmail")) {
        localStorage.setItem("userEmail", d.user.email);
        setUserEmail(d.user.email);
      }
      // Days Active — backend's user.activeDays is populated by /heartbeat ($addToSet of today's SAST date).
      // Merge with anything locally stored so we don't regress on offline-only days.
      if (Array.isArray(d?.user?.activeDays)) {
        try {
          const local = JSON.parse(localStorage.getItem("daysActive") || "[]");
          const merged = Array.from(new Set([...(d.user.activeDays || []), ...local])).sort();
          localStorage.setItem("daysActive", JSON.stringify(merged));
          setDaysActive(merged);
        } catch {}
      }
      // Restore salah log + streak so a fresh device picks up server-synced history.
      // Endpoint may not exist on older backends — silently ignore on error.
      try {
        const sr = await fetch(`${BACKEND}/api/salah/${id}`);
        if (sr.ok) {
          const sd = await sr.json();
          const remoteLog = (sd && typeof sd.log === "object" && sd.log) ? sd.log : null;
          const remoteStreak = sd && sd.streak;
          if (remoteLog) {
            setSalahLog(prevLocal => {
              // Per-day merge: local writes-through, but pull in any remote dates we don't have yet.
              const merged = { ...remoteLog, ...prevLocal };
              localStorage.setItem("salahLog", JSON.stringify(merged));
              return merged;
            });
          }
          if (remoteStreak && typeof remoteStreak.count === "number") {
            // Restore from remote when local streak is empty/older
            try {
              const localStreak = JSON.parse(localStorage.getItem("salahStreak") || '{"count":0,"lastDate":null}');
              const remoteIsBetter =
                !localStreak.lastDate ||
                (remoteStreak.lastDate && remoteStreak.lastDate >= localStreak.lastDate && remoteStreak.count >= localStreak.count);
              if (remoteIsBetter) {
                setSalahStreak(remoteStreak);
                localStorage.setItem("salahStreak", JSON.stringify(remoteStreak));
              }
            } catch {
              setSalahStreak(remoteStreak);
              localStorage.setItem("salahStreak", JSON.stringify(remoteStreak));
            }
          }
        }
      } catch {}
      userFetchDoneRef.current = true;
    } catch { userFetchDoneRef.current = true; }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Show onboarding ceremony once for any logged-in user who hasn't seen it yet.
  // Fires when userId + userName are both available (covers existing users whose
  // name is backfilled from the backend after checkUserStatus runs).
  useEffect(() => {
    if (!userId || !userName || localStorage.getItem("ceremonyShown")) return;
    localStorage.setItem("ceremonyShown", "1");
    ceremonyNameRef.current = userName;
    setShowOnboardingCeremony(true);
    setCeremonyFading(false);
    setTimeout(() => hapticCeremony(), 600);
    setTimeout(() => {
      setCeremonyFading(true);
      setTimeout(() => {
        setShowOnboardingCeremony(false);
        setCeremonyFading(false);
      }, 900);
    }, 3500);
  }, [userId, userName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Verify user on mount + on every foreground resume
  useEffect(() => {
    const id = localStorage.getItem("azaan-userId");
    if (!id) return;
    checkUserStatus();

    // Heartbeat — tell backend user is online (every 2 minutes)
    const sendHeartbeat = () => {
      const today = localDateStr();
      const todayPrayed = [];
      try { const log = JSON.parse(localStorage.getItem("salahLog") || "{}"); const d = log[today] || {}; ["Fajr","Dhuhr","Asr","Maghrib","Isha"].forEach(p => { if (d[p]) todayPrayed.push(p); }); } catch {}
      // Track current feature being used
      const activity = JSON.parse(localStorage.getItem("featureActivity") || "{}");
      fetch(`${BACKEND}/api/users/${id}/heartbeat`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ prayedToday: todayPrayed, activity }) }).catch(() => {});
    };
    sendHeartbeat();
    const heartbeatInterval = setInterval(sendHeartbeat, 120000);

    // Fetch settings from backend (called on load + each time app regains focus)
    const fetchAppSettings = () => {
      // Check Pro expiry client-side (catches mid-session expiration)
      const localExpiry = localStorage.getItem("proExpiry");
      if (localExpiry && new Date(localExpiry) <= new Date()) {
        localStorage.removeItem("proExpiry");
        localStorage.removeItem("isPro");
        clearProCookie();
        setIsPro(false);
        setProExpiry(null);
      }
      fetch(`${BACKEND}/api/settings`)
        .then(r => r.json())
        .then(d => {
          if (typeof d.donationsEnabled === "boolean") setDonationsEnabled(d.donationsEnabled);
          if (typeof d.proPromptEnabled === "boolean") setProPromptEnabled(d.proPromptEnabled);
          if (typeof d.paymentsEnabled === "boolean") setPaymentsEnabled(d.paymentsEnabled);
          if (d.appBanner) setAppBanner(d.appBanner); else setAppBanner("");
          if (d.tabsConfig) { setTabsConfig(d.tabsConfig); localStorage.setItem("tabsConfig", JSON.stringify(d.tabsConfig)); }
          if (d.remoteConfig) {
            // Safety: if backend settings.json fallback doesn't have monetizationMode,
            // inherit from top-level support.enabled so support mode never flickers to trial
            if (!d.remoteConfig.monetizationMode && d.support?.enabled) {
              d.remoteConfig.monetizationMode = "support";
            }
            if (!d.remoteConfig.support && d.support) {
              d.remoteConfig.support = d.support;
            }
            // Normalize global station URLs to relay HTTPS format
            if (d.remoteConfig.globalStations) {
              d.remoteConfig.globalStations = d.remoteConfig.globalStations.map(normalizeGlobalStation);
            }
            setRemoteConfig(d.remoteConfig);
            settingsLoadedRef.current = true;
            localStorage.setItem("remoteConfig", JSON.stringify(d.remoteConfig));
            // Revoke super-user if admin removed this user from superUserIds
            const suIds = d.remoteConfig.superUserIds || [];
            const storedUid = localStorage.getItem("azaan-userId") || localStorage.getItem("userId");
            if (localStorage.getItem("devMode") === "true" && !suIds.includes(storedUid) && !SUPER_USER_IDS.includes(storedUid)) {
              setDevMode(false);
              localStorage.removeItem("devMode");
            }
          }
          // What's New overlay — show if admin has set one and user hasn't seen this version
          if (d.whatsNew && d.whatsNew.id && d.whatsNew.items && d.whatsNew.items.length > 0) {
            const seen = localStorage.getItem("whatsNewSeen");
            if (seen !== d.whatsNew.id) {
              setWhatsNewData(d.whatsNew);
              setTimeout(() => setShowWhatsNew(true), 800);
            }
          }
        })
        .catch(() => {});
    };
    fetchAppSettings();

    // Silently preload Ayah 35 text + default reciter audio for instant first-tap play
    const savedReciter = localStorage.getItem("ahzab35LastReciter") || "ar.alafasy";
    loadAhzab35(savedReciter).catch(() => {});
    // Auto-restore overlay if user was last using Gem Verse (e.g. car scenario)
    if (localStorage.getItem("ahzab35LastOpen")) {
      setAhzab35ReciterId(savedReciter);
      setShowAhzab35(true);
    }

    // Re-check whenever user brings the app back to foreground (covers PWA resume)
    const checkLmBan = async () => {
      if (isNative()) { setLmBanned(false); return; } // Skip in native — ATS blocks non-standard ports
      try {
        const ctrl = new AbortController();
        setTimeout(() => ctrl.abort(), 5000);
        const res = await fetch("https://livemasjid.com:8000/status-json.xsl", { signal: ctrl.signal });
        setLmBanned(res.status === 403);
      } catch (_) { setLmBanned(false); }
    };
    checkLmBan();

    const onVisible = () => { if (document.visibilityState === "visible") { checkUserStatus(); sendHeartbeat(); fetchAppSettings(); checkLmBan(); fetchLiveStatus(); } };
    document.addEventListener("visibilitychange", onVisible);

    // Backfill: sync any existing followed masjids to the backend on load
    const stored = localStorage.getItem("followedMounts");
    const mounts = stored ? JSON.parse(stored) : [];
    const storedNotify = localStorage.getItem("notifyMounts");
    const notify = storedNotify ? JSON.parse(storedNotify) : mounts; // legacy: all followed = notified
    if (mounts.length > 0 && !isNative()) {
      (async () => {
        try {
          let subscription = null;
          if ("serviceWorker" in navigator && "PushManager" in window && !isNative()) {
            const reg = await navigator.serviceWorker.ready;
            subscription = await reg.pushManager.getSubscription();
            // Auto-recover: if notifs were enabled but subscription is gone, re-subscribe silently
            if (!subscription && localStorage.getItem("notifEnabled") === "true" && Notification.permission === "granted") {
              subscription = await registerPush().then(() =>
                reg.pushManager.getSubscription()
              ).catch(() => null);
            }
          }
          await fetch(`${BACKEND}/api/subscribe`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ subscription, followedMounts: mounts, notifyMounts: notify, userId: id })
          });
        } catch (e) {}
      })();
    }
    const settingsInterval = setInterval(fetchAppSettings, 120000); // poll every 2 min
    return () => { document.removeEventListener("visibilitychange", onVisible); clearInterval(heartbeatInterval); clearInterval(settingsInterval); };
  }, [checkUserStatus]);

  // Poll unread feedback count for maintainer badge
  useEffect(() => {
    if (!isMaintainer || !userId) return;
    const pollMaintainerUnread = () => {
      fetch(`${BACKEND}/api/maintainer/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId }),
      })
        .then(r => r.json())
        .then(d => { if (typeof d.unreadCount === "number") setMaintainerUnreadCount(d.unreadCount); })
        .catch(() => {});
    };
    pollMaintainerUnread();
    const id = setInterval(pollMaintainerUnread, 60000);
    return () => clearInterval(id);
  }, [isMaintainer, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Focus Mode safety net: if the current tab is in focusHiddenTabs (set
  // from any of the 70+ setTab call sites), snap to Salaah. Salaah is the
  // only tab that's never hideable, so it's always a safe fallback.
  useEffect(() => {
    if (focusHiddenTabs.includes(tab)) {
      setTab("masjids");
    }
  }, [focusHiddenTabs, tab]);

  // Track feature usage for activity log
  useEffect(() => {
    const feature = tab === "tools" && toolView !== "grid" ? toolView : tab;
    if (feature && feature !== "grid") {
      try {
        const activity = JSON.parse(localStorage.getItem("featureActivity") || "{}");
        activity[feature] = new Date().toISOString();
        localStorage.setItem("featureActivity", JSON.stringify(activity));
      } catch {}
    }
  }, [tab, toolView]);

  // Ibadah greeting typewriter animation (phase 1: typewriter, phase 2: arabic fade)
  useEffect(() => {
    if (tab !== "tools" || toolView !== "grid" || !userName) { greetingPlayedRef.current = false; setGreetingStep(0); return; }
    const hour = new Date().getHours();
    const timeGreet = hour >= 5 && hour < 12 ? "alhamdulillah for this morning" : hour >= 12 && hour < 17 ? "barakallahu feek" : hour >= 17 && hour < 21 ? "subhanallah, what a day" : "alhamdulillah for this day";
    const full = `${userName}, ${timeGreet}`;
    const endStep = full.length + 1; // +1 = arabic fade phase
    if (greetingPlayedRef.current) { setGreetingStep(endStep); return; }
    setGreetingStep(0);
    let i = 0;
    let fadeTimeout = null;
    const iv = setInterval(() => {
      i++;
      setGreetingStep(i);
      if (i >= full.length) {
        clearInterval(iv);
        // after typewriter done, trigger arabic fade after 400ms
        fadeTimeout = setTimeout(() => { setGreetingStep(endStep); greetingPlayedRef.current = true; }, 400);
      }
    }, 40);
    return () => { clearInterval(iv); if (fadeTimeout) clearTimeout(fadeTimeout); };
  }, [tab, toolView, userName]);

  // Close hijri calendar on tab change
  useEffect(() => {
    if (showHijriCalendar && !hijriCalClosing) {
      setHijriCalClosing(true);
      setTimeout(() => { setShowHijriCalendar(false); setHijriCalClosing(false); }, 500);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Cycle Arabic greeting on Masjids tab every 7 seconds. Only ticks while
  // Salaah/Masjids is the active tab — previously this ran forever in the
  // background regardless of tab, burning ~14 setStates/min for nothing.
  useEffect(() => {
    if (tab !== "masjids") return;
    const id = setInterval(() => setGreetingIdx(i => (i + 1) % ARABIC_GREETINGS.length), 7000);
    return () => clearInterval(id);
  }, [tab]);

  // Scroll to currently playing surah when reopening the surah picker
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (toolView === "quran" && surahKey) {
      setTimeout(() => {
        document.getElementById("surah-row-" + surahKey)?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 150);
    }
  }, [toolView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pro Expired detection — auto-navigate to purchase screen when Pro expires
  const prevIsProRef = useRef(isPro);
  useEffect(() => {
    const wasProBefore = prevIsProRef.current;
    prevIsProRef.current = isPro;
    // If user just lost Pro (was true, now false) and has no trial and allFree is off
    if (wasProBefore && !isPro && !isTrial && !remoteConfig.allFree && !isSupportMode) {
      setTab("tools");
      setToolView("siratpro");
      siratProSourceRef.current = null;
    }
  }, [isPro, isTrial, remoteConfig.allFree]); // eslint-disable-line react-hooks/exhaustive-deps

  // Weather for landscape display
  const [weather, setWeather] = useState(null);
  useEffect(() => {
    if (!userLat || !userLng) return;
    const fetchWeather = async () => {
      try {
        const wx = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${userLat}&longitude=${userLng}&current=temperature_2m,weathercode,windspeed_10m&temperature_unit=celsius&wind_speed_unit=kmh`);
        const wxData = await wx.json();
        const c = wxData.current;
        const code = c.weathercode;
        const emoji =
          code === 0 ? "☀️" :
          code <= 3  ? "⛅" :
          code <= 48 ? "🌫️" :
          code <= 55 ? "🌦️" :
          code <= 67 ? "🌧️" :
          code <= 77 ? "❄️" :
          code <= 82 ? "🌦️" :
          code <= 99 ? "⛈️" : "🌡️";
        const desc =
          code === 0 ? "Clear" :
          code <= 2  ? "Partly cloudy" :
          code === 3 ? "Overcast" :
          code <= 48 ? "Foggy" :
          code <= 55 ? "Drizzle" :
          code <= 67 ? "Rain" :
          code <= 77 ? "Snow" :
          code <= 82 ? "Showers" :
          code <= 99 ? "Thunderstorm" : "—";
        setWeather({ temp: Math.round(c.temperature_2m), wind: Math.round(c.windspeed_10m), emoji, desc });
      } catch {}
    };
    fetchWeather();
    const id = setInterval(fetchWeather, 10 * 60 * 1000); // refresh every 10 min
    return () => clearInterval(id);
  }, [userLat, userLng]);

  // Fetch prayer times (using lat/lng — works worldwide)
  // No fallback: when location isn't set, prayer times stay null and the
  // PrayerStrip renders an empty state prompting the user to set their location.
  useEffect(() => {
    if (!userLat || !userLng) {
      setPrayerTimes(null);
      return;
    }
    const lat = userLat;
    const lng = userLng;
    const fetchPrayers = async () => {
      try {
        const now = new Date();
        const dd = String(now.getDate()).padStart(2,"0");
        const mm = String(now.getMonth()+1).padStart(2,"0");
        const yyyy = now.getFullYear();
        const res = await axios.get(
          `https://api.aladhan.com/v1/timings/${dd}-${mm}-${yyyy}?latitude=${lat}&longitude=${lng}&method=3&school=${school}&tune=5,0,0,5,0,4,4,6,0`
        );
        if (res.data.code === 200) {
          setPrayerTimes(res.data.data.timings);
          syncPrayerTimesFromAladhan({ date: `${yyyy}-${mm}-${dd}`, timings: res.data.data.timings });
          siriWritePrayerTimes({ date: `${yyyy}-${mm}-${dd}`, timings: res.data.data.timings, lat, lng });
          const h = res.data.data.date.hijri;
          // If admin has set a full override string, use it directly
          const rc = JSON.parse(localStorage.getItem("remoteConfig") || "{}");
          if (rc.hijriOverride) {
            setHijri(rc.hijriOverride);
          } else {
            const offset = rc.hijriOffset !== undefined ? rc.hijriOffset : 0;
            if (offset === 0) {
              setHijri(`${h.day} ${h.month.en} ${h.year} AH`);
            } else {
              // Apply hijriOffset. We try `Intl.DateTimeFormat` first
              // (instant, no network), then fall back to re-querying
              // Aladhan with the shifted Gregorian date if Intl returns
              // junk on this device.
              //
              // Why the fallback: on some Android browsers (older
              // Chrome, Samsung Internet, MIUI, certain WebViews) the
              // `calendar: 'islamic-umalqura'` option is silently
              // ignored. The year still comes back from the Islamic
              // calendar (e.g., 1447) but the month name falls back to
              // Gregorian ("December"), producing nonsense like
              // "3 December 1447". The `-u-ca-` Unicode locale
              // extension is honoured more reliably than the option
              // form, and we sanity-check the returned month name
              // against the list of Gregorian month names before
              // trusting it.
              let resolved = false;
              try {
                const shifted = new Date();
                shifted.setDate(shifted.getDate() + offset);
                const parts = new Intl.DateTimeFormat('en-u-ca-islamic-umalqura', {
                  day: 'numeric', month: 'long', year: 'numeric'
                }).formatToParts(shifted);
                const iDay = parts.find(p => p.type === 'day')?.value;
                const iMonth = parts.find(p => p.type === 'month')?.value;
                const iYear = parts.find(p => p.type === 'year')?.value;
                const GREGORIAN_MONTHS = new Set(['January','February','March','April','May','June','July','August','September','October','November','December']);
                if (iDay && iMonth && iYear && !GREGORIAN_MONTHS.has(iMonth)) {
                  setHijri(`${iDay} ${iMonth} ${iYear} AH`);
                  resolved = true;
                }
              } catch {}
              if (!resolved) {
                try {
                  const shifted = new Date();
                  shifted.setDate(shifted.getDate() + offset);
                  const dd2 = String(shifted.getDate()).padStart(2,"0");
                  const mm2 = String(shifted.getMonth()+1).padStart(2,"0");
                  const yyyy2 = shifted.getFullYear();
                  const r2 = await axios.get(`https://api.aladhan.com/v1/gToH/${dd2}-${mm2}-${yyyy2}`);
                  if (r2.data?.code === 200) {
                    const sh = r2.data.data.hijri;
                    setHijri(`${sh.day} ${sh.month.en} ${sh.year} AH`);
                    resolved = true;
                  }
                } catch {}
              }
              if (!resolved) setHijri(`${h.day} ${h.month.en} ${h.year} AH`);
            }
          }
        }
      } catch (e) {}
    };
    fetchPrayers();
  }, [userLat, userLng, school]);

  // Fetch stations from backend.
  // NB: there used to be a probeRadioStreams() pass here that opened a GET on
  // every radio station's live audio URL on each launch. Its results were
  // immediately overwritten by the "radio streams are always live" effect
  // below, so it only ever cost a wasted listener slot on each station's
  // Icecast server (per user, per app open) — a real problem for small
  // community stations with a listener cap. Removed.
  const fetchStations = useCallback(() => {
    // The cached/bundled list is already on screen, so this only refreshes it.
    // 30s timeout covers a Render cold start; on failure retry a few times.
    const load = (tries) => {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 30000);
      return fetch(`${BACKEND}/api/stations`, { signal: ctrl.signal })
        .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
        .then(d => {
          if (!d || !Array.isArray(d.radio) || !Array.isArray(d.masjids)) throw new Error("bad stations payload");
          setStations(d);
          try { localStorage.setItem("stationsCache", JSON.stringify(d)); } catch {}
        })
        .catch(() => { if (tries > 0) setTimeout(() => load(tries - 1), 15000); })
        .finally(() => clearTimeout(t));
    };
    return load(2);
  }, []);
  useEffect(() => { fetchStations(); }, [fetchStations]);

  // Eagerly load LiveMasjid catalog on mount so the Salaah tab's "Live Now"
  // section can show every currently-live masjid (not just followed/featured).
  // Prefers localStorage cache (24h TTL), falls back to backend proxy.
  useEffect(() => {
    const LM_CACHE_KEY = "lm_catalog";
    const LM_CACHE_TTL = 24 * 60 * 60 * 1000;
    if (livemasjidCacheRef.current && livemasjidCacheRef.current.length > 0) return;
    try {
      const stored = localStorage.getItem(LM_CACHE_KEY);
      if (stored) {
        const { ts, mounts } = JSON.parse(stored);
        if (mounts && mounts.length > 0 && Date.now() - ts < LM_CACHE_TTL) {
          livemasjidCacheRef.current = mounts;
          setMasjidCatalogTick(t => t + 1);
          return;
        }
      }
    } catch (_) {}
    // Background fetch — never awaited, failures are silent
    let aborted = false;
    (async () => {
      try {
        const r = await fetch(`${BACKEND}/api/livemasjid-catalog`);
        if (aborted) return;
        const data = await r.json();
        const fetched = Array.isArray(data) ? data : (data.mounts || data.data || []);
        if (fetched.length > 0) {
          livemasjidCacheRef.current = fetched;
          try { localStorage.setItem(LM_CACHE_KEY, JSON.stringify({ ts: Date.now(), mounts: fetched })); } catch (_) {}
          setMasjidCatalogTick(t => t + 1);
        }
      } catch (_) {}
    })();
    return () => { aborted = true; };
  }, []);

  // Fetch now-playing programme metadata via backend proxy (avoids CORS)
  useEffect(() => {
    if (tab !== "radio" || !stations?.radio) return;
    const fetchMeta = async () => {
      try {
        const r = await fetch(`${BACKEND}/api/radio-nowplaying`);
        if (!r.ok) return;
        const d = await r.json();
        setRadioMeta(prev => {
          const next = { ...prev };
          for (const s of stations.radio) {
            // Both stations now live on iono.fm, so match the channel id, not
            // the host — "iono.fm" alone would label Al Ansaar with CII's show.
            const isAnsaar = s.url.includes("al-ansaar") || /\/x(?:ice|hls)\/467_/.test(s.url);
            const isCii = /\/x(?:ice|hls)\/109_/.test(s.url);
            if (isAnsaar && d["al-ansaar"]) next[s.url] = d["al-ansaar"];
            else if (isCii && d["cii"]) next[s.url] = d["cii"];
          }
          return next;
        });
      } catch {}
    };
    fetchMeta();
    const id = setInterval(fetchMeta, 30000);
    return () => clearInterval(id);
  }, [tab, stations]); // eslint-disable-line react-hooks/exhaustive-deps

  // On every app open AND every userId change (eg right after Sign in with
  // Apple): re-sync push subscription to backend. Triggers saveSub() cleanup
  // backend-side, which removes any duplicate subscription records from old
  // accounts/sessions on the same device. Critically, the userId dep means
  // newly-registered users get registerNativePush called immediately —
  // without that, requestPermissions() never fires and iOS never prompts
  // for push permission.
  useEffect(() => {
    const uid = localStorage.getItem("azaan-userId");
    if (!uid) return;
    if (isNative()) {
      // Native: register APNs token and sync follows via registerNativePush
      (async () => {
        const lat = parseFloat(localStorage.getItem("userLat") || "") || null;
        const lng = parseFloat(localStorage.getItem("userLng") || "") || null;
        const _dne = localStorage.getItem("dailyNotifEnabled") !== "false";
        const follows = JSON.parse(localStorage.getItem("followedMounts") || "[]");
        const notify = JSON.parse(localStorage.getItem("notifyMounts") || "null") ?? follows;
        await registerNativePush(uid, follows, notify, {
          lat, lng, school: parseInt(localStorage.getItem("school") || "1"), dailyNotifEnabled: _dne,
          dailyNameEnabled: localStorage.getItem("dailyNameEnabled") === "true",
          dailyNameHour: parseInt(localStorage.getItem("dailyNameHour") || "8"),
          prayerReminderEnabled: localStorage.getItem("prayerReminderEnabled") === "true",
          azaanPrayers: JSON.parse(localStorage.getItem("azaanPrayers") || "[]"),
        });
      })();
      return;
    }
    (async () => {
      try {
        let subscription = null;
        if ("serviceWorker" in navigator && "PushManager" in window) {
          const reg = await navigator.serviceWorker.ready;
          subscription = await reg.pushManager.getSubscription();
        }
        const lat = parseFloat(localStorage.getItem("userLat") || "") || null;
        const lng = parseFloat(localStorage.getItem("userLng") || "") || null;
        const _dne = localStorage.getItem("dailyNotifEnabled") !== "false";
        await fetch(`${BACKEND}/api/subscribe`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            subscription,
            followedMounts: JSON.parse(localStorage.getItem("followedMounts") || "[]"),
            notifyMounts: JSON.parse(localStorage.getItem("notifyMounts") || "null"),
            userId: uid,
            lat, lng,
            school: parseInt(localStorage.getItem("school") || "1"),
            dailyNotifEnabled: _dne,
            dailyNameEnabled: localStorage.getItem("dailyNameEnabled") === "true",
            dailyNameHour: parseInt(localStorage.getItem("dailyNameHour") || "8"),
            prayerReminderEnabled: localStorage.getItem("prayerReminderEnabled") === "true"
          })
        });
      } catch {}
    })();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-play station when app opened from a notification
  useEffect(() => {
    if (!autoPlayMount) return;
    const isFullUrl = autoPlayMount.startsWith('http');
    const url = isFullUrl ? autoPlayMount : `https://relay.livemasjid.com:8443/${autoPlayMount}`;
    const station = { url, name: autoPlayMount };
    setTab('masjids');
    setPlaying(station);
    setPlayStatus('buffering');
    playStatusRef.current = 'buffering';
    const audio = getAudio();
    audio.pause();
    const isIPad = navigator.userAgent.includes("iPad") || (navigator.userAgent.includes("Macintosh") && navigator.maxTouchPoints > 1);
    const streamUrl = isIPad ? url.replace(/\/?$/, ".mp3") : url;
    audio.src = streamUrl;
    audio.load();
    let attempt = 0;
    const tryPlay = () => {
      audio.play()
        .then(() => {
          setAutoplayBlocked(false); setPlayStatus('playing'); playStatusRef.current = 'playing';
          requestWakeLock();
          if ("mediaSession" in navigator) {
            navigator.mediaSession.metadata = new MediaMetadata({ title: station.name, artist: "Sirat", album: "Live Stream", artwork: [{ src: "/logo192.png", sizes: "192x192", type: "image/png" }] });
            navigator.mediaSession.playbackState = "playing";
            navigator.mediaSession.setActionHandler("pause", () => { audio.pause(); releaseWakeLock(); setPlayStatus("stopped"); setPlaying(null); });
            navigator.mediaSession.setActionHandler("stop", () => { audio.pause(); releaseWakeLock(); setPlayStatus("stopped"); setPlaying(null); });
          }
        })
        .catch(() => {
          if (attempt < 2) { attempt++; setTimeout(tryPlay, 1000); }
          else { setAutoplayBlocked(true); setPlayStatus('error'); playStatusRef.current = 'error'; }
        });
    };
    audio.onended = () => { setPlaying(null); setPlayStatus('stopped'); playStatusRef.current = 'stopped'; if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "none"; };
    audio.onerror = () => { setAutoplayBlocked(true); setPlayStatus('error'); playStatusRef.current = 'error'; };
    tryPlay();
    window.history.replaceState({}, '', '/');
  }, [autoPlayMount]); // eslint-disable-line react-hooks/exhaustive-deps

  // Once stations data loads, update the display name for notification-opened streams
  useEffect(() => {
    if (!autoPlayMount || !stations || !playing) return;
    const allSt = [...(stations.masjids||[]), ...(stations.radio||[])];
    const found = allSt.find(s => s.url === playing.url || s.url.includes(autoPlayMount));
    if (found && found.name !== playing.name) setPlaying(prev => prev ? { ...prev, name: found.name } : null);
  }, [stations]); // eslint-disable-line react-hooks/exhaustive-deps

  // Landscape orientation detection
  useEffect(() => {
    const mq = window.matchMedia('(orientation: landscape)');
    const handler = (e) => { setIsLandscape(e.matches); if (!e.matches) setLandscapeWallDismissed(false); };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Scroll to a specific ayah after Quran surah loads
  useEffect(() => {
    if (!quranScrollTarget || !quranReadCache[quranReadNum]?.length) return;
    const timer = setTimeout(() => {
      const el = document.getElementById(`q-ayah-${quranScrollTarget}`);
      if (el) el.scrollIntoView({ block: 'start', behavior: 'instant' });
      setQuranScrollTarget(null);
    }, 150);
    return () => clearTimeout(timer);
  }, [quranScrollTarget, quranReadCache, quranReadNum]);

  // Load masjid names database
  useEffect(() => {
    fetch(`${BACKEND}/api/masjid-names`)
      .then(r => r.json())
      .then(names => setMasjidNames(names))
      .catch(() => {});
  }, []);;

  // Stream status — backend polls Icecast every 30s; we fetch from backend to avoid CORS issues
  const fetchLiveStatus = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND}/api/live-status`);
      if (!res.ok) return;
      // data = { mountName: true/false } keyed by normalized mount (no .mp3, no moz/moc prefix)
      const data = await res.json();
      const liveMounts = new Set(
        Object.entries(data).filter(([, live]) => live).map(([m]) => m + ".mp3")
      );
      liveMountsRef.current = liveMounts;
      const results = {};
      const allUrls = new Set();
      (followedMounts || []).forEach(mount => {
        allUrls.add(followedUrls[mount] || `https://relay.livemasjid.com:8443/${mount}`);
      });
      (stations?.masjids || []).forEach(s => { if (s.url) allUrls.add(s.url); });
      (remoteConfig.globalStations || []).forEach(gs => { if (gs.url) allUrls.add(gs.url); });
      allUrls.forEach(url => {
        if (!url.includes("livemasjid.com")) return;
        // Extract mount name from any livemasjid URL format (relay or direct)
        const mount = (url.split("/").pop() || "").replace(/\.mp3$/i, "").replace(/^(moz|moc|moms)/, "");
        results[url] = mount ? (data[mount] === true) : false;
      });
      setStreamStatus(prev => ({ ...prev, ...results }));
    } catch (e) { /* backend unavailable — leave status unchanged */ }
  }, [stations, followedMounts, followedUrls]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Radio streams are always live — mark them true once
    if (stations?.radio?.length) {
      const results = {};
      stations.radio.forEach(s => { results[s.url] = true; });
      setStreamStatus(prev => ({ ...prev, ...results }));
    }

    fetchLiveStatus();
    const masjidId = setInterval(fetchLiveStatus, 15000);

    return () => { clearInterval(masjidId); };
  }, [fetchLiveStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-play azaan when a notified masjid goes live; auto-stop when offline
  useEffect(() => {
    // First run: snapshot current status but don't auto-play
    // Second run: we now have real data — snapshot again to establish baseline
    // This prevents auto-playing streams that were already live when app opened
    if (!autoPlayInitRef.current) {
      autoPlayInitRef.current = 1; // first run
      const snapshot = {};
      for (const mount of effectiveNotify) {
        const url = `https://relay.livemasjid.com:8443/${mount}`;
        snapshot[mount] = getStreamStatus(url);
      }
      prevStreamStatusRef.current = snapshot;
      return;
    }
    if (autoPlayInitRef.current === 1) {
      // Second run — real status data arrived. Snapshot as baseline, don't act.
      autoPlayInitRef.current = 2;
      const snapshot = {};
      for (const mount of effectiveNotify) {
        const url = `https://relay.livemasjid.com:8443/${mount}`;
        snapshot[mount] = getStreamStatus(url);
      }
      prevStreamStatusRef.current = snapshot;
      return;
    }

    const prev = prevStreamStatusRef.current;

    for (const mount of effectiveNotify) {
      const url = `https://relay.livemasjid.com:8443/${mount}`;
      const wasLive = prev[mount];
      const nowLive = getStreamStatus(url);

      // Offline → Live: auto-play
      // Plays even if user is listening to something else — azaan takes priority
      if (nowLive === true && wasLive === false) {
        if (playing?.url === url && playStatus === 'playing') continue; // already playing this
        hapticSuccess();
        playStation({ url, name: followedNames[mount] || mount });
      }

      // Live → Offline: stop if currently playing this mount
      if (nowLive === false && wasLive === true) {
        if (playing && playing.url === url) {
          const audio = getAudio();
          audio.pause();
          setPlaying(null);
          setPlayStatus("stopped");
        }
      }
    }

    // Snapshot current status for next comparison
    const snapshot = {};
    for (const mount of effectiveNotify) {
      const url = `https://relay.livemasjid.com:8443/${mount}`;
      snapshot[mount] = getStreamStatus(url);
    }
    prevStreamStatusRef.current = snapshot;
  }, [streamStatus, effectiveNotify]);

  // Deep link: ?khatm=KHATM-XXXX → open Khatm join flow
  useEffect(() => {
    if (khatmJoinCode && userId) {
      setTab("tools");
      setToolView("khatm");
    }
  }, [khatmJoinCode, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep link: ?witness=WILL_ID → open witness will view
  useEffect(() => {
    if (witnessWillId && userId) {
      setTab("tools");
      setToolView("witnesswill");
    }
  }, [witnessWillId, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep link: ?fipshare=OWNER_ID → open shared Family Info Pack viewer
  useEffect(() => {
    if (fipShareOwnerId && userId) {
      setTab("tools");
      setToolView("sharedfip");
    }
  }, [fipShareOwnerId, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch shared FIP inbox on load
  useEffect(() => {
    if (!userId) return;
    fetch(`${BACKEND}/api/familypack/shared-with-me?userId=${userId}`)
      .then(r => r.json()).then(j => setSharedFipInbox(j.shares || [])).catch(() => {});
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // If active tab is disabled by admin, redirect to first enabled tab
  useEffect(() => {
    const cfg = tabsConfig[tab];
    const isSuperUser = SUPER_USER_IDS.includes(userId) || (remoteConfig.superUserIds || []).includes(userId);
    const hasTabAccess = (remoteConfig.tabAccessIds?.[tab] || []).includes(userId);
    if (cfg?.enabled === false && !isSuperUser && !hasTabAccess) {
      const firstEnabled = ["masjids","radio","tools","settings",...(isMaintainer?["maintainer"]:[])].find(id => tabsConfig[id]?.enabled !== false);
      if (firstEnabled) setTab(firstEnabled);
    }
  }, [tabsConfig, tab, remoteConfig.superUserIds, remoteConfig.tabAccessIds, userId]);

  // Countdown timer
  useEffect(() => {
    const tick = () => {
      if (!prayerTimes) return;
      const now = new Date();
      const nowSec = now.getHours()*3600 + now.getMinutes()*60 + now.getSeconds();
      const nowMin = now.getHours()*60 + now.getMinutes();
      let nextIdx = -1;
      for (let i = 0; i < PRAYER_NAMES.length; i++) {
        const t = to24min(prayerTimes[PRAYER_NAMES[i]]);
        if (t > nowMin) { nextIdx = i; break; }
      }
      if (nextIdx === -1) nextIdx = 0;
      setActiveIdx(nextIdx);
      setNextPrayer(PRAYER_NAMES[nextIdx]);
      const nextMin = to24min(prayerTimes[PRAYER_NAMES[nextIdx]]);
      let diff = nextMin * 60 - nowSec;
      if (diff < 0) diff += 86400;
      setCountdown(formatTime(diff));

      // Azaan alerts: auto-play azaan clip at prayer time (foreground only)
      const today = localDateStr();
      const azPrayers = azaanPrayers;
      if (azPrayers.length > 0) {
        for (const prayer of TRACKABLE_PRAYERS) {
          if (!azPrayers.includes(prayer)) continue;
          const pMin = to24min(prayerTimes[prayer]);
          if (nowMin === pMin && !azaanPlayedRef.current[today + ":" + prayer]) {
            azaanPlayedRef.current[today + ":" + prayer] = true;
            console.log(`[AZAAN] Triggered! ${prayer} at ${prayerTimes[prayer]}, nowMin=${nowMin}, pMin=${pMin}, playing=${!!playingRef.current}`);
            // Don't interrupt live masjid/radio stream
            if (!playingRef.current || playStatusRef.current !== "playing") {
              playAzaanClip(prayer);
            } else {
              console.log("[AZAAN] Skipped — audio already playing");
            }
          }
        }
        // Reset played ref at midnight (new day)
        const keys = Object.keys(azaanPlayedRef.current);
        if (keys.length > 0 && !keys[0].startsWith(today)) {
          azaanPlayedRef.current = {};
        }
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [prayerTimes, azaanPrayers]); // eslint-disable-line react-hooks/exhaustive-deps

  // Audio playback
  const playStation = useCallback((station) => {
    hapticPlayPause();
    const audio = getAudio();
    if (playing && playing.url === station.url && playStatus === "playing") {
      playAttemptRef.current++;
      audio.pause();
      setPlaying(null);
      setPlayStatus("stopped");
      if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "paused";
      return;
    }
    // Stop surah if playing — mutual exclusivity
    yasinBufs.forEach(b => { if (b) { b.ontimeupdate = null; b.onended = null; b.onerror = null; b.pause(); } });
    setSurahIsPlaying(false); setSurahCurrentAyah(null); surahCurrentAyahRef.current = null;
    audio.pause();
    setPlaying(station);
    setPlayStatus("buffering");
    playStatusRef.current = "buffering";
    trackEvent("stream_play", { name: station.name, url: station.url });
    setAutoplayBlocked(false);
    try { localStorage.setItem("lastStation", JSON.stringify({url:station.url,name:station.name})); } catch(e) {}

    const candidates = streamCandidates(station.url);
    // A single URL keeps the original 45s grace period — international streams
    // (e.g. Makkah from SA) can genuinely take 30+ seconds to buffer. When
    // there are alternates to fall back on, give each one a shorter window so
    // the whole chain still resolves well inside that budget.
    const perAttemptMs = candidates.length > 1 ? 15000 : 45000;

    const attempt = (idx, parentToken) => {
      if (parentToken !== undefined && parentToken !== playAttemptRef.current) return;
      if (idx >= candidates.length) {
        // Every mount is unreachable. Surface it as an error ("Offline" /
        // "Tap to retry") rather than silently dropping back to idle, which
        // read to users as "connecting forever".
        setDebugPlayLog(`✗ all ${candidates.length} stream URL(s) failed`);
        // Retire this attempt and detach the handlers BEFORE clearing src —
        // clearing it can itself raise an error event, which would otherwise
        // re-enter here and loop.
        playAttemptRef.current++;
        if (bufferingTimeoutRef.current) clearTimeout(bufferingTimeoutRef.current);
        audio.onerror = null;
        audio.onended = null;
        audio.pause();
        audio.src = "";
        setPlayStatus("error");
        playStatusRef.current = "error";
        return;
      }
      const myToken = ++playAttemptRef.current;
      const alive = () => myToken === playAttemptRef.current;
      const streamUrl = candidates[idx];
      setDebugPlayLog(`try ${idx + 1}/${candidates.length} | url=${streamUrl}`);

      if (bufferingTimeoutRef.current) clearTimeout(bufferingTimeoutRef.current);
      bufferingTimeoutRef.current = setTimeout(() => {
        if (!alive() || playStatusRef.current !== "buffering") return;
        // Stalled with no error event — the classic dead-mount hang.
        audio.pause();
        attempt(idx + 1, myToken);
      }, perAttemptMs);

      audio.onerror = () => {
        if (!alive()) return;
        // Swapping src aborts the previous load; ignore anything that isn't
        // about the URL this attempt is actually waiting on. (Worst case the
        // per-attempt timeout still advances the chain.)
        if (audio.src !== streamUrl) return;
        setDebugPlayLog(`✗ code=${audio.error?.code} | src=${audio.src}`);
        if (bufferingTimeoutRef.current) clearTimeout(bufferingTimeoutRef.current);
        attempt(idx + 1, myToken);
      };
      audio.onended = () => {
        if (!alive()) return;
        if (bufferingTimeoutRef.current) clearTimeout(bufferingTimeoutRef.current);
        setPlaying(null); setPlayStatus("stopped"); playStatusRef.current = "stopped";
        if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "none";
      };

      audio.src = streamUrl;
      audio.play()
        .then(() => {
          if (!alive()) return;
          setDebugPlayLog(`✓ playing: ${streamUrl}`);
          if (bufferingTimeoutRef.current) clearTimeout(bufferingTimeoutRef.current);
          setPlayStatus("playing");
          playStatusRef.current = "playing";
          requestWakeLock();
          if ("mediaSession" in navigator) {
            navigator.mediaSession.metadata = new MediaMetadata({
              title: station.name || "Live Radio", artist: "Sirat", album: "Live Stream",
              artwork: [{ src: "/logo192.png", sizes: "192x192", type: "image/png" }]
            });
            navigator.mediaSession.playbackState = "playing";
            navigator.mediaSession.setActionHandler("play", () => { audio.play(); requestWakeLock(); setPlayStatus("playing"); });
            navigator.mediaSession.setActionHandler("pause", () => { audio.pause(); releaseWakeLock(); setPlayStatus("stopped"); setPlaying(null); });
            navigator.mediaSession.setActionHandler("stop", () => { audio.pause(); releaseWakeLock(); setPlayStatus("stopped"); setPlaying(null); });
          }
        })
        .catch((e) => {
          if (!alive()) return;
          setDebugPlayLog(`✗ play() error: ${e.name}: ${e.message} | src=${streamUrl}`);
          if (bufferingTimeoutRef.current) clearTimeout(bufferingTimeoutRef.current);
          // A blocked autoplay says nothing about the stream — don't burn the
          // fallbacks on it, just ask the user to tap again.
          if (e && e.name === "NotAllowedError") {
            setAutoplayBlocked(true);
            setPlayStatus("error");
            playStatusRef.current = "error";
            return;
          }
          attempt(idx + 1, myToken);
        });
    };
    attempt(0);
  }, [playing, playStatus]);

  // SW → app: play a station when notification is tapped and app is already open
  const _swPlayRef = useRef(null);
  _swPlayRef.current = (mount) => {
    const allSt = [...(stations?.masjids || []), ...(stations?.radio || [])];
    const isUrl = mount.startsWith('http');
    const station = (isUrl ? allSt.find(s => s.url === mount) : allSt.find(s => s.url.includes(mount)))
      || { name: mount, url: isUrl ? mount : `https://relay.livemasjid.com:8443/${mount}` };
    setTab(isUrl && stations?.radio?.some(s => s.url === mount) ? 'radio' : 'masjids');
    // If already playing this station, just switch to the tab (don't toggle off)
    if (playing && playing.url === station.url && (playStatus === "playing" || playStatus === "buffering")) return;
    playStation(station);
    // Auto-retry once if play() was blocked (iOS notification gesture context may be delayed)
    setTimeout(() => {
      if (playStatusRef.current === "error") playStation(station);
    }, 1500);
  };
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handler = (e) => {
      if (e.data?.type === 'PLAY_MOUNT' && e.data.mount) _swPlayRef.current(e.data.mount);
      if (e.data?.type === 'PLAY_AZAAN' && e.data.prayer) playAzaanClip(e.data.prayer);
      if (e.data?.type === 'OPEN_FEEDBACK') { setTab("tools"); setToolView("feedback"); fetchFeedbackHistory(); }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    // Fallback: on some devices (especially iOS PWA) the page may not be
    // controlled by the SW yet, so client.postMessage() arrives on window
    window.addEventListener('message', handler);
    return () => { navigator.serviceWorker.removeEventListener('message', handler); window.removeEventListener('message', handler); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Native push listeners (Capacitor only): foreground push + notification tap
  useEffect(() => {
    if (isNative()) {
      setupNativePushListeners(
        (mount) => _swPlayRef.current && _swPlayRef.current(mount),
        (prayer) => playAzaanClip(prayer || "Fajr")
      );
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Live Activities (iOS native only) ─────────────────────────────────────
  // At most ONE activity is alive at any time. Priority order:
  //   nextPrayer > jumuah > tahajjud
  // This prevents the lock-screen "two cards stacked" bug where Tahajjud sat
  // behind the next-prayer card late at night.
  //
  // Visibility for `nextPrayer` is gated by `liveActivityWindow`:
  //   "always" — always when there's a next prayer
  //   "60min" / "30min" / "10min" / "5min" — only within N minutes of the prayer
  //   "off"   — never (also tears down jumuah/tahajjud)
  //
  // Morning override: between Fajr and Dhuhr, nextPrayer is always shown so the
  // user can see today's full prayer schedule (regardless of window) — unless
  // the user picked "off".
  //
  // Post-time grace: for 30 minutes after a prayer's time, the *current* prayer
  // stays in the activity (urgency state — handled in Swift), then transitions
  // to the next prayer.
  // Install the iOS pushTokenUpdate listener once. Each Activity.request() emits
  // a token; the listener POSTs it to azaan-backend, which schedules a
  // `liveactivity` end-event APNs push at endsAt+30min. This is what makes the
  // lock-screen card disappear cleanly after the salaah time passes — without
  // it, the card freezes on the muted ring until the user re-opens the app.
  useEffect(() => {
    installLiveActivityPushTokenListener(BACKEND);
  }, []);

  useEffect(() => {
    if (!window.Capacitor?.isNativePlatform?.()) return;
    if (!prayerTimes) return;
    const POST_TIME_GRACE_MIN = 30;
    const WINDOW_MAP = { "5min": 5, "10min": 10, "30min": 30, "60min": 60, off: -1, always: Infinity };

    // computeLiveNextPrayer skips past prayers — but we want a 5-min grace where
    // the *just-passed* prayer is still the "current" relevant one. This helper
    // returns whichever prayer is happening or coming next.
    const pickRelevantPrayer = (now) => {
      const order = ["Fajr", "Dhuhr", "Asr", "Maghrib", "Isha"];
      const today = order
        .map(name => ({ name, time: todayAt(prayerTimes[name]) }))
        .filter(p => p.time);
      // 1. Inside the post-time grace window of any prayer that already passed today
      const inGrace = today.find(p => now >= p.time && now < p.time.getTime() + POST_TIME_GRACE_MIN * 60000);
      if (inGrace) return inGrace;
      // 2. Otherwise, fall back to the existing helper (next future prayer, or tomorrow's Fajr)
      return computeLiveNextPrayer(prayerTimes);
    };

    const tick = () => {
      const now = new Date();
      const allPrayers = computeLiveAllPrayers(prayerTimes, salahLog);
      const prayedSig = allPrayers.map(p => p.prayed ? "1" : "0").join("");
      const windowMins = WINDOW_MAP[liveActivityWindow] ?? Infinity;
      const off = liveActivityWindow === "off";

      // Morning override: between Fajr and Dhuhr today, nextPrayer is always shown
      const fajrToday = todayAt(prayerTimes.Fajr);
      const dhuhrToday = todayAt(prayerTimes.Dhuhr);
      const inMorningOverride = !!(fajrToday && dhuhrToday && now >= fajrToday && now < dhuhrToday);

      // ── Compute eligibility for each kind ────────────────────────────────
      const np = pickRelevantPrayer(now);
      const minsUntilNp = np ? Math.max(0, (np.time.getTime() - now.getTime()) / 60000) : Infinity;
      const wantNextPrayer = !off && !!np && (inMorningOverride || windowMins >= 0 && minsUntilNp <= windowMins);

      const jumTime = computeLiveJumuah(prayerTimes);
      const wantJumuah = !off && !!jumTime && jumTime > now;

      const tah = computeLiveTahajjud(prayerTimes);
      const inEveningWindow = now.getHours() >= 21 || now.getHours() < 5;
      const wantTahajjud = !off && !!tah && inEveningWindow && now < tah.end;

      // ── Pick winner (priority: nextPrayer > jumuah > tahajjud) ───────────
      let winner = null;
      let payload = null;
      let sig = null;

      if (wantNextPrayer) {
        winner = "nextPrayer";
        // Include the pre/post-time bucket in the sig. The widget's
        // TimelineView is supposed to flip to the urgent post-time UI on
        // its own at endsAt — but iOS does NOT reliably tick TimelineView
        // for Live Activities while the phone is locked. Without a fresh
        // push from JS, the lock-screen card stays in pre-time mode (the
        // Mark Prayed button never appears, countdownLabel never flips
        // to PRAY NOW). Splitting the sig forces tick() to push a new
        // activity at the boundary so iOS re-renders.
        const inPostTime = now >= np.time && now.getTime() < np.time.getTime() + POST_TIME_GRACE_MIN * 60000;
        sig = `${np.name}:${np.time.getTime()}:${prayedSig}:${inPostTime ? "post" : "pre"}`;
        payload = {
          kind: "nextPrayer",
          primaryName: np.name,
          startsAt: now,
          endsAt: np.time,
          subtitle: formatLiveTime(np.time),
          countdownLabel: inPostTime ? "PRAY NOW" : "until adhan",
          allPrayers
        };
      } else if (wantJumuah) {
        winner = "jumuah";
        sig = String(jumTime.getTime());
        payload = {
          kind: "jumuah",
          primaryName: "Jumu'ah",
          startsAt: now,
          endsAt: jumTime,
          subtitle: formatLiveTime(jumTime),
          countdownLabel: "until khutbah",
          allPrayers: []
        };
      } else if (wantTahajjud) {
        winner = "tahajjud";
        const inProgress = now >= tah.start;
        sig = `${tah.end.getTime()}:${inProgress}`;
        payload = {
          kind: "tahajjud",
          primaryName: "Last Third",
          startsAt: now,
          endsAt: inProgress ? tah.end : tah.start,
          subtitle: inProgress ? `Until Fajr ${formatLiveTime(tah.end)}` : `Begins ${formatLiveTime(tah.start)}`,
          countdownLabel: inProgress ? "until fajr" : "until tahajjud",
          allPrayers: []
        };
      }

      // ── End every non-winner kind, unconditionally ───────────────────────
      // The ref-gated check that used to live here would skip endLiveActivity
      // when `lastLiveActivitySigRef.current[kind]` was falsy — fine for the
      // effect re-run case it was written for, but fatal across process
      // restarts: when the user force-quits the app while a "nextPrayer" Fajr
      // activity is alive, iOS keeps the activity on the lock screen long
      // past its staleDate. Hours later when the app reopens at night, the
      // ref starts empty, the picker chooses "tahajjud" as winner, and the
      // orphaned Fajr card is never ended — so the user sees a stale Fajr
      // countdown on top of (or instead of) the tahajjud card. Calling end()
      // unconditionally is a cheap no-op when nothing matches the kind on
      // the iOS side.
      ["nextPrayer", "jumuah", "tahajjud"].forEach(kind => {
        if (kind !== winner) {
          endLiveActivity(kind);
          lastLiveActivitySigRef.current[kind] = null;
        }
      });

      // ── Start the winner if not already running with the same signature ─
      // Record the sig only AFTER a successful start (id !== null). iOS rejects
      // Activity.request when the app isn't in foreground state ("Target is not
      // foreground") — recording the sig optimistically would short-circuit
      // every retry. Leaving sig unset means the next tick (or foreground
      // visibilitychange) will retry until iOS accepts the start.
      if (winner && sig !== lastLiveActivitySigRef.current[winner]) {
        const targetWinner = winner;
        const targetSig = sig;
        startLiveActivity(payload).then(id => {
          if (id) lastLiveActivitySigRef.current[targetWinner] = targetSig;
        });
      }
    };

    tick();
    const interval = setInterval(tick, 30000);
    const onVis = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [prayerTimes, salahLog, liveActivityWindow]); // eslint-disable-line react-hooks/exhaustive-deps

  const stopAudio = () => {
    const audio = getAudio();
    playAttemptRef.current++; // cancel any in-flight connection attempt
    if (bufferingTimeoutRef.current) clearTimeout(bufferingTimeoutRef.current);
    audio.pause();
    setPlaying(null);
    setPlayStatus("stopped");
    releaseWakeLock();
    if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "none";
  };

  // Stop azaan clip
  const stopAzaanClip = () => {
    if (azaanAudioRef.current) { try { azaanAudioRef.current.pause(); azaanAudioRef.current.src = ""; } catch {} }
    azaanAudioRef.current = null;
    setAzaanPlaying(false);
    if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "none";
  };

  // Dismiss dhikr overlay and fan menu when app goes to background so they
  // don't block the UI when the user returns. The dhikr overlay is full-screen
  // (zIndex:1060) and intercepts all taps — if left open across a minimize/reopen
  // the tab bar becomes unreachable. Fan menu backdrop (zIndex:998) has the same
  // effect. Both are meaningless context to resume after a background trip.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState !== "hidden") return;
      clearTimeout(dhikrTimerRef.current);
      setShowDhikr(false);
      abortFanMenu();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Play azaan clip for a prayer
  const playAzaanClip = (prayer) => {
    // If already playing, stop it (toggle for manual tap, fresh start for auto-trigger)
    if (azaanAudioRef.current) { stopAzaanClip(); if (prayer === "Preview") return; }
    // Makkah Masjid Al-Haram azaan from aladhan.com CDN (real recording)
    // Local /azaan-makkah.mp3 is a WAV placeholder, do not use it
    const AZAAN_URL = "https://cdn.aladhan.com/audio/adhans/a8.mp3";
    const audio = new Audio(AZAAN_URL);
    azaanAudioRef.current = audio;
    audio.volume = 1.0;
    setAzaanPlaying("loading");
    audio.oncanplay = () => setAzaanPlaying("playing");
    if ("mediaSession" in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: `${prayer} Azaan`,
        artist: "Makkah",
        album: "Sirat",
        artwork: [{ src: "/logo192.png", sizes: "192x192", type: "image/png" }]
      });
      navigator.mediaSession.playbackState = "playing";
      navigator.mediaSession.setActionHandler("pause", () => { stopAzaanClip(); });
      navigator.mediaSession.setActionHandler("play", () => { audio.play(); setAzaanPlaying("playing"); if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "playing"; });
    }
    console.log(`[AZAAN] Playing azaan for ${prayer} from ${AZAAN_URL}`);
    audio.play().catch(err => { console.log("[AZAAN] play() blocked:", err.message); setAzaanPlaying(false); });
    audio.onended = () => { stopAzaanClip(); };
  };

  // tapTasbeeh moved to src/components/Tasbeeh.js

  
  // startQibla & startCompass moved to src/components/Qibla.js

  const toggleNotifyMount = async (mount) => {
    hapticToggle();
    const current = notifyMounts !== null ? notifyMounts : [...followedMounts];
    const updated = current.includes(mount) ? current.filter(m => m !== mount) : [...current, mount];
    setNotifyMounts(updated);
    localStorage.setItem("notifyMounts", JSON.stringify(updated));
    // Sync to backend — only notifyMounts get push notifications
    syncFollowsToBackend(followedMounts, updated);
  };

  const exportPrayerCalendar = () => {
    if (!prayerTimes) return;
    const pad = n => String(n).padStart(2, '0');
    const today = new Date();
    const fmtDt = (d, timeStr) => {
      const clean = timeStr.replace(/ \(.*\)/, '').replace(/ \(.*/, '');
      const [h, m] = clean.split(':').map(Number);
      return `${d.getFullYear()}${pad(d.getMonth()+1)}${pad(d.getDate())}T${pad(h)}${pad(m)}00`;
    };
    const events = TRACKABLE_PRAYERS.flatMap(prayer => {
      const t = prayerTimes[prayer];
      if (!t) return [];
      return Array.from({ length: 30 }, (_, i) => {
        const d = new Date(today);
        d.setDate(today.getDate() + i);
        const dt = fmtDt(d, t);
        return ['BEGIN:VEVENT', `DTSTART;TZID=Africa/Johannesburg:${dt}`, 'DURATION:PT5M', `SUMMARY:${prayer}`, `UID:sirat-${prayer}-${dt}@sirat`, 'END:VEVENT'].join('\r\n');
      });
    });
    const ics = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Sirat//Prayer Times//EN', 'CALSCALE:GREGORIAN', ...events, 'END:VCALENDAR'].join('\r\n');
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'sirat-prayer-times.ics';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const syncFollowsToBackend = async (follows, notify) => {
    try {
      const lat = parseFloat(localStorage.getItem("userLat") || "") || null;
      const lng = parseFloat(localStorage.getItem("userLng") || "") || null;
      const _dne = localStorage.getItem("dailyNotifEnabled") !== "false";
      // If user is not Pro/trial, send empty notifyMounts so they don't get azaan push notifications
      const masjidAccess = userFeatures.masjid !== false && (remoteConfig.allFree || isSupportMode || isPro || isTrial || remoteConfig.features?.masjid === true || userFeatures.masjid === true);
      const effectiveNotifyMounts = masjidAccess ? notify : [];
      const uid = localStorage.getItem("azaan-userId");
      const extraPayload = { lat, lng, school: parseInt(localStorage.getItem("school") || "1"), dailyNotifEnabled: _dne, dailyNameEnabled: localStorage.getItem("dailyNameEnabled") === "true", dailyNameHour: parseInt(localStorage.getItem("dailyNameHour") || "8"), prayerReminderEnabled: localStorage.getItem("prayerReminderEnabled") === "true", azaanPrayers: JSON.parse(localStorage.getItem("azaanPrayers") || "[]") };

      if (isNative()) {
        // Native iOS/Android: register APNs token instead of web push subscription
        await registerNativePush(uid, follows, effectiveNotifyMounts, extraPayload);
        return;
      }

      let subscription = null;
      if ("serviceWorker" in navigator && "PushManager" in window) {
        const registration = await navigator.serviceWorker.ready;
        subscription = await registration.pushManager.getSubscription();
      }
      await fetch(`${BACKEND}/api/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subscription, followedMounts: follows, notifyMounts: effectiveNotifyMounts,
          userId: uid, ...extraPayload
        })
      });
    } catch (err) {
      console.log("Failed to sync follows:", err);
    }
  };

  const detectLocation = async (force) => {
    if (!navigator.geolocation) return false;
    // Cooldown: skip if detected less than 10 min ago (unless forced)
    if (!force && Date.now() - locationCooldownRef.current < 10 * 60 * 1000) return true;
    setLocationLoading(true);
    try {
      const pos = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000 });
      });
      const { latitude: lat, longitude: lng } = pos.coords;
      setUserLat(lat); setUserLng(lng);
      localStorage.setItem("userLat", String(lat));
      localStorage.setItem("userLng", String(lng));
      locationCooldownRef.current = Date.now();
      localStorage.setItem("userLatTime", String(Date.now()));
      // Reverse geocode to get city name
      try {
        const geo = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&zoom=18&addressdetails=1`, { headers: { "Accept-Language": "en" } });
        const geoData = await geo.json();
        const addr = geoData.address || {};
        // Walk all known place-name fields in order of granularity (most specific → most general),
        // dedupe, and take the two most specific non-country names. Nominatim is inconsistent about
        // which key holds the suburb vs the city (e.g. Westville is sometimes `town`, Durban is
        // `city`), so this avoids losing the suburb just because it's under an unexpected key.
        const candidates = [
          addr.suburb, addr.neighbourhood, addr.quarter, addr.city_district,
          addr.district, addr.residential, addr.borough, addr.hamlet,
          addr.village, addr.town, addr.municipality, addr.city,
          addr.county, addr.state_district, addr.state
        ];
        const seen = new Set();
        const ordered = candidates.filter(p => {
          if (!p || seen.has(p)) return false;
          seen.add(p);
          return true;
        });
        const parts = [ordered[0], ordered[1], addr.country].filter(Boolean);
        const label = parts.length > 0 ? parts.join(", ") : `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
        setDetectedCity(label);
        localStorage.setItem("detectedCity", label);
      } catch {}
      syncFollowsToBackend(followedMounts, effectiveNotify);
      setLocationLoading(false);
      return true;
    } catch {
      setLocationLoading(false);
      return false;
    }
  };

  // Permission onboarding flow — runs once after registration
  const permSteps = (() => {
    const hasOrientPerm = typeof DeviceOrientationEvent !== "undefined" && typeof DeviceOrientationEvent.requestPermission === "function";
    return [
      {
        id:"location", icon:"📍", title:"Accurate Prayer Times",
        description:"Sirat uses your location to calculate your exact 5 daily prayer times wherever you are in the world.",
        allowLabel:"Allow Location",
        iconBg1:"#041a0e", iconBg2:"#071a14", iconBgL1:"#e0f7f0", iconBgL2:"#eef8f4",
        iconBorder:"rgba(0,201,167,0.3)",
        btnGrad1:"#00c9a7", btnGrad2:"#00a886", btnShadow:"rgba(0,201,167,0.3)",
      },
      ...(hasOrientPerm ? [{
        id:"orientation", icon:"🧭", title:"Qibla Compass",
        description:"Allow motion & orientation so Sirat's compass can point you towards the Kaaba in Mecca.",
        allowLabel:"Allow Motion & Orientation",
        iconBg1:"#04101a", iconBg2:"#071420", iconBgL1:"#e0eef8", iconBgL2:"#eef4fb",
        iconBorder:"rgba(100,160,255,0.3)",
        btnGrad1:"#6aa0ff", btnGrad2:"#4a80ef", btnShadow:"rgba(100,160,255,0.3)",
        btnTextColor:"#07090c",
      }] : []),
      // PWA install prompt — Android (beforeinstallprompt) or iOS Safari (manual instructions)
      ...(() => {
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone || isNative();
        if (isStandalone) return []; // Already installed / native app
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        if (deferredInstallPrompt.current) return [{
          id:"install", icon:"📲", title:"Add to Home Screen",
          description:"Install Sirat as an app on your phone — launches instantly, works offline, and feels like a native app.",
          allowLabel:"Install Sirat",
          iconBg1:"#0d0a1a", iconBg2:"#14071a", iconBgL1:"#f0e8f8", iconBgL2:"#f4eefb",
          iconBorder:"rgba(160,100,255,0.3)",
          btnGrad1:"#a06aff", btnGrad2:"#8050ef", btnShadow:"rgba(160,100,255,0.3)",
          btnTextColor:"#fff",
        }];
        if (isIOS) return [{
          id:"install-ios", icon:"📲", title:"Add to Home Screen",
          description:"_ios_install_", // Special marker — PermissionFlow renders visual instructions
          allowLabel:"Done — I've Added It",
          skipLabel:"I'll do it later",
          iconBg1:"#0d0a1a", iconBg2:"#14071a", iconBgL1:"#f0e8f8", iconBgL2:"#f4eefb",
          iconBorder:"rgba(160,100,255,0.3)",
          btnGrad1:"#a06aff", btnGrad2:"#8050ef", btnShadow:"rgba(160,100,255,0.3)",
          btnTextColor:"#fff",
        }];
        return [];
      })(),
    ];
  })();

  const advancePermFlow = () => {
    const nextStep = permFlowStep + 1;
    if (nextStep >= permSteps.length) {
      setShowPermFlow(false);
      localStorage.setItem("permFlowDone", "1");
      // Skip redundant onboarding tips — permission flow already covers these
      localStorage.setItem("onboardingDone", "1");
      setShowOnboardingTips(false);
    } else {
      setPermFlowStep(nextStep);
    }
  };

  const [permConfirmMsg, setPermConfirmMsg] = useState("");
  const handlePermAllow = async () => {
    const step = permSteps[permFlowStep];
    if (step.id === "location") {
      const ok = await detectLocation(true);
      if (ok) {
        const city = localStorage.getItem("detectedCity") || "";
        if (city) {
          setPermConfirmMsg(`✅ Prayer times loaded for ${city}`);
          await new Promise(r => setTimeout(r, 1500));
          setPermConfirmMsg("");
        }
      }
    } else if (step.id === "notifications") {
      const ok = await registerPush();
      if (ok) { setNotifEnabled(true); localStorage.setItem("notifEnabled", "true"); syncFollowsToBackend(followedMounts, effectiveNotify); }
    } else if (step.id === "orientation") {
      try { await DeviceOrientationEvent.requestPermission(); } catch {}
    } else if (step.id === "install") {
      if (deferredInstallPrompt.current) {
        try {
          deferredInstallPrompt.current.prompt();
          await deferredInstallPrompt.current.userChoice;
        } catch {}
        deferredInstallPrompt.current = null;
      }
    } else if (step.id === "install-ios") {
      // User says they've added it — nothing to do programmatically
    }
    advancePermFlow();
  };

  const handlePermSkip = () => advancePermFlow();

  // Auto-detect location on mount and when app regains focus
  useEffect(() => {
    if (locationMode !== "auto") return;
    const hasCache = localStorage.getItem("userLat") && localStorage.getItem("userLng");
    const lastFix = parseInt(localStorage.getItem("userLatTime") || "0", 10);
    const COLD_STALE_MS = 60 * 60 * 1000; // 1 hour
    if (!hasCache) {
      // No cached location at all — must detect
      detectLocation();
    } else if (Date.now() - lastFix > COLD_STALE_MS) {
      // Cached location is stale (>1h old) — refresh on cold launch so travellers
      // who killed the app and reopened in a new city get fresh times automatically.
      // Use force=true to bypass the in-memory cooldown which was just seeded from lastFix.
      detectLocation(true);
    }
    const onVisible = () => { if (document.visibilityState === "visible") detectLocation(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [locationMode]);

  const followMount = async (mount, name, url) => {
    hapticToggle();
    const isNewFollow = !followedMounts.includes(mount);
    const updated = followedMounts.includes(mount)
      ? followedMounts.filter(m => m !== mount)
      : [...followedMounts, mount];
    setFollowedMounts(updated);
    localStorage.setItem("followedMounts", JSON.stringify(updated));
    if (name && isNewFollow) {
      const updatedNames = { ...followedNames, [mount]: name };
      setFollowedNames(updatedNames);
      localStorage.setItem("followedNames", JSON.stringify(updatedNames));
    }
    if (url && isNewFollow) {
      const updatedUrls = { ...followedUrls, [mount]: url };
      setFollowedUrls(updatedUrls);
      localStorage.setItem("followedUrls", JSON.stringify(updatedUrls));
    }
    // If unfollowing, also remove from notifyMounts
    if (!isNewFollow) {
      const currentNotify = notifyMounts !== null ? notifyMounts : [...followedMounts];
      const updatedNotify = currentNotify.filter(m => m !== mount);
      setNotifyMounts(updatedNotify);
      localStorage.setItem("notifyMounts", JSON.stringify(updatedNotify));
    }
    // If following from search results, dismiss search so user lands on My List
    if (isNewFollow && showSearch) {
      setSearchQuery("");
      setShowSearch(false);
    }

    const notify = notifyMounts !== null ? notifyMounts : updated;
    syncFollowsToBackend(updated, !isNewFollow ? notify.filter(m => m !== mount) : notify);
  };
  
  const searchSeqRef = useRef(0); // race-condition guard for async search
  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { setShowSearch(false); return; }
    setShowSearch(true);
    const lowerQ = q.toLowerCase();
    const seq = ++searchSeqRef.current; // capture sequence number for this search

    // Try LiveMasjid full catalog first — includes every masjid with suburb/city info
    // Cached in localStorage for 24h so each device only hits their API once per day
    const LM_CACHE_KEY = "lm_catalog";
    const LM_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours
    try {
      if (!livemasjidCacheRef.current) {
        // Check localStorage first
        try {
          const stored = localStorage.getItem(LM_CACHE_KEY);
          if (stored) {
            const { ts, mounts: cached } = JSON.parse(stored);
            if (Date.now() - ts < LM_CACHE_TTL && cached && cached.length > 0) {
              livemasjidCacheRef.current = cached;
            }
          }
        } catch (_) {}

        // Fetch from LiveMasjid if cache miss or expired
        // Try direct URL first (fast, no backend cold-start delay), fall back to backend proxy
        if (!livemasjidCacheRef.current) {
          let data;
          try {
            const ctrl = new AbortController();
            const t = setTimeout(() => ctrl.abort(), 5000);
            const r = await fetch("https://www.livemasjid.com/api/get_mountdetail_new.php", { signal: ctrl.signal });
            clearTimeout(t);
            data = await r.json();
          } catch (_) {
            // CORS or timeout — try backend proxy
            const r2 = await fetch(`${BACKEND}/api/livemasjid-catalog`);
            data = await r2.json();
          }
          if (seq !== searchSeqRef.current) return; // stale search — abort
          const fetchedMounts = Array.isArray(data) ? data : (data.mounts || data.data || []);
          // Only cache if we actually got results — don't cache empty responses
          if (fetchedMounts.length > 0) {
            livemasjidCacheRef.current = fetchedMounts;
            try {
              localStorage.setItem(LM_CACHE_KEY, JSON.stringify({ ts: Date.now(), mounts: fetchedMounts }));
            } catch (_) {} // storage quota exceeded — skip silently
          } else {
            // API returned empty — clear stale cache and fall through to backend
            localStorage.removeItem(LM_CACHE_KEY);
            throw new Error("LiveMasjid API returned empty catalog");
          }
        }
      }
      const mounts = livemasjidCacheRef.current;
      if (!mounts || mounts.length === 0) throw new Error("Empty catalog");
      const isLiveSearch = ["live","🔴","on air","streaming"].includes(lowerQ);
      const filtered = mounts
        .filter(m =>
          isLiveSearch ? (liveMountsRef.current.has((m.mname || "") + ".mp3") || liveMountsRef.current.has(m.mname || "") || m.sstatus === "green") :
          (m.mname   || "").toLowerCase().includes(lowerQ) ||
          (m.sname   || "").toLowerCase().includes(lowerQ) ||
          (m.sdesc   || "").toLowerCase().includes(lowerQ) ||
          (m.location|| "").toLowerCase().includes(lowerQ)
        )
        .sort((a, b) => {
          const aLive = liveMountsRef.current.has((a.mname || "") + ".mp3") || liveMountsRef.current.has(a.mname || "") || a.sstatus === "green";
          const bLive = liveMountsRef.current.has((b.mname || "") + ".mp3") || liveMountsRef.current.has(b.mname || "") || b.sstatus === "green";
          return (bLive ? 1 : 0) - (aLive ? 1 : 0);
        })
        .slice(0, 60)
        .map(m => {
          // Use the actual source URL from the API — pick audio/mpeg, else first source
          const mp3Src = (m.ssources || []).find(s => s.ctype === 'audio/mpeg');
          const anySrc = (m.ssources || [])[0];
          const httpUrl = (mp3Src || anySrc)?.url || `http://livemasjid.com:8000/${m.mname}`;
          // Extract mount name and build relay HTTPS URL
          const urlMount = (httpUrl.split("/").pop() || m.mname).replace(/\.mp3$/i, "");
          const url = `https://relay.livemasjid.com:8443/${urlMount}`;
          return {
            mount:    m.mname,
            name:     m.sname || m.mname,
            location: m.sdesc || m.location || "",
            url,
            live:     m.sstatus === "green",
            timesUrl: TIMES_MAP[m.mname] || null,
          };
        });
      if (seq === searchSeqRef.current) setAllStreams(filtered);
      return;
    } catch (_) {
      // CORS, network, or empty catalog — fall back to backend
      livemasjidCacheRef.current = null; // clear bad cache so next search retries
    }

    // Fallback: existing backend search
    try {
      const res = await fetch(`${BACKEND}/api/all-streams`);
      if (seq !== searchSeqRef.current) return; // stale search — abort
      const data = await res.json();
      const enriched = data.map(s => ({ ...s, name: masjidNames[s.mount] || s.name, location: "" }));
      if (seq === searchSeqRef.current) {
        setAllStreams(
          enriched
            .filter(s => s.mount.toLowerCase().includes(lowerQ) || s.name.toLowerCase().includes(lowerQ))
            .slice(0, 60)
        );
      }
    } catch (_) {}
  };
  // resetTasbeeh moved to src/components/Tasbeeh.js

  // Landscape detection
  useEffect(() => {
    const check = () => setIsLandscape(window.innerWidth > window.innerHeight);
    window.addEventListener("resize", check);
    window.addEventListener("orientationchange", check);
    return () => { window.removeEventListener("resize", check); window.removeEventListener("orientationchange", check); };
  }, []);

  // iOS background audio recovery: if stream/surah dropped while app was backgrounded, restart it
  // Also re-acquire wake lock (iOS releases it when page becomes hidden)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      // Re-acquire wake lock on foreground (iOS releases it when hidden)
      if (playing || surahIsPlaying) requestWakeLock();
      // Radio recovery
      const audio = getAudio();
      if (playing && audio.paused && playStatus === "playing") {
        audio.src = playing.url;
        audio.play().catch(() => {});
      }
      // Surah recovery — if surah was playing but buffer got paused by OS
      if (!surahPausedRef.current && surahIsPlaying) {
        const buf = yasinBufs[yasinBufActive];
        if (buf && buf.paused && buf.src) {
          buf.play().catch(() => {});
        }
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [playing, playStatus, surahIsPlaying]);

  // Show notification prompt once, 30s after load, for registered users who haven't enabled notifications.
  // Never fire during the onboarding tips flow — new users already see notifications explained there.
  useEffect(() => {
    if (!userId) return;
    if (notifEnabled) return;
    if (showOnboardingTips) return;
    if (localStorage.getItem("onboardingDone") !== "1") return;
    if (localStorage.getItem("notifPromptDismissed")) return;
    if (typeof Notification !== "undefined" && Notification.permission === "granted") return;
    const t = setTimeout(() => setShowNotifPrompt(true), 30000);
    return () => clearTimeout(t);
  }, [userId, notifEnabled, showOnboardingTips]);

  const playSurahFrom = (ayahs, idx) => {
    if (!ayahs || idx >= ayahs.length) { setSurahIsPlaying(false); setSurahCurrentAyah(null); surahCurrentAyahRef.current = null; return; }
    if (idx === 0) trackEvent("quran_play", { surah: surahKey, reciter: selectedReciter });
    // Always ensure paused ref is cleared so playback can proceed
    surahPausedRef.current = false;
    // Stop radio if playing — mutual exclusivity
    playAttemptRef.current++;
    if (bufferingTimeoutRef.current) clearTimeout(bufferingTimeoutRef.current);
    const audio = getAudio(); audio.pause(); setPlaying(null); setPlayStatus("stopped");
    setupYasinBuf(0); setupYasinBuf(1);
    yasinBufs.forEach(b => { b.ontimeupdate = null; b.onended = null; b.onerror = null; b.pause(); });

    // Set Media Session metadata — use surahKeyRef so name is always current even in stale closures
    if ("mediaSession" in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: SURAH_INFO[surahKeyRef.current]?.name || "Quran",
        artist: (RECITERS.find(r => r.id === selectedReciter) || RECITERS[0]).name,
        album: "Sirat — Quran Reader",
        artwork: [
          { src: "/logo192.png", sizes: "192x192", type: "image/png" },
          { src: "/logo512.png", sizes: "512x512", type: "image/png" },
        ],
      });
      navigator.mediaSession.setActionHandler("pause", pauseSurah);
      navigator.mediaSession.setActionHandler("play", () => resumeSurah(ayahs));
      const surahKeys = Object.keys(SURAH_INFO);
      navigator.mediaSession.setActionHandler("nexttrack", async () => {
        // Check play-all queue first
        const queue = playAllQueueRef.current;
        if (queue) {
          const idx = queue.indexOf(surahKeyRef.current);
          if (idx < queue.length - 1) {
            const nextAyahs = await loadSurah(queue[idx + 1]);
            if (nextAyahs) playSurahFrom(nextAyahs, 0);
          } else {
            playAllQueueRef.current = null;
          }
          return;
        }
        // Normal next-surah behaviour
        const idx = surahKeys.indexOf(surahKeyRef.current);
        if (idx < surahKeys.length - 1) {
          const nextAyahs = await loadSurah(surahKeys[idx + 1]);
          if (nextAyahs) playSurahFrom(nextAyahs, 0);
        }
      });
      navigator.mediaSession.setActionHandler("previoustrack", async () => {
        const idx = surahKeys.indexOf(surahKeyRef.current);
        if (idx > 0) {
          const prevAyahs = await loadSurah(surahKeys[idx - 1]);
          if (prevAyahs) playSurahFrom(prevAyahs, 0);
        }
      });
      navigator.mediaSession.setActionHandler("seekforward", null);
      navigator.mediaSession.setActionHandler("seekbackward", null);
    }

    // Track real elapsed seconds across completed ayahs so lock screen shows correct surah time
    let surahElapsedSecs = 0;
    // Stable known total; only expands if actual playback exceeds estimate
    surahTotalSecsRef.current = SURAH_INFO[surahKeyRef.current]?.totalSecs || 900;

    const go = (i, active) => {
      if (i >= ayahs.length) {
        // Check play-all queue — advance to next surah before stopping
        const queue = playAllQueueRef.current;
        if (queue) {
          const qIdx = queue.indexOf(surahKeyRef.current);
          if (qIdx < queue.length - 1) {
            loadSurah(queue[qIdx + 1]).then(nextAyahs => { if (nextAyahs) playSurahFrom(nextAyahs, 0); });
            return;
          } else {
            playAllQueueRef.current = null;
          }
        }
        // Surah finished — stop both buffers and clean up all handlers
        yasinBufs.forEach(b => { if (b) { b.ontimeupdate = null; b.onended = null; b.onerror = null; b.onpause = null; b.pause(); } });
        setSurahIsPlaying(false); setSurahCurrentAyah(null); surahCurrentAyahRef.current = null;
        releaseWakeLock();
        if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "none";
        // Auto-log prompt: check if user has active khatms for this surah
        try {
          const finishedKey = surahKeyRef.current;
          if (userId && finishedKey) {
            fetch(`${BACKEND}/api/khatm/mine/${userId}`).then(r => r.json()).then(d => {
              const matching = (d.active || []).filter(k => k.surahKey === finishedKey);
              if (matching.length > 0) setKhatmLogPrompt({ surahKey: finishedKey, khatms: matching });
            }).catch(() => {});
          }
        } catch {}
        return;
      }
      // Set playing state immediately — before play() resolves — so lock screen shows pause button
      if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "playing";
      const buf = yasinBufs[active];
      const next = 1 - active;
      const other = yasinBufs[next];
      // Clear handlers on the other buffer so it can't interfere, but don't pause yet
      // (we pause it after the new buffer starts, to avoid silence gaps)
      if (other) { other.ontimeupdate = null; other.onended = null; other.onerror = null; other.onpause = null; }
      if (buf._url !== ayahs[i].audio) { buf._url = ayahs[i].audio; buf.src = ayahs[i].audio; }
      buf.volume = surahVolumeRef.current;
      buf.playbackRate = surahSpeedRef.current;
      // Eagerly preload next ayah into the other buffer so it's ready for crossover
      if (i + 1 < ayahs.length) preloadYasinBuf(next, ayahs[i + 1].audio);
      let crossoverFired = false;
      buf.ontimeupdate = () => {
        if ("mediaSession" in navigator && buf.duration > 0) {
          try {
            const position = surahElapsedSecs + buf.currentTime;
            surahPositionRef.current = position;
            surahTotalSecsRef.current = Math.max(surahTotalSecsRef.current, position + 1);
            navigator.mediaSession.setPositionState({ duration: surahTotalSecsRef.current, playbackRate: 1, position });
          } catch(e) {}
        }
        // Transition to next ayah just before current ends
        if (!crossoverFired && !surahPausedRef.current && buf.duration && buf.duration > 0 && (buf.duration - buf.currentTime) < 0.3) {
          crossoverFired = true;
          buf.ontimeupdate = null;
          buf.onended = null;
          buf.onerror = null;
          surahElapsedSecs += buf.duration;
          go(i + 1, next);
        }
      };
      buf.onended = () => {
        buf.ontimeupdate = null;
        buf.onerror = null;
        if (crossoverFired || surahPausedRef.current) return;
        crossoverFired = true;
        surahElapsedSecs += buf.duration || 0;
        go(i + 1, next);
      };
      buf.onerror = () => { buf.ontimeupdate = null; buf.onended = null; if (!crossoverFired) { crossoverFired = true; go(i + 1, next); } };
      yasinBufActive = active;
      buf.play().then(() => {
        // Now that the new buffer is audibly playing, stop the old one
        if (other && !other.paused) other.pause();
        setSurahCurrentAyah(i);
        surahCurrentAyahRef.current = i;
        if (surahPausedRef.current) { buf.pause(); setSurahIsPlaying(false); return; }
        requestWakeLock();
        setSurahIsPlaying(true);
        document.getElementById("ayah-" + i)?.scrollIntoView({ behavior: "smooth", block: "center" });
        if ("mediaSession" in navigator) {
          try {
            surahPositionRef.current = surahElapsedSecs;
            surahTotalSecsRef.current = Math.max(surahTotalSecsRef.current, surahElapsedSecs + 1);
            navigator.mediaSession.setPositionState({ duration: surahTotalSecsRef.current, playbackRate: 1, position: surahElapsedSecs });
          } catch(e) {}
        }
      }).catch(() => { if (!crossoverFired) { crossoverFired = true; go(i + 1, next); } });
    };
    go(idx, 0);
  };

  const pauseSurah = () => {
    surahPausedRef.current = true;
    // Pause both buffers — during crossover the old one might still be audible
    yasinBufs.forEach(b => { if (b && !b.paused) b.pause(); });
    setSurahIsPlaying(false);
    if ("mediaSession" in navigator) {
      // playbackRate: 0 tells iOS to freeze the progress bar at the current position
      try { navigator.mediaSession.setPositionState({ duration: surahTotalSecsRef.current, playbackRate: 0, position: surahPositionRef.current }); } catch(e) {}
      navigator.mediaSession.playbackState = "paused";
    }
  };

  const resumeSurah = (ayahs) => {
    if (yasinBufs[yasinBufActive]) {
      surahPausedRef.current = false;
      if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "playing";
      yasinBufs[yasinBufActive].play().then(() => {
        setSurahIsPlaying(true);
      }).catch(() => {
        surahPausedRef.current = true;
        if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "paused";
      });
    }
  };

  const stopSurah = () => {
    surahPausedRef.current = false;
    yasinBufs.forEach(b => { if (b) { b.ontimeupdate = null; b.onended = null; b.onerror = null; b.onpause = null; b.pause(); b.src = ""; b._url = ""; } });
    setSurahIsPlaying(false); setSurahCurrentAyah(null); surahCurrentAyahRef.current = null;
    releaseWakeLock();
    if (sleepTimerRef.current) { clearTimeout(sleepTimerRef.current); setSleepTimerLabel(null); }
    if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "none";
  };

  const startSleepTimer = (minutes) => {
    if (sleepTimerRef.current) clearTimeout(sleepTimerRef.current);
    setSleepTimerLabel(minutes + "m");
    sleepTimerEndRef.current = Date.now() + minutes * 60 * 1000;
    setShowSleepMenu(false);
    sleepTimerRef.current = setTimeout(() => { pauseSurah(); setSleepTimerLabel(null); sleepTimerEndRef.current = null; }, minutes * 60 * 1000);
  };

  const tierLabel = (t) => t.days === 0 ? "once" : `${t.days} days`;

  const compressFeedbackImage = (file) => new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = e => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1600; // backend limit is 5MB — keep resolution high so text is readable
        let w = img.width, h = img.height;
        if (w > MAX) { h = Math.round(h * MAX / w); w = MAX; }
        if (h > MAX) { w = Math.round(w * MAX / h); h = MAX; }
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.88));
      };
      img.onerror = () => resolve(null);
      img.src = e.target.result;
    };
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });

  const sendFeedback = async () => {
    const msg = feedbackText.trim();
    // Read image directly from the input ref — bypasses any stale closure issue
    const imgToSend = feedbackImageRef.current?._pendingImage || feedbackImage;
    if ((!msg && !imgToSend) || feedbackSending) return;
    setFeedbackSending(true);
    // Record locally FIRST before any state changes or async work
    const localEntry = { id: Date.now(), text: msg, image: imgToSend || null, time: new Date().toISOString() };
    setLocalSentMsgs(prev => [...prev, localEntry]);
    setFeedbackText("");
    setFeedbackImage(null);
    if (feedbackImageRef.current) feedbackImageRef.current._pendingImage = null;
    setTimeout(() => { const el = feedbackScrollRef.current; if (el) el.scrollTop = el.scrollHeight; }, 50);
    try {
      const cleanPhone = feedbackPhone.replace(/[^0-9]/g, '');
      await fetch(`${BACKEND}/api/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: userId || "guest", message: msg, phone: cleanPhone ? "+" + cleanPhone : null, image: imgToSend || null })
      });
      if (feedbackPhone.trim()) localStorage.setItem("feedbackPhone", feedbackPhone.trim());
    } catch {}
    setFeedbackSending(false);
  };

  const fetchFeedbackHistory = async () => {
    if (!userId || userId === "guest") return;
    setFeedbackHistoryLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/feedback/${userId}`);
      const d = await r.json();
      if (Array.isArray(d.feedback)) {
        // Preserve locally-held images that the server doesn't store yet
        setFeedbackHistory(prev => {
          const localImages = {};
          prev.forEach(m => { if (m.image) localImages[m.message] = m.image; });
          return d.feedback.map(m => ({ ...m, image: m.image || localImages[m.message] || null }));
        });
      }
    } catch {}
    setFeedbackHistoryLoading(false);
  };

  const fetchRating = async () => {
    if (!userId || userId === "guest") return;
    try {
      const r = await fetch(`${BACKEND}/api/rating/${userId}`);
      const d = await r.json();
      if (d.rating) { setRatingStars(d.rating.stars); setRatingComment(d.rating.comment || ""); setRatingSubmitted(true); }
      setRatingLoaded(true);
    } catch { setRatingLoaded(true); }
  };

  const submitRating = async () => {
    if (!ratingStars || ratingSending) return;
    setRatingSending(true);
    try {
      await fetch(`${BACKEND}/api/rating`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, stars: ratingStars, comment: ratingComment.trim() })
      });
      setRatingSubmitted(true);
    } catch {}
    setRatingSending(false);
  };

  // Load feedback history + rating once on mount + auto-open if from notification
  useEffect(() => {
    if (userId && userId !== "guest") { fetchFeedbackHistory(); fetchRating(); }
    if (autoOpenFeedback) { setTab("tools"); setToolView("feedback"); }
    // Auto-play azaan when opened from notification tap (?azaan=Fajr)
    if (autoPlayAzaan) {
      setTimeout(() => playAzaanClip(autoPlayAzaan), 500);
      // Clean URL param
      try { window.history.replaceState({}, "", window.location.pathname); } catch {}
    }
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll feedback chat to bottom when view opens
  useEffect(() => {
    if (toolView === "feedback") {
      setTimeout(() => { const el = feedbackScrollRef.current; if (el) el.scrollTop = el.scrollHeight; }, 80);
    }
  }, [toolView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track visual viewport height so feedback chat can shrink when iOS keyboard opens
  useEffect(() => {
    if (!(toolView === "feedback") || !window.visualViewport) return;
    const onResize = () => { setVvHeight(window.visualViewport.height); setVvOffsetTop(window.visualViewport.offsetTop || 0); };
    onResize();
    window.visualViewport.addEventListener("resize", onResize);
    window.visualViewport.addEventListener("scroll", onResize);
    return () => { window.visualViewport.removeEventListener("resize", onResize); window.visualViewport.removeEventListener("scroll", onResize); setVvHeight(null); setVvOffsetTop(0); };
  }, [toolView]);

  // Helper: get supporter tier from cumulative total
  const getSupporterTier = useCallback((total, config) => {
    const t = config?.tiers || {};
    const bronze = t.bronze ?? 50;
    const silver = t.silver ?? 150;
    const gold   = t.gold   ?? 500;
    if (total >= gold)   return { name: "Gold",   color: "#ffb300", next: null,     nextAmount: null };
    if (total >= silver) return { name: "Silver", color: "#C0C0C0", next: "Gold",   nextAmount: gold };
    if (total >= bronze) return { name: "Bronze", color: "#cd7f32", next: "Silver", nextAmount: silver };
    return { name: null, color: null, next: "Bronze", nextAmount: bronze };
  }, []);

  const handlePaystackSuccess = useCallback((tier, reference) => {
    hapticSuccess();
    // Support mode: track contribution without activating Pro
    if (tier.label === "Support") {
      setShowDonation(false);
      setShowProPrompt(false);
      const prev = parseInt(localStorage.getItem("supportTotal") || "0");
      const newTotal = prev + tier.price;
      localStorage.setItem("supportTotal", String(newTotal));
      localStorage.setItem("supportCount", String(parseInt(localStorage.getItem("supportCount") || "0") + 1));
      localStorage.setItem("hasSupported", "true");
      localStorage.setItem("lastSupportDate", new Date().toISOString());
      supportTotalRef.current = newTotal;
      // Persist to backend immediately (optimistic — webhook is backup)
      const id = localStorage.getItem("azaan-userId");
      if (id) {
        fetch(`${BACKEND}/api/users/${id}/support`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ amount: tier.price, reference })
        }).catch(() => {});
      }
      // Check tier upgrade
      const sup = remoteConfig.support || {};
      const oldTier = getSupporterTier(prev, sup);
      const newTier = getSupporterTier(newTotal, sup);
      const upgraded = newTier.name && newTier.name !== oldTier.name;
      setSupportToastTier(newTier);
      setSupportToastUpgraded(upgraded);
      setShowSupportToast(true);
      setTimeout(() => setShowSupportToast(false), 6000);
      return;
    }

    const isLifetimeTier = tier.days === 0;
    setShowDonation(false);
    setShowProPrompt(false);
    setIsPro(true);
    if (isLifetimeTier) {
      localStorage.setItem("isPro", "true");
      setProExpiry(null);
      localStorage.removeItem("proExpiry");
      setProCookie();
    } else {
      // Accumulate: add days to existing expiry if still active, otherwise start from now
      const existingExpiry = localStorage.getItem("proExpiry");
      const startFrom = (existingExpiry && new Date(existingExpiry) > new Date()) ? new Date(existingExpiry) : new Date();
      const expiry = new Date(startFrom.getTime() + tier.days * 86400000);
      const expiryStr = expiry.toISOString();
      setProExpiry(expiryStr);
      localStorage.setItem("proExpiry", expiryStr);
      localStorage.setItem("isPro", "true");
      setProCookie();
    }
    setShowPaymentSuccess(true);
    setTimeout(() => { setShowPaymentSuccess(false); }, 5000);
  }, [remoteConfig.support, getSupporterTier]);

  const paystackRef = useRef(null);
  if (!paystackRef.current) paystackRef.current = new PaystackPop();

  const handlePaystack = async (tier) => {
    const key = remoteConfig.paystackKey;
    if (!key) { alert("Payments are not available right now. Please try again shortly."); return; }
    setPaystackLoading(true);
    try {
      const ref = `sirat-${userId || "guest"}-${Date.now()}`;
      await paystackRef.current.checkout({
        key,
        email: userEmail || `${userId || "guest"}@sirat.co.za`,
        amount: tier.price * 100,
        currency: "ZAR",
        channels: ["card", "apple_pay"],
        reference: ref,
        metadata: { userId: userId || "guest", tier: { days: tier.days, label: tier.label, price: tier.price }, custom_fields: [{ display_name: "User ID", variable_name: "user_id", value: userId || "guest" }] },
        onSuccess() { setPaystackLoading(false); handlePaystackSuccess(tier, ref); },
        onCancel() { setPaystackLoading(false); }
      });
    } catch (e) { setPaystackLoading(false); console.error("Paystack error:", e); }
  };

  // All features permanently free — no Pro prompts
  // eslint-disable-next-line no-unused-vars
  const maybeShowProPrompt = useCallback(() => {}, []);

  // Periodic support prompt — in support mode, show sheet after N sessions with cooldown
  useEffect(() => {
    if (!isSupportMode) return;
    const sup = remoteConfig.support || {};
    if (!sup.enabled) return;
    const sessions = parseInt(localStorage.getItem("supportPromptCount") || "0") + 1;
    localStorage.setItem("supportPromptCount", String(sessions));
    const threshold = sup.promptAfterSessions || 5;
    const cooldownDays = sup.promptCooldownDays || 7;
    const lastPrompt = localStorage.getItem("lastSupportPrompt");
    const cooldownMs = cooldownDays * 86400000;
    const supported = localStorage.getItem("hasSupported") === "true";
    const effectiveCooldown = supported ? cooldownMs * 2 : cooldownMs;
    if (sessions >= threshold && (!lastPrompt || Date.now() - new Date(lastPrompt).getTime() >= effectiveCooldown)) {
      localStorage.setItem("supportPromptCount", "0");
      localStorage.setItem("lastSupportPrompt", new Date().toISOString());
      setTimeout(() => setShowDonation(true), 3000); // slight delay after load
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cancelSleepTimer = () => {
    if (sleepTimerRef.current) clearTimeout(sleepTimerRef.current);
    sleepTimerEndRef.current = null;
    setSleepTimerLabel(null);
  };

  // Countdown for sleep timer
  useEffect(() => {
    if (!sleepTimerLabel) { setSleepTimerRemaining(null); return; }
    const tick = () => {
      if (!sleepTimerEndRef.current) return;
      const rem = Math.max(0, Math.round((sleepTimerEndRef.current - Date.now()) / 1000));
      setSleepTimerRemaining(rem);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [sleepTimerLabel]);

  const loadSurah = async (key) => {
    maybeShowProPrompt();
    setSurahKey(key);
    surahKeyRef.current = key; // update ref immediately so handlers read correct key before re-render
    setToolView("surah");
    // Track recently played (max 4, dedup)
    setRecentSurahs(prev => {
      const updated = [key, ...prev.filter(k => k !== key)].slice(0, 4);
      localStorage.setItem("recentSurahs", JSON.stringify(updated));
      return updated;
    });
    const cacheKey = key + ":" + selectedReciter;
    if (surahCacheRef.current[cacheKey]) return surahCacheRef.current[cacheKey];
    setSurahLoading(true);
    const info = SURAH_INFO[key];
    try {
      const [arRes, enRes, audioRes] = await Promise.all([
        fetch("https://api.alquran.cloud/v1/surah/" + info.num),
        fetch("https://api.alquran.cloud/v1/surah/" + info.num + "/en.sahih"),
        fetch("https://api.alquran.cloud/v1/surah/" + info.num + "/" + selectedReciter),
      ]);
      const [arData, enData, audioData] = await Promise.all([arRes.json(), enRes.json(), audioRes.json()]);
      const ayahs = arData.data.ayahs.map((a, i) => ({
        number: a.numberInSurah,
        arabic: a.text,
        translation: enData.data.ayahs[i].text,
        audio: audioData.data.ayahs[i].audio,
      }));
      setSurahCache(c => ({ ...c, [cacheKey]: ayahs }));
      surahCacheRef.current = { ...surahCacheRef.current, [cacheKey]: ayahs };
      // Preload first ayah so play starts instantly
      setupYasinBuf(0);
      preloadYasinBuf(0, ayahs[0].audio);
      setSurahLoading(false);
      return ayahs;
    } catch {
      alert("Could not load " + info.name + ". Please check your connection.");
      setToolView("grid");
    }
    setSurahLoading(false);
  };

  // TEMP DIAGNOSTIC — observe window-level pointer events near the tab bar.
  // Logs to console.warn so DebugScreen captures the trace. Remove once the
  // "tab bar dies after a while" bug is diagnosed and fixed.
  useEffect(() => installTabBarObserver(), []);

  // ── Siri Shortcuts / App Intents bridge (iOS native only) ────────────────
  // Native Siri intents write a pendingAction to the App Group UserDefaults.
  // On every foreground transition we drain the pending action and execute it.
  useEffect(() => {
    siriWriteUserName(userName);
  }, [userName]);

  useEffect(() => {
    const handlePending = async () => {
      const action = await siriConsumePendingAction();
      if (!action || !action.type) return;
      if (action.type === "playSurah" && action.surah && SURAH_INFO[action.surah]) {
        setTab("tools");
        const ayahs = await loadSurah(action.surah);
        if (ayahs) playSurahFrom(ayahs, 0);
      } else if (action.type === "openTab" && typeof action.tab === "string") {
        // Live Activity / home-widget URL tap — AppDelegate writes
        // {type:"openTab", tab:"<host>"}. Map aliases to primary tab names.
        const aliasMap = { salaah: "masjids", ibadah: "tools", quran: "tools", ghidha: "hayah", fasting: "hayah", muraqabah: "hayah", hayah: "hayah", exercise: "hayah" };
        const target = aliasMap[action.tab] || action.tab;
        if (["masjids", "radio", "tools", "hayah", "settings"].includes(target)) {
          setTab(target);
          if (target === "tools") {
            if (action.tab === "quran") setToolView("fullquran");
            else setToolView("grid");
          } else if (target === "hayah") {
            // Fasting and Muraqabah moved out of Hayah — route deep links
            // through Tools so the link still lands on the right screen.
            // Riyadah Live Activity uses host=exercise so its tap lands
            // straight in the active workout.
            if (action.tab === "fasting")        { setTab("tools"); setToolView("fasting"); }
            else if (action.tab === "muraqabah") { setTab("tools"); setToolView("muraqabah"); }
            else if (action.tab === "exercise")  setHayahFeature("exercise");
            else                                  setHayahFeature("ghidha");
          }
        }
      } else if (action.type === "markPrayer" && TRACKABLE_PRAYERS.includes(action.prayer)) {
        const today = action.date || localDateStr();
        setSalahLog(prev => {
          const dayLog = prev[today] || {};
          if (dayLog[action.prayer] === true) return prev;
          const updatedDay = { ...dayLog, [action.prayer]: true };
          const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 90);
          const cutoffStr = cutoff.getFullYear() + "-" + String(cutoff.getMonth()+1).padStart(2,"0") + "-" + String(cutoff.getDate()).padStart(2,"0");
          const pruned = {};
          Object.keys(prev).forEach(k => { if (k >= cutoffStr) pruned[k] = prev[k]; });
          pruned[today] = updatedDay;
          try { localStorage.setItem("salahLog", JSON.stringify(pruned)); } catch {}
          return pruned;
        });
      } else if (action.type === "logFrequentMeal" && action.mealName && action.date) {
        // Written by LogFrequentMealIntent — add the full meal entry to the calorie log.
        try {
          const dateKey = action.date;
          const rawLog = JSON.parse(localStorage.getItem("calorieLog") || "{}");
          const emptyDay = { meals: { breakfast: [], lunch: [], dinner: [], snacks: [] }, water: 0, weight: null, caloriesBurned: 0, steps: 0 };
          const dayData = { ...emptyDay, ...(rawLog[dateKey] || {}), meals: { ...emptyDay.meals, ...(rawLog[dateKey]?.meals || {}) } };
          const slot = action.slot || "snacks";
          const entry = {
            id: "wfm_" + Date.now().toString(36),
            name: action.mealName,
            brand: "Widget", calories: Number(action.calories) || 0,
            protein: Number(action.protein) || 0, carbs: Number(action.carbs) || 0, fat: Number(action.fat) || 0,
            fiber: 0, sugar: 0, sodium: 0,
            servingG: Number(action.servingG) || 100, servingLabel: action.servingLabel || "1 serving",
            source: "widget", halal: true, loggedAt: new Date().toISOString(),
          };
          dayData.meals[slot] = [...(dayData.meals[slot] || []), entry];
          rawLog[dateKey] = dayData;
          localStorage.setItem("calorieLog", JSON.stringify(rawLog));
          const goals = JSON.parse(localStorage.getItem("calorieGoals") || '{"calories":2000,"protein":150,"carbs":225,"fat":65}');
          const allEntries = Object.values(dayData.meals).flat();
          siriWriteCalorieData({
            date: dateKey,
            consumed:    Math.round(allEntries.reduce((s, e) => s + (e.calories || 0), 0)),
            goal:        goals.calories    || 2000,
            protein:     Math.round(allEntries.reduce((s, e) => s + (e.protein  || 0), 0)),
            proteinGoal: goals.protein     || 150,
            carbs:       Math.round(allEntries.reduce((s, e) => s + (e.carbs    || 0), 0)),
            carbsGoal:   goals.carbs       || 225,
            fat:         Math.round(allEntries.reduce((s, e) => s + (e.fat      || 0), 0)),
            fatGoal:     goals.fat         || 65,
          });
          window.dispatchEvent(new CustomEvent("sirat:calorieSync"));
        } catch {}
      } else if (action.type === "quickAddCalories" && action.calories && action.date) {
        // Written by QuickAddCaloriesIntent — add a lightweight food entry to the
        // calorie log so the widget quick-add round-trips back into the app data.
        const calories = Number(action.calories) || 0;
        if (calories > 0) {
          try {
            const dateKey = action.date;
            const rawLog = JSON.parse(localStorage.getItem("calorieLog") || "{}");
            const emptyDay = { meals: { suhoor: [], breakfast: [], lunch: [], asr: [], dinner: [], isha: [] }, water: 0, weight: null, caloriesBurned: 0, steps: 0 };
            const dayData = { ...emptyDay, ...(rawLog[dateKey] || {}), meals: { ...emptyDay.meals, ...(rawLog[dateKey]?.meals || {}) } };
            const entry = {
              id: "wqa_" + Date.now().toString(36),
              name: `Quick Add · ${calories} kcal`,
              brand: "Widget", calories, protein: 0, carbs: 0, fat: 0,
              fiber: 0, sugar: 0, sodium: 0, servingG: 1, servingLabel: "1 serving",
              source: "widget_quick_add", halal: true, loggedAt: new Date().toISOString()
            };
            dayData.meals.breakfast = [...(dayData.meals.breakfast || []), entry];
            rawLog[dateKey] = dayData;
            localStorage.setItem("calorieLog", JSON.stringify(rawLog));
            // Re-sync the updated totals back to the widget
            const goals = JSON.parse(localStorage.getItem("calorieGoals") || '{"calories":2000,"protein":150,"carbs":225,"fat":65}');
            const allEntries = Object.values(dayData.meals).flat();
            siriWriteCalorieData({
              date: dateKey,
              consumed:    Math.round(allEntries.reduce((s, e) => s + (e.calories || 0), 0)),
              goal:        goals.calories    || 2000,
              protein:     Math.round(allEntries.reduce((s, e) => s + (e.protein  || 0), 0)),
              proteinGoal: goals.protein     || 150,
              carbs:       Math.round(allEntries.reduce((s, e) => s + (e.carbs    || 0), 0)),
              carbsGoal:   goals.carbs       || 225,
              fat:         Math.round(allEntries.reduce((s, e) => s + (e.fat      || 0), 0)),
              fatGoal:     goals.fat         || 65,
            });
            // Let CalorieCounter pick up the change if it's mounted
            window.dispatchEvent(new CustomEvent("sirat:calorieSync"));
          } catch {}
        }
      } else if (action.type === "syncCompletion" && action.date) {
        // Written by MarkPrayerFromWidgetIntent — flat 1/0 integers per prayer.
        // Using flat structure avoids Capacitor bridge dropping false/0 values
        // from nested NSDictionary objects.
        const dateKey = action.date;
        setSalahLog(prev => {
          const dayLog = prev[dateKey] || {};
          const updated = { ...dayLog };
          TRACKABLE_PRAYERS.forEach(p => {
            if (p in action) updated[p] = action[p] === 1;
          });
          const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 90);
          const cutoffStr = cutoff.getFullYear() + "-" + String(cutoff.getMonth()+1).padStart(2,"0") + "-" + String(cutoff.getDate()).padStart(2,"0");
          const pruned = {};
          Object.keys(prev).forEach(k => { if (k >= cutoffStr) pruned[k] = prev[k]; });
          pruned[dateKey] = updated;
          try { localStorage.setItem("salahLog", JSON.stringify(pruned)); } catch {}
          siriWriteWeeklyLog(pruned);
          return pruned;
        });
      }
    };
    // On every foreground, read sirat.completion.<today> directly from the
    // App Group UserDefaults key written by the widget extension. This is the
    // reliable path — no cross-process pendingAction intermediary needed.
    const syncFromWidget = async () => {
      const today = localDateStr();
      const comp = await siriReadPrayerCompletion(today);
      if (!comp) return;
      setSalahLog(prev => {
        const dayLog = prev[today] || {};
        const updated = { ...dayLog };
        let changed = false;
        TRACKABLE_PRAYERS.forEach(p => {
          const val = comp[p] === 1;
          if (!!updated[p] !== val) { updated[p] = val; changed = true; }
        });
        if (!changed) return prev;
        const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 90);
        const cutoffStr = cutoff.getFullYear() + "-" + String(cutoff.getMonth()+1).padStart(2,"0") + "-" + String(cutoff.getDate()).padStart(2,"0");
        const pruned = {};
        Object.keys(prev).forEach(k => { if (k >= cutoffStr) pruned[k] = prev[k]; });
        pruned[today] = updated;
        try { localStorage.setItem("salahLog", JSON.stringify(pruned)); } catch {}
        siriWriteWeeklyLog(pruned);
        return pruned;
      });
    };

    handlePending();
    syncFromWidget();
    const onVis = () => {
      if (document.visibilityState === "visible") {
        handlePending();
        syncFromWidget();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    // Live Activity widgetURL taps push a NotificationCenter event that the
    // SiratIntents plugin forwards as "openURL". This is the warm-resume
    // path — fires before visibilitychange settles UserDefaults sync.
    const unsubscribeURL = siriAddOpenURLListener(({ host }) => {
      const aliasMap = { salaah: "masjids", ibadah: "tools", quran: "tools", ghidha: "hayah", fasting: "hayah", muraqabah: "hayah", hayah: "hayah", exercise: "hayah" };
      const target = aliasMap[host] || host;
      if (["masjids", "radio", "tools", "hayah", "settings"].includes(target)) {
        setTab(target);
        if (target === "tools") {
          if (host === "quran") setToolView("fullquran");
          else setToolView("grid");
        } else if (target === "hayah") {
          if (host === "fasting")        { setTab("tools"); setToolView("fasting"); }
          else if (host === "muraqabah") { setTab("tools"); setToolView("muraqabah"); }
          else if (host === "exercise")  setHayahFeature("exercise");
          else                            setHayahFeature("ghidha");
        }
      }
    });
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      unsubscribeURL();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── iCloud Key-Value sync (iOS native only) ─────────────────────────────
  // Hydrate-on-mount + remote-change listener: applies merges and updates
  // both localStorage and React state for synced keys. Per-state push
  // effects below mirror local state to iCloud whenever it changes.
  useEffect(() => {
    if (!window.Capacitor?.isNativePlatform?.()) return;
    let unsubscribe = () => {};

    const applyMerge = (key, mergedStr) => {
      if (mergedStr == null) return;
      try { localStorage.setItem(key, mergedStr); } catch {}
      switch (key) {
        case "salahLog":        try { setSalahLog(JSON.parse(mergedStr)); } catch {} break;
        case "salahStreak":     try { setSalahStreak(JSON.parse(mergedStr)); } catch {} break;
        case "isPro":           setIsPro(mergedStr === "true"); break;
        case "proExpiry":       setProExpiry(mergedStr || null); break;
        case "trialStart":      setTrialStart(mergedStr || null); break;
        case "userName":        setUserName(mergedStr); break;
        case "userSurname":     setUserSurname(mergedStr); break;
        case "userEmail":       setUserEmail(mergedStr); break;
        case "profilePhoto":    setProfilePhoto(mergedStr); break;
        case "darkMode":        setDarkMode(mergedStr === "true"); break;
        case "selectedReciter": setSelectedReciter(mergedStr); break;
        case "school":          setSchool(parseInt(mergedStr) || 1); break;
        case "surahBookmark":   try { setSurahBookmark(JSON.parse(mergedStr)); } catch {} break;
        case "recentSurahs":    try { setRecentSurahs(JSON.parse(mergedStr)); } catch {} break;
        case "quranFontSize": { const n = parseInt(mergedStr); if (n > 0) setQuranFontSize(n); break; }
        case "userWhatsapp":    setUserWhatsapp(mergedStr); break;
        case "followedMounts":  try { setFollowedMounts(JSON.parse(mergedStr)); } catch {} break;
        case "followedNames":   try { setFollowedNames(JSON.parse(mergedStr)); } catch {} break;
        case "masjidOrder":     try { setMasjidOrder(JSON.parse(mergedStr)); } catch {} break;
        case "radioOrder":      try { setRadioOrder(JSON.parse(mergedStr)); } catch {} break;
        case "notifEnabled":    setNotifEnabled(mergedStr === "true"); break;
        // fastingLog, fastingStreak, tasbeeh, duaJournal, azkarCounts,
        // tasbeehSet, tajweedStats, tabsConfig, etc. live in child
        // components or are read directly from localStorage — localStorage
        // update is enough; they re-read on next mount. No setter call
        // required here.
        default: break;
      }
    };

    const applyKeySet = (remote) => {
      if (!remote) return;
      Object.keys(remote).forEach((key) => {
        if (!ICLOUD_SYNCED_KEYS.includes(key)) return;
        const local = localStorage.getItem(key);
        const merged = icloudMergeIfNeeded(key, local, remote[key]);
        if (merged != null) applyMerge(key, merged);
      });
    };

    icloudPullAll().then(applyKeySet);
    unsubscribe = icloudAttachListener(applyKeySet);
    return () => { try { unsubscribe(); } catch {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Per-state push effects — mirror local state to iCloud whenever it
  // changes. NSUbiquitousKeyValueStore dedupes identical writes internally,
  // so re-pushes after a hydrate-driven setState are harmless no-ops.
  useEffect(() => { icloudPushOne("salahLog", JSON.stringify(salahLog)); }, [salahLog]);
  useEffect(() => { icloudPushOne("salahStreak", JSON.stringify(salahStreak)); }, [salahStreak]);
  useEffect(() => { icloudPushOne("isPro", String(isPro)); }, [isPro]);
  useEffect(() => { icloudPushOne("proExpiry", proExpiry); }, [proExpiry]);
  useEffect(() => { icloudPushOne("trialStart", trialStart); }, [trialStart]);
  useEffect(() => { icloudPushOne("userName", userName); }, [userName]);
  useEffect(() => { icloudPushOne("userSurname", userSurname); }, [userSurname]);
  useEffect(() => { icloudPushOne("userEmail", userEmail); }, [userEmail]);
  useEffect(() => { icloudPushOne("profilePhoto", profilePhoto); }, [profilePhoto]);
  useEffect(() => { icloudPushOne("darkMode", String(darkMode)); }, [darkMode]);
  useEffect(() => { icloudPushOne("selectedReciter", selectedReciter); }, [selectedReciter]);
  useEffect(() => { icloudPushOne("school", String(school)); }, [school]);
  useEffect(() => { icloudPushOne("surahBookmark", JSON.stringify(surahBookmark)); }, [surahBookmark]);
  useEffect(() => { icloudPushOne("recentSurahs", JSON.stringify(recentSurahs)); }, [recentSurahs]);
  useEffect(() => { icloudPushOne("quranFontSize", String(quranFontSize)); }, [quranFontSize]);
  useEffect(() => { icloudPushOne("userWhatsapp", userWhatsapp); }, [userWhatsapp]);
  useEffect(() => { icloudPushOne("followedMounts", JSON.stringify(followedMounts)); }, [followedMounts]);
  useEffect(() => { icloudPushOne("followedNames", JSON.stringify(followedNames)); }, [followedNames]);
  useEffect(() => { icloudPushOne("masjidOrder", JSON.stringify(masjidOrder)); }, [masjidOrder]);
  useEffect(() => { icloudPushOne("radioOrder", JSON.stringify(radioOrder)); }, [radioOrder]);
  useEffect(() => { icloudPushOne("notifEnabled", String(notifEnabled)); }, [notifEnabled]);

  // Periodic snapshot for synced keys that live in child components
  // (fastingLog, tasbeeh, duaJournal, ...). React state effects above
  // miss these; this catches them every 30s + on foreground.
  // Initial flush is deferred 4 seconds so the 77-key burst doesn't
  // saturate the Capacitor bridge during the startup initialization storm
  // (which causes up to 97 simultaneous plugin.setValue calls on mount,
  // contributing to UI unresponsiveness on first open after a new build).
  useEffect(() => {
    if (!window.Capacitor?.isNativePlatform?.()) return;
    const lastPushed = {};
    const flush = () => {
      ICLOUD_SYNCED_KEYS.forEach((key) => {
        let v;
        if (key === "calorieLog") {
          // Trim to last 90 days before pushing — iCloud KV has a 1 MB
          // aggregate limit and a full multi-year log would exceed it.
          try {
            const full = JSON.parse(localStorage.getItem("calorieLog") || "{}");
            const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 90);
            const cutoffStr = cutoff.toISOString().slice(0, 10);
            v = JSON.stringify(Object.fromEntries(Object.entries(full).filter(([d]) => d >= cutoffStr)));
          } catch { v = localStorage.getItem(key); }
        } else {
          v = localStorage.getItem(key);
        }
        if (v == null) return;
        if (lastPushed[key] === v) return;
        lastPushed[key] = v;
        icloudPushOne(key, v);
      });
    };
    const initTimer = setTimeout(flush, 4000);
    const interval = setInterval(flush, 30000);
    const onVis = () => { if (document.visibilityState === "visible") flush(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearTimeout(initTimer); clearInterval(interval); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  // ── Hayah backend sync ───────────────────────────────────────────────────
  // Pulls full Hayah history from the server on login so data survives
  // logout, reinstall, or a device switch regardless of iCloud availability.
  // Pushes any changed keys every 60 s + on foreground.
  useEffect(() => {
    if (!userId) return;
    const HAYAH_KEYS = [
      "hayahPrefs","ghidhaProfile","calorieGoals","caloriesFavorites",
      "calorieWeightLog","calorieFrequency","calorieLog","sirat-workouts","ghidhaMigrationV",
    ];

    const pull = async () => {
      try {
        const r = await fetch(`${BACKEND}/api/hayah/${userId}`);
        if (!r.ok) return;
        const { data } = await r.json();
        if (!data) return;
        HAYAH_KEYS.forEach((key) => {
          if (data[key] == null) return;
          const serverVal = typeof data[key] === "string" ? data[key] : JSON.stringify(data[key]);
          const local = localStorage.getItem(key);
          if (key === "calorieLog") {
            // Date-based merge: keep whichever side has more items per day
            let lObj = {}; try { lObj = JSON.parse(local || "{}"); } catch {}
            let sObj = {}; try { sObj = JSON.parse(serverVal); } catch {}
            const merged = { ...sObj };
            Object.keys(lObj).forEach((dt) => {
              const lItems = Object.values(lObj[dt]?.meals || {}).flat().length;
              const sItems = Object.values(merged[dt]?.meals || {}).flat().length;
              if (lItems >= sItems) merged[dt] = lObj[dt];
            });
            const mergedStr = JSON.stringify(merged);
            if (mergedStr !== local) localStorage.setItem(key, mergedStr);
          } else if (!local) {
            // Restore from server only if the device has no local data
            localStorage.setItem(key, serverVal);
          }
        });
        // Signal CalorieCounter to re-read state from localStorage
        window.dispatchEvent(new CustomEvent("hayah-server-sync"));
      } catch {}
    };

    pull();

    const lastPushed = {};
    const push = async () => {
      const payload = {};
      HAYAH_KEYS.forEach((key) => {
        const v = localStorage.getItem(key);
        if (v == null) return;
        if (lastPushed[key] === v) return;
        payload[key] = v;
        lastPushed[key] = v;
      });
      if (Object.keys(payload).length === 0) return;
      try {
        await fetch(`${BACKEND}/api/hayah/sync`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId, data: payload }),
        });
      } catch {}
    };

    const initTimer = setTimeout(push, 8000);
    const interval = setInterval(push, 60000);
    const onVis = () => { if (document.visibilityState === "visible") push(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearTimeout(initTimer); clearInterval(interval); document.removeEventListener("visibilitychange", onVis); };
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Ahzab 35 helpers ─────────────────────────────────────────────────────
  const loadAhzab35 = async (reciterId) => {
    if (!ahzab35TextRef.current) {
      const [arRes, enRes] = await Promise.all([
        fetch("https://api.alquran.cloud/v1/ayah/33:35"),
        fetch("https://api.alquran.cloud/v1/ayah/33:35/en.sahih"),
      ]);
      const [arData, enData] = await Promise.all([arRes.json(), enRes.json()]);
      ahzab35TextRef.current = {
        arabic:      arData.data.text,
        translation: enData.data.text,
      };
      setAhzab35TextLoaded(true);
    }
    if (!ahzab35CacheRef.current[reciterId]) {
      const audioRes  = await fetch(`https://api.alquran.cloud/v1/ayah/33:35/${reciterId}`);
      const audioData = await audioRes.json();
      ahzab35CacheRef.current[reciterId] = audioData.data.audio;
    }
    return ahzab35CacheRef.current[reciterId];
  };

  const scheduleControlsFade = () => {
    if (ahzab35FadeTimerRef.current) clearTimeout(ahzab35FadeTimerRef.current);
    ahzab35FadeTimerRef.current = setTimeout(() => setAhzab35ShowControls(false), 10000);
  };

  const ahzab35WakeControls = () => {
    setAhzab35ShowControls(true);
    if (ahzab35Ref.current && !ahzab35Ref.current.paused) scheduleControlsFade();
  };

  const playChime = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = 528;
      osc.type = "sine";
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.28, ctx.currentTime + 0.4);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 2.8);
      osc.start(); osc.stop(ctx.currentTime + 2.8);
    } catch {}
  };

  const startAhzab35Countdown = (durationMinutes) => {
    if (ahzab35CountdownRef.current) clearInterval(ahzab35CountdownRef.current);
    if (!durationMinutes) { setAhzab35SecondsLeft(null); return; }
    let secs = durationMinutes * 60;
    setAhzab35SecondsLeft(secs);
    ahzab35CountdownRef.current = setInterval(() => {
      secs -= 1;
      setAhzab35SecondsLeft(secs);
      if (secs <= 0) {
        clearInterval(ahzab35CountdownRef.current);
        stopAhzab35();
        setAhzab35SessionDone(true);
        setTimeout(() => setAhzab35SessionDone(false), 4000);
        playChime();
      }
    }, 1000);
  };

  const stopAhzab35 = () => {
    if (ahzab35Ref.current) {
      ahzab35Ref.current.pause();
      ahzab35Ref.current.onended = null;
      ahzab35Ref.current.src = "";
    }
    setAhzab35Playing(false);
    setAhzab35Loading(false);
    clearInterval(ahzab35CountdownRef.current);
    clearTimeout(ahzab35FadeTimerRef.current);
    setAhzab35SecondsLeft(null);
    setAhzab35ShowControls(true);
    if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "none";
  };

  const pauseAhzab35 = () => {
    ahzab35Ref.current?.pause();
    setAhzab35Playing(false);
    clearTimeout(ahzab35FadeTimerRef.current);
    setAhzab35ShowControls(true);
    if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "paused";
  };

  const playAhzab35 = async (reciterId) => {
    setAhzab35Loading(true);
    try {
      const audioUrl = await loadAhzab35(reciterId);
      playAttemptRef.current++;
      if (bufferingTimeoutRef.current) clearTimeout(bufferingTimeoutRef.current);
      const radioAudio = getAudio(); radioAudio.pause(); setPlaying(null); setPlayStatus("stopped");
      if (surahCurrentAyahRef.current !== null) pauseSurah();
      if (!ahzab35Ref.current) ahzab35Ref.current = new Audio();
      const buf = ahzab35Ref.current;
      buf.src = audioUrl;
      buf.onended = () => {
        if (ahzab35LoopRef.current) { buf.currentTime = 0; buf.play().catch(() => {}); }
        else { setAhzab35Playing(false); }
      };
      if ("mediaSession" in navigator) {
        const reciter = RECITERS.find(r => r.id === reciterId) || RECITERS[0];
        navigator.mediaSession.metadata = new MediaMetadata({
          title:   "Al-Ahzab · Ayah 35",
          artist:  reciter.name,
          album:   "Sirat — Quran Reader",
          artwork: [{ src: "/logo192.png", sizes: "192x192", type: "image/png" }],
        });
        navigator.mediaSession.setActionHandler("pause", pauseAhzab35);
        navigator.mediaSession.setActionHandler("play",  () => playAhzab35(ahzab35ReciterId));
      }
      await buf.play();
      setAhzab35Playing(true);
      scheduleControlsFade();
      startAhzab35Countdown(ahzab35DurationRef.current);
      localStorage.setItem("ahzab35LastOpen", "1");
      localStorage.setItem("ahzab35LastReciter", reciterId);
    } catch (e) {
      console.error("Ahzab35 play error", e);
    } finally {
      setAhzab35Loading(false);
    }
  };

  const switchAhzab35Reciter = async (reciterId) => {
    const wasPlaying = ahzab35Playing;
    stopAhzab35();
    setAhzab35ReciterId(reciterId);
    ahzab35LoopRef.current = ahzab35Loop;
    if (wasPlaying) await playAhzab35(reciterId);
  };

  // Keep refs in sync with state for use inside async/closure contexts
  useEffect(() => { ahzab35LoopRef.current = ahzab35Loop; }, [ahzab35Loop]);
  useEffect(() => { ahzab35DurationRef.current = ahzab35Duration; }, [ahzab35Duration]);

  const loadQuranSurah = async (num, targetAyah = null) => {
    setQuranReadNum(num);
    setQuranCurrentPage(0);
    if (targetAyah) setQuranScrollTarget(targetAyah);
    setToolView("fullquranreading");
    if (quranReadCacheRef.current[num]) return;
    setQuranReadLoading(true);
    try {
      const [arRes, enRes] = await Promise.all([
        fetch("https://api.alquran.cloud/v1/surah/" + num),
        fetch("https://api.alquran.cloud/v1/surah/" + num + "/en.sahih"),
      ]);
      const [arData, enData] = await Promise.all([arRes.json(), enRes.json()]);
      const ayahs = arData.data.ayahs.map((a, i) => ({
        number: a.numberInSurah,
        arabic: a.text,
        translation: enData.data.ayahs[i].text,
      }));
      quranReadCacheRef.current = { ...quranReadCacheRef.current, [num]: ayahs };
      setQuranReadCache(c => ({ ...c, [num]: ayahs }));
    } catch {
      alert("Could not load surah. Please check your connection.");
      setToolView("fullquran");
    }
    setQuranReadLoading(false);
  };

  // Quran search — local surah filter + API ayah text search
  const searchQuran = (q) => {
    setQuranSearchQuery(q);
    clearTimeout(quranSearchTimer.current);
    if (!q.trim()) { setQuranSearchResults(null); setQuranSearchLoading(false); return; }
    const lower = q.toLowerCase().trim();
    // Instant local surah name/number match
    const surahMatches = QURAN_SURAHS.filter(s =>
      s.translit.toLowerCase().includes(lower) ||
      s.english.toLowerCase().includes(lower) ||
      s.arabic.includes(q) ||
      String(s.num) === lower
    ).map(s => ({ type: "surah", surah: s }));
    setQuranSearchResults(surahMatches);
    // Debounced API search for ayah text (only if query is 3+ chars)
    if (q.trim().length >= 3) {
      setQuranSearchLoading(true);
      quranSearchTimer.current = setTimeout(async () => {
        try {
          const res = await fetch("https://api.alquran.cloud/v1/search/" + encodeURIComponent(q.trim()) + "/all/en.sahih");
          const data = await res.json();
          if (data.code === 200 && data.data?.matches) {
            const ayahResults = data.data.matches.slice(0, 20).map(m => ({
              type: "ayah",
              surahNum: m.surah.number,
              surahName: m.surah.englishName,
              ayahNum: m.numberInSurah,
              text: m.text,
            }));
            // Merge: surah matches first, then ayah results
            setQuranSearchResults(prev => [...(prev || []).filter(r => r.type === "surah"), ...ayahResults]);
          }
        } catch {}
        setQuranSearchLoading(false);
      }, 500);
    }
  };

  // Debounced city search for autocomplete suggestions
  const fetchCitySuggestions = (query) => {
    if (citySearchTimerRef.current) clearTimeout(citySearchTimerRef.current);
    if (!query || query.length < 3) { setCitySuggestions([]); return; }
    citySearchTimerRef.current = setTimeout(async () => {
      try {
        const mapResult = d => {
          const a = d.address || {};
          const parts = [a.suburb || a.neighbourhood || "", a.city || a.town || a.village || "", a.country || ""].filter(Boolean);
          return { lat: parseFloat(d.lat), lng: parseFloat(d.lon), label: parts.join(", ") || d.display_name, display_name: d.display_name };
        };
        const dedupe = (results) => {
          const seen = new Set();
          return results.filter(r => {
            if (seen.has(r.label)) return false;
            seen.add(r.label);
            return true;
          });
        };
        // Search SA first, then international if few results
        const saRes = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=8&addressdetails=1&countrycodes=za`, { headers: { "Accept-Language": "en" } });
        const saData = await saRes.json();
        const saResults = dedupe(saData.map(mapResult));
        if (saResults.length >= 3) {
          setCitySuggestions(saResults.slice(0, 5));
        } else {
          const intRes = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&addressdetails=1`, { headers: { "Accept-Language": "en" } });
          const intData = await intRes.json();
          // SA results first, then international (deduped by label)
          const merged = dedupe([...saData.map(mapResult), ...intData.map(mapResult)]).slice(0, 5);
          setCitySuggestions(merged);
        }
      } catch { setCitySuggestions([]); }
    }, 400);
  };

  // Select a city suggestion
  const selectCitySuggestion = (suggestion) => {
    setUserLat(suggestion.lat); setUserLng(suggestion.lng);
    localStorage.setItem("userLat", String(suggestion.lat));
    localStorage.setItem("userLng", String(suggestion.lng));
    setDetectedCity(suggestion.label);
    localStorage.setItem("detectedCity", suggestion.label);
    setManualCityInput(suggestion.label);
    setCitySuggestions([]);
    syncFollowsToBackend(followedMounts, effectiveNotify);
  };

  // Search and set manual city location via Nominatim geocoding
  const searchManualCity = async (query) => {
    if (!query || query.length < 2) return;
    setCitySuggestions([]);
    setLocationLoading(true);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1&addressdetails=1`, { headers: { "Accept-Language": "en" } });
      const data = await res.json();
      if (data.length > 0) {
        const { lat, lon } = data[0];
        const a = data[0].address || {};
        const latF = parseFloat(lat), lngF = parseFloat(lon);
        setUserLat(latF); setUserLng(lngF);
        localStorage.setItem("userLat", String(latF));
        localStorage.setItem("userLng", String(lngF));
        const parts = [a.suburb || a.neighbourhood || "", a.city || a.town || a.village || "", a.country || ""].filter(Boolean);
        const label = parts.join(", ") || data[0].display_name;
        setDetectedCity(label);
        localStorage.setItem("detectedCity", label);
        syncFollowsToBackend(followedMounts, effectiveNotify);
      }
    } catch {}
    setLocationLoading(false);
  };

  const handleAppleAuth = async () => {
    setRegistering(true);
    try {
      const { signInWithApple } = await import("./appleAuth");
      const data = await signInWithApple({ inviteCode: inviteCode || undefined });
      const u = data.user;
      localStorage.setItem("azaan-userId", u._id);
      setUserCookie(u._id);
      if (u.name) { localStorage.setItem("userName", u.name); setUserName(u.name); }
      if (u.surname) { localStorage.setItem("userSurname", u.surname); setUserSurname(u.surname); }
      if (u.email) { localStorage.setItem("userEmail", u.email); setUserEmail(u.email); }
      if (!localStorage.getItem("registeredAt")) {
        localStorage.setItem("registeredAt", new Date().toISOString());
      }
      setUserId(u._id);
      await checkUserStatus(); // sync pro/features immediately after Apple sign-in
      if (data.isNew && !localStorage.getItem("ceremonyShown")) {
        localStorage.setItem("ceremonyShown", "1");
        ceremonyNameRef.current = (u.name || "Friend").trim();
        setShowOnboardingCeremony(true);
        setCeremonyFading(false);
        setTimeout(() => {
          setCeremonyFading(true);
          setTimeout(() => {
            setShowOnboardingCeremony(false);
            setCeremonyFading(false);
            setWelcomeName(u.name || "");
            setShowWelcome(true);
            welcomeTimerRef.current = setTimeout(() => { setShowWelcome(false); if (!localStorage.getItem("permFlowDone")) { setPermFlowStep(0); setShowPermFlow(true); } }, 4000);
          }, 900);
        }, 3500);
      }
    } catch (err) {
      if (err && err.needsInvite) {
        alert("Please enter your invite code first, then tap Sign in with Apple again.");
      } else {
        alert("Apple sign-in: " + (err.message || err));
      }
    } finally {
      setRegistering(false);
    }
  };

  const validateInviteCode = async (code) => {
    const trimmed = (code || "").trim();
    if (!trimmed) return { valid: false, reason: "empty" };
    try {
      const res = await fetch(`${BACKEND}/api/verify-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: trimmed }),
      });
      if (!res.ok) return { valid: false, reason: "server" };
      return await res.json();
    } catch {
      return { valid: false, reason: "network" };
    }
  };

  const handleRegister = async () => {
    if (!userName.trim() || !userSurname.trim() || !inviteCode) {
      alert("Please enter your name, surname, and invite code");
      return;
    }
    setRegistering(true);
    try {
      const res = await fetch(`${BACKEND}/api/users/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: userName.trim(), surname: userSurname.trim(), whatsapp: userWhatsapp.trim() ? "+" + userWhatsapp.replace(/[^0-9]/g, "") : "", email: userEmail.trim().toLowerCase() || undefined, inviteCode, platform: /iPhone|iPad|iPod/.test(navigator.userAgent) ? 'iOS' : /Android/.test(navigator.userAgent) ? 'Android' : 'Web' })
      });
      if (!res.ok) {
        const error = await res.json();
        alert(error.error || "Registration failed");
        setRegistering(false);
        return;
      }
      const data = await res.json();
      localStorage.setItem("azaan-userId", data.user._id);
      setUserCookie(data.user._id); // shared between Safari and PWA on iOS
      localStorage.setItem("userName", userName.trim());
      localStorage.setItem("userSurname", userSurname.trim());
      if (userEmail.trim()) localStorage.setItem("userEmail", userEmail.trim().toLowerCase());
      const wNum = userWhatsapp.replace(/\D/g, "");
      if (wNum.length === 11 && wNum.startsWith("27")) localStorage.setItem("userWhatsapp", wNum);
      if (!localStorage.getItem("registeredAt")) {
        localStorage.setItem("registeredAt", new Date().toISOString());
      }
      if (data.proMinutes !== undefined && data.proMinutes !== null) {
        // Pro code used at registration — activate Pro, skip trial
        setIsPro(true); localStorage.setItem("isPro", "true"); setProCookie();
        if (data.proMinutes === 0) {
          setProExpiry(null); localStorage.removeItem("proExpiry");
        } else {
          const exp = new Date(Date.now() + data.proMinutes * 60000).toISOString();
          setProExpiry(exp); localStorage.setItem("proExpiry", exp);
        }
        // Do NOT set trialStart — code duration is the only access
        setWelcomeProLabel(data.proLabel || (data.proMinutes === 0 ? "Lifetime" : formatTimeRemaining(new Date(Date.now() + data.proMinutes * 60000))));
      } else if (!data.restored) {
        // Fresh registration — app is free, no trial
      } else {
        // Restored session — app is free, no trial
      }
      // Helper: kick off permission flow for new users
      const triggerPermFlow = () => {
        if (!localStorage.getItem("permFlowDone")) {
          setPermFlowStep(0);
          setShowPermFlow(true);
        }
      };
      // Set userId immediately so the main app renders (ceremony/welcome are overlays in the main app)
      setUserId(data.user._id);

      // Show onboarding ceremony once for any user who hasn't seen it yet
      if (!localStorage.getItem("ceremonyShown")) {
        localStorage.setItem("ceremonyShown", "1");
        ceremonyNameRef.current = userName.trim();
        setShowOnboardingCeremony(true);
        setCeremonyFading(false);
        setTimeout(() => {
          setCeremonyFading(true);
          setTimeout(() => {
            setShowOnboardingCeremony(false);
            setCeremonyFading(false);
            if (!data.restored) {
              setWelcomeName(userName);
              setShowWelcome(true);
              welcomeTimerRef.current = setTimeout(() => { setShowWelcome(false); triggerPermFlow(); }, 4000);
            } else {
              triggerPermFlow();
            }
          }, 900);
        }, 3500);
      } else {
        // Already seen ceremony — go straight in
        if (!data.restored) {
          setWelcomeName(userName);
          setShowWelcome(true);
          welcomeTimerRef.current = setTimeout(() => { setShowWelcome(false); triggerPermFlow(); }, 4000);
        } else {
          triggerPermFlow();
        }
      }
    } catch (err) {
      alert("Error: " + err.message);
      setRegistering(false);
    }
  };

  const getCardClass = (station) => {
    if (!playing || playing.url !== station.url) return "";
    if (playStatus === "playing") return "playing";
    if (playStatus === "buffering") return "buffering";
    if (playStatus === "error") return "error";
    return "";
  };

  const StationCard = ({ station, type }) => {
    const cls = getCardClass(station);
    const isActive = playing && playing.url === station.url;
    const isLive = getStreamStatus(station.url);
    const lastSavedUrl = (() => { try { return JSON.parse(localStorage.getItem("lastStation"))?.url; } catch { return null; } })();
    const isLastPlayed = !isActive && lastSavedUrl === station.url;

    const ANSAAR_LOGO = (
      <svg viewBox="0 0 54 54" style={{width:"100%",height:"100%"}} xmlns="http://www.w3.org/2000/svg">
        <circle cx="27" cy="27" r="27" fill="#fff"/>
        <circle cx="27" cy="27" r="25.5" fill="none" stroke="#9e9e9e" strokeWidth="2.5"/>
        <circle cx="27" cy="27" r="21" fill="none" stroke="#c8102e" strokeWidth="2"/>
        <text x="7" y="31" fill="#c8102e" fontSize="11" fontWeight="900" fontFamily="Arial,Helvetica,sans-serif">al</text>
        <text x="19" y="31" fill="#888" fontSize="11" fontWeight="700" fontFamily="Arial,Helvetica,sans-serif">ansaar</text>
        <text x="27" y="40" textAnchor="middle" fill="#bbb" fontSize="6.5" fontFamily="Arial,Helvetica,sans-serif" letterSpacing="0.5">Radio</text>
      </svg>
    );
    const CII_LOGO = (
      <svg viewBox="0 0 54 54" style={{width:"100%",height:"100%"}} xmlns="http://www.w3.org/2000/svg">
        <rect width="54" height="54" fill="#141414"/>
        <text x="7" y="38" fill="#fff" fontSize="22" fontWeight="900" fontFamily="-apple-system,BlinkMacSystemFont,sans-serif" letterSpacing="-1">cii</text>
        <circle cx="43" cy="15" r="9" fill="none" stroke="#e05c00" strokeWidth="1.8"/>
        <ellipse cx="43" cy="15" rx="4.5" ry="9" fill="none" stroke="#e05c00" strokeWidth="1.2"/>
        <line x1="34" y1="15" x2="52" y2="15" stroke="#e05c00" strokeWidth="1.2"/>
      </svg>
    );
    // SVG logo thumbnails — keyed by URL substring for reliability (backend names may vary).
    // Al Ansaar and CII both stream from iono.fm now, so their keys are the
    // channel id, not the host — keying on "iono.fm" gave Al Ansaar CII's logo.
    const STATION_LOGOS_BY_URL = {
      "al-ansaar": ANSAAR_LOGO,
      "/xice/467": ANSAAR_LOGO,
      "/xhls/467": ANSAAR_LOGO,
      "/xice/109": CII_LOGO,
      "/xhls/109": CII_LOGO,
      "bbcmedia": (
        <svg viewBox="0 0 54 54" style={{width:"100%",height:"100%"}} xmlns="http://www.w3.org/2000/svg">
          <rect width="54" height="54" fill="#BB1919"/>
          <text x="27" y="35" textAnchor="middle" fill="white" fontSize="19" fontWeight="900" fontFamily="-apple-system,BlinkMacSystemFont,sans-serif" letterSpacing="1.5">BBC</text>
        </svg>
      ),
      "radioislam": (
        <svg viewBox="0 0 54 54" style={{width:"100%",height:"100%"}} xmlns="http://www.w3.org/2000/svg">
          <rect width="54" height="54" fill="#f4f4f4"/>
          <text x="27" y="25" textAnchor="middle" fill="#1a4d8c" fontSize="12" fontWeight="900" fontFamily="-apple-system,BlinkMacSystemFont,sans-serif" letterSpacing="1.5">RADIO</text>
          <line x1="8" y1="30" x2="46" y2="30" stroke="#ccc" strokeWidth="0.8"/>
          <text x="27" y="43" textAnchor="middle" fill="#8b1a1a" fontSize="14" fontWeight="900" fontFamily="-apple-system,BlinkMacSystemFont,sans-serif" letterSpacing="0.5">ISLAM</text>
        </svg>
      ),
      "cairo": (
        <svg viewBox="0 0 54 54" style={{width:"100%",height:"100%"}} xmlns="http://www.w3.org/2000/svg">
          <rect width="54" height="54" fill="#1a0f00"/>
          <text x="27" y="30" textAnchor="middle" fill="#c8a84b" fontSize="13" fontWeight="800" fontFamily="-apple-system,BlinkMacSystemFont,sans-serif">Cairo</text>
          <text x="27" y="43" textAnchor="middle" fill="#c8a84b" fontSize="9" fontWeight="600" fontFamily="-apple-system,BlinkMacSystemFont,sans-serif" opacity="0.65">EGYPT</text>
        </svg>
      ),
    };
    const stationLogo = Object.entries(STATION_LOGOS_BY_URL).find(([key]) =>
      station.url.toLowerCase().includes(key)
    )?.[1] || null;
    // Fallback initials for unknown stations
    const stationInitials = station.name.split(" ").filter(Boolean).slice(0,2).map(w=>w[0].toUpperCase()).join("");
    const fallbackPalette = ["#00c9a7","#ffb300","#6c5ce7","#00b894","#0984e3","#a29bfe"];
    const fallbackColor = fallbackPalette[station.name.split("").reduce((a,c)=>a+c.charCodeAt(0),0) % fallbackPalette.length];

    if (type === "radio" || type === "radio-landscape") {
      const isLandscape = type === "radio-landscape";

      if (isLandscape) {
        return (
          <div className={`station-card ${cls}`} onClick={() => playStation(station)} style={{
            padding:"12px 14px", alignItems:"center", gap:"12px",
          }}>
            <div style={{
              width:"52px",height:"52px",borderRadius:"14px",overflow:"hidden",
              border:`1.5px solid ${isActive?"var(--acc2)":stationLogo?"rgba(255,255,255,0.08)":fallbackColor+"50"}`,
              display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,
              background:isActive&&playStatus==="playing"?"rgba(0,201,167,0.15)":stationLogo?"transparent":`${fallbackColor}1a`,
            }}>
              {isActive && playStatus === "playing"
                ? <span style={{fontSize:"18px",color:"var(--accent)"}}>♫</span>
                : stationLogo || <span style={{fontSize:"12px",fontWeight:"900",color:fallbackColor}}>{stationInitials}</span>
              }
            </div>
            <div className="s-info">
              <div className="s-name">{station.name}</div>
              <div className="s-badges" style={{marginTop:"4px"}}>
                {!isActive && isLive === true && <span className="badge badge-live">LIVE</span>}
                {!isActive && isLive !== true && <span className="badge badge-offline">Offline</span>}
                {isActive && playStatus === "playing"   && <span className="badge badge-playing">Playing</span>}
                {isActive && playStatus === "buffering" && <span className="badge badge-buffering">Connecting</span>}
                {isActive && playStatus === "error"     && <span className="badge badge-error">Offline</span>}
              </div>
            </div>
          </div>
        );
      }

      // Portrait — horizontal card, flex:1 to fill screen
      return (
        <div className={`station-card ${cls}`} onClick={() => playStation(station)} style={{
          flex:1, padding:"20px 24px", alignItems:"center", gap:"20px",
        }}>
          <div style={{
            width:"100px",height:"100px",borderRadius:"22px",overflow:"hidden",
            border:`1.5px solid ${isActive?"var(--acc2)":stationLogo?"rgba(255,255,255,0.08)":fallbackColor+"50"}`,
            display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,
            background:isActive&&playStatus==="playing"?"rgba(0,201,167,0.15)":stationLogo?"transparent":`${fallbackColor}1a`,
            transition:"all .3s",
          }}>
            {isActive && playStatus === "playing"
              ? <span style={{fontSize:"28px",color:"var(--accent)"}}>♫</span>
              : stationLogo || <span style={{fontSize:"20px",fontWeight:"900",color:fallbackColor}}>{stationInitials}</span>
            }
          </div>
          <div className="s-info">
            <div style={{fontSize:"24px",fontWeight:"800",whiteSpace:"normal",lineHeight:1.2,marginBottom:"6px"}}>{station.name}</div>
            <div style={{fontSize:"12px",color:darkMode?"rgba(116,185,255,0.5)":"rgba(80,130,190,0.85)",fontWeight:"700",letterSpacing:"1.5px",marginBottom:"10px"}}>LIVE RADIO</div>
            <div className="s-badges" style={{gap:"8px"}}>
              {!isActive && isLive === true && <span className="badge badge-live" style={{fontSize:"13px",padding:"5px 14px"}}>LIVE</span>}
              {!isActive && isLive !== true && <span className="badge badge-offline" style={{fontSize:"13px",padding:"5px 14px"}}>Offline</span>}
              {isLastPlayed && <span className="badge" style={{background:"rgba(255,179,0,0.12)",color:"var(--gold)",border:"1px solid rgba(255,179,0,0.25)",fontSize:"11px",padding:"5px 12px",letterSpacing:"1.5px"}}>Last played</span>}
              {isActive && playStatus === "playing"   && <span className="badge badge-playing" style={{fontSize:"13px",padding:"5px 14px"}}>Playing</span>}
              {isActive && playStatus === "buffering" && <span className="badge badge-buffering" style={{fontSize:"13px",padding:"5px 14px"}}>Connecting</span>}
              {isActive && playStatus === "error"     && <span className="badge badge-error" style={{fontSize:"13px",padding:"5px 14px"}}>Offline</span>}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={`station-card ${cls}`} onClick={() => playStation(station)} style={{padding:"28px 24px",alignItems:"center"}}>
        {/* Avatar */}
        <div style={{
          width:"72px",height:"72px",borderRadius:"18px",overflow:"hidden",
          border:`1.5px solid ${isActive?"var(--acc2)":stationLogo?"rgba(255,255,255,0.08)":fallbackColor+"50"}`,
          display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,
          background:isActive&&playStatus==="playing"?"rgba(0,201,167,0.15)":stationLogo?"transparent":`${fallbackColor}1a`,
          transition:"all .3s",
        }}>
          {isActive && playStatus === "playing"
            ? <span style={{fontSize:"22px",color:"var(--accent)"}}>♫</span>
            : stationLogo
              ? stationLogo
              : <span style={{fontSize:"14px",fontWeight:"900",color:fallbackColor,letterSpacing:"-0.5px"}}>{stationInitials}</span>
          }
        </div>
        <div className="s-info">
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",width:"100%"}}>
            <div style={{flex:1,minWidth:0}}>
              <div className="s-name" style={{fontSize:"19px"}}>{station.name}</div>
              <div style={{fontSize:"11px",color:darkMode?"rgba(116,185,255,0.5)":"rgba(80,130,190,0.85)",fontWeight:"700",letterSpacing:"1.2px",marginBottom:"6px"}}>LIVE RADIO</div>
            </div>
            {station.websiteUrl && (
              <a href={station.websiteUrl} target="_blank" rel="noreferrer"
                 style={{fontSize:"10px",padding:"4px 10px",background:"transparent",border:"1px solid rgba(116,185,255,.3)",borderRadius:"6px",color:"rgba(116,185,255,.7)",textDecoration:"none",flexShrink:0,marginLeft:"8px"}}
                 onClick={e=>{ e.stopPropagation(); window.open(station.websiteUrl,"_blank"); }}>
                Site
              </a>
            )}
          </div>
          <div className="s-badges">
            {!isActive && isLive === true && <span className="badge badge-live">LIVE</span>}
            {!isActive && isLive !== true && <span className="badge badge-offline">Offline</span>}
            {isLastPlayed && <span className="badge" style={{background:"rgba(255,179,0,0.12)",color:"var(--gold)",border:"1px solid rgba(255,179,0,0.25)",fontSize:"9px",letterSpacing:"1.5px"}}>Last played</span>}
            {isActive && playStatus === "playing"   && <span className="badge badge-playing">Playing</span>}
            {isActive && playStatus === "buffering" && <span className="badge badge-buffering">Connecting</span>}
            {isActive && playStatus === "error"     && <span className="badge badge-error">Offline</span>}
          </div>
        </div>
      </div>
    );
  };

  const NowPlayingBar = () => (
    <div className={`np-bar ${playStatus !== "stopped" && playing ? playStatus : ""}`}>
      <div className="np-label">Now Playing</div>
      <div className="np-inner">
        <div className="np-art">
          {playStatus === "playing"   && <div className="wave" style={{transform:"scale(1.4)",transformOrigin:"center"}}><span/><span/><span/><span/><span/><span/></div>}
          {playStatus === "buffering" && <div className="spinner"/>}
          {playStatus === "error"     && <span style={{fontSize:"22px",color:"var(--danger)"}}>⚠</span>}
          {(!playing || playStatus === "stopped") && <span style={{fontSize:"18px",opacity:0.25}}>○</span>}
        </div>
        <div className="np-info">
          {playing ? (
            <>
              <div className="np-name">{playing.name}</div>
              {playStatus === "buffering" && <div className="np-sub">Connecting...</div>}
              {playStatus === "error"     && <div className="np-sub error">{autoplayBlocked ? "Tap Retry to play" : "Stream offline"}</div>}
            </>
          ) : (
            <div className="np-name stopped">Nothing playing</div>
          )}
        </div>
        {(playStatus === "playing" || playStatus === "buffering") && (
          <button className="btn-stop" onClick={stopAudio}>■ Stop</button>
        )}
        {playStatus === "error" && (
          <button className="btn-retry" onClick={() => playing && playStation(playing)}>Retry</button>
        )}
      </div>
    </div>
  );

  // Surah mini-player — slim bar matching the radio MiniPlayer. Rendered above
  // scroll-content as part of the app's freeze pane so it stays visible on every
  // screen where a surah is loaded (except the full Surah view).
  const SurahMiniPlayer = () => {
    const info = SURAH_INFO[surahKey];
    const name = info?.name || "Surah";
    const total = info?.count || 0;
    const ayahNum = surahCurrentAyah !== null ? surahCurrentAyah + 1 : null;
    const border = surahIsPlaying ? "rgba(0,201,167,0.25)" : "rgba(255,179,0,0.28)";
    const accent = surahIsPlaying ? "var(--accent)" : "var(--gold)";
    return (
      <div onClick={() => { setTab("tools"); setToolView("surah"); }}
        style={{margin:"0 16px",padding:"9px 14px",background:"var(--s1)",border:`1px solid ${border}`,borderRadius:"12px",display:"flex",alignItems:"center",gap:"10px",cursor:"pointer",userSelect:"none"}}>
        {surahIsPlaying && <div className="wave" style={{transform:"scale(0.65)",transformOrigin:"left center",flexShrink:0}}><span/><span/><span/><span/><span/><span/></div>}
        {!surahIsPlaying && <span style={{fontSize:"13px",flexShrink:0,color:"var(--gold)"}}>⏸</span>}
        <span style={{flex:1,fontSize:"12px",fontWeight:"600",color:accent,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
          {name}{ayahNum !== null ? ` · Ayah ${ayahNum}/${total}` : ""}
        </span>
        {surahIsPlaying
          ? <button onClick={e => { e.stopPropagation(); pauseSurah(); }}
              style={{padding:"3px 10px",background:"transparent",border:"1px solid rgba(0,201,167,0.35)",borderRadius:"8px",color:"var(--accent)",fontSize:"11px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>Pause</button>
          : <button onClick={e => { e.stopPropagation(); resumeSurah(surahCache[surahKey + ":" + selectedReciter]); }}
              style={{padding:"3px 10px",background:"transparent",border:"1px solid rgba(255,179,0,0.35)",borderRadius:"8px",color:"var(--gold)",fontSize:"11px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>Play</button>
        }
        <button onClick={e => { e.stopPropagation(); stopSurah(); }}
          style={{padding:"3px 10px",background:"transparent",border:"1px solid rgba(255,107,107,0.3)",borderRadius:"8px",color:"var(--danger)",fontSize:"11px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>Stop</button>
      </div>
    );
  };

  // Slim now-playing indicator for radio streams — lives in the freeze pane.
  const MiniPlayer = () => {
    if (!playing || playStatus === "stopped") return null;
    const isErr = playStatus === "error";
    const isBuf = playStatus === "buffering";
    const accent = isErr ? "var(--danger)" : isBuf ? "var(--gold)" : "var(--accent)";
    const border = isErr ? "rgba(255,107,107,0.25)" : isBuf ? "rgba(255,179,0,0.25)" : "rgba(0,201,167,0.2)";
    return (
      <div style={{margin:"0 16px",padding:"9px 14px",background:"var(--s1)",border:`1px solid ${border}`,borderRadius:"12px",display:"flex",alignItems:"center",gap:"10px"}}>
        {playStatus === "playing" && <div className="wave" style={{transform:"scale(0.65)",transformOrigin:"left center",flexShrink:0}}><span/><span/><span/><span/><span/><span/></div>}
        {isBuf && <div className="spinner" style={{width:"14px",height:"14px",border:"2px solid var(--border)",borderTopColor:"var(--gold)",flexShrink:0}}/>}
        {isErr && <span style={{fontSize:"12px",flexShrink:0}}>⚠</span>}
        <span style={{flex:1,fontSize:"12px",fontWeight:"600",color:accent,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{playing.name}</span>
        {isErr && <button onClick={()=>playStation(playing)} style={{padding:"3px 10px",background:"transparent",border:`1px solid ${border}`,borderRadius:"8px",color:accent,fontSize:"11px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>Retry</button>}
        {!isErr && <button onClick={stopAudio} style={{padding:"3px 10px",background:"transparent",border:"1px solid rgba(255,107,107,0.3)",borderRadius:"8px",color:"var(--danger)",fontSize:"11px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>Stop</button>}
      </div>
    );
  };

  // Subscribe to egzon/dossari audio state so their mini players re-render on play/pause/stop.
  const [, _bumpPlayerState] = useState(0);
  useEffect(() => {
    const force = () => _bumpPlayerState(x => x + 1);
    const u1 = egzonPlayer.subscribe(force);
    const u2 = dossariPlayer.subscribe(force);
    return () => { u1(); u2(); };
  }, []);

  // Reciter mini-player factory — shared row for Egzon & Dossari.
  const ReciterMiniPlayer = ({ player, label, reciterColor, goView, currentView }) => {
    const num = player.playingNum;
    if (num == null) return null;
    if (currentView === goView) return null;
    const surah = player.SURAHS.find(s => s.num === num);
    if (!surah) return null;
    const isPlaying = player.isPlaying;
    const border = isPlaying ? "rgba(0,201,167,0.25)" : "rgba(255,179,0,0.28)";
    const accent = isPlaying ? "var(--accent)" : "var(--gold)";
    return (
      <div onClick={() => { setTab("tools"); setToolView(goView); }}
        style={{margin:"0 16px",padding:"9px 14px",background:"var(--s1)",border:`1px solid ${border}`,borderRadius:"12px",display:"flex",alignItems:"center",gap:"10px",cursor:"pointer",userSelect:"none"}}>
        {isPlaying && <div className="wave" style={{transform:"scale(0.65)",transformOrigin:"left center",flexShrink:0}}><span/><span/><span/><span/><span/><span/></div>}
        {!isPlaying && <span style={{fontSize:"13px",flexShrink:0,color:"var(--gold)"}}>⏸</span>}
        <span style={{flex:1,fontSize:"12px",fontWeight:"600",color:accent,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
          <span style={{color:reciterColor,marginRight:"6px"}}>{label}</span>· {surah.name}
        </span>
        {isPlaying
          ? <button onClick={e => { e.stopPropagation(); player.pause(); }}
              style={{padding:"3px 10px",background:"transparent",border:"1px solid rgba(0,201,167,0.35)",borderRadius:"8px",color:"var(--accent)",fontSize:"11px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>Pause</button>
          : <button onClick={e => { e.stopPropagation(); player.resume(); }}
              style={{padding:"3px 10px",background:"transparent",border:"1px solid rgba(255,179,0,0.35)",borderRadius:"8px",color:"var(--gold)",fontSize:"11px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>Play</button>
        }
        <button onClick={e => { e.stopPropagation(); player.stop(); }}
          style={{padding:"3px 10px",background:"transparent",border:"1px solid rgba(255,107,107,0.3)",borderRadius:"8px",color:"var(--danger)",fontSize:"11px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>Stop</button>
      </div>
    );
  };

  const toggleSalah = (prayerName) => {
    if (!prayerTimes || !TRACKABLE_PRAYERS.includes(prayerName)) return;
    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    if (nowMin < to24min(prayerTimes[prayerName])) return; // can't mark future prayers
    localStorage.setItem("salahHintShown", "1");
    const today = localDateStr();
    const todayLog = salahLog[today] || {};
    const newVal = !todayLog[prayerName];
    const updatedDay = { ...todayLog, [prayerName]: newVal };
    // 90-day pruning
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 90);
    const cutoffStr = cutoff.getFullYear() + "-" + String(cutoff.getMonth()+1).padStart(2,"0") + "-" + String(cutoff.getDate()).padStart(2,"0");
    const pruned = {};
    Object.keys(salahLog).forEach(k => { if (k >= cutoffStr) pruned[k] = salahLog[k]; });
    pruned[today] = updatedDay;
    setSalahLog(pruned);
    localStorage.setItem("salahLog", JSON.stringify(pruned));
    siriWritePrayerCompletion(updatedDay, today);
    siriWriteWeeklyLog(pruned);
    // Sync salah log to backend (debounced — waits 3s after last tap)
    if (window._salahSyncTimer) clearTimeout(window._salahSyncTimer);
    window._salahSyncTimer = setTimeout(() => {
      const uid = localStorage.getItem("azaan-userId");
      if (uid) fetch(`${BACKEND}/api/salah/sync`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ userId: uid, log: pruned, streak: salahStreak }) }).catch(() => {});
    }, 3000);
    // Trigger pop animation
    salahAnimRef.current = prayerName;
    setTimeout(() => { salahAnimRef.current = null; }, 350);
    // Offer dhikr after salah when marking as prayed (not when unmarking)
    if (newVal) {
      setDhikrPrayer(prayerName);
      setDhikrStep(0);
      setDhikrCount(0);
      dhikrTimerRef.current = setTimeout(() => setShowDhikr(true), 400);
    }
    // Haptic on mark (single success pulse on each tap)
    if (newVal) hapticPrayerMarked();
    // Streak rule: ANY prayer logged today counts as a streak day. Bumps once per day.
    // The "all 5" celebration toast still fires separately so completing a perfect day
    // still feels rewarded.
    const yesterday = new Date(now); yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.getFullYear() + "-" + String(yesterday.getMonth()+1).padStart(2,"0") + "-" + String(yesterday.getDate()).padStart(2,"0");
    const anyDoneToday = TRACKABLE_PRAYERS.some(p => updatedDay[p]);
    const allDoneToday = TRACKABLE_PRAYERS.every(p => updatedDay[p]);

    if (newVal && anyDoneToday && salahStreak.lastDate !== today) {
      // First prayer logged today — bump the streak.
      const yLog = pruned[yesterdayStr];
      const yAny = yLog && TRACKABLE_PRAYERS.some(p => yLog[p]);
      const continues = salahStreak.lastDate === yesterdayStr || (yAny && !salahStreak.lastDate);
      const newCount = continues ? salahStreak.count + 1 : 1;
      const newStreak = { count: newCount, lastDate: today };
      setSalahStreak(newStreak);
      localStorage.setItem("salahStreak", JSON.stringify(newStreak));
      if (typeof Preferences !== 'undefined') Preferences.set({ key: 'salahStreak', value: String(newStreak.count ?? 0) }).catch(() => {});
      setSalahToastData({ streak: newStreak.count, allDone: allDoneToday });
      setShowSalahToast(true);
      setTimeout(() => setShowSalahToast(false), 8000);
    }
    if (allDoneToday && newVal) {
      hapticPrayerComplete();
      if (salahStreak.lastDate === today) {
        setSalahToastData({ streak: salahStreak.count, allDone: true });
        setShowSalahToast(true);
        setTimeout(() => setShowSalahToast(false), 8000);
      }
    }
    if (!newVal && salahStreak.lastDate === today && !anyDoneToday) {
      // User unmarked the last prayer of today — undo today's streak bump.
      const ns = { count: Math.max(0, salahStreak.count - 1), lastDate: salahStreak.count > 1 ? yesterdayStr : null };
      setSalahStreak(ns);
      localStorage.setItem("salahStreak", JSON.stringify(ns));
      if (typeof Preferences !== 'undefined') Preferences.set({ key: 'salahStreak', value: String(ns.count ?? 0) }).catch(() => {});
    }
  };

  // Suspended screen
  if (isSuspended) {
    return (
      <div style={{minHeight:"100vh",background:"var(--bg)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"32px 24px",textAlign:"center",gap:"16px"}}>
        <div style={{fontSize:"48px"}}>🚫</div>
        <div style={{fontSize:"20px",fontWeight:"800",color:"var(--text)"}}>Account Suspended</div>
        {suspendReason ? <div style={{fontSize:"14px",color:"var(--muted)",lineHeight:1.6,maxWidth:"300px"}}>Reason: {suspendReason}</div> : null}
        <div style={{fontSize:"13px",color:"var(--muted)",lineHeight:1.6,maxWidth:"300px"}}>Please contact support to resolve this.</div>
        <button onClick={() => { setIsSuspended(false); window.location.reload(); }} style={{marginTop:"8px",padding:"12px 28px",background:"transparent",border:"1px solid var(--border)",borderRadius:"12px",color:"var(--muted)",fontSize:"14px",cursor:"pointer",fontFamily:"inherit"}}>Try Again</button>
      </div>
    );
  }

  // Claim screen — shown while fetching a code from ?claim=1 group invite link
  if (claimingCode || (claimError && !userId)) {
    return (
      <div style={{position:"fixed",inset:0,background:darkMode?"#07090c":"#f0f5fa",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:"20px",padding:"32px"}}>
        <div style={{fontSize:"52px"}}>{claimError ? "😔" : "🌙"}</div>
        {claimError ? (
          <>
            <div style={{fontSize:"18px",fontWeight:"800",color:"var(--text)",textAlign:"center"}}>No Codes Available</div>
            <div style={{fontSize:"13px",color:"var(--muted)",textAlign:"center",lineHeight:1.6,maxWidth:"280px"}}>{claimError}</div>
            <button onClick={() => setClaimError(null)} style={{padding:"14px 32px",background:"linear-gradient(135deg,#00c9a7,#00e5c0)",color:"#07090c",border:"none",borderRadius:"14px",fontSize:"15px",fontWeight:"800",cursor:"pointer",fontFamily:"inherit"}}>Enter Code Manually</button>
          </>
        ) : (
          <>
            <div style={{fontSize:"18px",fontWeight:"800",color:"var(--text)",textAlign:"center"}}>Getting your invite code…</div>
            <div style={{fontSize:"13px",color:"var(--muted)",textAlign:"center",lineHeight:1.6}}>Just a moment — assigning you a unique code</div>
            <div style={{display:"flex",gap:"6px"}}>
              {[0,1,2].map(i => <div key={i} style={{width:"8px",height:"8px",borderRadius:"50%",background:"var(--accent)",animation:`pulse 1.2s ease-in-out ${i*0.2}s infinite`}}/>)}
            </div>
          </>
        )}
      </div>
    );
  }

  // Khatm deep-link: non-registered user arriving via khatm link → quick signup
  if (!userId && khatmJoinCode && !khatmShowFullSignup) {
    const _gold = darkMode ? "#ffb300" : "#7a4a00";
    const _inputStyle = { width: "100%", padding: "14px 16px", borderRadius: "14px", border: `1px solid rgba(${darkMode ? "255,179,0" : "122,74,0"},0.3)`, background: "var(--s1,rgba(255,255,255,0.04))", color: "var(--text)", fontSize: "16px", fontFamily: "inherit", outline: "none", boxSizing: "border-box", marginBottom: "10px" };
    const handleKhatmGuestJoin = async () => {
      if (!khatmGuestName.trim()) { setKhatmGuestError("Please enter your first name"); return; }
      if (!khatmGuestSurname.trim()) { setKhatmGuestError("Please enter your surname"); return; }
      if (!khatmGuestPhone.trim()) { setKhatmGuestError("Please enter your phone number"); return; }
      setKhatmGuestError(""); setKhatmGuestLoading(true);
      try {
        const r = await fetch(`${BACKEND}/api/khatm/guest-join`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: khatmGuestName.trim(), surname: khatmGuestSurname.trim(), phone: khatmGuestPhone.trim(), code: khatmJoinCode })
        });
        const d = await r.json();
        if (d.ok && d.userId) {
          localStorage.setItem("azaan-userId", d.userId);
          localStorage.setItem("userName", khatmGuestName.trim());
          localStorage.setItem("userSurname", khatmGuestSurname.trim());
          setUserName(khatmGuestName.trim());
          setUserSurname(khatmGuestSurname.trim());
          setKhatmGuestDetail(d.khatm || null);
          setUserId(d.userId);
          // Nudge to install after they've had a moment to see the khatm
          const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone || isNative();
          if (!isStandalone) setTimeout(() => setKhatmShowA2HS(true), 4000);
        } else { setKhatmGuestError(d.error || "Could not join. Try again."); }
      } catch { setKhatmGuestError("Could not connect. Try again."); }
      setKhatmGuestLoading(false);
    };
    return (
      <div style={{ position: "fixed", inset: 0, background: "var(--bg)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "32px 24px", fontFamily: "inherit", overflowY: "auto" }}>
        <svg viewBox="0 0 44 44" width="52" height="52" fill="none" style={{ marginBottom: "10px" }}>
          <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85"/>
          <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85" transform="rotate(60 22 22)"/>
          <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85" transform="rotate(120 22 22)"/>
        </svg>
        <div style={{ fontSize: "11px", color: "var(--muted)", letterSpacing: "3px", textTransform: "uppercase", marginBottom: "20px" }}>Sirat · Collective Ibadah</div>
        <div style={{ fontSize: "22px", fontWeight: "800", color: _gold, fontFamily: "'Scheherazade New','Amiri',serif", direction: "rtl", marginBottom: "8px" }}>بِسْمِ اللَّهِ الرَّحْمَنِ الرَّحِيم</div>
        <div style={{ fontSize: "20px", fontWeight: "800", color: "var(--text)", marginBottom: "6px", textAlign: "center" }}>You've been invited to a Khatm</div>
        <div style={{ fontSize: "13px", color: "var(--muted)", textAlign: "center", maxWidth: "280px", lineHeight: 1.6, marginBottom: "24px" }}>
          Enter your details to join and contribute your readings in sha Allah.
        </div>
        <div style={{ width: "100%", maxWidth: "320px" }}>
          <input value={khatmGuestName} onChange={e => { setKhatmGuestName(e.target.value); setKhatmGuestError(""); }}
            placeholder="First name" autoFocus style={_inputStyle} />
          <input value={khatmGuestSurname} onChange={e => { setKhatmGuestSurname(e.target.value); setKhatmGuestError(""); }}
            placeholder="Surname" style={_inputStyle} />
          <input value={khatmGuestPhone} onChange={e => { setKhatmGuestPhone(e.target.value); setKhatmGuestError(""); }}
            placeholder="Phone number (e.g. +27821234567)" type="tel"
            onKeyDown={e => e.key === "Enter" && handleKhatmGuestJoin()}
            style={{ ..._inputStyle, marginBottom: khatmGuestError ? "6px" : "12px" }} />
          {khatmGuestError && <div style={{ color: "var(--danger)", fontSize: "12px", marginBottom: "10px" }}>{khatmGuestError}</div>}
          <button onClick={handleKhatmGuestJoin} disabled={khatmGuestLoading}
            style={{ width: "100%", padding: "14px", borderRadius: "14px", border: "none", background: `linear-gradient(135deg,${_gold},#ff8f00)`, color: darkMode ? "#000" : "#fff", fontSize: "15px", fontWeight: "800", cursor: "pointer", fontFamily: "inherit", opacity: khatmGuestLoading ? 0.6 : 1, marginBottom: "12px" }}>
            {khatmGuestLoading ? "Joining…" : "Join Khatm 🕊️"}
          </button>
          <button onClick={() => setKhatmShowFullSignup(true)}
            style={{ width: "100%", padding: "12px", borderRadius: "14px", border: "1px solid var(--border)", background: "transparent", color: "var(--muted)", fontSize: "13px", cursor: "pointer", fontFamily: "inherit" }}>
            Already have an account? Sign in
          </button>
        </div>
      </div>
    );
  }

  // Signup flow - shown when no userId
  if (!userId) {
    return (
      <SignupFlow
        darkMode={darkMode}
        remoteConfig={remoteConfig}
        userName={userName} setUserName={setUserName}
        userSurname={userSurname} setUserSurname={setUserSurname}
        userWhatsapp={userWhatsapp} setUserWhatsapp={setUserWhatsapp}
        userEmail={userEmail} setUserEmail={setUserEmail}
        inviteCode={inviteCode} setInviteCode={setInviteCode}
        validateInviteCode={validateInviteCode}
        registering={registering}
        handleRegister={handleRegister}
        handleAppleAuth={handleAppleAuth}
        isNative={isNative()}
        showNotifPrompt={showNotifPrompt} setShowNotifPrompt={setShowNotifPrompt}
        registerPush={registerPush}
        setNotifEnabled={setNotifEnabled}
        syncFollowsToBackend={syncFollowsToBackend}
        followedMounts={followedMounts}
        effectiveNotify={effectiveNotify}
        showWelcome={showWelcome}
        welcomeName={welcomeName}
        welcomeProLabel={welcomeProLabel}
        onDismissWelcome={() => {
          if (welcomeTimerRef.current) clearTimeout(welcomeTimerRef.current);
          setShowWelcome(false);
          if (!localStorage.getItem("permFlowDone")) {
            setPermFlowStep(0);
            setShowPermFlow(true);
          }
        }}
      />
    );
  }

  // FaceID app lock — takes precedence over everything else so a cancelled
  // FaceID prompt (including tapping Cancel or Use Passcode → Cancel) keeps
  // the rest of the app invisible until the user actually passes biometrics.
  if (faceIdLock && faceLocked) {
    return (
      <div style={{position:"fixed",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"32px",textAlign:"center",gap:"22px",
        background: darkMode ? "linear-gradient(160deg,#0a0806 0%,#050403 100%)" : "linear-gradient(160deg,#f7ecd8 0%,#efdcb4 100%)",
        color: "var(--text)", fontFamily: '-apple-system,BlinkMacSystemFont,"SF Pro Text",system-ui,sans-serif'}}>
        <div style={{width:"64px",height:"64px",borderRadius:"20px",background:darkMode?"rgba(255,179,0,0.12)":"rgba(184,112,8,0.12)",border:`1px solid ${darkMode?"rgba(255,179,0,0.25)":"rgba(184,112,8,0.25)"}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:"30px"}}>🔒</div>
        <div>
          <div style={{fontFamily:'"JetBrains Mono",ui-monospace,monospace',fontSize:"10px",letterSpacing:"2.5px",color:"var(--muted)",textTransform:"uppercase",marginBottom:"8px"}}>Sirat · locked</div>
          <div style={{fontFamily:'"Instrument Serif",Georgia,serif',fontStyle:"italic",fontSize:"32px",color:"var(--text)",letterSpacing:"-0.02em",lineHeight:1.1}}>Unlock with Face&nbsp;ID</div>
          <div style={{fontSize:"13px",color:"var(--muted)",marginTop:"10px",maxWidth:"280px",lineHeight:1.5}}>Your ibadah log, dua journal and wasiyyah stay private behind Face ID.</div>
        </div>
        <button onClick={tryFaceUnlock} disabled={faceAsking}
          style={{padding:"13px 28px",borderRadius:"14px",
            background: darkMode ? "linear-gradient(135deg,#ffb300,#cc8e00)" : "linear-gradient(135deg,#b87008,#8a5406)",
            color: darkMode ? "#0a0806" : "#fff7e3",
            border:"none",cursor:faceAsking?"wait":"pointer",
            fontFamily:"inherit",fontSize:"14px",fontWeight:800,letterSpacing:"0.2px",
            boxShadow: darkMode ? "0 10px 22px -8px rgba(255,179,0,0.55)" : "0 10px 22px -8px rgba(184,112,8,0.4)",
            opacity:faceAsking?0.7:1,
            display:"flex",alignItems:"center",gap:"8px"}}>
          {faceAsking ? "Authenticating…" : "Unlock with Face ID"}
        </button>

      </div>
    );
  }

  // Maintenance mode — full screen lock (admin can override remotely, bypass list exempts selected users)
  if (remoteConfig.maintenance && !isMaintainer && !(remoteConfig.maintenanceBypass || []).includes(userId)) {
    return (
      <>
        <div style={{position:"fixed",inset:0,background:"var(--bg)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"32px",textAlign:"center",gap:"20px"}}>
          <div style={{fontSize:"56px"}}>🔧</div>
          <div style={{fontSize:"24px",fontWeight:"800",color:"var(--gold)"}}>{remoteConfig.maintenanceTitle || "Down for maintenance"}</div>
          <div style={{fontSize:"15px",color:"var(--muted)",lineHeight:1.7,maxWidth:"300px"}}>{remoteConfig.maintenanceMsg || "We'll be back shortly. JazakAllah for your patience."}</div>
          <div style={{marginTop:"8px",display:"flex",alignItems:"center",gap:"8px",opacity:0.5}}>
            <svg viewBox="0 0 44 44" width="18" height="18" fill="none"><ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85"/><ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85" transform="rotate(60 22 22)"/><ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85" transform="rotate(120 22 22)"/></svg>
            <span style={{fontSize:"13px",color:"var(--muted)",letterSpacing:"2px"}}>Sirat</span>
          </div>
        </div>
      </>
    );
  }

  // Paywall mode — full app lock after trial expires (configurable via admin)
  // When enabled, the entire app is locked behind a subscription instead of per-feature gating
  // Landscape mode: full-screen now-playing only — rotate to control
  if (isLandscape) {
    // Ahzab35 — landscape: immersive, Arabic centred full-screen, reciters + play below
    if (showAhzab35) {
      return (
        <div style={{position:"fixed",inset:0,zIndex:1200,background:"#07090c",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",overflow:"hidden",padding:"0 56px 100px 24px"}}>
          {/* × dismiss — top right */}
          <button onClick={() => { stopAhzab35(); setShowAhzab35(false); localStorage.removeItem("ahzab35LastOpen"); }}
            style={{position:"absolute",top:14,right:18,background:"transparent",border:"none",fontSize:"22px",color:"rgba(255,255,255,0.3)",cursor:"pointer",padding:"4px",lineHeight:1,zIndex:2}}>×</button>

          {/* Arabic — fills the screen, centred */}
          <div style={{fontSize:"clamp(20px,3.8vw,32px)",fontFamily:"'Scheherazade New','Amiri',serif",color:darkMode?"rgba(255,179,0,0.95)":"rgba(140,85,0,0.95)",textAlign:"center",direction:"rtl",lineHeight:2.3,width:"100%",textShadow:ahzab35Playing?"0 0 28px rgba(255,179,0,0.5), 0 0 60px rgba(255,179,0,0.15)":"none",transition:"text-shadow .8s"}}>
            {ahzab35TextRef.current?.arabic || "جارٍ التحميل…"}
          </div>

          {/* Reciters */}
          <div style={{overflowX:"auto",width:"100%",padding:"18px 0 0",WebkitOverflowScrolling:"touch"}}>
            <div style={{display:"flex",gap:"8px",width:"max-content",margin:"0 auto"}}>
              {RECITERS.map(r => {
                const active = r.id === ahzab35ReciterId;
                return (
                  <button key={r.id} onClick={() => switchAhzab35Reciter(r.id)}
                    style={{padding:"5px 14px",borderRadius:"20px",border:`1px solid ${active?"rgba(255,179,0,0.8)":"rgba(255,255,255,0.12)"}`,background:active?"rgba(255,179,0,0.15)":"transparent",color:active?"rgba(255,179,0,0.95)":"rgba(255,255,255,0.35)",fontSize:"11px",fontWeight:active?"700":"400",cursor:"pointer",whiteSpace:"nowrap",fontFamily:"inherit",transition:"all .2s"}}>
                    {r.short}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Play / pause — SVG */}
          <button onClick={() => ahzab35Playing ? pauseAhzab35() : playAhzab35(ahzab35ReciterId)} disabled={ahzab35Loading}
            style={{marginTop:"16px",width:"52px",height:"52px",borderRadius:"50%",background:"linear-gradient(145deg,#ffb300,#e09000)",border:"none",cursor:ahzab35Loading?"not-allowed":"pointer",boxShadow:ahzab35Playing?"0 0 28px rgba(255,179,0,0.55)":"0 4px 14px rgba(255,179,0,0.2)",transition:"box-shadow .4s",display:"flex",alignItems:"center",justifyContent:"center",color:"#07090c",opacity:ahzab35Loading?0.6:1}}>
            {ahzab35Playing
              ? <svg width="14" height="18" viewBox="0 0 14 18" fill="currentColor"><rect x="0" y="0" width="5" height="18" rx="1.5"/><rect x="9" y="0" width="5" height="18" rx="1.5"/></svg>
              : <svg width="14" height="18" viewBox="0 0 14 18" fill="currentColor" style={{marginLeft:"2px"}}><path d="M0 0 L14 9 L0 18 Z"/></svg>
            }
          </button>
        </div>
      );
    }

    // Surah landscape overlay — show when reading/playing a surah
    if (tab === "tools" && toolView === "surah" && surahCurrentAyah !== null) {
      const info = SURAH_INFO[surahKey];
      const ayahs = surahCache[surahKey + ":" + selectedReciter];
      const currentAyah = ayahs && surahCurrentAyah !== null ? ayahs[surahCurrentAyah] : null;
      if (currentAyah && ayahs) {
        return (
          <>
            <div className="landscape-overlay"
              onTouchStart={e => { surahTouchStartX.current = e.touches[0].clientX; surahTouchStartY.current = e.touches[0].clientY; }}
              onTouchEnd={e => {
                if (surahTouchStartX.current === null) return;
                const dx = e.changedTouches[0].clientX - surahTouchStartX.current;
                const dy = Math.abs(e.changedTouches[0].clientY - surahTouchStartY.current);
                surahTouchStartX.current = null;
                if (Math.abs(dx) < dy * 0.8 || Math.abs(dx) < 45) return;
                if (dx < 0 && surahCurrentAyah + 1 < ayahs.length) playSurahFrom(ayahs, surahCurrentAyah + 1);
                if (dx > 0 && surahCurrentAyah > 0) playSurahFrom(ayahs, surahCurrentAyah - 1);
              }}
            >
              {/* Subtle glow */}
              <div style={{position:"absolute",width:"500px",height:"500px",borderRadius:"50%",background:"radial-gradient(circle, rgba(255,179,0,0.05) 0%, transparent 70%)",pointerEvents:"none"}}/>

              {/* Surah + ayah counter */}
              <div style={{display:"flex",gap:"14px",alignItems:"center",position:"relative",opacity:0.6}}>
                <span style={{fontSize:"11px",color:"var(--muted)",letterSpacing:"2.5px",textTransform:"uppercase"}}>{info.arabic}</span>
                <span style={{width:"3px",height:"3px",borderRadius:"50%",background:"var(--muted)",display:"inline-block"}}/>
                <span style={{fontSize:"11px",color:"var(--muted)",letterSpacing:"1.5px"}}>{surahCurrentAyah + 1} / {info.count}</span>
              </div>

              {/* Arabic verse */}
              <div className="landscape-arabic">{currentAyah.arabic}</div>

              {/* Translation */}
              <div className="landscape-translation">{currentAyah.translation}</div>

              {/* Swipe hint */}
              <div style={{fontSize:"10px",color:"rgba(148,164,176,0.25)",letterSpacing:"2px",marginTop:"6px",userSelect:"none"}}>← swipe to navigate →</div>
            </div>
          </>
        );
      }
    }

    // Qibla overlay in landscape — ask user to rotate back to portrait
    if (showQiblaOverlay) {
      return (
        <div style={{position:'fixed',inset:0,zIndex:9200,background:darkMode?'#07090c':'#f0f5fa',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:'16px'}}>
          <div style={{fontSize:'52px'}}>🧭</div>
          <div style={{fontSize:'18px',fontWeight:'800',color:'var(--text)'}}>Rotate to Portrait</div>
          <div style={{fontSize:'13px',color:'var(--muted)',textAlign:'center',maxWidth:'260px',lineHeight:1.6}}>The Qibla compass requires portrait orientation to work accurately.</div>
          <button onClick={() => setShowQiblaOverlay(false)} style={{marginTop:'8px',padding:'10px 22px',background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.2)',borderRadius:'12px',color:'var(--text)',fontSize:'13px',cursor:'pointer',fontFamily:'inherit'}}>Close</button>
        </div>
      );
    }

    // Masjids in landscape — full-screen premium wall display (glassmorphic, for iPad)
    if (tab === "masjids" && !landscapeWallDismissed) {
      const now = new Date();
      const hh = String(now.getHours()).padStart(2,'0');
      const mm = String(now.getMinutes()).padStart(2,'0');
      const ss = String(now.getSeconds()).padStart(2,'0');
      const dateFull = now.toLocaleDateString('en-ZA', { weekday:'long', day:'numeric', month:'long', year:'numeric' });
      const WALL_PRAYERS = [
        { key:'Fajr',    ar:'الفجر' },
        { key:'Dhuhr',   ar:'الظهر' },
        { key:'Asr',     ar:'العصر' },
        { key:'Maghrib', ar:'المغرب' },
        { key:'Isha',    ar:'العشاء' },
      ];
      const todayKey = localDateStr();
      const todayLog = salahLog[todayKey] || {};
      const nextKey = nextPrayer === 'Sunrise' ? 'Dhuhr' : nextPrayer;
      // Active = the current prayer window (most recent trackable prayer whose time has passed, but next hasn't)
      const activeKey = (() => {
        if (!prayerTimes) return null;
        const nowMin = now.getHours() * 60 + now.getMinutes();
        let current = null;
        for (const p of WALL_PRAYERS) {
          const t = prayerTimes[p.key] ? to24min(prayerTimes[p.key]) : null;
          if (t !== null && t <= nowMin) current = p.key;
        }
        return current;
      })();
      const ornateCorner = (pos) => {
        const s = { position:'absolute', width:'clamp(22px,2.2vw,40px)', height:'clamp(22px,2.2vw,40px)', pointerEvents:'none' };
        const border = '1.5px solid rgba(255,179,0,0.55)';
        if (pos === 'tl') return { ...s, top:'-1px', left:'-1px', borderTop:border, borderLeft:border, borderTopLeftRadius:'18px' };
        if (pos === 'tr') return { ...s, top:'-1px', right:'-1px', borderTop:border, borderRight:border, borderTopRightRadius:'18px' };
        if (pos === 'bl') return { ...s, bottom:'-1px', left:'-1px', borderBottom:border, borderLeft:border, borderBottomLeftRadius:'18px' };
        return { ...s, bottom:'-1px', right:'-1px', borderBottom:border, borderRight:border, borderBottomRightRadius:'18px' };
      };
      const ornateDivider = (
        <div style={{display:'flex',alignItems:'center',gap:'clamp(8px,1vw,18px)',width:'100%'}}>
          <div style={{flex:1,height:'1px',background:'linear-gradient(90deg,transparent,rgba(255,179,0,0.35),transparent)'}}/>
          <div style={{width:'6px',height:'6px',borderRadius:'50%',background:'var(--gold)',boxShadow:'0 0 8px rgba(255,179,0,0.6)'}}/>
          <div style={{width:'clamp(10px,1.2vw,20px)',height:'1px',background:'var(--gold)',opacity:0.55}}/>
          <div style={{fontSize:'clamp(10px,1.2vw,14px)',color:'var(--gold)',letterSpacing:'2px',opacity:0.75}}>◈</div>
          <div style={{width:'clamp(10px,1.2vw,20px)',height:'1px',background:'var(--gold)',opacity:0.55}}/>
          <div style={{width:'6px',height:'6px',borderRadius:'50%',background:'var(--gold)',boxShadow:'0 0 8px rgba(255,179,0,0.6)'}}/>
          <div style={{flex:1,height:'1px',background:'linear-gradient(90deg,transparent,rgba(255,179,0,0.35),transparent)'}}/>
        </div>
      );
      return (
        <>
          <div style={{
            position:'fixed', inset:0,
            background: darkMode
              ? 'radial-gradient(ellipse at top, #0c1117 0%, #07090c 55%, #050608 100%)'
              : 'radial-gradient(ellipse at top, #f6faff 0%, #eef3f8 55%, #e4ecf4 100%)',
            display:'flex', flexDirection:'column',
            padding:'clamp(18px,2.4vh,32px) clamp(24px,3vw,48px)',
            gap:'clamp(14px,2vh,28px)',
            overflow:'hidden', userSelect:'none',
          }}>

            {/* Ambient background glows */}
            <div style={{position:'absolute',top:'-12vh',left:'-10vw',width:'50vw',height:'50vw',borderRadius:'50%',background:'radial-gradient(circle, rgba(0,201,167,0.08) 0%, transparent 65%)',pointerEvents:'none'}}/>
            <div style={{position:'absolute',bottom:'-8vh',right:'-10vw',width:'55vw',height:'55vw',borderRadius:'50%',background:'radial-gradient(circle, rgba(255,179,0,0.07) 0%, transparent 65%)',pointerEvents:'none'}}/>
            <div style={{position:'absolute',inset:0,background:"radial-gradient(circle at 50% 120%, rgba(255,179,0,0.03) 0%, transparent 40%)",pointerEvents:'none'}}/>

            {/* Header — back/logo (left), location + Gregorian date (right) */}
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'2vw',position:'relative',zIndex:2}}>
              <button
                onClick={() => setLandscapeWallDismissed(true)}
                title="Back to app"
                style={{display:'flex',alignItems:'center',gap:'clamp(8px,1vw,14px)',
                  background:darkMode?'rgba(255,255,255,0.04)':'rgba(255,255,255,0.6)',
                  backdropFilter:'blur(20px) saturate(1.2)',WebkitBackdropFilter:'blur(20px) saturate(1.2)',
                  border:`1px solid ${darkMode?'rgba(255,255,255,0.08)':'rgba(0,0,0,0.06)'}`,
                  borderRadius:'999px',padding:'clamp(6px,0.9vh,10px) clamp(12px,1.4vw,20px)',
                  color:'var(--muted)',cursor:'pointer'}}
              >
                <span style={{fontSize:'clamp(14px,1.6vw,18px)',fontWeight:'300',lineHeight:1}}>‹</span>
                <svg viewBox="0 0 44 44" width="clamp(16px,1.8vw,22px)" height="clamp(16px,1.8vw,22px)" fill="none">
                  <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="var(--accent)" strokeWidth="1.6" opacity="0.85"/>
                  <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="var(--accent)" strokeWidth="1.6" opacity="0.85" transform="rotate(60 22 22)"/>
                  <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="var(--accent)" strokeWidth="1.6" opacity="0.85" transform="rotate(120 22 22)"/>
                </svg>
                <span style={{fontSize:'clamp(10px,1.1vw,13px)',fontWeight:'800',letterSpacing:'3px',color:'var(--text)',textTransform:'uppercase'}}>Sirat</span>
              </button>
              <div style={{display:'flex',alignItems:'center',gap:'clamp(10px,1.4vw,20px)',
                background:darkMode?'rgba(255,255,255,0.03)':'rgba(255,255,255,0.55)',
                backdropFilter:'blur(20px) saturate(1.2)',WebkitBackdropFilter:'blur(20px) saturate(1.2)',
                border:`1px solid ${darkMode?'rgba(255,255,255,0.07)':'rgba(0,0,0,0.05)'}`,
                borderRadius:'999px',padding:'clamp(6px,0.9vh,10px) clamp(16px,1.6vw,24px)'}}>
                {detectedCity && (
                  <>
                    <div style={{fontSize:'clamp(11px,1.2vw,15px)',fontWeight:'700',letterSpacing:'2px',color:'var(--text)',textTransform:'uppercase'}}>
                      {detectedCity}
                    </div>
                    <div style={{width:'1px',height:'clamp(10px,1.3vh,16px)',background:'rgba(148,164,176,0.35)'}}/>
                  </>
                )}
                <div style={{fontSize:'clamp(10px,1.1vw,14px)',fontWeight:'600',letterSpacing:'2.5px',color:'var(--muted)',textTransform:'uppercase'}}>
                  {dateFull}
                </div>
              </div>
            </div>

            {/* Prayer times hero — ornate-bordered glassmorphic card wrapper */}
            <div style={{position:'relative',flex:'0 0 auto',marginTop:'clamp(4px,0.8vh,12px)'}}>
              {ornateDivider}
              <div style={{
                position:'relative',marginTop:'clamp(10px,1.4vh,20px)',
                background:darkMode?'rgba(14,19,25,0.55)':'rgba(255,255,255,0.45)',
                backdropFilter:'blur(24px) saturate(1.3)',WebkitBackdropFilter:'blur(24px) saturate(1.3)',
                border:`1px solid ${darkMode?'rgba(255,179,0,0.15)':'rgba(140,107,31,0.18)'}`,
                borderRadius:'clamp(18px,2.4vw,30px)',
                padding:'clamp(18px,3vh,40px) clamp(14px,2vw,28px)',
                boxShadow:darkMode
                  ? '0 30px 80px -30px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.04)'
                  : '0 30px 80px -30px rgba(26,18,8,0.25), inset 0 1px 0 rgba(255,255,255,0.7)',
              }}>
                {/* Ornate corner accents */}
                <div style={ornateCorner('tl')}/>
                <div style={ornateCorner('tr')}/>
                <div style={ornateCorner('bl')}/>
                <div style={ornateCorner('br')}/>

                <div style={{display:'flex',gap:'clamp(8px,1.4vw,22px)',alignItems:'stretch'}}>
                  {WALL_PRAYERS.map(({key, ar}) => {
                    const isActive = key === activeKey;
                    const isNext   = key === nextKey;
                    const isPrayed = !!todayLog[key];
                    const timeVal  = prayerTimes?.[key]?.replace(/ \(.*\)/, '') || '--:--';
                    // Colour resolution: prayed (gold) > active (teal) > next (gold faint) > neutral
                    const accent = isPrayed ? 'var(--gold)' : isActive ? 'var(--accent)' : isNext ? 'var(--gold)' : 'var(--text)';
                    const accentDim = isPrayed ? 'rgba(255,179,0,0.8)' : isActive ? 'rgba(0,201,167,0.85)' : isNext ? 'rgba(255,179,0,0.8)' : 'rgba(148,164,176,0.7)';
                    const cardBg = darkMode
                      ? (isActive ? 'linear-gradient(160deg,rgba(0,201,167,0.14),rgba(0,201,167,0.04))'
                         : isPrayed ? 'linear-gradient(160deg,rgba(255,179,0,0.12),rgba(255,179,0,0.03))'
                         : isNext ? 'linear-gradient(160deg,rgba(255,179,0,0.07),rgba(255,179,0,0.02))'
                         : 'rgba(255,255,255,0.03)')
                      : (isActive ? 'linear-gradient(160deg,rgba(0,158,135,0.14),rgba(0,158,135,0.04))'
                         : isPrayed ? 'linear-gradient(160deg,rgba(184,112,8,0.14),rgba(184,112,8,0.04))'
                         : isNext ? 'linear-gradient(160deg,rgba(184,112,8,0.08),rgba(184,112,8,0.02))'
                         : 'rgba(255,255,255,0.45)');
                    const borderCol = isActive ? 'rgba(0,201,167,0.45)'
                      : isPrayed ? 'rgba(255,179,0,0.45)'
                      : isNext ? 'rgba(255,179,0,0.3)'
                      : (darkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)');
                    return (
                      <div key={key} style={{
                        flex:1,
                        display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                        gap:'clamp(6px,1vh,14px)',
                        background: cardBg,
                        backdropFilter:'blur(16px) saturate(1.2)', WebkitBackdropFilter:'blur(16px) saturate(1.2)',
                        border:`1px solid ${borderCol}`,
                        borderRadius:'clamp(14px,1.8vw,22px)',
                        padding:'clamp(14px,2.4vh,28px) clamp(8px,1vw,16px)',
                        textAlign:'center',
                        position:'relative', overflow:'hidden',
                        animation: isActive ? 'wallActiveFloat 3.4s ease-in-out infinite'
                          : isPrayed ? 'wallPrayedPulse 2.8s ease-in-out infinite'
                          : 'none',
                        boxShadow: isActive
                          ? '0 0 48px rgba(0,201,167,0.35), 0 28px 56px -18px rgba(0,201,167,0.25), inset 0 1px 0 rgba(0,201,167,0.25)'
                          : isPrayed ? 'inset 0 1px 0 rgba(255,179,0,0.22)'
                          : isNext ? '0 0 32px rgba(255,179,0,0.12), inset 0 1px 0 rgba(255,179,0,0.14)'
                          : 'inset 0 1px 0 rgba(255,255,255,0.04)',
                        transition:'transform 0.4s ease',
                      }}>
                        {/* Top accent bar */}
                        <div style={{position:'absolute',top:0,left:'18%',right:'18%',height:'2px',borderRadius:'0 0 2px 2px',background:accentDim,opacity:isActive||isPrayed||isNext?1:0.35}}/>
                        {isPrayed && (
                          <div style={{position:'absolute',top:'clamp(8px,1vh,14px)',right:'clamp(8px,1vw,14px)',width:'clamp(18px,2vh,26px)',height:'clamp(18px,2vh,26px)',borderRadius:'50%',background:'var(--gold)',color:darkMode?'#0a0704':'#fffbe9',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'clamp(11px,1.3vh,16px)',fontWeight:'900',boxShadow:'0 0 12px rgba(255,179,0,0.6)'}}>✓</div>
                        )}
                        <div style={{fontSize:'clamp(10px,1.4vh,15px)',fontWeight:'800',letterSpacing:'3px',textTransform:'uppercase',color:accent}}>
                          {key}
                        </div>
                        <div style={{fontFamily:"'Amiri','Scheherazade New',serif",fontSize:'clamp(20px,3.6vh,44px)',fontWeight:'700',color:accentDim,lineHeight:1,direction:'rtl'}}>
                          {ar}
                        </div>
                        <div style={{fontSize:'clamp(28px,7.5vh,56px)',fontWeight:'700',letterSpacing:'-1px',fontVariantNumeric:'tabular-nums',color:accent,lineHeight:1}}>
                          {timeVal}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div style={{marginTop:'clamp(10px,1.4vh,20px)'}}>{ornateDivider}</div>
            </div>

            {/* Giant digital clock + Hijri hero */}
            <div style={{flex:'1 1 auto',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:'clamp(8px,1.4vh,18px)',textAlign:'center',position:'relative',zIndex:2}}>
              <div style={{display:'flex',alignItems:'flex-start',justifyContent:'center',animation:'wallClockShimmer 4.2s ease-in-out infinite'}}>
                <span style={{
                  fontSize:'clamp(72px,18vh,180px)',fontWeight:'200',
                  backgroundImage:darkMode
                    ? 'linear-gradient(180deg,#fff5d9 0%, #ffb300 55%, #cc8e00 100%)'
                    : 'linear-gradient(180deg,#3a2a08 0%, #8c6b1f 55%, #6a501a 100%)',
                  WebkitBackgroundClip:'text',backgroundClip:'text',
                  WebkitTextFillColor:'transparent',color:'transparent',
                  letterSpacing:'-4px',fontVariantNumeric:'tabular-nums',lineHeight:0.95,
                }}>{hh}:{mm}</span>
                <span style={{fontSize:'clamp(28px,6vh,70px)',fontWeight:'200',color:'var(--muted)',fontVariantNumeric:'tabular-nums',marginTop:'0.55em',marginLeft:'6px',lineHeight:1}}>:{ss}</span>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:'clamp(12px,1.6vw,24px)',marginTop:'clamp(4px,0.6vh,10px)'}}>
                <div style={{width:'clamp(40px,6vw,100px)',height:'1px',background:'linear-gradient(90deg,transparent,var(--gold))'}}/>
                <div style={{width:'7px',height:'7px',borderRadius:'50%',background:'var(--gold)',boxShadow:'0 0 10px rgba(255,179,0,0.6)'}}/>
                <div style={{width:'clamp(40px,6vw,100px)',height:'1px',background:'linear-gradient(90deg,var(--gold),transparent)'}}/>
              </div>
              {hijri && (
                <div style={{marginTop:'clamp(4px,0.6vh,10px)'}}>
                  <div style={{fontSize:'clamp(10px,1.3vh,14px)',fontWeight:'800',letterSpacing:'4px',color:'var(--muted)',textTransform:'uppercase',marginBottom:'clamp(4px,0.6vh,10px)'}}>Hijri</div>
                  <div style={{fontSize:'clamp(20px,3.4vh,40px)',fontWeight:'700',color:'var(--gold)',letterSpacing:'1px'}}>
                    {hijri.replace(/\s*AH\s*$/i, '').trim()}
                  </div>
                </div>
              )}
              {countdown && nextPrayer && nextPrayer !== '--' && (
                <div style={{marginTop:'clamp(6px,1vh,14px)',fontSize:'clamp(11px,1.5vh,16px)',letterSpacing:'3px',color:'var(--muted)',textTransform:'uppercase',fontWeight:'700'}}>
                  Next · <span style={{color:'var(--accent)'}}>{nextPrayer === 'Sunrise' ? 'Shuruq' : nextPrayer}</span> · <span style={{color:'var(--gold)',fontVariantNumeric:'tabular-nums'}}>{countdown}</span>
                </div>
              )}
            </div>

          </div>
        </>
      );
    }

    // For all other non-radio tabs in landscape, render normally (fall through)
    if (tab !== "radio") {} else {

    const isPlaying = playStatus === 'playing';
    const isBuffering = playStatus === 'buffering';
    const isError = playStatus === 'error';
    const accentColor = isPlaying ? '#00c9a7' : isError ? '#ff6b6b' : '#ffb300';
    return (
      <>
        <div style={{
          position:'fixed', inset:0,
          background: darkMode ? '#07090c' : '#f0f5fa',
          display:'flex', flexDirection:'column',
          overflow:'hidden',
        }}>
          {/* Top bar — branding + nav + next prayer */}
          <div style={{display:'flex',alignItems:'center',padding:'10px 20px',borderBottom:`1px solid ${darkMode?'rgba(255,255,255,0.06)':'rgba(0,0,0,0.06)'}`,flexShrink:0,gap:'16px'}}>
            {/* Sirat branding */}
            <div style={{display:'flex',alignItems:'center',gap:'7px',flexShrink:0}}>
              <svg viewBox="0 0 44 44" width="16" height="16" fill="none">
                <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="var(--accent)" strokeWidth="1.6" opacity="0.8"/>
                <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="var(--accent)" strokeWidth="1.6" opacity="0.8" transform="rotate(60 22 22)"/>
                <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="var(--accent)" strokeWidth="1.6" opacity="0.8" transform="rotate(120 22 22)"/>
              </svg>
              <span style={{fontSize:'12px',fontWeight:'800',letterSpacing:'3px',color:'var(--gold)',textTransform:'uppercase'}}>Sirat</span>
            </div>
            {/* Tab navigation */}
            <div style={{display:'flex',gap:'4px',flex:1,justifyContent:'center'}}>
              {[
                {id:'masjids',icon:'🕌',label:'Salaah'},
                {id:'radio',  icon:'📻',label:'Radio'},
                {id:'qibla',  icon:'🧭',label:'Qibla'},
                {id:'tools',  icon:'🤲',label:'Ibadah'},
                {id:'settings',icon:'⚙️',label:'Settings'},
                ...(isMaintainer ? [{id:'maintainer',icon:'⚙️',label:'Admin'}] : []),
              ].map(({id,icon,label}) => (
                <button key={id} onClick={() => { setTab(id); if(id==='tools') setToolView('grid'); }}
                  style={{
                    background: tab===id ? (darkMode?'rgba(0,201,167,0.12)':'rgba(0,158,135,0.1)') : 'transparent',
                    border: `1px solid ${tab===id ? 'rgba(0,201,167,0.35)' : 'transparent'}`,
                    borderRadius:'8px', padding:'5px 12px',
                    display:'flex',alignItems:'center',gap:'6px',
                    cursor:'pointer', fontFamily:'inherit',
                  }}>
                  <span style={{fontSize:'14px',lineHeight:1}}>{icon}</span>
                  <span style={{fontSize:'9px',fontWeight:'700',letterSpacing:'0.5px',color: tab===id ? 'var(--accent)' : 'var(--muted)',textTransform:'uppercase'}}>{label}</span>
                </button>
              ))}
            </div>
            {/* Next prayer */}
            {nextPrayer && countdown && (
              <div style={{textAlign:'right',flexShrink:0}}>
                <div style={{fontSize:'8px',color:'var(--muted)',letterSpacing:'2px',textTransform:'uppercase'}}>Next · {nextPrayer}</div>
                <div style={{fontSize:'16px',fontWeight:'800',color:'var(--gold)',letterSpacing:'1px',fontVariantNumeric:'tabular-nums'}}>{countdown}</div>
              </div>
            )}
          </div>

          {/* Main content */}
          <div style={{flex:1,display:'flex',flexDirection:'row',alignItems:'stretch',overflow:'hidden',padding:'16px 24px 12px',gap:'24px'}}>

            {/* Now playing / hero area — left side */}
            <div style={{
              flex:1,
              display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',
              position:'relative',gap:'10px',minWidth:0,
            }}>
              {/* Background glow */}
              <div style={{position:'absolute',width:'320px',height:'320px',borderRadius:'50%',background:`radial-gradient(circle, ${accentColor}15 0%, transparent 70%)`,pointerEvents:'none',transition:'background 0.6s ease'}}/>

              {playing ? (
                <>
                  <div style={{fontSize:'9px',color:'var(--muted)',letterSpacing:'4px',textTransform:'uppercase',fontWeight:'700'}}>Now Playing</div>
                  <div style={{
                    width:'64px', height:'64px', borderRadius:'16px',
                    background: darkMode ? 'linear-gradient(135deg,#0d1f1a,#071410)' : 'linear-gradient(135deg,#d8eae4,#c8dfd8)',
                    border:`2px solid ${accentColor}`,
                    display:'flex', alignItems:'center', justifyContent:'center', fontSize:'28px',
                    boxShadow:`0 0 32px ${accentColor}44`,
                    transition:'border-color 0.4s, box-shadow 0.4s',
                  }}>♫</div>
                  <div style={{fontSize:'clamp(18px,3vw,28px)',fontWeight:'800',color:'var(--text)',textAlign:'center',lineHeight:1.2,maxWidth:'400px'}}>
                    {playing.name}
                  </div>
                  {isPlaying   && <div className="wave" style={{transform:'scale(1.6)'}}><span/><span/><span/><span/><span/><span/></div>}
                  {isBuffering && <div style={{color:'var(--gold)',fontSize:'12px',fontWeight:'600',letterSpacing:'2px',textTransform:'uppercase'}}>Connecting...</div>}
                  {isError     && <div style={{color:'var(--danger)',fontSize:'12px',fontWeight:'600',letterSpacing:'2px',textTransform:'uppercase'}}>Stream offline</div>}
                  <div style={{display:'flex',gap:'10px',marginTop:'4px'}}>
                    {(isPlaying || isBuffering) && (
                      <button onClick={stopAudio} style={{padding:'8px 28px',background:'rgba(255,107,107,0.15)',color:'var(--danger)',border:'1px solid rgba(255,107,107,0.4)',borderRadius:'20px',fontSize:'13px',fontWeight:'700',cursor:'pointer',letterSpacing:'0.5px',fontFamily:'inherit'}}>
                        ■ Stop
                      </button>
                    )}
                    {isError && (
                      <button onClick={() => playStation(playing)} style={{padding:'8px 28px',background:'rgba(0,201,167,0.12)',color:'var(--accent)',border:'1px solid rgba(0,201,167,0.3)',borderRadius:'20px',fontSize:'13px',fontWeight:'700',cursor:'pointer',fontFamily:'inherit'}}>
                        ↺ Retry
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div style={{fontSize:'11px',color:'var(--muted)',letterSpacing:'3px',textTransform:'uppercase',fontWeight:'700'}}>Islamic Radio</div>
                  <div style={{fontSize:'clamp(20px,3.5vw,32px)',fontWeight:'800',color:'var(--text)',textAlign:'center',lineHeight:1.3}}>
                    South African Stations
                  </div>
                  <div style={{fontSize:'12px',color:'var(--muted)',opacity:0.6,maxWidth:'300px',textAlign:'center',lineHeight:1.5}}>Select a station to start listening</div>
                </>
              )}
            </div>

            {/* Station cards — right side, vertical list */}
            <div style={{
              width:'clamp(280px,40vw,420px)',flexShrink:0,
              display:'flex',flexDirection:'column',gap:'8px',
              overflowY:'auto',paddingRight:'4px',
            }}>
              {stations
                ? stations.radio.map(s => <StationCard key={s.url} station={s} type="radio-landscape" />)
                : <div style={{textAlign:'center',color:'var(--muted)',padding:'20px',fontSize:'13px'}}>Loading...</div>
              }
            </div>
          </div>
        </div>
      </>
    );
    } // end else (tab === "radio")
  }

  // ─── Teaching Mode gate ───────────────────────────────────────
  if (teachingMode) {
    return (
      <TeachingScreen
        teachingView={teachingView}
        setTeachingView={setTeachingView}
        setTeachingMode={setTeachingMode}
        darkMode={darkMode}
        showIntro={showTeachingIntro}
        setShowIntro={setShowTeachingIntro}
      />
    );
  }

  // ── Fan menu SVG icons ────────────────────────────────────────────────────
  const IC = '#F4C56A';
  const IconTasbeeh = () => (
    <svg viewBox="0 0 40 40" width="38" height="38" fill="none">
      {Array.from({length:11}).map((_,i) => { const a=(i/11)*Math.PI*2-Math.PI/2; return <circle key={i} cx={20+Math.cos(a)*13} cy={20+Math.sin(a)*13} r="1.6" fill={IC}/>; })}
      <circle cx="20" cy="20" r="7" stroke={IC} strokeWidth="1.2"/>
      <circle cx="20" cy="20" r="1.8" fill={IC}/>
    </svg>
  );
  const IconNames = () => (
    <svg viewBox="0 0 40 40" width="38" height="38" fill="none">
      <polygon points="20,4 36,20 20,36 4,20" stroke={IC} strokeWidth="1.4"/>
      <rect x="12" y="12" width="16" height="16" stroke={IC} strokeWidth="1.2"/>
      <circle cx="20" cy="20" r="2.5" fill={IC}/>
    </svg>
  );
  const IconQuran = () => (
    <svg viewBox="0 0 40 40" width="38" height="38" fill="none" strokeLinecap="round">
      <path d="M20 8 L20 32" stroke={IC} strokeWidth="1.5"/>
      <path d="M20 10 Q10 11 8 14 L8 30 Q10 28 20 29" stroke={IC} strokeWidth="1.3"/>
      <path d="M20 10 Q30 11 32 14 L32 30 Q30 28 20 29" stroke={IC} strokeWidth="1.3"/>
      <line x1="10" y1="17" x2="18" y2="16.5" stroke={IC} strokeWidth="1" opacity="0.6"/>
      <line x1="10" y1="20" x2="18" y2="19.5" stroke={IC} strokeWidth="1" opacity="0.6"/>
      <line x1="22" y1="17" x2="30" y2="16.5" stroke={IC} strokeWidth="1" opacity="0.6"/>
      <line x1="22" y1="20" x2="30" y2="19.5" stroke={IC} strokeWidth="1" opacity="0.6"/>
    </svg>
  );
  const IconDuas = () => (
    <svg viewBox="0 0 40 40" width="38" height="38" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 29 L10 19 Q10 15 13 15 Q15 15 15 18 L15 22 Q17 20 19.5 20 Q22 20 22 22 L22 29 Z" stroke={IC} strokeWidth="1.3"/>
      <path d="M28 29 L30 19 Q30 15 27 15 Q25 15 25 18 L25 22 Q23 20 20.5 20 Q18 20 18 22 L18 29 Z" stroke={IC} strokeWidth="1.3"/>
    </svg>
  );
  const IconAzkar = () => (
    <svg viewBox="0 0 40 40" width="38" height="38" fill="none">
      <path d="M27 12 A10 10 0 1 0 27 28 A7 7 0 1 1 27 12 Z" stroke={IC} strokeWidth="1.3" strokeLinejoin="round"/>
    </svg>
  );
  const IconFasting = () => (
    <svg viewBox="0 0 40 40" width="38" height="38" fill="none">
      <path d="M28 14 Q32 20 28 26 A10 10 0 1 1 28 14 Z" stroke={IC} strokeWidth="1.3" strokeLinejoin="round"/>
    </svg>
  );
  const IconQibla = () => (
    <svg viewBox="0 0 40 40" width="38" height="38" fill="none">
      <circle cx="20" cy="20" r="13" stroke={IC} strokeWidth="1.3"/>
      <path d="M20 8 L17.5 16 L20 14.5 L22.5 16 Z" fill={IC}/>
      <line x1="20" y1="14.5" x2="20" y2="26" stroke={IC} strokeWidth="1.2" opacity="0.5"/>
      <circle cx="20" cy="20" r="2" fill={IC}/>
    </svg>
  );
  const IconHadith = () => (
    <svg viewBox="0 0 40 40" width="38" height="38" fill="none" strokeLinecap="round">
      <rect x="9" y="10" width="22" height="20" rx="2" stroke={IC} strokeWidth="1.3"/>
      <line x1="13" y1="16" x2="27" y2="16" stroke={IC} strokeWidth="1.1" opacity="0.7"/>
      <line x1="13" y1="20" x2="27" y2="20" stroke={IC} strokeWidth="1.1" opacity="0.7"/>
      <line x1="13" y1="24" x2="22" y2="24" stroke={IC} strokeWidth="1.1" opacity="0.7"/>
    </svg>
  );
  const IconMosque = () => (
    <svg viewBox="0 0 40 40" width="38" height="38" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 24 Q11 14 20 14 Q29 14 29 24" stroke={IC} strokeWidth="1.3"/>
      <rect x="8" y="16" width="4" height="14" rx="1" stroke={IC} strokeWidth="1.1"/>
      <path d="M8 16 Q10 12 12 16" stroke={IC} strokeWidth="1.1"/>
      <rect x="28" y="16" width="4" height="14" rx="1" stroke={IC} strokeWidth="1.1"/>
      <path d="M28 16 Q30 12 32 16" stroke={IC} strokeWidth="1.1"/>
      <line x1="8" y1="30" x2="32" y2="30" stroke={IC} strokeWidth="1.3"/>
    </svg>
  );
  const IconZakat = () => (
    <svg viewBox="0 0 40 40" width="38" height="38" fill="none" strokeLinecap="round">
      <circle cx="20" cy="20" r="13" stroke={IC} strokeWidth="1.3"/>
      <path d="M23 15 Q17 15 17 18 Q17 21 23 21 Q29 21 23 25 Q17 25 17 28" stroke={IC} strokeWidth="1.4"/>
    </svg>
  );
  const IconCalendar = () => (
    <svg viewBox="0 0 40 40" width="38" height="38" fill="none" strokeLinecap="round">
      <rect x="8" y="11" width="24" height="20" rx="3" stroke={IC} strokeWidth="1.3"/>
      <line x1="8" y1="17" x2="32" y2="17" stroke={IC} strokeWidth="1.1"/>
      <line x1="15" y1="11" x2="15" y2="7" stroke={IC} strokeWidth="1.3"/>
      <line x1="25" y1="11" x2="25" y2="7" stroke={IC} strokeWidth="1.3"/>
      {[13,20,27].map(x => [22,27].map(y => <circle key={`${x}${y}`} cx={x} cy={y} r="1.4" fill={IC} opacity="0.8"/>))}
    </svg>
  );
  const IconSettings = () => (
    <svg viewBox="0 0 40 40" width="38" height="38" fill="none">
      <circle cx="20" cy="20" r="5" stroke={IC} strokeWidth="1.4"/>
      <circle cx="20" cy="20" r="12" stroke={IC} strokeWidth="1.2" strokeDasharray="4 2.8" strokeLinecap="round"/>
    </svg>
  );
  const IconSurahs = () => (
    <svg viewBox="0 0 40 40" width="38" height="38" fill="none" strokeLinecap="round">
      <path d="M9 19 Q9 12 15 12 L25 12 Q31 12 31 19" stroke={IC} strokeWidth="1.3"/>
      <circle cx="12" cy="24" r="4.5" stroke={IC} strokeWidth="1.3"/><circle cx="12" cy="24" r="1.5" fill={IC}/>
      <circle cx="28" cy="24" r="4.5" stroke={IC} strokeWidth="1.3"/><circle cx="28" cy="24" r="1.5" fill={IC}/>
    </svg>
  );
  const IconAhzab35 = () => (
    <svg viewBox="0 0 40 40" width="38" height="38" fill="none">
      <polygon points="20,5 22.5,16 34,16 24.5,22.5 27.5,34 20,27.5 12.5,34 15.5,22.5 6,16 17.5,16" stroke={IC} strokeWidth="1.3" strokeLinejoin="round"/>
    </svg>
  );
  const IconVerse = () => (
    <svg viewBox="0 0 40 40" width="38" height="38" fill="none" strokeLinecap="round">
      <rect x="10" y="10" width="20" height="24" rx="2" stroke={IC} strokeWidth="1.3"/>
      <line x1="14" y1="16" x2="26" y2="16" stroke={IC} strokeWidth="1.1" opacity="0.7"/>
      <line x1="14" y1="20" x2="26" y2="20" stroke={IC} strokeWidth="1.1" opacity="0.7"/>
      <line x1="14" y1="24" x2="21" y2="24" stroke={IC} strokeWidth="1.1" opacity="0.7"/>
      <path d="M17 7 L20 10 L23 7" stroke={IC} strokeWidth="1.2"/>
    </svg>
  );
  const IconEgzon = () => (
    <svg viewBox="0 0 40 40" width="38" height="38" fill="none" strokeLinecap="round">
      <rect x="15" y="6" width="10" height="18" rx="5" stroke={IC} strokeWidth="1.3"/>
      <path d="M10 22 Q10 32 20 32 Q30 32 30 22" stroke={IC} strokeWidth="1.3"/>
      <line x1="20" y1="32" x2="20" y2="36" stroke={IC} strokeWidth="1.3"/>
      <line x1="15" y1="36" x2="25" y2="36" stroke={IC} strokeWidth="1.3"/>
    </svg>
  );
  const IconDossari = () => (
    <svg viewBox="0 0 40 40" width="38" height="38" fill="none" strokeLinecap="round">
      <rect x="15" y="7" width="10" height="17" rx="5" stroke={IC} strokeWidth="1.3"/>
      <path d="M10 22 Q10 31 20 31 Q30 31 30 22" stroke={IC} strokeWidth="1.3"/>
      <line x1="7" y1="17" x2="9" y2="17" stroke={IC} strokeWidth="1.1" opacity="0.6"/>
      <line x1="6" y1="21" x2="9" y2="21" stroke={IC} strokeWidth="1.1" opacity="0.6"/>
      <line x1="31" y1="17" x2="33" y2="17" stroke={IC} strokeWidth="1.1" opacity="0.6"/>
      <line x1="31" y1="21" x2="34" y2="21" stroke={IC} strokeWidth="1.1" opacity="0.6"/>
      <line x1="20" y1="31" x2="20" y2="35" stroke={IC} strokeWidth="1.3"/>
    </svg>
  );
  const IconRightNow = () => (
    <svg viewBox="0 0 40 40" width="38" height="38" fill="none" strokeLinecap="round">
      <circle cx="20" cy="20" r="13" stroke={IC} strokeWidth="1.3"/>
      <path d="M20 11 L20 20 L26 24" stroke={IC} strokeWidth="1.5"/><circle cx="20" cy="20" r="1.5" fill={IC}/>
    </svg>
  );
  const IconKhatm = () => (
    <svg viewBox="0 0 40 40" width="38" height="38" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 12 Q14 8 10 12 Q8 16 12 19 L20 28 L28 19 Q32 16 30 12 Q26 8 20 12 Z" stroke={IC} strokeWidth="1.3"/>
      <line x1="20" y1="28" x2="20" y2="34" stroke={IC} strokeWidth="1.2" opacity="0.5"/>
      <line x1="16" y1="33" x2="24" y2="33" stroke={IC} strokeWidth="1.2" opacity="0.5"/>
    </svg>
  );
  const IconMuraqabah = () => (
    <svg viewBox="0 0 40 40" width="38" height="38" fill="none">
      <path d="M28 12 Q32 18 28 24 A10 10 0 1 1 28 12 Z" stroke={IC} strokeWidth="1.3" strokeLinejoin="round"/>
      <circle cx="28" cy="10" r="1.3" fill={IC}/><circle cx="32" cy="14" r="0.9" fill={IC} opacity="0.7"/><circle cx="31" cy="9" r="0.8" fill={IC} opacity="0.5"/>
    </svg>
  );
  const IconTawbah = () => (
    <svg viewBox="0 0 40 40" width="38" height="38" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 32 Q10 24 10 17 A8 8 0 0 1 20 12 A8 8 0 0 1 30 17 Q30 24 20 32 Z" stroke={IC} strokeWidth="1.3"/>
      <line x1="20" y1="7" x2="20" y2="10" stroke={IC} strokeWidth="1.2" opacity="0.6"/>
      <line x1="25" y1="8.5" x2="23.5" y2="11" stroke={IC} strokeWidth="1.2" opacity="0.6"/>
      <line x1="15" y1="8.5" x2="16.5" y2="11" stroke={IC} strokeWidth="1.2" opacity="0.6"/>
    </svg>
  );
  const IconMirath = () => (
    <svg viewBox="0 0 40 40" width="38" height="38" fill="none" strokeLinecap="round">
      <line x1="20" y1="8" x2="20" y2="34" stroke={IC} strokeWidth="1.3"/>
      <line x1="11" y1="14" x2="29" y2="14" stroke={IC} strokeWidth="1.3"/>
      <path d="M8 14 L8 21 Q8 27 14 27 Q20 27 20 21 L20 14" stroke={IC} strokeWidth="1.2"/>
      <path d="M20 14 L20 21 Q20 27 26 27 Q32 27 32 21 L32 14" stroke={IC} strokeWidth="1.2"/>
      <line x1="14" y1="34" x2="26" y2="34" stroke={IC} strokeWidth="1.3"/>
    </svg>
  );
  const IconDuaJournal = () => (
    <svg viewBox="0 0 40 40" width="38" height="38" fill="none" strokeLinecap="round">
      <rect x="9" y="9" width="17" height="22" rx="2" stroke={IC} strokeWidth="1.3"/>
      <line x1="13" y1="15" x2="22" y2="15" stroke={IC} strokeWidth="1.1" opacity="0.7"/>
      <line x1="13" y1="19" x2="22" y2="19" stroke={IC} strokeWidth="1.1" opacity="0.7"/>
      <line x1="13" y1="23" x2="18" y2="23" stroke={IC} strokeWidth="1.1" opacity="0.7"/>
      <path d="M27 19 L32 14 L36 18 L31 23 Z" stroke={IC} strokeWidth="1.2"/>
      <path d="M31 23 L29 31 L27 29 Z" stroke={IC} strokeWidth="1.1"/>
    </svg>
  );
  const IconIstikharah = () => (
    <svg viewBox="0 0 40 40" width="38" height="38" fill="none">
      <path d="M28 12 Q32 18 28 24 A10 10 0 1 1 28 12 Z" stroke={IC} strokeWidth="1.3" strokeLinejoin="round"/>
      <circle cx="30" cy="8" r="2" fill={IC}/><circle cx="34" cy="13" r="1.2" fill={IC} opacity="0.7"/><circle cx="33" cy="7" r="1" fill={IC} opacity="0.5"/>
    </svg>
  );
  const IconMoodGuide = () => (
    <svg viewBox="0 0 40 40" width="38" height="38" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 30 Q10 22 10 16 A8 8 0 0 1 20 10 A8 8 0 0 1 30 16 Q30 22 20 30 Z" stroke={IC} strokeWidth="1.3"/>
      <path d="M6 20 Q3 20 3 17" stroke={IC} strokeWidth="1.1" opacity="0.5"/>
      <path d="M34 20 Q37 20 37 17" stroke={IC} strokeWidth="1.1" opacity="0.5"/>
    </svg>
  );
  const IconTajweed = () => (
    <svg viewBox="0 0 40 40" width="38" height="38" fill="none" strokeLinecap="round">
      <path d="M5 20 L8 20" stroke={IC} strokeWidth="1.3"/>
      <path d="M9 20 Q11 14 13 20 Q15 26 17 20 Q19 14 21 20 Q23 26 25 20 Q27 14 29 20 Q31 26 33 20" stroke={IC} strokeWidth="1.3"/>
      <path d="M33 20 L36 20" stroke={IC} strokeWidth="1.3"/>
    </svg>
  );
  const IconHajj = () => (
    <svg viewBox="0 0 40 40" width="38" height="38" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <rect x="12" y="15" width="16" height="17" rx="1" stroke={IC} strokeWidth="1.3"/>
      <rect x="15" y="9" width="10" height="8" stroke={IC} strokeWidth="1.2"/>
      <rect x="17" y="5" width="6" height="6" stroke={IC} strokeWidth="1.1"/>
      <line x1="8" y1="32" x2="32" y2="32" stroke={IC} strokeWidth="1.3" opacity="0.6"/>
    </svg>
  );
  const IconJanazah = () => (
    <svg viewBox="0 0 40 40" width="38" height="38" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 8 Q12 10 10 18 L10 26 L20 30 L30 26 L30 18 Q28 10 20 8 Z" stroke={IC} strokeWidth="1.3"/>
      <line x1="20" y1="8" x2="20" y2="30" stroke={IC} strokeWidth="1" opacity="0.4"/>
      <line x1="10" y1="18" x2="30" y2="18" stroke={IC} strokeWidth="1" opacity="0.4"/>
      <line x1="20" y1="30" x2="20" y2="36" stroke={IC} strokeWidth="1.1" opacity="0.5"/>
    </svg>
  );
  const IconCardGenerator = () => (
    <svg viewBox="0 0 40 40" width="38" height="38" fill="none" strokeLinecap="round">
      <rect x="7" y="10" width="26" height="20" rx="3" stroke={IC} strokeWidth="1.3"/>
      <path d="M14 25 L18 21 L22 24 L26 18 L31 25" stroke={IC} strokeWidth="1.2"/>
      <circle cx="13" cy="16" r="2.5" stroke={IC} strokeWidth="1.1"/>
    </svg>
  );
  const IconFaraid = () => (
    <svg viewBox="0 0 40 40" width="38" height="38" fill="none" strokeLinecap="round">
      <line x1="20" y1="7" x2="20" y2="35" stroke={IC} strokeWidth="1.3"/>
      <line x1="10" y1="13" x2="30" y2="13" stroke={IC} strokeWidth="1.3"/>
      <path d="M7 13 L7 20 Q7 26 13.5 26 Q20 26 20 20 L20 13" stroke={IC} strokeWidth="1.2"/>
      <path d="M20 13 L20 20 Q20 26 26.5 26 Q33 26 33 20 L33 13" stroke={IC} strokeWidth="1.2"/>
      <line x1="14" y1="35" x2="26" y2="35" stroke={IC} strokeWidth="1.3"/>
      <circle cx="7" cy="13" r="1.8" fill={IC} opacity="0.6"/>
      <circle cx="33" cy="13" r="1.8" fill={IC} opacity="0.6"/>
    </svg>
  );
  const IconIslamicWill = () => (
    <svg viewBox="0 0 40 40" width="38" height="38" fill="none" strokeLinecap="round">
      <rect x="9" y="7" width="19" height="24" rx="2" stroke={IC} strokeWidth="1.3"/>
      <line x1="13" y1="13" x2="24" y2="13" stroke={IC} strokeWidth="1.1" opacity="0.7"/>
      <line x1="13" y1="17" x2="24" y2="17" stroke={IC} strokeWidth="1.1" opacity="0.7"/>
      <line x1="13" y1="21" x2="20" y2="21" stroke={IC} strokeWidth="1.1" opacity="0.7"/>
      <path d="M13 26 Q18 25 23 27" stroke={IC} strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M27 17 L32 12 L36 16 L31 21 Z" stroke={IC} strokeWidth="1.2"/>
      <path d="M31 21 L29 29 L27 27 Z" stroke={IC} strokeWidth="1.1"/>
    </svg>
  );

  // ── Fan menu options — hoisted so both fan render and settings picker can use them ──
  const FAN_ALL_OPTIONS = [
    { id:'hadith',          label:'Hadith',      icon:<IconHadith/>,        action:()=>{ setShowHadithOverlay(true); } },
    { id:'ahzab35',         label:'Gem Verse',   icon:<IconAhzab35/>,       action:()=>{ setShowAhzab35(true); } },
    { id:'verse',           label:'Daily Verse', icon:<IconVerse/>,         action:()=>{ setShowVerseOverlay(true); } },
    { id:'egzon',           label:'Egzon',       icon:<IconEgzon/>,         action:()=>{ setTab("tools"); setToolView("egzon"); } },
    { id:'dossari',         label:'Dossari',     icon:<IconDossari/>,       action:()=>{ setTab("tools"); setToolView("dossari"); } },
    { id:'rightnow',        label:'Right Now',   icon:<IconRightNow/>,      action:()=>{ setTab("tools"); setToolView("rightnow"); } },
    { id:'khatm',           label:'Khatm',       icon:<IconKhatm/>,         action:()=>{ setTab("tools"); setToolView("khatm"); } },
    { id:'tasbeeh',         label:'Tasbeeh',     icon:<IconTasbeeh/>,       action:()=>{ setTab("tools"); setToolView("tasbeeh"); } },
    { id:'muraqabah',       label:'Muraqabah',   icon:<IconMuraqabah/>,     action:()=>{ setTab("tools"); setToolView("muraqabah"); } },
    { id:'tawbah',          label:'Tawbah',      icon:<IconTawbah/>,        action:()=>{ setTab("tools"); setToolView("tawbah"); } },
    { id:'fasting',         label:'Fasting',     icon:<IconFasting/>,       action:()=>{ setTab("tools"); setToolView("fasting"); } },
    { id:'hayah',           label:'Hayah',       icon:<span style={{fontSize:"20px"}}>❋</span>, action:()=>{ try { localStorage.removeItem("ghidhaLastView"); } catch {} setTab("hayah"); setHayahFeature("today"); setHayahResetKey(k => k + 1); } },
    { id:'ghidha',          label:'Calories',    icon:<span style={{fontSize:"20px"}}>🥗</span>, action:()=>{ try { localStorage.setItem("ghidhaLastView", "log"); } catch {} setTab("hayah"); setHayahFeature("ghidha"); setHayahResetKey(k => k + 1); } },
    { id:'exercise',        label:'Exercise',    icon:<span style={{fontSize:"20px"}}>💪</span>, action:()=>{ setTab("hayah"); setHayahFeature("exercise"); } },
    { id:'zakat',           label:'Zakat',       icon:<IconZakat/>,         action:()=>{ setTab("tools"); setToolView("zakat"); } },
    { id:'mirath',          label:'Mirath',      icon:<IconMirath/>,        action:()=>{ setTab("tools"); setToolView("mirath"); } },
    { id:'faraid',          label:'Faraid',      icon:<IconFaraid/>,        action:()=>{ setTab("tools"); setToolView("faraid"); } },
    { id:'islamicwill',     label:'Will',        icon:<IconIslamicWill/>,   action:()=>{ setTab("tools"); setToolView("islamicwill"); } },
    { id:'quran',           label:'Surahs',      icon:<IconSurahs/>,        action:()=>{ setTab("tools"); setToolView("quran"); } },
    { id:'fullquran',       label:'Quran',       icon:<IconQuran/>,         action:()=>{ setTab("tools"); setToolView("fullquran"); } },
    { id:'azkar',           label:'Azkar',       icon:<IconAzkar/>,         action:()=>{ setTab("tools"); setToolView("azkar"); } },
    { id:'names',           label:'99 Names',    icon:<IconNames/>,         action:()=>{ setTab("tools"); setToolView("names"); } },
    { id:'duas',            label:'Duas',        icon:<IconDuas/>,          action:()=>{ setTab("tools"); setToolView("duas"); } },
    { id:'duajournal',      label:'Dua Journal', icon:<IconDuaJournal/>,    action:()=>{ setTab("tools"); setToolView("duajournal"); } },
    { id:'istikharah',      label:'Istikharah',  icon:<IconIstikharah/>,    action:()=>{ setTab("tools"); setToolView("istikharah"); } },
    { id:'moodguide',       label:'Nafs',        icon:<IconMoodGuide/>,     action:()=>{ setTab("tools"); setToolView("moodguide"); } },
    { id:'tajweed',         label:'Tajweed',     icon:<IconTajweed/>,       action:()=>{ setTab("tools"); setToolView("tajweed"); } },
    { id:'hajj',            label:'Hajj',        icon:<IconHajj/>,          action:()=>{ setTab("tools"); setToolView("hajj"); } },
    { id:'janazah',         label:'Janazah',     icon:<IconJanazah/>,       action:()=>{ setTab("tools"); setToolView("janazah"); } },
    { id:'cardgenerator',   label:'Cards',       icon:<IconCardGenerator/>, action:()=>{ setTab("tools"); setToolView("cardgenerator"); } },
    { id:'islamiccalendar', label:'Calendar',    icon:<IconCalendar/>,      action:()=>{ setTab("tools"); setToolView("islamiccalendar"); } },
    { id:'qibla',           label:'Qibla',       icon:<IconQibla/>,         action:()=>{ setShowQiblaOverlay(true); } },
    { id:'mosque',          label:'Mosques',     icon:<IconMosque/>,        action:()=>{ setTab("masjids"); } },
    { id:'settings',        label:'Settings',    icon:<IconSettings/>,      action:()=>{ setTab("settings"); } },
  ];

  // ── Hayah-only fan options (NATIVE / TestFlight builds) ──
  // On the iOS build Yusuf lives in Hayah, so Quick Access becomes his most
  // frequent Hayah screens (usage-sorted, just like the PWA fan). PWA users
  // keep the full FAN_ALL_OPTIONS set unchanged — this list is only used when
  // running natively (see the fan render below).
  const goGhidhaView = (view) => () => {
    try { localStorage.setItem("ghidhaLastView", view); localStorage.setItem("lastTabAt", String(Date.now())); } catch {}
    setTab("hayah"); setHayahFeature("ghidha"); setHayahResetKey(k => k + 1);
  };
  const HAYAH_FAN_OPTIONS = [
    { id:'hayah',         label:'Today',    icon:<span style={{fontSize:"20px"}}>❋</span>,  action:()=>{ try { localStorage.removeItem("ghidhaLastView"); } catch {} setTab("hayah"); setHayahFeature("today"); setHayahResetKey(k => k + 1); } },
    { id:'ghidha',        label:'Calories', icon:<span style={{fontSize:"20px"}}>🥗</span>, action:goGhidhaView("log") },
    { id:'aicoach',       label:'AI Coach', icon:<span style={{fontSize:"20px"}}>💬</span>, action:()=>{ setTab("hayah"); setHayahFeature("ai"); } },
    { id:'exercise',      label:'Exercise', icon:<span style={{fontSize:"20px"}}>💪</span>, action:()=>{ setTab("hayah"); setHayahFeature("exercise"); } },
    { id:'ghidhahistory', label:'History',  icon:<span style={{fontSize:"20px"}}>🗓</span>, action:goGhidhaView("history") },
    { id:'ghidhareports', label:'Reports',  icon:<span style={{fontSize:"20px"}}>📊</span>, action:goGhidhaView("weekly") },
    { id:'ghidhagoals',   label:'Goals',    icon:<span style={{fontSize:"20px"}}>🎯</span>, action:goGhidhaView("goals") },
    { id:'hayahsettings', label:'Settings', icon:<IconSettings/>,                          action:()=>{ setTab("hayah"); setHayahFeature("settings"); } },
  ];

  /* Freeze-pane mini players visibility — derived from playing state.
     When ANY mini player is shown above the scroll-content, the per-tab
     sticky header below should NOT add its own env(safe-area-inset-top)
     padding (the freeze pane already handles status-bar clearance).
     Each sticky header reads `freezePaneVisible` from this scope. */
  const showRadio = playing && playStatus !== "stopped" && !(playing.url.includes('livemasjid.com') ? tab === "masjids" : tab === "radio");
  const showSurah = surahCurrentAyah !== null && !(tab === "tools" && toolView === "surah");
  const showEgzon = egzonPlayer.playingNum != null && !(tab === "tools" && toolView === "egzon");
  const showDossari = dossariPlayer.playingNum != null && !(tab === "tools" && toolView === "dossari");
  const freezePaneVisible = showRadio || showSurah || showEgzon || showDossari;
  const stickyHeaderTopPad = freezePaneVisible ? "0px" : "env(safe-area-inset-top)";

  return (
    <>

      {/* ── LANDSCAPE QURAN BOOK ── rendered outside .app to bypass willChange:transform stacking context */}
      {isLandscape && toolView === "fullquranreading" && (() => {
        const surahMeta = QURAN_SURAHS.find(s => s.num === quranReadNum) || QURAN_SURAHS[0];
        const ayahs = quranReadCache[quranReadNum] || [];
        const paperBg = "#f9f5e6";
        const paperInk = "#1c1208";
        const paperMuted = "#6b5a3e";
        const paperGold = "#8b6914";
        const paperBorder = "#e0d5b8";

        const toEastern = n => String(n).replace(/\d/g, d => String.fromCharCode(0x0660 + +d));
        const ayahMarker = n => `﴿${toEastern(n)}﴾`;

        const paginateAyahs = (arr, target = 300) => {
          const pages = []; let page = [], chars = 0;
          for (const a of arr) {
            if (chars + a.arabic.length > target && page.length > 0) { pages.push(page); page = [a]; chars = a.arabic.length; }
            else { page.push(a); chars += a.arabic.length; }
          }
          if (page.length) pages.push(page);
          return pages.length ? pages : [[]];
        };
        const pages = paginateAyahs(ayahs);
        const totalPages = pages.length;

        const goToPage = (newPage) => {
          const clamped = Math.max(0, Math.min(newPage, totalPages - 1));
          setQuranCurrentPage(clamped);
          const pageAyahs = pages[clamped] || [];
          if (pageAyahs.length) {
            const lastAyah = pageAyahs[pageAyahs.length - 1].number;
            if (lastAyah > (quranProgress[quranReadNum] || 0)) {
              const np = { ...quranProgress, [quranReadNum]: lastAyah };
              setQuranProgress(np);
              localStorage.setItem('quranProgress', JSON.stringify(np));
            }
          }
        };

        const isBookmarked = bookmarks.some(b => b.num === quranReadNum && b.page === quranCurrentPage);
        const toggleBookmark = () => {
          let newBms;
          if (isBookmarked) {
            newBms = bookmarks.filter(b => !(b.num === quranReadNum && b.page === quranCurrentPage));
          } else {
            const firstAyah = (pages[quranCurrentPage] || [])[0];
            newBms = [...bookmarks, { num: quranReadNum, translit: surahMeta.translit, arabic: surahMeta.arabic, page: quranCurrentPage, totalPages, ayahFrom: firstAyah ? firstAyah.number : 1, date: new Date().toLocaleDateString('en-GB', {day:'numeric',month:'short',year:'numeric'}) }];
          }
          setBookmarks(newBms);
          localStorage.setItem('quranBookmarks', JSON.stringify(newBms));
        };

        let bookSwipeX = null;
        // RTL Mushaf: right half = lower page number (read first), left half = higher
        const hasBack    = quranCurrentPage > 0;
        const hasForward = quranCurrentPage + 2 < totalPages;

        const goToPageDir = (newPage, dir) => {
          const clamped = Math.max(0, Math.min(newPage, totalPages - 1));
          if (clamped === quranCurrentPage) return;
          // Snapshot the page that will physically "turn": right page for forward, left for back
          const snapshotIdx = dir === 'forward' ? quranCurrentPage : quranCurrentPage + 1;
          setBookFlipSnapshot(snapshotIdx);
          setBookFlipDir(dir);
          goToPage(clamped);
          setTimeout(() => { setBookFlipDir(null); setBookFlipSnapshot(null); }, 520);
        };

        const pageStyle = { flex:1, background:paperBg, height:"100%", padding:"16px 18px 12px", display:"flex", flexDirection:"column", overflow:"hidden" };

        const renderPage = (pageAyahs, pageIdx) => {
          if (!pageAyahs || !pageAyahs.length) return (
            <div style={{...pageStyle, alignItems:"center", justifyContent:"center"}}>
              <div style={{fontSize:"28px", color:paperBorder}}>—</div>
            </div>
          );
          const showBismillah = pageIdx === 0 && quranReadNum !== 9 && quranReadNum !== 1;
          return (
            <div style={pageStyle}>
              <div style={{textAlign:"center",fontSize:"10px",color:paperMuted,marginBottom:"8px",letterSpacing:"1.5px",fontWeight:"600"}}>
                {pageIdx + 1} / {totalPages}
              </div>
              {showBismillah && (
                <div style={{textAlign:"center",fontSize:"18px",color:paperGold,fontFamily:"'Scheherazade New', serif",direction:"rtl",lineHeight:1.8,marginBottom:"6px"}}>
                  بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
                </div>
              )}
              <div style={{flex:1,direction:"rtl",textAlign:"justify",fontSize:"26px",lineHeight:2.1,color:paperInk,fontFamily:"'Scheherazade New', serif",overflow:"hidden",wordSpacing:"2px"}}>
                {pageAyahs.map(a => (
                  <span key={a.number}>
                    {a.arabic}
                    <span style={{color:paperGold,fontSize:"18px",fontFamily:"'Scheherazade New', serif"}}> {ayahMarker(a.number)} </span>
                  </span>
                ))}
              </div>
            </div>
          );
        };

        if (!ayahs.length) return null;

        return (
          <div
            key="quran-book"
            style={{position:"fixed",top:0,left:0,width:"100vw",height:"100vh",background:"#100600",zIndex:9999,display:"flex",flexDirection:"column"}}
            onTouchStart={e => { bookSwipeX = e.touches[0].clientX; }}
            onTouchEnd={e => {
              if (bookSwipeX === null) return;
              const dx = e.changedTouches[0].clientX - bookSwipeX;
              bookSwipeX = null;
              // RTL: swipe RIGHT = read forward (advance), swipe LEFT = go back
              if (dx > 50)  goToPageDir(quranCurrentPage + 2, 'forward');
              else if (dx < -50) goToPageDir(quranCurrentPage - 2, 'back');
            }}
          >
            {/* Header */}
            <div style={{display:"flex",alignItems:"center",gap:"10px",padding:"10px 14px",background:"rgba(8,3,0,0.92)",flexShrink:0,borderBottom:"1px solid rgba(212,168,83,0.18)"}}>
              <button onClick={() => setToolView("fullquran")} style={{background:"transparent",border:"1px solid rgba(255,255,255,0.2)",borderRadius:"6px",color:"rgba(255,255,255,0.75)",fontSize:"13px",padding:"5px 12px",cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>← List</button>
              {quranReadNum > 1 && <button onClick={() => loadQuranSurah(quranReadNum - 1)} style={{background:"transparent",border:"1px solid rgba(255,255,255,0.15)",borderRadius:"6px",color:"rgba(255,255,255,0.55)",fontSize:"12px",padding:"5px 10px",cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>‹ {QURAN_SURAHS.find(s=>s.num===quranReadNum-1)?.translit}</button>}
              <div style={{flex:1,textAlign:"center"}}>
                <span style={{fontSize:"17px",color:"#d4a853",fontFamily:"'Scheherazade New', serif",marginLeft:"8px"}}>{surahMeta.arabic}</span>
                <span style={{fontSize:"12px",color:"rgba(255,255,255,0.45)",marginRight:"8px"}}> · {surahMeta.translit}</span>
              </div>
              {quranReadNum < 114 && <button onClick={() => loadQuranSurah(quranReadNum + 1)} style={{background:"transparent",border:"1px solid rgba(255,255,255,0.15)",borderRadius:"6px",color:"rgba(255,255,255,0.55)",fontSize:"12px",padding:"5px 10px",cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>{QURAN_SURAHS.find(s=>s.num===quranReadNum+1)?.translit} ›</button>}
              <button onClick={toggleBookmark} style={{background:isBookmarked?"rgba(212,168,83,0.2)":"transparent",border:`1px solid ${isBookmarked?"rgba(212,168,83,0.5)":"rgba(255,255,255,0.2)"}`,borderRadius:"6px",fontSize:"15px",padding:"5px 10px",cursor:"pointer",color:isBookmarked?"#d4a853":"rgba(255,255,255,0.4)"}}>🔖</button>
            </div>
            {/* Book spread — fills remaining height */}
            <div style={{flex:1,display:"flex",alignItems:"stretch",overflow:"hidden",padding:"8px 0"}}>
              {/* Left arrow — go BACK (earlier pages, RTL) */}
              <div onClick={() => goToPageDir(quranCurrentPage - 2, 'back')} style={{width:"36px",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",cursor:hasBack?"pointer":"default",opacity:hasBack?0.7:0.12,userSelect:"none"}}>
                <span style={{fontSize:"30px",color:"rgba(255,255,255,0.6)"}}>‹</span>
              </div>
              {/* Spread — position:relative so the flip overlay can anchor to it */}
              <div style={{flex:1,position:"relative",display:"flex",alignItems:"stretch",overflow:"hidden",borderRadius:"8px",boxShadow:"0 8px 48px rgba(0,0,0,0.85)"}}>
                {/* LEFT half — higher page number (quranCurrentPage+1) */}
                <div style={{flex:1,borderRadius:"6px 0 0 6px",overflow:"hidden",display:"flex"}}>
                  {renderPage(pages[quranCurrentPage + 1], quranCurrentPage + 1)}
                </div>
                {/* Spine */}
                <div style={{width:"12px",background:"linear-gradient(to right,#5a2500,#1a0800,#5a2500)",flexShrink:0,boxShadow:"inset 3px 0 8px rgba(0,0,0,0.7),inset -3px 0 8px rgba(0,0,0,0.7)"}}/>
                {/* RIGHT half — lower page number (quranCurrentPage) — read first in Arabic */}
                <div style={{flex:1,borderRadius:"0 6px 6px 0",overflow:"hidden",display:"flex"}}>
                  {renderPage(pages[quranCurrentPage], quranCurrentPage)}
                </div>
                {/* ── Flip overlay: the page physically being turned ── */}
                {bookFlipDir !== null && bookFlipSnapshot !== null && pages[bookFlipSnapshot] && (() => {
                  const isForward = bookFlipDir === 'forward';
                  return (
                    <>
                      {/* The turning page */}
                      <div style={{
                        position:"absolute", top:0,
                        left:  isForward ? "calc(50% + 6px)" : 0,
                        width: "calc(50% - 6px)", height:"100%",
                        transformOrigin: isForward ? "left center" : "right center",
                        animation: `${isForward ? 'flipPageForward' : 'flipPageBack'} 0.48s ease-in forwards`,
                        overflow:"hidden",
                        borderRadius: isForward ? "0 6px 6px 0" : "6px 0 0 6px",
                        zIndex:12, pointerEvents:"none",
                      }}>
                        {renderPage(pages[bookFlipSnapshot], bookFlipSnapshot)}
                        {/* Curling-edge shadow gradient on the page itself */}
                        <div style={{
                          position:"absolute", inset:0, pointerEvents:"none",
                          background: isForward
                            ? "linear-gradient(to right, rgba(0,0,0,0.45) 0%, transparent 55%)"
                            : "linear-gradient(to left,  rgba(0,0,0,0.45) 0%, transparent 55%)",
                        }}/>
                      </div>
                      {/* Shadow cast onto the newly-revealed page beneath */}
                      <div style={{
                        position:"absolute", top:0,
                        left:  isForward ? "calc(50% + 6px)" : 0,
                        width: "calc(50% - 6px)", height:"100%",
                        background: isForward
                          ? "linear-gradient(to right, rgba(0,0,0,0.5) 0%, transparent 60%)"
                          : "linear-gradient(to left,  rgba(0,0,0,0.5) 0%, transparent 60%)",
                        animation: `${isForward ? 'flipShadowForward' : 'flipShadowBack'} 0.48s ease-out forwards`,
                        zIndex:11, pointerEvents:"none",
                      }}/>
                    </>
                  );
                })()}
              </div>
              {/* Right arrow — go FORWARD (later pages, RTL) */}
              <div onClick={() => goToPageDir(quranCurrentPage + 2, 'forward')} style={{width:"36px",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",cursor:hasForward?"pointer":"default",opacity:hasForward?0.7:0.12,userSelect:"none"}}>
                <span style={{fontSize:"30px",color:"rgba(255,255,255,0.6)"}}>›</span>
              </div>
            </div>
          </div>
        );
      })()}

      <div className="app"
        style={(() => {
          // Main tabs (masjids/radio/qibla/tools/settings) now do their
          // OWN status-bar clearance via each tab's top header using
          // `paddingTop: calc(14px + env(safe-area-inset-top))`. That
          // gives us full-bleed sticky headers that content scrolls
          // BEHIND — the iOS Settings / Mail / Music pattern. .app's
          // inline paddingTop is kept only for the feedback + khatm
          // full-screen views which don't have their own top header.
          const fanIsActive = showFanMenu && fanStage !== 'idle' && fanStage !== 'init';
          const base = (toolView === "feedback" || toolView === "khatm") ? {paddingTop:"env(safe-area-inset-top, 0px)"} : {};
          return { ...base, filter: fanIsActive ? 'blur(14px) brightness(0.4) saturate(0.8)' : 'none', transform: fanIsActive ? 'scale(1.04)' : 'scale(1)', transition: 'filter 500ms ease, transform 600ms ease', pointerEvents: fanIsActive ? 'none' : undefined };
        })()}
        onTouchStart={e => {
          if (tab === "masjids" && scrollContentRef.current?.scrollTop === 0)
            ptrStartY.current = e.touches[0].clientY;
        }}
        onTouchMove={e => {
          if (ptrStartY.current === null) return;
          const dy = e.touches[0].clientY - ptrStartY.current;
          if (dy > 0 && scrollContentRef.current?.scrollTop === 0) {
            setPtrY(Math.min(dy * 0.45, 70));
          } else {
            ptrStartY.current = null;
            setPtrY(0);
          }
        }}
        onTouchEnd={() => {
          if (ptrY >= 55 && !ptrRefreshing) {
            hapticToggle();
            setPtrRefreshing(true);
            Promise.all([fetchStations(), fetchLiveStatus()]).finally(() => { setPtrRefreshing(false); setPtrY(0); });
          } else {
            setPtrY(0);
          }
          ptrStartY.current = null;
        }}
      >
        {/* Header */}
        <div className="header" style={(toolView === "feedback" || toolView === "khatm" || tab === "radio" || tab === "masjids" || tab === "settings" || tab === "tools" || tab === "hayah" || tab === "maintainer") ? {display:"none"} : undefined}>
          <div style={{position:"relative",flexShrink:0}}>
          <div ref={logoRef} onClick={() => { openFanMenu(); if (showLogoHint) { setShowLogoHint(false); localStorage.setItem("logoHintShown","1"); } }} style={{width:"44px",height:"44px",borderRadius:"13px",visibility: showFanMenu ? "hidden" : "visible",background:darkMode?"rgba(4,18,16,0.5)":"rgba(224,247,243,0.5)",backdropFilter:"blur(16px) saturate(180%)",WebkitBackdropFilter:"blur(16px) saturate(180%)",border:darkMode?"1px solid rgba(0,201,167,0.25)":"1px solid rgba(0,158,135,0.2)",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:showLogoHint?(darkMode?"0 0 14px rgba(0,201,167,0.6)":"0 0 14px rgba(0,158,135,0.5)"):(darkMode?"0 2px 16px rgba(0,201,167,0.15), inset 0 1px 0 rgba(255,255,255,0.05)":"0 2px 16px rgba(0,158,135,0.1), inset 0 1px 0 rgba(255,255,255,0.4)"),cursor:"pointer",animation:remoteConfig.iconGlow!==false?"logoPulse 3s ease-in-out infinite":"none"}}>
            <svg viewBox="0 0 44 44" width="38" height="38" fill="none" xmlns="http://www.w3.org/2000/svg">
              <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85"/>
              <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85" transform="rotate(60 22 22)"/>
              <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85" transform="rotate(120 22 22)"/>
            </svg>
          </div>
          {showLogoHint && (
            <div style={{position:"absolute",top:"calc(100% + 8px)",left:0,background:"var(--accent)",color:"#07090c",fontSize:"10px",fontWeight:"800",padding:"5px 10px",borderRadius:"8px",whiteSpace:"nowrap",zIndex:200,animation:"fadeInBg 0.5s ease",boxShadow:"0 3px 12px rgba(0,201,167,0.45)",pointerEvents:"none"}}>
              Tap for Quick Access
              <div style={{position:"absolute",top:"-4px",left:"14px",width:0,height:0,borderLeft:"4px solid transparent",borderRight:"4px solid transparent",borderBottom:"4px solid var(--accent)"}}/>
            </div>
          )}
          </div>
          <div style={{flex:1,paddingLeft:"2px"}}>
            <div onClick={handleSiratTap} className="header-title" style={{fontSize:"26px",letterSpacing:"0.5px",display:"flex",alignItems:"baseline",gap:"6px",cursor:"default"}}>Sirat{isSupportMode && hasSupported && (() => { const t = getSupporterTier(supportTotalRef.current, remoteConfig.support); return t.name ? <span style={{fontSize:"8px",fontWeight:"800",color:t.color,lineHeight:1,padding:"2px 7px",border:`1.5px solid ${t.color}`,borderRadius:"6px",opacity:0.9,position:"relative",top:"-1px",letterSpacing:"1.2px"}}>{t.name.toUpperCase()}</span> : null; })()}</div>
            <div className="header-sub">The Straight Path</div>
          </div>
          <div ref={hijriRef} onClick={toggleHijriCalendar} style={{textAlign:'right',flexShrink:0,cursor:'pointer'}}>
            {hijri && <div className="hijri" style={{color: showHijriCalendar ? "var(--gold)" : undefined, transition:"color 0.2s"}}>{hijri}</div>}
            <div style={{fontSize:'10px',color:'var(--muted)',fontWeight:'500',opacity:0.7}}>{headerNow.toLocaleDateString('en-ZA',{weekday:'short',day:'numeric',month:'short',year:'numeric'})}</div>
          </div>
          <div style={{position:"relative",flexShrink:0}}>
            <button
              onClick={() => { setDarkMode(d => !d); if (showThemeHint) { setShowThemeHint(false); localStorage.setItem("themeHintShown","1"); } }}
              aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
              style={{background:"transparent",border:"none",cursor:"pointer",padding:"6px",borderRadius:"10px",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"20px",lineHeight:1,opacity:0.75,transition:"opacity 0.2s, box-shadow 0.3s",boxShadow:showThemeHint&&darkMode?"0 0 14px rgba(255,179,0,0.6)":"none"}}
              onMouseEnter={e=>e.currentTarget.style.opacity=1}
              onMouseLeave={e=>e.currentTarget.style.opacity=0.75}
            >
              {darkMode ? "☀️" : "🌙"}
            </button>
            {showThemeHint && (
              <div style={{position:"absolute",top:"calc(100% + 8px)",right:0,background:"var(--gold)",color:"#07090c",fontSize:"10px",fontWeight:"800",padding:"5px 10px",borderRadius:"8px",whiteSpace:"nowrap",zIndex:200,animation:"fadeInBg 0.5s ease",boxShadow:"0 3px 12px rgba(255,179,0,0.45)",pointerEvents:"none"}}>
                {theme === "dark" ? "Tap for light mode" : theme === "light" ? "Tap for Apple mode" : "Tap for dark mode"}
                <div style={{position:"absolute",top:"-4px",right:"14px",width:0,height:0,borderLeft:"4px solid transparent",borderRight:"4px solid transparent",borderBottom:"4px solid var(--gold)"}}/>
              </div>
            )}
          </div>
        </div>


        {/* Tips overlay moved to fixed overlay position below */}

        {showVerseOverlay && (() => {
          const VERSES = [
            {ar:"إِنَّ مَعَ الْعُسْرِ يُسْرًا",tr:"Inna maʿal-ʿusri yusrā",en:"Indeed, with hardship comes ease.",ref:"94:6"},
            {ar:"وَمَن يَتَوَكَّلْ عَلَى اللَّهِ فَهُوَ حَسْبُهُ",tr:"Wa man yatawakkal ʿalallāhi fahuwa ḥasbuh",en:"Whoever relies upon Allah — He is sufficient for him.",ref:"65:3"},
            {ar:"فَاذْكُرُونِي أَذْكُرْكُمْ",tr:"Fadhkurūnī adhkurkum",en:"Remember Me, and I will remember you.",ref:"2:152"},
            {ar:"وَإِذَا سَأَلَكَ عِبَادِي عَنِّي فَإِنِّي قَرِيبٌ",tr:"Wa idhā saʾalaka ʿibādī ʿannī fa'innī qarīb",en:"When My servants ask about Me — I am near.",ref:"2:186"},
            {ar:"لَا يُكَلِّفُ اللَّهُ نَفْسًا إِلَّا وُسْعَهَا",tr:"Lā yukallifullāhu nafsan illā wusʿahā",en:"Allah does not burden a soul beyond what it can bear.",ref:"2:286"},
            {ar:"إِنَّ اللَّهَ مَعَ الصَّابِرِينَ",tr:"Innallāha maʿa ṣ-ṣābirīn",en:"Indeed, Allah is with the patient.",ref:"2:153"},
            {ar:"حَسْبُنَا اللَّهُ وَنِعْمَ الْوَكِيلُ",tr:"Ḥasbunallāhu wa niʿmal-wakīl",en:"Allah is sufficient for us — the best Disposer of affairs.",ref:"3:173"},
            {ar:"وَهُوَ مَعَكُمْ أَيْنَ مَا كُنتُمْ",tr:"Wa huwa maʿakum ayna mā kuntum",en:"He is with you wherever you are.",ref:"57:4"},
            {ar:"رَبِّ زِدْنِي عِلْمًا",tr:"Rabbi zidnī ʿilmā",en:"My Lord, increase me in knowledge.",ref:"20:114"},
            {ar:"وَمَا تَوْفِيقِي إِلَّا بِاللَّهِ",tr:"Wa mā tawfīqī illā billāh",en:"My success is only through Allah.",ref:"11:88"},
            {ar:"أَلَا بِذِكْرِ اللَّهِ تَطْمَئِنُّ الْقُلُوبُ",tr:"Alā bidhikrillāhi taṭmaʾinnul-qulūb",en:"Verily, in the remembrance of Allah do hearts find rest.",ref:"13:28"},
            {ar:"وَعَسَىٰ أَن تَكْرَهُوا شَيْئًا وَهُوَ خَيْرٌ لَّكُمْ",tr:"Wa ʿasā an takrahū shayʾan wa huwa khayrun lakum",en:"Perhaps you dislike something that is good for you.",ref:"2:216"},
            {ar:"إِنَّ اللَّهَ لَا يُضِيعُ أَجْرَ الْمُحْسِنِينَ",tr:"Innallāha lā yudīʿu ajral-muḥsinīn",en:"Indeed, Allah does not waste the reward of the righteous.",ref:"9:120"},
            {ar:"وَلَا تَيْأَسُوا مِن رَّوْحِ اللَّهِ",tr:"Wa lā tayʾasū min rawḥillāh",en:"Do not despair of the mercy of Allah.",ref:"12:87"},
            {ar:"إِنَّ اللَّهَ لَا يُغَيِّرُ مَا بِقَوْمٍ حَتَّىٰ يُغَيِّرُوا مَا بِأَنفُسِهِمْ",tr:"Innallāha lā yughayyiru mā biqawmin ḥattā yughayyirū mā bi-anfusihim",en:"Allah does not change a people's condition until they change what is within themselves.",ref:"13:11"},
            {ar:"وَمَن يَتَّقِ اللَّهَ يَجْعَل لَّهُ مَخْرَجًا",tr:"Wa man yattaqillāha yajʿal lahu makhrajā",en:"Whoever fears Allah — He will make a way out for him.",ref:"65:2"},
            {ar:"وَاللَّهُ يُحِبُّ الصَّابِرِينَ",tr:"Wallāhu yuḥibbuṣ-ṣābirīn",en:"And Allah loves the patient.",ref:"3:146"},
            {ar:"وَمَا خَلَقْتُ الْجِنَّ وَالْإِنسَ إِلَّا لِيَعْبُدُونِ",tr:"Wa mā khalaqtul-jinna wal-insa illā liyaʿbudūn",en:"I did not create jinn and mankind except to worship Me.",ref:"51:56"},
            {ar:"رَبَّنَا آتِنَا فِي الدُّنْيَا حَسَنَةً وَفِي الْآخِرَةِ حَسَنَةً",tr:"Rabbanā ātinā fid-dunyā ḥasanatan wa fil-ākhirati ḥasanah",en:"Our Lord, grant us good in this world and good in the Hereafter.",ref:"2:201"},
            {ar:"وَلَا تَهِنُوا وَلَا تَحْزَنُوا وَأَنتُمُ الْأَعْلَوْنَ إِن كُنتُم مُّؤْمِنِينَ",tr:"Wa lā tahinū wa lā taḥzanū wa antumul-aʿlawna in kuntum muʾminīn",en:"Do not weaken or grieve — you will be superior if you are true believers.",ref:"3:139"},
            {ar:"وَإِن تَعُدُّوا نِعْمَةَ اللَّهِ لَا تُحْصُوهَا",tr:"Wa in taʿuddū niʿmatallāhi lā tuḥṣūhā",en:"If you tried to count Allah's blessings, you could never enumerate them.",ref:"16:18"},
            {ar:"إِنَّ الَّذِينَ قَالُوا رَبُّنَا اللَّهُ ثُمَّ اسْتَقَامُوا تَتَنَزَّلُ عَلَيْهِمُ الْمَلَائِكَةُ",tr:"Innalladhīna qālū rabbunallāhu thummastaaqāmū tatanazzalu ʿalayhimul-malāʾikah",en:"Those who say 'Our Lord is Allah' and remain steadfast — the angels descend upon them.",ref:"41:30"},
            {ar:"وَلَذِكْرُ اللَّهِ أَكْبَرُ",tr:"Wa ladhikrillāhi akbar",en:"And the remembrance of Allah is the greatest.",ref:"29:45"},
            {ar:"سَنُرِيهِمْ آيَاتِنَا فِي الْآفَاقِ وَفِي أَنفُسِهِمْ",tr:"Sanurīhim āyātinā fil-āfāqi wa fī anfusihim",en:"We will show them Our signs in the horizons and within themselves.",ref:"41:53"},
          ];
          const v = VERSES[Math.floor(Date.now()/86400000) % VERSES.length];
          return (
            <div className="welcome-overlay" onClick={() => setShowVerseOverlay(false)}>
              <div className="welcome-card" onClick={e => e.stopPropagation()}>
                <div style={{width:"64px",height:"64px",borderRadius:"18px",background:darkMode?"linear-gradient(135deg,#041210,#071a14)":"linear-gradient(135deg,#e0f7f3,#eef8f6)",border:darkMode?"1px solid rgba(0,201,167,0.35)":"1px solid rgba(0,158,135,0.3)",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 20px",animation:remoteConfig.iconGlow!==false?"logoPulse 3s ease-in-out infinite":"none"}}>
                  <svg viewBox="0 0 44 44" width="54" height="54" fill="none"><ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85"/><ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85" transform="rotate(60 22 22)"/><ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85" transform="rotate(120 22 22)"/></svg>
                </div>
                {userName && <>
                  <div style={{fontSize:"9px",color:"var(--accent)",letterSpacing:"4px",fontWeight:"600",textTransform:"uppercase",marginBottom:"6px"}}>Assalamu Alaykum</div>
                  <div style={{fontSize:"20px",fontWeight:"700",fontFamily:"Georgia,'Times New Roman',serif",color:"var(--gold)",letterSpacing:"1px",marginBottom:"18px"}}>{userName}</div>
                </>}
                <div style={{width:"36px",height:"1px",background:"linear-gradient(90deg,transparent,rgba(0,201,167,0.4),transparent)",margin:"0 auto 18px"}}/>
                <div style={{fontSize:"9px",color:"var(--accent)",letterSpacing:"3px",textTransform:"uppercase",marginBottom:"14px"}}>Quran · {v.ref}</div>
                <div style={{fontSize:"22px",fontFamily:"serif",color:"var(--gold)",lineHeight:1.8,marginBottom:"12px",direction:"rtl"}}>{v.ar}</div>
                <div style={{fontSize:"11px",color:"var(--accent)",fontStyle:"italic",marginBottom:"10px",opacity:0.75}}>{v.tr}</div>
                <div style={{fontSize:"13px",color:"var(--text)",opacity:0.85,lineHeight:1.6,marginBottom:"22px"}}>"{v.en}"</div>
                <div style={{width:"36px",height:"1px",background:"linear-gradient(90deg,transparent,rgba(255,179,0,0.2),transparent)",margin:"0 auto 18px"}}/>
                <div style={{display:"flex",gap:"10px",justifyContent:"center"}}>
                  <button onClick={async () => {
                    try {
                      const blob = await generateShareCardBlob({ kind: "verse", ar: v.ar, en: v.en, ref: `Quran · ${v.ref}` });
                      const file = new File([blob], "sirat-verse.png", { type: "image/png" });
                      // Image-only share — the card visually contains everything; no caption.
                      if (navigator.canShare?.({ files: [file] })) {
                        await navigator.share({ files: [file] });
                      } else {
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url; a.download = "sirat-verse.png"; a.click();
                        setTimeout(() => URL.revokeObjectURL(url), 1000);
                      }
                    } catch (e) { if (e.name !== "AbortError") console.error(e); }
                  }} style={{background:"rgba(0,201,167,0.12)",border:"1px solid rgba(0,201,167,0.3)",borderRadius:"10px",color:"var(--accent)",fontSize:"11px",letterSpacing:"1.5px",padding:"8px 18px",cursor:"pointer",fontFamily:"inherit",fontWeight:"700"}}>SHARE</button>
                  <button onClick={() => setShowVerseOverlay(false)} style={{background:"transparent",border:"1px solid var(--border)",borderRadius:"10px",color:"var(--muted)",fontSize:"11px",letterSpacing:"2px",padding:"8px 20px",cursor:"pointer",fontFamily:"inherit"}}>CLOSE</button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Debug panel (dev/staging only — hidden on production builds) */}
        {debugMode && debugPlayLog && tab === "masjids" && (
          <div style={{background:"rgba(255,0,0,0.08)",borderBottom:"1px solid rgba(255,0,0,0.25)",padding:"10px 16px",fontSize:"13px",color:"var(--text)",fontFamily:"monospace",wordBreak:"break-all",lineHeight:"1.6"}}>
            <div style={{fontSize:"10px",color:"rgba(255,100,100,0.7)",fontWeight:"700",marginBottom:"4px",letterSpacing:"1px"}}>DEBUG</div>
            {debugPlayLog}
          </div>
        )}

        {/* Offline banner */}
        {isOffline && (
          <div style={{background:"linear-gradient(90deg,rgba(255,107,107,0.15),rgba(255,107,107,0.08))",borderBottom:"1px solid rgba(255,107,107,0.3)",padding:"9px 16px",textAlign:"center",fontSize:"13px",fontWeight:"600",color:"var(--danger)",letterSpacing:"0.3px",display:"flex",alignItems:"center",justifyContent:"center",gap:"8px"}}>
            <span style={{fontSize:"15px"}}>⚠</span> You're offline — some features may be unavailable
          </div>
        )}

        {/* App Banner — set from admin panel (Masjids only; Radio has its own hub header) */}
        {appBanner && tab === "masjids" && (
          <div style={{background:"linear-gradient(90deg,rgba(0,201,167,0.12),rgba(0,201,167,0.06))",borderBottom:"1px solid rgba(0,201,167,0.2)",padding:"9px 16px",textAlign:"center",fontSize:"13px",fontWeight:"600",color:"var(--accent)",letterSpacing:"0.2px"}}>
            {appBanner}
          </div>
        )}

        {/* IP ban warning — shown on Masjids tab when LiveMasjid blocks the user's network */}
        {lmBanned && tab === "masjids" && followedMounts.length > 0 && (
          <div style={{background:"rgba(255,152,0,0.1)",borderBottom:"1px solid rgba(255,152,0,0.3)",padding:"8px 16px",textAlign:"center",fontSize:"12px",fontWeight:"600",color:"#ff9800",letterSpacing:"0.2px"}}>
            🔒 Your network is blocked by LiveMasjid — streams may appear offline. Switch to mobile data.
          </div>
        )}

        {/* Setup reminder banners — nudge users who skipped install/notifications */}
        {tab === "masjids" && userId && userId !== "guest" && localStorage.getItem("permFlowDone") && (() => {
          const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone || isNative();
          const banners = [];
          if (!isStandalone) {
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
            banners.push(
              <div key="install-banner" style={{background:"linear-gradient(90deg,rgba(160,100,255,0.12),rgba(160,100,255,0.05))",borderBottom:"1px solid rgba(160,100,255,0.25)",padding:"10px 16px",display:"flex",alignItems:"center",gap:"10px"}}>
                <span style={{fontSize:"16px",flexShrink:0}}>📲</span>
                <div style={{flex:1,fontSize:"12px",color:"var(--text)",lineHeight:1.4}}>
                  <strong>Add Sirat to your Home Screen</strong> to receive Azaan notifications and live masjid alerts — {isIOS ? <>tap <strong>⋯</strong> → <strong>Share</strong> → scroll down → <strong>Add to Home Screen</strong></> : <>tap <strong>⋮</strong> then <strong>Install app</strong></>}
                </div>
              </div>
            );
          }
          return banners.length > 0 ? banners : null;
        })()}

        {/* Friday / Jummah banner — Masjids tab only, on Fridays */}
        {tab === "masjids" && new Date().getDay() === 5 && (!prayerTimes || (new Date().getHours() * 60 + new Date().getMinutes()) < to24min(prayerTimes.Maghrib)) && (
          <div style={{padding:"12px 16px",paddingTop:"calc(12px + env(safe-area-inset-top))",background:darkMode?"linear-gradient(135deg,rgba(0,201,167,0.08),rgba(255,179,0,0.06))":"linear-gradient(135deg,rgba(0,201,167,0.1),rgba(255,179,0,0.08))",borderBottom:`1px solid ${darkMode?"rgba(255,179,0,0.15)":"rgba(184,112,8,0.12)"}`}}>
            <div style={{display:"flex",alignItems:"center",gap:"10px",marginBottom:"8px"}}>
              <span style={{fontSize:"20px"}}>🕌</span>
              <div style={{flex:1}}>
                <div style={{fontSize:"14px",fontWeight:"800",color:"var(--gold)"}}>Jumu'ah Mubarak</div>
                <div style={{fontSize:"11px",color:"var(--muted)",marginTop:"1px"}}>Send abundant salawat upon the Prophet ﷺ</div>
              </div>
            </div>
            <div style={{display:"flex",gap:"8px"}}>
              <button onClick={() => {
                hapticImpact("light");
                const newCount = fridaySalawat + 1;
                setFridaySalawat(newCount);
                localStorage.setItem("fridaySalawat", JSON.stringify({ date: localDateStr(), count: newCount }));
              }} style={{flex:1,padding:"10px",background:darkMode?"rgba(255,179,0,0.1)":"rgba(255,179,0,0.12)",border:`1px solid ${darkMode?"rgba(255,179,0,0.25)":"rgba(184,112,8,0.2)"}`,borderRadius:"12px",color:"var(--gold)",fontSize:"13px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:"6px"}}>
                ﷺ Salawat {fridaySalawat > 0 ? `(${fridaySalawat})` : ""}
              </button>
              <button onClick={() => { setSurahKey("kahf"); setTab("tools"); setToolView("surah"); loadSurah("kahf").then(ayahs => { if (ayahs) playSurahFrom(ayahs, 0); }); }} style={{flex:1,padding:"10px",background:darkMode?"rgba(0,201,167,0.1)":"rgba(0,201,167,0.12)",border:`1px solid ${darkMode?"rgba(0,201,167,0.25)":"rgba(0,158,135,0.2)"}`,borderRadius:"12px",color:"var(--accent)",fontSize:"13px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:"6px"}}>
                🌿 Surah Al-Kahf
              </button>
            </div>
          </div>
        )}

        {/* Full Now Playing bar — show during buffering, playing, and error.
            Radio tab has its own hero now, so only LM streams on Masjids use the full bar. */}
        {/* On the Salaah tab, every surface (chip, Live Now card, Featured card,
            search row) embeds its own playing state — so the big NowPlayingBar
            would double up. Hide it while a LiveMasjid stream plays on this tab. */}

        {/* Pull-to-refresh indicator */}
        {tab === "masjids" && (ptrY > 0 || ptrRefreshing) && (
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:`${ptrRefreshing ? 44 : ptrY}px`,overflow:"hidden",transition:ptrRefreshing ? "height 0.2s ease" : "none",flexShrink:0}}>
            <div style={{width:"24px",height:"24px",borderRadius:"50%",border:"2.5px solid var(--border)",borderTopColor:"var(--accent)",animation:ptrRefreshing?"spin 0.7s linear infinite":"none",opacity:ptrRefreshing?1:Math.min(ptrY/55,1),transform:ptrRefreshing?"none":`rotate(${ptrY*4}deg)`,transition:ptrRefreshing?"opacity 0.15s":"none"}} />
          </div>
        )}

        {/* Freeze-pane mini players — sit above scroll-content so they never
            scroll off-screen. Any combination of radio, surah, Egzon or Dossari
            plays simultaneously at the top of the app on every tab.
            When this pane is visible, the per-tab sticky header below
            reads `freezePaneVisible` (computed at App scope ~line 2475)
            and skips its own env(safe-area-inset-top) padding so we
            don't double up on status-bar clearance. */}
        {freezePaneVisible && (
          <div style={{flexShrink:0,display:"flex",flexDirection:"column",gap:"8px",padding:"calc(10px + env(safe-area-inset-top)) 0 0"}}>
            {showRadio && <MiniPlayer />}
            {showSurah && <SurahMiniPlayer />}
            {showEgzon && <ReciterMiniPlayer player={egzonPlayer} label="Egzon" reciterColor="var(--gold)" goView="egzon" currentView={toolView} />}
            {showDossari && <ReciterMiniPlayer player={dossariPlayer} label="Dossari" reciterColor="var(--accent)" goView="dossari" currentView={toolView} />}
          </div>
        )}

        <div className="scroll-content" ref={scrollContentRef}>

        {/* Masjids */}
        {tab === "masjids" && (tabsConfig["masjids"]?.enabled ?? true) && (() => {
          const cfg = tabsConfig["masjids"];
          if (cfg && cfg.enabled === false) return (
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"60px 28px",textAlign:"center",gap:"14px",flex:1}}>
              <span style={{fontSize:"48px"}}>🕌</span>
              <div style={{fontSize:"18px",fontWeight:"800",color:"var(--text)"}}>{cfg.label || "Salaah"}</div>
              <div style={{fontSize:"14px",color:"var(--muted)",lineHeight:1.65,maxWidth:"280px"}}>{cfg.disabledMessage || "This section is currently unavailable."}</div>
            </div>
          );
          // Shared visual tokens — match Radio hub
          const hub = darkMode
            ? { elev:"rgba(255,255,255,0.03)", border:"rgba(255,255,255,0.08)", borderStrong:"rgba(255,255,255,0.14)",
                dim:"#a6b6c6", muted:"#6b7e92", brass:"#e8c07c", brass2:"#b58648", heroText:"#fff" }
            : { elev:"#ffffff", border:"rgba(10,20,30,0.08)", borderStrong:"rgba(10,20,30,0.14)",
                dim:"#4a5a6a", muted:"#8293a3", brass:"#a67839", brass2:"#7a5527", heroText:"#fff" };
          const mono = '"JetBrains Mono",ui-monospace,SFMono-Regular,Menlo,monospace';
          const serif = '"Instrument Serif",Georgia,serif';
          const masjidPalette = [
            ["#2e5a4a","#17302a"], ["#5a3a2a","#2a1a14"], ["#3a4a6a","#1a2230"],
            ["#5a2e4a","#2a141a"], ["#2e4a5a","#142230"], ["#4a5a2a","#1a2214"],
          ];
          const masjidAccentForMount = (mount) => {
            if (!mount) return masjidPalette[0];
            const h = [...mount].reduce((a,c) => ((a<<5)-a + c.charCodeAt(0)) | 0, 0);
            return masjidPalette[Math.abs(h) % masjidPalette.length];
          };
          // Data derivations for hero + sections
          const hasLoc = !!(userLat && userLng);
          const today = localDateStr();
          const todayLog = salahLog[today] || {};
          const prayedCount = TRACKABLE_PRAYERS.filter(p => todayLog[p]).length;
          const yesterdayDate = new Date(); yesterdayDate.setDate(yesterdayDate.getDate() - 1);
          const yesterdayStr = yesterdayDate.getFullYear() + "-" + String(yesterdayDate.getMonth()+1).padStart(2,"0") + "-" + String(yesterdayDate.getDate()).padStart(2,"0");
          const streakCurrent = (salahStreak.lastDate === today || salahStreak.lastDate === yesterdayStr) ? salahStreak.count : 0;
          const nextLabel = nextPrayer === "Sunrise" ? "Shuruq" : nextPrayer;
          const nextTimeStr = prayerTimes?.[nextPrayer]?.replace(/ \(.*\)/, "") || "--:--";
          const visibleFollowed = followedMounts.filter(m => !isBlockedMount(m));
          const liveMasjidsCount = visibleFollowed.filter(m => isMountLive(m)).length;
          const weekdayStr = new Date().toLocaleDateString(undefined, {weekday:"long"});
          const heroEyebrow = `${weekdayStr}${hijri ? " · " + hijri : ""}`;
          // Sehri / Iftar pill during Ramadan windows
          const nowMinPill = new Date().getHours() * 60 + new Date().getMinutes();
          const fajrMinPill = prayerTimes ? to24min(prayerTimes.Fajr) : null;
          const maghribMinPill = prayerTimes ? to24min(prayerTimes.Maghrib) : null;
          const imsakStr = prayerTimes?.Imsak?.replace(/ \(.*\)/, "");
          const maghribStrPill = prayerTimes?.Maghrib?.replace(/ \(.*\)/, "");
          let sehriPill = null;
          if (prayerTimes && fajrMinPill != null && maghribMinPill != null) {
            if (nowMinPill < fajrMinPill) sehriPill = <>Sehri ends <b>{imsakStr}</b> · Iftar <b>{maghribStrPill}</b></>;
            else if (nowMinPill >= maghribMinPill) sehriPill = <>Tomorrow · Sehri <b>{imsakStr}</b> · Iftar <b>{maghribStrPill}</b></>;
          }
          return (
          <div className="section" onClick={() => { if (longPressMount) setLongPressMount(null); }} style={{display:"flex",flexDirection:"column",flex:1,minHeight:"100%",padding:0,gap:0}}>

            {/* ── Editorial header ─────────────────────────────── */}
            <div style={{padding:"14px 20px 16px",paddingTop:stickyHeaderTopPad,display:"flex",alignItems:"flex-end",justifyContent:"space-between",gap:"12px",position:"sticky",top:0,zIndex:10,background:darkMode?`rgba(20,16,10,${0.65*headerScroll})`:`rgba(250,243,225,${0.7*headerScroll})`,backdropFilter:`blur(${34*headerScroll}px) saturate(200%)`,WebkitBackdropFilter:`blur(${34*headerScroll}px) saturate(200%)`,borderBottom:`1px solid ${darkMode?`rgba(255,179,0,${0.28*headerScroll})`:`rgba(184,112,8,${0.28*headerScroll})`}`,boxShadow:`0 8px 18px ${darkMode?`rgba(0,0,0,${0.22*headerScroll})`:`rgba(0,0,0,${0.05*headerScroll})`}`,transition:"background .15s ease, border-color .15s ease, box-shadow .15s ease"}}>
              <div style={{minWidth:0}}>
                <div onClick={toggleHijriCalendar} role="button" tabIndex={0}
                  onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleHijriCalendar(); } }}
                  aria-label="Open Hijri calendar"
                  style={{fontFamily:mono,fontSize:"9.5px",letterSpacing:"2.5px",color:hub.muted,textTransform:"uppercase",marginBottom:"6px",cursor:"pointer",display:"inline-block",whiteSpace:"nowrap"}}>{heroEyebrow}</div>
                <div style={{display:"flex",alignItems:"center",gap:"10px"}}>
                  <div
                    ref={logoRef}
                    onClick={() => { openFanMenu(); if (showLogoHint) { setShowLogoHint(false); localStorage.setItem("logoHintShown","1"); } }}
                    aria-label="Sirat shortcuts"
                    style={{width:"36px",height:"36px",borderRadius:"11px",flexShrink:0,visibility: showFanMenu ? "hidden" : "visible",
                      background: darkMode ? "rgba(4,18,16,0.5)" : "rgba(224,247,243,0.5)",
                      backdropFilter:"blur(16px) saturate(180%)", WebkitBackdropFilter:"blur(16px) saturate(180%)",
                      border: darkMode ? "1px solid rgba(0,201,167,0.25)" : "1px solid rgba(0,158,135,0.2)",
                      display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",
                      boxShadow: darkMode ? "0 2px 12px rgba(0,201,167,0.15)" : "0 2px 12px rgba(0,158,135,0.1)",
                      animation: remoteConfig.iconGlow !== false ? "logoPulse 3s ease-in-out infinite" : "none"}}>
                    <svg viewBox="0 0 44 44" width="30" height="30" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85"/>
                      <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85" transform="rotate(60 22 22)"/>
                      <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85" transform="rotate(120 22 22)"/>
                    </svg>
                  </div>
                  <div style={{fontFamily:serif,fontStyle:"italic",fontSize:"34px",color:"var(--text)",letterSpacing:"-0.02em",lineHeight:1}}>Salaah</div>
                </div>
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <div style={{fontFamily:mono,fontSize:"9.5px",letterSpacing:"2px",color:hub.muted,textTransform:"uppercase",marginBottom:"3px"}}>Following</div>
                <div style={{fontFamily:mono,fontSize:"18px",fontWeight:700,color:hub.brass,letterSpacing:"-0.5px"}}>
                  {followedMounts.length}<span style={{fontSize:"11px",color:hub.muted,marginLeft:"4px",fontWeight:500}}>/ {liveMasjidsCount} live</span>
                </div>
              </div>
            </div>

            {/* ── Next-Prayer hero ─────────────────────────────── */}
            {hasLoc && prayerTimes ? (() => {
              // Dial geometry
              const center = 80, ringR = 64;
              const totalDay = 24 * 60;
              const nowMinutes = new Date().getHours() * 60 + new Date().getMinutes();
              const mkPos = (mins) => {
                const deg = (mins / totalDay) * 360 - 90;
                const rad = deg * Math.PI / 180;
                return { x: center + ringR * Math.cos(rad), y: center + ringR * Math.sin(rad) };
              };
              const markers = PRAYER_NAMES.map(p => {
                const t = prayerTimes[p]; if (!t) return null;
                const mins = to24min(t);
                return { name: p, mins, ...mkPos(mins), isNext: p === nextPrayer, isPrayed: !!todayLog[p], hasPassed: mins <= nowMinutes };
              }).filter(Boolean);
              const nowDotPos = mkPos(nowMinutes);
              const glyphFor = (p) => ({ Fajr:"🌙", Sunrise:"🌅", Dhuhr:"☀", Asr:"🌤", Maghrib:"🌆", Isha:"🌃" }[p] || "🕌");
              // Remaining minutes until next prayer (for pulse)
              const nextMin = prayerTimes?.[nextPrayer] ? to24min(prayerTimes[nextPrayer]) : null;
              const remaining = nextMin != null ? ((nextMin - nowMinutes + 1440) % 1440) : null;
              const soon = remaining != null && remaining <= 15;

              return (
                <div style={{margin:"0 16px 16px",padding:"18px 18px 16px",borderRadius:"22px",position:"relative",overflow:"hidden",
                  background:`linear-gradient(135deg,#041a15 0%,#071e18 55%,#0a0d10 100%)`,
                  border:`1px solid ${soon ? "rgba(232,192,124,0.45)" : "rgba(0,201,167,0.22)"}`,
                  boxShadow:"0 20px 40px rgba(0,0,0,0.35)",color:hub.heroText,
                  animation: soon ? "softPulse 2.2s ease-in-out infinite" : "none"}}>
                  <div aria-hidden style={{position:"absolute",inset:0,pointerEvents:"none",
                    backgroundImage:`radial-gradient(circle at 22% 12%, ${hub.brass}3a, transparent 55%), radial-gradient(circle at 82% 88%, rgba(0,201,167,0.22), transparent 55%)`}}/>
                  {/* Top meta row */}
                  <div style={{position:"relative",display:"flex",alignItems:"center",justifyContent:"space-between",gap:"10px",marginBottom:"10px"}}>
                    <div onClick={() => detectLocation(true)} style={{display:"inline-flex",alignItems:"center",gap:"6px",padding:"4px 10px",background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.14)",borderRadius:"99px",fontFamily:mono,fontSize:"9.5px",fontWeight:700,letterSpacing:"1.5px",color:"#fff",textTransform:"uppercase",cursor:"pointer"}}>
                      <span style={{fontSize:"10px"}}>📍</span>
                      {locationLoading ? "Detecting…" : (detectedCity || "Location set")}
                    </div>
                    {streakCurrent > 0 && (
                      <div style={{display:"inline-flex",alignItems:"center",gap:"4px",padding:"4px 10px",background:"rgba(232,192,124,0.16)",border:"1px solid rgba(232,192,124,0.3)",borderRadius:"99px",fontFamily:mono,fontSize:"10px",fontWeight:800,letterSpacing:"1px",color:hub.brass}}>
                        🔥 {streakCurrent}
                      </div>
                    )}
                  </div>

                  {/* Main: countdown | dial */}
                  <div style={{position:"relative",display:"flex",alignItems:"center",gap:"14px"}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontFamily:mono,fontSize:"10.5px",letterSpacing:"2px",opacity:0.75,textTransform:"uppercase",marginBottom:"4px"}}>Next prayer</div>
                      <div style={{fontFamily:serif,fontStyle:"italic",fontSize:"34px",lineHeight:1,letterSpacing:"-0.02em",marginBottom:"6px"}}>{nextLabel}</div>
                      <div style={{fontFamily:mono,fontSize:"26px",fontWeight:700,letterSpacing:"-0.5px",color:hub.brass,lineHeight:1,marginBottom:"6px"}}>{countdown || "--:--"}</div>
                      <div style={{fontFamily:mono,fontSize:"11px",opacity:0.65,letterSpacing:"0.3px"}}>at {nextTimeStr}</div>
                    </div>
                    {/* Arabic name of the next prayer — large watermark between countdown and dial, fades in on prayer transition */}
                    <div key={`pw-${nextPrayer}`} aria-hidden style={{position:"absolute",top:"70%",right:"170px",transform:"translateY(-50%)",fontFamily:"'Scheherazade New','Amiri',serif",fontSize:"88px",color:hub.brass,opacity:0.15,direction:"rtl",lineHeight:0.9,letterSpacing:"-0.02em",whiteSpace:"nowrap",pointerEvents:"none",animation:"arabicRevealIn 1.1s cubic-bezier(0.22,1,0.36,1) 0.2s backwards","--arabic-reveal-to":0.15}}>{PRAYER_ARABIC[nextPrayer] || ""}</div>
                    {/* Dial */}
                    <div style={{flexShrink:0,width:"160px",height:"160px",position:"relative"}}>
                      <svg viewBox="0 0 160 160" width="160" height="160" style={{position:"absolute",inset:0}}>
                        {/* Outer ring track */}
                        <circle cx={center} cy={center} r={ringR} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1.5"/>
                        {/* Prayer markers */}
                        {markers.map(m => (
                          <circle key={m.name} cx={m.x} cy={m.y} r={m.isNext ? 5.5 : (m.isPrayed ? 4 : 3)}
                            fill={m.isNext ? hub.brass : (m.isPrayed ? "#00c9a7" : (m.hasPassed ? "rgba(255,255,255,0.35)" : "rgba(255,255,255,0.25)"))}
                            stroke={m.isNext ? "rgba(232,192,124,0.4)" : "none"} strokeWidth={m.isNext ? 3 : 0}/>
                        ))}
                        {/* Now indicator */}
                        <circle cx={nowDotPos.x} cy={nowDotPos.y} r="2.5" fill="#fff" opacity="0.85"/>
                      </svg>
                      {/* Center glyph — tap for prayer detail sheet */}
                      <div style={{position:"absolute",inset:0,display:"grid",placeItems:"center"}}>
                        <button onClick={(e) => { e.stopPropagation(); setPrayerDetailFor(nextPrayer); }}
                          aria-label={`${nextLabel} details`}
                          style={{width:"76px",height:"76px",borderRadius:"50%",
                            background:`radial-gradient(circle at 35% 35%,${hub.brass}4a,rgba(4,26,21,0.8) 70%)`,
                            border:"1px solid rgba(232,192,124,0.25)",
                            display:"grid",placeItems:"center",fontSize:"32px",lineHeight:1,
                            cursor:"pointer",padding:0,fontFamily:"inherit",
                            boxShadow:"0 2px 10px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.08)",
                            transition:"transform 0.15s ease"}}
                          onMouseDown={e => e.currentTarget.style.transform = "scale(0.95)"}
                          onMouseUp={e => e.currentTarget.style.transform = "scale(1)"}
                          onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}>
                          {glyphFor(nextPrayer)}
                        </button>
                      </div>
                    </div>
                  </div>

                  {sehriPill && (
                    <div style={{position:"relative",marginTop:"14px",padding:"8px 12px",background:"rgba(232,192,124,0.08)",border:"1px solid rgba(232,192,124,0.22)",borderRadius:"10px",fontFamily:mono,fontSize:"11px",color:hub.brass,letterSpacing:"0.3px",textAlign:"center"}}>
                      {sehriPill}
                    </div>
                  )}

                  {/* Today's salah dots */}
                  <div style={{position:"relative",marginTop:"14px",display:"flex",alignItems:"center",gap:"8px"}}>
                    <div style={{flex:1,display:"flex",gap:"6px",alignItems:"stretch"}}>
                      {TRACKABLE_PRAYERS.map(name => {
                        const isPrayed = !!todayLog[name];
                        const nowM = new Date().getHours()*60 + new Date().getMinutes();
                        const rawTime = prayerTimes?.[name];
                        const timeStr = rawTime ? rawTime.replace(/ \(.*\)/, "") : "--:--";
                        const hasPassed = rawTime ? to24min(rawTime) <= nowM : false;
                        const canTap = hasPassed;
                        const isAnim = salahAnimRef.current === name;
                        return (
                          <button key={name} onClick={(e) => { e.stopPropagation(); if (canTap) toggleSalah(name); }}
                            disabled={!canTap}
                            title={name + " · " + timeStr + (isPrayed ? " ✓" : "")}
                            style={{flex:1,padding:"14px 4px",background:isPrayed?"rgba(232,192,124,0.18)":(hasPassed?"rgba(255,255,255,0.04)":"transparent"),
                              border:`1px solid ${isPrayed?"rgba(232,192,124,0.55)":"rgba(255,255,255,0.08)"}`,borderRadius:"12px",
                              color:isPrayed?hub.brass:"rgba(255,255,255,0.7)",fontFamily:mono,fontWeight:700,letterSpacing:"0.3px",
                              cursor:canTap?"pointer":"default",lineHeight:1.1,
                              display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:"5px",
                              animation:isAnim?"livePulse 0.8s ease":"none"}}>
                            <div style={{fontSize:"10px",letterSpacing:"1px",textTransform:"uppercase",opacity:isPrayed?1:0.75}}>{name.slice(0,3)}</div>
                            <div style={{fontSize:"14px",fontWeight:800,letterSpacing:"-0.3px",color:isPrayed?hub.brass:"#fff"}}>
                              {isPrayed ? <>✓ <span style={{fontSize:"11px",opacity:0.7,fontWeight:600}}>{timeStr}</span></> : timeStr}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <div style={{fontFamily:mono,fontSize:"10px",fontWeight:700,color:hub.brass,flexShrink:0}}>{prayedCount}/5</div>
                  </div>
                  {/* Sunrise (Shuruq) — informational, not trackable. Tap for detail sheet. */}
                  {prayerTimes.Sunrise && (() => {
                    const sunriseStr = prayerTimes.Sunrise.replace(/ \(.*\)/, "");
                    const sunrisePassed = to24min(prayerTimes.Sunrise) <= new Date().getHours()*60 + new Date().getMinutes();
                    return (
                      <button onClick={(e) => { e.stopPropagation(); setPrayerDetailFor("Sunrise"); }}
                        aria-label={`Sunrise at ${sunriseStr}`}
                        style={{position:"relative",marginTop:"8px",width:"100%",padding:"8px 12px",
                          background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:"10px",
                          display:"flex",alignItems:"center",justifyContent:"space-between",gap:"8px",
                          fontFamily:mono,color:"rgba(255,255,255,0.75)",cursor:"pointer",opacity:sunrisePassed?0.7:1}}>
                        <span style={{display:"inline-flex",alignItems:"center",gap:"6px",fontSize:"10px",fontWeight:700,letterSpacing:"1px",textTransform:"uppercase"}}>
                          <span style={{fontSize:"13px"}}>🌅</span> Sunrise <span style={{opacity:0.55,fontWeight:600,letterSpacing:"0.5px"}}>· Shuruq</span>
                        </span>
                        <span style={{fontSize:"14px",fontWeight:800,letterSpacing:"-0.3px",color:"#fff"}}>{sunriseStr}</span>
                      </button>
                    );
                  })()}
                  <button onClick={() => setShowQiblaOverlay(true)}
                    style={{marginTop:"12px",width:"100%",padding:"10px 14px",
                      background:"rgba(232,192,124,0.1)",border:"1px solid rgba(232,192,124,0.38)",
                      borderRadius:"12px",color:hub.brass,fontFamily:mono,fontSize:"11px",
                      fontWeight:800,letterSpacing:"1.5px",textTransform:"uppercase",
                      cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:"8px"}}>
                    <span style={{fontSize:"14px"}}>🧭</span> Find Qibla
                  </button>
                </div>
              );
            })() : (
              <div style={{margin:"0 16px 16px",padding:"20px 20px",borderRadius:"22px",background:hub.elev,border:`1px solid ${hub.border}`,textAlign:"center"}}>
                <div style={{fontSize:"36px",marginBottom:"10px"}}>📍</div>
                <div style={{fontFamily:serif,fontStyle:"italic",fontSize:"22px",color:"var(--text)",marginBottom:"6px"}}>Set your location</div>
                <div style={{fontFamily:mono,fontSize:"11px",color:hub.muted,letterSpacing:"0.3px",lineHeight:1.6,marginBottom:"14px",maxWidth:"280px",marginLeft:"auto",marginRight:"auto"}}>Sirat needs your location to show accurate prayer times.</div>
                <button disabled={locationLoading} onClick={() => detectLocation(true)}
                  style={{padding:"10px 22px",background:"linear-gradient(135deg,#00c9a7,#009e87)",border:"none",borderRadius:"12px",color:"#07090c",fontFamily:mono,fontSize:"11px",fontWeight:800,letterSpacing:"1.5px",textTransform:"uppercase",cursor:"pointer"}}>
                  {locationLoading ? "Detecting…" : "Enable Location"}
                </button>
                <div onClick={() => setTab("settings")} style={{marginTop:"12px",fontFamily:mono,fontSize:"10.5px",color:hub.muted,letterSpacing:"0.5px",cursor:"pointer"}}>
                  or choose a city in Settings →
                </div>
              </div>
            )}

            {!stations ? (
              <div style={{textAlign:"center",color:"var(--muted)",padding:"40px 20px",fontSize:"14px"}}>Loading...</div>
            ) : (
              <>
                {!showSearch && (<>
                {/* ── My Masjids chip carousel (blocked mounts filtered) ── */}
                <div style={{display:"flex",alignItems:"flex-end",justifyContent:"space-between",padding:"4px 20px 10px"}}>
                  <div>
                    <div style={{fontFamily:mono,fontSize:"9.5px",letterSpacing:"2.5px",color:hub.muted,marginBottom:"3px",textTransform:"uppercase"}}>My Masjids</div>
                    <div style={{fontFamily:serif,fontStyle:"italic",fontSize:"20px",color:"var(--text)",letterSpacing:"-0.02em",lineHeight:1.1}}>Your favourites</div>
                  </div>
                  {visibleFollowed.length > 0 && <div style={{fontFamily:mono,fontSize:"11px",fontWeight:700,color:hub.brass,letterSpacing:"0.5px"}}>{visibleFollowed.length}</div>}
                </div>
                {visibleFollowed.length === 0 ? (
                  <div style={{margin:"0 16px 14px"}}>
                    <button onClick={() => { setShowSearch(true); }} style={{width:"100%",padding:"22px 16px",background:"transparent",border:`1.5px dashed ${hub.borderStrong}`,borderRadius:"18px",color:hub.muted,fontFamily:mono,fontSize:"11px",fontWeight:700,letterSpacing:"1.5px",textTransform:"uppercase",cursor:"pointer"}}>
                      <div style={{fontSize:"22px",marginBottom:"6px"}}>🕌</div>
                      + Add your first masjid
                    </button>
                  </div>
                ) : (
                  <div style={{display:"flex",gap:"10px",padding:"0 16px 14px",overflowX:"auto",overflowY:"hidden",scrollbarWidth:"none",WebkitOverflowScrolling:"touch",touchAction:"pan-x pan-y"}}>
                    {visibleFollowed.map(mount => {
                      const url = followedUrls[mount] || `https://relay.livemasjid.com:8443/${mount}`;
                      const station = {url, name: followedNames[mount] || mount};
                      const isActive = playing && playing.url === url;
                      const isLive = isMountLive(mount) || getStreamStatus(url) === true;
                      const hasNotify = effectiveNotify.includes(mount);
                      const notifExcluded = (remoteConfig.notifyExcludeMounts || []).includes(mount);
                      const [c1, c2] = masjidAccentForMount(mount);
                      const initial = (followedNames[mount] || mount).trim().charAt(0).toUpperCase();
                      return (
                        <div key={mount} style={{flexShrink:0,width:"150px",padding:"12px",borderRadius:"18px",
                          background:hub.elev,border:`1px solid ${isActive ? hub.brass : hub.border}`,
                          boxShadow: darkMode ? "none" : "0 1px 2px rgba(0,0,0,0.04)",
                          cursor:"pointer",position:"relative",transition:"border-color .2s, transform .2s",
                          transform: isActive ? "translateY(-1px)" : "none"}}>
                          {/* Remove button */}
                          <button onClick={(e) => { e.stopPropagation(); followMount(mount); }}
                            aria-label="Remove"
                            style={{position:"absolute",top:"6px",right:"6px",zIndex:2,width:"22px",height:"22px",borderRadius:"50%",background:"rgba(0,0,0,0.45)",border:"none",color:"rgba(255,255,255,0.75)",fontSize:"14px",lineHeight:1,cursor:"pointer",display:"grid",placeItems:"center"}}>×</button>
                          <div onClick={() => (isActive && (playStatus === "playing" || playStatus === "buffering") ? stopAudio() : playStation(station))}
                            role="button" tabIndex={0}
                            onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); (isActive && (playStatus === "playing" || playStatus === "buffering") ? stopAudio() : playStation(station)); } }}
                            style={{width:"126px",height:"126px",borderRadius:"14px",overflow:"hidden",position:"relative",
                              background:`linear-gradient(135deg,${c1},${c2})`,
                              boxShadow:"0 6px 14px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.08)"}}>
                            <div aria-hidden style={{position:"absolute",inset:0,
                              backgroundImage:"repeating-linear-gradient(45deg,rgba(255,255,255,0.05) 0 6px,transparent 6px 12px)"}}/>
                            <div style={{position:"absolute",inset:0,display:"grid",placeItems:"center",
                              fontFamily:serif,fontStyle:"italic",fontSize:"54px",color:"rgba(255,255,255,0.92)",letterSpacing:"-0.02em",
                              opacity: isActive && playStatus === "playing" ? 0.35 : 1,transition:"opacity 0.3s ease"}}>{initial}</div>
                            {isLive && !isActive && (
                              <div style={{position:"absolute",top:"8px",left:"8px",display:"flex",alignItems:"center",gap:"4px",
                                padding:"3px 7px",background:"rgba(0,0,0,0.55)",borderRadius:"99px",
                                fontFamily:mono,fontSize:"9px",fontWeight:800,letterSpacing:"1.5px",color:"#fff"}}>
                                <span style={{display:"inline-block",width:"6px",height:"6px",borderRadius:"50%",background:"#ef6a5a",animation:"softPulse 1.4s ease-in-out infinite"}}/>
                                LIVE
                              </div>
                            )}
                            {!isLive && !isActive && (
                              <div style={{position:"absolute",top:"8px",left:"8px",display:"flex",alignItems:"center",gap:"4px",
                                padding:"3px 7px",background:"rgba(0,0,0,0.5)",borderRadius:"99px",
                                fontFamily:mono,fontSize:"9px",fontWeight:800,letterSpacing:"1.5px",color:"rgba(255,255,255,0.72)"}}>
                                <span style={{display:"inline-block",width:"6px",height:"6px",borderRadius:"50%",background:"rgba(255,255,255,0.45)"}}/>
                                OFFLINE
                              </div>
                            )}
                            {isActive && (
                              <div style={{position:"absolute",top:"8px",left:"8px",display:"flex",alignItems:"center",gap:"4px",
                                padding:"3px 8px",background:playStatus==="error"?"#ef6a5a":hub.brass,color:playStatus==="error"?"#fff":"#1a1208",borderRadius:"99px",
                                fontFamily:mono,fontSize:"9px",fontWeight:800,letterSpacing:"1.5px"}}>
                                {playStatus === "playing" ? "PLAYING" : playStatus === "buffering" ? "…" : playStatus === "error" ? "OFFLINE" : "TUNED"}
                              </div>
                            )}
                            {/* Wave visualizer overlay when playing */}
                            {isActive && playStatus === "playing" && (
                              <div aria-hidden style={{position:"absolute",inset:0,display:"flex",alignItems:"flex-end",justifyContent:"center",gap:"3px",padding:"0 0 16px",pointerEvents:"none"}}>
                                {[0,1,2,3,4].map(i => (
                                  <span key={i} style={{width:"4px",height:"28px",background:"rgba(255,255,255,0.92)",borderRadius:"2px",transformOrigin:"bottom",
                                    animation:`waveBar ${0.6 + i*0.12}s ease-in-out ${i*0.07}s infinite`,boxShadow:"0 0 8px rgba(255,255,255,0.35)"}}/>
                                ))}
                              </div>
                            )}
                            {isActive && playStatus === "buffering" && (
                              <div aria-hidden style={{position:"absolute",inset:0,display:"grid",placeItems:"center",pointerEvents:"none"}}>
                                <div className="spinner" style={{width:"28px",height:"28px",border:"3px solid rgba(255,255,255,0.18)",borderTopColor:"#fff",borderRadius:"50%",animation:"spin 0.7s linear infinite"}}/>
                              </div>
                            )}
                            {/* Stop button overlay when active */}
                            {isActive && (playStatus === "playing" || playStatus === "buffering") && (
                              <button onClick={(e) => { e.stopPropagation(); stopAudio(); }}
                                aria-label="Stop"
                                style={{position:"absolute",bottom:"8px",left:"8px",width:"32px",height:"32px",borderRadius:"50%",background:"#fff",color:"#0b1116",border:"none",fontSize:"12px",fontWeight:800,cursor:"pointer",display:"grid",placeItems:"center",boxShadow:"0 4px 10px rgba(0,0,0,0.4)"}}>■</button>
                            )}
                            {!notifExcluded && hasNotify && !isActive && (
                              <div style={{position:"absolute",bottom:"8px",right:"8px",width:"22px",height:"22px",borderRadius:"50%",background:"rgba(0,201,167,0.85)",color:"#07090c",display:"grid",placeItems:"center",fontSize:"11px",boxShadow:"0 2px 6px rgba(0,0,0,0.3)"}}>🔔</div>
                            )}
                          </div>
                          <div style={{marginTop:"10px",fontSize:"12.5px",fontWeight:700,color:isActive?hub.brass:"var(--text)",letterSpacing:"-0.2px",lineHeight:1.25,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{followedNames[mount] || mount}</div>
                          {isActive ? (
                            <div style={{marginTop:"4px",fontFamily:mono,fontSize:"9.5px",fontWeight:700,letterSpacing:"1.5px",textTransform:"uppercase",color:playStatus==="error"?"#ef6a5a":hub.brass}}>
                              {playStatus === "playing" ? "● Live broadcast" : playStatus === "buffering" ? "● Connecting…" : playStatus === "error" ? (autoplayBlocked ? "Tap to retry" : "Offline") : "Tuned"}
                            </div>
                          ) : (
                            <div style={{marginTop:"4px",display:"flex",gap:"6px",alignItems:"center"}}>
                              {!notifExcluded && (
                                <button onClick={(e) => { e.stopPropagation(); toggleNotifyMount(mount); }}
                                  title={hasNotify ? "Disable alerts" : "Enable alerts"}
                                  style={{padding:"2px 7px",borderRadius:"6px",background:hasNotify?"rgba(0,201,167,0.12)":"transparent",border:`1px solid ${hasNotify?"rgba(0,201,167,0.4)":hub.border}`,color:hasNotify?"#00c9a7":hub.muted,fontSize:"11px",cursor:"pointer",lineHeight:1}}>
                                  {hasNotify ? "🔔" : "🔕"}
                                </button>
                              )}
                              {TIMES_MAP[mount] && (
                                <a href={TIMES_MAP[mount]} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()}
                                  style={{padding:"2px 7px",borderRadius:"6px",background:"transparent",border:`1px solid ${hub.border}`,color:hub.muted,fontFamily:mono,fontSize:"8.5px",fontWeight:700,letterSpacing:"1px",textDecoration:"none",textTransform:"uppercase"}}>
                                  Times
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {/* Trailing "+ add more" tile */}
                    <div onClick={() => setShowSearch(true)}
                      role="button" tabIndex={0}
                      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setShowSearch(true); } }}
                      style={{flexShrink:0,width:"150px",padding:"12px",borderRadius:"18px",
                        background:"transparent",border:`1.5px dashed ${hub.borderStrong}`,
                        cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:"176px",gap:"6px",color:hub.muted}}>
                      <div style={{fontSize:"26px"}}>＋</div>
                      <div style={{fontFamily:mono,fontSize:"10px",fontWeight:700,letterSpacing:"1.5px",textTransform:"uppercase",textAlign:"center"}}>Add masjid</div>
                    </div>
                  </div>
                )}
                </>)}

                {/* ── Discover: header + search bar + (accordions or results) ── */}
                {(() => {
                  const featuredList = (remoteConfig.globalStations || []).filter(gs => {
                    const m = (gs.url || "").split("/").pop();
                    return !isBlockedMount(m);
                  });
                  // Live Now: union of (followed + featured + catalog) that is currently live
                  // masjidCatalogTick triggers re-evaluation when catalog arrives from backend
                  void masjidCatalogTick;
                  const liveMountsSet = liveMountsRef.current || new Set();
                  const cleanMount = (m) => (m || "").replace(/\.mp3$/, "");
                  const catalog = livemasjidCacheRef.current || [];
                  const catByMount = new Map();
                  catalog.forEach(m => {
                    const key = cleanMount(m.mname || "");
                    if (key) catByMount.set(key, m);
                  });
                  const liveNowFollowed = followedMounts
                    .filter(m => !isBlockedMount(m) && isMountLive(m))
                    .map(m => ({ url: followedUrls[m] || `https://relay.livemasjid.com:8443/${m}`, name: followedNames[m] || m, mount: m, isFollowed: true }));
                  const followedSet = new Set(liveNowFollowed.map(x => x.mount));
                  const liveNowFeatured = featuredList
                    .filter(gs => getStreamStatus(gs.url) === true)
                    .map(gs => ({ ...gs, mount: cleanMount(gs.url.split("/").pop()), isFollowed: followedMounts.some(m => gs.url.includes(m)) }))
                    .filter(x => !followedSet.has(x.mount));
                  const featuredSet = new Set(liveNowFeatured.map(x => x.mount));
                  // Build catalog-sourced list — any live mount not already in followed/featured
                  const liveNowCatalog = Array.from(liveMountsSet)
                    .map(cleanMount)
                    .filter(m => m && !isBlockedMount(m) && !followedSet.has(m) && !featuredSet.has(m))
                    .map(m => {
                      const meta = catByMount.get(m);
                      return {
                        url: `https://relay.livemasjid.com:8443/${m}`,
                        name: meta?.sname || meta?.sdesc || m,
                        location: meta?.location || null,
                        mount: m,
                        isFollowed: false,
                      };
                    })
                    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
                  const liveNowList = [...liveNowFollowed, ...liveNowFeatured, ...liveNowCatalog];

                  const cats = [
                    liveNowList.length > 0 && { id:"live", label:"Live Now", glyph:"ح", eyebrow:"STREAMING · RIGHT NOW", accent:"#00c9a7", items: liveNowList },
                    featuredList.length > 0 && { id:"featured", label:"Featured", glyph:"ن", eyebrow:"SIRAT PICKS", accent:"#ffb300", items: featuredList.map(gs => ({ ...gs, mount: gs.url.split("/").pop().replace(".mp3",""), isFollowed: followedMounts.some(m => gs.url.includes(m)) })) },
                  ].filter(Boolean);

                  return (
                    <div style={{padding:"0 0 10px"}}>
                      <div style={{padding:"10px 20px 6px"}}>
                        <div style={{fontFamily:mono,fontSize:"9.5px",letterSpacing:"2.5px",color:hub.muted,marginBottom:"3px",textTransform:"uppercase"}}>Discover</div>
                        <div style={{fontFamily:serif,fontStyle:"italic",fontSize:"20px",color:"var(--text)",letterSpacing:"-0.02em",lineHeight:1.1}}>Find more masjids</div>
                      </div>
                      <div style={{padding:"0 16px 12px",position:"relative"}}>
                        <span style={{position:"absolute",left:"28px",top:"50%",transform:"translateY(-50%)",fontSize:"15px",pointerEvents:"none",opacity:0.65}}>🔍</span>
                        <input
                          type="text"
                          placeholder="Search by name, city, suburb or 'live'..."
                          value={searchQuery}
                          onChange={(e) => handleSearch(e.target.value)}
                          style={{padding:"12px 40px 12px 38px",background:hub.elev,border:`1px solid ${hub.border}`,borderRadius:"12px",color:"var(--text)",fontFamily:mono,fontSize:"13px",letterSpacing:"0.2px",width:"100%",boxSizing:"border-box",outline:"none"}}
                        />
                        {(showSearch || searchQuery) && (
                          <button onClick={() => { setSearchQuery(""); setShowSearch(false); }}
                            aria-label="Cancel search"
                            style={{position:"absolute",right:"24px",top:"50%",transform:"translateY(-50%)",background:"transparent",border:"none",color:hub.muted,fontSize:"20px",cursor:"pointer",padding:"4px 8px",lineHeight:1}}>×</button>
                        )}
                      </div>
                      {showSearch ? (
                        !searchQuery ? (
                          <div style={{textAlign:"center",padding:"24px 20px"}}>
                            <div style={{fontFamily:mono,fontSize:"10px",letterSpacing:"2px",color:hub.muted,textTransform:"uppercase",marginBottom:"8px"}}>Search</div>
                            <div style={{fontFamily:serif,fontStyle:"italic",fontSize:"18px",color:"var(--text)"}}>Type a masjid, city or suburb</div>
                            <div style={{fontFamily:mono,fontSize:"11px",color:hub.muted,marginTop:"6px",letterSpacing:"0.3px"}}>or tap × to go back</div>
                          </div>
                        ) : allStreams.length === 0 ? (
                          <div style={{textAlign:"center",padding:"24px 20px"}}>
                            <div style={{fontFamily:mono,fontSize:"10px",letterSpacing:"2px",color:hub.muted,textTransform:"uppercase",marginBottom:"8px"}}>No results</div>
                            <div style={{fontFamily:serif,fontStyle:"italic",fontSize:"18px",color:"var(--text)"}}>No masjids match "{searchQuery}"</div>
                            <div style={{fontFamily:mono,fontSize:"11px",color:hub.muted,marginTop:"6px",letterSpacing:"0.3px"}}>Try a city, suburb, or the word "live"</div>
                          </div>
                        ) : (
                          <div style={{padding:"0 16px 4px"}}>
                            {allStreams.map(s => {
                              const actuallyLive = isMountLive(s.mount);
                              const isThisPlaying = playing && playing.url === s.url;
                              const isBuffering = isThisPlaying && playStatus === "buffering";
                              const isActive = isThisPlaying && playStatus === "playing";
                              const [c1, c2] = masjidAccentForMount(s.mount);
                              const isFollowing = followedMounts.includes(s.mount);
                              const initial = (s.name || s.mount).trim().charAt(0).toUpperCase();
                              return (
                                <div key={s.mount} className="masjid-card" style={{background:hub.elev,border:`1px solid ${hub.border}`}}>
                                  <div aria-hidden style={{position:"absolute",right:"-30px",top:"-30px",width:"90px",height:"90px",borderRadius:"50%",background:`radial-gradient(circle,${c1}55,transparent 70%)`,pointerEvents:"none"}}/>
                                  <div style={{position:"relative",display:"flex",alignItems:"center",gap:"12px"}}>
                                    <div style={{width:"54px",height:"54px",borderRadius:"16px",flexShrink:0,
                                      background:`linear-gradient(135deg,${c1},${c2})`,
                                      display:"grid",placeItems:"center",color:"rgba(255,255,255,0.92)",
                                      fontFamily:serif,fontStyle:"italic",fontSize:"26px",letterSpacing:"-0.02em",
                                      boxShadow:"0 4px 10px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.08)"}}>{initial}</div>
                                    <div style={{flex:1,minWidth:0}}>
                                      <div style={{fontSize:"14px",fontWeight:700,color:"var(--text)",lineHeight:1.25,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.name}</div>
                                      {s.location && <div style={{fontFamily:mono,fontSize:"10.5px",color:hub.muted,marginTop:"2px",letterSpacing:"0.3px",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.location}</div>}
                                      <div style={{marginTop:"5px",display:"inline-flex",alignItems:"center",gap:"5px",padding:"2px 8px",borderRadius:"99px",
                                        background: actuallyLive ? "rgba(239,106,90,0.14)" : "rgba(255,255,255,0.05)",
                                        border: `1px solid ${actuallyLive ? "rgba(239,106,90,0.3)" : "rgba(255,255,255,0.1)"}`,
                                        fontFamily:mono,fontSize:"9px",fontWeight:800,letterSpacing:"1.5px",color: actuallyLive ? "#ef6a5a" : hub.muted,textTransform:"uppercase"}}>
                                        <span style={{display:"inline-block",width:"5px",height:"5px",borderRadius:"50%",background: actuallyLive ? "#ef6a5a" : hub.muted,animation: actuallyLive ? "softPulse 1.4s ease-in-out infinite" : "none"}}/>
                                        {actuallyLive ? "LIVE" : "Offline"}
                                      </div>
                                    </div>
                                    <div style={{display:"flex",flexDirection:"column",gap:"6px",flexShrink:0,alignItems:"stretch"}}>
                                      {actuallyLive && (
                                        <button onClick={() => (isActive && (playStatus === "playing" || playStatus === "buffering") ? stopAudio() : playStation(s))} aria-label={isActive ? "Stop" : "Play"}
                                          style={{width:"38px",height:"38px",borderRadius:"50%",background:isBuffering?hub.brass:"#00c9a7",border:"none",color:"#07090c",fontSize:"14px",fontWeight:800,cursor:"pointer",fontFamily:"inherit",lineHeight:1,display:"grid",placeItems:"center"}}>
                                          {isBuffering ? "…" : isActive ? "■" : "▶"}
                                        </button>
                                      )}
                                      <button onClick={() => followMount(s.mount, s.name, s.url)}
                                        style={{padding:"6px 10px",borderRadius:"10px",background:isFollowing?"rgba(0,201,167,0.12)":"transparent",border:`1px solid ${isFollowing?"rgba(0,201,167,0.4)":hub.border}`,color:isFollowing?"#00c9a7":"var(--text)",fontFamily:mono,fontSize:"9.5px",fontWeight:800,letterSpacing:"1px",textTransform:"uppercase",cursor:"pointer"}}>
                                        {isFollowing ? "✓ Added" : "+ Add"}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )
                      ) : cats.length === 0 ? (
                        <div style={{padding:"6px 20px 16px",fontFamily:mono,fontSize:"11px",color:hub.muted,letterSpacing:"0.3px"}}>Type a name or city to search masjids around the world.</div>
                      ) : (cats.map(cat => {
                        const isOpen = openMasjidCat === cat.id;
                        const accent = cat.accent;
                        return (
                          <div key={cat.id} style={{margin:"0 16px 12px",borderRadius:"18px",overflow:"hidden",
                            background: darkMode
                              ? `linear-gradient(160deg, ${isOpen ? accent + "14" : "rgba(255,255,255,0.02)"} 0%, rgba(0,0,0,0) 100%)`
                              : `linear-gradient(160deg, ${isOpen ? accent + "1a" : "rgba(0,0,0,0.02)"} 0%, rgba(0,0,0,0) 100%)`,
                            border: `1px solid ${isOpen ? accent + "55" : (darkMode ? "rgba(255,255,255,0.08)" : "rgba(10,20,30,0.08)")}`,
                            transition:"border-color 0.2s ease, background 0.2s ease"}}>
                            <button onClick={() => setOpenMasjidCat(openMasjidCat === cat.id ? null : cat.id)}
                              style={{width:"100%",padding:"14px 16px",background:"transparent",border:"none",cursor:"pointer",
                                display:"flex",alignItems:"center",gap:"12px",color:"var(--text)",fontFamily:"inherit",textAlign:"left"}}>
                              <div style={{width:"34px",height:"34px",borderRadius:"10px",flexShrink:0,
                                background: darkMode ? `linear-gradient(135deg,${accent}2a,${accent}0d)` : `linear-gradient(135deg,${accent}22,${accent}0a)`,
                                border:`1px solid ${accent}38`,
                                fontFamily:'"Amiri","Scheherazade New",serif',fontSize:"18px",color:accent,fontWeight:700,
                                display:"grid",placeItems:"center",lineHeight:1}}>{cat.glyph}</div>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{fontFamily:mono,fontSize:"10px",fontWeight:600,letterSpacing:"2.5px",color:darkMode?"#5a7387":"#8293a3",textTransform:"uppercase"}}>{cat.eyebrow}</div>
                                <div style={{display:"flex",alignItems:"baseline",gap:"8px",marginTop:"2px"}}>
                                  <span style={{fontFamily:serif,fontStyle:"italic",fontSize:"22px",color:"var(--text)",letterSpacing:"-0.02em",lineHeight:1}}>{cat.label}</span>
                                  <span style={{fontFamily:mono,fontSize:"11px",fontWeight:700,color:accent,letterSpacing:"0.5px"}}>{cat.items.length}</span>
                                </div>
                              </div>
                              <div style={{width:"26px",height:"26px",borderRadius:"8px",display:"grid",placeItems:"center",color:darkMode?"#5a7387":"#8293a3",fontSize:"13px",transition:"transform 0.25s ease",transform: isOpen ? "rotate(180deg)" : "rotate(0deg)"}}>⌄</div>
                            </button>
                            {isOpen && (
                              <div style={{padding:"4px 12px 14px",display:"flex",flexDirection:"column",gap:"8px",animation:"fadeIn 0.25s ease-out"}}>
                                {cat.items.map((it, i) => {
                                  const isActive = playing && playing.url === it.url;
                                  const isBuffering = isActive && playStatus === "buffering";
                                  const isPlayingNow = isActive && playStatus === "playing";
                                  const isError = isActive && playStatus === "error";
                                  // Prefer the raw Icecast live set (always accurate for LiveMasjid
                                  // catalog items); fall back to streamStatus for non-LM URLs.
                                  const isLive = (it.mount && isMountLive(it.mount)) || getStreamStatus(it.url) === true;
                                  const [c1, c2] = masjidAccentForMount(it.mount || it.name);
                                  const initial = (it.name || it.mount || "·").trim().charAt(0).toUpperCase();
                                  const isFollowed = it.isFollowed;
                                  const statusLabel = isPlayingNow ? "PLAYING" : isBuffering ? "CONNECTING" : isError ? "OFFLINE" : isLive ? "LIVE" : "Offline";
                                  const statusCol = isPlayingNow ? hub.brass : (isError ? "#ef6a5a" : (isLive ? "#ef6a5a" : hub.muted));
                                  const handleToggle = (e) => {
                                    if (e) e.stopPropagation();
                                    if (isActive && (isPlayingNow || isBuffering)) stopAudio();
                                    else playStation({ url: it.url, name: it.name });
                                  };
                                  return (
                                    <div key={(it.mount || it.url) + i} className="masjid-card"
                                      onClick={handleToggle}
                                      style={{background:hub.elev,border:`1px solid ${isActive ? hub.brass : hub.border}`,
                                        boxShadow: isActive ? "0 6px 20px rgba(232,192,124,0.12)" : undefined,
                                        transition:"border-color .2s, box-shadow .2s"}}>
                                      <div aria-hidden style={{position:"absolute",right:"-30px",top:"-30px",width:"90px",height:"90px",borderRadius:"50%",background:`radial-gradient(circle,${isActive?hub.brass+"55":c1+"55"},transparent 70%)`,pointerEvents:"none"}}/>
                                      <div style={{position:"relative",display:"flex",alignItems:"center",gap:"12px"}}>
                                        <div style={{width:"54px",height:"54px",borderRadius:"16px",flexShrink:0,position:"relative",overflow:"hidden",
                                          background:`linear-gradient(135deg,${c1},${c2})`,
                                          boxShadow:"0 4px 10px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.08)"}}>
                                          <div aria-hidden style={{position:"absolute",inset:0,backgroundImage:"repeating-linear-gradient(45deg,rgba(255,255,255,0.05) 0 6px,transparent 6px 12px)"}}/>
                                          <div style={{position:"absolute",inset:0,display:"grid",placeItems:"center",color:"rgba(255,255,255,0.92)",fontFamily:serif,fontStyle:"italic",fontSize:"26px",letterSpacing:"-0.02em",opacity:isPlayingNow?0.3:1,transition:"opacity 0.3s ease"}}>{initial}</div>
                                          {isPlayingNow && (
                                            <div aria-hidden style={{position:"absolute",inset:0,display:"flex",alignItems:"flex-end",justifyContent:"center",gap:"2px",padding:"0 0 8px",pointerEvents:"none"}}>
                                              {[0,1,2,3].map(b => (
                                                <span key={b} style={{width:"3px",height:"18px",background:"#fff",borderRadius:"2px",transformOrigin:"bottom",animation:`waveBar ${0.6 + b*0.12}s ease-in-out ${b*0.07}s infinite`}}/>
                                              ))}
                                            </div>
                                          )}
                                          {isBuffering && (
                                            <div aria-hidden style={{position:"absolute",inset:0,display:"grid",placeItems:"center"}}>
                                              <div className="spinner" style={{width:"20px",height:"20px",border:"2.5px solid rgba(255,255,255,0.2)",borderTopColor:"#fff",borderRadius:"50%",animation:"spin 0.7s linear infinite"}}/>
                                            </div>
                                          )}
                                        </div>
                                        <div style={{flex:1,minWidth:0}}>
                                          <div style={{fontSize:"14px",fontWeight:700,color:isActive?hub.brass:"var(--text)",lineHeight:1.25,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{it.name}</div>
                                          {it.location && <div style={{fontFamily:mono,fontSize:"10.5px",color:hub.muted,marginTop:"2px",letterSpacing:"0.3px",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{it.location}</div>}
                                          <div style={{marginTop:"5px",display:"inline-flex",alignItems:"center",gap:"5px",padding:"2px 8px",borderRadius:"99px",
                                            background: isPlayingNow ? "rgba(232,192,124,0.16)" : isError ? "rgba(239,106,90,0.14)" : isLive ? "rgba(239,106,90,0.14)" : "rgba(255,255,255,0.05)",
                                            border: `1px solid ${isPlayingNow ? "rgba(232,192,124,0.3)" : (isError || isLive) ? "rgba(239,106,90,0.3)" : "rgba(255,255,255,0.1)"}`,
                                            fontFamily:mono,fontSize:"9px",fontWeight:800,letterSpacing:"1.5px",color:statusCol,textTransform:"uppercase"}}>
                                            <span style={{display:"inline-block",width:"5px",height:"5px",borderRadius:"50%",background:statusCol,animation: (isLive || isPlayingNow || isBuffering) ? "softPulse 1.4s ease-in-out infinite" : "none"}}/>
                                            {statusLabel}
                                          </div>
                                        </div>
                                        <div style={{display:"flex",flexDirection:"column",gap:"6px",flexShrink:0,alignItems:"stretch"}}>
                                          <button onClick={handleToggle} aria-label={isPlayingNow || isBuffering ? "Stop" : "Play"}
                                            style={{width:"40px",height:"40px",borderRadius:"50%",
                                              background:isPlayingNow?"#fff":isBuffering?hub.brass:isError?"#ef6a5a":isLive?"#00c9a7":"rgba(255,255,255,0.08)",
                                              border:"none",color:isPlayingNow?"#0b1116":isError?"#fff":isLive?"#07090c":"var(--muted)",
                                              fontSize:"14px",fontWeight:800,cursor:"pointer",fontFamily:"inherit",lineHeight:1,display:"grid",placeItems:"center",
                                              boxShadow: isActive ? "0 6px 14px rgba(0,0,0,0.35)" : "none"}}>
                                            {isBuffering ? "…" : (isPlayingNow ? "■" : isError ? "↻" : "▶")}
                                          </button>
                                          {!isFollowed && it.mount && !isActive && (
                                            <button onClick={(e) => { e.stopPropagation(); followMount(it.mount, it.name, it.url); }}
                                              style={{padding:"6px 10px",borderRadius:"10px",background:"transparent",border:`1px solid ${hub.border}`,color:"var(--text)",fontFamily:mono,fontSize:"9.5px",fontWeight:800,letterSpacing:"1px",textTransform:"uppercase",cursor:"pointer"}}>
                                              + Add
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                      {isError && (
                                        <div style={{position:"relative",marginTop:"8px",fontFamily:mono,fontSize:"10px",color:"#ef6a5a",letterSpacing:"0.5px"}}>
                                          {autoplayBlocked ? "Tap retry to play" : "Stream offline — tap retry"}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      }))}
                    </div>
                  );
                })()}

              </>
            )}
            {stations && <div style={{textAlign:"center",padding:"8px 16px 4px",pointerEvents:"none"}}>
              <span style={{fontSize:"10px",color:"var(--muted)",opacity:0.5}}>In collaboration with <span style={{color:"var(--accent)",fontWeight:"600"}}>LiveMasjid</span></span>
            </div>}
          </div>
          );})()}

        {/* Radio */}
        {tab === "radio" && (tabsConfig["radio"]?.enabled ?? true) && (() => {
          const cfg = tabsConfig["radio"];
          if (cfg && cfg.enabled === false) return (
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"60px 28px",textAlign:"center",gap:"14px",flex:1}}>
              <span style={{fontSize:"48px"}}>📻</span>
              <div style={{fontSize:"18px",fontWeight:"800",color:"var(--text)"}}>{cfg.label || "Radio"}</div>
              <div style={{fontSize:"14px",color:"var(--muted)",lineHeight:1.65,maxWidth:"280px"}}>{cfg.disabledMessage || "This section is currently unavailable."}</div>
            </div>
          );
          // Hub palette — warm brass accent, dark-first, matches existing tokens
          const hub = darkMode
            ? { elev:"rgba(255,255,255,0.03)", border:"rgba(255,255,255,0.08)", borderStrong:"rgba(255,255,255,0.14)",
                dim:"#a6b6c6", muted:"#6b7e92", brass:"#e8c07c", brass2:"#b58648", heroText:"#fff" }
            : { elev:"#ffffff", border:"rgba(10,20,30,0.08)", borderStrong:"rgba(10,20,30,0.14)",
                dim:"#4a5a6a", muted:"#8293a3", brass:"#a67839", brass2:"#7a5527", heroText:"#fff" };
          const mono = '"JetBrains Mono",ui-monospace,SFMono-Regular,Menlo,monospace';
          const serif = '"Instrument Serif",Georgia,serif';
          const stationPalette = [
            ["#3a6b5a","#1a3028"], ["#6b4f2e","#2e2218"], ["#4a3a6b","#1e1a2e"],
            ["#6b3a3a","#2e1818"], ["#2e4a6b","#18222e"], ["#6b5a2e","#2e2818"],
          ];
          const radioList = stations?.radio || [];
          const heroStation = (playing && radioList.find(s => s.url === playing.url))
            || radioList.find(s => getStreamStatus(s.url) === true)
            || radioList[0]
            || null;
          const heroIsActive = !!(playing && heroStation && playing.url === heroStation.url);
          const heroInitial = heroStation ? heroStation.name.trim().charAt(0) : "·";
          const liveCount = radioList.filter(s => getStreamStatus(s.url) === true).length;
          const nowDate = new Date();
          const weekday = nowDate.toLocaleDateString(undefined,{weekday:"long"});
          const eyebrowText = `${weekday}${hijri ? " · " + hijri : ""}`;

          return (
          <div className="section" style={{display:"flex",flexDirection:"column",flex:1,minHeight:"100%",padding:0,gap:0}}
          >
            {/* Editorial header — starts at top of screen, day+date eyebrow, Sirat logo inline */}
            <div style={{padding:"14px 20px 16px",paddingTop:stickyHeaderTopPad,display:"flex",alignItems:"flex-end",justifyContent:"space-between",gap:"12px"}}>
              <div style={{minWidth:0}}>
                <div ref={hijriRef} onClick={toggleHijriCalendar} role="button" tabIndex={0}
                  onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleHijriCalendar(); } }}
                  aria-label="Open Hijri calendar"
                  style={{fontFamily:mono,fontSize:"9.5px",letterSpacing:"2.5px",color:hub.muted,textTransform:"uppercase",marginBottom:"6px",cursor:"pointer",display:"inline-block",transition:"color 0.2s"}}>{eyebrowText}</div>
                <div style={{display:"flex",alignItems:"center",gap:"10px"}}>
                  <div
                    ref={logoRef}
                    onClick={() => { openFanMenu(); if (showLogoHint) { setShowLogoHint(false); localStorage.setItem("logoHintShown","1"); } }}
                    aria-label="Sirat shortcuts"
                    style={{width:"36px",height:"36px",borderRadius:"11px",flexShrink:0,visibility: showFanMenu ? "hidden" : "visible",
                      background: darkMode ? "rgba(4,18,16,0.5)" : "rgba(224,247,243,0.5)",
                      backdropFilter:"blur(16px) saturate(180%)", WebkitBackdropFilter:"blur(16px) saturate(180%)",
                      border: darkMode ? "1px solid rgba(0,201,167,0.25)" : "1px solid rgba(0,158,135,0.2)",
                      display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",
                      boxShadow: darkMode ? "0 2px 12px rgba(0,201,167,0.15)" : "0 2px 12px rgba(0,158,135,0.1)",
                      animation: remoteConfig.iconGlow !== false ? "logoPulse 3s ease-in-out infinite" : "none"}}>
                    <svg viewBox="0 0 44 44" width="30" height="30" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85"/>
                      <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85" transform="rotate(60 22 22)"/>
                      <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85" transform="rotate(120 22 22)"/>
                    </svg>
                  </div>
                  <div style={{fontFamily:serif,fontStyle:"italic",fontSize:"34px",color:"var(--text)",letterSpacing:"-0.02em",lineHeight:1}}>Radio</div>
                </div>
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <div style={{fontFamily:mono,fontSize:"9.5px",letterSpacing:"2px",color:hub.muted,textTransform:"uppercase",marginBottom:"3px"}}>Live now</div>
                <div style={{fontFamily:mono,fontSize:"18px",fontWeight:700,color:hub.brass,letterSpacing:"-0.5px"}}>
                  {liveCount}<span style={{fontSize:"11px",color:hub.muted,marginLeft:"4px",fontWeight:500}}>/ {radioList.length || "—"}</span>
                </div>
              </div>
            </div>

            {/* Now-playing hero */}
            {!stations ? (
              <div style={{margin:"0 16px 18px",padding:"28px 20px",borderRadius:"22px",background:hub.elev,border:`1px solid ${hub.border}`,textAlign:"center",color:hub.muted,fontSize:"13px"}}>Tuning in…</div>
            ) : heroStation ? (
              <RadioHeroCard
                station={{
                  name: heroStation.name,
                  arabicInitial: heroStation.arabicInitial || heroInitial,
                  url: heroStation.url,
                  websiteUrl: heroStation.websiteUrl,
                }}
                playbackState={heroIsActive ? playStatus : "stopped"}
                nowPlaying={heroIsActive && playStatus === "playing" ? radioMeta[heroStation.url] : null}
                autoplayBlocked={autoplayBlocked}
                darkMode={darkMode}
                onPlayPause={() => (heroIsActive && (playStatus === "playing" || playStatus === "buffering") ? stopAudio() : playStation(heroStation))}
              />
            ) : (
              <div style={{margin:"0 16px 18px",padding:"28px 20px",borderRadius:"22px",background:hub.elev,border:`1px solid ${hub.border}`,textAlign:"center",color:hub.muted,fontSize:"13px"}}>No stations available right now.</div>
            )}

            {/* Station chips section */}
            <div style={{display:"flex",alignItems:"flex-end",justifyContent:"space-between",padding:"0 20px",marginBottom:"12px"}}>
              <div>
                <div style={{fontFamily:mono,fontSize:"9.5px",letterSpacing:"2.5px",color:hub.muted,marginBottom:"3px",textTransform:"uppercase"}}>Stations</div>
                <div style={{fontSize:"18px",fontWeight:700,letterSpacing:"-0.3px",color:"var(--text)"}}>On the air now</div>
              </div>
              {ptrRefreshing && <div style={{fontFamily:mono,fontSize:"10px",color:hub.muted,letterSpacing:"1.5px"}}>REFRESHING…</div>}
            </div>

            {stations ? (
              radioList.length ? (
                <div style={{display:"flex",gap:"10px",padding:"0 16px 4px",overflowX:"auto",overflowY:"hidden",scrollbarWidth:"none",WebkitOverflowScrolling:"touch",touchAction:"pan-x pan-y"}}>
                  {radioList.map((s, i) => {
                    const [c1, c2] = stationPalette[i % stationPalette.length];
                    const isActive = playing && playing.url === s.url;
                    const sLive = getStreamStatus(s.url) === true;
                    const initial = s.name.trim().charAt(0);
                    return (
                      <div key={s.url} onClick={() => playStation(s)}
                        role="button" tabIndex={0}
                        onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); playStation(s); } }}
                        style={{flexShrink:0,width:"150px",padding:"12px",borderRadius:"18px",
                          background:hub.elev,border:`1px solid ${isActive ? hub.brass : hub.border}`,
                          boxShadow: darkMode ? "none" : "0 1px 2px rgba(0,0,0,0.04)",
                          cursor:"pointer",position:"relative",transition:"border-color .2s, transform .2s",
                          transform: isActive ? "translateY(-1px)" : "none"}}>
                        <div style={{width:"126px",height:"126px",borderRadius:"14px",overflow:"hidden",position:"relative",
                          background:`linear-gradient(135deg,${c1},${c2})`,
                          boxShadow:"0 6px 14px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.08)"}}>
                          <div aria-hidden style={{position:"absolute",inset:0,
                            backgroundImage:"repeating-linear-gradient(45deg,rgba(255,255,255,0.05) 0 6px,transparent 6px 12px)"}}/>
                          <div style={{position:"absolute",inset:0,display:"grid",placeItems:"center",
                            fontFamily:serif,fontStyle:"italic",fontSize:"54px",color:"rgba(255,255,255,0.9)",letterSpacing:"-0.02em"}}>{initial}</div>
                          {sLive && !isActive && (
                            <div style={{position:"absolute",top:"8px",left:"8px",display:"flex",alignItems:"center",gap:"4px",
                              padding:"3px 7px",background:"rgba(0,0,0,0.55)",borderRadius:"99px",
                              fontFamily:mono,fontSize:"9px",fontWeight:800,letterSpacing:"1.5px",color:"#fff"}}>
                              <span style={{display:"inline-block",width:"6px",height:"6px",borderRadius:"50%",background:"#ef6a5a",animation:"softPulse 1.4s ease-in-out infinite"}}/>
                              LIVE
                            </div>
                          )}
                          {isActive && (
                            <div style={{position:"absolute",top:"8px",left:"8px",display:"flex",alignItems:"center",gap:"4px",
                              padding:"3px 8px",background:hub.brass,color:"#1a1208",borderRadius:"99px",
                              fontFamily:mono,fontSize:"9px",fontWeight:800,letterSpacing:"1.5px"}}>
                              {playStatus === "playing" ? "PLAYING" : playStatus === "buffering" ? "…" : playStatus === "error" ? "OFFLINE" : "TUNED"}
                            </div>
                          )}
                        </div>
                        <div style={{marginTop:"10px",fontSize:"12.5px",fontWeight:700,color:"var(--text)",letterSpacing:"-0.2px",lineHeight:1.25,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.name}</div>
                        <div style={{marginTop:"3px",fontFamily:mono,fontSize:"10px",color:hub.muted,letterSpacing:"0.5px"}}>{sLive ? "Live radio" : "Off air"}</div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{margin:"0 16px",padding:"18px",borderRadius:"16px",background:hub.elev,border:`1px solid ${hub.border}`,textAlign:"center",color:hub.muted,fontSize:"13px"}}>No stations yet.</div>
              )
            ) : (
              <div style={{display:"flex",gap:"10px",padding:"0 16px 4px",overflowX:"hidden"}}>
                {[0,1,2].map(i => (
                  <div key={i} style={{flexShrink:0,width:"150px",padding:"12px",borderRadius:"18px",background:hub.elev,border:`1px solid ${hub.border}`}}>
                    <div style={{width:"126px",height:"126px",borderRadius:"14px",background:"linear-gradient(135deg,rgba(255,255,255,0.06),rgba(255,255,255,0.02))"}}/>
                    <div style={{marginTop:"10px",height:"12px",width:"80%",borderRadius:"4px",background:"rgba(255,255,255,0.08)"}}/>
                    <div style={{marginTop:"6px",height:"9px",width:"50%",borderRadius:"4px",background:"rgba(255,255,255,0.05)"}}/>
                  </div>
                ))}
              </div>
            )}

          </div>
          );
        })()}

        {/* Tools */}
        {tab === "tools" && (
          <>
            {/* Ibadah hub — editorial header + streak + featured + verse (grid view only) */}
            {toolView === "grid" && (() => {
              const IB_MONO = '"JetBrains Mono",ui-monospace,SFMono-Regular,Menlo,monospace';
              const IB_SERIF = '"Instrument Serif",Georgia,serif';
              const IB_ARABIC = '"Amiri","Scheherazade New",serif';
              const IB_GOLD = darkMode ? "#ffb300" : "#b87008";
              const IB_GOLD2 = darkMode ? "#cc8e00" : "#8a5406";
              const IB_ACCENT = darkMode ? "#00c9a7" : "#009e87";
              const IB_ACCENT2 = darkMode ? "#008f76" : "#007a69";
              const IB_DIM = darkMode ? "#a8b6c4" : "#4a5a6a";
              const IB_MUTED = darkMode ? "#5a7387" : "#8293a3";
              const IB_BORDER = darkMode ? "rgba(255,255,255,0.08)" : "rgba(10,20,30,0.08)";
              const IB_PAPER = darkMode
                ? "linear-gradient(160deg,#1a1410 0%,#0e0a06 100%)"
                : "linear-gradient(160deg,#f7ecd8 0%,#efdcb4 100%)";

              // --- Streak calculation from salahLog ---
              const today = localDateStr();
              const prayerKeys = ["Fajr","Dhuhr","Asr","Maghrib","Isha"];
              const todayLog = salahLog[today] || {};
              const todayCount = prayerKeys.filter(p => todayLog[p]).length;
              const todayPct = Math.round((todayCount / 5) * 100);
              const weeklyDots = (() => {
                const arr = [];
                const now = new Date();
                for (let i = 6; i >= 0; i--) {
                  const d = new Date(now); d.setDate(now.getDate() - i);
                  const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
                  const log = salahLog[key] || {};
                  const done = prayerKeys.filter(p => log[p]).length;
                  const letter = ["S","M","T","W","T","F","S"][d.getDay()];
                  arr.push({ letter, pct: done / 5, today: i === 0 });
                }
                return arr;
              })();
              const streakN = salahStreak.count || 0;

              // --- Featured today (rotates by time of day) ---
              const hour = new Date().getHours();
              const featuredTable = hour >= 4 && hour < 11
                ? { key:"azkar", view:"azkar", title:"Morning Azkar", arabic:"أَذْكَارُ الصَّبَاحِ", subtitle:"Begin the day with remembrance — the shield of the believer.", cta:"Begin azkar", glyph:"ص", accent:IB_GOLD, accent2:IB_GOLD2, hasRibbon:true }
                : hour >= 11 && hour < 16
                ? { key:"tasbeeh", view:"tasbeeh", title:"Tasbeeh", arabic:"سُبْحَانَ ٱللَّٰه", subtitle:"Keep a gentle count through your day.", cta:"Continue counting", glyph:"۳۳", accent:IB_GOLD, accent2:IB_GOLD2, hasRibbon:false }
                : hour >= 16 && hour < 21
                ? { key:"azkar", view:"azkar", title:"Evening Azkar", arabic:"أَذْكَارُ الْمَسَاء", subtitle:"Close the day with protection and remembrance.", cta:"Begin azkar", glyph:"م", accent:"#a078ff", accent2:"#643cc8", hasRibbon:true }
                : { key:"surahs", view:"quran", title:"Night Recitation", arabic:"سُورَة الْمُلْك", subtitle:"A Surah that protects the one who recites it before sleep.", cta:"Open Surah player", glyph:"ك", accent:IB_ACCENT, accent2:IB_ACCENT2, hasRibbon:false };

              // tasbeeh progress for ribbon or display
              const tasbeehDone = parseInt(localStorage.getItem("tasbeeh") || "0");
              const tasbeehTarget = 300;
              let featProgress;
              if (featuredTable.key === "tasbeeh") {
                featProgress = { done: Math.min(tasbeehDone, tasbeehTarget), total: tasbeehTarget };
              } else if (featuredTable.key === "azkar") {
                featProgress = { done: todayCount * 6, total: 33 }; // decorative mapping
              } else {
                featProgress = { done: todayCount, total: 5 };
              }

              // --- Inspiration pool — single source shared with the share modal ---
              const IB_INSPIRATIONS_RAW = IB_INSPIRATION_POOL;
              // Islamic Friday window: Thu Maghrib → Fri Maghrib. While inside, lock the card to Jumu'ah (no cycling).
              const _maghribMin = (prayerTimes && prayerTimes.Maghrib) ? (() => {
                const m = String(prayerTimes.Maghrib).match(/(\d{1,2}):(\d{2})/);
                return m ? parseInt(m[1]) * 60 + parseInt(m[2]) : null;
              })() : null;
              const _nowMin = (() => { const d = new Date(); return d.getHours() * 60 + d.getMinutes(); })();
              const _todayDow = new Date().getDay(); // 4 = Thursday, 5 = Friday
              const isIslamicFriday = _maghribMin !== null && (
                (_todayDow === 4 && _nowMin >= _maghribMin) ||
                (_todayDow === 5 && _nowMin < _maghribMin)
              );
              const IB_INSPIRATIONS = IB_INSPIRATIONS_RAW;
              const _jumuahPos = IB_INSPIRATIONS.findIndex(x => x.kind === "jumuah");
              // When locked, always render jumuah regardless of ibadahInspIdx
              const _actualInspIdx = isIslamicFriday && _jumuahPos >= 0 ? _jumuahPos : ibadahInspIdx;
              const insp = IB_INSPIRATIONS[((_actualInspIdx % IB_INSPIRATIONS.length) + IB_INSPIRATIONS.length) % IB_INSPIRATIONS.length];
              const inspIsVerse = insp.kind === "verse";
              const inspIsJumuah = insp.kind === "jumuah";
              const jumuahDisplayName = (userName && userName.trim()) ? userName.trim() : "Friend";
              // Detect Arabic characters → pick calligraphy font for that name
              const hasArabicChars = (s) => typeof s === "string" && /[؀-ۿݐ-ݿﭐ-﷿ﹰ-﻿]/.test(s);
              const jumuahNameIsArabic = hasArabicChars(jumuahDisplayName);
              // If the user typed an English transliteration of a known Arabic-origin name,
              // render the Arabic form beneath as a subtitle (Yusuf → يوسف)
              const jumuahNameAr = jumuahNameIsArabic ? "" : lookupNameAr(jumuahDisplayName);
              // Multi-recipient (comma-separated) — "Mum, Dad, Yusuf" → rendered as "Mum, Dad & Yusuf"
              const jumuahRecipients = jumuahRecipient ? jumuahRecipient.split(",").map(s => s.trim()).filter(Boolean) : [];
              const jumuahRecipientDisplay = jumuahRecipients.length === 0 ? ""
                : jumuahRecipients.length === 1 ? jumuahRecipients[0]
                : jumuahRecipients.length === 2 ? `${jumuahRecipients[0]} & ${jumuahRecipients[1]}`
                : `${jumuahRecipients.slice(0, -1).join(", ")} & ${jumuahRecipients[jumuahRecipients.length - 1]}`;
              const jumuahRecipientIsArabic = hasArabicChars(jumuahRecipientDisplay);
              // Per-recipient Arabic lookup (skipped if whole display is already Arabic)
              const jumuahRecipientsAr = jumuahRecipientIsArabic ? [] : jumuahRecipients.map(n => lookupNameAr(n));
              const jumuahRecipientsArAll = jumuahRecipientsAr.length > 0 && jumuahRecipientsAr.every(Boolean);
              // Only combine when there are multiple names — single names are handled by jumuahRecipientsAr[0]
              const jumuahRecipientArJoined = jumuahRecipientsArAll && jumuahRecipientsAr.length > 1
                ? (jumuahRecipientsAr.length === 2 ? `${jumuahRecipientsAr[0]} و ${jumuahRecipientsAr[1]}`
                   : `${jumuahRecipientsAr.slice(0, -1).join("، ")} و ${jumuahRecipientsAr[jumuahRecipientsAr.length - 1]}`)
                : "";
              const jumuahPlaceholder = "Tap to add a personal message…";
              const jumuahDefaultMsg = "May Allah accept your duas and bless your day.";
              // Suggested message chips — tap to fill the textarea
              const JUMUAH_SUGGESTIONS = [
                "May Allah bless you",
                "Remember me in your duʿa",
                "May Allah grant you khair",
                "Send salawat upon ﷺ"
              ];
              // Arabic greeting variations — rotate weekly alongside the hadith for freshness
              const JUMUAH_GREETINGS = [
                "جُمُعَةٌ مُبَارَكَةٌ",
                "جُمُعَةٌ طَيِّبَةٌ",
                "بَارَكَ اللَّهُ لَكَ"
              ];
              // Hijri day/month parsing — used for Eid detection and the signed-date footer.
              // `hijri` state is a string like "10 Rajab 1447 AH" (populated from Aladhan API)
              const _hijriParts = (() => {
                if (!hijri || typeof hijri !== "string") return null;
                const m = hijri.match(/^(\d+)\s+(\S+)\s+(\d+)/);
                if (!m) return null;
                return { day: parseInt(m[1]), monthEn: m[2], year: m[3] };
              })();
              const _hijriMonth = _hijriParts ? _hijriParts.monthEn.toLowerCase() : "";
              const _hijriDay = _hijriParts ? _hijriParts.day : 0;
              // Eid detection — 1 Shawwal (Fitr) or 10 Dhul Hijjah (Adha)
              const isEidFitr = _hijriDay === 1 && (_hijriMonth.startsWith("shaw") || _hijriMonth.startsWith("šaw"));
              const isEidAdha = _hijriDay === 10 && (_hijriMonth.startsWith("dhu") || _hijriMonth.startsWith("dhū") || _hijriMonth.startsWith("ḏū"));
              const eidKind = isEidFitr ? "fitr" : isEidAdha ? "adha" : null;
              // Friday-themed hadith/verse library — rotates WEEKLY (different every Jumu'ah)
              const JUMUAH_EXTRAS = [
                { ar:"خَيْرُ يَوْمٍ طَلَعَتْ عَلَيْهِ الشَّمْسُ يَوْمُ الْجُمُعَةِ", en:"The best day on which the sun has risen is Friday.", ref:"Muslim · 854" },
                { ar:"أَكْثِرُوا عَلَيَّ مِنَ الصَّلَاةِ يَوْمَ الْجُمُعَةِ", en:"Send abundant salawat upon me on Friday.", ref:"Abū Dāwūd · 1047" },
                { ar:"إِنَّ فِي الْجُمُعَةِ سَاعَةً لَا يُوَافِقُهَا عَبْدٌ مُسْلِمٌ يَسْأَلُ اللَّهَ شَيْئًا إِلَّا أَعْطَاهُ", en:"There is an hour on Friday in which no Muslim asks Allah for anything except that He gives it to him.", ref:"Bukhārī · 935" },
                { ar:"مَنْ قَرَأَ سُورَةَ الْكَهْفِ يَوْمَ الْجُمُعَةِ أَضَاءَ لَهُ مِنَ النُّورِ مَا بَيْنَ الْجُمُعَتَيْنِ", en:"Whoever recites Surah Al-Kahf on Friday, a light shines for him between the two Fridays.", ref:"Al-Ḥākim · 3392" },
                { ar:"إِذَا نُودِيَ لِلصَّلَاةِ مِن يَوْمِ الْجُمُعَةِ فَاسْعَوْا إِلَىٰ ذِكْرِ اللَّهِ", en:"When the call is made for Friday prayer, hasten to the remembrance of Allah.", ref:"Quran · Al-Jumuʿah 62:9" },
                { ar:"مَنِ اغْتَسَلَ ثُمَّ أَتَى الْجُمُعَةَ غُفِرَ لَهُ مَا بَيْنَهُ وَبَيْنَ الْجُمُعَةِ الْأُخْرَى", en:"Whoever bathes and then attends Jumuʿah, his sins between this Friday and the next are forgiven.", ref:"Muslim · 857" },
                { ar:"إِنَّ مِنْ أَفْضَلِ أَيَّامِكُمْ يَوْمَ الْجُمُعَةِ فَأَكْثِرُوا عَلَيَّ مِنَ الصَّلَاةِ فِيهِ", en:"Among the best of your days is Friday, so send abundant salawat upon me on it.", ref:"Abū Dāwūd · 1047" },
                { ar:"فِيهِ خُلِقَ آدَمُ وَفِيهِ أُدْخِلَ الْجَنَّةَ وَفِيهِ أُخْرِجَ مِنْهَا", en:"On Friday Adam was created, admitted to Paradise, and expelled from it.", ref:"Muslim · 854" },
                { ar:"لَا تَقُومُ السَّاعَةُ إِلَّا فِي يَوْمِ الْجُمُعَةِ", en:"The Hour will not be established except on a Friday.", ref:"Muslim · 854" },
                { ar:"يَا أَيُّهَا الَّذِينَ آمَنُوا صَلُّوا عَلَيْهِ وَسَلِّمُوا تَسْلِيمًا", en:"O you who believe! Send blessings upon him and greet him with respect.", ref:"Quran · Al-Aḥzāb 33:56" },
                { ar:"وَمَن يُطِعِ اللَّهَ وَرَسُولَهُ فَقَدْ فَازَ فَوْزًا عَظِيمًا", en:"Whoever obeys Allah and His Messenger has achieved a great victory.", ref:"Quran · Al-Aḥzāb 33:71" },
                { ar:"إِنَّ أَقْرَبَكُمْ مِنِّي يَوْمَ الْقِيَامَةِ أَكْثَرُكُمْ عَلَيَّ صَلَاةً", en:"The closest of you to me on the Day of Resurrection are those who sent the most salawat upon me.", ref:"Tirmidhī · 484" },
                { ar:"مَنْ صَلَّى عَلَيَّ وَاحِدَةً صَلَّى اللَّهُ عَلَيْهِ عَشْرًا", en:"Whoever sends one blessing upon me, Allah sends ten blessings upon him.", ref:"Muslim · 408" },
                { ar:"وَمِنْ خَيْرِ أَيَّامِكُمْ يَوْمُ الْجُمُعَةِ فِيهِ النَّفْخَةُ وَفِيهِ الصَّعْقَةُ", en:"Among the best of your days is Friday — on it the trumpet will be blown and the thunderclap will occur.", ref:"Abū Dāwūd · 1046" },
                { ar:"مَنِ اغْتَسَلَ يَوْمَ الْجُمُعَةِ غُسْلَ الْجَنَابَةِ ثُمَّ رَاحَ فَكَأَنَّمَا قَرَّبَ بَدَنَةً", en:"Whoever bathes on Friday as one bathes for janabah and then goes to prayer is like one who offered a camel in sacrifice.", ref:"Bukhārī · 881" },
              ];
              // Week-index (weeks since epoch) — ensures every Jumu'ah shows a different hadith/verse.
              const _weekIdx = Math.floor((Date.now() / 1000 + 345600) / 604800); // shift so Friday starts a new week
              // Eid overrides the Friday extras with Eid-specific hadith
              const EID_EXTRAS = {
                fitr: { ar:"تَقَبَّلَ اللَّهُ مِنَّا وَمِنْكُمْ", en:"May Allah accept from us and from you.", ref:"Eid greeting · Companions" },
                adha: { ar:"وَتَعَاوَنُوا عَلَى الْبِرِّ وَالتَّقْوَى", en:"Help one another in righteousness and piety.", ref:"Quran · Al-Māʾidah 5:2" },
              };
              const jumuahExtra = eidKind
                ? EID_EXTRAS[eidKind]
                : JUMUAH_EXTRAS[((_weekIdx % JUMUAH_EXTRAS.length) + JUMUAH_EXTRAS.length) % JUMUAH_EXTRAS.length];
              // Display greeting — Eid overrides, otherwise rotates weekly across JUMUAH_GREETINGS
              const jumuahGreetingAr = eidKind === "fitr"
                ? "عِيدُ الْفِطْرِ مُبَارَكٌ"
                : eidKind === "adha"
                  ? "عِيدُ الْأَضْحَى مُبَارَكٌ"
                  : JUMUAH_GREETINGS[((_weekIdx % JUMUAH_GREETINGS.length) + JUMUAH_GREETINGS.length) % JUMUAH_GREETINGS.length];
              const jumuahEyebrow = eidKind === "fitr"
                ? "Eid ul-Fitr Mubarak"
                : eidKind === "adha"
                  ? "Eid ul-Adha Mubarak"
                  : "Jumu'ah Mubarak";
              // Hijri date stamp — compact form shown beneath the name. Fall back to the raw
              // string with the trailing "AH" stripped if the regex missed a month name.
              const hijriStamp = _hijriParts
                ? `${_hijriParts.day} ${_hijriParts.monthEn} ${_hijriParts.year}`
                : (hijri && typeof hijri === "string" ? hijri.replace(/\s*AH\s*$/i, "").trim() : "");
              // Stale-message detector — if the saved message is from a previous week, show a "refresh" hint
              const messageIsStale = !!jumuahMessage && jumuahMessageWeek > 0 && jumuahMessageWeek !== _weekIdx;
              // Inspiration card theming — follows the user's share card style (paper / night), not the UI dark mode,
              // so the in-app preview matches the shared PNG for all three kinds (verse, hadith, jumuah).
              const _inspNight = shareCardStyle === 'night';
              const inspTheme = _inspNight
                ? { bg:"linear-gradient(160deg,#0d0e12 0%,#07080b 100%)", border:"rgba(197,155,58,0.28)", watermarkOpacity:0.09, gold:"#e8c468", gold2:"#c59b3a", ink:"#f0e6cf", inkDim:"rgba(240,230,207,0.82)", inkMuted:"rgba(240,230,207,0.5)", divider:"rgba(197,155,58,0.35)", dashedDivider:"rgba(197,155,58,0.3)", ctaInk:"#0b0c10" }
                : { bg:"linear-gradient(165deg,#f4ead4 0%,#e8d9b0 100%)", border:"rgba(140,107,31,0.32)", watermarkOpacity:0.12, gold:"#8c6b1f", gold2:"#6a501a", ink:"#1a1208", inkDim:"rgba(26,18,8,0.78)", inkMuted:"rgba(26,18,8,0.5)", divider:"rgba(140,107,31,0.45)", dashedDivider:"rgba(140,107,31,0.3)", ctaInk:"#fff7e3" };

              // Share handlers — reuse real platform share APIs
              const ibShareText = `"${insp.en}"\n\n— ${insp.ref}${insp.narrator ? "\n(Narrated by " + insp.narrator + ")" : ""}\n\nvia Sirat`;
              const ibShareTo = (target) => {
                if (target === "wa") {
                  window.open("https://wa.me/?text=" + encodeURIComponent(ibShareText), "_blank");
                } else if (target === "copy" || target === "ig") {
                  (async () => {
                    try { await navigator.clipboard.writeText(ibShareText); setComingSoonToast(target === "ig" ? "Copied — paste into Instagram" : "Copied to clipboard"); setTimeout(() => setComingSoonToast(null), 2200); }
                    catch { setComingSoonToast("Copy not available"); setTimeout(() => setComingSoonToast(null), 2200); }
                  })();
                } else if (target === "save") {
                  if (navigator.share) { navigator.share({ text: ibShareText }).catch(() => {}); }
                  else { (async () => { try { await navigator.clipboard.writeText(ibShareText); setComingSoonToast("Copied to clipboard"); setTimeout(() => setComingSoonToast(null), 2200); } catch {} })(); }
                }
              };

              // --- Handlers ---
              const onFeatClick = () => {
                if (featuredTable.key === "azkar") navTransition(() => setToolView("azkar"));
                else if (featuredTable.key === "tasbeeh") navTransition(() => setToolView("tasbeeh"));
                else if (featuredTable.key === "surahs") navTransition(() => setToolView("quran"));
              };

              // --- Weekday + hijri label ---
              const weekday = new Date().toLocaleDateString(undefined,{weekday:"long"});
              const eyebrowTxt = `${weekday}${hijri ? " · " + hijri : ""}`;

              return (
                <div style={{padding:"0"}}>
                  {/* Editorial header — matches Radio tab: starts at top, Sirat logo inline with title, safe-area padded */}
                  <div style={{padding:"14px 20px 16px",paddingTop:stickyHeaderTopPad,display:"flex",alignItems:"flex-end",justifyContent:"space-between",gap:"12px",position:"sticky",top:0,zIndex:10,background:darkMode?`rgba(20,16,10,${0.65*headerScroll})`:`rgba(250,243,225,${0.7*headerScroll})`,backdropFilter:`blur(${34*headerScroll}px) saturate(200%)`,WebkitBackdropFilter:`blur(${34*headerScroll}px) saturate(200%)`,borderBottom:`1px solid ${darkMode?`rgba(255,179,0,${0.28*headerScroll})`:`rgba(184,112,8,${0.28*headerScroll})`}`,boxShadow:`0 8px 18px ${darkMode?`rgba(0,0,0,${0.22*headerScroll})`:`rgba(0,0,0,${0.05*headerScroll})`}`,transition:"background .15s ease, border-color .15s ease, box-shadow .15s ease"}}>
                    <div style={{minWidth:0}}>
                      <div ref={hijriRef} onClick={toggleHijriCalendar} role="button" tabIndex={0}
                        onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleHijriCalendar(); } }}
                        aria-label="Open Hijri calendar"
                        style={{fontFamily:IB_MONO,fontSize:"9.5px",letterSpacing:"2.5px",color:IB_MUTED,textTransform:"uppercase",marginBottom:"6px",cursor:"pointer",display:"inline-block",whiteSpace:"nowrap"}}>{eyebrowTxt}</div>
                      <div style={{display:"flex",alignItems:"center",gap:"10px"}}>
                        <div
                          ref={logoRef}
                          onClick={() => { openFanMenu(); if (showLogoHint) { setShowLogoHint(false); localStorage.setItem("logoHintShown","1"); } }}
                          aria-label="Sirat shortcuts"
                          style={{width:"36px",height:"36px",borderRadius:"11px",flexShrink:0,visibility: showFanMenu ? "hidden" : "visible",
                            background: darkMode ? "rgba(4,18,16,0.5)" : "rgba(224,247,243,0.5)",
                            backdropFilter:"blur(16px) saturate(180%)", WebkitBackdropFilter:"blur(16px) saturate(180%)",
                            border: darkMode ? "1px solid rgba(0,201,167,0.25)" : "1px solid rgba(0,158,135,0.2)",
                            display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",
                            boxShadow: darkMode ? "0 2px 12px rgba(0,201,167,0.15)" : "0 2px 12px rgba(0,158,135,0.1)",
                            animation: remoteConfig.iconGlow !== false ? "logoPulse 3s ease-in-out infinite" : "none"}}>
                          <svg viewBox="0 0 44 44" width="30" height="30" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85"/>
                            <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85" transform="rotate(60 22 22)"/>
                            <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85" transform="rotate(120 22 22)"/>
                          </svg>
                        </div>
                        <div style={{fontFamily:IB_SERIF,fontStyle:"italic",fontSize:"34px",color:"var(--text)",letterSpacing:"-0.02em",lineHeight:1}}>Ibadah</div>
                      </div>
                    </div>
                    {userName && (
                      <div style={{textAlign:"right",flexShrink:0}}>
                        <div style={{fontFamily:IB_MONO,fontSize:"9.5px",letterSpacing:"2px",color:IB_MUTED,textTransform:"uppercase",marginBottom:"2px"}}>Assalamu alaykum</div>
                        <div style={{fontFamily:IB_SERIF,fontStyle:"italic",fontSize:"17px",color:IB_GOLD,letterSpacing:"-0.3px",lineHeight:1.1}}>{userName}</div>
                      </div>
                    )}
                  </div>

                  {/* Dhul Hijjah banner — auto-shows on days 1–13. Tap → Takbeer
                      counter. The first 10 days are described in the hadith as
                      the days in which deeds are most beloved to Allah. */}
                  {_hijriParts && _hijriMonth.startsWith("dhu") && _hijriMonth.toLowerCase().includes("h") && _hijriDay >= 1 && _hijriDay <= 13 && (() => {
                    const daysToEid = Math.max(0, 10 - _hijriDay);
                    const eidLabel = _hijriDay < 10
                      ? `${daysToEid} day${daysToEid === 1 ? '' : 's'} to Eid al-Adha`
                      : _hijriDay === 10
                        ? 'Eid al-Adha · Day 1'
                        : `Tashriq · Day ${_hijriDay - 10} of 3`;
                    return (
                      <div
                        onClick={() => navTransition(() => setToolView("takbeer"))}
                        role="button"
                        style={{
                          margin:'0 16px 14px', padding:'14px 16px', cursor:'pointer',
                          background: darkMode
                            ? 'linear-gradient(135deg, rgba(255,179,0,0.14), rgba(255,179,0,0.04))'
                            : 'linear-gradient(135deg, rgba(184,112,8,0.12), rgba(184,112,8,0.03))',
                          border: `1px solid ${IB_GOLD}55`, borderRadius: 16,
                          boxShadow: `0 4px 16px ${IB_GOLD}1f`,
                          WebkitTapHighlightColor: 'transparent',
                        }}
                      >
                        <div style={{display:'flex',alignItems:'baseline',justifyContent:'space-between',gap:8,marginBottom:6}}>
                          <div style={{fontFamily:IB_MONO,fontSize:9.5,letterSpacing:'2.4px',fontWeight:800,color:IB_GOLD,textTransform:'uppercase'}}>
                            Day {_hijriDay} of Dhul Hijjah
                          </div>
                          <div style={{fontFamily:IB_MONO,fontSize:9,color:IB_MUTED,letterSpacing:'1.2px',textTransform:'uppercase'}}>
                            {eidLabel}
                          </div>
                        </div>
                        <div style={{fontFamily:IB_SERIF,fontStyle:'italic',fontSize:15.5,color:'var(--text)',lineHeight:1.5,marginBottom:8}}>
                          "No deeds are more beloved to Allah than those done in these ten days."
                        </div>
                        <div style={{fontFamily:IB_MONO,fontSize:9.5,color:IB_MUTED,letterSpacing:'0.6px',marginBottom:10}}>
                          ﷺ · Bukhari 969
                        </div>
                        <div style={{display:'flex',alignItems:'center',gap:8}}>
                          <span style={{
                            padding:'6px 13px', borderRadius:9,
                            background:IB_GOLD, color: darkMode ? '#1a1208' : '#fff7e3',
                            fontFamily:IB_MONO, fontSize:10, fontWeight:800, letterSpacing:'1.2px', textTransform:'uppercase',
                          }}>
                            ✦ Begin Takbeer
                          </span>
                          {_hijriDay >= 9 && _hijriDay <= 13 && (
                            <span style={{
                              padding:'5px 10px', borderRadius:9,
                              border:`1px solid ${IB_ACCENT}55`,
                              background:`${IB_ACCENT}14`, color: IB_ACCENT,
                              fontFamily:IB_MONO, fontSize:9, fontWeight:800, letterSpacing:'1.2px',
                            }}>
                              MUQAYYAD ACTIVE
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Streak strip */}
                  <div style={{margin:"0 16px 16px",padding:"14px 16px",borderRadius:"16px",display:"flex",alignItems:"center",gap:"14px",
                    background: darkMode ? "linear-gradient(135deg,rgba(255,179,0,0.08),rgba(255,179,0,0.02))" : "linear-gradient(135deg,rgba(184,112,8,0.08),rgba(184,112,8,0.02))",
                    border: `1px solid ${darkMode ? "rgba(255,179,0,0.18)" : "rgba(184,112,8,0.18)"}`}}>
                    <div
                      onTouchStart={(e) => {
                        if (streakN <= 0) return;
                        window._streakResetTimer = setTimeout(() => {
                          if (!window.confirm(`Reset your ${streakN}-day Ibadah streak?\n\nThis wipes your entire salah history and puts the streak back to zero. Cannot be undone.`)) return;
                          if (!window.confirm("Are you sure? Tap OK to confirm reset.")) return;
                          setSalahLog({});
                          localStorage.removeItem("salahLog");
                          setSalahStreak({ count: 0, lastDate: null });
                          localStorage.setItem("salahStreak", JSON.stringify({ count: 0, lastDate: null }));
                          if (typeof Preferences !== 'undefined') Preferences.set({ key: 'salahStreak', value: '0' }).catch(() => {});
                        }, 700);
                      }}
                      onTouchEnd={() => { if (window._streakResetTimer) { clearTimeout(window._streakResetTimer); window._streakResetTimer = null; } }}
                      onTouchCancel={() => { if (window._streakResetTimer) { clearTimeout(window._streakResetTimer); window._streakResetTimer = null; } }}
                      title={streakN > 0 ? "Hold to reset streak" : undefined}
                      style={{width:"44px",height:"44px",borderRadius:"12px",flexShrink:0,
                        background:`linear-gradient(135deg,${IB_GOLD},${IB_GOLD2})`,
                        display:"grid",placeItems:"center",color:darkMode?"#1a1208":"#fff7e3",
                        fontFamily:IB_SERIF,fontStyle:"italic",fontSize:"22px",fontWeight:700,
                        boxShadow:`0 6px 14px ${IB_GOLD}33`,
                        WebkitUserSelect:"none",userSelect:"none",WebkitTouchCallout:"none"}}>{streakN}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:"13.5px",fontWeight:700,color:"var(--text)",letterSpacing:"-0.2px"}}>
                        {streakN > 0 ? `${streakN}-day Ibadah streak` : "Start your Ibadah streak"}
                      </div>
                      <div style={{display:"flex",gap:"5px",marginTop:"6px"}}>
                        {weeklyDots.map((d, i) => {
                          const alpha = d.pct >= 1 ? "" : d.pct > 0 ? Math.floor(d.pct * 0xaa).toString(16).padStart(2,"0") : "22";
                          return (
                            <div key={i} style={{flex:1,textAlign:"center"}}>
                              <div style={{height:"4px",borderRadius:"2px",
                                background: d.today ? "transparent" : `${IB_GOLD}${alpha}`,
                                border: d.today ? `1px dashed ${IB_GOLD}80` : "none",
                                opacity: d.pct > 0 || d.today ? 1 : 0.25}}/>
                              <div style={{fontFamily:IB_MONO,fontSize:"8.5px",color: d.today ? IB_GOLD : IB_MUTED,marginTop:"4px",fontWeight:700,letterSpacing:"0.8px"}}>{d.letter}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontFamily:IB_MONO,fontSize:"9.5px",letterSpacing:"1.5px",color:IB_MUTED,textTransform:"uppercase"}}>Today</div>
                      <div style={{fontSize:"15px",fontWeight:800,color:"var(--text)",letterSpacing:"-0.2px",marginTop:"2px"}}>{todayPct}%</div>
                    </div>
                  </div>

                  {/* Featured today hero */}
                  <div onClick={onFeatClick}
                    role="button" tabIndex={0}
                    onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onFeatClick(); } }}
                    style={{margin:"0 16px 18px",padding:"20px 20px 18px",borderRadius:"22px",position:"relative",overflow:"hidden",cursor:"pointer",
                      background: IB_PAPER,
                      border: `1px solid ${darkMode ? "rgba(255,179,0,0.18)" : "rgba(184,112,8,0.22)"}`,
                      boxShadow: darkMode ? `0 30px 60px -30px ${featuredTable.accent}66, inset 0 1px 0 rgba(255,255,255,0.04)` : `0 24px 48px -24px ${featuredTable.accent}44`}}>
                    {/* Decorative rings */}
                    <svg width="220" height="220" viewBox="0 0 220 220" aria-hidden style={{position:"absolute",right:"-40px",top:"-30px",opacity:darkMode?0.18:0.15,pointerEvents:"none"}}>
                      <g fill="none" stroke={featuredTable.accent} strokeWidth="0.8">
                        {[30,55,80,105].map(r=><circle key={r} cx="110" cy="110" r={r}/>)}
                        <polygon points="110,30 155,75 155,145 110,190 65,145 65,75" strokeWidth="1"/>
                        <polygon points="110,50 145,82.5 145,137.5 110,170 75,137.5 75,82.5"/>
                      </g>
                    </svg>
                    {/* Big glyph */}
                    <div aria-hidden style={{position:"absolute",right:"-10px",top:"-30px",fontSize:"200px",fontFamily:IB_ARABIC,color:featuredTable.accent,opacity:darkMode?0.07:0.09,lineHeight:0.8,pointerEvents:"none"}}>{featuredTable.glyph}</div>

                    <div style={{position:"relative"}}>
                      <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"14px"}}>
                        <div style={{padding:"4px 10px",borderRadius:"99px",
                          background: darkMode ? "rgba(255,179,0,0.15)" : "rgba(184,112,8,0.12)",
                          border: `1px solid ${darkMode ? "rgba(255,179,0,0.3)" : "rgba(184,112,8,0.3)"}`,
                          fontFamily:IB_MONO,fontSize:"9.5px",fontWeight:800,letterSpacing:"2px",color:featuredTable.accent}}>✦ FEATURED TODAY</div>
                        <div style={{fontSize:"10.5px",color:IB_MUTED,fontFamily:IB_MONO,letterSpacing:"1px"}}>rotates daily</div>
                      </div>

                      <div style={{fontFamily:IB_ARABIC,fontSize:"26px",color:"var(--text)",fontWeight:700,direction:"rtl",textAlign:"right",marginBottom:"6px",lineHeight:1.2}}>{featuredTable.arabic}</div>

                      <div style={{fontFamily:IB_SERIF,fontStyle:"italic",fontSize:"28px",color:"var(--text)",letterSpacing:"-0.02em",lineHeight:1.1,marginBottom:"6px"}}>{featuredTable.title}</div>
                      <div style={{fontSize:"12.5px",color:IB_DIM,maxWidth:"300px",lineHeight:1.5,marginBottom:"14px"}}>{featuredTable.subtitle}</div>

                      {featuredTable.hasRibbon && (
                        <div style={{display:"flex",gap:"3px",marginBottom:"14px",padding:"6px 0"}}>
                          {Array.from({length:26}).map((_,i)=>{
                            const ribbonDone = i < Math.round((featProgress.done/featProgress.total) * 26);
                            const ribbonNow = i === Math.round((featProgress.done/featProgress.total) * 26);
                            return (
                              <div key={i} style={{width:"10px",height:"22px",borderRadius:"2px",flexShrink:0,
                                background: ribbonNow ? featuredTable.accent : ribbonDone ? (darkMode?"rgba(255,179,0,0.55)":"rgba(184,112,8,0.55)") : (darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.05)"),
                                transform: ribbonNow ? "scaleY(1.2)" : undefined,
                                boxShadow: ribbonNow ? `0 0 12px ${featuredTable.accent}` : "none"}}/>
                            );
                          })}
                        </div>
                      )}

                      <div style={{display:"flex",alignItems:"center",gap:"12px"}}>
                        <button onClick={e => { e.stopPropagation(); onFeatClick(); }}
                          style={{flex:1,padding:"13px 18px",borderRadius:"14px",
                            background:`linear-gradient(135deg,${featuredTable.accent},${featuredTable.accent2})`,
                            color:darkMode?"#0a0806":"#fff7e3",border:"none",cursor:"pointer",
                            fontFamily:"inherit",fontSize:"13.5px",fontWeight:800,letterSpacing:"0.2px",
                            display:"flex",alignItems:"center",justifyContent:"center",gap:"8px",
                            boxShadow:`0 10px 22px -8px ${featuredTable.accent}99`}}>
                          {featuredTable.cta} <span style={{fontSize:"14px"}}>→</span>
                        </button>
                        <div style={{textAlign:"right",flexShrink:0}}>
                          <div style={{fontFamily:IB_MONO,fontSize:"9px",letterSpacing:"1.5px",color:IB_MUTED,textTransform:"uppercase"}}>Progress</div>
                          <div style={{fontFamily:IB_SERIF,fontStyle:"italic",fontSize:"22px",color:featuredTable.accent,letterSpacing:"-0.02em",lineHeight:1,marginTop:"2px"}}>
                            {featProgress.done}<span style={{color:IB_MUTED,fontSize:"14px"}}>/{featProgress.total}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Inspiration — verse, hadith, or Jumu'ah (locked during Islamic Friday). Footer chevrons cycle, Share opens sheet. */}
                  <div
                    style={{margin:"0 16px 18px",padding:"18px 20px",position:"relative",borderRadius:"18px",overflow:"hidden",
                      background: inspTheme.bg,
                      border: `1px solid ${inspTheme.border}`,
                      transition: "transform 0.12s ease"}}>
                    <div aria-hidden style={{position:"absolute",right:"-10px",top:"-22px",fontFamily:IB_ARABIC,fontSize:"150px",lineHeight:0.8,color:inspTheme.gold,opacity:inspTheme.watermarkOpacity,pointerEvents:"none"}}>۝</div>

                    <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"10px",position:"relative"}}>
                      {/* Prev chevron pinned left — stays at a fixed y regardless of card height */}
                      {!isIslamicFriday && (
                        <button
                          onClick={e => { e.stopPropagation(); setIbadahInspIdx(i => (i - 1 + IB_INSPIRATIONS.length) % IB_INSPIRATIONS.length); }}
                          aria-label="Previous inspiration"
                          style={{flexShrink:0,width:"26px",height:"26px",borderRadius:"999px",background:`${inspTheme.gold}14`,border:`1px solid ${inspTheme.gold}44`,color:inspTheme.gold,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"inherit",fontSize:"14px",lineHeight:1,padding:0}}
                        >‹</button>
                      )}
                      <div style={{fontFamily:IB_MONO,fontSize:"10px",fontWeight:600,letterSpacing:"2.5px",color:inspTheme.gold,textTransform:"uppercase"}}>
                        {inspIsJumuah ? jumuahEyebrow : inspIsVerse ? "Verse of the day" : "Hadith of the day"}
                      </div>
                      <div style={{height:"1px",flex:1,background: inspTheme.divider}}/>
                      <div style={{fontFamily:IB_MONO,fontSize:"9.5px",color:inspTheme.inkMuted,letterSpacing:"1px"}}>{inspIsJumuah ? (jumuahsSentCount > 0 ? `#${jumuahsSentCount + 1}` : (hijriStamp || insp.ref)) : insp.ref}</div>
                      {/* Next chevron pinned right */}
                      {!isIslamicFriday && (
                        <button
                          onClick={e => { e.stopPropagation(); setIbadahInspIdx(i => (i + 1) % IB_INSPIRATIONS.length); }}
                          aria-label="Next inspiration"
                          style={{flexShrink:0,width:"26px",height:"26px",borderRadius:"999px",background:`${inspTheme.gold}14`,border:`1px solid ${inspTheme.gold}44`,color:inspTheme.gold,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"inherit",fontSize:"14px",lineHeight:1,padding:0}}
                        >›</button>
                      )}
                    </div>

                    {inspIsJumuah ? (
                      <div key="jumuah" style={{position:"relative",animation:"fadeIn 0.3s ease-out",textAlign:"center",padding:"6px 0 2px",direction:"ltr"}}>
                        {/* Arabic greeting (varies weekly; Eid overrides) */}
                        <div style={{fontFamily:IB_ARABIC,fontSize:eidKind?"34px":"30px",color:inspTheme.ink,fontWeight:700,direction:"rtl",lineHeight:1.4,marginBottom:"12px"}}>{jumuahGreetingAr}</div>

                        {/* Thin gold divider above "For/From" */}
                        <div style={{width:"48px",height:"1px",background:`linear-gradient(90deg,transparent,${inspTheme.gold},transparent)`,margin:"0 auto 10px"}}/>

                        {/* Optional "For" recipient field — tap to personalise. Multiple names separated by commas. */}
                        <div style={{marginBottom:"8px"}}>
                          <div style={{fontFamily:IB_MONO,fontSize:"9px",fontWeight:700,letterSpacing:"4px",color:inspTheme.inkMuted,textTransform:"uppercase",marginBottom:"2px"}}>For</div>
                          {/* Live preview of the combined display (e.g. "Mum, Dad & Yusuf") — what actually renders on the share card */}
                          {jumuahRecipients.length > 1 && (
                            <div style={{
                              fontFamily: jumuahRecipientIsArabic ? "'Scheherazade New','Amiri',serif" : "'Dancing Script', cursive",
                              fontSize: jumuahRecipients.length > 3 ? "22px" : "26px",
                              fontWeight:700,
                              color:inspTheme.gold,
                              lineHeight:1.2,
                              direction: jumuahRecipientIsArabic ? "rtl" : "ltr",
                              textAlign:"center",
                              marginBottom: jumuahRecipientArJoined ? "2px" : "4px"
                            }}>{jumuahRecipientDisplay}</div>
                          )}
                          {jumuahRecipients.length === 1 && jumuahRecipientsAr[0] && (
                            <div style={{
                              fontFamily:"'Scheherazade New','Amiri',serif",
                              fontSize:"22px",
                              fontWeight:700,
                              color:inspTheme.gold,
                              opacity:0.85,
                              lineHeight:1.2,
                              direction:"rtl",
                              textAlign:"center",
                              marginTop:"2px"
                            }}>{jumuahRecipientsAr[0]}</div>
                          )}
                          {jumuahRecipientArJoined && (
                            <div style={{
                              fontFamily:"'Scheherazade New','Amiri',serif",
                              fontSize: jumuahRecipients.length > 3 ? "18px" : "20px",
                              fontWeight:700,
                              color:inspTheme.gold,
                              opacity:0.85,
                              lineHeight:1.2,
                              direction:"rtl",
                              textAlign:"center",
                              marginTop:"2px",
                              marginBottom:"4px"
                            }}>{jumuahRecipientArJoined}</div>
                          )}
                          <input
                            type="text"
                            value={jumuahRecipient}
                            onChange={e => {
                              const txt = e.target.value.slice(0, 80);
                              setJumuahRecipient(txt);
                              try { localStorage.setItem("jumuahRecipient", txt); } catch {}
                            }}
                            onClick={e => e.stopPropagation()}
                            onKeyDown={e => { e.stopPropagation(); if (e.key === "Enter") e.preventDefault(); }}
                            dir={hasArabicChars(jumuahRecipient) ? "rtl" : "ltr"}
                            maxLength={80}
                            placeholder="Name(s) — separate with commas"
                            spellCheck={false}
                            autoCorrect="off"
                            style={{
                              fontFamily: hasArabicChars(jumuahRecipient) ? "'Scheherazade New','Amiri',serif" : "'Dancing Script', cursive",
                              fontSize: jumuahRecipients.length > 1 ? "16px" : "30px",
                              fontWeight:700,
                              color:inspTheme.gold,
                              lineHeight:1.1,
                              letterSpacing:"0.3px",
                              textAlign:"center",
                              outline:"none",
                              border:"none",
                              background:"transparent",
                              padding:"2px 8px",
                              width:"100%",
                              boxSizing:"border-box",
                              direction: hasArabicChars(jumuahRecipient) ? "rtl" : "ltr",
                              opacity: jumuahRecipient ? 1 : 0.55,
                            }}
                          />
                        </div>

                        {/* "FROM" label */}
                        <div style={{fontFamily:IB_MONO,fontSize:"9px",fontWeight:700,letterSpacing:"4px",color:inspTheme.inkMuted,textTransform:"uppercase",marginBottom:"2px"}}>From</div>

                        {/* Hero: user's name (auto-switches to Arabic calligraphy when the name contains Arabic characters) */}
                        <div className="jumuah-name-shimmer" style={{
                          fontFamily: jumuahNameIsArabic ? "'Scheherazade New','Amiri',serif" : "'Dancing Script', cursive",
                          fontSize: jumuahNameIsArabic ? "56px" : "54px",
                          fontWeight:700,
                          color:inspTheme.gold,
                          lineHeight:1.2,
                          letterSpacing:"0.5px",
                          marginBottom: jumuahNameAr ? "2px" : "6px",
                          direction: jumuahNameIsArabic ? "rtl" : "ltr",
                          textShadow:_inspNight?"0 2px 18px rgba(232,196,104,0.3)":"0 1px 2px rgba(140,107,31,0.22)"
                        }}>{jumuahDisplayName}</div>
                        {jumuahNameAr && (
                          <div style={{
                            fontFamily:"'Scheherazade New','Amiri',serif",
                            fontSize:"28px",
                            fontWeight:700,
                            color:inspTheme.gold,
                            opacity:0.85,
                            direction:"rtl",
                            lineHeight:1.2,
                            marginBottom:"6px"
                          }}>{jumuahNameAr}</div>
                        )}

                        {/* Hijri date stamp — signed-letter feel */}
                        {hijriStamp && (
                          <div style={{fontFamily:IB_MONO,fontSize:"9px",fontWeight:600,letterSpacing:"2.5px",color:inspTheme.inkMuted,textTransform:"uppercase",marginBottom:"10px"}}>{hijriStamp}</div>
                        )}

                        {/* Thin gold divider below the name */}
                        <div style={{width:"48px",height:"1px",background:`linear-gradient(90deg,transparent,${inspTheme.gold},transparent)`,margin:"0 auto 14px"}}/>

                        {/* Editable personal message — tap to edit, saved locally */}
                        <textarea
                          value={jumuahMessage}
                          onChange={e => {
                            const txt = e.target.value.slice(0, 120);
                            setJumuahMessage(txt);
                            try { localStorage.setItem("jumuahMessage", txt); localStorage.setItem("jumuahMessageWeek", String(_weekIdx)); } catch {}
                            setJumuahMessageWeek(_weekIdx);
                          }}
                          onClick={e => e.stopPropagation()}
                          onKeyDown={e => {
                            e.stopPropagation();
                            if (e.key === "Enter") e.preventDefault();
                          }}
                          dir="ltr"
                          rows={2}
                          maxLength={120}
                          placeholder={jumuahPlaceholder}
                          spellCheck={false}
                          autoCorrect="off"
                          style={{
                            width:"100%",
                            boxSizing:"border-box",
                            fontFamily:IB_SERIF,
                            fontStyle:"italic",
                            fontSize:"16px",
                            color:inspTheme.inkDim,
                            lineHeight:1.5,
                            letterSpacing:"-0.01em",
                            outline:"none",
                            border:"none",
                            background:"transparent",
                            resize:"none",
                            textAlign:"center",
                            padding:"4px 10px",
                            cursor:"text",
                            direction:"ltr",
                            display:"block",
                            fontWeight:400,
                            opacity: messageIsStale ? 0.55 : 1
                          }}
                        />

                        {/* Stale-message hint — only when saved in a previous week */}
                        {messageIsStale && (
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              setJumuahMessage("");
                              setJumuahMessageWeek(_weekIdx);
                              try { localStorage.removeItem("jumuahMessage"); localStorage.setItem("jumuahMessageWeek", String(_weekIdx)); } catch {}
                            }}
                            style={{background:"transparent",border:"none",cursor:"pointer",fontFamily:IB_MONO,fontSize:"9.5px",fontWeight:700,letterSpacing:"1.5px",color:inspTheme.gold,textTransform:"uppercase",marginTop:"2px",padding:"4px 8px"}}
                          >↻ From last week · tap to replace</button>
                        )}

                        {/* Suggested message chips — tap to fill */}
                        {/* Suggested message chips — always available so the user can change their mind. Tap replaces the current message. */}
                        {!eidKind && (
                          <>
                            {jumuahMessage && (
                              <div style={{fontFamily:IB_MONO,fontSize:"8.5px",fontWeight:700,letterSpacing:"2px",color:inspTheme.inkMuted,textTransform:"uppercase",marginTop:"10px",marginBottom:"2px"}}>Or try another</div>
                            )}
                            <div style={{display:"flex",flexWrap:"wrap",gap:"6px",justifyContent:"center",marginTop:jumuahMessage?"4px":"10px"}}>
                              {JUMUAH_SUGGESTIONS.map(s => {
                                const active = jumuahMessage === s;
                                return (
                                  <button
                                    key={s}
                                    onClick={e => {
                                      e.stopPropagation();
                                      setJumuahMessage(s);
                                      setJumuahMessageWeek(_weekIdx);
                                      try { localStorage.setItem("jumuahMessage", s); localStorage.setItem("jumuahMessageWeek", String(_weekIdx)); } catch {}
                                    }}
                                    style={{
                                      padding:"6px 10px",
                                      borderRadius:"999px",
                                      background: active ? `${inspTheme.gold}33` : `${inspTheme.gold}1a`,
                                      border:`1px solid ${active ? inspTheme.gold : inspTheme.gold+"55"}`,
                                      color:inspTheme.gold,
                                      fontFamily:IB_SERIF,
                                      fontStyle:"italic",
                                      fontSize:"11.5px",
                                      cursor:"pointer",
                                      letterSpacing:"-0.01em",
                                      fontWeight: active ? 700 : 400
                                    }}
                                  >{s}</button>
                                );
                              })}
                            </div>
                          </>
                        )}

                        {/* Friday/Eid hadith/verse — rotates weekly */}
                        <div style={{marginTop:"16px",paddingTop:"14px",borderTop:`1px dashed ${inspTheme.dashedDivider}`}}>
                          <div style={{fontFamily:IB_ARABIC,fontSize:"20px",color:inspTheme.ink,fontWeight:700,direction:"rtl",lineHeight:1.6,marginBottom:"8px"}}>{jumuahExtra.ar}</div>
                          <div style={{fontFamily:IB_SERIF,fontStyle:"italic",fontSize:"13.5px",color:inspTheme.inkDim,lineHeight:1.45,letterSpacing:"-0.01em",marginBottom:"6px"}}>"{jumuahExtra.en}"</div>
                          <div style={{fontFamily:IB_MONO,fontSize:"9px",fontWeight:700,letterSpacing:"1.5px",color:inspTheme.gold,textTransform:"uppercase"}}>— {jumuahExtra.ref}</div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div key={insp.ar} style={{fontFamily:IB_ARABIC,fontSize:"26px",color:inspTheme.ink,fontWeight:700,direction:"rtl",textAlign:"right",marginBottom:"10px",lineHeight:1.5,position:"relative",animation:"fadeIn 0.3s ease-out"}}>{insp.ar}</div>
                        <div style={{fontFamily:IB_SERIF,fontStyle:"italic",fontSize:"16px",color:inspTheme.inkDim,letterSpacing:"-0.01em",lineHeight:1.45,position:"relative"}}>{insp.en}</div>
                        {insp.narrator && (
                          <div style={{fontFamily:IB_MONO,fontSize:"9.5px",color:inspTheme.inkMuted,letterSpacing:"1px",marginTop:"10px",textTransform:"uppercase",position:"relative"}}>Narrated by {insp.narrator}</div>
                        )}
                      </>
                    )}

                    {/* Footer: optional lock indicator + share */}
                    <div style={{display:"flex",alignItems:"center",gap:"10px",marginTop:"14px",position:"relative",paddingTop:"12px",borderTop:`1px dashed ${inspTheme.dashedDivider}`}}>
                      {isIslamicFriday && (
                        <div style={{display:"flex",alignItems:"center",gap:"6px",flex:1}}>
                          <span style={{fontSize:"13px",color:inspTheme.inkMuted}}>🕌</span>
                          <span style={{fontFamily:IB_MONO,fontSize:"10px",fontWeight:700,letterSpacing:"1.5px",color:inspTheme.inkMuted,textTransform:"uppercase"}}>Jumu'ah day · locked</span>
                        </div>
                      )}
                      {!isIslamicFriday && <div style={{flex:1}} />}
                      <button onClick={e => { e.stopPropagation(); setIbadahShareOpen(true); }}
                        style={{display:"flex",alignItems:"center",gap:"6px",padding:"7px 12px",borderRadius:"999px",
                          background:`linear-gradient(135deg,${inspTheme.gold},${inspTheme.gold2})`,border:"none",cursor:"pointer",
                          color:inspTheme.ctaInk,fontFamily:IB_MONO,fontSize:"10px",fontWeight:800,
                          letterSpacing:"1.5px",textTransform:"uppercase",boxShadow:`0 6px 14px -4px ${inspTheme.gold}99`}}>
                        <span style={{fontSize:"12px"}}>↗</span> Share
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Share sheet modal — rendered above the tools grid, inside the Ibadah tab */}
            {toolView === "grid" && ibadahShareOpen && (() => {
              const IB_MONO = '"JetBrains Mono",ui-monospace,SFMono-Regular,Menlo,monospace';
              const IB_SERIF = '"Instrument Serif",Georgia,serif';
              const IB_ARABIC = '"Amiri","Scheherazade New",serif';
              const IB_GOLD = darkMode ? "#ffb300" : "#b87008";
              const IB_GOLD2 = darkMode ? "#cc8e00" : "#8a5406";
              const IB_ACCENT = darkMode ? "#00c9a7" : "#009e87";
              const IB_BORDER = darkMode ? "rgba(255,255,255,0.08)" : "rgba(10,20,30,0.08)";
              const IB_DIM = darkMode ? "#a8b6c4" : "#4a5a6a";
              const IB_MUTED = darkMode ? "#5a7387" : "#8293a3";
              // TODO: guard the jumuah entry by `new Date().getDay() === 5` before launch (Friday-only).
              const IB_INSPIRATIONS_MODAL = IB_INSPIRATION_POOL;
              // Mirror the card's Islamic-Friday lock — while inside the Thu Maghrib → Fri Maghrib window
              // the card displays Jumu'ah regardless of ibadahInspIdx, so the share modal must too.
              const _mMaghribMin = (prayerTimes && prayerTimes.Maghrib) ? (() => {
                const mm = String(prayerTimes.Maghrib).match(/(\d{1,2}):(\d{2})/);
                return mm ? parseInt(mm[1]) * 60 + parseInt(mm[2]) : null;
              })() : null;
              const _mNowMin = (() => { const d = new Date(); return d.getHours() * 60 + d.getMinutes(); })();
              const _mTodayDow = new Date().getDay();
              const mIsIslamicFriday = _mMaghribMin !== null && (
                (_mTodayDow === 4 && _mNowMin >= _mMaghribMin) ||
                (_mTodayDow === 5 && _mNowMin < _mMaghribMin)
              );
              const _mJumuahPos = IB_INSPIRATIONS_MODAL.findIndex(x => x.kind === "jumuah");
              const _mActualInspIdx = mIsIslamicFriday && _mJumuahPos >= 0 ? _mJumuahPos : ibadahInspIdx;
              const mInsp = IB_INSPIRATIONS_MODAL[((_mActualInspIdx % IB_INSPIRATIONS_MODAL.length) + IB_INSPIRATIONS_MODAL.length) % IB_INSPIRATIONS_MODAL.length];
              const mIsJumuah = mInsp.kind === "jumuah";
              const mDisplayName = (userName && userName.trim()) ? userName.trim() : "Friend";
              const mJumuahMsg = (jumuahMessage && jumuahMessage.trim()) ? jumuahMessage.trim() : "May Allah accept your duas and bless your day.";
              // Friday hadith/verse (rotates WEEKLY) — must match the card shown on the Ibadah tab
              const M_JUMUAH_EXTRAS = [
                { ar:"خَيْرُ يَوْمٍ طَلَعَتْ عَلَيْهِ الشَّمْسُ يَوْمُ الْجُمُعَةِ", en:"The best day on which the sun has risen is Friday.", ref:"Muslim · 854" },
                { ar:"أَكْثِرُوا عَلَيَّ مِنَ الصَّلَاةِ يَوْمَ الْجُمُعَةِ", en:"Send abundant salawat upon me on Friday.", ref:"Abū Dāwūd · 1047" },
                { ar:"إِنَّ فِي الْجُمُعَةِ سَاعَةً لَا يُوَافِقُهَا عَبْدٌ مُسْلِمٌ يَسْأَلُ اللَّهَ شَيْئًا إِلَّا أَعْطَاهُ", en:"There is an hour on Friday in which no Muslim asks Allah for anything except that He gives it to him.", ref:"Bukhārī · 935" },
                { ar:"مَنْ قَرَأَ سُورَةَ الْكَهْفِ يَوْمَ الْجُمُعَةِ أَضَاءَ لَهُ مِنَ النُّورِ مَا بَيْنَ الْجُمُعَتَيْنِ", en:"Whoever recites Surah Al-Kahf on Friday, a light shines for him between the two Fridays.", ref:"Al-Ḥākim · 3392" },
                { ar:"إِذَا نُودِيَ لِلصَّلَاةِ مِن يَوْمِ الْجُمُعَةِ فَاسْعَوْا إِلَىٰ ذِكْرِ اللَّهِ", en:"When the call is made for Friday prayer, hasten to the remembrance of Allah.", ref:"Quran · Al-Jumuʿah 62:9" },
                { ar:"مَنِ اغْتَسَلَ ثُمَّ أَتَى الْجُمُعَةَ غُفِرَ لَهُ مَا بَيْنَهُ وَبَيْنَ الْجُمُعَةِ الْأُخْرَى", en:"Whoever bathes and then attends Jumuʿah, his sins between this Friday and the next are forgiven.", ref:"Muslim · 857" },
                { ar:"إِنَّ مِنْ أَفْضَلِ أَيَّامِكُمْ يَوْمَ الْجُمُعَةِ فَأَكْثِرُوا عَلَيَّ مِنَ الصَّلَاةِ فِيهِ", en:"Among the best of your days is Friday, so send abundant salawat upon me on it.", ref:"Abū Dāwūd · 1047" },
                { ar:"فِيهِ خُلِقَ آدَمُ وَفِيهِ أُدْخِلَ الْجَنَّةَ وَفِيهِ أُخْرِجَ مِنْهَا", en:"On Friday Adam was created, admitted to Paradise, and expelled from it.", ref:"Muslim · 854" },
                { ar:"لَا تَقُومُ السَّاعَةُ إِلَّا فِي يَوْمِ الْجُمُعَةِ", en:"The Hour will not be established except on a Friday.", ref:"Muslim · 854" },
                { ar:"يَا أَيُّهَا الَّذِينَ آمَنُوا صَلُّوا عَلَيْهِ وَسَلِّمُوا تَسْلِيمًا", en:"O you who believe! Send blessings upon him and greet him with respect.", ref:"Quran · Al-Aḥzāb 33:56" },
                { ar:"وَمَن يُطِعِ اللَّهَ وَرَسُولَهُ فَقَدْ فَازَ فَوْزًا عَظِيمًا", en:"Whoever obeys Allah and His Messenger has achieved a great victory.", ref:"Quran · Al-Aḥzāb 33:71" },
                { ar:"إِنَّ أَقْرَبَكُمْ مِنِّي يَوْمَ الْقِيَامَةِ أَكْثَرُكُمْ عَلَيَّ صَلَاةً", en:"The closest of you to me on the Day of Resurrection are those who sent the most salawat upon me.", ref:"Tirmidhī · 484" },
                { ar:"مَنْ صَلَّى عَلَيَّ وَاحِدَةً صَلَّى اللَّهُ عَلَيْهِ عَشْرًا", en:"Whoever sends one blessing upon me, Allah sends ten blessings upon him.", ref:"Muslim · 408" },
                { ar:"وَمِنْ خَيْرِ أَيَّامِكُمْ يَوْمُ الْجُمُعَةِ فِيهِ النَّفْخَةُ وَفِيهِ الصَّعْقَةُ", en:"Among the best of your days is Friday — on it the trumpet will be blown and the thunderclap will occur.", ref:"Abū Dāwūd · 1046" },
                { ar:"مَنِ اغْتَسَلَ يَوْمَ الْجُمُعَةِ غُسْلَ الْجَنَابَةِ ثُمَّ رَاحَ فَكَأَنَّمَا قَرَّبَ بَدَنَةً", en:"Whoever bathes on Friday as one bathes for janabah and then goes to prayer is like one who offered a camel in sacrifice.", ref:"Bukhārī · 881" },
              ];
              const _mWeekIdx = Math.floor((Date.now() / 1000 + 345600) / 604800);
              // Eid + greeting + hijri for the preview/share (parallel to the card)
              const _mHijriParts = (() => {
                if (!hijri || typeof hijri !== "string") return null;
                const m = hijri.match(/^(\d+)\s+(\S+)\s+(\d+)/);
                if (!m) return null;
                return { day: parseInt(m[1]), monthEn: m[2], year: m[3] };
              })();
              const _mHijriMonth = _mHijriParts ? _mHijriParts.monthEn.toLowerCase() : "";
              const _mHijriDay = _mHijriParts ? _mHijriParts.day : 0;
              const _mIsEidFitr = _mHijriDay === 1 && (_mHijriMonth.startsWith("shaw") || _mHijriMonth.startsWith("šaw"));
              const _mIsEidAdha = _mHijriDay === 10 && (_mHijriMonth.startsWith("dhu") || _mHijriMonth.startsWith("dhū") || _mHijriMonth.startsWith("ḏū"));
              const mEidKind = _mIsEidFitr ? "fitr" : _mIsEidAdha ? "adha" : null;
              const M_EID_EXTRAS = {
                fitr: { ar:"تَقَبَّلَ اللَّهُ مِنَّا وَمِنْكُمْ", en:"May Allah accept from us and from you.", ref:"Eid greeting · Companions" },
                adha: { ar:"وَتَعَاوَنُوا عَلَى الْبِرِّ وَالتَّقْوَى", en:"Help one another in righteousness and piety.", ref:"Quran · Al-Māʾidah 5:2" },
              };
              const mJumuahExtra = mEidKind ? M_EID_EXTRAS[mEidKind] : M_JUMUAH_EXTRAS[((_mWeekIdx % M_JUMUAH_EXTRAS.length) + M_JUMUAH_EXTRAS.length) % M_JUMUAH_EXTRAS.length];
              const M_GREETINGS = ["جُمُعَةٌ مُبَارَكَةٌ", "جُمُعَةٌ طَيِّبَةٌ", "بَارَكَ اللَّهُ لَكَ"];
              const mGreetingAr = mEidKind === "fitr" ? "عِيدُ الْفِطْرِ مُبَارَكٌ"
                : mEidKind === "adha" ? "عِيدُ الْأَضْحَى مُبَارَكٌ"
                : M_GREETINGS[((_mWeekIdx % M_GREETINGS.length) + M_GREETINGS.length) % M_GREETINGS.length];
              const mGreetingTitle = mEidKind === "fitr" ? "Eid ul-Fitr Mubarak"
                : mEidKind === "adha" ? "Eid ul-Adha Mubarak"
                : "Jumu'ah Mubarak";
              // Multi-recipient combined display
              const _mRecipientsArr = jumuahRecipient ? jumuahRecipient.split(",").map(s => s.trim()).filter(Boolean) : [];
              const mRecipient = _mRecipientsArr.length === 0 ? ""
                : _mRecipientsArr.length === 1 ? _mRecipientsArr[0]
                : _mRecipientsArr.length === 2 ? `${_mRecipientsArr[0]} & ${_mRecipientsArr[1]}`
                : `${_mRecipientsArr.slice(0, -1).join(", ")} & ${_mRecipientsArr[_mRecipientsArr.length - 1]}`;
              const _mHasArabic = (s) => typeof s === "string" && /[؀-ۿݐ-ݿﭐ-﷿ﹰ-﻿]/.test(s);
              const mRecipientIsArabic = _mHasArabic(mRecipient);
              const mDisplayNameIsArabic = _mHasArabic(mDisplayName);
              // Auto-translate the user's name to Arabic if known (Yusuf → يوسف, etc.)
              const mDisplayNameAr = mDisplayNameIsArabic ? "" : lookupNameAr(mDisplayName);
              const _mRecipientsAr = mRecipientIsArabic ? [] : _mRecipientsArr.map(n => lookupNameAr(n));
              const _mRecipientsArAllResolve = _mRecipientsAr.length > 0 && _mRecipientsAr.every(Boolean);
              // For single recipient, mRecipientAr is just the one lookup; for multiple, join with Arabic conjunctions.
              const mRecipientAr = _mRecipientsArAllResolve
                ? (_mRecipientsAr.length === 1 ? _mRecipientsAr[0]
                   : _mRecipientsAr.length === 2 ? `${_mRecipientsAr[0]} و ${_mRecipientsAr[1]}`
                   : `${_mRecipientsAr.slice(0, -1).join("، ")} و ${_mRecipientsAr[_mRecipientsAr.length - 1]}`)
                : "";
              const mHijriStamp = _mHijriParts
                ? `${_mHijriParts.day} ${_mHijriParts.monthEn} ${_mHijriParts.year}`
                : (hijri && typeof hijri === "string" ? hijri.replace(/\s*AH\s*$/i, "").trim() : "");
              const mText = mIsJumuah
                ? `${mGreetingTitle}\n\n${mRecipient ? `For ${mRecipient}\n` : ""}From ${mDisplayName}\n${mHijriStamp ? mHijriStamp + "\n" : ""}\n${mJumuahMsg}\n\n"${mJumuahExtra.en}"\n— ${mJumuahExtra.ref}\n\nvia Sirat · sirat.co.za`
                : `"${mInsp.en}"\n\n— ${mInsp.ref}${mInsp.narrator ? "\n(Narrated by " + mInsp.narrator + ")" : ""}\n\nvia Sirat`;
              const mShareTo = (target) => {
                (async () => {
                  try {
                    if (target === "copy") {
                      await navigator.clipboard.writeText(mText);
                      setComingSoonToast("Copied to clipboard");
                      setTimeout(() => setComingSoonToast(null), 2200);
                    } else {
                      const blob = await generateShareCardBlob(
                        mIsJumuah
                          ? { ...mInsp, message: mJumuahMsg, extra: mJumuahExtra, recipient: mRecipient, recipientIsArabic: mRecipientIsArabic, recipientAr: mRecipientAr, userNameIsArabic: mDisplayNameIsArabic, userNameAr: mDisplayNameAr, greetingAr: mGreetingAr, greetingTitle: mGreetingTitle, hijri: mHijriStamp, eidKind: mEidKind }
                          : mInsp,
                        shareCardStyle,
                        userName.trim()
                      );
                      const file = new File([blob], "sirat-share.png", { type: "image/png" });
                      // Image-only share for wa / ig / save — the card visually contains everything; no caption.
                      if (navigator.canShare?.({ files: [file] })) {
                        await navigator.share({ files: [file] });
                      } else {
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url; a.download = "sirat-share.png"; a.click();
                        setTimeout(() => URL.revokeObjectURL(url), 1000);
                        setComingSoonToast("Image saved — attach it on " + (target === "wa" ? "WhatsApp" : target === "ig" ? "Instagram" : "your app"));
                        setTimeout(() => setComingSoonToast(null), 2600);
                      }
                      // Increment Jumuah shares counter after a successful share attempt
                      if (mIsJumuah) {
                        const next = (parseInt(localStorage.getItem("jumuahsSentCount") || "0") || 0) + 1;
                        try { localStorage.setItem("jumuahsSentCount", String(next)); } catch {}
                        setJumuahsSentCount(next);
                      }
                    }
                  } catch (e) { if (e.name !== "AbortError") console.error(e); }
                })();
                setIbadahShareOpen(false);
              };
              return (
                <div onClick={() => setIbadahShareOpen(false)}
                  style={{position:"fixed",inset:0,zIndex:1400,
                    background:"rgba(4,6,10,0.72)",
                    backdropFilter:"blur(14px)",WebkitBackdropFilter:"blur(14px)",
                    display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"flex-end",
                    paddingBottom:"calc(32px + env(safe-area-inset-bottom, 0px))",
                    animation:"fadeInBg 0.2s ease-out"}}>
                  <div onClick={e => e.stopPropagation()}
                    style={{width:"calc(100% - 28px)",maxWidth:"440px",
                      background: darkMode ? "#0e1319" : "#ffffff",
                      borderRadius:"24px",padding:"20px 18px 22px",
                      border:`1px solid ${IB_BORDER}`,
                      boxShadow:"0 -20px 60px rgba(0,0,0,0.45)"}}>
                    <div style={{width:"36px",height:"4px",borderRadius:"2px",background:IB_BORDER,margin:"0 auto 18px"}}/>
                    <div style={{textAlign:"center",marginBottom:"18px"}}>
                      <div style={{fontFamily:IB_MONO,fontSize:"10px",fontWeight:600,letterSpacing:"2.5px",color:IB_GOLD,textTransform:"uppercase"}}>Share</div>
                      <div style={{fontFamily:IB_SERIF,fontStyle:"italic",fontSize:"22px",color:"var(--text)",letterSpacing:"-0.02em",marginTop:"4px"}}>{mIsJumuah ? "Jumu'ah Mubarak" : (mInsp.kind === "verse" ? "Verse" : "Hadith") + " of the day"}</div>
                    </div>
                    {/* Share card graphic — mirrors the actual PNG theme (paper vs night) */}
                    {(() => {
                      const night = shareCardStyle === 'night';
                      const pv = night
                        ? { bg:"linear-gradient(160deg,#1a1208 0%,#0a0704 100%)", ornamentStroke:"#ffb300", ornamentOpacity:0.10, watermark:"#ffb300", watermarkOp:0.08, logoBg:"linear-gradient(135deg,#ffb300,#cc8e00)", logoInk:"#1a1208", brand:"#f3e3c2", hairline:"rgba(255,179,0,0.22)", gold:"#ffb300", goldDim:"#e0cfa8", ink:"#f7ecd8", muted:"rgba(224,207,168,0.7)", mutedDim:"rgba(224,207,168,0.6)", recipient:"#f0d88a", recipientAr:"#e6c877", nameShadow:"0 2px 18px rgba(255,179,0,0.3)", nameAr:"#ffb300", extraAr:"#f0e0b4", extraEn:"#c9b88a", narrator:"#a08860", divider:"rgba(255,179,0,0.2)" }
                        : { bg:"linear-gradient(160deg,#f4ead4 0%,#e8d9b0 100%)", ornamentStroke:"#8c6b1f", ornamentOpacity:0.12, watermark:"#8c6b1f", watermarkOp:0.09, logoBg:"linear-gradient(135deg,#8c6b1f,#6a501a)", logoInk:"#f4ead4", brand:"#1a1208", hairline:"rgba(140,107,31,0.35)", gold:"#8c6b1f", goldDim:"#6a501a", ink:"#1a1208", muted:"rgba(26,18,8,0.55)", mutedDim:"rgba(26,18,8,0.45)", recipient:"#8c6b1f", recipientAr:"#a07d26", nameShadow:"0 1px 2px rgba(140,107,31,0.22)", nameAr:"#8c6b1f", extraAr:"#1a1208", extraEn:"rgba(26,18,8,0.65)", narrator:"rgba(26,18,8,0.5)", divider:"rgba(140,107,31,0.25)" };
                      return (
                        <div style={{display:"flex",justifyContent:"center",marginBottom:"20px"}}>
                          <div style={{width:"320px",maxWidth:"100%",height:"400px",borderRadius:"22px",overflow:"hidden",position:"relative",
                            background:pv.bg,
                            boxShadow:"0 24px 60px rgba(0,0,0,0.45)",
                            padding:"28px 26px",display:"flex",flexDirection:"column",color:pv.brand}}>
                            <svg width="260" height="260" viewBox="0 0 220 220" aria-hidden style={{position:"absolute",right:"-60px",top:"-40px",opacity:pv.ornamentOpacity}}>
                              <g fill="none" stroke={pv.ornamentStroke} strokeWidth="0.8">
                                {[30,55,80,105].map(r => <circle key={r} cx="110" cy="110" r={r}/>)}
                                <polygon points="110,30 155,75 155,145 110,190 65,145 65,75" strokeWidth="1"/>
                                <polygon points="110,50 145,82.5 145,137.5 110,170 75,137.5 75,82.5"/>
                              </g>
                            </svg>
                            <div aria-hidden style={{position:"absolute",left:"-8px",bottom:"-40px",fontFamily:IB_ARABIC,fontSize:"200px",lineHeight:0.8,color:pv.watermark,opacity:pv.watermarkOp,pointerEvents:"none"}}>۝</div>
                            <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"18px",position:"relative"}}>
                              <div style={{width:"22px",height:"22px",borderRadius:"6px",background:pv.logoBg,display:"grid",placeItems:"center",color:pv.logoInk,fontFamily:IB_SERIF,fontStyle:"italic",fontSize:"14px",fontWeight:700}}>ṣ</div>
                              <div style={{fontFamily:IB_SERIF,fontStyle:"italic",fontSize:"16px",color:pv.brand,letterSpacing:"-0.02em"}}>Sirat</div>
                              <div style={{height:"1px",flex:1,background:pv.hairline}}/>
                              <div style={{fontFamily:IB_MONO,fontSize:"9px",fontWeight:700,letterSpacing:"2px",color:pv.gold,textTransform:"uppercase"}}>{mIsJumuah ? "Jumuʿah" : mInsp.kind === "verse" ? "Verse" : "Hadīth"}</div>
                            </div>
                            {mIsJumuah ? (
                              <>
                                <div style={{flex:1,display:"flex",flexDirection:"column",justifyContent:"center",alignItems:"center",gap:"6px",position:"relative"}}>
                                  <div style={{fontFamily:IB_ARABIC,fontSize:mEidKind?"28px":"24px",color:pv.ink,fontWeight:700,direction:"rtl",textAlign:"center",lineHeight:1.4}}>{mGreetingAr}</div>
                                  <div style={{width:"40px",height:"1px",background:`linear-gradient(90deg,transparent,${pv.gold},transparent)`}}/>
                                  {mRecipient && (
                                    <>
                                      <div style={{fontFamily:IB_MONO,fontSize:"8px",fontWeight:700,letterSpacing:"3px",color:pv.muted,textTransform:"uppercase"}}>For</div>
                                      <div style={{
                                        fontFamily: mRecipientIsArabic ? "'Scheherazade New','Amiri',serif" : "'Dancing Script', cursive",
                                        fontSize: _mRecipientsArr.length > 3 ? "22px" : _mRecipientsArr.length > 1 ? "24px" : "28px",
                                        fontWeight:700,color:pv.recipient,lineHeight:1.2,textAlign:"center",marginTop:"-4px",
                                        direction: mRecipientIsArabic ? "rtl" : "ltr"
                                      }}>{mRecipient}</div>
                                      {mRecipientAr && (
                                        <div style={{fontFamily:"'Scheherazade New','Amiri',serif",fontSize:"18px",fontWeight:700,color:pv.recipientAr,opacity:0.85,direction:"rtl",textAlign:"center",lineHeight:1.2,marginTop:"-2px"}}>{mRecipientAr}</div>
                                      )}
                                    </>
                                  )}
                                  <div style={{fontFamily:IB_MONO,fontSize:"8px",fontWeight:700,letterSpacing:"3px",color:pv.muted,textTransform:"uppercase"}}>From</div>
                                  <div style={{
                                    fontFamily: mDisplayNameIsArabic ? "'Scheherazade New','Amiri',serif" : "'Dancing Script', cursive",
                                    fontSize:"40px",
                                    fontWeight:700,color:pv.gold,lineHeight:1.2,textAlign:"center",textShadow:pv.nameShadow,marginTop:"-4px",
                                    direction: mDisplayNameIsArabic ? "rtl" : "ltr"
                                  }}>{mDisplayName}</div>
                                  {mDisplayNameAr && (
                                    <div style={{fontFamily:"'Scheherazade New','Amiri',serif",fontSize:"24px",fontWeight:700,color:pv.nameAr,opacity:0.9,direction:"rtl",textAlign:"center",lineHeight:1.2,marginTop:"-4px"}}>{mDisplayNameAr}</div>
                                  )}
                                  {mHijriStamp && (
                                    <div style={{fontFamily:IB_MONO,fontSize:"8px",fontWeight:600,letterSpacing:"2px",color:pv.mutedDim,textTransform:"uppercase"}}>{mHijriStamp}</div>
                                  )}
                                  <div style={{width:"40px",height:"1px",background:`linear-gradient(90deg,transparent,${pv.gold},transparent)`}}/>
                                  <div style={{fontFamily:IB_SERIF,fontStyle:"italic",fontSize:"13px",color:pv.goldDim,textAlign:"center",lineHeight:1.4,letterSpacing:"-0.01em",padding:"0 4px"}}>{mJumuahMsg}</div>
                                  <div style={{width:"30%",height:"1px",background:pv.divider,marginTop:"2px"}}/>
                                  <div style={{fontFamily:IB_ARABIC,fontSize:"15px",color:pv.extraAr,fontWeight:700,direction:"rtl",textAlign:"center",lineHeight:1.5,padding:"0 4px"}}>{mJumuahExtra.ar}</div>
                                  <div style={{fontFamily:IB_SERIF,fontStyle:"italic",fontSize:"11px",color:pv.extraEn,textAlign:"center",lineHeight:1.4,padding:"0 4px"}}>"{mJumuahExtra.en}"</div>
                                </div>
                                <div style={{marginTop:"auto",position:"relative",textAlign:"center"}}>
                                  <div style={{fontFamily:IB_MONO,fontSize:"9px",fontWeight:700,letterSpacing:"2px",color:pv.gold,textTransform:"uppercase"}}>— {mJumuahExtra.ref}</div>
                                </div>
                              </>
                            ) : (
                              <>
                                <div style={{flex:1,display:"flex",flexDirection:"column",justifyContent:"center",gap:"18px",position:"relative"}}>
                                  <div style={{fontFamily:IB_ARABIC,fontSize:"30px",color:pv.ink,fontWeight:700,direction:"rtl",textAlign:"center",lineHeight:1.55}}>{mInsp.ar}</div>
                                  <div style={{fontFamily:IB_SERIF,fontStyle:"italic",fontSize:"18px",color:pv.goldDim,textAlign:"center",lineHeight:1.45,letterSpacing:"-0.01em"}}>"{mInsp.en}"</div>
                                </div>
                                <div style={{marginTop:"auto",position:"relative",textAlign:"center"}}>
                                  <div style={{fontFamily:IB_MONO,fontSize:"10px",fontWeight:700,letterSpacing:"2px",color:pv.gold,textTransform:"uppercase"}}>{mInsp.ref}</div>
                                  {mInsp.narrator && <div style={{fontFamily:IB_SERIF,fontStyle:"italic",fontSize:"12px",color:pv.narrator,marginTop:"6px"}}>Narrated by {mInsp.narrator}</div>}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                    {/* Action buttons */}
                    <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"8px",marginBottom:"14px"}}>
                      {[
                        { id:"wa", label:"WhatsApp", glyph:"✆", color:"#25d366" },
                        { id:"ig", label:"Instagram", glyph:"◎", color:"#e1306c" },
                        { id:"copy", label:"Copy", glyph:"⎘", color:IB_GOLD },
                        { id:"save", label:"Share", glyph:"↗", color:IB_ACCENT },
                      ].map(opt => (
                        <button key={opt.id} onClick={() => mShareTo(opt.id)}
                          style={{padding:"12px 6px",borderRadius:"14px",
                            background: darkMode ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)",
                            border:`1px solid ${IB_BORDER}`,
                            color:"var(--text)",cursor:"pointer",fontFamily:"inherit",
                            display:"flex",flexDirection:"column",alignItems:"center",gap:"6px"}}>
                          <span style={{width:"34px",height:"34px",borderRadius:"10px",display:"grid",placeItems:"center",
                            background:`linear-gradient(135deg,${opt.color}33,${opt.color}11)`,
                            border:`1px solid ${opt.color}44`,color:opt.color,fontSize:"16px"}}>{opt.glyph}</span>
                          <span style={{fontSize:"11px",fontWeight:700,letterSpacing:"-0.1px"}}>{opt.label}</span>
                        </button>
                      ))}
                    </div>
                    <button onClick={() => setIbadahShareOpen(false)}
                      style={{width:"100%",padding:"13px",borderRadius:"14px",background:"transparent",
                        border:`1px solid ${IB_BORDER}`,color:IB_DIM,cursor:"pointer",fontFamily:"inherit",
                        fontSize:"13px",fontWeight:700,letterSpacing:"-0.1px"}}>Close</button>
                  </div>
                </div>
              );
            })()}

            {/* Grid */}
            <div className="tools-grid">
              {(() => {
                const allFree = remoteConfig.allFree || isSupportMode;
                const feats = remoteConfig.features || {};
                const isMaintenanceBypass = (remoteConfig.maintenanceBypass || []).includes(userId);
                const canAccess = () => true; // all features permanently free
                const chevron = (color) => <div style={{color,opacity:0.6,flexShrink:0}}><svg width="9" height="16" viewBox="0 0 9 16" fill="none"><path d="M1 1l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></div>;
                // eslint-disable-next-line no-unused-vars
                const proTag = () => null; // removed — no locked features

                const ibadahTools = {
                  tasbeeh: { icon:"📿", name:"Tasbeeh", desc:"Dhikr counter", feat:"tasbeeh", prompt:"tasbeeh", view:"tasbeeh",
                    extra: (() => { const t = parseInt(localStorage.getItem("tasbeeh") || "0"); const s = parseInt(localStorage.getItem("tasbeehSet") || "0"); return t > 0 ? <div style={{fontSize:"11px",color:"var(--muted)"}}>{t} total · {s} sets</div> : null; })(),
                    dark:{bg:"",border:"",glow:"rgba(0,201,167,0.15)",accent:"var(--accent)",iconBg:"rgba(0,201,167,0.12)",iconBorder:"rgba(0,201,167,0.3)"},
                    light:{bg:"",border:"",glow:"rgba(0,158,135,0.1)",accent:"var(--accent)",iconBg:"rgba(0,158,135,0.1)",iconBorder:"rgba(0,158,135,0.25)"} },
                  zakat: { icon:"🤲", name:"Zakat Calculator", desc:"Calculate your annual obligation", feat:"zakat", prompt:"zakat", view:"zakat",
                    dark:{bg:"linear-gradient(145deg,#001a10,#000f09)",border:"rgba(0,201,100,0.2)",glow:"rgba(0,201,100,0.12)",accent:"#00c864",iconBg:"rgba(0,201,100,0.12)",iconBorder:"rgba(0,201,100,0.3)"},
                    light:{bg:"linear-gradient(145deg,#edfaf5,#e0f7ec)",border:"rgba(0,160,70,0.2)",glow:"rgba(0,160,70,0.08)",accent:"#00963c",iconBg:"rgba(0,160,70,0.1)",iconBorder:"rgba(0,160,70,0.25)"} },
                  surahs: { icon:"🎧", name:"Surahs", desc:`${Object.keys(SURAH_INFO).length} Surahs · ${RECITERS.length} reciters · gapless HQ audio`, feat:"surahs", prompt:"surah", view:"quran",
                    dark:{bg:"linear-gradient(145deg,#120a00,#0a0600)",border:"rgba(255,179,0,0.22)",glow:"rgba(255,179,0,0.1)",accent:"#ffb300",iconBg:"rgba(255,179,0,0.12)",iconBorder:"rgba(255,179,0,0.3)"},
                    light:{bg:"linear-gradient(145deg,#fffbee,#fff5d6)",border:"rgba(184,112,8,0.25)",glow:"rgba(184,112,8,0.08)",accent:"#b87008",iconBg:"rgba(184,112,8,0.1)",iconBorder:"rgba(184,112,8,0.25)"} },
                  quran: { icon:"📗", name:"Quran", desc:"All 114 Surahs · Arabic & Translation", feat:"quran", prompt:"quran", view:"fullquran",
                    dark:{bg:"linear-gradient(145deg,#001214,#000a0c)",border:"rgba(0,180,200,0.22)",glow:"rgba(0,180,200,0.1)",accent:"#00b4c8",iconBg:"rgba(0,180,200,0.12)",iconBorder:"rgba(0,180,200,0.3)"},
                    light:{bg:"linear-gradient(145deg,#edfaff,#d6f5ff)",border:"rgba(0,130,160,0.25)",glow:"rgba(0,130,160,0.08)",accent:"#008598",iconBg:"rgba(0,130,160,0.1)",iconBorder:"rgba(0,130,160,0.25)"} },
                  azkar: { icon:"🌙", name:"Azkar", desc:"Morning & Evening Adhkar", feat:"azkar", prompt:"azkar", view:"azkar",
                    dark:{bg:"linear-gradient(145deg,#0a1400,#060a00)",border:"rgba(130,200,60,0.2)",glow:"rgba(130,200,60,0.1)",accent:"#82c83c",iconBg:"rgba(130,200,60,0.12)",iconBorder:"rgba(130,200,60,0.3)"},
                    light:{bg:"linear-gradient(145deg,#eefae8,#e0f5da)",border:"rgba(80,140,10,0.25)",glow:"rgba(80,140,10,0.08)",accent:"#508c0a",iconBg:"rgba(80,140,10,0.1)",iconBorder:"rgba(80,140,10,0.25)"} },
                  names: { icon:"✨", name:"99 Names", desc:"Asma ul-Husna", feat:"names", prompt:"names", view:"names",
                    dark:{bg:"linear-gradient(145deg,#1a0a14,#0f0608)",border:"rgba(180,160,255,0.2)",glow:"rgba(180,160,255,0.1)",accent:"#b4a0ff",iconBg:"rgba(180,160,255,0.12)",iconBorder:"rgba(180,160,255,0.3)"},
                    light:{bg:"linear-gradient(145deg,#faf0ff,#f5e0ff)",border:"rgba(100,60,200,0.25)",glow:"rgba(100,60,200,0.08)",accent:"#643cc8",iconBg:"rgba(100,60,200,0.1)",iconBorder:"rgba(100,60,200,0.25)"} },
                  duas: { icon:"📖", name:"Dua Collection", desc:`${DUA_CATEGORIES.length} categories · Hisn al-Muslim`, feat:"duas", prompt:"duas", view:"duas",
                    dark:{bg:"linear-gradient(145deg,#0c0a14,#06040a)",border:"rgba(239,154,154,0.22)",glow:"rgba(239,154,154,0.1)",accent:"#ef9a9a",iconBg:"rgba(239,154,154,0.12)",iconBorder:"rgba(239,154,154,0.3)"},
                    light:{bg:"linear-gradient(145deg,#fff5f5,#ffe8e8)",border:"rgba(198,40,40,0.2)",glow:"rgba(198,40,40,0.08)",accent:"#c62828",iconBg:"rgba(198,40,40,0.1)",iconBorder:"rgba(198,40,40,0.2)"} },
                  istikharah: { icon:"🌠", name:"Istikharah", desc:"Step-by-step guide · Dua · Full explanation", feat:"istikharah", prompt:"istikharah", view:"istikharah",
                    dark:{bg:"linear-gradient(145deg,#060c1a,#030810)",border:"rgba(100,160,255,0.22)",glow:"rgba(100,160,255,0.1)",accent:"#64a0ff",iconBg:"rgba(100,160,255,0.12)",iconBorder:"rgba(100,160,255,0.3)"},
                    light:{bg:"linear-gradient(145deg,#eef3ff,#dde8ff)",border:"rgba(50,100,220,0.25)",glow:"rgba(50,100,220,0.08)",accent:"#3264dc",iconBg:"rgba(50,100,220,0.1)",iconBorder:"rgba(50,100,220,0.25)"} },
                  khatm: { icon:"🕊️", name:"ختم القرآن", desc:"Khatm · Esaale Sawaab", feat:"khatm", prompt:"khatm", view:"khatm",
                    extra: (() => { try { const d = JSON.parse(localStorage.getItem("khatmActiveCount") || "0"); return d > 0 ? <div style={{fontSize:"11px",color:"var(--muted)"}}>{d} active</div> : null; } catch { return null; } })(),
                    dark:{bg:"linear-gradient(145deg,#0a0014,#060008)",border:"rgba(160,120,255,0.2)",glow:"rgba(160,120,255,0.12)",accent:"#a078ff",iconBg:"rgba(160,120,255,0.12)",iconBorder:"rgba(160,120,255,0.3)"},
                    light:{bg:"linear-gradient(145deg,#f5f0ff,#ede0ff)",border:"rgba(100,60,200,0.25)",glow:"rgba(100,60,200,0.08)",accent:"#643cc8",iconBg:"rgba(100,60,200,0.1)",iconBorder:"rgba(100,60,200,0.25)"} },
                  rightnow: { icon:"⏱️", name:"Right Now", desc:"What should I do right now?", feat:"rightnow", prompt:"rightnow", view: "rightnow",
                    dark:{bg:"linear-gradient(145deg,#14100a,#0a0805)",border:"rgba(255,179,0,0.22)",glow:"rgba(255,179,0,0.12)",accent:"#ffb300",iconBg:"rgba(255,179,0,0.12)",iconBorder:"rgba(255,179,0,0.3)"},
                    light:{bg:"linear-gradient(145deg,#fff8ee,#fff0d6)",border:"rgba(184,112,8,0.25)",glow:"rgba(184,112,8,0.08)",accent:"#b87008",iconBg:"rgba(184,112,8,0.1)",iconBorder:"rgba(184,112,8,0.25)"} },
                  moodguide: { icon:"🫀", name:"Nafs & Niyyah", desc:"Quran, duas & advice for your heart", feat:"moodguide", prompt:"moodguide", view:"moodguide",
                    dark:{bg:"linear-gradient(145deg,#0a1014,#06080c)",border:"rgba(100,180,220,0.22)",glow:"rgba(100,180,220,0.1)",accent:"#64b4dc",iconBg:"rgba(100,180,220,0.12)",iconBorder:"rgba(100,180,220,0.3)"},
                    light:{bg:"linear-gradient(145deg,#eef6fa,#ddeef5)",border:"rgba(40,120,180,0.25)",glow:"rgba(40,120,180,0.08)",accent:"#2878b4",iconBg:"rgba(40,120,180,0.1)",iconBorder:"rgba(40,120,180,0.25)"} },
                  tajweed: { icon:"🕌", name:"Tajweed", desc:"Practice recitation with tajweed rules", feat:"tajweed", prompt:"tajweed", view:"tajweed",
                    dark:{bg:"linear-gradient(145deg,#0c0a14,#06040c)",border:"rgba(180,140,255,0.22)",glow:"rgba(180,140,255,0.1)",accent:"#b48cff",iconBg:"rgba(180,140,255,0.12)",iconBorder:"rgba(180,140,255,0.3)"},
                    light:{bg:"linear-gradient(145deg,#f5f0ff,#ede0ff)",border:"rgba(120,80,200,0.25)",glow:"rgba(120,80,200,0.08)",accent:"#7850c8",iconBg:"rgba(120,80,200,0.1)",iconBorder:"rgba(120,80,200,0.25)"} },
                  mirath: { icon:"⚖️", name:"Wasiyyah & Mirath", desc:"Islamic estate & inheritance planning", feat:"islamicwill", prompt:"islamicwill", view:"mirath",
                    dark:{bg:"linear-gradient(145deg,#0c0a06,#080604)",border:"rgba(200,180,100,0.22)",glow:"rgba(200,180,100,0.1)",accent:"#c8b464",iconBg:"rgba(200,180,100,0.12)",iconBorder:"rgba(200,180,100,0.3)"},
                    light:{bg:"linear-gradient(145deg,#faf8f0,#f5f0e0)",border:"rgba(160,130,40,0.25)",glow:"rgba(160,130,40,0.08)",accent:"#a08228",iconBg:"rgba(160,130,40,0.1)",iconBorder:"rgba(160,130,40,0.25)"} },
                  hajj: { icon:"🕋", name:"Hajj & Umrah", desc:"Step-by-step ritual companion", feat:"hajj", prompt:"hajj", view:"hajj",
                    dark:{bg:"linear-gradient(145deg,#14100a,#0a0805)",border:"rgba(255,179,0,0.22)",glow:"rgba(255,179,0,0.12)",accent:"#ffb300",iconBg:"rgba(255,179,0,0.12)",iconBorder:"rgba(255,179,0,0.3)"},
                    light:{bg:"linear-gradient(145deg,#fff8ee,#fff0d6)",border:"rgba(184,112,8,0.25)",glow:"rgba(184,112,8,0.08)",accent:"#b87008",iconBg:"rgba(184,112,8,0.1)",iconBorder:"rgba(184,112,8,0.25)"} },
                  janazah: { icon:"🕊️", name:"Janazah Guide", desc:"Ghusl, kafan, salah & burial · offline", feat:"janazah", prompt:"janazah", view:"janazah",
                    dark:{bg:"linear-gradient(145deg,#0a0c10,#050608)",border:"rgba(180,190,210,0.2)",glow:"rgba(180,190,210,0.08)",accent:"#b4bed2",iconBg:"rgba(180,190,210,0.1)",iconBorder:"rgba(180,190,210,0.28)"},
                    light:{bg:"linear-gradient(145deg,#f4f6fa,#e8edf5)",border:"rgba(100,115,140,0.22)",glow:"rgba(100,115,140,0.07)",accent:"#64738c",iconBg:"rgba(100,115,140,0.08)",iconBorder:"rgba(100,115,140,0.22)"} },
                  muraqabah: { icon:"🌌", name:"Muraqabah", desc:"Islamic contemplative meditation", feat:"muraqabah", prompt:"muraqabah", view:"muraqabah",
                    extra: (() => { try { const log = JSON.parse(localStorage.getItem("muraqabahLog") || "[]"); return log.length > 0 ? <div style={{fontSize:"11px",color:"var(--muted)"}}>{log.length} session{log.length !== 1 ? "s" : ""}</div> : null; } catch { return null; } })(),
                    dark:{bg:"linear-gradient(145deg,#0a0614,#06030a)",border:"rgba(160,100,255,0.2)",glow:"rgba(160,100,255,0.12)",accent:"#a064ff",iconBg:"rgba(160,100,255,0.12)",iconBorder:"rgba(160,100,255,0.3)"},
                    light:{bg:"linear-gradient(145deg,#f8f0ff,#f0e0ff)",border:"rgba(120,60,200,0.25)",glow:"rgba(120,60,200,0.08)",accent:"#783cc8",iconBg:"rgba(120,60,200,0.1)",iconBorder:"rgba(120,60,200,0.25)"} },
                  fasting: { icon:"🌙", name:"Fasting", desc:"Daily saum log · live Iftar countdown · Sunnah days · streaks", feat:"fasting", prompt:"fasting", view:"fasting",
                    extra: (() => { try { const log = JSON.parse(localStorage.getItem("fastingLog")||"{}"); const streak = JSON.parse(localStorage.getItem("fastingStreak")||'{"count":0}').count; const total = Object.values(log).filter(v=>v!==false&&v!=null).length; return total>0?<div style={{fontSize:"11px",color:"var(--muted)"}}>{total} fasts{streak>1?` · 🔥${streak}`:""}</div>:null; } catch { return null; } })(),
                    dark:{bg:"linear-gradient(145deg,#0a1400,#060a00)",border:"rgba(130,200,60,0.2)",glow:"rgba(130,200,60,0.12)",accent:"#82c83c",iconBg:"rgba(130,200,60,0.12)",iconBorder:"rgba(130,200,60,0.3)"},
                    light:{bg:"linear-gradient(145deg,#eefae8,#e0f5da)",border:"rgba(80,140,10,0.25)",glow:"rgba(80,140,10,0.08)",accent:"#508c0a",iconBg:"rgba(80,140,10,0.1)",iconBorder:"rgba(80,140,10,0.25)"} },
                  ghidha: { icon:"🥗", name:"Calories", desc:"Calorie & nourishment tracker", feat:"ghidha", prompt:"ghidha", view:"calorie",
                    extra: (() => { try { const log = JSON.parse(localStorage.getItem("calorieLog")||"{}"); const goals = JSON.parse(localStorage.getItem("calorieGoals")||"{\"calories\":2000}"); const d = new Date(); const k = d.getFullYear()+"-"+String(d.getMonth()+1).padStart(2,"0")+"-"+String(d.getDate()).padStart(2,"0"); const td = log[k]; if(!td)return null; const t = Object.values(td.meals||{}).flat().reduce((s,e)=>s+(e.calories||0),0); const p = Math.round((t/(goals.calories||2000))*100); return t>0?<div style={{fontSize:"11px",color:"var(--muted)"}}>{Math.round(t)} kcal · {p}%</div>:null; } catch { return null; } })(),
                    dark:{bg:"linear-gradient(145deg,#0a1400,#060a00)",border:"rgba(130,200,60,0.2)",glow:"rgba(130,200,60,0.12)",accent:"#82c83c",iconBg:"rgba(130,200,60,0.12)",iconBorder:"rgba(130,200,60,0.3)"},
                    light:{bg:"linear-gradient(145deg,#eefae8,#e0f5da)",border:"rgba(80,140,10,0.25)",glow:"rgba(80,140,10,0.08)",accent:"#508c0a",iconBg:"rgba(80,140,10,0.1)",iconBorder:"rgba(80,140,10,0.25)"} },
                  tawbah: { icon:"🕊️", name:"Tawbah", desc:"A private space between you and Allah", feat:"tawbah", prompt:"tawbah", view:"tawbah",
                    dark:{bg:"linear-gradient(145deg,#0d0a00,#080600)",border:"rgba(255,179,0,0.2)",glow:"rgba(255,179,0,0.1)",accent:"#ffb300",iconBg:"rgba(255,179,0,0.1)",iconBorder:"rgba(255,179,0,0.25)"},
                    light:{bg:"linear-gradient(145deg,#fffcf0,#fff8e0)",border:"rgba(184,112,8,0.2)",glow:"rgba(184,112,8,0.07)",accent:"#b87008",iconBg:"rgba(184,112,8,0.08)",iconBorder:"rgba(184,112,8,0.2)"} },
                  duajournal: { icon:"🤲", name:"Dua Journal", desc:"Your personal duas & answered prayers", feat:"duajournal", prompt:"duajournal", view:"duajournal",
                    extra: (() => { try { const d = JSON.parse(localStorage.getItem("duaJournal") || "{}"); const list = d.duas || []; const a = list.filter(x=>x.status==="active").length; const ans = list.filter(x=>x.status==="answered").length; return (a||ans) ? <div style={{fontSize:"11px",color:"var(--muted)"}}>{a} active{ans?` · ${ans} answered`:""}</div> : null; } catch { return null; } })(),
                    dark:{bg:"",border:"",glow:"rgba(255,179,0,0.12)",accent:"var(--gold)",iconBg:"rgba(255,179,0,0.1)",iconBorder:"rgba(255,179,0,0.3)"},
                    light:{bg:"",border:"",glow:"rgba(184,112,8,0.08)",accent:"var(--gold2)",iconBg:"rgba(184,112,8,0.1)",iconBorder:"rgba(184,112,8,0.25)"} },
                  egzon: { icon:"🎙️", name:"Recitations by Egzon Ibrahimi", desc:"25 surahs · For Khudaija Jamal", feat:"egzon", prompt:"egzon", view:"egzon",
                    dark:{bg:"linear-gradient(145deg,#141008,#0a0804)",border:"rgba(255,179,0,0.25)",glow:"rgba(255,179,0,0.14)",accent:"#ffb300",iconBg:"rgba(255,179,0,0.12)",iconBorder:"rgba(255,179,0,0.3)"},
                    light:{bg:"linear-gradient(145deg,#fff8ee,#fff0d6)",border:"rgba(184,112,8,0.28)",glow:"rgba(184,112,8,0.1)",accent:"#b87008",iconBg:"rgba(184,112,8,0.1)",iconBorder:"rgba(184,112,8,0.25)"} },
                  takbeer: { icon:"☪️", name:"Takbeer of Dhul Hijjah", desc:"The 10 most beloved days · count the takbeer", feat:"takbeer", prompt:"takbeer", view:"takbeer",
                    dark:{bg:"linear-gradient(145deg,#1a1408,#0d0a04)",border:"rgba(255,179,0,0.30)",glow:"rgba(255,179,0,0.18)",accent:"#ffb300",iconBg:"rgba(255,179,0,0.14)",iconBorder:"rgba(255,179,0,0.35)"},
                    light:{bg:"linear-gradient(145deg,#fff6dc,#ffeec0)",border:"rgba(184,112,8,0.32)",glow:"rgba(184,112,8,0.12)",accent:"#b87008",iconBg:"rgba(184,112,8,0.12)",iconBorder:"rgba(184,112,8,0.3)"} },
                  dossari: { icon:"🕌", name:"Sheikh Yasser Al Dossari", desc:"Full Quran · For Hasnain Khan", feat:"dossari", prompt:"dossari", view:"dossari",
                    dark:{bg:"linear-gradient(145deg,#080e14,#040810)",border:"rgba(79,195,247,0.25)",glow:"rgba(79,195,247,0.12)",accent:"#4fc3f7",iconBg:"rgba(79,195,247,0.12)",iconBorder:"rgba(79,195,247,0.3)"},
                    light:{bg:"linear-gradient(145deg,#eef6ff,#ddeeff)",border:"rgba(2,119,189,0.25)",glow:"rgba(2,119,189,0.08)",accent:"#0277bd",iconBg:"rgba(2,119,189,0.1)",iconBorder:"rgba(2,119,189,0.25)"} },
                  ahzab35: { icon:"✨", name:"Ayah of Light", desc:"Al-Ahzab · 35", feat:"ahzab35", prompt:"ahzab35", view:"ahzab35",
                    dark:{bg:"linear-gradient(145deg,#1a1400,#0f0a00)",border:"rgba(255,179,0,0.25)",glow:"rgba(255,179,0,0.14)",accent:"#ffb300",iconBg:"rgba(255,179,0,0.12)",iconBorder:"rgba(255,179,0,0.3)"},
                    light:{bg:"linear-gradient(145deg,#fffbee,#fff5d6)",border:"rgba(184,112,8,0.25)",glow:"rgba(184,112,8,0.08)",accent:"#b87008",iconBg:"rgba(184,112,8,0.1)",iconBorder:"rgba(184,112,8,0.25)"} },
                  verse: { icon:"🕋", name:"Daily Verse", desc:"A new Quran verse every day", feat:"verse", prompt:"verse", view:"verse",
                    dark:{bg:"linear-gradient(145deg,#0a0c14,#050610)",border:"rgba(0,201,167,0.22)",glow:"rgba(0,201,167,0.1)",accent:"#00c9a7",iconBg:"rgba(0,201,167,0.12)",iconBorder:"rgba(0,201,167,0.3)"},
                    light:{bg:"linear-gradient(145deg,#e0f7f3,#eef8f6)",border:"rgba(0,158,135,0.22)",glow:"rgba(0,158,135,0.08)",accent:"#009e87",iconBg:"rgba(0,158,135,0.1)",iconBorder:"rgba(0,158,135,0.25)"} },
                  cardgenerator: { icon:"🖼️", name:"Share Cards", desc:"Jumu'ah · Eid · Nikah · and more", feat:"cardgenerator", prompt:"cardgenerator", view:"cardgenerator",
                    dark:{bg:"linear-gradient(145deg,#140a10,#0a0608)",border:"rgba(255,140,200,0.22)",glow:"rgba(255,140,200,0.12)",accent:"#ff8cc8",iconBg:"rgba(255,140,200,0.12)",iconBorder:"rgba(255,140,200,0.3)"},
                    light:{bg:"linear-gradient(145deg,#fff0f8,#ffe0f0)",border:"rgba(200,60,120,0.22)",glow:"rgba(200,60,120,0.08)",accent:"#c83c78",iconBg:"rgba(200,60,120,0.1)",iconBorder:"rgba(200,60,120,0.25)"} },
                };
                const defaultOrder = ["hadith","ahzab35","verse","egzon","dossari","rightnow","khatm","tasbeeh","takbeer","muraqabah","tawbah","fasting","ghidha","zakat","mirath","surahs","quran","azkar","names","duas","duajournal","istikharah","moodguide","tajweed","hajj","janazah","cardgenerator"];
                const order = remoteConfig.ibadahOrder || defaultOrder;
                const ibadahHidden = remoteConfig.ibadahHidden || {};
                const ibadahLabels = remoteConfig.ibadahLabels || {};
                const ibadahStatus = remoteConfig.ibadahStatus || {};
                const isSuperUser = devMode && remoteConfig.superUserBypassComingSoon;
                const getStatus = (k) => {
                  const s = ibadahStatus[k] || (ibadahHidden[k] ? 'hidden' : 'live');
                  if (isMaintenanceBypass) return 'live'; // maintenance bypass sees all tools
                  if (isSuperUser && s === 'coming') return 'live'; // super user only bypasses coming-soon
                  // explicit per-user islamicwill grant always shows the tool regardless of hidden/coming status
                  if (ibadahTools[k]?.feat === 'islamicwill' && userFeatures.islamicwill === true) return 'live';
                  return s;
                };
                // Ensure all tools appear (even if admin config is stale)
                const allKeys = [...new Set([...defaultOrder, "hadith"])];
                const isValidKey = k => ibadahTools[k] || k === "hadith";
                const orderWithoutHadith = [...order.filter(k => isValidKey(k) && k !== "hadith"), ...allKeys.filter(k => !order.includes(k) && isValidKey(k) && k !== "hadith")];
                // Hadith always first — pinned as hero card regardless of remote order
                const finalOrder = [
                  ...(getStatus("hadith") === "hidden"  ? [] : ["hadith"]),
                  ...orderWithoutHadith,
                ].filter(k => getStatus(k) !== "hidden");

                const showComingSoon = (name) => {
                  setComingSoonToast(name);
                  setTimeout(() => setComingSoonToast(null), 2500);
                };

                // --- Category definitions ---
                const categories = [
                  { id: "quran", icon: "📖", glyph: "ق", label: "Quran & Audio", eyebrow: "Recitation · Reading", desc: "Surahs, reciters & Tajweed", keys: ["ahzab35","verse","surahs","quran","egzon","dossari","tajweed"],
                    dark:{bg:"linear-gradient(145deg,#120a00,#0a0600)",border:"rgba(255,179,0,0.22)",glow:"rgba(255,179,0,0.1)",accent:"#ffb300",iconBg:"rgba(255,179,0,0.12)",iconBorder:"rgba(255,179,0,0.3)"},
                    light:{bg:"linear-gradient(145deg,#fffbee,#fff5d6)",border:"rgba(184,112,8,0.25)",glow:"rgba(184,112,8,0.08)",accent:"#b87008",iconBg:"rgba(184,112,8,0.1)",iconBorder:"rgba(184,112,8,0.25)"} },
                  { id: "dhikr", icon: "📿", glyph: "ذ", label: "Dhikr & Dua", eyebrow: "Remembrance", desc: "Tasbeeh, Azkar & supplications", keys: ["tasbeeh","takbeer","azkar","names","duas","duajournal","istikharah","khatm"],
                    dark:{bg:"linear-gradient(145deg,#041a14,#071e16)",border:"rgba(0,201,167,0.22)",glow:"rgba(0,201,167,0.1)",accent:"#00c9a7",iconBg:"rgba(0,201,167,0.12)",iconBorder:"rgba(0,201,167,0.3)"},
                    light:{bg:"linear-gradient(145deg,#e0f7f3,#eef8f6)",border:"rgba(0,158,135,0.22)",glow:"rgba(0,158,135,0.08)",accent:"#009e87",iconBg:"rgba(0,158,135,0.1)",iconBorder:"rgba(0,158,135,0.25)"} },
                  { id: "spiritual", icon: "🌙", glyph: "ح", label: "Daily & Spiritual", eyebrow: "Sunnah · Reflection", desc: "Fasting, reflection & guidance", keys: ["rightnow","muraqabah","moodguide","fasting","ghidha","tawbah","janazah"],
                    dark:{bg:"linear-gradient(145deg,#0a0614,#06030a)",border:"rgba(160,100,255,0.22)",glow:"rgba(160,100,255,0.1)",accent:"#a064ff",iconBg:"rgba(160,100,255,0.12)",iconBorder:"rgba(160,100,255,0.3)"},
                    light:{bg:"linear-gradient(145deg,#f8f0ff,#f0e0ff)",border:"rgba(120,60,200,0.22)",glow:"rgba(120,60,200,0.08)",accent:"#783cc8",iconBg:"rgba(120,60,200,0.1)",iconBorder:"rgba(120,60,200,0.25)"} },
                  { id: "tools", icon: "⚖️", glyph: "و", label: "Tools & Planning", eyebrow: "Zakat · Wasiyyah · Cards", desc: "Zakat, inheritance, Hajj & share cards", keys: ["zakat","mirath","hajj","cardgenerator"],
                    dark:{bg:"linear-gradient(145deg,#0c0a06,#080604)",border:"rgba(200,180,100,0.22)",glow:"rgba(200,180,100,0.1)",accent:"#c8b464",iconBg:"rgba(200,180,100,0.12)",iconBorder:"rgba(200,180,100,0.3)"},
                    light:{bg:"linear-gradient(145deg,#faf8f0,#f5f0e0)",border:"rgba(160,130,40,0.25)",glow:"rgba(160,130,40,0.08)",accent:"#a08228",iconBg:"rgba(160,130,40,0.1)",iconBorder:"rgba(160,130,40,0.25)"} },
                ];
                const toggleCat = (id) => {
                  // single-open: closing if already open, else switch to this id
                  setOpenCat(openCat === id ? null : id);
                };
                const allCatKeys = new Set(categories.flatMap(c => c.keys));
                const heroKeys = new Set(); // hadith hero block removed per design
                const newFeatures = remoteConfig.newFeatures || [];

                // --- Compact card renderer ---
                const renderCompactCard = (key) => {
                  const t = ibadahTools[key];
                  if (!t) return null;
                  if (t.feat === "islamicwill" && userFeatures.islamicwill !== true) return null;
                  const toolStatus = getStatus(key);
                  const isComing = toolStatus === "coming";
                  const s = darkMode ? t.dark : t.light;

                  if (isComing) {
                    return (
                      <div key={key} className="ibadah-compact-card" onClick={() => showComingSoon(ibadahLabels[key] || t.name)} style={s.bg ? {background:s.bg,border:`1px solid ${s.border}`,opacity:0.6,filter:"saturate(0.7)"} : {background:"linear-gradient(135deg, #041a14 0%, #071e16 100%)",border:"1px solid rgba(0,201,167,0.25)",opacity:0.6,filter:"saturate(0.7)"}}>
                        <div style={{position:"absolute",right:"-20px",top:"-20px",width:"80px",height:"80px",borderRadius:"50%",background:`radial-gradient(circle,${s.glow} 0%,transparent 70%)`,pointerEvents:"none"}}/>
                        <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
                          <div style={{width:"36px",height:"36px",borderRadius:"12px",background:s.iconBg,border:`1px solid ${s.iconBorder}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:"18px",flexShrink:0}}>{t.icon}</div>
                          <span style={{fontSize:"9px",fontWeight:"700",color:darkMode?"#ffb300":"#b87008",background:darkMode?"rgba(255,179,0,0.12)":"rgba(184,112,8,0.1)",padding:"2px 6px",borderRadius:"6px",border:`1px solid ${darkMode?"rgba(255,179,0,0.25)":"rgba(184,112,8,0.2)"}`}}>Soon</span>
                        </div>
                        <div style={{fontSize:"14px",fontWeight:"800",color:"var(--text)",lineHeight:1.2}}>{ibadahLabels[key] || t.name}</div>
                      </div>
                    );
                  }

                  const isNewFeature = newFeatures.includes(key) && !seenNewFeatures.includes(key);
                  const freeTool = ["duajournal","tasbeeh","azkar","names","duas"].includes(key);
                  const hasAccess = freeTool || canAccess(t.feat);
                  const handleToolClick = () => {
                    if (isNewFeature) {
                      const updated = [...seenNewFeatures, key];
                      setSeenNewFeatures(updated);
                      localStorage.setItem("seenNewFeatures", JSON.stringify(updated));
                    }
                    if (t.action === "exportCalendar") { exportPrayerCalendar(); return; }
                    if (key === "ahzab35") { try { const c = JSON.parse(localStorage.getItem("fanUsageCounts")||"{}"); c.ahzab35 = (c.ahzab35||0) + 1; localStorage.setItem("fanUsageCounts", JSON.stringify(c)); } catch {} setShowAhzab35(true); return; }
                    if (key === "verse") { try { const c = JSON.parse(localStorage.getItem("fanUsageCounts")||"{}"); c.verse = (c.verse||0) + 1; localStorage.setItem("fanUsageCounts", JSON.stringify(c)); } catch {} setShowVerseOverlay(true); return; }
                    trackEvent("feature_tap", { feature: key });
                    try { const c = JSON.parse(localStorage.getItem("fanUsageCounts")||"{}"); c[t.view] = (c[t.view]||0) + 1; localStorage.setItem("fanUsageCounts", JSON.stringify(c)); } catch {}
                    navTransition(() => setToolView(t.view));
                  };

                  return (
                    <div key={key} className="ibadah-compact-card" onClick={handleToolClick} style={s.bg ? {background:s.bg,border:`1px solid ${s.border}`} : {background:"linear-gradient(135deg, #041a14 0%, #071e16 100%)",border:"1px solid rgba(0,201,167,0.25)"}}>
                      <div style={{position:"absolute",right:"-20px",top:"-20px",width:"80px",height:"80px",borderRadius:"50%",background:`radial-gradient(circle,${s.glow} 0%,transparent 70%)`,pointerEvents:"none"}}/>
                      <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
                        <div style={{width:"36px",height:"36px",borderRadius:"12px",background:s.iconBg,border:`1px solid ${s.iconBorder}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:"18px",flexShrink:0,position:"relative"}}>
                          {t.icon}
                          {isNewFeature && <span style={{position:"absolute",top:"-3px",right:"-3px",width:"10px",height:"10px",borderRadius:"50%",background:"#ff4444",border:"2px solid var(--bg)",animation:"pulse 1.5s ease-in-out infinite"}}/>}
                        </div>
                        {isNewFeature && <span style={{fontSize:"8px",fontWeight:"800",color:"#fff",background:"linear-gradient(135deg,#ff4444,#ff6b6b)",padding:"2px 6px",borderRadius:"5px",letterSpacing:"0.5px",animation:"pulse 1.5s ease-in-out infinite"}}>NEW</span>}
                        {key === "ahzab35" && ahzab35Playing && <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:"3px",padding:"2px 6px",background:darkMode?"rgba(255,179,0,0.15)":"rgba(184,112,8,0.1)",borderRadius:"8px",border:`1px solid ${darkMode?"rgba(255,179,0,0.2)":"rgba(184,112,8,0.15)"}`}}><div className="wave" style={{transform:"scale(0.5)",margin:"-3px 0"}}><span style={{background:s.accent}}/><span style={{background:s.accent}}/><span style={{background:s.accent}}/></div></div>}
                      </div>
                      <div style={{fontSize:"14px",fontWeight:"800",color:"var(--text)",lineHeight:1.2}}>{ibadahLabels[key] || t.name}</div>
                      <div style={{fontSize:"10px",color:s.accent,fontWeight:"600",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{t.desc}</div>
                      {t.extra}
                    </div>
                  );
                };

                // --- Render hero cards (hadith, ahzab35) ---
                const renderHeroCard = (key) => {
                  const toolStatus = getStatus(key);
                  const isComing = toolStatus === "coming";

                  if (key === "hadith") {
                    if (isComing) {
                      return (
                        <div key="hadith" onClick={() => showComingSoon("Daily Hadith")} style={{borderRadius:"16px",background:darkMode?"linear-gradient(135deg,rgba(0,80,50,0.35) 0%,rgba(0,50,35,0.45) 100%)":"linear-gradient(135deg,rgba(0,100,60,0.05) 0%,rgba(0,80,45,0.08) 100%)",border:`1px solid ${darkMode?"rgba(255,179,0,0.12)":"rgba(184,112,8,0.12)"}`,padding:"16px",cursor:"pointer",opacity:0.6,filter:"saturate(0.7)",position:"relative",overflow:"hidden"}}>
                          <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
                            <span style={{fontSize:"16px"}}>📖</span>
                            <span style={{fontSize:"11px",fontWeight:"700",color:darkMode?"rgba(255,179,0,0.85)":"rgba(184,112,8,0.85)",letterSpacing:"1.2px",textTransform:"uppercase"}}>{ibadahLabels.hadith || "Daily Hadith"}</span>
                            <span style={{marginLeft:"auto",fontSize:"9px",fontWeight:"700",color:darkMode?"#ffb300":"#b87008",background:darkMode?"rgba(255,179,0,0.12)":"rgba(184,112,8,0.1)",padding:"2px 8px",borderRadius:"6px",border:`1px solid ${darkMode?"rgba(255,179,0,0.25)":"rgba(184,112,8,0.2)"}`}}>Coming Soon</span>
                          </div>
                          <div style={{fontSize:"12px",color:"var(--muted)",marginTop:"10px",fontStyle:"italic"}}>In Shaa Allah</div>
                        </div>
                      );
                    }
                    if (!remoteConfig?.dailyHadith) return null;
                    const h = remoteConfig.dailyHadith;
                    return (
                      <div key="hadith" onClick={() => setShowHadithOverlay(true)} style={{borderRadius:"16px",background:darkMode?"linear-gradient(135deg,rgba(0,80,50,0.55) 0%,rgba(0,50,35,0.75) 100%)":"linear-gradient(135deg,rgba(0,100,60,0.08) 0%,rgba(0,80,45,0.12) 100%)",border:`1px solid ${darkMode?"rgba(255,179,0,0.2)":"rgba(184,112,8,0.2)"}`,padding:"12px",cursor:"pointer",boxShadow:darkMode?"0 4px 18px rgba(0,0,0,0.35)":"0 2px 12px rgba(0,0,0,0.06)",position:"relative",overflow:"hidden"}}>
                        <div style={{position:"absolute",top:-18,right:-18,fontSize:"72px",opacity:0.06,transform:"rotate(20deg)",userSelect:"none",pointerEvents:"none"}}>☪</div>
                        <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"6px"}}>
                          <span style={{fontSize:"14px"}}>📖</span>
                          <span style={{fontSize:"10px",fontWeight:"700",color:darkMode?"rgba(255,179,0,0.85)":"rgba(184,112,8,0.85)",letterSpacing:"1.2px",textTransform:"uppercase"}}>{ibadahLabels.hadith || "Daily Hadith"}</span>
                          <span style={{marginLeft:"auto",fontSize:"9px",color:darkMode?"rgba(255,255,255,0.35)":"rgba(0,0,0,0.35)"}}>Tap to read</span>
                        </div>
                        {h.arabic && (
                          <div style={{fontSize:"14px",color:darkMode?"rgba(255,179,0,0.9)":"rgba(184,112,8,0.9)",textAlign:"right",lineHeight:1.6,marginBottom:"6px",fontFamily:"'Scheherazade New','Amiri',serif",direction:"rtl"}}>
                            {h.arabic}
                          </div>
                        )}
                        <div style={{fontSize:"12px",color:darkMode?"rgba(255,255,255,0.82)":"rgba(0,0,0,0.72)",lineHeight:1.4,marginBottom:"4px"}}>
                          "{h.english}"
                        </div>
                        <div style={{fontSize:"10px",color:darkMode?"rgba(255,179,0,0.65)":"rgba(184,112,8,0.65)",fontStyle:"italic"}}>{h.source}</div>
                      </div>
                    );
                  }
                  return null;
                };

                // --- Build the layout ---
                const heroCards = []; // daily hadith hero removed — content lives in Inspiration card above

                const categoryElements = categories.map(cat => {
                  const visibleKeys = cat.keys
                    .filter(k => finalOrder.includes(k))
                    .sort((a, b) => finalOrder.indexOf(a) - finalOrder.indexOf(b));
                  if (visibleKeys.length === 0) return null;
                  const cards = visibleKeys.map(renderCompactCard).filter(Boolean);
                  if (cards.length === 0) return null;
                  const isOpen = openCat === cat.id;
                  const cs = darkMode ? cat.dark : cat.light;
                  const accent = cs.accent;
                  return (
                    <div key={cat.id} style={{margin:"0 16px 12px",borderRadius:"18px",overflow:"hidden",
                      background: darkMode
                        ? `linear-gradient(160deg, ${isOpen ? accent + "14" : "rgba(255,255,255,0.02)"} 0%, rgba(0,0,0,0) 100%)`
                        : `linear-gradient(160deg, ${isOpen ? accent + "1a" : "rgba(0,0,0,0.02)"} 0%, rgba(0,0,0,0) 100%)`,
                      border: `1px solid ${isOpen ? accent + "55" : (darkMode ? "rgba(255,255,255,0.08)" : "rgba(10,20,30,0.08)")}`,
                      transition:"border-color 0.2s ease, background 0.2s ease"}}>
                      <button onClick={() => toggleCat(cat.id)}
                        style={{width:"100%",padding:"14px 16px",background:"transparent",border:"none",cursor:"pointer",
                          display:"flex",alignItems:"center",gap:"12px",color:"var(--text)",fontFamily:"inherit",textAlign:"left"}}>
                        <div style={{width:"34px",height:"34px",borderRadius:"10px",flexShrink:0,
                          background: darkMode ? `linear-gradient(135deg,${accent}2a,${accent}0d)` : `linear-gradient(135deg,${accent}22,${accent}0a)`,
                          border:`1px solid ${accent}38`,
                          fontFamily:'"Amiri","Scheherazade New",serif',fontSize:"18px",color:accent,fontWeight:700,
                          display:"grid",placeItems:"center",lineHeight:1}}>{cat.glyph || cat.icon}</div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontFamily:'"JetBrains Mono",ui-monospace,SFMono-Regular,Menlo,monospace',fontSize:"10px",fontWeight:600,letterSpacing:"2.5px",color:darkMode?"#5a7387":"#8293a3",textTransform:"uppercase"}}>{cat.eyebrow || cat.desc}</div>
                          <div style={{display:"flex",alignItems:"baseline",gap:"8px",marginTop:"2px"}}>
                            <span style={{fontFamily:'"Instrument Serif",Georgia,serif',fontStyle:"italic",fontSize:"22px",color:"var(--text)",letterSpacing:"-0.02em",lineHeight:1}}>{cat.label}</span>
                            <span style={{fontFamily:'"JetBrains Mono",ui-monospace,SFMono-Regular,Menlo,monospace',fontSize:"11px",fontWeight:700,color:accent,letterSpacing:"0.5px"}}>{cards.length}</span>
                          </div>
                        </div>
                        <div style={{width:"26px",height:"26px",borderRadius:"8px",display:"grid",placeItems:"center",color:darkMode?"#5a7387":"#8293a3",fontSize:"13px",transition:"transform 0.25s ease",transform: isOpen ? "rotate(180deg)" : "rotate(0deg)"}}>⌄</div>
                      </button>
                      {isOpen && (
                        <div style={{padding:"4px 12px 14px",display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:"8px",animation:"fadeIn 0.25s ease-out"}}>
                          {cards}
                        </div>
                      )}
                    </div>
                  );
                }).filter(Boolean);

                // Catch-all: tools not in any category (future-proofing)
                const uncategorized = finalOrder.filter(k => !heroKeys.has(k) && !allCatKeys.has(k) && ibadahTools[k]);
                const uncatCards = uncategorized.map(renderCompactCard).filter(Boolean);

                return (
                  <>
                    {heroCards}
                    {categoryElements}
                    {uncatCards.length > 0 && (
                      <div>
                        <div className="ibadah-section-header">More</div>
                        <div className="ibadah-compact-grid">{uncatCards}</div>
                      </div>
                    )}
                    {/* Support Sirat card hidden from Ibadah grid — still reachable from Settings / support prompt */}
                  </>
                );
              })()}

            </div>

            {/* All non-grid views share one panel div for interactive swipe-back */}
            {toolView !== "grid" && (
            <div ref={toolViewPanelRef}
              onTouchStart={onBackSwipeStart}
              onTouchMove={onBackSwipeMove}
              onTouchEnd={onBackSwipeEnd}
              style={{
                position:"fixed", top:0, left:0, right:0, bottom:0,
                maxWidth: isLandscape ? "100vw" : "480px",
                margin:"0 auto",
                background:"var(--bg)",
                zIndex:45,
                paddingTop:(isLandscape && toolView === "quran") ? "0" : (toolView === "feedback" || toolView === "khatm") ? "env(safe-area-inset-top, 0px)" : isLandscape ? "51px" : "env(safe-area-inset-top, 0px)",
                paddingBottom:(isLandscape && toolView === "quran") ? "0" : isLandscape ? "calc(44px + env(safe-area-inset-bottom))" : "calc(60px + env(safe-area-inset-bottom))",
                display:"flex", flexDirection:"column",
                overflow:"hidden",
                willChange:"transform",
              }}>
              {/* Ibadah mini-header — shown at top of every tool sub-view */}
              {toolView !== "feedback" && toolView !== "khatm" && toolView !== "calorie" && !(isLandscape && toolView === "quran") && (() => {
                const _IB_MONO = '"JetBrains Mono",ui-monospace,SFMono-Regular,Menlo,monospace';
                const _IB_SERIF = '"Instrument Serif",Georgia,serif';
                const _IB_GOLD = darkMode ? "#ffb300" : "#b87008";
                const _IB_MUTED = darkMode ? "#5a7387" : "#8293a3";
                const _IB_BORDER = darkMode ? "rgba(255,255,255,0.08)" : "rgba(10,20,30,0.08)";
                const _weekday = new Date().toLocaleDateString(undefined,{weekday:"long"});
                const _eyebrow = `${_weekday}${hijri ? " · " + hijri : ""}`;
                return (
                  <div style={{flexShrink:0,padding:"12px 20px 10px",display:"flex",alignItems:"flex-end",justifyContent:"space-between",gap:"12px",borderBottom:`1px solid ${_IB_BORDER}`}}>
                    <div style={{minWidth:0}}>
                      <div style={{fontFamily:_IB_MONO,fontSize:"9.5px",letterSpacing:"2.5px",color:_IB_MUTED,textTransform:"uppercase",marginBottom:"6px"}}>{_eyebrow}</div>
                      <div style={{display:"flex",alignItems:"center",gap:"10px"}}>
                        <div ref={logoRef} onClick={() => { openFanMenu(); if (showLogoHint) { setShowLogoHint(false); localStorage.setItem("logoHintShown","1"); } }}
                          style={{width:"36px",height:"36px",borderRadius:"11px",flexShrink:0,visibility:showFanMenu?"hidden":"visible",
                            background:darkMode?"rgba(4,18,16,0.5)":"rgba(224,247,243,0.5)",
                            backdropFilter:"blur(16px) saturate(180%)",WebkitBackdropFilter:"blur(16px) saturate(180%)",
                            border:darkMode?"1px solid rgba(0,201,167,0.25)":"1px solid rgba(0,158,135,0.2)",
                            display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",
                            boxShadow:darkMode?"0 2px 12px rgba(0,201,167,0.15)":"0 2px 12px rgba(0,158,135,0.1)",
                            animation:remoteConfig.iconGlow!==false?"logoPulse 3s ease-in-out infinite":"none"}}>
                          <svg viewBox="0 0 44 44" width="30" height="30" fill="none">
                            <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85"/>
                            <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85" transform="rotate(60 22 22)"/>
                            <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85" transform="rotate(120 22 22)"/>
                          </svg>
                        </div>
                        <div style={{fontFamily:_IB_SERIF,fontStyle:"italic",fontSize:"34px",color:"var(--text)",letterSpacing:"-0.02em",lineHeight:1}}>Ibadah</div>
                      </div>
                    </div>
                    {userName && (
                      <div style={{textAlign:"right",flexShrink:0}}>
                        <div style={{fontFamily:_IB_MONO,fontSize:"9.5px",letterSpacing:"2px",color:_IB_MUTED,textTransform:"uppercase",marginBottom:"2px"}}>Assalamu alaykum</div>
                        <div style={{fontFamily:_IB_SERIF,fontStyle:"italic",fontSize:"17px",color:_IB_GOLD,letterSpacing:"-0.3px",lineHeight:1.1}}>{userName}</div>
                      </div>
                    )}
                  </div>
                );
              })()}
              <div className="no-sb" style={{flex:1,minHeight:0,overflowY:"auto",WebkitOverflowScrolling:"touch",touchAction:"pan-y"}}>

            {toolView === "quran" && (() => {
              // ── Filtering & grouping ──────────────────────────────────────────────
              const PARA30_NUMS = new Set([78,79,80,81,82,83,84,85,86,87,88,89,90,91,92,
                93,94,95,96,97,98,99,100,101,102,103,104,105,106,107,108,109,110,111,112,113,114]);
              const FEATURED_KEYS = new Set(["yaseen","kahf","rahman","mulk"]);
              const allSurahEntries = Object.entries(SURAH_INFO);
              const LONG_KEYS = new Set(allSurahEntries.filter(([,s]) => (s.totalSecs||0) >= 600).map(([k])=>k));
              const PARA30_KEYS = allSurahEntries.filter(([,s]) => PARA30_NUMS.has(s.num)).sort((a,b)=>a[1].num-b[1].num).map(([k])=>k);
              const isFiltered = !!surahSearch || surahFilter !== "all";
              const filteredSurahs = allSurahEntries.filter(([k,s]) => {
                const q = surahSearch.toLowerCase().trim();
                const nameNorm = s.name.toLowerCase().replace("surah ","").replace(/-/g," ");
                const matchesSearch = !q || s.name.toLowerCase().includes(q) || nameNorm.includes(q) || k.includes(q) || s.arabic.includes(surahSearch);
                const matchesFilter = surahFilter === "para30" ? PARA30_NUMS.has(s.num) : surahFilter === "long" ? LONG_KEYS.has(k) : true;
                return matchesSearch && matchesFilter;
              });
              const totalMins = Math.round(filteredSurahs.reduce((acc,[,s]) => acc + (s.totalSecs||0), 0) / 60);
              const totalHrs = Math.floor(totalMins / 60);
              const remMins = totalMins % 60;
              const timeLabel = totalHrs > 0 ? `~${totalHrs}h ${remMins}m` : `~${totalMins}m`;

              // ── Play-all handler ──────────────────────────────────────────────────
              const playAllPara30 = () => {
                if (playAllQueueRef.current) { playAllQueueRef.current = null; return; }
                playAllQueueRef.current = [...PARA30_KEYS];
                loadSurah("naba").then(ayahs => { if (ayahs) playSurahFrom(ayahs, 0); });
              };

              // ── Row renderer ──────────────────────────────────────────────────────
              const renderRow = (k, s, queuePos) => {
                const isPlaying = surahKey === k && surahIsPlaying;
                const isPaused  = surahKey === k && !surahIsPlaying && surahCurrentAyah !== null;
                const isBookmarkedRow = surahBookmark?.key === k;
                const surahUnlocked = true; // all surahs permanently free
                // Progress arc
                const r = 19, circumference = 2 * Math.PI * r;
                const progress = isBookmarkedRow ? Math.min((surahBookmark.ayahIdx) / Math.max(s.count - 1, 1), 1) : 0;
                const dashOffset = circumference * (1 - progress);
                return (
                  <div
                    key={k}
                    id={"surah-row-" + k}
                    onClick={() => loadSurah(k)}
                    style={{display:"flex",alignItems:"center",gap:"12px",padding:"12px 16px",minHeight:"68px",borderBottom:"1px solid var(--border)",cursor:"pointer",background:isPlaying?"rgba(255,179,0,0.05)":isPaused?"rgba(255,179,0,0.02)":"transparent",borderLeft:isPlaying?"3px solid var(--gold)":isPaused?"3px solid rgba(255,179,0,0.35)":"3px solid transparent",transition:"background 0.2s",WebkitTapHighlightColor:"transparent"}}
                  >
                    {/* Progress arc + number badge */}
                    <div style={{position:"relative",width:"44px",height:"44px",flexShrink:0}}>
                      <svg width="44" height="44" style={{position:"absolute",inset:0}}>
                        {/* Track ring */}
                        <circle cx="22" cy="22" r={r} fill="none" stroke="var(--border)" strokeWidth="2" opacity="0.5" />
                        {/* Progress arc — only shown when there is progress */}
                        {progress > 0 && (
                          <circle cx="22" cy="22" r={r} fill="none"
                            stroke="var(--gold)" strokeWidth="2.5" opacity="0.7"
                            strokeDasharray={circumference} strokeDashoffset={dashOffset}
                            strokeLinecap="round"
                            transform="rotate(-90 22 22)"
                          />
                        )}
                      </svg>
                      {/* Number / playing indicator */}
                      <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column"}}>
                        {isPlaying ? (
                          <div style={{background:"var(--gold)",borderRadius:"50%",width:"30px",height:"30px",display:"flex",alignItems:"center",justifyContent:"center"}}>
                            <div className="wave" style={{gap:"1.5px",justifyContent:"center",margin:0}}>
                              <span style={{background:"#000",width:"2px",height:"8px"}}/><span style={{background:"#000",width:"2px",height:"11px"}}/><span style={{background:"#000",width:"2px",height:"7px"}}/>
                            </div>
                          </div>
                        ) : (
                          <span style={{fontSize:"13px",fontWeight:"700",color:isPaused?"var(--gold)":"var(--muted)",lineHeight:1}}>{s.num}</span>
                        )}
                        {/* Queue position badge */}
                        {queuePos != null && (
                          <span style={{fontSize:"8px",color:"var(--gold)",fontWeight:"800",lineHeight:1,marginTop:"1px"}}>{queuePos}</span>
                        )}
                      </div>
                    </div>

                    {/* Icon */}
                    <span style={{fontSize:"20px",flexShrink:0}}>{s.icon}</span>

                    {/* Name block */}
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:"15px",fontWeight:"700",color:"var(--text)",lineHeight:1.2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{s.name.replace("Surah ","")}</div>
                      <div style={{fontSize:"12px",fontFamily:"'Scheherazade New','Amiri',Georgia,serif",color:"var(--gold)",opacity:0.75,direction:"rtl",textAlign:"left",marginTop:"2px"}}>{s.arabic.replace("سورة ","")}</div>
                    </div>

                    {/* Bookmark dot */}
                    {isBookmarkedRow && !isPlaying && !isPaused && (
                      <span style={{fontSize:"10px",opacity:0.55,flexShrink:0}}>🔖</span>
                    )}

                    {/* Right info */}
                    <div style={{flexShrink:0,textAlign:"right",minWidth:"44px"}}>
                      {isPlaying ? (
                        <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:"3px"}}>
                          <span style={{fontSize:"10px",color:"var(--gold)",fontWeight:"700"}}>Ayah {surahCurrentAyah + 1}/{s.count}</span>
                        </div>
                      ) : isPaused ? (
                        <span style={{fontSize:"11px",color:"var(--muted)"}}>⏸ {surahCurrentAyah + 1}</span>
                      ) : !surahUnlocked ? (
                        <span style={{fontSize:"14px",opacity:0.4}}>🔒</span>
                      ) : (
                        <div style={{display:"flex",alignItems:"center",gap:"2px"}}>
                          <span style={{fontSize:"11px",color:"var(--muted)"}}>{s.totalSecs ? `~${Math.ceil(s.totalSecs/60)}m` : `${s.count} ayahs`}</span>
                          <span style={{fontSize:"11px",color:"var(--muted)",opacity:0.3}}>›</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              };

              // ── Reciter switcher (same logic as before) ───────────────────────────
              const reciterSwitchHandler = async (r) => {
                if (r.id === selectedReciter) { setShowReciterPicker(false); return; }
                hapticToggle();
                setSelectedReciter(r.id);
                localStorage.setItem("selectedReciter", r.id);
                setShowReciterPicker(false);
                if (surahKey && SURAH_INFO[surahKey]) {
                  const ck = surahKey + ":" + r.id;
                  if (!surahCacheRef.current[ck]) {
                    setSurahLoading(true);
                    const inf = SURAH_INFO[surahKey];
                    try {
                      const existingKey = Object.keys(surahCacheRef.current).find(k => k.startsWith(surahKey + ":"));
                      const existingAyahs = existingKey ? surahCacheRef.current[existingKey] : null;
                      const auRes = await fetch("https://api.alquran.cloud/v1/surah/" + inf.num + "/" + r.id);
                      const auD = await auRes.json();
                      if (auD.code === 200 && auD.data?.ayahs) {
                        let newAyahs;
                        if (existingAyahs) {
                          newAyahs = existingAyahs.map((ay, i) => ({ ...ay, audio: auD.data.ayahs[i].audio }));
                        } else {
                          const [arRes, enRes] = await Promise.all([
                            fetch("https://api.alquran.cloud/v1/surah/" + inf.num),
                            fetch("https://api.alquran.cloud/v1/surah/" + inf.num + "/en.sahih"),
                          ]);
                          const [arD, enD] = await Promise.all([arRes.json(), enRes.json()]);
                          newAyahs = arD.data.ayahs.map((ay, i) => ({ number: ay.numberInSurah, arabic: ay.text, translation: enD.data.ayahs[i].text, audio: auD.data.ayahs[i].audio }));
                        }
                        setSurahCache(c => ({ ...c, [ck]: newAyahs }));
                        surahCacheRef.current = { ...surahCacheRef.current, [ck]: newAyahs };
                        preloadYasinBuf(0, newAyahs[0].audio);
                      }
                    } catch {}
                    setSurahLoading(false);
                  }
                }
              };

              // ── Section rendering (when unfiltered) ───────────────────────────────
              const renderSection = (label, entries) => entries.length === 0 ? null : (
                <>
                  <div style={{padding:"14px 16px 4px",fontSize:"9px",fontWeight:"800",color:"var(--muted)",letterSpacing:"2px",textTransform:"uppercase",borderBottom:"1px solid var(--border)"}}>{label}</div>
                  {entries.map(([k,s]) => renderRow(k, s, playAllQueueRef.current ? playAllQueueRef.current.indexOf(k) + 1 || null : null))}
                </>
              );

              const featuredEntries = allSurahEntries.filter(([k]) => FEATURED_KEYS.has(k));
              const juzAmmaEntries  = allSurahEntries.filter(([,s]) => PARA30_NUMS.has(s.num) && !FEATURED_KEYS.has(Object.entries(SURAH_INFO).find(([,x])=>x===s)?.[0]));
              const otherEntries    = allSurahEntries.filter(([k,s]) => !FEATURED_KEYS.has(k) && !PARA30_NUMS.has(s.num));

              return (
                <div style={{paddingBottom:"100px"}}>
                  {/* ── Header ───────────────────────────────────────────────────── */}
                  <div style={{position:"sticky",top:0,zIndex:20,background:"var(--bg)",borderBottom:"1px solid var(--border)"}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 16px 8px"}}>
                      <button onClick={() => setToolView("grid")} style={{background:"transparent",border:"1px solid var(--border)",borderRadius:"8px",color:"var(--muted)",fontSize:"13px",padding:"6px 14px",cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>← Ibadah</button>
                      <div style={{position:"relative"}}>
                        <button onClick={() => setShowReciterPicker(v => !v)} style={{display:"flex",alignItems:"center",gap:"6px",padding:"7px 12px",background:"var(--s2)",border:"1px solid var(--border)",borderRadius:"10px",cursor:"pointer",fontFamily:"inherit"}}>
                          <span style={{fontSize:"13px"}}>🎙</span>
                          <span style={{fontSize:"12px",fontWeight:"600",color:"var(--gold)",maxWidth:"110px",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{(RECITERS.find(r=>r.id===selectedReciter)||RECITERS[0]).short || (RECITERS.find(r=>r.id===selectedReciter)||RECITERS[0]).name}</span>
                          <span style={{fontSize:"10px",color:"var(--muted)"}}>{showReciterPicker?"▲":"▼"}</span>
                        </button>
                        {showReciterPicker && (
                          <div style={{position:"absolute",right:0,top:"calc(100% + 6px)",width:"260px",background:"var(--s1)",border:"1px solid var(--border)",borderRadius:"12px",overflow:"hidden",maxHeight:"300px",overflowY:"auto",zIndex:40,boxShadow:"0 8px 24px rgba(0,0,0,0.4)"}}>
                            {RECITERS.map(r => (
                              <button key={r.id} onClick={() => reciterSwitchHandler(r)}
                                style={{width:"100%",padding:"12px 14px",background:r.id===selectedReciter?"rgba(255,179,0,0.08)":"transparent",border:"none",borderBottom:"1px solid var(--border)",color:r.id===selectedReciter?"var(--gold)":"var(--text)",fontSize:"14px",fontWeight:r.id===selectedReciter?"700":"400",cursor:"pointer",fontFamily:"inherit",textAlign:"left",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                                <span>{r.name}</span>
                                {r.id===selectedReciter && <span style={{color:"var(--gold)",fontSize:"14px"}}>✓</span>}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* ── Recently played chips ─────────────────────────────────── */}
                    {recentSurahs.length > 0 && (
                      <div onTouchStart={e => e.stopPropagation()} style={{display:"flex",alignItems:"center",gap:"8px",padding:"0 16px 10px",overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
                        <span style={{fontSize:"10px",color:"var(--muted)",fontWeight:"600",letterSpacing:"0.5px",flexShrink:0}}>Recent:</span>
                        {recentSurahs.filter(k => SURAH_INFO[k]).map(k => {
                          const s = SURAH_INFO[k];
                          const isActive = surahKey === k && (surahIsPlaying || surahCurrentAyah !== null);
                          return (
                            <button key={k} onClick={() => loadSurah(k)}
                              style={{display:"flex",alignItems:"center",gap:"5px",padding:"5px 10px",background:"var(--s2)",border:`1px solid ${isActive?"rgba(255,179,0,0.5)":"var(--border)"}`,borderRadius:"20px",cursor:"pointer",fontFamily:"inherit",flexShrink:0,whiteSpace:"nowrap"}}>
                              <span style={{fontSize:"13px"}}>{s.icon}</span>
                              <span style={{fontSize:"11px",fontWeight:"600",color:isActive?"var(--gold)":"var(--text)"}}>{s.name.replace("Surah ","")}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* ── Search ───────────────────────────────────────────────── */}
                    <div style={{position:"relative",padding:"0 16px 10px"}}>
                      <span style={{position:"absolute",left:"26px",top:"50%",transform:"translateY(-60%)",fontSize:"14px",pointerEvents:"none",opacity:0.5}}>🔍</span>
                      <input
                        type="text"
                        placeholder="Search surahs..."
                        value={surahSearch}
                        onChange={e => setSurahSearch(e.target.value)}
                        style={{width:"100%",padding:"9px 12px 9px 36px",background:"var(--s2)",border:"1px solid var(--border)",borderRadius:"12px",color:"var(--text)",fontSize:"14px",fontFamily:"inherit",outline:"none",boxSizing:"border-box",opacity:0.9}}
                      />
                      {surahSearch && (
                        <button onClick={() => setSurahSearch("")} style={{position:"absolute",right:"24px",top:"50%",transform:"translateY(-60%)",background:"transparent",border:"none",color:"var(--muted)",fontSize:"18px",cursor:"pointer",padding:"4px"}}>×</button>
                      )}
                    </div>

                    {/* ── Filter pills ─────────────────────────────────────────── */}
                    <div style={{display:"flex",gap:"8px",padding:"0 16px 12px"}}>
                      {[["all","All"],["para30","Para 30"],["long","Long"]].map(([val,label]) => (
                        <button key={val} onClick={() => setSurahFilter(val)}
                          style={{padding:"6px 14px",background:surahFilter===val?"var(--gold)":"var(--s2)",border:`1px solid ${surahFilter===val?"var(--gold)":"var(--border)"}`,borderRadius:"20px",color:surahFilter===val?"#000":"var(--muted)",fontSize:"12px",fontWeight:surahFilter===val?"800":"600",cursor:"pointer",fontFamily:"inherit",transition:"all 0.15s",flexShrink:0}}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* ── Play All Juz Amma ─────────────────────────────────────────── */}
                  {surahFilter === "para30" && !surahSearch && (
                    <div style={{padding:"12px 16px 4px"}}>
                      <button onClick={playAllPara30}
                        style={{width:"100%",padding:"14px",background:playAllQueueRef.current?"var(--s3)":"var(--gold)",border:`1px solid ${playAllQueueRef.current?"var(--border)":"rgba(255,179,0,0.3)"}`,borderRadius:"14px",color:playAllQueueRef.current?"var(--text)":"#000",fontSize:"15px",fontWeight:"800",cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:"10px"}}>
                        {playAllQueueRef.current ? <>■ Stop Playlist</> : <>▶ Play All Juz Amma <span style={{fontWeight:"400",opacity:0.7}}>· {PARA30_KEYS.length} surahs</span></>}
                      </button>
                    </div>
                  )}

                  {/* ── Count + time summary ─────────────────────────────────────── */}
                  {filteredSurahs.length > 0 && (
                    <div style={{padding:"10px 16px 4px",fontSize:"11px",color:"var(--muted)",display:"flex",alignItems:"center",gap:"6px"}}>
                      <span>{filteredSurahs.length} surahs</span>
                      <span style={{opacity:0.4}}>·</span>
                      <span style={{color:"var(--gold)",opacity:0.7}}>{timeLabel}</span>
                    </div>
                  )}

                  {/* ── Continue reading banner ──────────────────────────────────── */}
                  {surahBookmark && SURAH_INFO[surahBookmark.key] && !isFiltered && (
                    <div style={{margin:"8px 16px",display:"flex",alignItems:"center",gap:"12px",padding:"12px 14px",background:"rgba(255,179,0,0.06)",border:"1px solid rgba(255,179,0,0.22)",borderRadius:"14px"}}>
                      <span style={{fontSize:"18px"}}>🔖</span>
                      <div style={{flex:1,minWidth:0,cursor:"pointer"}} onClick={() => loadSurah(surahBookmark.key)}>
                        <div style={{fontSize:"11px",fontWeight:"700",color:"var(--gold)",letterSpacing:"0.3px"}}>Continue where you left off</div>
                        <div style={{fontSize:"13px",color:"var(--text)",fontWeight:"600",marginTop:"2px"}}>{SURAH_INFO[surahBookmark.key].name} · Ayah {surahBookmark.ayahIdx + 1}</div>
                      </div>
                      <button onClick={e => { e.stopPropagation(); setSurahBookmark(null); localStorage.removeItem("surahBookmark"); }}
                        style={{background:"transparent",border:"1px solid rgba(255,100,100,0.3)",borderRadius:"6px",color:"rgba(255,100,100,0.7)",fontSize:"12px",padding:"4px 8px",cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>✕</button>
                    </div>
                  )}

                  {/* ── Surah rows ───────────────────────────────────────────────── */}
                  {isFiltered ? (
                    // Flat filtered list
                    filteredSurahs.length > 0
                      ? filteredSurahs.map(([k,s]) => renderRow(k, s, playAllQueueRef.current ? playAllQueueRef.current.indexOf(k) + 1 || null : null))
                      : <div style={{textAlign:"center",padding:"40px 20px",color:"var(--muted)",fontSize:"14px"}}>No surahs match your search</div>
                  ) : (
                    // Sectioned list
                    <>
                      {renderSection("Featured", featuredEntries)}
                      {renderSection("Juz Amma · Para 30", juzAmmaEntries)}
                      {renderSection("More Surahs", otherEntries)}
                    </>
                  )}
                </div>
              );
            })()}

            {toolView === "azkar" && <Lazy><Azkar onBack={() => setToolView("grid")} /></Lazy>}

            {toolView === "names" && <Lazy><NamesOfAllah onBack={() => setToolView("grid")} /></Lazy>}

            {toolView === "istikharah" && (() => {
              const accentColor = darkMode ? "#64a0ff" : "#3264dc";
              const accentFaint = darkMode ? "rgba(100,160,255,0.12)" : "rgba(50,100,220,0.08)";
              const accentBorder = darkMode ? "rgba(100,160,255,0.25)" : "rgba(50,100,220,0.2)";
              const steps = [
                { num: 1, title: "Make Wudu", body: "Perform ablution as you would for any Salah." },
                { num: 2, title: "Pray 2 Rakats", body: "Offer two rakats of nafl (voluntary) Salah with the intention of Istikharah. Recite any surah after Al-Fatiha in each rakat." },
                { num: 3, title: "Read the Dua", body: "After the second rakat, before or after the salaam, read the dua below. As you say \"this matter\", think of or mention your decision." },
                { num: 4, title: "Trust in Allah", body: "Go about your life with reliance on Allah. The answer is not necessarily a dream — it is a feeling of ease, a door that opens or closes." },
              ];
              const duaArabic = "اللَّهُمَّ إِنِّي أَسْتَخِيرُكَ بِعِلْمِكَ وَأَسْتَقْدِرُكَ بِقُدْرَتِكَ وَأَسْأَلُكَ مِنْ فَضْلِكَ الْعَظِيمِ فَإِنَّكَ تَقْدِرُ وَلَا أَقْدِرُ وَتَعْلَمُ وَلَا أَعْلَمُ وَأَنْتَ عَلَّامُ الْغُيُوبِ اللَّهُمَّ إِنْ كُنْتَ تَعْلَمُ أَنَّ هَذَا الْأَمْرَ خَيْرٌ لِي فِي دِينِي وَمَعَاشِي وَعَاقِبَةِ أَمْرِي فَاقْدُرْهُ لِي وَيَسِّرْهُ لِي ثُمَّ بَارِكْ لِي فِيهِ وَإِنْ كُنْتَ تَعْلَمُ أَنَّ هَذَا الْأَمْرَ شَرٌّ لِي فِي دِينِي وَمَعَاشِي وَعَاقِبَةِ أَمْرِي فَاصْرِفْهُ عَنِّي وَاصْرِفْنِي عَنْهُ وَاقْدُرْ لِيَ الْخَيْرَ حَيْثُ كَانَ ثُمَّ أَرْضِنِي";
              const duaTranslit = "Allāhumma innī astakhīruka bi-ʿilmika, wa astaqdiruka bi-qudratika, wa asʾaluka min faḍlikal-ʿaẓīm — fa-innaka taqdiru wa lā aqdiru, wa taʿlamu wa lā aʿlamu, wa anta ʿallāmul-ghuyūb. Allāhumma in kunta taʿlamu anna hādhal-amra khayrun lī fī dīnī wa maʿāshī wa ʿāqibati amrī, faqdurhu lī wa yassirhu lī thumma bārik lī fīh. Wa in kunta taʿlamu anna hādhal-amra sharrun lī fī dīnī wa maʿāshī wa ʿāqibati amrī, fasrifhu ʿannī wasrifnī ʿanhu — waqdur liyal-khayra ḥaythu kāna thumma arḍinī.";
              const duaTranslation = "O Allah, I seek Your guidance by virtue of Your knowledge, and I seek ability by virtue of Your power, and I ask You of Your great bounty. You have power, I have none. You know, I know not. You are the Knower of hidden things.\n\nO Allah, if in Your knowledge this matter is good for me in my religion, livelihood and the outcome of my affairs — then ordain it for me, make it easy for me, and bless it for me. And if in Your knowledge it is bad for me in my religion, livelihood and the outcome of my affairs — then turn it away from me and turn me away from it. Ordain for me the good wherever it may be, then make me content with it.";
              const faqs = [
                { q: "Do I need to see a dream?", a: "No. Most scholars say there is no requirement for a dream. The sign is ease — a heart that inclines toward the right decision, doors that open or close naturally." },
                { q: "Can I repeat it?", a: "Yes. If you haven't decided yet, you may repeat Istikharah up to seven times over consecutive days." },
                { q: "Can I pray it for someone else?", a: "Some scholars permit it on behalf of a family member who is unable (e.g. illness). Otherwise, the person should pray it themselves." },
                { q: "What if I still can't decide?", a: "After making Istikharah and sincere dua, consult those with knowledge and experience (shura), then make a decision and trust Allah. Indecision itself is a test." },
              ];
              return (
                <div style={{display:"flex",flexDirection:"column",height:"100%",background:"var(--bg)",overflowY:"auto"}}>
                  {/* Header */}
                  <div style={{display:"flex",alignItems:"center",gap:"12px",padding:"16px 20px 12px",position:"sticky",top:0,background:"var(--bg)",zIndex:10,borderBottom:`1px solid ${accentBorder}`}}>
                    <button onClick={() => setToolView("grid")} style={{background:"none",border:"none",color:"var(--text)",cursor:"pointer",padding:"4px",display:"flex",alignItems:"center",justifyContent:"center",borderRadius:"8px",width:"32px",height:"32px"}}>
                      <svg width="10" height="17" viewBox="0 0 10 17" fill="none"><path d="M9 1L1 8.5L9 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </button>
                    <div>
                      <div style={{fontSize:"18px",fontWeight:"800",color:"var(--text)"}}>Salah al-Istikharah</div>
                      <div style={{fontSize:"11px",color:accentColor,fontWeight:"600",letterSpacing:"0.5px"}}>Seeking Allah's Guidance</div>
                    </div>
                    <div style={{marginLeft:"auto",fontSize:"22px"}}>🌠</div>
                  </div>

                  <div style={{padding:"16px 20px 100px",display:"flex",flexDirection:"column",gap:"20px"}}>
                    {/* Intro card */}
                    <div style={{borderRadius:"16px",background:darkMode?"linear-gradient(135deg,rgba(6,12,26,0.9),rgba(3,8,16,1))":"linear-gradient(135deg,rgba(238,243,255,1),rgba(221,232,255,0.8))",border:`1px solid ${accentBorder}`,padding:"18px",position:"relative",overflow:"hidden"}}>
                      <div style={{position:"absolute",top:-20,right:-20,fontSize:"80px",opacity:0.05,userSelect:"none",pointerEvents:"none"}}>🌠</div>
                      <div style={{fontSize:"13px",color:darkMode?"rgba(255,255,255,0.75)":"rgba(0,0,0,0.65)",lineHeight:1.7}}>
                        Istikharah (الاستخارة) is a Sunnah prayer for seeking Allah's guidance before making an important decision — a marriage, a job, a move, a major purchase. It is a sign of reliance on Allah (tawakkul) and humility before His infinite knowledge.
                      </div>
                      <div style={{marginTop:"12px",fontSize:"12px",color:accentColor,fontWeight:"600"}}>📚 Narrated by Jabir ibn Abdullah (Bukhari)</div>
                    </div>

                    {/* Steps */}
                    <div>
                      <div style={{fontSize:"11px",fontWeight:"700",color:"var(--muted)",letterSpacing:"1.2px",textTransform:"uppercase",marginBottom:"10px"}}>How to Perform</div>
                      <div style={{display:"flex",flexDirection:"column",gap:"10px"}}>
                        {steps.map(s => (
                          <div key={s.num} style={{display:"flex",gap:"14px",alignItems:"flex-start",padding:"14px",borderRadius:"14px",background:darkMode?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.02)",border:`1px solid ${darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.07)"}`}}>
                            <div style={{width:"28px",height:"28px",borderRadius:"50%",background:accentFaint,border:`1px solid ${accentBorder}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:"12px",fontWeight:"800",color:accentColor}}>{s.num}</div>
                            <div>
                              <div style={{fontSize:"14px",fontWeight:"700",color:"var(--text)",marginBottom:"4px"}}>{s.title}</div>
                              <div style={{fontSize:"13px",color:darkMode?"rgba(255,255,255,0.6)":"rgba(0,0,0,0.6)",lineHeight:1.6}}>{s.body}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Dua */}
                    <div style={{borderRadius:"16px",border:`1px solid ${accentBorder}`,overflow:"hidden"}}>
                      <div style={{background:accentFaint,padding:"10px 16px",borderBottom:`1px solid ${accentBorder}`}}>
                        <span style={{fontSize:"11px",fontWeight:"700",color:accentColor,letterSpacing:"1.2px",textTransform:"uppercase"}}>The Dua</span>
                      </div>
                      <div style={{padding:"18px",display:"flex",flexDirection:"column",gap:"16px"}}>
                        {/* Arabic */}
                        <div style={{fontSize:"20px",lineHeight:2.2,color:darkMode?"rgba(255,255,255,0.92)":"rgba(0,0,0,0.85)",textAlign:"right",direction:"rtl",fontFamily:"'Scheherazade New','Amiri',serif"}}>
                          {duaArabic}
                        </div>
                        {/* Divider */}
                        <div style={{height:"1px",background:darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.07)"}}/>
                        {/* Transliteration */}
                        <div style={{fontSize:"13px",color:accentColor,lineHeight:1.8,fontStyle:"italic"}}>
                          {duaTranslit}
                        </div>
                        {/* Divider */}
                        <div style={{height:"1px",background:darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.07)"}}/>
                        {/* Translation */}
                        <div style={{fontSize:"13px",color:darkMode?"rgba(255,255,255,0.7)":"rgba(0,0,0,0.65)",lineHeight:1.75,whiteSpace:"pre-line"}}>
                          {duaTranslation}
                        </div>
                      </div>
                    </div>

                    {/* Key note about "this matter" */}
                    <div style={{display:"flex",gap:"12px",padding:"14px",borderRadius:"14px",background:darkMode?"rgba(255,179,0,0.06)":"rgba(184,112,8,0.05)",border:`1px solid ${darkMode?"rgba(255,179,0,0.2)":"rgba(184,112,8,0.15)"}`}}>
                      <div style={{fontSize:"20px",flexShrink:0}}>💡</div>
                      <div style={{fontSize:"13px",color:darkMode?"rgba(255,255,255,0.75)":"rgba(0,0,0,0.65)",lineHeight:1.65}}>
                        When you reach <span style={{color:darkMode?"#ffb300":"#b87008",fontWeight:"700"}}>"this matter"</span> (هَذَا الْأَمْرَ), pause and think of your specific decision or say it aloud in your own language.
                      </div>
                    </div>

                    {/* FAQ */}
                    <div>
                      <div style={{fontSize:"11px",fontWeight:"700",color:"var(--muted)",letterSpacing:"1.2px",textTransform:"uppercase",marginBottom:"10px"}}>Common Questions</div>
                      <div style={{display:"flex",flexDirection:"column",gap:"10px"}}>
                        {faqs.map((faq, i) => (
                          <div key={i} style={{padding:"14px",borderRadius:"14px",background:darkMode?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.02)",border:`1px solid ${darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.07)"}`}}>
                            <div style={{fontSize:"13px",fontWeight:"700",color:"var(--text)",marginBottom:"6px"}}>{faq.q}</div>
                            <div style={{fontSize:"13px",color:darkMode?"rgba(255,255,255,0.6)":"rgba(0,0,0,0.6)",lineHeight:1.65}}>{faq.a}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Footer dua */}
                    <div style={{textAlign:"center",padding:"10px 0 4px"}}>
                      <div style={{fontSize:"18px",color:accentColor,fontFamily:"'Scheherazade New','Amiri',serif",direction:"rtl",marginBottom:"6px"}}>وَمَن يَتَوَكَّلْ عَلَى اللَّهِ فَهُوَ حَسْبُهُ</div>
                      <div style={{fontSize:"12px",color:darkMode?"rgba(255,255,255,0.4)":"rgba(0,0,0,0.4)"}}>And whoever relies upon Allah — then He is sufficient for him. (65:3)</div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Dua Collection — Category List */}
            {toolView === "duas" && (() => {
              return (
                <div style={{display:"flex",flexDirection:"column",height:"100%",background:"var(--bg)",overflowY:"auto"}}>
                  <div style={{display:"flex",alignItems:"center",gap:"12px",padding:"16px 20px 12px",position:"sticky",top:0,background:"var(--bg)",zIndex:10,borderBottom:`1px solid ${darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.08)"}`}}>
                    <button onClick={() => setToolView("grid")} style={{background:"none",border:"none",color:"var(--text)",cursor:"pointer",padding:"4px",display:"flex",alignItems:"center",justifyContent:"center",borderRadius:"8px",width:"32px",height:"32px"}}>
                      <svg width="10" height="17" viewBox="0 0 10 17" fill="none"><path d="M9 1L1 8.5L9 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </button>
                    <div>
                      <div style={{fontSize:"18px",fontWeight:"800",color:"var(--text)"}}>Dua Collection</div>
                      <div style={{fontSize:"11px",color:"var(--muted)",fontWeight:"600"}}>Hisn al-Muslim · {DUA_CATEGORIES.reduce((s,c) => s + c.duas.length, 0)} duas</div>
                    </div>
                  </div>
                  <div style={{padding:"12px 16px 100px",display:"flex",flexDirection:"column",gap:"10px"}}>
                    {DUA_CATEGORIES.map(cat => {
                      const color = darkMode ? cat.color.dark : cat.color.light;
                      return (
                        <div key={cat.id} onClick={() => { setSelectedDuaCat(cat.id); setToolView("duadetail"); }}
                          className="tasbeeh-hero" style={{cursor:"pointer"}}>
                          <div style={{width:"48px",height:"48px",borderRadius:"14px",background:darkMode?`${color}18`:`${color}14`,border:`1px solid ${color}40`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:"24px",flexShrink:0}}>
                            {cat.icon}
                          </div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"2px"}}>
                              <span style={{fontSize:"15px",fontWeight:"700",color:"var(--text)"}}>{cat.name}</span>
                              <span style={{fontSize:"12px",color,fontFamily:"'Scheherazade New','Amiri',serif"}}>{cat.nameAr}</span>
                            </div>
                            <div style={{fontSize:"12px",color:"var(--muted)"}}>{cat.duas.length} dua{cat.duas.length !== 1 ? "s" : ""}</div>
                          </div>
                          <div style={{color:"var(--muted)",opacity:0.4,flexShrink:0}}>
                            <svg width="9" height="16" viewBox="0 0 9 16" fill="none"><path d="M1 1l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Dua Collection — Category Detail */}
            {toolView === "duadetail" && (() => {
              const cat = DUA_CATEGORIES.find(c => c.id === selectedDuaCat);
              if (!cat) return null;
              const color = darkMode ? cat.color.dark : cat.color.light;
              const colorFaint = `${color}20`;
              const colorBorder = `${color}40`;
              return (
                <div style={{display:"flex",flexDirection:"column",height:"100%",background:"var(--bg)",overflowY:"auto"}}>
                  <div style={{display:"flex",alignItems:"center",gap:"12px",padding:"16px 20px 12px",position:"sticky",top:0,background:"var(--bg)",zIndex:10,borderBottom:`1px solid ${colorBorder}`}}>
                    <button onClick={() => setToolView("duas")} style={{background:"none",border:"none",color:"var(--text)",cursor:"pointer",padding:"4px",display:"flex",alignItems:"center",justifyContent:"center",borderRadius:"8px",width:"32px",height:"32px"}}>
                      <svg width="10" height="17" viewBox="0 0 10 17" fill="none"><path d="M9 1L1 8.5L9 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </button>
                    <div style={{flex:1}}>
                      <div style={{fontSize:"18px",fontWeight:"800",color:"var(--text)"}}>{cat.icon} {cat.name}</div>
                      <div style={{fontSize:"12px",color,fontWeight:"600",fontFamily:"'Scheherazade New','Amiri',serif"}}>{cat.nameAr}</div>
                    </div>
                  </div>
                  <div style={{padding:"12px 16px 100px",display:"flex",flexDirection:"column",gap:"16px"}}>
                    {cat.duas.map((dua, i) => (
                      <div key={i} style={{borderRadius:"16px",border:`1px solid ${colorBorder}`,overflow:"hidden"}}>
                        {/* Dua number header */}
                        <div style={{background:colorFaint,padding:"8px 16px",borderBottom:`1px solid ${colorBorder}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                          <span style={{fontSize:"11px",fontWeight:"700",color,letterSpacing:"0.5px"}}>Dua {i + 1} of {cat.duas.length}</span>
                          {dua.repeat > 1 && (
                            <span style={{fontSize:"10px",fontWeight:"700",color,background:colorFaint,border:`1px solid ${colorBorder}`,borderRadius:"8px",padding:"2px 8px"}}>
                              Repeat {dua.repeat}x
                            </span>
                          )}
                        </div>
                        <div style={{padding:"16px",display:"flex",flexDirection:"column",gap:"14px"}}>
                          {/* Arabic */}
                          <div style={{fontSize:"20px",lineHeight:2.2,color:darkMode?"rgba(255,255,255,0.92)":"rgba(0,0,0,0.85)",textAlign:"right",direction:"rtl",fontFamily:"'Scheherazade New','Amiri',serif"}}>
                            {dua.arabic}
                          </div>
                          <div style={{height:"1px",background:darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.07)"}}/>
                          {/* Transliteration */}
                          <div style={{fontSize:"13px",color,lineHeight:1.75,fontStyle:"italic"}}>
                            {dua.translit}
                          </div>
                          <div style={{height:"1px",background:darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.07)"}}/>
                          {/* Translation */}
                          <div style={{fontSize:"13px",color:darkMode?"rgba(255,255,255,0.7)":"rgba(0,0,0,0.65)",lineHeight:1.7}}>
                            {dua.translation}
                          </div>
                          {/* Source */}
                          <div style={{fontSize:"11px",color:"var(--muted)",fontStyle:"italic"}}>
                            {dua.source}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {toolView === "fullquran" && isLandscape && (() => {
              // Full-screen landscape layout for the 114 surah list
              const TOTAL_QURAN_AYAHS = 6236;
              const totalRead = Object.entries(quranProgress).reduce((sum, [num, lastAyah]) => {
                const s = QURAN_SURAHS.find(x => x.num === +num);
                return sum + Math.min(lastAyah, s ? s.ayahs : 0);
              }, 0);
              const pctComplete = Math.round(totalRead / TOTAL_QURAN_AYAHS * 100);
              return (
                <div style={{position:'fixed',inset:0,background:'var(--bg)',zIndex:50,display:'flex',flexDirection:'row',overflow:'hidden'}}>
                  {/* Left nav */}
                  <div style={{width:'clamp(180px,28vw,280px)',flexShrink:0,borderRight:`1px solid var(--border)`,background:darkMode?'#0d1117':'#e8eef5',display:'flex',flexDirection:'column',padding:'14px 10px',gap:'6px'}}>
                    <div style={{display:'flex',alignItems:'center',gap:'7px',marginBottom:'10px',paddingBottom:'10px',borderBottom:`1px solid var(--border)`}}>
                      <svg viewBox="0 0 44 44" width="14" height="14" fill="none"><ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="var(--accent)" strokeWidth="1.6" opacity="0.8"/><ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="var(--accent)" strokeWidth="1.6" opacity="0.8" transform="rotate(60 22 22)"/><ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="var(--accent)" strokeWidth="1.6" opacity="0.8" transform="rotate(120 22 22)"/></svg>
                      <span style={{fontSize:'10px',fontWeight:'800',letterSpacing:'3px',color:'var(--muted)',textTransform:'uppercase'}}>Sirat</span>
                    </div>
                    {[{id:'masjids',icon:'🕌',label:'Salaah'},{id:'radio',icon:'📻',label:'Radio'},{id:'qibla',icon:'🧭',label:'Qibla'},{id:'tools',icon:'🤲',label:'Ibadah'},{id:'settings',icon:'⚙️',label:'Settings'}].map(({id,icon,label}) => (
                      <button key={id} onClick={() => { setTab(id); if(id==='tools') setToolView('grid'); }}
                        style={{background:tab===id?(darkMode?'rgba(0,201,167,0.12)':'rgba(0,158,135,0.1)'):'transparent',border:`1px solid ${tab===id?'rgba(0,201,167,0.35)':'transparent'}`,borderRadius:'10px',padding:'10px 12px',display:'flex',alignItems:'center',gap:'10px',cursor:'pointer',fontFamily:'inherit',textAlign:'left'}}>
                        <span style={{fontSize:'20px'}}>{icon}</span>
                        <span style={{fontSize:'12px',fontWeight:'700',color:tab===id?'var(--accent)':'var(--muted)'}}>{label}</span>
                      </button>
                    ))}
                  </div>
                  {/* Right — surah list */}
                  <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden'}}>
                    {/* Header */}
                    <div style={{padding:'12px 16px',borderBottom:`1px solid var(--border)`,flexShrink:0}}>
                      <div style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'8px'}}>
                        <button onClick={() => setToolView('grid')} style={{background:'transparent',border:'1px solid var(--border)',borderRadius:'8px',color:'var(--muted)',fontSize:'13px',padding:'6px 14px',cursor:'pointer',fontFamily:'inherit',flexShrink:0}}>← Back</button>
                        <div style={{flex:1}}>
                          <div style={{fontSize:'16px',fontWeight:'800',color:'var(--text)'}}>القرآن الكريم</div>
                          <div style={{fontSize:'11px',color:'var(--muted)'}}>The Noble Quran · 114 Surahs</div>
                        </div>
                      </div>
                      <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                        <div style={{flex:1,height:'5px',background:'var(--s2)',borderRadius:'3px',overflow:'hidden'}}>
                          <div style={{height:'100%',width:`${pctComplete}%`,background:`linear-gradient(to right, ${darkMode?'#00b4c8':'#008598'}, ${darkMode?'#00d4a0':'#009070'})`,borderRadius:'3px',transition:'width 0.5s'}}/>
                        </div>
                        <div style={{fontSize:'11px',fontWeight:'700',color:darkMode?'#00b4c8':'#008598',flexShrink:0}}>{pctComplete}%</div>
                        {readingStreak.count > 0 && <div style={{fontSize:'11px',fontWeight:'700',color:'var(--gold)',flexShrink:0}}>🔥 {readingStreak.count} day{readingStreak.count !== 1 ? 's' : ''}</div>}
                      </div>
                    </div>
                    {/* Bookmarks + surah list */}
                    <div style={{flex:1,overflowY:'auto',padding:'0 12px 20px'}}>
                      {bookmarks.length > 0 && (
                        <div style={{padding:'12px 0 4px'}}>
                          <div style={{fontSize:'11px',fontWeight:'700',color:'var(--muted)',letterSpacing:'1px',marginBottom:'8px'}}>BOOKMARKS</div>
                          {bookmarks.map((bm, i) => (
                            <div key={i} style={{display:'flex',alignItems:'center',gap:'10px',padding:'10px 12px',background:'var(--s2)',border:'1px solid var(--border)',borderRadius:'10px',marginBottom:'6px'}}>
                              <div style={{fontSize:'18px',flexShrink:0}}>🔖</div>
                              <div onClick={() => loadQuranSurah(bm.num, bm.ayahNum || null)} style={{flex:1,minWidth:0,cursor:'pointer'}}>
                                <div style={{fontSize:'13px',fontWeight:'700',color:'var(--text)'}}>{bm.translit}</div>
                                <div style={{fontSize:'11px',color:'var(--muted)'}}>{bm.ayahNum ? `Ayah ${bm.ayahNum}` : ''} · {bm.date}</div>
                              </div>
                              <div style={{fontSize:'15px',color:'var(--gold)',fontFamily:"'Scheherazade New', serif",direction:'rtl',flexShrink:0}}>{bm.arabic}</div>
                              <button onClick={() => { const nb = bookmarks.filter((_,j)=>j!==i); setBookmarks(nb); localStorage.setItem('quranBookmarks',JSON.stringify(nb)); }} style={{background:'transparent',border:'1px solid rgba(255,100,100,0.3)',borderRadius:'6px',color:'rgba(255,100,100,0.7)',fontSize:'12px',padding:'4px 8px',cursor:'pointer',fontFamily:'inherit',flexShrink:0}}>✕</button>
                            </div>
                          ))}
                          <div style={{height:'1px',background:'var(--border)',margin:'8px 0 4px'}}/>
                        </div>
                      )}
                      {QURAN_SURAHS.map(s => {
                        const read = quranProgress[s.num] || 0;
                        const pct = Math.round(read / s.ayahs * 100);
                        const done = read >= s.ayahs;
                        const inProgress = read > 0 && !done;
                        return (
                          <div key={s.num} onClick={() => loadQuranSurah(s.num)} style={{display:'flex',alignItems:'center',gap:'12px',padding:'11px 6px',borderBottom:'1px solid var(--border)',cursor:'pointer',WebkitTapHighlightColor:'transparent'}}>
                            <div style={{width:'32px',height:'32px',borderRadius:'8px',background:done?(darkMode?'rgba(0,200,100,0.15)':'rgba(0,160,70,0.12)'):(darkMode?'rgba(0,180,200,0.12)':'rgba(0,130,160,0.1)'),border:`1px solid ${done?(darkMode?'rgba(0,200,100,0.3)':'rgba(0,160,70,0.3)'):(darkMode?'rgba(0,180,200,0.25)':'rgba(0,130,160,0.2)')}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'11px',fontWeight:'700',color:done?(darkMode?'#00c864':'#00963c'):(darkMode?'#00b4c8':'#008598'),flexShrink:0}}>
                              {done ? '✓' : s.num}
                            </div>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontSize:'14px',fontWeight:'700',color:'var(--text)'}}>{s.translit}</div>
                              <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
                                <div style={{fontSize:'11px',color:'var(--muted)'}}>{s.english} · {s.ayahs} ayahs</div>
                                {inProgress && <div style={{fontSize:'10px',fontWeight:'700',color:darkMode?'#00b4c8':'#008598'}}>{pct}%</div>}
                              </div>
                            </div>
                            <div style={{fontSize:'16px',fontWeight:'600',color:'var(--gold)',fontFamily:"'Scheherazade New', serif",direction:'rtl'}}>{s.arabic}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })()}

            {toolView === "fullquran" && !isLandscape && (() => {
              const TOTAL_QURAN_AYAHS = 6236;
              const totalRead = Object.entries(quranProgress).reduce((sum, [num, lastAyah]) => {
                const s = QURAN_SURAHS.find(x => x.num === +num);
                return sum + Math.min(lastAyah, s ? s.ayahs : 0);
              }, 0);
              const pctComplete = Math.round(totalRead / TOTAL_QURAN_AYAHS * 100);
              return (
                <div style={{display:"flex",flexDirection:"column",minHeight:"100%"}}>
                  {/* Header */}
                  <div style={{padding:"14px 16px 10px",borderBottom:"1px solid var(--border)",position:"sticky",top:0,background:"var(--bg)",zIndex:2}}>
                    <div style={{display:"flex",alignItems:"center",gap:"12px",marginBottom:"10px"}}>
                      <button onClick={() => { setToolView("grid"); setQuranSearchQuery(""); setQuranSearchResults(null); setSelectedJuz(0); }} style={{background:"transparent",border:"1px solid var(--border)",borderRadius:"8px",color:"var(--muted)",fontSize:"13px",padding:"6px 14px",cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>← Back</button>
                      <div style={{flex:1}}>
                        <div style={{fontSize:"18px",fontWeight:"800",color:"var(--text)"}}>القرآن الكريم</div>
                        <div style={{fontSize:"11px",color:"var(--muted)"}}>The Noble Quran · 114 Surahs · 30 Juz</div>
                      </div>
                    </div>
                    {/* Progress bar */}
                    <div style={{display:"flex",alignItems:"center",gap:"10px",marginBottom:"10px"}}>
                      <div style={{flex:1,height:"5px",background:"var(--s2)",borderRadius:"3px",overflow:"hidden"}}>
                        <div style={{height:"100%",width:`${pctComplete}%`,background:`linear-gradient(to right, ${darkMode?"#00b4c8":"#008598"}, ${darkMode?"#00d4a0":"#009070"})`,borderRadius:"3px",transition:"width 0.5s"}}/>
                      </div>
                      <div style={{fontSize:"12px",fontWeight:"700",color:darkMode?"#00b4c8":"#008598",flexShrink:0}}>{pctComplete}%</div>
                    </div>
                    {/* Search bar */}
                    <div style={{position:"relative",marginBottom:"8px"}}>
                      <span style={{position:"absolute",left:"12px",top:"50%",transform:"translateY(-50%)",fontSize:"14px",pointerEvents:"none"}}>🔍</span>
                      <input
                        type="text"
                        placeholder="Search surahs, topics, or ayah text..."
                        value={quranSearchQuery}
                        onChange={(e) => searchQuran(e.target.value)}
                        style={{padding:"10px 36px 10px 36px",background:"var(--s2)",border:"1px solid var(--border)",borderRadius:"10px",color:"var(--text)",fontSize:"14px",width:"100%",boxSizing:"border-box",outline:"none",fontFamily:"inherit"}}
                      />
                      {quranSearchQuery && (
                        <button onClick={() => { setQuranSearchQuery(""); setQuranSearchResults(null); }} style={{position:"absolute",right:"10px",top:"50%",transform:"translateY(-50%)",background:"transparent",border:"none",color:"var(--muted)",fontSize:"18px",cursor:"pointer",padding:"0 4px"}}>×</button>
                      )}
                    </div>
                    {/* Juz navigation — horizontal scroll pills */}
                    <div style={{display:"flex",gap:"6px",overflowX:"auto",paddingBottom:"4px",WebkitOverflowScrolling:"touch",scrollbarWidth:"none",msOverflowStyle:"none"}}>
                      <button onClick={() => setSelectedJuz(0)} style={{padding:"5px 12px",borderRadius:"16px",border:"1px solid "+(selectedJuz===0?(darkMode?"rgba(0,180,200,0.5)":"rgba(0,130,160,0.5)"):"var(--border)"),background:selectedJuz===0?(darkMode?"rgba(0,180,200,0.15)":"rgba(0,130,160,0.12)"):"transparent",color:selectedJuz===0?(darkMode?"#00b4c8":"#008598"):"var(--muted)",fontSize:"12px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap",flexShrink:0}}>All</button>
                      {JUZ_START.map(j => (
                        <button key={j.juz} onClick={() => setSelectedJuz(j.juz)} style={{padding:"5px 12px",borderRadius:"16px",border:"1px solid "+(selectedJuz===j.juz?(darkMode?"rgba(0,180,200,0.5)":"rgba(0,130,160,0.5)"):"var(--border)"),background:selectedJuz===j.juz?(darkMode?"rgba(0,180,200,0.15)":"rgba(0,130,160,0.12)"):"transparent",color:selectedJuz===j.juz?(darkMode?"#00b4c8":"#008598"):"var(--muted)",fontSize:"12px",fontWeight:"600",cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap",flexShrink:0}}>Juz {j.juz}</button>
                      ))}
                    </div>
                  </div>
                  <div style={{flex:1,overflowY:"auto",padding:"0 0 80px"}}>
                    {/* Search results */}
                    {quranSearchResults !== null ? (
                      <div style={{padding:"8px 12px"}}>
                        {quranSearchLoading && (
                          <div style={{textAlign:"center",padding:"12px",color:"var(--muted)",fontSize:"13px"}}>
                            <span className="spinner" style={{display:"inline-block",width:"16px",height:"16px",verticalAlign:"middle",marginRight:"8px"}}/>Searching ayahs...
                          </div>
                        )}
                        {quranSearchResults.length === 0 && !quranSearchLoading && (
                          <div style={{textAlign:"center",padding:"40px 20px",color:"var(--muted)"}}>
                            <div style={{fontSize:"32px",marginBottom:"8px"}}>🔍</div>
                            <div style={{fontSize:"14px"}}>No results for "{quranSearchQuery}"</div>
                          </div>
                        )}
                        {quranSearchResults.filter(r => r.type === "surah").length > 0 && (
                          <div style={{marginBottom:"12px"}}>
                            <div style={{fontSize:"11px",fontWeight:"700",color:"var(--muted)",letterSpacing:"1px",marginBottom:"6px"}}>SURAHS</div>
                            {quranSearchResults.filter(r => r.type === "surah").map(r => (
                              <div key={r.surah.num} onClick={() => { loadQuranSurah(r.surah.num); setQuranSearchQuery(""); setQuranSearchResults(null); }} style={{display:"flex",alignItems:"center",gap:"12px",padding:"11px 6px",borderBottom:"1px solid var(--border)",cursor:"pointer"}}>
                                <div style={{width:"36px",height:"36px",borderRadius:"10px",background:darkMode?"rgba(0,180,200,0.12)":"rgba(0,130,160,0.1)",border:"1px solid "+(darkMode?"rgba(0,180,200,0.25)":"rgba(0,130,160,0.2)"),display:"flex",alignItems:"center",justifyContent:"center",fontSize:"12px",fontWeight:"700",color:darkMode?"#00b4c8":"#008598",flexShrink:0}}>{r.surah.num}</div>
                                <div style={{flex:1,minWidth:0}}>
                                  <div style={{fontSize:"15px",fontWeight:"700",color:"var(--text)"}}>{r.surah.translit}</div>
                                  <div style={{fontSize:"12px",color:"var(--muted)"}}>{r.surah.english} · {r.surah.ayahs} ayahs</div>
                                </div>
                                <div style={{fontSize:"17px",fontWeight:"600",color:"var(--gold)",fontFamily:"'Scheherazade New', serif",direction:"rtl"}}>{r.surah.arabic}</div>
                              </div>
                            ))}
                          </div>
                        )}
                        {quranSearchResults.filter(r => r.type === "ayah").length > 0 && (
                          <div>
                            <div style={{fontSize:"11px",fontWeight:"700",color:"var(--muted)",letterSpacing:"1px",marginBottom:"6px"}}>AYAHS</div>
                            {quranSearchResults.filter(r => r.type === "ayah").map((r, i) => (
                              <div key={i} onClick={() => { loadQuranSurah(r.surahNum, r.ayahNum); setQuranSearchQuery(""); setQuranSearchResults(null); }} style={{padding:"12px 10px",borderBottom:"1px solid var(--border)",cursor:"pointer"}}>
                                <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"6px"}}>
                                  <div style={{fontSize:"11px",fontWeight:"700",color:darkMode?"#00b4c8":"#008598",background:darkMode?"rgba(0,180,200,0.12)":"rgba(0,130,160,0.1)",padding:"3px 8px",borderRadius:"6px"}}>{r.surahName} {r.surahNum}:{r.ayahNum}</div>
                                </div>
                                <div style={{fontSize:"13px",color:"var(--text)",lineHeight:1.6}}>{r.text.length > 200 ? r.text.slice(0, 200) + "..." : r.text}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                    <>
                    {/* Bookmarks section */}
                    {bookmarks.length > 0 && selectedJuz === 0 && (
                      <div style={{padding:"12px 12px 4px"}}>
                        <div style={{fontSize:"11px",fontWeight:"700",color:"var(--muted)",letterSpacing:"1px",marginBottom:"8px"}}>BOOKMARKS</div>
                        {bookmarks.map((bm, i) => (
                          <div key={i} style={{display:"flex",alignItems:"center",gap:"10px",padding:"10px 12px",background:"var(--s2)",border:"1px solid var(--border)",borderRadius:"10px",marginBottom:"6px"}}>
                            <div style={{fontSize:"18px",flexShrink:0}}>🔖</div>
                            <div onClick={() => loadQuranSurah(bm.num, bm.ayahNum || null)} style={{flex:1,minWidth:0,cursor:"pointer",WebkitTapHighlightColor:"transparent"}}>
                              <div style={{fontSize:"13px",fontWeight:"700",color:"var(--text)"}}>{bm.translit}</div>
                              <div style={{fontSize:"11px",color:"var(--muted)"}}>{bm.ayahNum ? `Ayah ${bm.ayahNum}` : bm.page != null ? `Page ${bm.page + 1}` : ""}{bm.totalPages ? ` of ${bm.totalPages}` : ""} · {bm.date}</div>
                            </div>
                            <div style={{fontSize:"15px",color:"var(--gold)",fontFamily:"'Scheherazade New', serif",direction:"rtl",flexShrink:0}}>{bm.arabic}</div>
                            <button onClick={() => { const nb = bookmarks.filter((_,j) => j !== i); setBookmarks(nb); localStorage.setItem('quranBookmarks', JSON.stringify(nb)); }} style={{background:"transparent",border:"1px solid rgba(255,100,100,0.3)",borderRadius:"6px",color:"rgba(255,100,100,0.7)",fontSize:"12px",padding:"4px 8px",cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>✕</button>
                          </div>
                        ))}
                        <div style={{height:"1px",background:"var(--border)",margin:"8px 0 4px"}}/>
                      </div>
                    )}
                    {/* Surah list — filtered by Juz */}
                    <div style={{padding:"0 12px"}}>
                      {(() => {
                        let surahs = QURAN_SURAHS;
                        if (selectedJuz > 0) {
                          const juzEntry = JUZ_START.find(j => j.juz === selectedJuz);
                          const nextJuz = JUZ_START.find(j => j.juz === selectedJuz + 1);
                          const startSurah = juzEntry ? juzEntry.surah : 1;
                          const endSurah = nextJuz ? nextJuz.surah - 1 : 114;
                          surahs = QURAN_SURAHS.filter(s => s.num >= startSurah && s.num <= endSurah);
                        }
                        return surahs.map(s => {
                          const read = quranProgress[s.num] || 0;
                          const pct = Math.round(read / s.ayahs * 100);
                          const done = read >= s.ayahs;
                          const inProgress = read > 0 && !done;
                          return (
                            <div key={s.num} onClick={() => loadQuranSurah(s.num)} style={{display:"flex",alignItems:"center",gap:"12px",padding:"11px 6px",borderBottom:"1px solid var(--border)",cursor:"pointer",WebkitTapHighlightColor:"transparent"}}>
                              <div style={{width:"36px",height:"36px",borderRadius:"10px",background:done?(darkMode?"rgba(0,200,100,0.15)":"rgba(0,160,70,0.12)"):(darkMode?"rgba(0,180,200,0.12)":"rgba(0,130,160,0.1)"),border:`1px solid ${done?(darkMode?"rgba(0,200,100,0.3)":"rgba(0,160,70,0.3)"):(darkMode?"rgba(0,180,200,0.25)":"rgba(0,130,160,0.2)")}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:"12px",fontWeight:"700",color:done?(darkMode?"#00c864":"#00963c"):(darkMode?"#00b4c8":"#008598"),flexShrink:0}}>
                                {done ? "✓" : s.num}
                              </div>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{fontSize:"15px",fontWeight:"700",color:"var(--text)"}}>{s.translit}</div>
                                <div style={{display:"flex",alignItems:"center",gap:"6px"}}>
                                  <div style={{fontSize:"12px",color:"var(--muted)"}}>{s.english} · {s.ayahs} ayahs</div>
                                  {inProgress && <div style={{fontSize:"10px",fontWeight:"700",color:darkMode?"#00b4c8":"#008598"}}>{pct}%</div>}
                                </div>
                                {inProgress && (
                                  <div style={{height:"2px",background:"var(--s2)",borderRadius:"1px",marginTop:"4px",overflow:"hidden"}}>
                                    <div style={{height:"100%",width:`${pct}%`,background:darkMode?"#00b4c8":"#008598",borderRadius:"1px"}}/>
                                  </div>
                                )}
                              </div>
                              <div style={{fontSize:"17px",fontWeight:"600",color:"var(--gold)",fontFamily:"'Scheherazade New', serif",direction:"rtl"}}>{s.arabic}</div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                    </>
                    )}
                  </div>
                </div>
              );
            })()}

            {toolView === "fullquranreading" && (() => {
              const surahMeta = QURAN_SURAHS.find(s => s.num === quranReadNum) || QURAN_SURAHS[0];
              const ayahs = quranReadCache[quranReadNum] || [];

              // Convert number to Eastern Arabic numerals
              const toEastern = n => String(n).replace(/\d/g, d => String.fromCharCode(0x0660 + +d));
              // Arabic ornate brackets for ayah markers
              const ayahMarker = n => `﴿${toEastern(n)}﴾`;

              // Paginate ayahs for book view (target ~300 Arabic chars per page)
              const paginateAyahs = (arr, target = 300) => {
                const pages = [];
                let page = [], chars = 0;
                for (const a of arr) {
                  if (chars + a.arabic.length > target && page.length > 0) {
                    pages.push(page); page = [a]; chars = a.arabic.length;
                  } else { page.push(a); chars += a.arabic.length; }
                }
                if (page.length) pages.push(page);
                return pages.length ? pages : [[]];
              };
              const pages = paginateAyahs(ayahs);
              const totalPages = pages.length;

              // Update reading progress
              const updateProgress = (pageIdx) => {
                const pageAyahs = pages[pageIdx] || [];
                if (!pageAyahs.length) return;
                const lastAyah = pageAyahs[pageAyahs.length - 1].number;
                const prev = quranProgress[quranReadNum] || 0;
                if (lastAyah > prev) {
                  const np = { ...quranProgress, [quranReadNum]: lastAyah };
                  setQuranProgress(np);
                  localStorage.setItem('quranProgress', JSON.stringify(np));
                  // Update reading streak
                  const today = new Date().toISOString().slice(0, 10);
                  if (readingStreak.lastDate !== today) {
                    const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
                    const newCount = readingStreak.lastDate === yesterday ? readingStreak.count + 1 : 1;
                    const ns = { count: newCount, lastDate: today };
                    setReadingStreak(ns);
                    localStorage.setItem("readingStreak", JSON.stringify(ns));
                  }
                }
              };

              const goToPage = (newPage) => {
                const clamped = Math.max(0, Math.min(newPage, totalPages - 1));
                setQuranCurrentPage(clamped);
                updateProgress(clamped);
              };

              // Bookmark helpers — portrait mode: one bookmark per surah, saves exact ayah
              const isBookmarked = bookmarks.some(b => b.num === quranReadNum);
              const toggleBookmark = () => {
                let newBms;
                if (isBookmarked) {
                  newBms = bookmarks.filter(b => b.num !== quranReadNum);
                } else {
                  // Find first visible ayah in the scroll container
                  let visibleAyah = ayahs[0]?.number || 1;
                  if (toolViewPanelRef.current) {
                    const panelTop = toolViewPanelRef.current.getBoundingClientRect().top;
                    for (const a of ayahs) {
                      const el = document.getElementById(`q-ayah-${a.number}`);
                      if (el && el.getBoundingClientRect().top >= panelTop + 80) {
                        visibleAyah = a.number;
                        break;
                      }
                    }
                  }
                  newBms = [...bookmarks, {
                    num: quranReadNum,
                    translit: surahMeta.translit,
                    arabic: surahMeta.arabic,
                    ayahNum: visibleAyah,
                    date: new Date().toLocaleDateString('en-GB', {day:'numeric',month:'short',year:'numeric'}),
                  }];
                }
                setBookmarks(newBms);
                localStorage.setItem('quranBookmarks', JSON.stringify(newBms));
              };

              // Touch swipe state for book page turning
              let bookTouchStartX = null;

              const pm = quranPaperMode;
              const paperBg = "#f9f5e6";
              const paperInk = "#1c1208";
              const paperMuted = "#6b5a3e";
              const paperGold = "#8b6914";
              const paperBorder = "#e0d5b8";
              const bg = pm ? paperBg : "var(--bg)";
              const ink = pm ? paperInk : "var(--text)";
              const muted = pm ? paperMuted : "var(--muted)";
              const gold = pm ? paperGold : "var(--gold)";
              const border = pm ? paperBorder : "var(--border)";

              // Landscape book is rendered outside the app container to bypass willChange:transform stacking context
              if (isLandscape) return null;

              // ── PORTRAIT MODE ────────────────────────────────────────────────
              return (
                <div style={{display:"flex",flexDirection:"column",minHeight:"100%",background:bg,transition:"background 0.3s"}}>
                  {/* Sticky header */}
                  <div style={{padding:"12px 16px",borderBottom:`1px solid ${border}`,display:"flex",alignItems:"center",gap:"10px",position:"sticky",top:0,background:bg,zIndex:2}}>
                    <button onClick={() => setToolView("fullquran")} style={{background:"transparent",border:`1px solid ${border}`,borderRadius:"8px",color:muted,fontSize:"13px",padding:"6px 12px",cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>← Back</button>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:"17px",fontWeight:"700",color:gold,fontFamily:"'Scheherazade New', serif",direction:"rtl",textAlign:"right",lineHeight:1.4}}>{surahMeta.arabic}</div>
                      <div style={{fontSize:"11px",color:muted}}>{surahMeta.translit} · {surahMeta.ayahs} ayahs</div>
                    </div>
                    {/* Font size controls */}
                    <div style={{display:"flex",alignItems:"center",gap:"2px",flexShrink:0}}>
                      <button onClick={() => { const s = Math.max(18, quranFontSize - 2); setQuranFontSize(s); localStorage.setItem("quranFontSize", s); }} style={{background:"transparent",border:`1px solid ${border}`,borderRadius:"6px 0 0 6px",color:muted,fontSize:"14px",padding:"4px 8px",cursor:"pointer",fontFamily:"inherit",fontWeight:"700"}}>A-</button>
                      <button onClick={() => { const s = Math.min(44, quranFontSize + 2); setQuranFontSize(s); localStorage.setItem("quranFontSize", s); }} style={{background:"transparent",border:`1px solid ${border}`,borderRadius:"0 6px 6px 0",color:muted,fontSize:"14px",padding:"4px 8px",cursor:"pointer",fontFamily:"inherit",fontWeight:"700"}}>A+</button>
                    </div>
                    {/* Bookmark */}
                    <button onClick={toggleBookmark} style={{background:isBookmarked?(pm?"rgba(139,105,20,0.2)":"rgba(0,180,200,0.15)"):"transparent",border:`1px solid ${isBookmarked?gold:border}`,borderRadius:"8px",fontSize:"16px",padding:"5px 8px",cursor:"pointer",color:isBookmarked?gold:muted}}>🔖</button>
                    {/* Paper mode toggle */}
                    <button onClick={() => setQuranPaperMode(p => !p)} style={{background:pm?"#e8dfc0":"transparent",border:`1px solid ${border}`,borderRadius:"8px",color:pm?paperGold:muted,fontSize:"13px",padding:"6px 10px",cursor:"pointer",fontFamily:"inherit",flexShrink:0,fontWeight:pm?"700":"400"}}>📄</button>
                  </div>

                  {quranReadLoading ? (
                    <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:"12px",padding:"40px",background:bg}}>
                      <div style={{width:"32px",height:"32px",borderRadius:"50%",border:`3px solid ${pm?"rgba(139,105,20,0.2)":"rgba(0,180,200,0.2)"}`,borderTopColor:gold,animation:"spin 0.8s linear infinite"}}/>
                      <div style={{fontSize:"14px",color:muted}}>Loading {surahMeta.translit}…</div>
                    </div>
                  ) : pm ? (
                    /* ── PAPER MODE ── */
                    <div style={{flex:1,overflowY:"auto",background:paperBg}}>
                      <div style={{textAlign:"center",padding:"24px 20px 8px"}}>
                        <div style={{fontSize:"13px",color:paperMuted,letterSpacing:"3px",marginBottom:"12px",fontVariant:"small-caps"}}>{surahMeta.translit.toUpperCase()}</div>
                        <div style={{display:"flex",alignItems:"center",gap:"8px",justifyContent:"center",marginBottom:"16px"}}>
                          <div style={{flex:1,height:"1px",background:`linear-gradient(to right, transparent, ${paperBorder})`}}/>
                          <div style={{fontSize:"18px",color:paperGold}}>❧</div>
                          <div style={{flex:1,height:"1px",background:`linear-gradient(to left, transparent, ${paperBorder})`}}/>
                        </div>
                        {quranReadNum !== 9 && quranReadNum !== 1 && (
                          <div style={{fontSize:"30px",color:paperGold,fontFamily:"'Scheherazade New', serif",direction:"rtl",lineHeight:2,marginBottom:"8px"}}>
                            بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
                          </div>
                        )}
                      </div>
                      <div style={{padding:"0 20px 16px",direction:"rtl",textAlign:"justify",fontSize:`${quranFontSize}px`,lineHeight:2.2,color:paperInk,fontFamily:"'Scheherazade New', serif"}}>
                        {ayahs.map(a => (
                          <span key={a.number}>
                            {a.arabic}
                            <span style={{color:paperGold,fontSize:"20px",fontFamily:"'Scheherazade New', serif"}}> {ayahMarker(a.number)} </span>
                          </span>
                        ))}
                      </div>
                      <div style={{background:"#f2ead0",borderTop:`1px solid ${paperBorder}`,padding:"20px 20px 100px"}}>
                        <div style={{fontSize:"11px",color:paperMuted,letterSpacing:"2px",marginBottom:"16px",fontVariant:"small-caps",textAlign:"center"}}>Translation · Sahih International</div>
                        {ayahs.map(a => (
                          <div id={`q-ayah-${a.number}`} key={a.number} style={{display:"flex",gap:"10px",marginBottom:"14px",alignItems:"flex-start"}}>
                            <div style={{minWidth:"24px",height:"24px",borderRadius:"50%",border:`1px solid ${paperBorder}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:"10px",fontWeight:"700",color:paperGold,flexShrink:0,marginTop:"2px"}}>{a.number}</div>
                            <div style={{fontSize:"14px",color:paperMuted,lineHeight:1.75}}>{a.translation}</div>
                          </div>
                        ))}
                      </div>
                      {/* Prev / Next surah */}
                      <div style={{display:"flex",gap:"10px",padding:"20px 20px 100px",borderTop:`1px solid ${paperBorder}`}}>
                        {quranReadNum > 1 && (
                          <button onClick={() => loadQuranSurah(quranReadNum - 1)} style={{flex:1,padding:"12px",background:"transparent",border:`1px solid ${paperBorder}`,borderRadius:"10px",color:paperMuted,fontSize:"13px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>
                            ← {QURAN_SURAHS.find(s => s.num === quranReadNum - 1)?.translit}
                          </button>
                        )}
                        {quranReadNum < 114 && (
                          <button onClick={() => loadQuranSurah(quranReadNum + 1)} style={{flex:1,padding:"12px",background:"transparent",border:`1px solid ${paperBorder}`,borderRadius:"10px",color:paperMuted,fontSize:"13px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit",textAlign:"right"}}>
                            {QURAN_SURAHS.find(s => s.num === quranReadNum + 1)?.translit} →
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    /* ── SCREEN MODE ── */
                    <div style={{flex:1,overflowY:"auto",padding:"0 16px 80px"}}>
                      {quranReadNum !== 9 && quranReadNum !== 1 && (
                        <div style={{textAlign:"center",padding:"20px 0 10px",fontSize:"26px",color:gold,fontFamily:"'Scheherazade New', serif",direction:"rtl",lineHeight:2}}>
                          بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
                        </div>
                      )}
                      {ayahs.map(a => (
                        <div id={`q-ayah-${a.number}`} key={a.number} style={{borderBottom:`1px solid ${border}`,paddingBottom:"18px",marginBottom:"18px"}}>
                          <div style={{display:"flex",justifyContent:"flex-end",marginBottom:"8px"}}>
                            <div style={{width:"28px",height:"28px",borderRadius:"50%",background:darkMode?"rgba(0,180,200,0.12)":"rgba(0,130,160,0.1)",border:`1px solid ${darkMode?"rgba(0,180,200,0.25)":"rgba(0,130,160,0.2)"}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:"11px",fontWeight:"700",color:darkMode?"#00b4c8":"#008598"}}>{a.number}</div>
                          </div>
                          <div style={{fontSize:`${quranFontSize}px`,color:ink,fontFamily:"'Scheherazade New', serif",direction:"rtl",textAlign:"right",lineHeight:2,marginBottom:"10px"}}>{a.arabic}</div>
                          <div style={{fontSize:"13px",color:muted,lineHeight:1.7,fontStyle:"italic"}}>{a.translation}</div>
                        </div>
                      ))}
                      {/* Prev / Next surah */}
                      <div style={{display:"flex",gap:"10px",padding:"8px 0 20px",borderTop:`1px solid ${border}`}}>
                        {quranReadNum > 1 && (
                          <button onClick={() => loadQuranSurah(quranReadNum - 1)} style={{flex:1,padding:"12px",background:"transparent",border:`1px solid ${border}`,borderRadius:"10px",color:muted,fontSize:"13px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>
                            ← {QURAN_SURAHS.find(s => s.num === quranReadNum - 1)?.translit}
                          </button>
                        )}
                        {quranReadNum < 114 && (
                          <button onClick={() => loadQuranSurah(quranReadNum + 1)} style={{flex:1,padding:"12px",background:"transparent",border:`1px solid ${border}`,borderRadius:"10px",color:muted,fontSize:"13px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit",textAlign:"right"}}>
                            {QURAN_SURAHS.find(s => s.num === quranReadNum + 1)?.translit} →
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {toolView === "tasbeeh" && <Tasbeeh darkMode={darkMode} />}
            {toolView === "takbeer" && <TakbeerCounter darkMode={darkMode} />}

            {toolView === "khatm" && <Lazy><Khatm darkMode={darkMode} userId={userId} userName={userName} userSurname={userSurname} backend={BACKEND} initialCode={khatmJoinCode} hijri={hijri} initialDetail={khatmGuestDetail} shareCardStyle={shareCardStyle} /></Lazy>}

            {toolView === "zakat" && <Lazy><ZakatCalculator darkMode={darkMode} /></Lazy>}

            {toolView === "calorie" && <CalorieCounter darkMode={darkMode} onBack={() => setToolView("grid")} onLogoClick={openFanMenu} prayerTimes={prayerTimes} />}

            {toolView === "moodguide" && <Lazy><MoodGuide darkMode={darkMode} onBack={() => setToolView("grid")} /></Lazy>}

            {toolView === "tajweed" && <Lazy><TajweedHelper darkMode={darkMode} onBack={() => setToolView("grid")} /></Lazy>}

            {toolView === "hajj" && <Lazy><HajjCompanion darkMode={darkMode} onBack={() => setToolView("grid")} /></Lazy>}
            {toolView === "janazah" && <Lazy><JanazahGuide darkMode={darkMode} onBack={() => setToolView("grid")} /></Lazy>}

            {toolView === "muraqabah" && <Lazy><Muraqabah darkMode={darkMode} onBack={() => setToolView("grid")} userName={userName} onOpenTool={(view) => setToolView(view)} /></Lazy>}

            {toolView === "fasting" && <Lazy><FastingTracker darkMode={darkMode} onBack={() => setToolView("grid")} prayerTimes={prayerTimes} /></Lazy>}

            {toolView === "duajournal" && <Lazy><DuaJournal darkMode={darkMode} onBack={() => setToolView("grid")} /></Lazy>}

            {toolView === "cardgenerator" && <Lazy><CardGenerator darkMode={darkMode} userName={userName} onBack={() => setToolView("grid")} /></Lazy>}

            {toolView === "tawbah" && (
              <div className="tawbah-screen">
                <div className="tawbah-header">
                  <button onClick={() => { setToolView("grid"); setTawbahText(""); setTawbahPhase("idle"); }}>‹</button>
                  <h2>Tawbah</h2>
                </div>
                <div className="tawbah-body">
                  {tawbahPhase !== "released" ? (
                    <>
                      <div className="tawbah-verse-block" style={{opacity: tawbahPhase === "releasing" ? 0 : 1, transition:"opacity 0.6s"}}>
                        <span className="tawbah-arabic-verse">قُل يَا عِبَادِيَ الَّذِينَ أَسْرَفُوا عَلَىٰ أَنفُسِهِمْ</span>
                        <span className="tawbah-arabic-verse" style={{fontSize:"19px"}}>لَا تَقْنَطُوا مِن رَّحْمَةِ اللَّهِ ۚ إِنَّ اللَّهَ يَغْفِرُ الذُّنُوبَ جَمِيعًا</span>
                        <span className="tawbah-verse-trans">"Do not despair of the mercy of Allah. Indeed, Allah forgives all sins." — Az-Zumar 39:53</span>
                      </div>
                      <div className={`tawbah-write-area${tawbahPhase === "releasing" ? " releasing" : ""}`}
                        onAnimationEnd={() => {
                          setTawbahPhase("released");
                          setTawbahText("");
                          setTimeout(() => setTawbahPhase("idle"), 4000);
                        }}>
                        <textarea
                          className="tawbah-textarea"
                          placeholder={"Write what is in your heart…\n\nNothing you write here is saved or ever leaves this screen."}
                          value={tawbahText}
                          onChange={e => setTawbahText(e.target.value)}
                          disabled={tawbahPhase === "releasing"}
                          rows={7}
                        />
                        <div className="tawbah-privacy">🔒 Private · never stored · never transmitted</div>
                      </div>
                      <button
                        className="tawbah-btn"
                        disabled={!tawbahText.trim() || tawbahPhase === "releasing"}
                        onClick={() => setTawbahPhase("releasing")}
                      >
                        أستغفر الله وأتوب إليه
                        <div style={{fontSize:"12px",fontWeight:400,marginTop:4,opacity:0.8}}>I seek Allah's forgiveness and repent to Him</div>
                      </button>
                    </>
                  ) : (
                    <div className="tawbah-released-state">
                      <div className="tawbah-glow-ring">🕊️</div>
                      <div className="tawbah-released-arabic">إِنَّ اللَّهَ يَغْفِرُ الذُّنُوبَ جَمِيعًا</div>
                      <div className="tawbah-released-msg">Indeed, Allah forgives all sins.</div>
                      <div className="tawbah-released-sub">Your words existed only between you and Him.{"\n"}He is As-Sami' — the All-Hearing.</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {toolView === "egzon" && <EgzonRecitations darkMode={darkMode} onBack={() => setToolView("grid")} />}

            {toolView === "dossari" && <DossariRecitations darkMode={darkMode} onBack={() => setToolView("grid")} />}

            {toolView === "islamiccalendar" && <Lazy><IslamicCalendar darkMode={darkMode} /></Lazy>}

            {/* Wasiyyah & Mirath — PIN/biometric auth gate */}
            {toolView === "mirath" && (() => {
              const accent = darkMode ? "#c8b464" : "#a08228";
              const accentFaint = darkMode ? "rgba(200,180,100,0.08)" : "rgba(160,130,40,0.06)";
              const accentBorder = darkMode ? "rgba(200,180,100,0.18)" : "rgba(160,130,40,0.15)";
              const pinInputStyle = {width:"48px",height:"56px",borderRadius:"12px",border:`2px solid ${accentBorder}`,background:darkMode?"rgba(255,255,255,0.04)":"rgba(0,0,0,0.03)",color:"var(--text)",fontSize:"22px",fontWeight:"800",textAlign:"center",outline:"none",caretColor:accent};
              const hasPinStored = !!localStorage.getItem("mirath_pin_hash");
              // ── Auth screen (not yet unlocked) ──
              if (!mirathUnlocked) {
                // Go straight to PIN entry — no auto-biometric
                if (!mirathAuthPhase) setTimeout(() => setMirathAuthPhase(hasPinStored ? "enter" : "setup"), 50);
                const handlePinSetup = async () => {
                  if (mirathPin.length < 4) { setMirathPinError("PIN must be at least 4 digits"); return; }
                  if (mirathAuthPhase === "setup") { setMirathAuthPhase("confirm"); setMirathPinError(""); return; }
                  if (mirathAuthPhase === "confirm") {
                    if (mirathPin !== mirathPinConfirm) { setMirathPinError("PINs don't match"); setMirathPinConfirm(""); return; }
                    const salt = getMirathSalt();
                    const hash = await mirathHashPin(mirathPin, salt);
                    localStorage.setItem("mirath_pin_hash", hash);
                    // Store raw key for biometric unlock path (HTTPS only)
                    if (HAS_SUBTLE) {
                      const ek = await mirathDeriveKey(mirathPin, salt, true);
                      if (ek) { const exported = await crypto.subtle.exportKey("raw", ek); localStorage.setItem("mirath_wrapped_key", btoa(String.fromCharCode(...new Uint8Array(exported)))); }
                    }
                    await mirathUnlock(mirathPin);
                    // Offer biometric enrollment
                    if (mirathBioAvailable) {
                      try {
                        if (isNative()) {
                          await mirathRegisterBio(); // native: just stores flag
                        } else if (typeof window.PublicKeyCredential !== "undefined" && window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable) {
                          const available = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
                          if (available) await mirathRegisterBio();
                        }
                      } catch {}
                    }
                    return;
                  }
                };
                const handlePinEntry = async () => {
                  if (mirathPin.length < 4) { setMirathPinError("Enter your PIN"); return; }
                  const salt = getMirathSalt();
                  const hash = await mirathHashPin(mirathPin, salt);
                  if (hash !== localStorage.getItem("mirath_pin_hash")) { setMirathPinError("Incorrect PIN"); setMirathPin(""); return; }
                  // Store wrapped key for biometric path (HTTPS only)
                  if (HAS_SUBTLE) {
                    const ek = await mirathDeriveKey(mirathPin, salt, true);
                    if (ek) { const exported = await crypto.subtle.exportKey("raw", ek); localStorage.setItem("mirath_wrapped_key", btoa(String.fromCharCode(...new Uint8Array(exported)))); }
                  }
                  await mirathUnlock(mirathPin);
                };
                return (
                  <div style={{display:"flex",flexDirection:"column",height:"100%",background:"var(--bg)"}}>
                    <div style={{display:"flex",alignItems:"center",gap:"12px",padding:"16px 20px 12px",borderBottom:`1px solid ${accentBorder}`}}>
                      <button onClick={() => navTransition(() => setToolView("grid"))} style={{background:"none",border:"none",color:"var(--text)",cursor:"pointer",padding:"4px",display:"flex",alignItems:"center",justifyContent:"center",borderRadius:"8px",width:"32px",height:"32px"}}>
                        <svg width="10" height="17" viewBox="0 0 10 17" fill="none"><path d="M9 1L1 8.5L9 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </button>
                      <div>
                        <div style={{fontSize:"18px",fontWeight:"800",color:"var(--text)"}}>Wasiyyah & Mirath</div>
                        <div style={{fontSize:"11px",color:"var(--muted)",fontWeight:"600"}}>Islamic Estate Planning</div>
                      </div>
                    </div>
                    <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"0 32px"}}>
                      {/* Lock icon */}
                      <div style={{width:"72px",height:"72px",borderRadius:"50%",background:accentFaint,border:`2px solid ${accentBorder}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:"32px",marginBottom:"20px"}}>🔒</div>
                      <div style={{fontSize:"16px",fontWeight:"800",color:"var(--text)",marginBottom:"6px",textAlign:"center"}}>
                        {mirathAuthPhase === "setup" ? "Set Up Security PIN" : mirathAuthPhase === "confirm" ? "Confirm Your PIN" : "Enter PIN"}
                      </div>
                      <div style={{fontSize:"12px",color:"var(--muted)",textAlign:"center",lineHeight:1.5,marginBottom:"24px"}}>
                        {mirathAuthPhase === "setup" ? "Protect your estate data with a PIN. This encrypts your Family Info Pack and secures access to your will." : mirathAuthPhase === "confirm" ? "Enter the same PIN again to confirm." : "Enter your PIN to access estate planning."}
                      </div>
                      {/* PIN input — visible field styled as dot boxes */}
                      <div style={{position:"relative",width:"220px",height:"52px",marginBottom:"8px"}}>
                        <input id="mirathPinInput" ref={el => { if (el && (mirathAuthPhase === "setup" || mirathAuthPhase === "confirm")) setTimeout(() => el.focus(), 300); }} type="tel" inputMode="numeric" maxLength={6} value={mirathAuthPhase === "confirm" ? mirathPinConfirm : mirathPin} onChange={e => { const v = e.target.value.replace(/\D/g,"").slice(0,6); mirathAuthPhase === "confirm" ? setMirathPinConfirm(v) : setMirathPin(v); setMirathPinError(""); }} onKeyDown={e => { if (e.key === "Enter") mirathAuthPhase === "enter" ? handlePinEntry() : handlePinSetup(); }} style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",opacity:0,fontSize:"24px",zIndex:2,caretColor:"transparent"}} />
                        <div onClick={() => { if (mirathAuthPhase !== "enter") document.getElementById("mirathPinInput")?.focus(); }} style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",display:"flex",gap:"10px",alignItems:"center",justifyContent:"center",zIndex:1}}>
                          {[0,1,2,3,4,5].map(i => (
                            <div key={i} style={{width:"14px",height:"14px",borderRadius:"50%",border:`2px solid ${accent}`,background:i < (mirathAuthPhase === "confirm" ? mirathPinConfirm : mirathPin).length ? accent : "transparent",transition:"all 0.15s"}} />
                          ))}
                        </div>
                      </div>
                      {mirathPinError && <div style={{fontSize:"12px",color:"#f44336",marginTop:"4px",marginBottom:"8px"}}>{mirathPinError}</div>}
                      <button onClick={mirathAuthPhase === "enter" ? handlePinEntry : handlePinSetup} style={{marginTop:"16px",padding:"12px 40px",borderRadius:"12px",border:"none",background:accent,color:darkMode?"#000":"#fff",fontSize:"14px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit"}}>
                        {mirathAuthPhase === "setup" ? "Next" : mirathAuthPhase === "confirm" ? "Set PIN & Continue" : "Unlock"}
                      </button>
                      {/* Optional Face ID button — native: always show if bio available; web: only if enrolled */}
                      {mirathAuthPhase === "enter" && (isNative() ? mirathBioAvailable : !!localStorage.getItem("mirath_bio_cred")) && (
                        <button onClick={async (e) => {
                          e.preventDefault();
                          // Blur any focused input to dismiss keyboard
                          document.activeElement?.blur();
                          try {
                            const ok = await mirathAuthBio();
                            if (ok) {
                              const wk = localStorage.getItem("mirath_wrapped_key");
                              if (wk && HAS_SUBTLE) { try { const raw = Uint8Array.from(atob(wk), c => c.charCodeAt(0)); mirathKeyRef.current = await crypto.subtle.importKey("raw", raw, { name: "AES-GCM" }, false, ["encrypt","decrypt"]); setMirathUnlocked(true); setMirathAuthPhase(""); return; } catch {} }
                              // Biometric passed — unlock even without wrapped key (native doesn't need encryption key for bio)
                              if (isNative()) { setMirathUnlocked(true); setMirathAuthPhase(""); return; }
                            }
                          } catch (err) {
                            setMirathPinError("Face ID failed — use PIN");
                          }
                        }} style={{marginTop:"16px",padding:"14px 32px",borderRadius:"14px",border:`2px solid ${accent}`,background:darkMode?"rgba(200,180,100,0.1)":"rgba(160,130,40,0.08)",color:accent,fontSize:"15px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:"8px"}}>
                          <span style={{fontSize:"20px"}}>🔓</span> Use Face ID
                        </button>
                      )}
                      {/* Tap PIN dots to bring up keyboard on enter screen */}
                      {mirathAuthPhase === "enter" && (
                        <button onClick={() => document.getElementById("mirathPinInput")?.focus()} style={{marginTop:"8px",background:"none",border:"none",color:"var(--muted)",fontSize:"12px",cursor:"pointer",fontFamily:"inherit",textDecoration:"underline"}}>
                          or enter PIN
                        </button>
                      )}
                    </div>
                    {/* Security footer */}
                    <div style={{padding:"16px 32px 24px",textAlign:"center"}}>
                      <div style={{fontSize:"10px",color:"var(--muted)",lineHeight:1.5}}>
                        Your data is encrypted with AES-256 and never leaves your device. Only you can unlock it with your PIN or biometrics.
                      </div>
                    </div>
                  </div>
                );
              }
              // ── Unlocked — show sub-grid ──
              const mirathTools = [
                { key:"faraid", icon:"📊", name:"Faraid Calculator", nameAr:"فرائض", desc:"Calculate Islamic inheritance shares", color: darkMode ? "#4caf50" : "#2e7d32" },
                ...(userFeatures.islamicwill === true ? [{ key:"islamicwill", icon:"📜", name:"Islamic Will", nameAr:"وصية", desc:"Create your Shari'ah-compliant will", color: darkMode ? "#c8b464" : "#a08228" }] : []),
                { key:"familyinfo", icon:"📋", name:"Family Info Pack", nameAr:"معلومات", desc:"Critical info your family needs", color: darkMode ? "#00c9a7" : "#009e87" },
                ...(sharedFipInbox.length > 0 ? [{ key:"sharedfip", icon:"📥", name:`Shared With You${sharedFipInbox.length > 1 ? ` (${sharedFipInbox.length})` : ""}`, nameAr:"مُشارك", desc:"Family Info Packs shared with you", color: darkMode ? "#ff9800" : "#e65100" }] : []),
              ];
              return (
                <div style={{display:"flex",flexDirection:"column",height:"100%",background:"var(--bg)",overflowY:"auto"}}>
                  <div style={{display:"flex",alignItems:"center",gap:"12px",padding:"16px 20px 12px",position:"sticky",top:0,background:"var(--bg)",zIndex:10,borderBottom:`1px solid ${darkMode?"rgba(200,180,100,0.15)":"rgba(160,130,40,0.12)"}`}}>
                    <button onClick={() => { mirathLock(); navTransition(() => setToolView("grid")); }} style={{background:"none",border:"none",color:"var(--text)",cursor:"pointer",padding:"4px",display:"flex",alignItems:"center",justifyContent:"center",borderRadius:"8px",width:"32px",height:"32px"}}>
                      <svg width="10" height="17" viewBox="0 0 10 17" fill="none"><path d="M9 1L1 8.5L9 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </button>
                    <div style={{flex:1}}>
                      <div style={{fontSize:"18px",fontWeight:"800",color:"var(--text)"}}>Wasiyyah & Mirath</div>
                      <div style={{fontSize:"11px",color:"var(--muted)",fontWeight:"600"}}>Islamic Estate Planning</div>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:"4px",padding:"4px 10px",borderRadius:"8px",background:darkMode?"rgba(76,175,80,0.12)":"rgba(46,125,50,0.08)",border:`1px solid ${darkMode?"rgba(76,175,80,0.25)":"rgba(46,125,50,0.2)"}`}}>
                      <span style={{fontSize:"10px"}}>🔓</span>
                      <span style={{fontSize:"10px",fontWeight:"700",color:darkMode?"#4caf50":"#2e7d32"}}>UNLOCKED</span>
                    </div>
                  </div>
                  {/* Hadith motivator */}
                  <div style={{margin:"12px 16px",padding:"14px 16px",borderRadius:"12px",background:darkMode?"rgba(200,180,100,0.06)":"rgba(160,130,40,0.06)",border:`1px solid ${darkMode?"rgba(200,180,100,0.12)":"rgba(160,130,40,0.1)"}`}}>
                    <div style={{fontSize:"13px",color:accent,fontStyle:"italic",lineHeight:1.6}}>
                      "It is not right for a Muslim who has something to bequeath to sleep two nights without his will being written."
                    </div>
                    <div style={{fontSize:"11px",color:"var(--muted)",marginTop:"6px"}}>— Sahih al-Bukhari & Muslim</div>
                  </div>
                  {/* Tool cards */}
                  <div style={{padding:"4px 16px 100px",display:"flex",flexDirection:"column",gap:"10px"}}>
                    {mirathTools.map(t => (
                      <div key={t.key} onClick={() => navTransition(() => setToolView(t.key))}
                        className="tasbeeh-hero" style={{cursor:"pointer"}}>
                        <div style={{width:"48px",height:"48px",borderRadius:"14px",background:darkMode?`${t.color}18`:`${t.color}14`,border:`1px solid ${t.color}40`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:"24px",flexShrink:0}}>
                          {t.icon}
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"2px"}}>
                            <span style={{fontSize:"15px",fontWeight:"700",color:"var(--text)"}}>{t.name}</span>
                            <span style={{fontSize:"12px",color:t.color,fontFamily:"'Scheherazade New','Amiri',serif"}}>{t.nameAr}</span>
                          </div>
                          <div style={{fontSize:"12px",color:"var(--muted)"}}>{t.desc}</div>
                        </div>
                        <div style={{color:"var(--muted)",opacity:0.4,flexShrink:0}}>
                          <svg width="9" height="16" viewBox="0 0 9 16" fill="none"><path d="M1 1l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Disclaimer */}
                  <div style={{padding:"0 16px 20px",marginTop:"auto"}}>
                    <div style={{fontSize:"11px",color:"var(--muted)",lineHeight:1.5,textAlign:"center"}}>
                      This is a planning tool. Please consult a learned Aalim or your local Jammiat ul-Ulama for guidance. Ensure your will is filed with the appropriate authorities.
                    </div>
                  </div>
                </div>
              );
            })()}

            {toolView === "faraid" && <Lazy><FaraidCalculator darkMode={darkMode} /></Lazy>}

            {toolView === "islamicwill" && userFeatures.islamicwill === true && <Lazy><IslamicWill darkMode={darkMode} userId={userId} userName={userName} userSurname={userSurname} backend={BACKEND} onTestWitness={(wId) => { setWitnessWillId(wId); setToolView("witnesswill"); }} onTestWitnessAsDebug={(wId) => { setDebugWitnessUserId(userId + "_debug"); setWitnessWillId(wId); setToolView("witnesswill"); }} /></Lazy>}

            {toolView === "witnesswill" && <Lazy><IslamicWill darkMode={darkMode} userId={debugWitnessUserId || userId} userName={userName} userSurname={userSurname} backend={BACKEND} witnessMode={true} witnessWillId={witnessWillId} witnessViewOnly={witnessViewOnly} /></Lazy>}

            {toolView === "familyinfo" && <Lazy><FamilyInfoPack darkMode={darkMode} encryptionKey={mirathKeyRef.current} userId={userId} userName={userName} userWhatsapp={userWhatsapp} backend={BACKEND} onBack={() => navTransition(() => setToolView("mirath"))} /></Lazy>}

            {toolView === "sharedfip" && <Lazy><FamilyInfoPack darkMode={darkMode} userId={userId} backend={BACKEND} viewerMode={true} viewerOwnerId={fipShareOwnerId || (sharedFipInbox.length === 1 ? sharedFipInbox[0].ownerId : null)} inbox={sharedFipInbox} onBackToInbox={() => setToolView("mirath")} onBack={() => navTransition(() => setToolView("mirath"))} pendingUnlockCode={_fipUnlockParam} /></Lazy>}

            {toolView === "rightnow" && (() => {
              const DHIKR_PROMPTS = [
                { arabic: "سُبْحَانَ اللَّهِ", transliteration: "SubhanAllah", meaning: "Glory be to Allah", task: "Say it 10 times right now", count: 10 },
                { arabic: "الْحَمْدُ لِلَّهِ", transliteration: "Alhamdulillah", meaning: "All praise is due to Allah", task: "Say it 10 times with gratitude", count: 10 },
                { arabic: "اللَّهُ أَكْبَرُ", transliteration: "Allahu Akbar", meaning: "Allah is the Greatest", task: "Say it 10 times right now", count: 10 },
                { arabic: "لَا إِلَٰهَ إِلَّا اللَّهُ", transliteration: "La ilaha illallah", meaning: "There is no god but Allah", task: "Say it 10 times with conviction", count: 10 },
                { arabic: "أَسْتَغْفِرُ اللَّهَ", transliteration: "Astaghfirullah", meaning: "I seek forgiveness from Allah", task: "Say it 10 times sincerely", count: 10 },
                { arabic: "سُبْحَانَ اللَّهِ وَبِحَمْدِهِ", transliteration: "SubhanAllahi wa bihamdihi", meaning: "Glory and praise be to Allah", task: "Say it 10 times — a tree is planted in Jannah for each", count: 10 },
                { arabic: "لَا حَوْلَ وَلَا قُوَّةَ إِلَّا بِاللَّهِ", transliteration: "La hawla wa la quwwata illa billah", meaning: "There is no power except with Allah", task: "Say it 10 times — a treasure of Jannah", count: 10 },
                { arabic: "اللَّهُمَّ صَلِّ عَلَى مُحَمَّدٍ", transliteration: "Allahumma salli ala Muhammad", meaning: "O Allah, send blessings upon Muhammad ﷺ", task: "Send 10 salawat upon the Prophet ﷺ", count: 10 },
              ];
              const AYAH_POOL = [
                { arabic: "فَإِنَّ مَعَ الْعُسْرِ يُسْرًا", translation: "For indeed, with hardship comes ease.", ref: "Quran 94:5" },
                { arabic: "وَمَن يَتَوَكَّلْ عَلَى اللَّهِ فَهُوَ حَسْبُهُ", translation: "And whoever puts their trust in Allah, He is sufficient for them.", ref: "Quran 65:3" },
                { arabic: "وَاذْكُرُوا اللَّهَ كَثِيرًا لَّعَلَّكُمْ تُفْلِحُونَ", translation: "And remember Allah often, so that you may be successful.", ref: "Quran 62:10" },
                { arabic: "إِنَّ اللَّهَ مَعَ الصَّابِرِينَ", translation: "Indeed, Allah is with the patient.", ref: "Quran 2:153" },
                { arabic: "وَلَسَوْفَ يُعْطِيكَ رَبُّكَ فَتَرْضَىٰ", translation: "And your Lord will give you, and you will be satisfied.", ref: "Quran 93:5" },
                { arabic: "أَلَا بِذِكْرِ اللَّهِ تَطْمَئِنُّ الْقُلُوبُ", translation: "Verily, in the remembrance of Allah do hearts find rest.", ref: "Quran 13:28" },
                { arabic: "ادْعُونِي أَسْتَجِبْ لَكُمْ", translation: "Call upon Me, I will respond to you.", ref: "Quran 40:60" },
                { arabic: "وَنَحْنُ أَقْرَبُ إِلَيْهِ مِنْ حَبْلِ الْوَرِيدِ", translation: "And We are closer to him than his jugular vein.", ref: "Quran 50:16" },
                { arabic: "رَبِّ اشْرَحْ لِي صَدْرِي وَيَسِّرْ لِي أَمْرِي", translation: "My Lord, expand my chest and ease my task for me.", ref: "Quran 20:25-26" },
                { arabic: "حَسْبُنَا اللَّهُ وَنِعْمَ الْوَكِيلُ", translation: "Allah is sufficient for us, and He is the best Disposer of affairs.", ref: "Quran 3:173" },
              ];
              const REFLECTIONS = [
                "Close your eyes for 30 seconds. Thank Allah for 3 things you can see when you open them.",
                "Think of someone who has wronged you. Make dua for them sincerely — this is the Sunnah.",
                "Take 5 slow breaths. With each exhale, say 'SubhanAllah' in your heart.",
                "Think about death for one minute. How would you want to meet Allah today?",
                "Look around you. Everything you see is a creation of Allah. Reflect on His power.",
                "Think of your parents. Make a silent dua for them right now.",
                "Ask yourself: what is one thing I can do today that pleases Allah?",
                "Remember a sin you committed recently. Ask Allah for forgiveness right now, sincerely.",
                "Think of a blessing you take for granted — your sight, your health, clean water. Say Alhamdulillah.",
                "Imagine standing before Allah on the Day of Judgement. What would you wish you had done more of?",
              ];

              // Time-aware content
              const now = new Date();
              const nowMins = now.getHours() * 60 + now.getMinutes();
              const parseTime = (t) => { if (!t) return null; const [h, m] = t.split(":").map(Number); return h * 60 + m; };
              let timeContext = null;
              if (prayerTimes) {
                const prayers = [
                  { name: "Fajr", time: parseTime(prayerTimes.Fajr) },
                  { name: "Sunrise", time: parseTime(prayerTimes.Sunrise) },
                  { name: "Dhuhr", time: parseTime(prayerTimes.Dhuhr) },
                  { name: "Asr", time: parseTime(prayerTimes.Asr) },
                  { name: "Maghrib", time: parseTime(prayerTimes.Maghrib) },
                  { name: "Isha", time: parseTime(prayerTimes.Isha) },
                ].filter(p => p.time !== null);
                const nextPrayer = prayers.find(p => p.time > nowMins);
                const lastPrayer = [...prayers].reverse().find(p => p.time <= nowMins);
                if (nextPrayer) {
                  const minsUntil = nextPrayer.time - nowMins;
                  if (minsUntil <= 30) {
                    timeContext = { type: "soon", prayer: nextPrayer.name, mins: minsUntil, message: `${nextPrayer.name} is in ${minsUntil} minutes. Make dua — it is accepted between the adhan and iqamah.` };
                  } else if (minsUntil <= 60) {
                    timeContext = { type: "approaching", prayer: nextPrayer.name, mins: minsUntil, message: `${nextPrayer.name} is coming in ${minsUntil} minutes. Use this time for dhikr and preparation.` };
                  }
                }
                if (!timeContext && lastPrayer) {
                  const minsSince = nowMins - lastPrayer.time;
                  if (minsSince <= 15 && lastPrayer.name !== "Sunrise") {
                    timeContext = { type: "just_prayed", prayer: lastPrayer.name, mins: minsSince, message: `You've just prayed ${lastPrayer.name}. Now is the best time for adhkar and dua.` };
                  }
                }
                if (!timeContext) {
                  if (nowMins < (parseTime(prayerTimes.Fajr) || 300)) {
                    timeContext = { type: "tahajjud", message: "This is the last third of the night — the most precious time for dua. Allah descends to the lowest heaven and asks: 'Is there anyone who calls on Me that I may answer?'" };
                  } else if (nowMins >= (parseTime(prayerTimes.Fajr) || 300) && nowMins < (parseTime(prayerTimes.Sunrise) || 360)) {
                    timeContext = { type: "fajr_sunrise", message: "Between Fajr and Sunrise — sit in your place of prayer and remember Allah. The reward is like a complete Hajj and Umrah." };
                  }
                }
              }

              const seed = Math.floor(Date.now() / 60000);
              const rand = (arr) => arr[(seed * 7 + arr.length) % arr.length];
              const dhikr = rand(DHIKR_PROMPTS);
              const ayah = rand(AYAH_POOL);
              const reflection = rand(REFLECTIONS);

              const accentColor = darkMode ? "#ffb300" : "#b87008";
              const accentFaint = darkMode ? "rgba(255,179,0,0.1)" : "rgba(184,112,8,0.06)";
              const accentBorder = darkMode ? "rgba(255,179,0,0.2)" : "rgba(184,112,8,0.2)";
              const teal = darkMode ? "#00c9a7" : "#009e87";

              return (
                <div style={{display:"flex",flexDirection:"column",height:"100%",background:"var(--bg)",overflowY:"auto"}}>
                  {/* Header */}
                  <div style={{display:"flex",alignItems:"center",gap:"12px",padding:"16px 20px 12px",position:"sticky",top:0,background:"var(--bg)",zIndex:10,borderBottom:`1px solid ${darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.08)"}`}}>
                    <button onClick={() => setToolView("grid")} style={{background:"none",border:"none",color:"var(--text)",cursor:"pointer",padding:"4px",display:"flex",alignItems:"center",justifyContent:"center",borderRadius:"8px",width:"32px",height:"32px"}}>
                      <svg width="10" height="17" viewBox="0 0 10 17" fill="none"><path d="M9 1L1 8.5L9 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </button>
                    <div style={{flex:1}}>
                      <div style={{fontSize:"18px",fontWeight:"800",color:"var(--text)"}}>Right Now</div>
                      <div style={{fontSize:"11px",color:accentColor,fontWeight:"600",letterSpacing:"0.5px"}}>What should I do right now?</div>
                    </div>
                    <button onClick={() => { setToolView("grid"); setTimeout(() => setToolView("rightnow"), 50); }} style={{background:accentFaint,border:`1px solid ${accentBorder}`,borderRadius:"12px",padding:"8px 14px",cursor:"pointer",display:"flex",alignItems:"center",gap:"6px"}}>
                      <span style={{fontSize:"14px"}}>🔄</span>
                      <span style={{fontSize:"12px",fontWeight:"700",color:accentColor}}>New</span>
                    </button>
                  </div>

                  <div style={{padding:"16px 20px 120px",display:"flex",flexDirection:"column",gap:"16px"}}>

                    {/* Time-aware context card */}
                    {timeContext && (
                      <div style={{
                        borderRadius:"16px",padding:"16px",position:"relative",overflow:"hidden",
                        background:darkMode?"linear-gradient(135deg,rgba(255,179,0,0.08),rgba(255,179,0,0.02))":"linear-gradient(135deg,rgba(184,112,8,0.08),rgba(184,112,8,0.03))",
                        border:`1px solid ${accentBorder}`,
                      }}>
                        <div style={{position:"absolute",top:-10,right:-10,fontSize:"60px",opacity:0.06,userSelect:"none",pointerEvents:"none"}}>🕐</div>
                        <div style={{fontSize:"11px",fontWeight:"700",color:accentColor,letterSpacing:"1px",textTransform:"uppercase",marginBottom:"8px"}}>
                          {timeContext.type === "tahajjud" ? "Tahajjud Time" : timeContext.type === "fajr_sunrise" ? "Golden Hour" : timeContext.type === "just_prayed" ? `After ${timeContext.prayer}` : `${timeContext.prayer} Coming`}
                        </div>
                        <div style={{fontSize:"14px",color:darkMode?"rgba(255,255,255,0.8)":"rgba(0,0,0,0.7)",lineHeight:1.7}}>
                          {timeContext.message}
                        </div>
                      </div>
                    )}

                    {/* Quick Dhikr */}
                    <div style={{
                      borderRadius:"16px",padding:"20px",position:"relative",overflow:"hidden",
                      background:darkMode?"rgba(0,201,167,0.06)":"rgba(0,158,135,0.04)",
                      border:`1px solid ${darkMode?"rgba(0,201,167,0.15)":"rgba(0,158,135,0.15)"}`,
                    }}>
                      <div style={{fontSize:"11px",fontWeight:"700",color:teal,letterSpacing:"1px",textTransform:"uppercase",marginBottom:"12px"}}>Quick Dhikr</div>
                      <div style={{fontSize:"28px",fontWeight:"600",color:"var(--text)",textAlign:"center",fontFamily:"'Scheherazade New', serif",lineHeight:1.8,marginBottom:"8px"}}>
                        {dhikr.arabic}
                      </div>
                      <div style={{textAlign:"center",fontSize:"14px",fontWeight:"600",color:teal,marginBottom:"4px"}}>{dhikr.transliteration}</div>
                      <div style={{textAlign:"center",fontSize:"12px",color:"var(--muted)",marginBottom:"12px"}}>{dhikr.meaning}</div>
                      <div style={{
                        textAlign:"center",fontSize:"13px",fontWeight:"700",color:darkMode?"rgba(255,255,255,0.7)":"rgba(0,0,0,0.6)",
                        background:darkMode?"rgba(0,201,167,0.1)":"rgba(0,158,135,0.08)",
                        border:`1px solid ${darkMode?"rgba(0,201,167,0.2)":"rgba(0,158,135,0.18)"}`,
                        borderRadius:"12px",padding:"10px 16px",
                      }}>
                        👉 {dhikr.task}
                      </div>
                    </div>

                    {/* Short Ayah */}
                    <div style={{
                      borderRadius:"16px",padding:"20px",position:"relative",overflow:"hidden",
                      background:darkMode?"linear-gradient(135deg,rgba(180,160,255,0.06),rgba(180,160,255,0.02))":"linear-gradient(135deg,rgba(100,60,200,0.06),rgba(100,60,200,0.02))",
                      border:`1px solid ${darkMode?"rgba(180,160,255,0.15)":"rgba(100,60,200,0.15)"}`,
                    }}>
                      <div style={{fontSize:"11px",fontWeight:"700",color:darkMode?"#b4a0ff":"#643cc8",letterSpacing:"1px",textTransform:"uppercase",marginBottom:"12px"}}>Ayah for You</div>
                      <div style={{fontSize:"24px",fontWeight:"600",color:"var(--text)",textAlign:"center",fontFamily:"'Scheherazade New', serif",lineHeight:1.8,marginBottom:"10px"}}>
                        {ayah.arabic}
                      </div>
                      <div style={{textAlign:"center",fontSize:"14px",color:darkMode?"rgba(255,255,255,0.75)":"rgba(0,0,0,0.65)",lineHeight:1.6,fontStyle:"italic",marginBottom:"8px"}}>
                        "{ayah.translation}"
                      </div>
                      <div style={{textAlign:"center",fontSize:"11px",fontWeight:"600",color:darkMode?"#b4a0ff":"#643cc8"}}>
                        — {ayah.ref}
                      </div>
                    </div>

                    {/* 1-Minute Reflection */}
                    <div style={{
                      borderRadius:"16px",padding:"20px",position:"relative",overflow:"hidden",
                      background:darkMode?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.02)",
                      border:`1px solid ${darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.08)"}`,
                    }}>
                      <div style={{fontSize:"11px",fontWeight:"700",color:accentColor,letterSpacing:"1px",textTransform:"uppercase",marginBottom:"12px"}}>1-Minute Reflection</div>
                      <div style={{fontSize:"15px",color:darkMode?"rgba(255,255,255,0.8)":"rgba(0,0,0,0.7)",lineHeight:1.8,textAlign:"center"}}>
                        {reflection}
                      </div>
                    </div>

                    {/* Quick actions */}
                    <div style={{display:"flex",gap:"10px"}}>
                      <button onClick={() => setToolView("tasbeeh")} style={{
                        flex:1,padding:"14px",borderRadius:"14px",cursor:"pointer",border:"none",
                        background:darkMode?"rgba(0,201,167,0.1)":"rgba(0,158,135,0.08)",
                        color:teal,fontSize:"13px",fontWeight:"700",
                      }}>
                        📿 Open Tasbeeh
                      </button>
                      <button onClick={() => setToolView("azkar")} style={{
                        flex:1,padding:"14px",borderRadius:"14px",cursor:"pointer",border:"none",
                        background:darkMode?"rgba(255,179,0,0.1)":"rgba(184,112,8,0.08)",
                        color:accentColor,fontSize:"13px",fontWeight:"700",
                      }}>
                        🌙 Open Azkar
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}

            {toolView === "surah" && (() => {
              const info = SURAH_INFO[surahKey];
              const ayahs = surahCache[surahKey + ":" + selectedReciter];
              const currentAyah = ayahs && surahCurrentAyah !== null ? ayahs[surahCurrentAyah] : null;
              const surahKeys = Object.keys(SURAH_INFO);
              const surahIdx = surahKeys.indexOf(surahKey);
              const prevSurahKey = surahIdx > 0 ? surahKeys[surahIdx - 1] : null;
              const nextSurahKey = surahIdx < surahKeys.length - 1 ? surahKeys[surahIdx + 1] : null;
              const isBookmarked = surahBookmark?.key === surahKey;
              const saveBookmark = () => {
                const bm = { key: surahKey, ayahIdx: surahCurrentAyah ?? 0 };
                setSurahBookmark(bm);
                localStorage.setItem("surahBookmark", JSON.stringify(bm));
              };
              return (
              <>
                {/* Sticky header — swipe left on header to go back without stopping audio */}
                <div className="surah-sticky" style={{top:0}}>
                  <button className="surah-back" onClick={() => setToolView("quran")} style={{fontSize:"13px",padding:"5px 10px"}}>← Surahs</button>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:"15px",fontWeight:"800",color:"var(--gold)"}}>{info.name}</div>
                    <div style={{fontSize:"11px",color:"var(--muted)",letterSpacing:"1px"}}>
                      {surahCurrentAyah !== null ? `Ayah ${surahCurrentAyah + 1} of ${info.count}` : `${info.arabic} · ${info.count} Ayahs`}
                    </div>
                  </div>

                  {/* Bookmark button */}
                  {ayahs && (
                    <button
                      onClick={saveBookmark}
                      title={isBookmarked ? `Bookmarked Ayah ${(surahBookmark?.ayahIdx??0)+1}` : "Bookmark current ayah"}
                      style={{padding:"6px 9px",background:isBookmarked?"rgba(255,179,0,0.15)":"transparent",border:"1px solid "+(isBookmarked?"rgba(255,179,0,0.4)":"var(--border)"),borderRadius:"12px",color:isBookmarked?"var(--gold)":"var(--muted)",fontSize:"14px",cursor:"pointer",flexShrink:0}}
                    >🔖</button>
                  )}

                  {/* Sleep timer */}
                  {ayahs && (
                    <div style={{position:"relative"}}>
                      <button
                        onClick={() => setShowSleepMenu(v => !v)}
                        title="Sleep timer"
                        style={{padding:"6px 10px",background: sleepTimerLabel ? "rgba(255,179,0,0.15)" : "transparent",border:"1px solid " + (sleepTimerLabel ? "rgba(255,179,0,0.4)" : "var(--border)"),borderRadius:"16px",color: sleepTimerLabel ? "var(--gold)" : "var(--muted)",fontSize:"12px",cursor:"pointer",fontFamily:"inherit",flexShrink:0}}
                      >
                        🌙{sleepTimerRemaining !== null ? ` ${Math.floor(sleepTimerRemaining/60)}:${String(sleepTimerRemaining%60).padStart(2,'0')}` : ""}
                      </button>
                      {showSleepMenu && (
                        <div className="sleep-menu">
                          {sleepTimerLabel && <div className="sleep-option" style={{color:"var(--danger)"}} onClick={cancelSleepTimer}>✕ Cancel ({sleepTimerLabel})</div>}
                          {[15,30,45,60].map(m => (
                            <div key={m} className="sleep-option" onClick={() => startSleepTimer(m)}>{m} minutes</div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Play / Pause / Resume + Speed */}
                  {ayahs && (
                    <div style={{display:"flex",alignItems:"center",gap:"8px",flexWrap:"wrap"}}>
                    {surahIsPlaying
                      ? <button onClick={pauseSurah} style={{padding:"8px 14px",background:"rgba(0,201,167,0.12)",border:"1px solid var(--acc2)",borderRadius:"20px",color:"var(--accent)",fontSize:"13px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>⏸ Pause</button>
                      : surahCurrentAyah !== null
                        ? <button onClick={() => resumeSurah(ayahs)} style={{padding:"8px 14px",background:"rgba(255,179,0,0.12)",border:"1px solid rgba(255,179,0,0.3)",borderRadius:"20px",color:"var(--gold)",fontSize:"13px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>▶ Resume</button>
                        : <button onClick={() => playSurahFrom(ayahs, 0)} style={{padding:"8px 14px",background:"rgba(255,179,0,0.12)",border:"1px solid rgba(255,179,0,0.3)",borderRadius:"20px",color:"var(--gold)",fontSize:"13px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>▶ Play</button>
                    }
                    <button onClick={() => {
                      hapticToggle();
                      const speeds = [0.75, 1, 1.25, 1.5];
                      const next = speeds[(speeds.indexOf(surahSpeed) + 1) % speeds.length];
                      setSurahSpeed(next);
                      surahSpeedRef.current = next;
                      localStorage.setItem("surahSpeed", String(next));
                      yasinBufs.forEach(b => { if (b) b.playbackRate = next; });
                    }} style={{padding:"8px 12px",background:surahSpeed !== 1 ? "rgba(0,201,167,0.12)" : "var(--s2)",border:"1px solid "+(surahSpeed !== 1 ? "var(--acc2)" : "var(--border)"),borderRadius:"20px",color:surahSpeed !== 1 ? "var(--accent)" : "var(--muted)",fontSize:"12px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>
                      {surahSpeed}x
                    </button>
                    </div>
                  )}

                  {/* Prev / Next surah navigation row */}
                  {(prevSurahKey || nextSurahKey) && (
                    <div style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",paddingTop:"8px",marginTop:"2px",borderTop:"1px solid rgba(255,255,255,0.06)"}}>
                      {prevSurahKey
                        ? <button onClick={() => loadSurah(prevSurahKey)} style={{background:"transparent",border:"none",color:"var(--muted)",fontSize:"12px",cursor:"pointer",fontFamily:"inherit",padding:"2px 0",display:"flex",alignItems:"center",gap:"3px"}}>‹ {SURAH_INFO[prevSurahKey].name}</button>
                        : <span/>
                      }
                      {nextSurahKey
                        ? <button onClick={() => loadSurah(nextSurahKey)} style={{background:"transparent",border:"none",color:"var(--muted)",fontSize:"12px",cursor:"pointer",fontFamily:"inherit",padding:"2px 0",display:"flex",alignItems:"center",gap:"3px"}}>{SURAH_INFO[nextSurahKey].name} ›</button>
                        : <span/>
                      }
                    </div>
                  )}
                </div>

                {/* Reciter picker — always visible, high z-index */}
                <div style={{padding:"8px 16px 0",position:"sticky",top:"60px",zIndex:30,background:"var(--bg)"}}>
                  <button
                    onClick={() => setShowReciterPicker(v => !v)}
                    style={{width:"100%",padding:"9px 14px",background:"var(--s2)",border:"1px solid var(--border)",borderRadius:"10px",color:"var(--text)",fontSize:"13px",cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"space-between"}}
                  >
                    <span style={{display:"flex",alignItems:"center",gap:"8px"}}>
                      <span style={{fontSize:"14px"}}>🎙</span>
                      <span style={{fontWeight:"600"}}>{(RECITERS.find(r => r.id === selectedReciter) || RECITERS[0]).name}</span>
                    </span>
                    <span style={{color:"var(--muted)",fontSize:"11px"}}>{showReciterPicker ? "▲" : "▼"}</span>
                  </button>
                  {showReciterPicker && (
                    <div style={{marginTop:"6px",background:"var(--s1)",border:"1px solid var(--border)",borderRadius:"12px",overflow:"hidden",maxHeight:"240px",overflowY:"auto",boxShadow:"0 8px 32px rgba(0,0,0,0.4)"}}>
                      {RECITERS.map(r => (
                        <button
                          key={r.id}
                          onClick={async () => {
                            setShowReciterPicker(false);
                            if (r.id === selectedReciter) return;
                            hapticToggle();
                            // Capture position from ref before any state changes
                            const resumeIdx = surahCurrentAyahRef.current;
                            const wasPlaying = surahIsPlaying;
                            // Stop current playback completely — destroy old buffers
                            yasinBufs.forEach(b => { b.ontimeupdate = null; b.onended = null; b.onerror = null; b.onpause = null; b.pause(); b.removeAttribute("src"); b.load(); });
                            // Replace with fresh Audio objects so stale buffer state can't interfere
                            yasinBufs[0] = new Audio(); yasinBufs[0].preload = "auto"; yasinBufs[0]._url = "";
                            yasinBufs[1] = new Audio(); yasinBufs[1].preload = "auto"; yasinBufs[1]._url = "";
                            yasinBufActive = 0;
                            setSurahIsPlaying(false);
                            surahPausedRef.current = false; // MUST be false so future play/resume works
                            // Show switching overlay
                            setReciterSwitching(true);
                            // Switch reciter
                            setSelectedReciter(r.id);
                            localStorage.setItem("selectedReciter", r.id);
                            // Load surah with new reciter — only fetch audio (reuse cached text)
                            const inf = SURAH_INFO[surahKey];
                            const ck = surahKey + ":" + r.id;
                            try {
                              let newAyahs = surahCacheRef.current[ck];
                              if (!newAyahs) {
                                // Find existing cached text for this surah (any reciter)
                                const existingKey = Object.keys(surahCacheRef.current).find(k => k.startsWith(surahKey + ":"));
                                const existingAyahs = existingKey ? surahCacheRef.current[existingKey] : null;
                                const controller = new AbortController();
                                const timeout = setTimeout(() => controller.abort(), 12000);
                                // Only fetch audio endpoint — text is reciter-independent
                                const auRes = await fetch("https://api.alquran.cloud/v1/surah/" + inf.num + "/" + r.id, { signal: controller.signal });
                                clearTimeout(timeout);
                                const auD = await auRes.json();
                                if (auD.code !== 200 || !auD.data?.ayahs) throw new Error("Reciter not available");
                                if (existingAyahs) {
                                  // Reuse Arabic + English from cache, only swap audio URLs
                                  newAyahs = existingAyahs.map((ay, i) => ({
                                    ...ay, audio: auD.data.ayahs[i].audio,
                                  }));
                                } else {
                                  // No cache at all — need text too (first load edge case)
                                  const [arRes, enRes] = await Promise.all([
                                    fetch("https://api.alquran.cloud/v1/surah/" + inf.num, { signal: controller.signal }),
                                    fetch("https://api.alquran.cloud/v1/surah/" + inf.num + "/en.sahih", { signal: controller.signal }),
                                  ]);
                                  const [arD, enD] = await Promise.all([arRes.json(), enRes.json()]);
                                  newAyahs = arD.data.ayahs.map((ay, i) => ({
                                    number: ay.numberInSurah, arabic: ay.text,
                                    translation: enD.data.ayahs[i].text, audio: auD.data.ayahs[i].audio,
                                  }));
                                }
                                setSurahCache(c => ({ ...c, [ck]: newAyahs }));
                                surahCacheRef.current = { ...surahCacheRef.current, [ck]: newAyahs };
                              }
                              setReciterSwitching(false);
                              // Preload first ayah audio for instant start
                              const startIdx = (wasPlaying && resumeIdx !== null) ? Math.min(resumeIdx, newAyahs.length - 1) : 0;
                              preloadYasinBuf(0, newAyahs[startIdx].audio);
                              // Auto-play from same position if was playing
                              if (wasPlaying && resumeIdx !== null) {
                                playSurahFrom(newAyahs, startIdx);
                              } else {
                                // Not playing — just scroll to where user was
                                if (resumeIdx !== null) {
                                  setTimeout(() => document.getElementById("ayah-" + resumeIdx)?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
                                }
                              }
                            } catch (err) {
                              setReciterSwitching(false);
                              // Revert reciter selection since this one failed
                              setSelectedReciter(selectedReciter);
                              localStorage.setItem("selectedReciter", selectedReciter);
                              alert((r.short || r.name) + " is not available for this surah. Try another reciter.");
                            }
                          }}
                          style={{width:"100%",padding:"12px 14px",background:r.id===selectedReciter?"rgba(255,179,0,0.08)":"transparent",border:"none",borderBottom:"1px solid var(--border)",color:r.id===selectedReciter?"var(--gold)":"var(--text)",fontSize:"14px",fontWeight:r.id===selectedReciter?"700":"400",cursor:"pointer",fontFamily:"inherit",textAlign:"left",display:"flex",alignItems:"center",justifyContent:"space-between"}}
                        >
                          <span>{r.name}</span>
                          {r.id === selectedReciter && <span style={{color:"var(--gold)",fontSize:"14px"}}>✓</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Reciter switching overlay */}
                {reciterSwitching && (
                  <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.5)",zIndex:50,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:"12px"}}>
                    <div className="spinner" style={{width:"32px",height:"32px"}}/>
                    <div style={{color:"#fff",fontSize:"14px",fontWeight:"600"}}>Switching reciter...</div>
                  </div>
                )}

                {surahLoading && (
                  <div style={{textAlign:"center",padding:"60px 20px",color:"var(--muted)"}}>
                    <div className="spinner" style={{margin:"0 auto 16px"}}/>
                    <div style={{fontSize:"14px"}}>Loading {info.name}...</div>
                  </div>
                )}

                {ayahs && (
                  <div>
                  <>
                    {/* Landscape immersive hint */}
                    <div style={{margin:"10px 16px 0",padding:"8px 14px",background:"rgba(0,201,167,0.06)",border:"1px solid rgba(0,201,167,0.15)",borderRadius:"10px",display:"flex",alignItems:"center",gap:"10px"}}>
                      <span style={{fontSize:"16px"}}>🔄</span>
                      <span style={{fontSize:"11px",color:"var(--acc2)",lineHeight:1.5}}>Rotate to landscape for an immersive full-screen recitation view</span>
                    </div>
                    <div style={{textAlign:"center",padding:"16px 20px 16px",background:"linear-gradient(180deg,#0a1a14,transparent)"}}>
                      <div style={{fontSize:"20px",color:"rgba(255,179,0,0.65)",fontFamily:"serif",letterSpacing:"3px",direction:"rtl",lineHeight:1.9}}>بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ</div>
                      <div style={{fontSize:"11px",color:"var(--muted)",marginTop:"6px",letterSpacing:"1px"}}>In the name of Allah, the Most Gracious, the Most Merciful</div>
                    </div>

                    {/* Bookmark return banner */}
                    {isBookmarked && surahBookmark.ayahIdx != null && (
                      <div
                        onClick={() => {
                          const el = document.getElementById("ayah-" + surahBookmark.ayahIdx);
                          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                        }}
                        style={{margin:"0 16px 8px",padding:"10px 14px",background:"rgba(255,179,0,0.08)",border:"1px solid rgba(255,179,0,0.25)",borderRadius:"10px",display:"flex",alignItems:"center",gap:"10px",cursor:"pointer"}}
                      >
                        <span style={{fontSize:"16px"}}>🔖</span>
                        <div style={{flex:1}}>
                          <div style={{fontSize:"12px",fontWeight:"700",color:"var(--gold)"}}>Bookmark — Ayah {surahBookmark.ayahIdx + 1}</div>
                          <div style={{fontSize:"10px",color:"var(--muted)"}}>Tap to jump to your saved position</div>
                        </div>
                        <span style={{color:"var(--gold)",fontSize:"14px",opacity:0.7}}>↓</span>
                      </div>
                    )}

                    {ayahs.map((a, i) => {
                      const isActive = surahCurrentAyah === i;
                      const isBookmarkedAyah = isBookmarked && surahBookmark.ayahIdx === i;
                      return (
                        <div key={a.number} id={"ayah-" + i} className="ayah-row"
                          onClick={() => playSurahFrom(ayahs, i)}
                          style={{cursor:"pointer",background:isActive?"rgba(255,179,0,0.06)":isBookmarkedAyah?"rgba(255,179,0,0.03)":"transparent",borderLeft:isActive?"3px solid var(--gold)":isBookmarkedAyah?"3px solid rgba(255,179,0,0.35)":"3px solid transparent",paddingLeft:isActive||isBookmarkedAyah?"13px":"16px",transition:"background 0.3s,border-color 0.3s",position:"relative"}}
                        >
                          <span className="ayah-num" style={isActive?{background:"rgba(255,179,0,0.25)",borderColor:"var(--gold)",color:"var(--gold)"}:{}}>{a.number}</span>
                          {isBookmarkedAyah && <span style={{position:"absolute",right:"12px",top:"50%",transform:"translateY(-50%)",fontSize:"11px",opacity:0.55,pointerEvents:"none"}}>🔖</span>}
                          <div className="ayah-arabic" style={isActive?{color:"var(--gold)"}:{}}>{a.arabic}</div>
                          <div className="ayah-translation">{a.translation}</div>
                        </div>
                      );
                    })}
                    <div style={{padding:"28px 20px",textAlign:"center"}}>
                      <div style={{fontSize:"15px",color:"rgba(255,179,0,0.5)",fontFamily:"serif",letterSpacing:"2px",direction:"rtl",marginBottom:"8px"}}>صَدَقَ اللَّهُ الْعَظِيمُ</div>
                      <div style={{fontSize:"11px",color:"var(--muted)",marginBottom:"20px"}}>Allah the Almighty has spoken the truth</div>
                      <button onClick={() => setToolView("quran")} style={{padding:"10px 28px",background:"transparent",border:"1px solid var(--border)",borderRadius:"10px",color:"var(--muted)",fontSize:"13px",cursor:"pointer",fontFamily:"inherit"}}>← All Surahs</button>
                    </div>
                  </>
                  </div>
                )}
              </>
              );
            })()}

            {toolView === "siratpro" && supportPageVisible && (() => {
              const sup = remoteConfig.support || {};
              const presets = sup.presets || [20, 50, 150];
              const customEnabled = sup.customEnabled !== false;
              const customMin = sup.customMin || 5;
              const heading = sup.heading || "Support Sirat";
              const message = sup.message || "Sirat is free for the Ummah. If you\u2019d like to support us, please do so below.";
              const curTotal = supportTotalRef.current;
              const curTier = getSupporterTier(curTotal, sup);
              const selectedAmt = donationCustom ? parseInt(donationCustom) : donationAmt;
              const isValidCustom = !donationCustom || parseInt(donationCustom) >= customMin;
              const canPay = selectedAmt > 0 && isValidCustom && paymentsEnabled && remoteConfig.paystackKey;
              return (
                <div style={{padding:"16px",paddingBottom:"100px"}}>
                  {/* Header */}
                  <div style={{display:"flex",alignItems:"center",gap:"12px",marginBottom:"24px"}}>
                    <button onClick={() => { if (siratProSourceRef.current === "settings") { setToolView("grid"); setTab("settings"); siratProSourceRef.current = null; } else { setToolView("grid"); } }} style={{background:"transparent",border:"none",cursor:"pointer",padding:"6px",borderRadius:"10px",color:"var(--text)",fontSize:"22px",lineHeight:1,display:"flex",alignItems:"center",justifyContent:"center"}}>‹</button>
                    <div style={{flex:1,textAlign:"center"}}>
                      <div style={{fontSize:"20px",fontWeight:"800",color:"var(--text)"}}>{heading}</div>
                      <div style={{fontSize:"11px",color:"var(--muted)",letterSpacing:"1.5px",textTransform:"uppercase",marginTop:"2px"}}>{sup.subheading || "Free to use · Yours to keep"}</div>
                    </div>
                    <div style={{width:"34px"}}/>
                  </div>

                  {/* Hero icon + message */}
                  <div style={{textAlign:"center",marginBottom:"24px",padding:"20px 16px",background:darkMode?"rgba(255,179,0,0.03)":"rgba(184,112,8,0.03)",border:`1px solid ${darkMode?"rgba(255,179,0,0.08)":"rgba(184,112,8,0.08)"}`,borderRadius:"18px"}}>
                    <div style={{fontSize:"48px",marginBottom:"12px",animation:"proIconFloat 2.5s ease-in-out infinite"}}>🌙</div>
                    <div style={{fontSize:"14px",color:"var(--muted)",lineHeight:1.7,maxWidth:"300px",margin:"0 auto"}}>{message}</div>
                  </div>

                  {/* Supporter tier progress */}
                  {curTotal > 0 && curTier.name && (
                    <div style={{padding:"14px",background:darkMode?"rgba(255,179,0,0.04)":"rgba(184,112,8,0.04)",border:`1px solid ${darkMode?"rgba(255,179,0,0.2)":"rgba(184,112,8,0.2)"}`,borderRadius:"14px",marginBottom:"20px",textAlign:"center"}}>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:"8px",marginBottom:"6px"}}>
                        <span style={{fontSize:"20px"}}>{curTier.icon}</span>
                        <span style={{fontSize:"15px",fontWeight:"800",color:curTier.color}}>{curTier.name} Supporter</span>
                      </div>
                      <div style={{fontSize:"12px",color:"var(--muted)"}}>Total given: R{curTotal}</div>
                      {curTier.next && (
                        <div style={{marginTop:"10px"}}>
                          <div style={{fontSize:"11px",color:"var(--muted)",marginBottom:"4px"}}>R{curTier.nextAmount - curTotal} more to reach {curTier.next}</div>
                          <div style={{height:"6px",background:"var(--s3)",borderRadius:"3px",overflow:"hidden"}}>
                            <div style={{height:"100%",width:`${Math.min(100, (curTotal / curTier.nextAmount) * 100)}%`,background:`linear-gradient(90deg, ${curTier.color}, var(--gold))`,borderRadius:"3px",transition:"width 0.3s"}}/>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Preset amounts */}
                  <div style={{fontSize:"11px",color:"var(--muted)",letterSpacing:"1.5px",textTransform:"uppercase",fontWeight:"700",marginBottom:"10px"}}>Choose an amount</div>
                  <div style={{display:"grid",gridTemplateColumns:presets.length<=3?"repeat("+presets.length+",1fr)":"repeat(3,1fr)",gap:"8px",marginBottom:"14px"}}>
                    {presets.map(amt => {
                      const sel = donationAmt===amt&&!donationCustom;
                      return (
                        <button key={amt} onClick={() => { setDonationAmt(amt); setDonationCustom(""); }}
                          style={{padding:"16px 8px",background:sel?(darkMode?"linear-gradient(135deg,#1a1200,#0f0a00)":"linear-gradient(135deg,#fffbee,#fff5d6)"):"var(--s2)",border:`1.5px solid ${sel?"rgba(255,179,0,0.7)":"var(--border)"}`,borderRadius:"14px",cursor:"pointer",textAlign:"center",color:"var(--text)",fontFamily:"inherit",transition:"all 0.15s",boxShadow:sel?`0 0 12px rgba(255,179,0,0.15)`:"none"}}>
                          <div style={{fontSize:"20px",fontWeight:"800",color:sel?"var(--gold)":"var(--text)"}}>R{amt}</div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Custom amount */}
                  {customEnabled && (
                    <div style={{marginBottom:"20px"}}>
                      <div style={{display:"flex",alignItems:"center",gap:"10px"}}>
                        <span style={{fontSize:"18px",fontWeight:"800",color:"var(--gold)"}}>R</span>
                        <input
                          type="number"
                          inputMode="numeric"
                          value={donationCustom}
                          onChange={e => { setDonationCustom(e.target.value); if (e.target.value) setDonationAmt(0); }}
                          onFocus={() => setDonationAmt(0)}
                          placeholder={`Custom (min R${customMin})`}
                          style={{flex:1,padding:"14px",background:"var(--s2)",border:`1px solid ${donationCustom&&!isValidCustom?"var(--danger)":"var(--border)"}`,borderRadius:"12px",color:"var(--text)",fontSize:"16px",fontWeight:"700",fontFamily:"inherit",outline:"none"}}
                        />
                      </div>
                      {donationCustom && !isValidCustom && (
                        <div style={{fontSize:"11px",color:"var(--danger)",marginTop:"6px"}}>Minimum amount is R{customMin}</div>
                      )}
                    </div>
                  )}


                  {/* Pay button */}
                  {canPay && selectedAmt > 0 && (
                    <button
                      disabled={paystackLoading}
                      onClick={() => handlePaystack({ label: "Support", price: selectedAmt, days: 0 })}
                      style={{width:"100%",padding:"17px",background:paystackLoading?"var(--s3)":"linear-gradient(110deg, #b8860b 0%, #ffb300 25%, #ffe066 50%, #ffb300 75%, #b8860b 100%)",backgroundSize:"200% 100%",animation:paystackLoading?"none":"proShimmer 3s linear infinite",border:"1px solid rgba(255,179,0,0.3)",borderRadius:"14px",color:paystackLoading?"var(--muted)":"#1a0e00",fontSize:"17px",fontWeight:"800",cursor:paystackLoading?"wait":"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:"8px",boxShadow:"0 0 20px rgba(255,179,0,0.2), 0 0 40px rgba(255,179,0,0.1)",letterSpacing:"0.3px",transition:"all 0.3s ease",marginBottom:"20px"}}
                    >
                      {paystackLoading ? <><span className="sp" style={{width:18,height:18,border:"2px solid var(--muted)",borderTopColor:"var(--gold)"}} /> Opening checkout...</> : <>Give R{selectedAmt}</>}
                    </button>
                  )}

                  {/* EFT / PayShap section (reused from pro) */}
                  {(() => {
                    const pd = remoteConfig.paymentDetails || {};
                    const hasPayment = pd.accountNumber || pd.payshapNumber;
                    if (!hasPayment || pd.hidden) return null;
                    const userRef = (userId || "").slice(-5).toUpperCase();
                    const copyToClipboard = (text, field) => {
                      try { if (navigator.clipboard && navigator.clipboard.writeText) { navigator.clipboard.writeText(text); } else { const ta = document.createElement("textarea"); ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0"; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta); } } catch {}
                      setCopiedField(field); setTimeout(() => setCopiedField(prev => prev === field ? null : prev), 1500);
                    };
                    return (
                      <div style={{marginBottom:"20px"}}>
                        <div style={{display:"flex",alignItems:"center",gap:"12px",marginBottom:"16px"}}>
                          <div style={{flex:1,height:"1px",background:darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.08)"}} />
                          <span style={{fontSize:"11px",color:"var(--muted)",fontWeight:"600",whiteSpace:"nowrap"}}>or support via bank transfer</span>
                          <div style={{flex:1,height:"1px",background:darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.08)"}} />
                        </div>
                        {pd.payshapNumber && (
                          <div style={{padding:"16px",background:darkMode?"rgba(0,201,167,0.04)":"rgba(0,158,135,0.04)",border:`1px solid ${darkMode?"rgba(0,201,167,0.2)":"rgba(0,158,135,0.2)"}`,borderRadius:"14px",marginBottom:"10px"}}>
                            <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"10px"}}>
                              <span style={{fontSize:"18px"}}>⚡</span>
                              <span style={{fontSize:"14px",fontWeight:"800",color:"var(--accent)"}}>PayShap</span>
                              <span style={{fontSize:"10px",color:"var(--muted)",background:"var(--s3)",padding:"2px 8px",borderRadius:"6px",fontWeight:"600"}}>Instant</span>
                            </div>
                            <div onClick={() => copyToClipboard(pd.payshapNumber, "payshap")} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 14px",background:"var(--s2)",borderRadius:"10px",cursor:"pointer",border:`1px solid ${copiedField==="payshap"?"var(--accent)":"var(--border)"}`,transition:"border 0.2s"}}>
                              <span style={{fontSize:"16px",fontWeight:"700",color:"var(--text)",fontFamily:"monospace",letterSpacing:"1px"}}>{pd.payshapNumber}</span>
                              <span style={{fontSize:"11px",color:"var(--accent)",fontWeight:"600"}}>{copiedField==="payshap"?"✓ Copied!":"tap to copy"}</span>
                            </div>
                          </div>
                        )}
                        {pd.accountNumber && (
                          <div style={{padding:"16px",background:darkMode?"rgba(255,179,0,0.03)":"rgba(184,112,8,0.03)",border:`1px solid ${darkMode?"rgba(255,179,0,0.15)":"rgba(184,112,8,0.15)"}`,borderRadius:"14px",marginBottom:"10px"}}>
                            <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"12px"}}>
                              <span style={{fontSize:"18px"}}>🏦</span>
                              <span style={{fontSize:"14px",fontWeight:"800",color:"var(--gold)"}}>EFT / Bank Transfer</span>
                            </div>
                            {[pd.bankName&&{label:"Bank",value:pd.bankName},pd.accountHolder&&{label:"Account Holder",value:pd.accountHolder},{label:"Account Number",value:pd.accountNumber,copy:true,field:"account"},pd.branchCode&&{label:"Branch Code",value:pd.branchCode},pd.accountType&&{label:"Account Type",value:pd.accountType}].filter(Boolean).map((row,i)=>(
                              <div key={i} onClick={row.copy?()=>copyToClipboard(row.value,row.field):undefined} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${darkMode?"rgba(255,255,255,0.04)":"rgba(0,0,0,0.06)"}`,cursor:row.copy?"pointer":"default"}}>
                                <span style={{fontSize:"12px",color:"var(--muted)"}}>{row.label}</span>
                                <span style={{fontSize:"13px",fontWeight:"700",color:"var(--text)",fontFamily:row.copy?"monospace":"inherit"}}>{row.value} {row.copy&&<span style={{fontSize:"10px",color:copiedField===row.field?"var(--accent)":"var(--gold)"}}>{copiedField===row.field?"✓ Copied!":"copy"}</span>}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        <div onClick={() => copyToClipboard(userRef, "ref")} style={{padding:"10px 14px",background:"var(--s2)",borderRadius:"10px",display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",border:`1px solid ${copiedField==="ref"?"var(--accent)":"transparent"}`,transition:"border 0.2s"}}>
                          <span style={{fontSize:"11px",color:"var(--muted)"}}>Reference:</span>
                          <span style={{fontSize:"13px",fontWeight:"700",color:"var(--accent)",fontFamily:"monospace"}}>{userRef} <span style={{fontSize:"10px",color:copiedField==="ref"?"var(--accent)":"var(--muted)"}}>{copiedField==="ref"?"✓ Copied!":"copy"}</span></span>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Redeem code — hidden in support mode */}
                  {!isSupportMode && <div style={{marginBottom:"20px",padding:"16px",background:darkMode?"rgba(255,179,0,0.04)":"rgba(184,112,8,0.04)",border:`1px solid ${darkMode?"rgba(255,179,0,0.2)":"rgba(184,112,8,0.2)"}`,borderRadius:"14px"}}>
                    <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"12px"}}>
                      <span style={{fontSize:"18px"}}>🎟</span>
                      <span style={{fontSize:"14px",fontWeight:"800",color:"var(--gold)"}}>Redeem a Code</span>
                    </div>
                    <div style={{display:"flex",gap:"8px"}}>
                      <input value={redeemCode} onChange={e => { let v = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g,"").replace(/-+/g,"-").slice(0,12); const m = v.replace(/-/g,"").match(/^([A-Z]+)(\d+)$/); if (m) v = m[1]+"-"+m[2]; setRedeemCode(v); }} placeholder="WORD-1234" style={{flex:1,padding:"14px",background:"var(--s2)",border:"1px solid var(--border)",borderRadius:"12px",color:"var(--text)",fontSize:"14px",fontWeight:"700",fontFamily:"monospace",letterSpacing:"1px",outline:"none"}} />
                      <button disabled={redeemLoading || !redeemCode.trim()} onClick={async () => {
                        setRedeemLoading(true);
                        try {
                          const r = await fetch(`${BACKEND}/api/redeem-pro-code`, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({code:redeemCode.trim(), userId}) });
                          const d = await r.json();
                          if (d.ok) {
                            setIsPro(true); localStorage.setItem("isPro","true"); setProCookie();
                            localStorage.removeItem("trialExpired");
                            if (d.proMinutes === 0) { setProExpiry(null); localStorage.removeItem("proExpiry"); }
                            else if (d.proMinutes > 0) { const exp = new Date(Date.now() + d.proMinutes * 60000).toISOString(); setProExpiry(exp); localStorage.setItem("proExpiry", exp); }
                            localStorage.removeItem("trialStart"); setTrialStart(null);
                            setRedeemCode("");
                            setProGrantedByAdmin(true); setShowPaymentSuccess(true); setTimeout(() => setShowPaymentSuccess(false), 6000);
                          } else { showError(d.error || "Invalid code"); }
                        } catch { showError("Network error. Try again."); }
                        setRedeemLoading(false);
                      }} style={{padding:"14px 20px",background:"var(--gold)",border:"none",borderRadius:"12px",color:"#000",fontSize:"14px",fontWeight:"800",cursor:"pointer",fontFamily:"inherit",opacity:(redeemLoading||!redeemCode.trim())?0.5:1}}>
                        {redeemLoading ? "..." : "Redeem"}
                      </button>
                    </div>
                  </div>}

                  {/* Sadaqah Hadith */}
                  <div style={{marginTop:"8px",padding:"20px",background:darkMode?"rgba(255,179,0,0.03)":"rgba(184,112,8,0.03)",border:`1px solid ${darkMode?"rgba(255,179,0,0.08)":"rgba(184,112,8,0.08)"}`,borderRadius:"16px",textAlign:"center"}}>
                    <div style={{fontSize:"11px",color:"var(--gold)",fontWeight:"700",letterSpacing:"1.5px",textTransform:"uppercase",marginBottom:"14px",opacity:0.7}}>The Prophet ﷺ said</div>
                    <div style={{fontSize:"15px",color:"var(--gold)",fontWeight:"600",lineHeight:1.9,marginBottom:"14px",fontFamily:"'Scheherazade New', serif",direction:"rtl"}}>إِذَا مَاتَ ابْنُ آدَمَ انْقَطَعَ عَمَلُهُ إِلَّا مِنْ ثَلَاثٍ: صَدَقَةٌ جَارِيَةٌ، أَوْ عِلْمٌ يُنْتَفَعُ بِهِ، أَوْ وَلَدٌ صَالِحٌ يَدْعُو لَهُ</div>
                    <div style={{fontSize:"12px",color:"var(--muted)",lineHeight:1.7,maxWidth:"280px",margin:"0 auto 12px"}}>"When the son of Adam dies, his deeds come to an end except for three: ongoing charity, knowledge from which others benefit, or a righteous child who prays for him."</div>
                    <div style={{fontSize:"10px",color:"var(--muted)",opacity:0.5,fontWeight:"600",letterSpacing:"0.5px"}}>Sahih Muslim, 1631</div>
                  </div>
                </div>
              );
            })()}

            {toolView === "siratpro" && !isSupportMode && (() => {
              const tiers = remoteConfig.proTiers || [
                { label: "Annual", price: remoteConfig.proPrice || 99, days: 365 },
                { label: "Full Access", price: remoteConfig.proLifetimePrice || 199, days: 0, badge: "BEST VALUE" }
              ];
              const proExpiryDate = proExpiry ? new Date(proExpiry) : null;
              const isUnlimited = isPro && !proExpiry;
              const isAnnual = isPro && !!proExpiry;
              const features = [
                {icon:"🕌", name:"350+ Masaajid", desc:"Access Masaajid & Ulema across South Africa and the world"},
                {icon:"🔔", name:"Salaah Time Notifications", desc:"Save masjids & get notified for every azaan and salaah"},
                {icon:"📻", name:"Islamic Radio", desc:"HQ streams from South African Islamic radio stations"},
                {icon:"🕐", name:"Salah Tracker", desc:"Track daily prayers · streaks & weekly stats"},
                {icon:"📗", name:"Full Quran", desc:"All 114 Surahs · Arabic, transliteration & translation"},
                {icon:"🎧", name:"Surah Audio", desc:"Gapless ayah-by-ayah playback · 10 reciters"},
                {icon:"🧭", name:"Qibla Compass", desc:"Find the direction of Mecca from anywhere"},
                {icon:"📿", name:"Tasbeeh", desc:"Dhikr counter for daily adhkar"},
                {icon:"🌿", name:"Azkar", desc:"Morning & Evening Adhkar"},
                {icon:"✨", name:"99 Names of Allah", desc:"Asma ul-Husna with meanings"},
                {icon:"🤲", name:"Zakat Calculator", desc:"Calculate your annual Zakat obligation"},
              ];
              return (
                <div style={{padding:"16px",paddingBottom:"100px"}}>
                  {/* Header */}
                  <div style={{display:"flex",alignItems:"center",gap:"12px",marginBottom:"24px"}}>
                    <button onClick={() => { if (siratProSourceRef.current === "settings") { setToolView("grid"); setTab("settings"); siratProSourceRef.current = null; } else { setToolView("grid"); } }} style={{background:"transparent",border:"none",cursor:"pointer",padding:"6px",borderRadius:"10px",color:"var(--text)",fontSize:"22px",lineHeight:1,display:"flex",alignItems:"center",justifyContent:"center"}}>‹</button>
                    <div style={{flex:1,textAlign:"center"}}>
                      <div style={{fontSize:"20px",fontWeight:"800",color:"var(--text)"}}>Sirat Pro</div>
                    </div>
                    <div style={{width:"34px"}}/>
                  </div>

                  {/* Pro Expired state — only show if user previously had trial/pro (not for new users or support mode) */}
                  {!isPro && !isTrial && !remoteConfig.allFree && !isSupportMode && (localStorage.getItem("trialStart") || localStorage.getItem("proExpiry")) && (
                    <div style={{padding:"16px",background:darkMode?"linear-gradient(135deg,#1a0800,#0f0500)":"linear-gradient(135deg,#fff5ee,#fff0e8)",border:"1px solid rgba(255,100,100,0.35)",borderRadius:"16px",display:"flex",alignItems:"center",gap:"12px",marginBottom:"16px"}}>
                      <div style={{width:"42px",height:"42px",borderRadius:"12px",background:"rgba(255,100,100,0.12)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"20px",flexShrink:0}}>⏳</div>
                      <div style={{flex:1}}>
                        <div style={{fontSize:"14px",fontWeight:"800",color:"var(--danger)"}}>Pro Access Expired</div>
                        <div style={{fontSize:"12px",color:"var(--muted)"}}>Renew your subscription to unlock all features</div>
                      </div>
                    </div>
                  )}

                  {/* Trial active state */}
                  {isTrial && (
                    <div style={{padding:"16px",background:"linear-gradient(135deg,#041a15,#071f18)",border:"1px solid rgba(0,201,167,0.35)",borderRadius:"16px",display:"flex",alignItems:"center",gap:"12px",marginBottom:"16px"}}>
                      <div style={{width:"42px",height:"42px",borderRadius:"12px",background:"rgba(0,201,167,0.12)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"20px",flexShrink:0}}>✨</div>
                      <div style={{flex:1}}>
                        <div style={{fontSize:"14px",fontWeight:"800",color:"var(--accent)"}}>Free Trial Active</div>
                        <div style={{fontSize:"12px",color:"var(--muted)"}}>{trialDaysLeft} day{trialDaysLeft !== 1 ? "s" : ""} left — upgrade to keep all features</div>
                      </div>
                    </div>
                  )}

                  {/* Pro active state */}
                  {isPro && (() => {
                    const msLeft = proExpiryDate ? proExpiryDate - new Date() : Infinity;
                    const isShort = msLeft < 86400000 && msLeft > 0; // < 24 hours
                    const color = isShort ? "var(--gold)" : "var(--accent)";
                    const bgGrad = isShort
                      ? (darkMode ? "linear-gradient(135deg,#1a1200,#0f0a00)" : "linear-gradient(135deg,#fffbee,#fff8f0)")
                      : "linear-gradient(135deg,#041210,#071a14)";
                    const borderColor = isShort ? "rgba(255,179,0,0.4)" : "rgba(0,201,167,0.4)";
                    return (
                      <div style={{padding:"16px",background:bgGrad,border:`1px solid ${borderColor}`,borderRadius:"16px",display:"flex",alignItems:"center",gap:"12px",marginBottom:"24px"}}>
                        <div style={{width:"42px",height:"42px",borderRadius:"12px",background:isShort?"rgba(255,179,0,0.15)":"rgba(0,201,167,0.15)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"20px",flexShrink:0}}>{isShort ? "⏳" : "✓"}</div>
                        <div style={{flex:1}}>
                          <div style={{fontSize:"14px",fontWeight:"800",color}}>
                            {isUnlimited ? "Full Access" : isShort ? "Limited Access" : "Active Plan"}
                          </div>
                          <div style={{fontSize:isShort?"14px":"12px",fontWeight:isShort?"700":"400",color:isShort?color:"var(--muted)"}}>
                            {isUnlimited ? "All features unlocked" : formatTimeRemaining(proExpiryDate)}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Redeem Pro Code — top of page for fastest activation */}
                  {!isUnlimited && (
                    <div style={{marginBottom:"20px",padding:"16px",background:darkMode?"rgba(255,179,0,0.04)":"rgba(184,112,8,0.04)",border:`1px solid ${darkMode?"rgba(255,179,0,0.2)":"rgba(184,112,8,0.2)"}`,borderRadius:"14px"}}>
                      <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"12px"}}>
                        <span style={{fontSize:"18px"}}>🎟</span>
                        <span style={{fontSize:"14px",fontWeight:"800",color:"var(--gold)"}}>Redeem a Code</span>
                      </div>
                      <div style={{display:"flex",gap:"8px"}}>
                        <input value={redeemCode} onChange={e => { let v = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g,"").replace(/-+/g,"-").slice(0,12); const m = v.replace(/-/g,"").match(/^([A-Z]+)(\d+)$/); if (m) v = m[1]+"-"+m[2]; setRedeemCode(v); }} placeholder="WORD-1234" style={{flex:1,padding:"14px",background:"var(--s2)",border:"1px solid var(--border)",borderRadius:"12px",color:"var(--text)",fontSize:"14px",fontWeight:"700",fontFamily:"monospace",letterSpacing:"1px",outline:"none"}} />
                        <button disabled={redeemLoading || !redeemCode.trim()} onClick={async () => {
                          setRedeemLoading(true);
                          try {
                            const r = await fetch(`${BACKEND}/api/redeem-pro-code`, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({code:redeemCode.trim(), userId}) });
                            const d = await r.json();
                            if (d.ok) {
                              setIsPro(true); localStorage.setItem("isPro","true"); setProCookie();
                              localStorage.removeItem("trialExpired");
                              if (d.proMinutes === 0) {
                                setProExpiry(null); localStorage.removeItem("proExpiry");
                              } else if (d.proMinutes > 0) {
                                const existingExpiry = proExpiry ? new Date(proExpiry) : null;
                                const startFrom = (existingExpiry && existingExpiry > new Date()) ? existingExpiry : new Date();
                                const exp = new Date(startFrom.getTime() + d.proMinutes * 60000).toISOString();
                                setProExpiry(exp); localStorage.setItem("proExpiry", exp);
                              }
                              localStorage.removeItem("trialStart"); setTrialStart(null);
                              setRedeemCode("");
                              setProGrantedByAdmin(true);
                              setShowPaymentSuccess(true);
                              setTimeout(() => setShowPaymentSuccess(false), 6000);
                            } else {
                              showError(d.error || "Invalid code");
                            }
                          } catch { showError("Network error. Try again."); }
                          setRedeemLoading(false);
                        }} style={{padding:"14px 20px",background:"var(--gold)",border:"none",borderRadius:"12px",color:"#000",fontSize:"14px",fontWeight:"800",cursor:"pointer",fontFamily:"inherit",opacity:(redeemLoading||!redeemCode.trim())?0.5:1}}>
                          {redeemLoading ? "..." : "Redeem"}
                        </button>
                      </div>
                      </div>
                  )}

                  {/* Divider */}
                  {!isUnlimited && !(remoteConfig.paymentDetails || {}).hidden && (
                    <div style={{display:"flex",alignItems:"center",gap:"12px",marginBottom:"20px"}}>
                      <div style={{flex:1,height:"1px",background:darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.08)"}} />
                      <span style={{fontSize:"11px",color:"var(--muted)",fontWeight:"600",whiteSpace:"nowrap"}}>or subscribe below</span>
                      <div style={{flex:1,height:"1px",background:darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.08)"}} />
                    </div>
                  )}

                  {/* Payment section — EFT + PayShap */}
                  {!isUnlimited && (() => {
                    const pd = remoteConfig.paymentDetails || {};
                    const hasPayment = pd.accountNumber || pd.payshapNumber;
                    const price = pd.price || 29;
                    const userRef = (userId || "").slice(-5).toUpperCase();
                    const copyToClipboard = (text, field) => {
                      try {
                        if (navigator.clipboard && navigator.clipboard.writeText) {
                          navigator.clipboard.writeText(text);
                        } else {
                          const ta = document.createElement("textarea");
                          ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
                          document.body.appendChild(ta); ta.select(); document.execCommand("copy");
                          document.body.removeChild(ta);
                        }
                      } catch {}
                      setCopiedField(field);
                      setTimeout(() => setCopiedField(prev => prev === field ? null : prev), 1500);
                    };
                    if (!hasPayment || pd.hidden) return null;
                    return (
                      <div style={{marginBottom:"20px"}}>
                        <div style={{fontSize:"11px",color:"var(--muted)",letterSpacing:"1.5px",textTransform:"uppercase",fontWeight:"700",marginBottom:"12px"}}>{isPro ? "Renew" : "Subscribe"}</div>
                        <div style={{textAlign:"center",marginBottom:"16px"}}>
                          <div style={{fontSize:"36px",fontWeight:"800",color:"var(--gold)"}}>R{price}<span style={{fontSize:"16px",color:"var(--muted)",fontWeight:"600"}}> / 30 days</span></div>
                        </div>

                        {/* PayShap */}
                        {pd.payshapNumber && (
                          <div style={{padding:"16px",background:darkMode?"rgba(0,201,167,0.04)":"rgba(0,158,135,0.04)",border:`1px solid ${darkMode?"rgba(0,201,167,0.2)":"rgba(0,158,135,0.2)"}`,borderRadius:"14px",marginBottom:"10px"}}>
                            <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"10px"}}>
                              <span style={{fontSize:"18px"}}>⚡</span>
                              <span style={{fontSize:"14px",fontWeight:"800",color:"var(--accent)"}}>Pay via PayShap</span>
                              <span style={{fontSize:"10px",color:"var(--muted)",background:"var(--s3)",padding:"2px 8px",borderRadius:"6px",fontWeight:"600"}}>Instant</span>
                            </div>
                            <div onClick={() => copyToClipboard(pd.payshapNumber, "payshap")} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 14px",background:"var(--s2)",borderRadius:"10px",cursor:"pointer",border:`1px solid ${copiedField==="payshap"?"var(--accent)":"var(--border)"}`,transition:"border 0.2s"}}>
                              <span style={{fontSize:"16px",fontWeight:"700",color:"var(--text)",fontFamily:"monospace",letterSpacing:"1px"}}>{pd.payshapNumber}</span>
                              <span style={{fontSize:"11px",color:copiedField==="payshap"?"var(--accent)":"var(--accent)",fontWeight:"600"}}>{copiedField==="payshap"?"\u2713 Copied!":"tap to copy"}</span>
                            </div>
                          </div>
                        )}

                        {/* EFT Bank Details */}
                        {pd.accountNumber && (
                          <div style={{padding:"16px",background:darkMode?"rgba(255,179,0,0.03)":"rgba(184,112,8,0.03)",border:`1px solid ${darkMode?"rgba(255,179,0,0.15)":"rgba(184,112,8,0.15)"}`,borderRadius:"14px",marginBottom:"10px"}}>
                            <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"12px"}}>
                              <span style={{fontSize:"18px"}}>🏦</span>
                              <span style={{fontSize:"14px",fontWeight:"800",color:"var(--gold)"}}>EFT / Bank Transfer</span>
                            </div>
                            {[
                              pd.bankName && {label:"Bank", value:pd.bankName},
                              pd.accountHolder && {label:"Account Holder", value:pd.accountHolder},
                              {label:"Account Number", value:pd.accountNumber, copy:true, field:"account"},
                              pd.branchCode && {label:"Branch Code", value:pd.branchCode},
                              pd.accountType && {label:"Account Type", value:pd.accountType},
                            ].filter(Boolean).map((row, i) => (
                              <div key={i} onClick={row.copy ? () => copyToClipboard(row.value, row.field) : undefined} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${darkMode?"rgba(255,255,255,0.04)":"rgba(0,0,0,0.06)"}`,cursor:row.copy?"pointer":"default"}}>
                                <span style={{fontSize:"12px",color:"var(--muted)"}}>{row.label}</span>
                                <span style={{fontSize:"13px",fontWeight:"700",color:"var(--text)",fontFamily:row.copy?"monospace":"inherit"}}>{row.value} {row.copy && <span style={{fontSize:"10px",color:copiedField===row.field?"var(--accent)":"var(--gold)"}}>{copiedField===row.field?"\u2713 Copied!":"copy"}</span>}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Reference */}
                        <div onClick={() => copyToClipboard(userRef, "ref")} style={{padding:"10px 14px",background:"var(--s2)",borderRadius:"10px",marginBottom:"12px",display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",border:`1px solid ${copiedField==="ref"?"var(--accent)":"transparent"}`,transition:"border 0.2s"}}>
                          <span style={{fontSize:"11px",color:"var(--muted)"}}>Reference:</span>
                          <span style={{fontSize:"13px",fontWeight:"700",color:"var(--accent)",fontFamily:"monospace"}}>{userRef} <span style={{fontSize:"10px",color:copiedField==="ref"?"var(--accent)":"var(--muted)"}}>{copiedField==="ref"?"\u2713 Copied!":"copy"}</span></span>
                        </div>

                        {/* Send Proof — choose: extend my account or get a code */}
                        <div style={{fontSize:"11px",color:"var(--muted)",marginBottom:"8px",textAlign:"center"}}>Send proof by choosing an option:</div>
                        <div style={{display:"flex",gap:"8px"}}>
                          <button onClick={() => { setFeedbackText(`Payment proof for Sirat Pro (R${price} per 30 days)\nRef: ${userRef}\n\n⭐ Please activate Pro on my account`); setToolView("feedback"); setTimeout(() => { if (feedbackImageRef.current) feedbackImageRef.current.click(); }, 600); }} style={{flex:1,padding:"14px 8px",background:"var(--accent)",border:"none",borderRadius:"14px",color:darkMode?"#000":"#fff",fontSize:"12px",fontWeight:"800",cursor:"pointer",fontFamily:"inherit",lineHeight:1.3}}>
                            Activate on{"\n"}My Account
                          </button>
                          <button onClick={() => { setFeedbackText(`Payment proof for Sirat Pro (R${price} per 30 days)\nRef: ${userRef}\n\n🎟 Please send me a Pro code`); setToolView("feedback"); setTimeout(() => { if (feedbackImageRef.current) feedbackImageRef.current.click(); }, 600); }} style={{flex:1,padding:"14px 8px",background:"linear-gradient(135deg,var(--gold),var(--gold2,#ff8f00))",border:"none",borderRadius:"14px",color:darkMode?"#000":"#fff",fontSize:"12px",fontWeight:"800",cursor:"pointer",fontFamily:"inherit",lineHeight:1.3}}>
                            Send Me{"\n"}a Code
                          </button>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Paystack pricing tiers + pay buttons */}
                  {!isUnlimited && paymentsEnabled && remoteConfig.paystackKey && (
                    <div style={{marginBottom:"20px"}}>
                    {tiers.length > 1 && (
                      <div style={{display:"grid",gridTemplateColumns:tiers.length===3?"1fr 1fr 1fr":"1fr 1fr",gap:"10px",marginBottom:"14px"}}>
                        {tiers.map((t, i) => {
                          const isLt = t.days === 0;
                          const sel = selectedProTier === i;
                          const color = isLt ? "var(--gold)" : "var(--accent)";
                          return (
                            <div key={i} onClick={() => setSelectedProTier(i)} style={{padding:"16px 12px",background:sel?(isLt?"linear-gradient(135deg,#1a1200,#0f0a00)":"linear-gradient(135deg,#041a15,#071f18)"):"var(--s2)",border:`1.5px solid ${sel?(isLt?"rgba(255,179,0,0.6)":"rgba(0,201,167,0.6)"):"var(--border)"}`,borderRadius:"14px",cursor:"pointer",textAlign:"center",position:"relative",transition:"border 0.2s"}}>
                              {t.badge && <div style={{position:"absolute",top:"-10px",left:"50%",transform:"translateX(-50%)",background:"var(--gold)",color:"#07090c",fontSize:"9px",fontWeight:"800",padding:"3px 8px",borderRadius:"20px",letterSpacing:"0.5px",whiteSpace:"nowrap"}}>{t.badge}</div>}
                              <div style={{fontSize:"22px",fontWeight:"800",color:sel?color:"var(--text)"}}>R{t.price}</div>
                              <div style={{fontSize:"11px",color:sel?color:"var(--muted)",fontWeight:"600",marginTop:"2px"}}>{tierLabel(t)}</div>
                              <div style={{fontSize:"11px",color:"var(--muted)",marginTop:"6px"}}>{t.label} Access</div>
                              {sel && <div style={{fontSize:"10px",color:color,fontWeight:"700",marginTop:"6px",letterSpacing:"0.5px"}}>SELECTED ✓</div>}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {tiers[selectedProTier] && (
                      <button
                        disabled={paystackLoading}
                        onClick={() => handlePaystack(tiers[selectedProTier])}
                        style={{width:"100%",padding:"17px",background:paystackLoading?"var(--s3)":"linear-gradient(110deg, #b8860b 0%, #ffb300 25%, #ffe066 50%, #ffb300 75%, #b8860b 100%)",backgroundSize:"200% 100%",animation:paystackLoading?"none":"proShimmer 3s linear infinite",border:"1px solid rgba(255,179,0,0.3)",borderRadius:"14px",color:paystackLoading?"var(--muted)":"#1a0e00",fontSize:"17px",fontWeight:"800",cursor:paystackLoading?"wait":"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:"8px",boxShadow:"0 0 20px rgba(255,179,0,0.2), 0 0 40px rgba(255,179,0,0.1)",letterSpacing:"0.3px",transition:"all 0.3s ease"}}
                      >
                        {paystackLoading ? <><span className="sp" style={{width:18,height:18,border:"2px solid var(--muted)",borderTopColor:"var(--gold)"}} /> Opening checkout...</> : <>{isPro ? "Renew" : isTrial ? "Upgrade Now" : "Get Sirat Pro"} — R{tiers[selectedProTier].price} for {tierLabel(tiers[selectedProTier])}</>}
                      </button>
                    )}
                    </div>
                  )}

                  {/* Feature list */}
                  <div style={{fontSize:"11px",color:"var(--muted)",letterSpacing:"1.5px",textTransform:"uppercase",fontWeight:"700",marginBottom:"12px"}}>What's included</div>
                  <div style={{display:"flex",flexDirection:"column",gap:"2px",marginBottom:"24px"}}>
                    {features.map(f => (
                      <div key={f.name} style={{display:"flex",alignItems:"center",gap:"12px",padding:"11px 12px",background:"var(--s2)",borderRadius:"12px"}}>
                        <div style={{width:"38px",height:"38px",borderRadius:"10px",background:"var(--s3)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"18px",flexShrink:0}}>{f.icon}</div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:"14px",fontWeight:"700",color:"var(--text)"}}>{f.name}</div>
                          <div style={{fontSize:"11px",color:"var(--muted)"}}>{f.desc}</div>
                        </div>
                        <div style={{fontSize:"13px",color:"var(--accent)",flexShrink:0}}>✓</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            </div>{/* end inner scroll div */}
            </div>
            )}
          </>
        )}

        {/* Feedback — WhatsApp-style chat */}
        {toolView === "feedback" && (() => {
          // Build timeline from server history
          const timeline = [];
          const sorted = [...feedbackHistory].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
          sorted.forEach(f => {
            // If a local sent message matches this server entry, prefer the local image (server may not store it)
            const matchingLocal = localSentMsgs.find(l =>
              l.text === f.message && Math.abs(new Date(l.time) - new Date(f.createdAt)) < 30000
            );
            timeline.push({ type: "user", text: f.message, image: (matchingLocal && matchingLocal.image) || f.image || null, time: f.createdAt, id: f._id });
            (f.replies || []).forEach((r, ri) => {
              timeline.push({ type: "admin", text: r.text, image: r.image || null, time: r.createdAt, id: (f._id || "") + "-r" + ri });
            });
          });
          // Add locally sent messages not yet confirmed by server
          localSentMsgs.forEach(l => {
            const alreadyInServer = feedbackHistory.some(f =>
              f.message === l.text && Math.abs(new Date(l.time) - new Date(f.createdAt)) < 30000
            );
            if (!alreadyInServer) {
              timeline.push({ type: "user", text: l.text, image: l.image, time: l.time, id: "local-" + l.id });
            }
          });
          // Sort everything by time
          timeline.sort((a, b) => new Date(a.time) - new Date(b.time));

          const sw = fbSwipeRef.current;
          return (
          <div
            ref={feedbackPanelRef}
            onTouchStart={e => {
              const x = e.touches[0].clientX;
              if (x > 40) return;
              sw.startX = x;
              sw.startY = e.touches[0].clientY;
              sw.startT = Date.now();
              sw.moving = false;
            }}
            onTouchMove={e => {
              if (sw.startX === null) return;
              const dx = e.touches[0].clientX - sw.startX;
              const dy = Math.abs(e.touches[0].clientY - sw.startY);
              if (!sw.moving) {
                if (dy > 8) { sw.startX = null; return; }
                if (dx < 6) return;
                sw.moving = true;
              }
              if (dx > 0) {
                e.preventDefault();
                const el = feedbackPanelRef.current;
                if (el) { el.style.transform = `translateX(${dx}px)`; el.style.transition = 'none'; }
              }
            }}
            onTouchEnd={e => {
              if (sw.startX === null || !sw.moving) { sw.startX = null; sw.moving = false; return; }
              const dx = e.changedTouches[0].clientX - sw.startX;
              const dt = Date.now() - sw.startT;
              const velocity = dx / dt;
              sw.startX = null; sw.moving = false;
              const el = feedbackPanelRef.current;
              if (!el) return;
              if (dx > window.innerWidth * 0.35 || (velocity >= 0.4 && dx > 40)) {
                el.style.transform = 'translateX(110%)';
                el.style.transition = 'transform 0.25s cubic-bezier(0.2,0.9,0.3,1)';
                setTimeout(() => { setTab("settings"); setToolView("grid"); }, 250);
              } else {
                el.style.transform = '';
                el.style.transition = 'transform 0.25s cubic-bezier(0.2,0.9,0.3,1)';
              }
            }}
            style={{position:"fixed",top:vvOffsetTop||0,left:0,right:0,height:vvHeight?`${vvHeight}px`:"100%",zIndex:9998,background:darkMode?"#0a0f14":"#f5f0e8",display:"flex",flexDirection:"column",userSelect:"none",willChange:"transform",paddingLeft:"env(safe-area-inset-left)",paddingRight:"env(safe-area-inset-right)"}}
          >
            {/* Header */}
            <div style={{paddingTop:"env(safe-area-inset-top)",background:darkMode?"linear-gradient(135deg,#0a0f14,#0d1520)":"linear-gradient(135deg,#f5f0e8,#f0ebe0)",borderBottom:`1px solid ${darkMode?"rgba(255,179,0,0.12)":"rgba(184,112,8,0.12)"}`,flexShrink:0}}>
              <div style={{padding:"12px 16px",display:"flex",alignItems:"center",gap:"12px"}}>
                <button onClick={() => { setTab("settings"); setToolView("grid"); }} style={{background:"transparent",border:"none",cursor:"pointer",width:"36px",height:"36px",borderRadius:"50%",color:"var(--gold)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  <svg width="10" height="18" viewBox="0 0 10 18" fill="none"><path d="M9 1L1 9l8 8" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
                <div style={{width:"34px",height:"34px",borderRadius:"10px",background:darkMode?"linear-gradient(135deg,#041210,#071a14)":"linear-gradient(135deg,#e0f7f3,#eef8f6)",border:darkMode?"1px solid rgba(0,201,167,0.35)":"1px solid rgba(0,158,135,0.3)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  <span style={{fontSize:"15px",fontWeight:"900",color:"var(--accent)"}}>S</span>
                </div>
                <div style={{flex:1}}>
                  <div style={{fontSize:"16px",fontWeight:"800",color:"var(--gold)"}}>Sirat Support</div>
                  <div style={{fontSize:"10px",color:"var(--muted)",marginTop:"1px",letterSpacing:"0.3px"}}>We typically reply within a day</div>
                </div>
                <div style={{display:"flex",gap:"3px",background:"var(--s3)",borderRadius:"10px",padding:"3px",flexShrink:0}}>
                  {[["dark","\uD83C\uDF19"],["light","\u2600\uFE0F"]].map(([t,icon]) => (
                    <button key={t}
                      onClick={() => { setDarkMode(t === "dark"); if (showThemeHint) { setShowThemeHint(false); localStorage.setItem("themeHintShown","1"); } }}
                      style={{background:theme===t?"var(--s1)":"transparent",border:"none",borderRadius:"8px",cursor:"pointer",padding:"5px 8px",fontSize:"14px",color:theme===t?"var(--text)":"var(--muted)",boxShadow:theme===t?"0 1px 4px rgba(0,0,0,0.18)":"none",transition:"all 0.2s",lineHeight:1,fontFamily:"inherit"}}
                    >{icon}</button>
                  ))}
                </div>
                {!feedbackPhone ? (
                  <button onClick={() => { setShowFeedbackPhone(true); }} style={{background:darkMode?"rgba(255,179,0,0.08)":"rgba(184,112,8,0.08)",border:`1px solid ${darkMode?"rgba(255,179,0,0.2)":"rgba(184,112,8,0.2)"}`,borderRadius:"10px",cursor:"pointer",padding:"7px 10px",display:"flex",alignItems:"center",gap:"5px"}}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="var(--gold)"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a8 8 0 01-4.243-1.212L4 20l1.212-3.757A8 8 0 1112 20z" fillRule="evenodd"/></svg>
                    <span style={{fontSize:"10px",fontWeight:"700",color:"var(--gold)"}}>Add</span>
                  </button>
                ) : (
                  <button onClick={() => { setShowFeedbackPhone(true); }} style={{background:darkMode?"rgba(37,211,102,0.08)":"rgba(37,211,102,0.08)",border:"1px solid rgba(37,211,102,0.25)",borderRadius:"10px",cursor:"pointer",padding:"7px 10px",display:"flex",alignItems:"center",gap:"5px"}}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a8 8 0 01-4.243-1.212L4 20l1.212-3.757A8 8 0 1112 20z" fillRule="evenodd"/></svg>
                    <span style={{fontSize:"10px",fontWeight:"600",color:"#25D366"}}>Linked</span>
                  </button>
                )}
              </div>
            </div>

            {/* Messages area */}
            <div
              ref={feedbackScrollRef}
              onTouchStart={e => {
                const el = feedbackScrollRef.current;
                if (!el) return;
                if (el.scrollTop <= 2) feedbackPullStartRef.current = e.touches[0].clientY;
                else feedbackPullStartRef.current = null;
              }}
              onTouchMove={e => {
                if (feedbackPullStartRef.current === null || feedbackRefreshing) return;
                const dy = e.touches[0].clientY - feedbackPullStartRef.current;
                if (dy > 0) setFeedbackPullY(Math.min(dy * 0.5, 60));
                else { setFeedbackPullY(0); feedbackPullStartRef.current = null; }
              }}
              onTouchEnd={() => {
                if (feedbackPullY > 45 && !feedbackRefreshing) {
                  setFeedbackRefreshing(true);
                  setFeedbackPullY(40);
                  fetchFeedbackHistory().then(() => {
                    setFeedbackRefreshing(false);
                    setFeedbackPullY(0);
                  });
                } else {
                  setFeedbackPullY(0);
                }
                feedbackPullStartRef.current = null;
              }}
              style={{flex:1,overflowY:"auto",WebkitOverflowScrolling:"touch",padding:"12px 16px 8px",display:"flex",flexDirection:"column",position:"relative",
                background:`${darkMode?"#080d12":"#f0ebe3"} url("data:image/svg+xml,${encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><defs><pattern id='p' width='120' height='120' patternUnits='userSpaceOnUse'><g opacity='${darkMode?"0.04":"0.08"}'><circle cx='60' cy='60' r='18' fill='none' stroke='${darkMode?"#ffb300":"#8b7355"}' stroke-width='0.5'/><circle cx='60' cy='60' r='8' fill='none' stroke='${darkMode?"#ffb300":"#8b7355"}' stroke-width='0.4'/><line x1='60' y1='42' x2='60' y2='78' stroke='${darkMode?"#ffb300":"#8b7355"}' stroke-width='0.3'/><line x1='42' y1='60' x2='78' y2='60' stroke='${darkMode?"#ffb300":"#8b7355"}' stroke-width='0.3'/><line x1='47' y1='47' x2='73' y2='73' stroke='${darkMode?"#ffb300":"#8b7355"}' stroke-width='0.3'/><line x1='73' y1='47' x2='47' y2='73' stroke='${darkMode?"#ffb300":"#8b7355"}' stroke-width='0.3'/><circle cx='0' cy='0' r='2' fill='${darkMode?"#ffb300":"#8b7355"}'/><circle cx='120' cy='0' r='2' fill='${darkMode?"#ffb300":"#8b7355"}'/><circle cx='0' cy='120' r='2' fill='${darkMode?"#ffb300":"#8b7355"}'/><circle cx='120' cy='120' r='2' fill='${darkMode?"#ffb300":"#8b7355"}'/><circle cx='60' cy='0' r='1.2' fill='${darkMode?"#ffb300":"#8b7355"}'/><circle cx='60' cy='120' r='1.2' fill='${darkMode?"#ffb300":"#8b7355"}'/><circle cx='0' cy='60' r='1.2' fill='${darkMode?"#ffb300":"#8b7355"}'/><circle cx='120' cy='60' r='1.2' fill='${darkMode?"#ffb300":"#8b7355"}'/></g></pattern></defs><rect width='120' height='120' fill='url(%23p)'/></svg>`)}")`,
              }}
            >
              {/* Subtle watermark */}
              <div style={{position:"sticky",top:"40%",left:0,right:0,display:"flex",flexDirection:"column",alignItems:"center",pointerEvents:"none",zIndex:0,marginBottom:"-80px",opacity:darkMode?0.025:0.055}}>
                <div style={{fontSize:"42px",fontWeight:"900",letterSpacing:"6px",color:darkMode?"#ffb300":"#a08060"}}>SIRAT</div>
                <div style={{fontSize:"9px",fontWeight:"600",letterSpacing:"4px",color:darkMode?"#ffb300":"#a08060",marginTop:"2px"}}>THE STRAIGHT PATH</div>
              </div>
              {/* Pull-to-refresh indicator */}
              {(feedbackPullY > 0 || feedbackRefreshing) && (
                <div style={{display:"flex",justifyContent:"center",paddingBottom:"8px",transition:feedbackRefreshing?"padding 0.2s":"none"}}>
                  <div style={{width:"28px",height:"28px",borderRadius:"50%",border:"2.5px solid var(--border)",borderTopColor:"var(--accent)",animation:feedbackRefreshing?"sp 0.8s linear infinite":"none",transform:`rotate(${feedbackPullY * 4}deg)`,opacity:Math.min(feedbackPullY / 40, 1)}} />
                </div>
              )}

              {/* Spinner only on initial load (no messages yet) */}
              {feedbackHistoryLoading && timeline.length === 0 && (
                <div style={{display:"flex",alignItems:"center",justifyContent:"center",flex:1}}>
                  <div style={{width:"28px",height:"28px",borderRadius:"50%",border:"2.5px solid var(--border)",borderTopColor:"var(--accent)",animation:"sp 0.8s linear infinite"}} />
                </div>
              )}

              {!feedbackHistoryLoading && timeline.length === 0 && (
                <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",flex:1,textAlign:"center",padding:"20px"}}>
                  <div style={{width:"80px",height:"80px",borderRadius:"50%",background:darkMode?"radial-gradient(circle, rgba(255,179,0,0.1), rgba(255,179,0,0.03))":"radial-gradient(circle, rgba(184,112,8,0.08), rgba(184,112,8,0.02))",border:`1.5px solid ${darkMode?"rgba(255,179,0,0.2)":"rgba(184,112,8,0.2)"}`,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:"18px",boxShadow:`0 8px 30px ${darkMode?"rgba(255,179,0,0.08)":"rgba(184,112,8,0.06)"}`}}>
                    <span style={{fontSize:"30px",fontWeight:"900",color:"var(--gold)",opacity:0.8}}>S</span>
                  </div>
                  <div style={{fontSize:"22px",fontWeight:"800",color:"var(--gold)",marginBottom:"4px",fontFamily:"'Scheherazade New', 'Amiri', serif"}}>السلام عليكم</div>
                  <div style={{fontSize:"15px",fontWeight:"700",color:"var(--gold)",marginBottom:"8px"}}>Assalamu Alaykum</div>
                  <div style={{fontSize:"13px",color:"var(--muted)",lineHeight:1.6,maxWidth:"260px"}}>Have a suggestion, found a bug, or need help? Send us a message below.</div>
                </div>
              )}

              {/* Spacer to push messages to bottom when few messages */}
              <div style={{flex:1}} />

              {timeline.map((m, i) => {
                const isUser = m.type === "user";
                const time = new Date(m.time).toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" });
                const date = new Date(m.time).toLocaleDateString("en-ZA", { day: "numeric", month: "short" });
                // Show date separator if first message or different day from previous
                const prevDate = i > 0 ? new Date(timeline[i-1].time).toLocaleDateString("en-ZA", { day: "numeric", month: "short" }) : null;
                const showDate = i === 0 || date !== prevDate;
                return (
                  <div key={m.id || i}>
                    {showDate && (
                      <div style={{display:"flex",justifyContent:"center",padding:"12px 0 8px"}}>
                        <span style={{fontSize:"10px",color:"var(--muted)",background:"var(--s1)",padding:"3px 12px",borderRadius:"10px",fontWeight:"600"}}>{date}</span>
                      </div>
                    )}
                    <div style={{display:"flex",justifyContent:isUser?"flex-end":"flex-start",marginBottom:"4px"}}>
                      <div style={{maxWidth:"82%"}}>
                        <div style={{
                          background: isUser ? (darkMode ? "rgba(255,179,0,0.06)" : "var(--s2)") : (darkMode ? "rgba(0,201,167,0.08)" : "rgba(0,158,135,0.06)"),
                          border: isUser ? `1px solid ${darkMode?"rgba(255,179,0,0.15)":"var(--border)"}` : (darkMode ? "1px solid rgba(0,201,167,0.2)" : "1px solid rgba(0,158,135,0.18)"),
                          borderLeft: isUser ? undefined : `3px solid ${darkMode?"rgba(0,201,167,0.4)":"rgba(0,158,135,0.35)"}`,
                          borderRadius: isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                          padding: m.image && !m.text ? "6px 6px 5px" : "9px 12px 5px"
                        }}>
                          {m.image && <img src={m.image} alt="attachment" style={{display:"block",maxWidth:"220px",maxHeight:"180px",objectFit:"cover",borderRadius:"10px",marginBottom:m.text?"6px":"2px",cursor:"zoom-in"}} onClick={() => setFeedbackLightbox(m.image)} />}
                          {m.text ? <div style={{fontSize:"13.5px",color:"var(--text)",whiteSpace:"pre-wrap",lineHeight:1.5}}>{m.text}</div> : null}
                          <div style={{fontSize:"9px",color:"var(--muted)",textAlign:isUser?"right":"left",marginTop:"2px",paddingLeft:m.image && !m.text?"6px":0,paddingRight:m.image && !m.text?"6px":0}}>{time}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* WhatsApp phone input — slides in above compose */}
            {showFeedbackPhone && (() => {
              const fbDigits = feedbackPhone.replace(/[^0-9]/g, "");
              const fbValid = fbDigits.length === 11 && fbDigits.startsWith("27");
              return (
              <div ref={el => { if (el) { const scroll = () => el.scrollIntoView({ behavior:"smooth", block:"nearest" }); scroll(); setTimeout(scroll, 300); setTimeout(scroll, 600); } }} style={{flexShrink:0,borderTop:`1px solid ${fbValid?"rgba(37,211,102,0.3)":darkMode?"rgba(255,179,0,0.12)":"rgba(184,112,8,0.12)"}`,background:darkMode?"rgba(255,179,0,0.03)":"rgba(184,112,8,0.03)",padding:"10px 14px",paddingBottom:"calc(10px + env(safe-area-inset-bottom))",animation:"fadeSlideUp 0.25s ease-out"}}>
                <div style={{fontSize:"11px",fontWeight:"700",color:fbValid?"#25D366":"var(--gold)",marginBottom:"6px",transition:"color 0.3s"}}>WhatsApp number{fbValid ? " ✓" : ""}</div>
                <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
                  <div style={{flex:1,display:"flex",alignItems:"center",background:"var(--s2)",border:`1px solid ${fbValid?"#25D366":"var(--border)"}`,borderRadius:"12px",overflow:"hidden",transition:"border-color 0.3s"}}>
                    <span style={{padding:"10px 0 10px 14px",color:fbValid?"#25D366":"var(--text)",fontSize:"14px",fontWeight:"700",flexShrink:0,transition:"color 0.3s"}}>+</span>
                    <input
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      name="phone"
                      value={feedbackPhone}
                      onChange={e => {
                        let digits = e.target.value.replace(/[^0-9]/g, "");
                        if (!digits) { setFeedbackPhone(""); return; }
                        // iOS autofill: (083) 786-0807 → convert leading 0 to 27
                        if (digits.startsWith("0")) digits = "27" + digits.slice(1);
                        digits = digits.slice(0, 11); // max 11 digits (27 + 9)
                        // Format: CC XX XXX XXXX (e.g. 27 83 786 0807)
                        const cc = digits.slice(0, 2);
                        const p1 = digits.slice(2, 4);
                        const p2 = digits.slice(4, 7);
                        const p3 = digits.slice(7, 11);
                        setFeedbackPhone([cc, p1, p2, p3].filter(Boolean).join(" "));
                      }}
                      placeholder="27 XX XXX XXXX"
                      maxLength={14}
                      autoFocus
                      onFocus={e => { const el = e.target; setTimeout(() => el.scrollIntoView({ behavior:"smooth", block:"center" }), 400); setTimeout(() => el.scrollIntoView({ behavior:"smooth", block:"center" }), 800); }}
                      style={{flex:1,background:"transparent",border:"none",padding:"10px 14px 10px 2px",color:fbValid?"#25D366":"var(--text)",fontSize:"14px",fontFamily:"inherit",outline:"none",boxSizing:"border-box",letterSpacing:"0.5px",transition:"color 0.3s"}}
                    />
                    {fbValid && <span style={{padding:"0 12px 0 0",fontSize:"16px",color:"#25D366",flexShrink:0,animation:"fadeIn 0.3s ease-out"}}>✓</span>}
                  </div>
                  <button onClick={() => { if (fbDigits.length > 0 && !fbValid) return; setShowFeedbackPhone(false); if (feedbackPhone.trim() && fbValid) localStorage.setItem("feedbackPhone", feedbackPhone.trim()); }} disabled={fbDigits.length > 0 && !fbValid} style={{background:fbValid?"#25D366":fbDigits.length > 0 && !fbValid?"var(--s3)":"var(--gold)",border:"none",borderRadius:"12px",cursor:fbDigits.length > 0 && !fbValid?"not-allowed":"pointer",color:fbValid?"#fff":fbDigits.length > 0 && !fbValid?"var(--muted)":"#07090c",fontSize:"13px",fontWeight:"700",padding:"10px 16px",fontFamily:"inherit",whiteSpace:"nowrap",transition:"background 0.3s, color 0.3s",opacity:fbDigits.length > 0 && !fbValid?0.5:1}}>Save</button>
                </div>
                <div style={{fontSize:"10px",color:fbValid?"#25D366":fbDigits.length > 0 && fbDigits.length < 11?"var(--danger)":"var(--muted)",marginTop:"4px",paddingLeft:"2px",transition:"color 0.3s"}}>{fbValid ? "Valid SA mobile number" : fbDigits.length > 0 ? `${fbDigits.length}/11 digits${!fbDigits.startsWith("27") ? " — must start with 27" : ""}` : "Optional — we can also reply on WhatsApp"}</div>
              </div>
              );
            })()}

            {/* Compose bar — hidden when phone input is showing */}
            {!showFeedbackPhone && (
            <div style={{flexShrink:0,borderTop:"1px solid var(--border)",background:"var(--s1)",padding:"8px 16px",paddingBottom:"calc(12px + env(safe-area-inset-bottom))"}}>
              {/* Image preview strip */}
              {feedbackImage && (
                <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"8px"}}>
                  <div style={{position:"relative",display:"inline-block"}}>
                    <img src={feedbackImage} alt="attachment preview" style={{height:"64px",maxWidth:"120px",objectFit:"cover",borderRadius:"10px",border:"1px solid var(--border)"}} />
                    <button
                      onClick={() => { setFeedbackImage(null); if (feedbackImageRef.current) feedbackImageRef.current._pendingImage = null; }}
                      style={{position:"absolute",top:"-6px",right:"-6px",width:"18px",height:"18px",borderRadius:"50%",background:"var(--danger)",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0}}
                    >
                      <svg width="8" height="8" viewBox="0 0 10 10" fill="white"><path d="M1 1l8 8M9 1l-8 8" stroke="white" strokeWidth="2" strokeLinecap="round"/></svg>
                    </button>
                  </div>
                </div>
              )}
              {/* Hidden file input */}
              <input
                ref={feedbackImageRef}
                type="file"
                accept="image/*"
                style={{display:"none"}}
                onChange={async e => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const compressed = await compressFeedbackImage(file);
                  if (compressed) {
                    if (feedbackImageRef.current) feedbackImageRef.current._pendingImage = compressed;
                    setFeedbackImage(compressed);
                  }
                  e.target.value = "";
                }}
              />
              <div style={{display:"flex",alignItems:"flex-end",gap:"8px"}}>
                {/* Attach image button */}
                <button
                  onClick={() => feedbackImageRef.current?.click()}
                  style={{width:"38px",height:"38px",borderRadius:"50%",background:"var(--s2)",border:"1px solid var(--border)",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,opacity:feedbackImage?1:0.75}}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="3" stroke="var(--gold)" strokeWidth="1.8"/><circle cx="8.5" cy="8.5" r="1.5" fill="var(--gold)"/><path d="M21 15l-5-5L5 21" stroke="var(--gold)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
                <textarea
                  ref={el => { if (el && feedbackText && el.scrollHeight > el.clientHeight) { el.style.height = "auto"; el.style.height = Math.min(el.scrollHeight, 120) + "px"; } }}
                  value={feedbackText}
                  onChange={e => { setFeedbackText(e.target.value); e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px"; }}
                  placeholder="Type a message..."
                  maxLength={500}
                  rows={1}
                  style={{flex:1,background:"var(--s2)",border:"1px solid var(--border)",borderRadius:"20px",padding:"10px 14px",color:"var(--text)",fontSize:"14px",fontFamily:"inherit",resize:"none",outline:"none",boxSizing:"border-box",maxHeight:"120px",lineHeight:"1.4"}}
                />
                <button
                  onClick={sendFeedback}
                  disabled={(!feedbackText.trim() && !feedbackImage) || feedbackSending}
                  style={{width:"38px",height:"38px",borderRadius:"50%",background:(feedbackText.trim()||feedbackImage)?"var(--gold)":"var(--s3)",border:"none",cursor:(feedbackText.trim()||feedbackImage)?"pointer":"default",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,opacity:feedbackSending?0.6:1,transition:"background 0.2s"}}
                >
                  {feedbackSending
                    ? <div style={{width:"16px",height:"16px",borderRadius:"50%",border:"2px solid rgba(255,255,255,0.3)",borderTopColor:"#fff",animation:"sp 0.7s linear infinite"}} />
                    : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{marginLeft:"2px"}}><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" fill={(feedbackText.trim()||feedbackImage)?"#07090c":"var(--muted)"}/></svg>
                  }
                </button>
              </div>
            </div>
            )}
            {/* Lightbox overlay for full-size image */}
            {feedbackLightbox && (
              <div onClick={() => setFeedbackLightbox(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.95)",zIndex:9999,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
                <img src={feedbackLightbox} alt="full size" style={{maxWidth:"100%",maxHeight:"90%",objectFit:"contain",cursor:"zoom-out"}} />
                <div style={{position:"fixed",top:"max(16px,env(safe-area-inset-top))",right:16,width:40,height:40,borderRadius:"50%",background:"rgba(255,255,255,0.2)",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:22,color:"#fff",zIndex:10000}} onClick={() => setFeedbackLightbox(null)}>✕</div>
                <div style={{position:"fixed",bottom:"max(24px,env(safe-area-inset-bottom))",left:"50%",transform:"translateX(-50%)",fontSize:12,color:"rgba(255,255,255,0.45)",pointerEvents:"none",whiteSpace:"nowrap"}}>Tap anywhere to close</div>
              </div>
            )}
          </div>
          );
        })()}
        {/* Hayah — body & soul hub (Ghidha + Fasting + Muraqabah + Exercise) */}
        {tab === "hayah" && (
          <HayahHub
            darkMode={darkMode}
            hayahFeature={hayahFeature}
            setHayahFeature={setHayahFeature}
            ghidhaResetKey={hayahResetKey}
            prayerTimes={prayerTimes}
            userName={userName}
            onLogoClick={openFanMenu}
            onOpenTool={(view) => { setTab("tools"); setToolView(view); }}
          />
        )}
        {/* Maintainer Dashboard */}
        {tab === "maintainer" && isMaintainer && (
          <Lazy>
            <MaintainerDashboard
              userId={userId}
              BACKEND={BACKEND}
              remoteConfig={remoteConfig}
              darkMode={darkMode}
              onUnreadChange={setMaintainerUnreadCount}
            />
          </Lazy>
        )}
        {/* Settings */}
        {tab === "settings" && (() => {
          const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone || isNative();
          const buildTime = process.env.REACT_APP_BUILD_TIME;
          // "Mar 30 20:31 SAST" → "30 Mar 2026 20:31 SAST"
          const deployedStr = buildTime ? buildTime.trim() : null;
          // Editorial tokens (mirrors Ibadah / Radio / Salaah closures)
          const ST_MONO = '"JetBrains Mono",ui-monospace,SFMono-Regular,Menlo,monospace';
          const ST_SERIF = '"Instrument Serif",Georgia,serif';
          const ST_GOLD = darkMode ? "#ffb300" : "#b87008";
          const ST_GOLD2 = darkMode ? "#cc8e00" : "#8a5406";
          const ST_ACCENT = darkMode ? "#00c9a7" : "#009e87";
          const ST_MUTED = darkMode ? "#5a7387" : "#8293a3";
          const ST_BORDER = darkMode ? "rgba(255,179,0,0.14)" : "rgba(184,112,8,0.16)";
          const settingsWeekday = new Date().toLocaleDateString(undefined, {weekday:"long"});
          const settingsEyebrow = `${settingsWeekday}${hijri ? " · " + hijri : ""}`;
          return (
          <div className="settings-wrap">
            {/* Editorial header — matches Radio / Salaah / Ibadah */}
            <div style={{padding:"14px 20px 18px",paddingTop:stickyHeaderTopPad,display:"flex",alignItems:"flex-end",justifyContent:"space-between",gap:"12px",position:"sticky",top:0,zIndex:10,background:darkMode?`rgba(20,16,10,${0.65*headerScroll})`:`rgba(250,243,225,${0.7*headerScroll})`,backdropFilter:`blur(${34*headerScroll}px) saturate(200%)`,WebkitBackdropFilter:`blur(${34*headerScroll}px) saturate(200%)`,borderBottom:`1px solid ${darkMode?`rgba(255,179,0,${0.28*headerScroll})`:`rgba(184,112,8,${0.28*headerScroll})`}`,boxShadow:`0 8px 18px ${darkMode?`rgba(0,0,0,${0.22*headerScroll})`:`rgba(0,0,0,${0.05*headerScroll})`}`,transition:"background .15s ease, border-color .15s ease, box-shadow .15s ease"}}>
              <div style={{minWidth:0}}>
                <div ref={hijriRef} onClick={toggleHijriCalendar} role="button" tabIndex={0}
                  onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleHijriCalendar(); } }}
                  aria-label="Open Hijri calendar"
                  style={{fontFamily:ST_MONO,fontSize:"9.5px",letterSpacing:"2.5px",color:ST_MUTED,textTransform:"uppercase",marginBottom:"6px",cursor:"pointer",display:"inline-block",whiteSpace:"nowrap"}}>{settingsEyebrow}</div>
                <div style={{display:"flex",alignItems:"center",gap:"10px"}}>
                  <div
                    ref={logoRef}
                    onClick={() => { openFanMenu(); if (showLogoHint) { setShowLogoHint(false); localStorage.setItem("logoHintShown","1"); } }}
                    aria-label="Sirat shortcuts"
                    style={{width:"36px",height:"36px",borderRadius:"11px",flexShrink:0,visibility: showFanMenu ? "hidden" : "visible",
                      background: darkMode ? "rgba(4,18,16,0.5)" : "rgba(224,247,243,0.5)",
                      backdropFilter:"blur(16px) saturate(180%)", WebkitBackdropFilter:"blur(16px) saturate(180%)",
                      border: darkMode ? "1px solid rgba(0,201,167,0.25)" : "1px solid rgba(0,158,135,0.2)",
                      display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",
                      boxShadow: darkMode ? "0 2px 12px rgba(0,201,167,0.15)" : "0 2px 12px rgba(0,158,135,0.1)",
                      animation: remoteConfig.iconGlow !== false ? "logoPulse 3s ease-in-out infinite" : "none"}}>
                    <svg viewBox="0 0 44 44" width="30" height="30" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85"/>
                      <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85" transform="rotate(60 22 22)"/>
                      <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85" transform="rotate(120 22 22)"/>
                    </svg>
                  </div>
                  <div style={{fontFamily:ST_SERIF,fontStyle:"italic",fontSize:"34px",color:"var(--text)",letterSpacing:"-0.02em",lineHeight:1}}>Settings</div>
                </div>
              </div>
              {deployedStr && (
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{fontFamily:ST_MONO,fontSize:"9.5px",letterSpacing:"2px",color:ST_MUTED,textTransform:"uppercase",marginBottom:"2px"}}>Deployed</div>
                  <div style={{fontFamily:ST_MONO,fontSize:"11px",color:ST_GOLD,letterSpacing:"0.3px"}}>{deployedStr}</div>
                </div>
              )}
            </div>
            {/* Profile card — hero-lite, editorial */}
            {userName && (
              <div className="settings-card" style={{padding:"22px 22px 18px"}}>
                <div style={{fontFamily:ST_MONO,fontSize:"9.5px",letterSpacing:"2.5px",color:ST_MUTED,textTransform:"uppercase",marginBottom:"14px"}}>Profile</div>
                <div style={{display:"flex",alignItems:"center",gap:"16px"}}>
                  {/* Avatar with photo picker — bigger, gold ring */}
                  <div style={{position:"relative",width:"76px",height:"76px",flexShrink:0,cursor:"pointer"}} onClick={() => {
                    if (profilePhoto) {
                      const action = window.confirm("Change photo?\n\nOK = new photo\nCancel = remove photo");
                      if (action) { document.getElementById("profile-photo-input").click(); }
                      else { setProfilePhoto(""); localStorage.removeItem("profilePhoto"); }
                    } else {
                      document.getElementById("profile-photo-input").click();
                    }
                  }}>
                    {profilePhoto ? (
                      <img src={profilePhoto} alt="" style={{width:"76px",height:"76px",borderRadius:"50%",objectFit:"cover",border:`2px solid ${ST_GOLD}`,boxShadow:`0 0 0 2px ${darkMode?"rgba(14,10,6,0.9)":"rgba(255,250,240,0.9)"}, 0 6px 18px ${ST_GOLD}33`}} />
                    ) : (
                      <div style={{width:"76px",height:"76px",borderRadius:"50%",
                        background:`radial-gradient(circle at 30% 30%, ${darkMode?"rgba(255,179,0,0.22)":"rgba(184,112,8,0.18)"}, ${darkMode?"rgba(0,201,167,0.10)":"rgba(0,158,135,0.10)"})`,
                        border:`2px solid ${ST_GOLD}`,
                        boxShadow:`0 0 0 2px ${darkMode?"rgba(14,10,6,0.9)":"rgba(255,250,240,0.9)"}, 0 6px 18px ${ST_GOLD}33`,
                        display:"flex",alignItems:"center",justifyContent:"center",
                        fontFamily:ST_SERIF,fontStyle:"italic",fontSize:"34px",color:ST_GOLD,letterSpacing:"-0.02em"}}>
                        {(userName || "?")[0].toUpperCase()}{(userSurname || "")[0]?.toUpperCase() || ""}
                      </div>
                    )}
                    <div style={{position:"absolute",bottom:"-2px",right:"-2px",width:"22px",height:"22px",borderRadius:"50%",background:ST_ACCENT,border:`2px solid ${darkMode?"#0d0a06":"#fffaf0"}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:"10px"}}>📷</div>
                    <input id="profile-photo-input" type="file" accept="image/*" style={{display:"none"}} onChange={e => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = () => {
                        const img = new Image();
                        img.onload = () => {
                          const canvas = document.createElement("canvas");
                          const size = 200;
                          canvas.width = size; canvas.height = size;
                          const ctx = canvas.getContext("2d");
                          const min = Math.min(img.width, img.height);
                          const sx = (img.width - min) / 2, sy = (img.height - min) / 2;
                          ctx.drawImage(img, sx, sy, min, min, 0, 0, size, size);
                          const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
                          setProfilePhoto(dataUrl);
                          localStorage.setItem("profilePhoto", dataUrl);
                        };
                        img.src = reader.result;
                      };
                      reader.readAsDataURL(file);
                      e.target.value = "";
                    }} />
                  </div>
                  {/* Name + email + mobile */}
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontFamily:ST_MONO,fontSize:"9.5px",letterSpacing:"2px",color:ST_MUTED,textTransform:"uppercase",marginBottom:"3px"}}>Assalamu alaykum</div>
                    <div style={{fontFamily:ST_SERIF,fontStyle:"italic",fontSize:"24px",color:ST_GOLD,letterSpacing:"-0.3px",lineHeight:1.05,marginBottom:"6px"}}>{userName}{userSurname ? ` ${userSurname}` : ""}</div>
                    {/* Email field */}
                    {userEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userEmail) && !editingEmail ? (
                      <div onClick={() => setEditingEmail(true)} style={{cursor:"pointer"}}>
                        <span style={{fontSize:"12px",color:"var(--muted)"}}>{userEmail}</span>
                      </div>
                    ) : (
                      <div style={{display:"flex",alignItems:"center",gap:"8px",background:"var(--s2)",border:"1px solid var(--border)",borderRadius:"10px",padding:"0 10px",marginTop:"4px",transition:"border-color 0.3s"}}>
                        <span style={{fontSize:"13px",color:"var(--muted)",flexShrink:0}}>✉️</span>
                        <input
                          type="text"
                          inputMode="email"
                          autoComplete="email"
                          autoFocus={editingEmail}
                          placeholder="Add your email"
                          defaultValue={userEmail}
                          onBlur={e => {
                            const input = e.target;
                            setTimeout(() => {
                              const em = input.value.trim().toLowerCase();
                              if (em && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
                                localStorage.setItem("userEmail", em);
                                setUserEmail(em);
                                const id = localStorage.getItem("azaan-userId");
                                if (id) fetch(`${BACKEND}/api/users/${id}/email`, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ email:em }) }).catch(()=>{});
                              } else if (!em) {
                                localStorage.removeItem("userEmail");
                                setUserEmail("");
                              }
                              setEditingEmail(false);
                            }, 500);
                          }}
                          style={{flex:1,background:"transparent",border:"none",padding:"9px 0",color:"var(--text)",fontSize:"13px",fontFamily:"inherit",outline:"none"}}
                        />
                      </div>
                    )}
                    {/* Mobile field */}
                    {(() => {
                      const mSaved = localStorage.getItem("userWhatsapp");
                      const mValid = mSaved && /^27\d{9}$/.test(mSaved.replace(/\s/g,""));
                      return mValid && !editingMobile ? (
                        <div onClick={() => setEditingMobile(true)} style={{cursor:"pointer", marginTop:"4px"}}>
                          <span style={{fontSize:"12px",color:"var(--muted)"}}>📱 +{mSaved}</span>
                        </div>
                      ) : (
                        <div style={{display:"flex",alignItems:"center",gap:"8px",background:"var(--s2)",border:"1px solid var(--border)",borderRadius:"10px",padding:"0 10px",marginTop:"4px",transition:"border-color 0.3s"}}>
                          <span style={{fontSize:"13px",color:"var(--muted)",flexShrink:0}}>📱 +27</span>
                          <input
                            type="tel"
                            inputMode="numeric"
                            autoComplete="tel"
                            autoFocus={editingMobile}
                            placeholder="XX XXX XXXX"
                            value={userWhatsapp.startsWith("27") ? userWhatsapp.slice(2).replace(/\D/g,"").replace(/(\d{2})(\d{3})(\d{4})/, "$1 $2 $3").trim() : ""}
                            onChange={e => {
                              let digits = e.target.value.replace(/\D/g,"");
                              // Strip leading 0 (iOS autofill: "(083) 123 4567" → "0831234567")
                              if (digits.startsWith("0")) digits = digits.slice(1);
                              // Strip country code if autofilled with it
                              if (digits.startsWith("27") && digits.length > 9) digits = digits.slice(2);
                              setUserWhatsapp("27" + digits);
                            }}
                            onBlur={() => {
                              const digits = userWhatsapp.replace(/\D/g,"");
                              if (digits.length === 11 && digits.startsWith("27")) {
                                localStorage.setItem("userWhatsapp", digits);
                              } else if (digits === "27" || digits.length < 3) {
                                localStorage.removeItem("userWhatsapp");
                                setUserWhatsapp("27");
                              }
                              setEditingMobile(false);
                            }}
                            style={{flex:1,background:"transparent",border:"none",padding:"9px 0",color:"var(--text)",fontSize:"13px",fontFamily:"inherit",outline:"none"}}
                          />
                        </div>
                      );
                    })()}
                    {/* User ID */}
                    {userId && (
                      <div style={{marginTop:"6px",cursor:"pointer"}} onClick={(e) => {
                        const span = e.currentTarget.querySelector("span");
                        try {
                          if (navigator.clipboard && navigator.clipboard.writeText) {
                            navigator.clipboard.writeText(userId);
                          } else {
                            const ta = document.createElement("textarea");
                            ta.value = userId; ta.style.cssText = "position:fixed;opacity:0;top:0;left:0";
                            document.body.appendChild(ta); ta.select();
                            document.execCommand("copy"); document.body.removeChild(ta);
                          }
                        } catch {}
                        if (span) { const orig = span.textContent; span.textContent = "Copied!"; span.style.color = "var(--accent)"; setTimeout(() => { span.textContent = orig; span.style.color = ""; }, 1500); }
                      }}>
                        <span style={{fontSize:"10px",color:"var(--muted)",fontFamily:"monospace",letterSpacing:"0.3px"}}>ID: {userId}</span>
                      </div>
                    )}
                  </div>
                </div>
                {/* Stats row — three editorial pills */}
                {(() => {
                  const today = localDateStr();
                  const d = new Date(); d.setDate(d.getDate()-1);
                  const yesterday = d.getFullYear()+"-"+String(d.getMonth()+1).padStart(2,"0")+"-"+String(d.getDate()).padStart(2,"0");
                  const totalPrayers = Object.values(salahLog).reduce((sum,day) =>
                    sum + ["Fajr","Dhuhr","Asr","Maghrib","Isha"].filter(p => day[p]).length, 0);
                  const streak = (salahStreak.lastDate===today||salahStreak.lastDate===yesterday) ? salahStreak.count : 0;
                  // Days Active: count of distinct local-dates the app has been opened, sourced
                  // from user.activeDays via /heartbeat. Always include today (the user is here now)
                  // even if the heartbeat hasn't completed its first round-trip yet.
                  const daysActiveSet = new Set(Array.isArray(daysActive) ? daysActive : []);
                  daysActiveSet.add(today);
                  const daysActiveCount = daysActiveSet.size;
                  const stats = [
                    { eyebrow:"Streak",      value: streak > 0 ? streak : "—",                 sub: streak > 0 ? "days" : "start" },
                    { eyebrow:"Prayers",     value: totalPrayers > 0 ? totalPrayers : "—",     sub: totalPrayers > 0 ? "logged" : "—" },
                    { eyebrow:"Days Active", value: daysActiveCount > 0 ? daysActiveCount : 1, sub: "with Sirat" },
                  ];
                  return (
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"8px",marginTop:"16px"}}>
                      {stats.map(s => (
                        <div key={s.eyebrow} style={{
                          padding:"10px 8px",
                          background: darkMode ? "rgba(255,179,0,0.06)" : "rgba(184,112,8,0.05)",
                          border: `1px solid ${ST_BORDER}`,
                          borderRadius:"12px",
                          textAlign:"center",
                        }}>
                          <div style={{fontFamily:ST_MONO,fontSize:"8.5px",letterSpacing:"1.8px",color:ST_MUTED,textTransform:"uppercase",marginBottom:"4px"}}>{s.eyebrow}</div>
                          <div style={{fontFamily:ST_SERIF,fontStyle:"italic",fontSize:"22px",color:ST_GOLD,letterSpacing:"-0.02em",lineHeight:1}}>{s.value}</div>
                          <div style={{fontFamily:ST_MONO,fontSize:"8.5px",color:ST_MUTED,letterSpacing:"1px",marginTop:"3px",textTransform:"uppercase"}}>{s.sub}</div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}
            {!isStandalone && (
              <div style={{padding:"14px 16px",background:darkMode?"rgba(255,179,0,0.07)":"rgba(184,112,8,0.07)",border:`1px solid ${ST_BORDER}`,borderRadius:"18px",margin:"0 16px",display:"flex",gap:"12px",alignItems:"flex-start"}}>
                <span style={{fontSize:"20px",flexShrink:0}}>📲</span>
                <div>
                  <div style={{fontSize:"13px",fontWeight:"700",color:"var(--gold)",marginBottom:"4px"}}>Add to Home Screen for notifications</div>
                  <div style={{fontSize:"12px",color:"var(--muted)",lineHeight:1.6}}>
                    You're viewing in a browser. To receive Azaan push notifications, add Sirat to your Home Screen:
                    tap <strong style={{color:"var(--text)"}}>Share → Add to Home Screen</strong> on iPhone, or <strong style={{color:"var(--text)"}}>⋮ → Install app</strong> on Android.
                  </div>
                </div>
              </div>
            )}
            <div className="settings-card">
              <div className="settings-title">Prayer Times</div>
              <div className="settings-row" style={{flexDirection:"column",alignItems:"stretch",gap:"8px"}}>
                <div style={{display:"flex",alignItems:"center",gap:"10px"}}>
                  <span style={{fontSize:"16px",flexShrink:0}}>{locationLoading ? "⏳" : "📍"}</span>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:"14px",fontWeight:"700",color:"var(--text)",lineHeight:1.3}}>{locationLoading ? "Detecting location..." : detectedCity || "Location not set"}</div>
                    {locationMode === "auto" && !locationLoading && (
                      <div onClick={() => detectLocation(true)} style={{fontSize:"10px",color:"var(--accent)",marginTop:"3px",cursor:"pointer"}}>{detectedCity ? "Tap to refresh" : "Tap to detect location"}</div>
                    )}
                  </div>
                  <div style={{display:"flex",background:"var(--s3)",borderRadius:"8px",border:"1px solid var(--border)",overflow:"hidden",flexShrink:0}}>
                    <button onClick={() => { setLocationMode("auto"); localStorage.setItem("locationMode","auto"); detectLocation(true); }} style={{padding:"6px 12px",fontSize:"11px",fontWeight:"700",fontFamily:"inherit",border:"none",cursor:"pointer",background:locationMode==="auto"?"var(--accent)":"transparent",color:locationMode==="auto"?"#07090c":"var(--muted)"}}>Auto</button>
                    <button onClick={() => { setLocationMode("manual"); localStorage.setItem("locationMode","manual"); }} style={{padding:"6px 12px",fontSize:"11px",fontWeight:"700",fontFamily:"inherit",border:"none",cursor:"pointer",background:locationMode==="manual"?"var(--accent)":"transparent",color:locationMode==="manual"?"#07090c":"var(--muted)"}}>Manual</button>
                  </div>
                </div>
                {/* Manual city search */}
                {locationMode === "manual" && (
                  <div style={{position:"relative",marginTop:"4px"}}>
                    <div style={{display:"flex",gap:"8px"}}>
                      <input
                        type="text"
                        placeholder="Search a city or suburb..."
                        value={manualCityInput}
                        onChange={e => { setManualCityInput(e.target.value); fetchCitySuggestions(e.target.value); }}
                        onKeyDown={e => { if (e.key === "Enter") { searchManualCity(manualCityInput); setCitySuggestions([]); } }}
                        onBlur={() => setTimeout(() => setCitySuggestions([]), 200)}
                        style={{flex:1,padding:"8px 12px",background:"var(--s3)",border:"1px solid var(--border)",borderRadius:"8px",color:"var(--text)",fontSize:"13px",fontFamily:"inherit"}}
                      />
                      <button onClick={() => { searchManualCity(manualCityInput); setCitySuggestions([]); }} style={{padding:"8px 14px",background:"var(--accent)",border:"1px solid var(--acc2)",borderRadius:"8px",color:"#07090c",fontSize:"12px",fontWeight:"800",cursor:"pointer",fontFamily:"inherit"}}>Search</button>
                    </div>
                    {citySuggestions.length > 0 && (
                      <div style={{position:"absolute",left:0,right:0,top:"100%",marginTop:"4px",background:darkMode?"#0d1117":"#fff",border:"1px solid var(--border)",borderRadius:"10px",overflow:"hidden",zIndex:10,boxShadow:"0 8px 24px rgba(0,0,0,0.3)"}}>
                        {citySuggestions.map((s, i) => (
                          <div key={i} onClick={() => selectCitySuggestion(s)} style={{padding:"10px 12px",fontSize:"13px",color:"var(--text)",cursor:"pointer",borderBottom:i < citySuggestions.length - 1 ? "1px solid var(--border)" : "none",background:"transparent"}}>
                            <div style={{fontWeight:"600"}}>{s.label}</div>
                            <div style={{fontSize:"10px",color:"var(--muted)",marginTop:"2px",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{s.display_name}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="settings-row">
                <span className="settings-label"><span style={{marginRight:"8px",fontSize:"16px"}}>🕐</span>Asr Time</span>
                <select value={school} onChange={e => { setSchool(e.target.value); localStorage.setItem("school", e.target.value); }} style={{background:"var(--s3)",border:"1px solid var(--border)",borderRadius:"8px",padding:"8px 10px",color:"var(--text)",fontSize:"13px",fontFamily:"inherit"}}>
                  <option value="0">Earlier (Shafi'i)</option>
                  <option value="1">Later (Hanafi)</option>
                </select>
              </div>
              <div style={{padding:"8px 12px 12px",fontSize:"11px",color:"var(--muted)",lineHeight:"1.5",borderTop:"1px solid var(--border)",marginTop:"4px"}}>
                Prayer times are calculated using the Muslim World League (MWL) method with adjustments aligned to the Jamiatul Ulama (JamiatKZN) timetable used by South African mosques.
              </div>
            </div>
            <div className="settings-card">
              <div className="settings-title">Notifications</div>
              <div className="settings-row" style={{flexDirection:"column",alignItems:"flex-start",gap:"6px"}}>
                <div style={{display:"flex",alignItems:"center",width:"100%"}}>
                  <span className="settings-label"><span style={{marginRight:"8px",fontSize:"16px"}}>🔔</span>Live Azaan Notifications</span>
                  <button
                    onClick={async () => {
                      if (notifEnabled) {
                        setNotifEnabled(false);
                        localStorage.setItem("notifEnabled", "false");
                        if (!isNative()) {
                          navigator.serviceWorker.ready.then(reg => {
                            reg.pushManager.getSubscription().then(sub => {
                              if (sub) {
                                sub.unsubscribe();
                                fetch(`${BACKEND}/api/unsubscribe`, {
                                  method:"POST", headers:{"Content-Type":"application/json"},
                                  body: JSON.stringify({ endpoint: sub.endpoint })
                                });
                              }
                            });
                          });
                        }
                      } else {
                        if (isNative()) {
                          // Native: APNs registration is handled inside syncFollowsToBackend
                          setNotifEnabled(true);
                          localStorage.setItem("notifEnabled", "true");
                          await syncFollowsToBackend(followedMounts, effectiveNotify);
                        } else {
                          const ok = await registerPush();
                          if (ok) { setNotifEnabled(true); localStorage.setItem("notifEnabled", "true"); syncFollowsToBackend(followedMounts, effectiveNotify); }
                          else alert("Could not enable notifications. Make sure you allow notifications for this site.");
                        }
                      }
                    }}
                    style={{padding:"8px 16px",background:notifEnabled?"var(--accent)":"var(--s3)",border:`1px solid ${notifEnabled?"var(--acc2)":"var(--border)"}`,borderRadius:"8px",color:notifEnabled?"#07090c":"var(--muted)",fontSize:"13px",fontWeight:"800",cursor:"pointer",fontFamily:"inherit",marginLeft:"auto"}}
                  >
                    {notifEnabled ? "On" : "Off"}
                  </button>
                </div>
                <div style={{fontSize:"11px",color:"var(--muted)",padding:"0 4px",lineHeight:"1.5"}}>
                  {notifEnabled ? "You'll be notified when your saved masjids go live — tap to listen to the azaan being called at your local masjid in real time." : "Get notified when a masjid you follow goes live, so you can listen to the actual azaan being called at your local masjid."}
                </div>
              </div>
              {/* Azaan Alerts — per-prayer azaan clip */}
              {SHOW_AZAAN_ALERTS && (
              <div className="settings-row" style={{flexDirection:"column",alignItems:"flex-start",gap:"8px"}}>
                <div style={{display:"flex",alignItems:"center",width:"100%"}}>
                  <span className="settings-label"><span style={{marginRight:"8px",fontSize:"16px"}}>🔊</span>Salaah Time Notifications</span>
                  <button
                    onClick={async () => {
                      const allOn = TRACKABLE_PRAYERS.every(p => azaanPrayers.includes(p));
                      const next = allOn ? [] : [...TRACKABLE_PRAYERS];
                      setAzaanPrayers(next);
                      localStorage.setItem("azaanPrayers", JSON.stringify(next));
                      if (next.length > 0) {
                        if (!userLat) await detectLocation(true);
                        if ("Notification" in window && Notification.permission === "default") {
                          try { await Notification.requestPermission(); } catch {}
                        }
                        if ("serviceWorker" in navigator && "PushManager" in window) {
                          try {
                            const reg = await navigator.serviceWorker.ready;
                            const existing = await reg.pushManager.getSubscription();
                            if (!existing) { await registerPush(); }
                          } catch {}
                        }
                      }
                      syncFollowsToBackend(followedMounts, effectiveNotify);
                    }}
                    style={{padding:"6px 12px",background:azaanPrayers.length>0?"var(--gold)":"var(--s3)",border:`1px solid ${azaanPrayers.length>0?"var(--gold2)":"var(--border)"}`,borderRadius:"8px",color:azaanPrayers.length>0?"#07090c":"var(--muted)",fontSize:"12px",fontWeight:"800",cursor:"pointer",fontFamily:"inherit",marginLeft:"auto"}}
                  >
                    {TRACKABLE_PRAYERS.every(p => azaanPrayers.includes(p)) ? "All On" : azaanPrayers.length > 0 ? `${azaanPrayers.length}/5` : "Off"}
                  </button>
                </div>
                <div style={{display:"flex",gap:"6px",flexWrap:"wrap",width:"100%",padding:"0 4px"}}>
                  {TRACKABLE_PRAYERS.map(prayer => {
                    const isOn = azaanPrayers.includes(prayer);
                    return (
                      <button key={prayer}
                        onClick={async () => {
                          const next = isOn ? azaanPrayers.filter(p => p !== prayer) : [...azaanPrayers, prayer];
                          setAzaanPrayers(next);
                          localStorage.setItem("azaanPrayers", JSON.stringify(next));
                          if (next.length > 0) {
                            if (!userLat) await detectLocation(true);
                            if ("Notification" in window && Notification.permission === "default") {
                              try { await Notification.requestPermission(); } catch {}
                            }
                            if ("serviceWorker" in navigator && "PushManager" in window) {
                              try {
                                const reg = await navigator.serviceWorker.ready;
                                const existing = await reg.pushManager.getSubscription();
                                if (!existing) { await registerPush(); }
                              } catch {}
                            }
                          }
                          syncFollowsToBackend(followedMounts, effectiveNotify);
                        }}
                        className={`azaan-prayer-pill${isOn ? " active" : ""}`}
                      >
                        {prayer}
                      </button>
                    );
                  })}
                </div>
                <div style={{fontSize:"11px",color:"var(--muted)",padding:"0 4px",lineHeight:"1.5"}}>
                  {azaanPrayers.length > 0
                    ? "A Makkah azaan will play at each selected prayer time. When the app is open, it plays automatically. When closed, you'll get a notification — tap it to hear the azaan."
                    : "Play a beautiful Makkah azaan clip at each prayer time. Ideal as a fallback when you're not near a masjid or don't have a live masjid saved."}
                </div>
                <button onClick={() => playAzaanClip("Preview")} style={{padding:"6px 14px",background:azaanPlaying?"var(--gold)":"transparent",border:"1px solid var(--gold)",borderRadius:"8px",color:azaanPlaying?"#07090c":"var(--gold)",fontSize:"11px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit",marginTop:"2px",transition:"all 0.2s"}}>
                  {azaanPlaying === "loading" ? "⏳ Loading..." : azaanPlaying === "playing" ? "⏹ Stop Azaan" : "▶ Preview Azaan"}
                </button>
              </div>
              )}

              {/* Daily Hadith notification */}
              <div className="settings-row">
                <span className="settings-label"><span style={{marginRight:"8px",fontSize:"16px"}}>📖</span>Daily Hadith (7am)</span>
                <button
                  onClick={async () => {
                    const next = !dailyNotifEnabled;
                    setDailyNotifEnabled(next);
                    localStorage.setItem("dailyNotifEnabled", String(next));
                    if (next) {
                      try {
                        const reg = await navigator.serviceWorker.ready;
                        const existing = await reg.pushManager.getSubscription();
                        if (!existing) { await registerPush(); }
                      } catch {}
                    }
                    syncFollowsToBackend(followedMounts, effectiveNotify);
                  }}
                  style={{padding:"8px 16px",background:dailyNotifEnabled?"var(--accent)":"var(--s3)",border:`1px solid ${dailyNotifEnabled?"var(--acc2)":"var(--border)"}`,borderRadius:"8px",color:dailyNotifEnabled?"#07090c":"var(--muted)",fontSize:"13px",fontWeight:"800",cursor:"pointer",fontFamily:"inherit"}}
                >
                  {dailyNotifEnabled ? "On" : "Off"}
                </button>
              </div>

              {/* Prayer Reminders (Did you pray? nudge) */}
              <div className="settings-row" style={{flexDirection:"column",alignItems:"flex-start",gap:"6px"}}>
                <div style={{display:"flex",alignItems:"center",width:"100%"}}>
                  <span className="settings-label"><span style={{marginRight:"8px",fontSize:"16px"}}>🕌</span>Prayer Reminders</span>
                  <button
                    onClick={async () => {
                      const next = !prayerReminderEnabled;
                      if (next && !userLat) {
                        const ok = await detectLocation(true);
                        if (!ok) { alert("Location access denied. Please allow location in your browser settings."); return; }
                      }
                      setPrayerReminderEnabled(next);
                      localStorage.setItem("prayerReminderEnabled", String(next));
                      if (next) {
                        try {
                          const reg = await navigator.serviceWorker.ready;
                          const existing = await reg.pushManager.getSubscription();
                          if (!existing) { await registerPush(); }
                        } catch {}
                      }
                      syncFollowsToBackend(followedMounts, effectiveNotify);
                    }}
                    style={{padding:"8px 16px",background:prayerReminderEnabled?"var(--accent)":"var(--s3)",border:`1px solid ${prayerReminderEnabled?"var(--acc2)":"var(--border)"}`,borderRadius:"8px",color:prayerReminderEnabled?"#07090c":"var(--muted)",fontSize:"13px",fontWeight:"800",cursor:"pointer",fontFamily:"inherit",marginLeft:"auto"}}
                  >
                    {prayerReminderEnabled ? "On" : "Off"}
                  </button>
                </div>
                <div style={{fontSize:"11px",color:"var(--muted)",padding:"0 4px"}}>
                  {prayerReminderEnabled ? "30 min after each prayer, you'll get a gentle \"Did you pray?\" reminder — only if you haven't marked it as prayed." : "Get a gentle reminder 30 min after each prayer time if you haven't marked it as prayed."}
                </div>
              </div>

              {/* Daily Name of Allah */}
              <div className="settings-row">
                <div style={{flex:1,minWidth:0}}>
                  <span className="settings-label"><span style={{marginRight:"8px",fontSize:"16px"}}>✨</span>Daily Name of Allah</span>
                  <div style={{fontSize:"11px",color:"var(--muted)",marginTop:"3px"}}>One of the 99 Names, each morning at 8am</div>
                </div>
                <button
                  onClick={async () => {
                    const v = !dailyNameEnabled;
                    setDailyNameEnabled(v);
                    localStorage.setItem("dailyNameEnabled", String(v));
                    // Same flow as Daily Hadith: ensure push subscription exists,
                    // then sync the new flag to the backend immediately so the
                    // morning cron knows about it. Without this call, toggling
                    // the switch never reached the backend until the next time
                    // some other action triggered subscribe (follow/unfollow,
                    // app reload). That was the "Daily Name of Allah doesn't
                    // work" bug.
                    if (v && !isNative()) {
                      try {
                        const reg = await navigator.serviceWorker.ready;
                        const existing = await reg.pushManager.getSubscription();
                        if (!existing) { await registerPush(); }
                      } catch {}
                    }
                    syncFollowsToBackend(followedMounts, effectiveNotify);
                  }}
                  style={{padding:"8px 16px",background:dailyNameEnabled?"var(--accent)":"var(--s3)",border:`1px solid ${dailyNameEnabled?"var(--acc2)":"var(--border)"}`,borderRadius:"8px",color:dailyNameEnabled?"#07090c":"var(--muted)",fontSize:"13px",fontWeight:"800",cursor:"pointer",fontFamily:"inherit"}}
                >
                  {dailyNameEnabled ? "On" : "Off"}
                </button>
              </div>

            </div>

            {/* ── Appearance ────────────────────────────────────── */}
            {(() => {
              const segOpt = (active) => ({
                flex:1, padding:"8px 0", border:"none", cursor:"pointer", fontFamily:ST_MONO, fontSize:"10.5px", fontWeight:700, letterSpacing:"1.5px", textTransform:"uppercase",
                background: active ? `linear-gradient(135deg,${ST_GOLD},${ST_GOLD2})` : "transparent",
                color: active ? "#0a0806" : ST_MUTED,
                transition:"all 0.18s",
              });
              const segWrap = {display:"flex",border:`1px solid ${ST_BORDER}`,borderRadius:"10px",overflow:"hidden",background:darkMode?"rgba(255,255,255,0.02)":"rgba(0,0,0,0.02)"};
              const reciterChoices = [{id:"",short:"Last used"}, ...RECITERS];
              const tabChoices = [{id:"masjids",label:"Salaah"},{id:"radio",label:"Radio"},{id:"tools",label:"Ibadah"}];
              return (
                <div className="settings-card">
                  <div className="settings-title">Appearance · Defaults</div>
                  <div className="settings-subtitle">How Sirat looks &amp; opens</div>
                  {/* Theme */}
                  <div className="settings-row" style={{flexDirection:"column",alignItems:"stretch",gap:"8px"}}>
                    <span className="settings-label">Theme</span>
                    <div style={segWrap}>
                      {[{v:"dark",label:"Dark"},{v:"light",label:"Light"},{v:"auto",label:"Auto"}].map(o => (
                        <button key={o.v} onClick={() => {
                          setDarkModePref(o.v); localStorage.setItem("darkModePref", o.v);
                          if (o.v === "auto") {
                            const sysDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
                            setDarkMode(sysDark); localStorage.setItem("darkMode", String(sysDark));
                          } else {
                            const wantDark = o.v === "dark";
                            setDarkMode(wantDark); localStorage.setItem("darkMode", String(wantDark));
                          }
                        }} style={segOpt(darkModePref === o.v)}>{o.label}</button>
                      ))}
                    </div>
                  </div>
                  {/* Default tab */}
                  <div className="settings-row" style={{flexDirection:"column",alignItems:"stretch",gap:"8px"}}>
                    <span className="settings-label">Default tab on launch</span>
                    <div style={segWrap}>
                      {tabChoices.map(o => (
                        <button key={o.id} onClick={() => { setDefaultTab(o.id); localStorage.setItem("defaultTab", o.id); }} style={segOpt(defaultTab === o.id)}>{o.label}</button>
                      ))}
                    </div>
                  </div>
                  {/* Skip splash on launch */}
                  <div className="settings-row" style={{flexDirection:"column",alignItems:"stretch",gap:"8px"}}>
                    <span className="settings-label">Skip splash on launch</span>
                    <div className="settings-value" style={{marginTop:"-2px",marginBottom:"4px",fontFamily:"inherit",fontSize:"11.5px",letterSpacing:"normal"}}>Open straight to your default tab</div>
                    <div style={segWrap}>
                      {[{v:false,label:"Off"},{v:true,label:"On"}].map(o => (
                        <button key={String(o.v)} onClick={() => {
                          setSkipSplash(o.v);
                          try { localStorage.setItem("skipSplash", o.v ? "1" : "0"); } catch {}
                        }} style={segOpt(skipSplash === o.v)}>{o.label}</button>
                      ))}
                    </div>
                  </div>
                  {/* Focus mode — pick which tabs to hide */}
                  {(() => {
                    // Hayah is iOS-TestFlight-only — gate it for PWA users so the
                    // word "Hayah" doesn't surface to web users who don't have it.
                    const hideableTabs = [
                      { id: "radio", label: "Radio" },
                      { id: "tools", label: "Ibadah" },
                      ...(isNative() ? [{ id: "hayah", label: "Hayah" }] : []),
                    ];
                    return (
                      <div className="settings-row" style={{flexDirection:"column",alignItems:"stretch",gap:"8px"}}>
                        <span className="settings-label">Focus mode</span>
                        <div className="settings-value" style={{marginTop:"-2px",marginBottom:"4px",fontFamily:"inherit",fontSize:"11.5px",letterSpacing:"normal"}}>
                          {focusHiddenTabs.length === 0
                            ? "Tap a tab to hide it from the bottom nav."
                            : `Hidden: ${focusHiddenTabs.map(id => (hideableTabs.find(t => t.id === id)?.label) || id).join(", ")}. Tap again to show.`}
                        </div>
                        <div style={{display:"flex",flexWrap:"wrap",gap:"6px"}}>
                          {hideableTabs.map(t => {
                            const hidden = focusHiddenTabs.includes(t.id);
                            return (
                              <button key={t.id} onClick={() => toggleFocusHiddenTab(t.id)} style={{
                                padding:"7px 12px",
                                borderRadius:"999px",
                                border: `1px solid ${hidden ? ST_GOLD : ST_BORDER}`,
                                background: hidden ? "rgba(255,179,0,0.12)" : (darkMode ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)"),
                                color: hidden ? ST_GOLD : ST_MUTED,
                                fontFamily: ST_MONO, fontSize:"10.5px", fontWeight:700, letterSpacing:"1px", textTransform:"uppercase",
                                cursor:"pointer",
                              }}>
                                {hidden ? "✕ " : ""}{t.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                  {/* Default reciter */}
                  <div className="settings-row">
                    <div style={{flex:1,minWidth:0}}>
                      <span className="settings-label">Default reciter</span>
                      <div className="settings-value" style={{marginTop:"3px",fontFamily:"inherit",fontSize:"11.5px",letterSpacing:"normal"}}>For Surah &amp; Khatm players</div>
                    </div>
                    <select value={defaultReciterPref} onChange={e => { setDefaultReciterPref(e.target.value); localStorage.setItem("defaultReciterPref", e.target.value); }}
                      style={{padding:"8px 10px",borderRadius:"10px",background:darkMode?"rgba(255,255,255,0.04)":"rgba(0,0,0,0.04)",border:`1px solid ${ST_BORDER}`,color:"var(--text)",fontFamily:ST_MONO,fontSize:"11.5px",cursor:"pointer",letterSpacing:"0.3px"}}>
                      {reciterChoices.map(r => <option key={r.id} value={r.id}>{r.short || r.name}</option>)}
                    </select>
                  </div>
                </div>
              );
            })()}


            {/* ── Share card style ─────────────────────────────── */}
            <div className="settings-card">
              <div className="settings-title">Share card style</div>
              <div className="settings-subtitle">How your shared verses &amp; hadiths look</div>
              <div style={{display:"flex",flexDirection:"column",gap:"10px",padding:"4px 0 2px"}}>
                {[
                  {v:"paper",name:"Paper & Ink",desc:"Warm cream, editorial, timeless",bg:"linear-gradient(135deg,#f4ead4,#e8d9b0)",textColor:"#1a1208",accent:"#8c6b1f"},
                  {v:"night",name:"Night Sky",desc:"Near-black, modern, IG-native",bg:"linear-gradient(135deg,#07080b,#0e121a)",textColor:"#f0e6cf",accent:"#c59b3a"},
                ].map(o => {
                  const isActive = shareCardStyle === o.v;
                  return (
                    <button key={o.v}
                      onClick={() => { setShareCardStyle(o.v); localStorage.setItem("shareCardStyle", o.v); }}
                      style={{
                        display:"flex",alignItems:"center",gap:"14px",
                        padding:"12px 14px",borderRadius:"12px",
                        border:isActive?`2px solid ${ST_GOLD}`:`1px solid ${ST_BORDER}`,
                        background:isActive?(darkMode?"rgba(255,179,0,0.06)":"rgba(184,112,8,0.04)"):(darkMode?"rgba(255,255,255,0.02)":"rgba(0,0,0,0.02)"),
                        cursor:"pointer",textAlign:"left",width:"100%",transition:"all 0.18s",
                      }}>
                      <div style={{width:"52px",height:"52px",borderRadius:"8px",background:o.bg,flexShrink:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:"4px"}}>
                        <div style={{width:"32px",height:"3px",borderRadius:"2px",background:o.accent,opacity:0.8}}/>
                        <div style={{width:"26px",height:"2px",borderRadius:"1px",background:o.textColor,opacity:0.3}}/>
                        <div style={{width:"30px",height:"2px",borderRadius:"1px",background:o.textColor,opacity:0.2}}/>
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontFamily:ST_MONO,fontSize:"11px",fontWeight:700,letterSpacing:"1px",color:"var(--text)",textTransform:"uppercase"}}>{o.name}</div>
                        <div style={{fontSize:"12px",color:ST_MUTED,marginTop:"3px",fontWeight:400}}>{o.desc}</div>
                      </div>
                      {isActive && <div style={{color:ST_GOLD,fontSize:"16px",flexShrink:0}}>✓</div>}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* ── Fan Quick Access ─────────────────────────────── */}
            {(() => {
              const pinned = fanPinnedIds || [];
              const hasPinned = pinned.length === 6;
              // Default display: first 6 from usage-sorted if no pinned, else pinned
              const usage2 = (() => { try { return JSON.parse(localStorage.getItem("fanUsageCounts")||"{}"); } catch { return {}; } })();
              const displaySlots = hasPinned
                ? pinned.map(id => FAN_ALL_OPTIONS.find(o => o.id === id)).filter(Boolean)
                : [...FAN_ALL_OPTIONS].sort((a,b)=>(usage2[b.id]||0)-(usage2[a.id]||0)).slice(0,6);
              return (
                <div style={{margin:"0 16px 12px",borderRadius:"16px",background:darkMode?"rgba(255,179,0,0.04)":"rgba(184,112,8,0.04)",border:`1px solid ${ST_BORDER}`,padding:"16px"}}>
                  <div className="settings-title">Quick Access · Fan</div>
                  <div className="settings-subtitle" style={{marginBottom:"14px"}}>{hasPinned ? "Your pinned shortcuts — tap any to change" : "Auto-sorted by use · tap to pin your own"}</div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:"8px",marginBottom:"12px"}}>
                    {Array.from({length:6}).map((_,i) => {
                      const item = displaySlots[i];
                      return (
                        <button key={i} onClick={() => { hapticUiSelection(); setFanEditingSlot(i); }} style={{
                          display:"flex",flexDirection:"column",alignItems:"center",gap:"5px",
                          padding:"10px 6px",borderRadius:"14px",border:`1px solid ${hasPinned ? (darkMode ? "rgba(244,197,106,0.3)" : "rgba(184,112,8,0.35)") : ST_BORDER}`,
                          background:hasPinned?(darkMode?"rgba(244,197,106,0.06)":"rgba(184,112,8,0.08)"):"transparent",
                          cursor:"pointer",WebkitTapHighlightColor:"transparent",
                        }}>
                          <div style={{width:"40px",height:"40px",borderRadius:"10px",background:darkMode?"rgba(244,197,106,0.08)":"rgba(184,112,8,0.14)",display:"flex",alignItems:"center",justifyContent:"center",filter:darkMode?"none":"brightness(0.5) saturate(1.4)"}}>
                            {item ? item.icon : <span style={{color:ST_MUTED,fontSize:"18px",filter:darkMode?"none":"brightness(2) saturate(0.7)"}}>＋</span>}
                          </div>
                          <span style={{fontSize:"9.5px",fontWeight:"700",color:item?ST_GOLD:ST_MUTED,letterSpacing:"0.2px",textAlign:"center",lineHeight:"1.2"}}>
                            {item ? item.label : "—"}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  {hasPinned && (
                    <button onClick={() => { setFanPinnedIds(null); localStorage.removeItem("fanPinnedIds"); }} style={{
                      width:"100%",padding:"8px",borderRadius:"10px",border:`1px solid ${ST_BORDER}`,
                      background:"transparent",color:ST_MUTED,fontSize:"11.5px",cursor:"pointer",
                      WebkitTapHighlightColor:"transparent",
                    }}>Reset to auto-sort</button>
                  )}
                </div>
              );
            })()}


            {/* Teaching Mode */}
            <div className="settings-card">
              <div className="settings-title">For Educators</div>
              <div className="settings-row" style={{flexDirection:"column",alignItems:"flex-start",gap:"6px"}}>
                <div style={{display:"flex",alignItems:"center",width:"100%"}}>
                  <span className="settings-label"><span style={{marginRight:"8px",fontSize:"16px"}}>📚</span>Teaching Mode</span>
                  <button
                    onClick={() => {
                      const next = !teachingMode;
                      setTeachingMode(next);
                      localStorage.setItem("teachingMode", String(next));
                      if (next) {
                        setTeachingView("grid");
                        if (!localStorage.getItem("teachingIntroSeen")) {
                          setShowTeachingIntro(true);
                        }
                      }
                    }}
                    style={{padding:"8px 16px",background:teachingMode?"var(--gold)":"var(--s3)",border:`1px solid ${teachingMode?"var(--gold2)":"var(--border)"}`,borderRadius:"8px",color:teachingMode?"#07090c":"var(--muted)",fontSize:"13px",fontWeight:"800",cursor:"pointer",fontFamily:"inherit",marginLeft:"auto"}}
                  >
                    {teachingMode ? "On" : "Off"}
                  </button>
                </div>
                <div style={{fontSize:"11px",color:"var(--muted)",padding:"0 4px"}}>
                  {teachingMode ? "Teaching Mode is active. The app is now in learning mode for madrassa / beginner use. Tap the header title 5× in Teaching Mode to exit." : "Transforms the app into a clean learning space — Salah guide, Quran surahs, duas, tajweed, Arabic letters and more. For madrassahs & new Muslims."}
                </div>
              </div>
            </div>

            {/* Support & Feedback */}
            <div className="settings-card">
              <div className="settings-title">Support</div>
              {/* Support Sirat / Pro status — mode-aware row at the top of Support */}
              {(() => {
                if (!remoteConfig.monetizationMode && !remoteConfig.allFree && !isPro) return null;
                if (isSupportMode && !supportPageVisible) return null;
                const go = () => { siratProSourceRef.current = "settings"; setTab("tools"); setToolView("siratpro"); };
                if (supportPageVisible) {
                  const tierName = hasSupported ? (() => { const t = getSupporterTier(supportTotalRef.current, remoteConfig.support); return t.name || null; })() : null;
                  return (
                    <div onClick={go} className="settings-row" style={{cursor:"pointer"}}>
                      <div style={{flex:1,minWidth:0}}>
                        <span className="settings-label" style={{color:"var(--gold)"}}><span style={{marginRight:"8px",fontSize:"16px"}}>🌙</span>{tierName ? `${tierName} Supporter` : "Support Sirat"}</span>
                        <div style={{fontSize:"11px",color:"var(--muted)",marginTop:"2px"}}>{hasSupported ? `R${supportTotalRef.current} given · BarakAllahu feek` : "Give if you'd like · May Allah reward you either way"}</div>
                      </div>
                      <span style={{fontSize:"11px",color:"var(--gold)",fontWeight:"700"}}>View ›</span>
                    </div>
                  );
                }
                if (isPro && !isTrial) {
                  return (
                    <div onClick={go} className="settings-row" style={{cursor:"pointer"}}>
                      <div style={{flex:1,minWidth:0}}>
                        <span className="settings-label" style={{color:"var(--accent)"}}><span style={{marginRight:"8px",fontSize:"16px"}}>✓</span>Sirat Pro — Active</span>
                        <div style={{fontSize:"11px",color:"var(--muted)",marginTop:"2px"}}>{proExpiry ? formatTimeRemaining(proExpiry) : "All features unlocked"}</div>
                      </div>
                      <span style={{color:"var(--muted)",opacity:0.5,fontSize:"18px"}}>›</span>
                    </div>
                  );
                }
                if (isTrial) {
                  return (
                    <div onClick={go} className="settings-row" style={{cursor:"pointer"}}>
                      <div style={{flex:1,minWidth:0}}>
                        <span className="settings-label" style={{color:"var(--accent)"}}><span style={{marginRight:"8px",fontSize:"16px"}}>✨</span>Free Trial — {trialDaysLeft} day{trialDaysLeft !== 1 ? "s" : ""} left</span>
                        <div style={{fontSize:"11px",color:"var(--muted)",marginTop:"2px"}}>All features unlocked · Upgrade to keep access</div>
                      </div>
                      <span style={{fontSize:"11px",color:"var(--accent)",fontWeight:"700"}}>Upgrade ›</span>
                    </div>
                  );
                }
                return (
                  <div onClick={go} className="settings-row" style={{cursor:"pointer"}}>
                    <div style={{flex:1,minWidth:0}}>
                      <span className="settings-label" style={{color:"var(--gold)"}}><span style={{marginRight:"8px",fontSize:"16px"}}>🌙</span>Sirat Pro</span>
                      <div style={{fontSize:"11px",color:"var(--muted)",marginTop:"2px"}}>Unlock all premium features</div>
                    </div>
                    <span style={{fontSize:"11px",color:"var(--gold)",fontWeight:"700"}}>View ›</span>
                  </div>
                );
              })()}
              {/* Chat with Us */}
              {(() => {
                const lastView = localStorage.getItem("lastFeedbackView") || "0";
                const unreadReplies = feedbackHistory.reduce((n, f) => n + (f.replies || []).filter(r => r.createdAt > lastView).length, 0);
                return (
                  <div onClick={() => { localStorage.setItem("lastFeedbackView", new Date().toISOString()); setTab("tools"); setToolView("feedback"); fetchFeedbackHistory(); }} className="settings-row" style={{cursor:"pointer",paddingTop:"10px",paddingBottom:"10px"}}>
                    <span className="settings-label"><span style={{marginRight:"8px",fontSize:"16px"}}>💬</span>Chat with Us</span>
                    <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
                      {unreadReplies > 0 && <span style={{background:"var(--accent)",color:"#07090c",fontSize:"10px",fontWeight:"800",borderRadius:"10px",padding:"2px 8px"}}>{unreadReplies}</span>}
                      <span style={{color:"var(--muted)",opacity:0.5,fontSize:"18px"}}>›</span>
                    </div>
                  </div>
                );
              })()}
              {/* Invite Friends */}
              <div className="settings-row" style={{cursor:"pointer"}} onClick={() => {
                setShowInviteSheet(true);
                if (userId && userId !== "guest") {
                  fetch(`${BACKEND}/api/user/invite-remaining`, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({userId}) })
                    .then(r => r.json()).then(d => { if (d.remaining !== undefined) setUserInviteRemaining(d.remaining); if (d.codes) setUserInviteCodes(d.codes); if (!d.ok && d.remaining === undefined) setUserInviteRemaining(0); }).catch(() => setUserInviteRemaining(0));
                }
              }}>
                <span className="settings-label"><span style={{marginRight:"8px",fontSize:"16px"}}>📤</span>Invite Friends</span>
                <span style={{color:"var(--muted)",opacity:0.5,fontSize:"18px"}}>›</span>
              </div>
              {/* Face ID lock */}
              {isNative() && (
                <div className="settings-row">
                  <div style={{flex:1,minWidth:0}}>
                    <span className="settings-label"><span style={{marginRight:"8px",fontSize:"16px"}}>🔐</span>Lock with Face ID</span>
                    <div style={{fontSize:"11px",color:"var(--muted)",marginTop:"2px"}}>Re-authenticate on launch &amp; resume</div>
                  </div>
                  <button
                    onClick={() => { const v = !faceIdLock; setFaceIdLock(v); localStorage.setItem("faceIdLock", String(v)); }}
                    style={{padding:"8px 16px",background:faceIdLock?"var(--accent)":"var(--s3)",border:`1px solid ${faceIdLock?"var(--acc2)":"var(--border)"}`,borderRadius:"8px",color:faceIdLock?"#07090c":"var(--muted)",fontSize:"13px",fontWeight:"800",cursor:"pointer",fontFamily:"inherit"}}
                  >
                    {faceIdLock ? "On" : "Off"}
                  </button>
                </div>
              )}
              {/* Privacy Policy */}
              <div className="settings-row" style={{cursor:"pointer"}} onClick={() => setShowLegalPage("privacy")}>
                <span className="settings-label"><span style={{marginRight:"8px",fontSize:"16px"}}>🔒</span>Privacy Policy</span>
                <span style={{color:"var(--muted)",opacity:0.5,fontSize:"18px"}}>›</span>
              </div>
              {/* Terms of Service */}
              <div className="settings-row" style={{cursor:"pointer"}} onClick={() => setShowLegalPage("terms")}>
                <span className="settings-label"><span style={{marginRight:"8px",fontSize:"16px"}}>📄</span>Terms of Service</span>
                <span style={{color:"var(--muted)",opacity:0.5,fontSize:"18px"}}>›</span>
              </div>
              {/* Advanced — collapsible drawer for destructive actions */}
              <div className="settings-row" style={{cursor:"pointer"}}
                onClick={() => setAdvancedExpanded(v => !v)}
                aria-expanded={advancedExpanded}>
                <span className="settings-label"><span style={{marginRight:"8px",fontSize:"16px"}}>⚙️</span>Advanced</span>
                <span style={{color:"var(--muted)",opacity:0.6,fontSize:"18px",transform: advancedExpanded ? "rotate(90deg)" : "none",transition:"transform 0.2s"}}>›</span>
              </div>
              {advancedExpanded && (
                <>
                  {/* Reset Salah History */}
                  <div className="settings-row" style={{cursor:"pointer"}} onClick={() => {
                    if (!window.confirm("Reset your entire Salah history?\n\nThis wipes every prayer you've marked and resets all streaks to zero. Your profile, notifications and other data stay intact. This cannot be undone.")) return;
                    if (!window.confirm("Are you sure? Tap OK to confirm reset.")) return;
                    setSalahLog({});
                    localStorage.removeItem("salahLog");
                    setSalahStreak({ count: 0, lastDate: null });
                    localStorage.setItem("salahStreak", JSON.stringify({ count: 0, lastDate: null }));
                    if (typeof Preferences !== 'undefined') Preferences.set({ key: 'salahStreak', value: '0' }).catch(() => {});
                  }}>
                    <div style={{flex:1,minWidth:0}}>
                      <span className="settings-label"><span style={{marginRight:"8px",fontSize:"16px"}}>🔄</span>Reset Salah History</span>
                      <div style={{fontSize:"11px",color:"var(--muted)",marginTop:"2px"}}>Clears all prayer logs and streaks</div>
                    </div>
                    <span style={{color:"var(--muted)",opacity:0.5,fontSize:"18px"}}>›</span>
                  </div>
                  {remoteConfig.showResetButton === true && (
                    <div className="settings-row" style={{cursor:"pointer"}} onClick={() => {
                      if (window.confirm("This will log you out and clear all local data. Continue?")) {
                        localStorage.clear();
                        document.cookie.split(";").forEach(c => { document.cookie = c.replace(/^ +/,"").replace(/=.*/, "=;expires=" + new Date(0).toUTCString() + ";path=/"); });
                        window.location.replace("/");
                      }
                    }}>
                      <div style={{flex:1,minWidth:0}}>
                        <span className="settings-label" style={{color:"var(--danger)"}}><span style={{marginRight:"8px",fontSize:"16px"}}>↩️</span>Reset / Switch Account</span>
                        <div style={{fontSize:"11px",color:"var(--muted)",marginTop:"2px"}}>Logs out and clears local data</div>
                      </div>
                      <span style={{color:"var(--danger)",opacity:0.5,fontSize:"18px"}}>›</span>
                    </div>
                  )}
                  {userId && (
                    <div className="settings-row" style={{cursor:"pointer"}} onClick={async () => {
                      if (!window.confirm("Permanently delete your account and all data?\n\nThis includes your Family Info Pack, Islamic Will, and salah history. This cannot be undone.")) return;
                      if (!window.confirm("Are you sure? This is permanent.")) return;
                      try { await fetch(`${BACKEND}/api/users/delete-account?userId=${userId}`, { method: "DELETE" }); } catch {}
                      localStorage.clear();
                      document.cookie.split(";").forEach(c => { document.cookie = c.replace(/^ +/,"").replace(/=.*/, "=;expires=" + new Date(0).toUTCString() + ";path=/"); });
                      window.location.replace("/");
                    }}>
                      <div style={{flex:1,minWidth:0}}>
                        <span className="settings-label" style={{color:"var(--danger)"}}><span style={{marginRight:"8px",fontSize:"16px"}}>🗑️</span>Delete My Account</span>
                        <div style={{fontSize:"11px",color:"var(--muted)",marginTop:"2px"}}>Permanently removes your account and all data</div>
                      </div>
                      <span style={{color:"var(--danger)",opacity:0.5,fontSize:"18px"}}>›</span>
                    </div>
                  )}
                </>
              )}
              {/* iOS app upsell — shown on web only; routes to Chat with Us */}
              {!isNative() && (
                <div className="settings-row" style={{cursor:"pointer",alignItems:"flex-start",borderBottom:"none"}} onClick={() => { localStorage.setItem("lastFeedbackView", new Date().toISOString()); setTab("tools"); setToolView("feedback"); fetchFeedbackHistory(); }}>
                  <div style={{flex:1,minWidth:0}}>
                    <span className="settings-label"><span style={{marginRight:"8px",fontSize:"16px"}}>📱</span>Want the iOS app?</span>
                    <div style={{fontSize:"11px",color:"var(--muted)",marginTop:"4px",lineHeight:1.55}}>Face ID lock, Live Activities and native haptics. <span style={{color:"var(--accent)",fontWeight:700}}>Contact us</span> for early access.</div>
                  </div>
                  <span style={{color:"var(--muted)",opacity:0.5,fontSize:"18px",marginTop:"2px"}}>›</span>
                </div>
              )}
            </div>

            {/* Rate Sirat — compact inline */}
            <div className="settings-card" style={{paddingBottom:ratingStars > 0 && !ratingSubmitted ? "16px" : undefined}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"4px 0"}}>
                <div>
                  <div style={{fontSize:"13px",fontWeight:"700",color:"var(--text)"}}>{ratingSubmitted ? "Thanks for rating!" : "How's Sirat?"}</div>
                  {ratingSubmitted && ratingStars > 0 && (
                    <div style={{display:"flex",alignItems:"center",gap:"8px",marginTop:"3px"}}>
                      <span style={{fontSize:"11px",color:"var(--accent)"}}>JazakAllah khayran</span>
                      <span onClick={() => setRatingSubmitted(false)} style={{fontSize:"11px",color:"var(--muted)",cursor:"pointer",textDecoration:"underline",opacity:0.7}}>Edit</span>
                    </div>
                  )}
                </div>
                <div style={{display:"flex",gap:"2px"}}>
                  {[1,2,3,4,5].map(s => (
                    <button key={s} onClick={() => { if (!ratingSubmitted) { setRatingStars(s); } }} style={{background:"none",border:"none",cursor:ratingSubmitted?"default":"pointer",padding:"2px",fontSize:"22px",transition:"transform .15s",transform:s <= ratingStars ? "scale(1.05)" : "scale(0.85)"}}>
                      <svg width="22" height="22" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z" fill={s <= ratingStars ? "var(--gold)" : "none"} stroke={s <= ratingStars ? "var(--gold)" : "var(--border)"} strokeWidth="1.5"/></svg>
                    </button>
                  ))}
                </div>
              </div>
              {ratingStars > 0 && !ratingSubmitted && (
                <div style={{marginTop:"10px"}}>
                  <textarea value={ratingComment} onChange={e => setRatingComment(e.target.value)} placeholder="Any thoughts? (optional)" maxLength={300} rows={2} style={{width:"100%",boxSizing:"border-box",background:darkMode?"var(--s1)":"#fff",border:"1px solid var(--border)",borderRadius:"10px",padding:"10px 12px",color:"var(--text)",fontSize:"13px",fontFamily:"inherit",resize:"none",outline:"none",marginBottom:"8px"}} />
                  <div style={{display:"flex",gap:"8px"}}>
                    <button onClick={() => { setRatingStars(0); setRatingComment(""); }} style={{flex:1,padding:"10px",borderRadius:"10px",border:"1px solid var(--border)",background:"transparent",color:"var(--muted)",fontWeight:"700",fontSize:"13px",cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                    <button onClick={submitRating} disabled={ratingSending} style={{flex:2,padding:"10px",borderRadius:"10px",border:"none",background:"linear-gradient(135deg,var(--gold),var(--gold2,#ff8f00))",color:darkMode?"#000":"#fff",fontWeight:"700",fontSize:"13px",cursor:"pointer",opacity:ratingSending?0.6:1,fontFamily:"inherit"}}>
                      {ratingSending ? "Submitting..." : "Submit"}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Live Activity window picker — iOS only */}
            {window.Capacitor?.isNativePlatform?.() && (
              <div className="settings-card" style={{padding:"14px"}}>
                <div style={{fontSize:"13px",fontWeight:"700",color:"var(--text)",marginBottom:"4px"}}>Live Activity</div>
                <div style={{fontSize:"11px",color:"var(--muted)",marginBottom:"10px",lineHeight:1.45}}>When the lock-screen card appears before a prayer · always on Fajr → Dhuhr</div>
                <div style={{display:"flex",gap:"6px",overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
                  {[
                    {v:"always", l:"Always"},
                    {v:"60min",  l:"60m"},
                    {v:"30min",  l:"30m"},
                    {v:"10min",  l:"10m"},
                    {v:"5min",   l:"5m"},
                    {v:"off",    l:"Off"}
                  ].map(opt => {
                    const active = liveActivityWindow === opt.v;
                    return (
                      <button
                        key={opt.v}
                        onClick={() => setLiveActivityWindow(opt.v)}
                        style={{
                          flex:"1 1 auto",
                          minWidth:"54px",
                          padding:"9px 6px",
                          borderRadius:"10px",
                          border: active ? "none" : `1px solid ${darkMode?"rgba(255,179,0,0.35)":"rgba(184,112,8,0.35)"}`,
                          background: active ? "var(--gold)" : "transparent",
                          color: active ? (darkMode?"#000":"#fff") : "var(--gold)",
                          fontSize:"12px",
                          fontWeight:"700",
                          cursor:"pointer",
                          fontFamily:"inherit",
                          whiteSpace:"nowrap"
                        }}
                      >
                        {opt.l}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Siri & iCloud debug — temporary diagnostic */}
            {(window.Capacitor?.isNativePlatform?.() || localStorage.getItem("sirat_debug") === "1") && (
              <div style={{padding:"12px 14px",margin:"0 0 12px"}}>
                <button onClick={async () => {
                  const siri = await siriGetDebugSnapshot();
                  const icloud = await getICloudDebugSnapshot();
                  const live = await getActivityDebug();
                  setSiriDebugData({ siri, icloud, live, ts: new Date().toLocaleString() });
                  setSiriDebugOpen(true);
                }} style={{width:"100%",padding:"12px",background:darkMode?"rgba(0,201,167,0.1)":"rgba(0,158,135,0.1)",border:`1px solid ${darkMode?"rgba(0,201,167,0.3)":"rgba(0,158,135,0.3)"}`,borderRadius:"12px",color:"var(--accent)",fontSize:"13px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit"}}>
                  Siri & iCloud Debug
                </button>
              </div>
            )}

            {/* Credits — compact footer */}
            <div style={{textAlign:"center",padding:"16px 8px 8px"}}>
              <div style={{fontSize:"13px",fontWeight:"800",color:"var(--gold)",fontFamily:"Georgia,serif",letterSpacing:"1.5px",marginBottom:"2px"}}>Sirat</div>
              <div style={{fontSize:"8px",color:"var(--muted)",letterSpacing:"3px",textTransform:"uppercase",marginBottom:"6px",opacity:0.6}}>The Straight Path</div>
              <div onClick={() => {
                devTapCountRef.current += 1;
                clearTimeout(devTapTimerRef.current);
                devTapTimerRef.current = setTimeout(() => { devTapCountRef.current = 0; }, 2000);
                if (devTapCountRef.current >= 10 && !devMode) {
                  devTapCountRef.current = 0;
                  const authorizedIds = [...SUPER_USER_IDS, ...(remoteConfig.superUserIds || [])];
                  if (authorizedIds.includes(userId)) {
                    setDevMode(true);
                    localStorage.setItem("devMode", "true");
                    setDevUnlockAnim(true);
                    setTimeout(() => setDevUnlockAnim(false), 5000);
                  } else {
                    setDevDeniedAnim(true);
                    setTimeout(() => setDevDeniedAnim(false), 3000);
                  }
                }
              }} style={{fontSize:"10px",color:darkMode?"rgba(148,164,176,0.35)":"rgba(80,100,110,0.4)",marginBottom:"8px",cursor:"default"}}>
                Ver {remoteConfig.version || "1.0.0"}{deployedStr ? ` · ${deployedStr}` : ""}
              </div>
              <div style={{fontSize:"10px",color:darkMode?"rgba(148,164,176,0.35)":"rgba(80,100,110,0.45)",lineHeight:1.8}}>
                Prayer times by <strong style={{color:darkMode?"rgba(148,164,176,0.5)":"rgba(80,100,110,0.6)"}}>Aladhan</strong> · Streams via{" "}
                <strong style={{color:darkMode?"rgba(148,164,176,0.5)":"rgba(80,100,110,0.6)"}}>LiveMasjid</strong>{" "}
                · Audio from <strong style={{color:darkMode?"rgba(148,164,176,0.5)":"rgba(80,100,110,0.6)"}}>everyayah</strong>
              </div>
              <div style={{fontSize:"9px",color:darkMode?"rgba(255,179,0,0.25)":"rgba(184,112,8,0.3)",letterSpacing:"2px",textTransform:"uppercase",fontWeight:"600",marginTop:"10px"}}>Made with love for the Ummah by YD</div>
            </div>

            {/* Dev Mode — Your Sirat Journey */}
            {devMode && (
              <div style={{marginTop:"4px",marginBottom:"8px"}}>
                <div onClick={() => setShowDevJourney(true)} style={{display:"flex",alignItems:"center",justifyContent:"center",gap:"8px",padding:"12px",background:darkMode?"rgba(0,201,167,0.05)":"rgba(0,158,135,0.05)",border:`1px solid ${darkMode?"rgba(0,201,167,0.15)":"rgba(0,158,135,0.15)"}`,borderRadius:"14px",cursor:"pointer"}}>
                  <span style={{fontSize:"14px"}}>🧑‍💻</span>
                  <span style={{fontSize:"13px",fontWeight:"700",color:"var(--accent)",animation:"devPulse 2.5s infinite"}}>Super-User Mode</span>
                </div>
              </div>
            )}

            {/* Native Push Diagnostics — visible in super-user mode on iOS native */}
            {devMode && isNative() && (() => {
              const dbg = getNativePushDebug();
              const lsFollows = (() => { try { return JSON.parse(localStorage.getItem("followedMounts") || "[]"); } catch { return []; } })();
              const lsNotifyRaw = localStorage.getItem("notifyMounts");
              const lsNotify = lsNotifyRaw === null ? null : (() => { try { return JSON.parse(lsNotifyRaw); } catch { return []; } })();
              const cachedToken = localStorage.getItem("apnsToken");
              const fmtAt = (iso) => iso ? new Date(iso).toLocaleTimeString() : "—";
              const statusColor = dbg.lastSubscribePostStatus === 200 ? "#00c9a7" : dbg.lastSubscribePostStatus === null ? "#888" : "#ef6a5a";
              return (
                <div style={{marginTop:"8px",marginBottom:"8px",padding:"12px",background:darkMode?"rgba(0,0,0,0.3)":"rgba(0,0,0,0.04)",border:`1px solid ${darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.08)"}`,borderRadius:"12px",fontFamily:"ui-monospace,monospace",fontSize:"11px",color:"var(--text)"}}>
                  <div style={{fontSize:"10px",fontWeight:700,letterSpacing:"1.5px",textTransform:"uppercase",color:"var(--muted)",marginBottom:"8px"}}>Native Push Debug · poll #{pushDebugTick}</div>
                  <div style={{display:"grid",gridTemplateColumns:"auto 1fr",gap:"4px 12px",alignItems:"baseline"}}>
                    <span style={{color:"var(--muted)"}}>userId</span><span>{userId ? `${userId.slice(0,8)}…${userId.slice(-4)}` : "(none)"}</span>
                    <span style={{color:"var(--muted)"}}>perm</span><span style={{color: dbg.permResult === 'granted' ? '#00c9a7' : dbg.permResult ? '#ef6a5a' : 'var(--muted)'}}>{dbg.permResult || "—"}</span>
                    <span style={{color:"var(--muted)"}}>registered</span><span>{dbg.registered ? `yes · token ${dbg.tokenLen}ch` : "no"}</span>
                    <span style={{color:"var(--muted)"}}>reg outcome</span><span style={{color: dbg.registrationOutcome === 'registration-event' ? '#00c9a7' : dbg.registrationOutcome ? '#ef6a5a' : 'var(--muted)'}}>{dbg.registrationOutcome || "—"} {dbg.registrationOutcomeAt ? `· ${fmtAt(dbg.registrationOutcomeAt)}` : ""}</span>
                    <span style={{color:"var(--muted)"}}>token src</span><span>{dbg.tokenSource || "—"}{cachedToken ? ` · LS …${cachedToken.slice(-8)}` : ""}</span>
                    <span style={{color:"var(--muted)"}}>last event</span><span>{dbg.lastEvent || "—"} <span style={{color:"var(--muted)"}}>· {fmtAt(dbg.lastEventAt)}</span></span>
                    <span style={{color:"var(--muted)"}}>last POST</span><span style={{color:statusColor}}>{dbg.lastSubscribePostStatus ?? "—"} {dbg.lastSubscribeError ? `(${dbg.lastSubscribeError.slice(0,40)})` : ""} <span style={{color:"var(--muted)"}}>· {fmtAt(dbg.lastSubscribePostAt)}</span></span>
                    <span style={{color:"var(--muted)"}}>follows (LS)</span><span>{JSON.stringify(lsFollows)}</span>
                    <span style={{color:"var(--muted)"}}>notify (LS)</span><span>{JSON.stringify(lsNotify)}</span>
                    <span style={{color:"var(--muted)"}}>last sent</span><span>follows={JSON.stringify(dbg.lastFollows)} notify={JSON.stringify(dbg.lastNotify)}</span>
                  </div>
                  <button onClick={async () => {
                    const uid = localStorage.getItem("azaan-userId");
                    const follows = lsFollows;
                    const notify = lsNotify !== null ? lsNotify : follows;
                    await registerNativePush(uid, follows, notify, {
                      lat: parseFloat(localStorage.getItem("userLat") || "") || null,
                      lng: parseFloat(localStorage.getItem("userLng") || "") || null,
                      school: parseInt(localStorage.getItem("school") || "1"),
                      dailyNotifEnabled: localStorage.getItem("dailyNotifEnabled") !== "false",
                      dailyNameEnabled: localStorage.getItem("dailyNameEnabled") === "true",
                    });
                    setHeaderNow(new Date());
                  }} style={{marginTop:"10px",padding:"8px 14px",background:"var(--accent)",border:"none",borderRadius:"8px",color:"#000",fontSize:"11px",fontWeight:700,cursor:"pointer"}}>Force re-sync now</button>
                </div>
              );
            })()}
          </div>
          );
        })()}

        </div>{/* end scroll-content */}
              </div>
      {/* Fixed disclaimers above bottom nav */}
      {tab === "radio" && stations && !showHijriCalendar && (
        <div style={{position:"fixed",bottom:`calc(28px + env(safe-area-inset-bottom))`,left:0,right:0,maxWidth:480,margin:"0 auto",textAlign:"center",padding:"5px 16px",zIndex:99,pointerEvents:"none"}}>
          <span style={{fontSize:"10px",color:"var(--muted)",opacity:0.75}}>
            Publicly accessible streams · not affiliated with or endorsed by any station
          </span>
        </div>
      )}

      {/* A2HS nudge for khatm guests joining via browser */}
      {khatmShowA2HS && (() => {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        const canInstall = !!deferredInstallPrompt.current;
        return (
          <div style={{ position: "fixed", left: "50%", transform: "translateX(-50%)", bottom: "calc(90px + env(safe-area-inset-bottom))", width: "calc(100% - 32px)", maxWidth: "420px", zIndex: 1200,
            background: darkMode ? "rgba(10,22,18,0.97)" : "rgba(255,255,255,0.97)", backdropFilter: "blur(16px)",
            border: `1px solid ${darkMode ? "rgba(0,201,167,0.3)" : "rgba(0,158,135,0.25)"}`, borderRadius: "18px",
            padding: "14px 16px", boxShadow: "0 8px 32px rgba(0,0,0,0.35)", display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{ fontSize: "26px", flexShrink: 0 }}>📲</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: "13px", fontWeight: "800", color: "var(--text)", marginBottom: "2px" }}>Save Sirat to your Home Screen</div>
              <div style={{ fontSize: "11px", color: "var(--muted)", lineHeight: 1.4 }}>
                {canInstall ? "Tap below to install for the best experience." : isIOS ? <>Tap <strong style={{color:"var(--text)"}}>Share ⎙</strong> → <strong style={{color:"var(--text)"}}>Add to Home Screen</strong></> : <>Tap <strong style={{color:"var(--text)"}}>⋮</strong> → <strong style={{color:"var(--text)"}}>Install app</strong></>}
              </div>
              {canInstall && (
                <button onClick={async () => { deferredInstallPrompt.current.prompt(); await deferredInstallPrompt.current.userChoice; deferredInstallPrompt.current = null; setKhatmShowA2HS(false); }}
                  style={{ marginTop: "8px", padding: "7px 16px", borderRadius: "10px", border: "none", background: "var(--accent)", color: "#fff", fontSize: "12px", fontWeight: "700", cursor: "pointer", fontFamily: "inherit" }}>
                  Install App
                </button>
              )}
            </div>
            <button onClick={() => setKhatmShowA2HS(false)}
              style={{ background: "none", border: "none", color: "var(--muted)", fontSize: "18px", cursor: "pointer", padding: "4px", flexShrink: 0, lineHeight: 1 }}>✕</button>
          </div>
        );
      })()}

      {/* Qibla full-screen overlay */}
      {showQiblaOverlay && (
        <div style={{position:"fixed",inset:0,zIndex:9200,background:"var(--bg)",display:"flex",flexDirection:"column"}}>
          <div style={{padding:"14px 20px 16px",paddingTop:"calc(14px + env(safe-area-inset-top, 0px))",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0,background:darkMode?"rgba(7,9,12,0.92)":"rgba(240,245,250,0.92)",backdropFilter:"blur(20px) saturate(180%)",WebkitBackdropFilter:"blur(20px) saturate(180%)",borderBottom:`1px solid ${darkMode?"rgba(255,179,0,0.2)":"rgba(184,112,8,0.18)"}`}}>
            <div>
              <div style={{fontFamily:'"JetBrains Mono",ui-monospace,monospace',fontSize:"9.5px",letterSpacing:"2.5px",color:"var(--muted)",textTransform:"uppercase",marginBottom:"4px"}}>Direction to Makkah</div>
              <div style={{fontFamily:'"Instrument Serif",Georgia,serif',fontStyle:"italic",fontSize:"26px",color:"var(--text)",letterSpacing:"-0.02em",lineHeight:1}}>Qibla</div>
            </div>
            <button onClick={() => setShowQiblaOverlay(false)}
              style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.14)",borderRadius:"50%",width:"36px",height:"36px",display:"flex",alignItems:"center",justifyContent:"center",color:"var(--text)",fontSize:"16px",cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>✕</button>
          </div>
          <div style={{flex:1,overflowY:"auto",WebkitOverflowScrolling:"touch"}}>
            <Lazy><Qibla darkMode={darkMode} /></Lazy>
          </div>
        </div>
      )}

      {/* Bottom Nav — Liquid Glass Tab Bar */}
      <TabBar
        darkMode={darkMode}
        hidden={toolView === "feedback"}
        tabs={[
          { id:"masjids", icon:"◇", label: (tabsConfig.masjids?.label && tabsConfig.masjids.label !== "Masjids") ? tabsConfig.masjids.label : "Salaah", active: tab==="masjids" && !(tab==="tools" && siratProSourceRef.current==="settings"), onClick:()=>navTransition(() => setTab("masjids")) },
          { id:"radio", icon:"◈", label: tabsConfig.radio?.label || "Radio", active: tab==="radio", onClick:()=>navTransition(() => setTab("radio")) },
          { id:"tools", icon:"✧", label: tabsConfig.tools?.label || "Ibadah", active: tab==="tools" && siratProSourceRef.current!=="settings", badge: (remoteConfig.newFeatures || []).some(f => !seenNewFeatures.includes(f)), onClick:()=>{ navTransition(() => { setTab("tools"); setToolView("grid"); setShowSleepMenu(false); setShowReciterPicker(false); siratProSourceRef.current = null; }); } },
          { id:"hayah", icon:"❋", label: tabsConfig.hayah?.label || "Hayah", active: tab==="hayah", onClick:()=>navTransition(() => {
            setTab("hayah");
            // Tapping Hayah always lands on Ghidha → Today, except when a
            // workout is mid-flight — then go straight to Riyadah so the
            // active session is one tap away. 24h freshness window matches
            // loadActiveSession() inside Riyadah.
            let hasActiveRiyadah = false;
            try {
              const raw = localStorage.getItem("sirat-riyadah-active");
              if (raw) {
                const sess = JSON.parse(raw);
                const lastTouched = Number(sess && sess.lastTouched) || 0;
                hasActiveRiyadah = lastTouched > 0 && (Date.now() - lastTouched) < 24 * 60 * 60 * 1000;
              }
            } catch {}
            if (hasActiveRiyadah) {
              setHayahFeature("exercise");
            } else {
              setHayahFeature("today");
            }
            setHayahResetKey(k => k + 1);
          }) },
          { id:"settings", icon:"≡", label: tabsConfig.settings?.label || "Settings", active: tab==="settings" || (tab==="tools" && siratProSourceRef.current==="settings"), onClick:()=>navTransition(() => setTab("settings")) },
          ...(isMaintainer ? [{ id:"maintainer", icon:"⚙", label:"Admin", active: tab==="maintainer", badge: maintainerUnreadCount > 0, badgeRed: true, onClick:()=>navTransition(() => setTab("maintainer")) }] : []),
        ].filter(t => !(tabsConfig[t.id]?.enabled === false && !isMaintainer && !SUPER_USER_IDS.includes(userId) && !(remoteConfig.superUserIds || []).includes(userId) && !(remoteConfig.tabAccessIds?.[t.id] || []).includes(userId)))
          .filter(t => !focusHiddenTabs.includes(t.id))}
      />

      {/* Dev Mode unlock animation — Arabic matrix rain + confetti */}
      {devUnlockAnim && (() => {
        const arabicWords = ["بِسْمِ","ٱللَّٰهِ","ٱلرَّحْمَٰنِ","ٱلرَّحِيمِ","سُبْحَانَ","ٱللَّٰهِ","ٱلْحَمْدُ","لِلَّٰهِ","ٱللَّٰهُ","أَكْبَرُ","لَا إِلَٰهَ","إِلَّا ٱللَّٰهُ","مَا شَاءَ","ٱللَّٰهُ","بَارَكَ","ٱللَّٰهُ"];
        const confettiColors = ["#ffb300","#00c9a7","#ff6b6b","#a078ff","#48c76e","#ffdc3c","#ff963c","#00e5c0","#e07000"];
        return (
          <div style={{position:"fixed",inset:0,zIndex:9999,pointerEvents:"none",overflow:"hidden",background:"rgba(0,0,0,0.3)"}}>
            {/* Arabic rain — more columns, bigger, brighter */}
            {Array.from({length:40}).map((_,i) => (
              <div key={"ar"+i} style={{
                position:"absolute",
                top:"-40px",
                left: (i * 2.5 + Math.random()*2) + "%",
                fontSize: (16 + Math.random()*20) + "px",
                color: i % 3 === 0 ? "rgba(0,201,167,0.9)" : i % 3 === 1 ? "rgba(255,179,0,0.85)" : "rgba(160,120,255,0.7)",
                fontFamily:"'Amiri','Scheherazade',serif",
                animation:`arabicRain ${1.5 + Math.random()*2}s linear ${Math.random()*1.2}s`,
                animationFillMode:"forwards",
                textShadow: `0 0 20px ${i%3===0?"rgba(0,201,167,0.7)":i%3===1?"rgba(255,179,0,0.6)":"rgba(160,120,255,0.5)"}`,
                fontWeight:"700",
              }}>{arabicWords[Math.floor(Math.random()*arabicWords.length)]}</div>
            ))}
            {/* Confetti — more particles, bigger */}
            {Array.from({length:60}).map((_,i) => (
              <div key={"cf"+i} style={{
                position:"absolute",
                top:"-20px",
                left: (Math.random()*100) + "%",
                width: (8+Math.random()*10)+"px",
                height: (8+Math.random()*10)+"px",
                borderRadius: Math.random()>0.4?"50%":"3px",
                background: confettiColors[i % confettiColors.length],
                animation:`devConfetti ${1.5+Math.random()*2.5}s linear ${Math.random()*0.8}s`,
                animationFillMode:"forwards",
                opacity:0.9,
                boxShadow: `0 0 6px ${confettiColors[i % confettiColors.length]}80`,
              }}/>
            ))}
            {/* Center text */}
            <div style={{
              position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",
              textAlign:"center",animation:"journeyIn 0.5s cubic-bezier(0.22,1,0.36,1)",
              background:"rgba(7,9,12,0.85)",backdropFilter:"blur(12px)",WebkitBackdropFilter:"blur(12px)",
              padding:"32px 40px",borderRadius:"24px",border:"1px solid rgba(0,201,167,0.3)",
              boxShadow:"0 0 60px rgba(0,201,167,0.2), 0 0 120px rgba(255,179,0,0.1)",
            }}>
              <div style={{fontSize:"42px",marginBottom:"10px"}}>🧑‍💻</div>
              <div style={{fontSize:"22px",fontWeight:"800",color:"#00c9a7",textShadow:"0 0 30px rgba(0,201,167,0.6)"}}>Super-User Mode</div>
              <div style={{fontSize:"15px",color:"rgba(255,179,0,0.9)",marginTop:"8px",fontWeight:"700",textShadow:"0 0 15px rgba(255,179,0,0.4)"}}>بِسْمِ ٱللَّٰهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ</div>
              <div style={{fontSize:"12px",color:"rgba(232,240,245,0.6)",marginTop:"10px",letterSpacing:"3px",textTransform:"uppercase"}}>Unlocked</div>
            </div>
          </div>
        );
      })()}

      {/* Dev Mode — Access Denied animation */}
      {devDeniedAnim && (
        <div style={{position:"fixed",inset:0,zIndex:9999,pointerEvents:"none",overflow:"hidden",background:"rgba(0,0,0,0.3)",display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{textAlign:"center",animation:"popIn 0.4s cubic-bezier(0.22,1,0.36,1)",background:"rgba(7,9,12,0.9)",backdropFilter:"blur(12px)",WebkitBackdropFilter:"blur(12px)",padding:"32px 40px",borderRadius:"24px",border:"1px solid rgba(255,100,100,0.3)",boxShadow:"0 0 60px rgba(255,100,100,0.15)"}}>
            <div style={{fontSize:"42px",marginBottom:"10px"}}>{"\u{1F512}"}</div>
            <div style={{fontSize:"18px",fontWeight:"800",color:"var(--danger)"}}>Access Denied</div>
            <div style={{fontSize:"12px",color:"rgba(232,240,245,0.5)",marginTop:"8px",maxWidth:"200px",lineHeight:1.5}}>Your account is not authorized for Super-User access</div>
          </div>
        </div>
      )}

      {/* Dev Mode — Your Sirat Journey overlay */}
      {showDevJourney && (() => {
        const suMenu = remoteConfig.superUserMenu || ["journey","resetHints","generateCodes","lock"];
        const codeLimit = remoteConfig.superUserCodeLimit || 5;
        const totalPrayers = Object.values(salahLog).reduce((sum, day) => {
          return sum + ["Fajr","Dhuhr","Asr","Maghrib","Isha"].filter(p => day[p]).length;
        }, 0);
        const daysTracked = Object.keys(salahLog).length;
        const weekDotData = [];
        for (let d = 6; d >= 0; d--) {
          const dt = new Date(); dt.setDate(dt.getDate() - d);
          const ds = dt.getFullYear() + "-" + String(dt.getMonth()+1).padStart(2,"0") + "-" + String(dt.getDate()).padStart(2,"0");
          const dayLog = salahLog[ds] || {};
          weekDotData.push(["Fajr","Dhuhr","Asr","Maghrib","Isha"].every(p => dayLog[p]));
        }
        const perfectThisWeek = weekDotData.filter(Boolean).length;
        const bestStreak = salahStreak.count || 0;
        const readStreak = readingStreak.count || 0;
        const joinDate = (() => { try { const u = localStorage.getItem("registeredAt") || localStorage.getItem("azaan-userId-date"); return u ? new Date(u).toLocaleDateString("en-ZA",{day:"numeric",month:"long",year:"numeric"}) : null; } catch { return null; } })();
        const daysSinceJoin = (() => { try { const u = localStorage.getItem("registeredAt") || localStorage.getItem("azaan-userId-date"); return u ? Math.floor((Date.now() - new Date(u).getTime()) / 86400000) : null; } catch { return null; } })();
        const lsKeys = Object.keys(localStorage).length;
        const lsSize = (() => { try { let t = 0; for (let k in localStorage) { if (localStorage.hasOwnProperty(k)) t += localStorage[k].length + k.length; } return (t * 2 / 1024).toFixed(1) + " KB"; } catch { return "—"; } })();
        const cachedSurahs = (() => { try { return Object.keys(localStorage).filter(k => k.startsWith("surahCache_")).length; } catch { return 0; } })();
        const platform = /iPhone|iPad|iPod/.test(navigator.userAgent) ? "iOS" : /Android/.test(navigator.userAgent) ? "Android" : "Web";
        const isPWA = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
        const stats = [
          { icon:"\u{1F932}", label:"Prayers Tracked", value: totalPrayers },
          { icon:"\u{1F4C5}", label:"Days Active", value: daysTracked },
          { icon:"\u2728", label:"Perfect Days This Week", value: perfectThisWeek + "/7" },
          { icon:"\u{1F525}", label:"Salah Streak", value: bestStreak + " day" + (bestStreak!==1?"s":"") },
          { icon:"\u{1F4D6}", label:"Quran Reading Streak", value: readStreak + " day" + (readStreak!==1?"s":"") },
        ];
        if (daysSinceJoin !== null) stats.push({ icon:"\u{1F331}", label:"Days on Sirat", value: daysSinceJoin });
        const techStats = [
          { label:"Platform", value: platform + (isPWA ? " (PWA)" : " (Browser)") },
          { label:"Theme", value: darkMode ? "Dark" : "Light" },
          { label:"Cached Surahs", value: cachedSurahs },
          { label:"LocalStorage", value: lsKeys + " keys \u00B7 " + lsSize },
          { label:"User ID", value: (userId || "\u2014").slice(0,12) + "..." },
        ];
        const handleGenerateOneCode = () => {
          setCodeGenLoading(true);
          fetch(`${BACKEND}/api/super-user/generate-codes`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ userId, count: 1 }),
          })
            .then(r => r.json())
            .then(d => {
              if (d.codes && d.codes.length > 0) { setGeneratedCodes(prev => [...d.codes, ...prev]); if (d.remaining !== undefined) setSuCodesRemaining(d.remaining); }
              else if (d.error) { alert(d.error); if (d.remaining !== undefined) setSuCodesRemaining(d.remaining); }
            })
            .catch(() => alert("Failed to generate code. Try again."))
            .finally(() => setCodeGenLoading(false));
        };
        // Fetch remaining count + existing unused codes on mount
        if (suCodesRemaining === null) {
          fetch(`${BACKEND}/api/super-user/code-remaining`, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({userId}) })
            .then(r => r.json()).then(d => { if (d.ok) { setSuCodesRemaining(d.remaining); if (d.suLabel) setSuCodeLabel(d.suLabel); if (d.codes && d.codes.length > 0) setGeneratedCodes(d.codes); } }).catch(() => {});
        }
        return (
          <>
            <div className="dev-journey-overlay" onClick={() => { setShowDevJourney(false); setGeneratedCodes([]); setSuCodesRemaining(null); }} />
            <div className="dev-journey" style={{background:darkMode?"linear-gradient(160deg,#0a1a14 0%,#071210 50%,#0a1a14 100%)":"linear-gradient(160deg,#f0faf7 0%,#eef8f6 50%,#f0faf7 100%)",border:`1.5px solid ${darkMode?"rgba(0,201,167,0.3)":"rgba(0,158,135,0.25)"}`,boxShadow:darkMode?"0 24px 64px rgba(0,0,0,0.8), 0 0 60px rgba(0,201,167,0.1)":"0 24px 64px rgba(0,0,0,0.1)"}}>
              <div style={{textAlign:"center",marginBottom:"16px"}}>
                <div style={{fontSize:"32px",marginBottom:"6px"}}>{"\u{1F9D1}\u200D\u{1F4BB}"}</div>
                <div style={{fontSize:"18px",fontWeight:"800",color:"var(--accent)"}}>Your Sirat Journey</div>
                <div style={{fontSize:"10px",color:"var(--gold)",letterSpacing:"2px",textTransform:"uppercase",fontWeight:"700",marginTop:"4px",opacity:0.7}}>Super-User</div>
                {joinDate && <div style={{fontSize:"11px",color:"var(--muted)",marginTop:"4px"}}>Member since {joinDate}</div>}
              </div>
              {/* Journey Stats — controlled by superUserMenu */}
              {suMenu.includes("journey") && (
                <div style={{display:"flex",flexDirection:"column",gap:"2px"}}>
                  {stats.map(s => (
                    <div key={s.label} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 14px",background:darkMode?"rgba(0,201,167,0.04)":"rgba(0,158,135,0.04)",borderRadius:"12px"}}>
                      <div style={{display:"flex",alignItems:"center",gap:"10px"}}>
                        <span style={{fontSize:"18px"}}>{s.icon}</span>
                        <span style={{fontSize:"13px",fontWeight:"600",color:"var(--text)"}}>{s.label}</span>
                      </div>
                      <span style={{fontSize:"15px",fontWeight:"800",color:"var(--accent)"}}>{s.value}</span>
                    </div>
                  ))}
                </div>
              )}
              {/* Technical / Device Info — controlled by superUserMenu */}
              {suMenu.includes("techStats") && (
                <div style={{marginTop:"14px"}}>
                  <div style={{fontSize:"10px",color:"var(--muted)",letterSpacing:"1.5px",textTransform:"uppercase",fontWeight:"700",marginBottom:"8px",paddingLeft:"4px",opacity:0.6}}>Device & Cache</div>
                  <div style={{background:darkMode?"rgba(255,255,255,0.02)":"rgba(0,0,0,0.02)",border:`1px solid ${darkMode?"rgba(255,255,255,0.05)":"rgba(0,0,0,0.06)"}`,borderRadius:"12px",padding:"10px 14px"}}>
                    {techStats.map(s => (
                      <div key={s.label} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"5px 0",borderBottom:`1px solid ${darkMode?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.04)"}`}}>
                        <span style={{fontSize:"12px",color:"var(--muted)"}}>{s.label}</span>
                        <span style={{fontSize:"12px",fontWeight:"700",color:"var(--text)",fontFamily:"monospace"}}>{s.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Generate Invite Codes — controlled by superUserMenu */}
              {suMenu.includes("generateCodes") && (
                <div style={{marginTop:"14px"}}>
                  <div style={{fontSize:"10px",color:"var(--muted)",letterSpacing:"1.5px",textTransform:"uppercase",fontWeight:"700",marginBottom:"8px",paddingLeft:"4px",opacity:0.6}}>Invite Codes</div>
                  <div style={{background:darkMode?"rgba(255,179,0,0.04)":"rgba(184,112,8,0.04)",border:`1px solid ${darkMode?"rgba(255,179,0,0.15)":"rgba(184,112,8,0.15)"}`,borderRadius:"14px",padding:"14px"}}>
                    <div style={{fontSize:"12px",color:"var(--muted)",marginBottom:"6px",lineHeight:1.5}}>
                      Generate invite codes to share with friends and family.
                    </div>
                    {suCodesRemaining !== null && <div style={{fontSize:"11px",color:suCodesRemaining > 0 ? "var(--accent)" : "#f85149",fontWeight:"700",marginBottom:"10px"}}>{suCodesRemaining > 0 ? `${suCodesRemaining} code${suCodesRemaining !== 1 ? "s" : ""} remaining` : "Code limit reached"}</div>}
                    <button onClick={handleGenerateOneCode} disabled={codeGenLoading || suCodesRemaining === 0} style={{width:"100%",padding:"12px",borderRadius:"12px",border:"none",background:"linear-gradient(135deg,var(--gold),var(--gold2,#ff8f00))",color:darkMode?"#000":"#fff",fontSize:"13px",fontWeight:"800",cursor:"pointer",fontFamily:"inherit",opacity:(codeGenLoading||suCodesRemaining===0)?0.5:1,marginBottom: generatedCodes.length > 0 ? "12px" : "0"}}>
                      {codeGenLoading ? "Generating..." : suCodesRemaining === 0 ? "Limit Reached" : "Generate Code"}
                    </button>
                    {generatedCodes.length > 0 && (
                      <div style={{display:"flex",flexDirection:"column",gap:"6px"}}>
                        <div style={{fontSize:"10px",color:"var(--muted)",letterSpacing:"1px",textTransform:"uppercase",fontWeight:"700",opacity:0.6,marginBottom:"2px"}}>Latest code (tap to copy)</div>
                        {[generatedCodes[generatedCodes.length - 1]].map((code, i) => {
                          ensureQrDataUrl(code);
                          const isCopied = copiedCodeIdx === i;
                          const deepLink = `${window.location.origin}?code=${code}`;
                          const waText = encodeURIComponent(`Assalamu Alaykum \u{1F319}\n\nI'd like to personally invite you to *Sirat \u2014 The Straight Path*.\n\nIt's a private Islamic app \u2014 prayer times, live masjid audio, Quran, Qibla & more. No ads, no noise. Just ibadah.\n\nSirat is invite-only and I thought of you. Here is your personal code:\n\n*${code}*\n\n\u{1F449} ${deepLink}\n\nMay Allah accept it from us all. \u{1F932}`);
                          return (
                          <div key={code} style={{display:"flex",flexDirection:"column",gap:"8px"}}>
                            <div onClick={() => {
                              try { if (navigator.clipboard && navigator.clipboard.writeText) { navigator.clipboard.writeText(code); } else { const ta = document.createElement("textarea"); ta.value = code; ta.style.position = "fixed"; ta.style.opacity = "0"; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta); } } catch {}
                              setCopiedCodeIdx(i); setTimeout(() => setCopiedCodeIdx(prev => prev === i ? null : prev), 2000);
                            }} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 12px",background:isCopied?(darkMode?"rgba(0,201,167,0.1)":"rgba(0,158,135,0.08)"):(darkMode?"rgba(255,179,0,0.06)":"rgba(184,112,8,0.06)"),borderRadius:"10px",cursor:"pointer",border:`1px solid ${isCopied?(darkMode?"rgba(0,201,167,0.3)":"rgba(0,158,135,0.3)"):(darkMode?"rgba(255,179,0,0.1)":"rgba(184,112,8,0.1)")}`,transition:"all 0.2s"}}>
                              <span style={{fontSize:"14px",fontWeight:"800",color:isCopied?"var(--accent)":"var(--gold)",fontFamily:"monospace",letterSpacing:"1px",transition:"all 0.2s",flex:1}}>{code}</span>
                              <span style={{fontSize:"10px",color:isCopied?"var(--accent)":"var(--muted)",opacity:isCopied?1:0.6,fontWeight:isCopied?"700":"400"}}>{isCopied ? "\u2713 Copied!" : "tap to copy"}</span>
                            </div>
                            {qrDataUrls[code] && (
                            <div style={{textAlign:"center",padding:"12px 0 4px"}}>
                              <img src={qrDataUrls[code]} alt="QR invite" style={{width:180,height:180,borderRadius:"12px",border:"2px solid rgba(255,179,0,0.35)"}} />
                              <div style={{fontSize:"10px",color:"var(--muted)",marginTop:"6px",letterSpacing:"0.5px"}}>Hold up for friend to scan</div>
                            </div>
                            )}
                            <div onClick={() => { window.open(`https://wa.me/?text=${waText}`, '_blank', 'noopener'); }}
                              style={{display:"flex",alignItems:"center",justifyContent:"center",gap:"7px",padding:"10px 12px",background:"rgba(37,211,102,0.12)",border:"1px solid rgba(37,211,102,0.3)",borderRadius:"10px",cursor:"pointer"}}>
                              <span style={{fontSize:"16px"}}>{"\u{1F4AC}"}</span>
                              <span style={{fontSize:"12px",fontWeight:"700",color:"#25D366"}}>Send via WhatsApp</span>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {/* Simulate Expiry — always visible in super-user (works even with allFree) */}
              <button onClick={async () => {
                const label = isTrial ? "trial" : isPro ? "Pro" : "access";
                if (!window.confirm(`Simulate ${label} expiry? This will revoke all access on this device AND the server so you can see the paywall flow.`)) return;
                // Set guard FIRST to prevent checkUserStatus from restoring Pro
                proRevokedRef.current = true;
                // Clear ALL access — Pro, trial, and allFree override
                localStorage.removeItem("isPro");
                localStorage.removeItem("proExpiry");
                localStorage.removeItem("trialStart");
                localStorage.setItem("trialExpired", "true"); // prevent auto-restart of trial
                clearProCookie();
                setIsPro(false);
                setProExpiry(null);
                setTrialStart(null);
                setShowDevJourney(false); setGeneratedCodes([]); setSuCodesRemaining(null);
                // Await the backend call so it completes before any future checkUserStatus
                try {
                  await fetch(`${BACKEND}/api/users/${userId}/revoke-pro`, { method:"POST", headers:{"Content-Type":"application/json"} });
                } catch {}
                // Clear the guard after backend confirmed
                proRevokedRef.current = false;
              }} style={{width:"100%",marginTop:"14px",padding:"11px",borderRadius:"12px",border:`1px solid rgba(255,100,100,0.2)`,background:"rgba(255,100,100,0.06)",color:"var(--danger)",fontSize:"12px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit"}}>
                {"\u23F1"} Simulate {isTrial ? "Trial" : isPro ? "Pro" : "Access"} Expiry
              </button>
              {/* Actions */}
              <div style={{marginTop:"14px",display:"flex",gap:"8px"}}>
                {suMenu.includes("resetHints") && (
                  <button onClick={() => {
                    // Reset ALL onboarding/hint state to simulate fresh new user
                    localStorage.removeItem("salahHintShown");
                    localStorage.removeItem("themeHintShown");
                    localStorage.removeItem("logoHintShown");
                    localStorage.removeItem("onboardingDone");
                    localStorage.removeItem("permFlowDone");
                    localStorage.removeItem("ceremonyShown");
                    localStorage.removeItem("whatsNewSeen");
                    localStorage.removeItem("seenNewFeatures");
                    localStorage.removeItem("setupBannerDismissed");
                    localStorage.removeItem("trialStart");
                    localStorage.removeItem("trialExpired");
                    localStorage.removeItem("proExpiry");
                    localStorage.removeItem("isPro");
                    setShowLogoHint(true);
                    setShowThemeHint(true);
                    setShowOnboardingTips(true);
                    setShowDevJourney(false);
                    setSeenNewFeatures([]);
                    setSetupBannerDismissed({});
                    setIsPro(false);
                    setTrialStart(null);
                    // Trigger ceremony + permission flow on next render
                    setTimeout(() => {
                      ceremonyNameRef.current = userName;
                      setShowOnboardingCeremony(true);
                      setCeremonyFading(false);
                      setTimeout(() => {
                        setCeremonyFading(true);
                        setTimeout(() => {
                          setShowOnboardingCeremony(false);
                          setCeremonyFading(false);
                          // Show welcome overlay, then permission flow
                          setWelcomeName(userName);
                          setShowWelcome(true);
                        }, 900);
                      }, 3500);
                    }, 300);
                  }} style={{flex:1,padding:"11px",borderRadius:"12px",border:`1px solid ${darkMode?"rgba(255,179,0,0.2)":"rgba(184,112,8,0.2)"}`,background:"transparent",color:"var(--gold)",fontSize:"12px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit"}}>
                    {"\u{1F504}"} Reset All Hints
                  </button>
                )}
                {suMenu.includes("lock") && (
                  <button onClick={() => {
                    setDevMode(false);
                    localStorage.removeItem("devMode");
                    setShowDevJourney(false);
                    setGeneratedCodes([]);
                    setSuCodesRemaining(null);
                  }} style={{flex:1,padding:"11px",borderRadius:"12px",border:`1px solid ${darkMode?"rgba(255,100,100,0.2)":"rgba(200,80,80,0.2)"}`,background:"transparent",color:"var(--danger)",fontSize:"12px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit"}}>
                    {"\u{1F512}"} Lock Super-User
                  </button>
                )}
              </div>
              {/* Motivational */}
              <div style={{textAlign:"center",marginTop:"14px",padding:"14px",background:darkMode?"rgba(255,179,0,0.05)":"rgba(184,112,8,0.05)",borderRadius:"14px",border:`1px solid ${darkMode?"rgba(255,179,0,0.12)":"rgba(184,112,8,0.12)"}`}}>
                <div style={{fontSize:"14px",color:"var(--gold)",fontFamily:"Georgia,serif",fontStyle:"italic",lineHeight:1.6}}>
                  {totalPrayers >= 100 ? '"Indeed, prayer prohibits immorality and wrongdoing"' :
                   totalPrayers >= 25 ? '"And seek help through patience and prayer"' :
                   '"So remember Me; I will remember you"'}
                </div>
                <div style={{fontSize:"10px",color:"var(--muted)",marginTop:"6px"}}>
                  {totalPrayers >= 100 ? "\u2014 Al-Ankabut 29:45" :
                   totalPrayers >= 25 ? "\u2014 Al-Baqarah 2:45" :
                   "\u2014 Al-Baqarah 2:152"}
                </div>
              </div>
              <button onClick={() => { setShowDevJourney(false); setGeneratedCodes([]); setSuCodesRemaining(null); }} style={{width:"100%",marginTop:"14px",padding:"14px",borderRadius:"14px",border:"none",background:"var(--accent)",color:"#07090c",fontSize:"15px",fontWeight:"800",cursor:"pointer",fontFamily:"inherit"}}>
                Close
              </button>
            </div>
          </>
        );
      })()}

      {/* Invite Friends sheet */}
      {showInviteSheet && (
        <div onClick={() => setShowInviteSheet(false)} style={{position:"fixed",inset:0,zIndex:999,background:"rgba(0,0,0,0.7)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"flex-end",padding:"0"}}>
          <div onClick={e => e.stopPropagation()} style={{width:"100%",maxWidth:"480px",background:"var(--s1)",borderRadius:"20px 20px 0 0",overflow:"hidden",border:"1px solid var(--border)",borderBottom:"none",maxHeight:"85vh",display:"flex",flexDirection:"column"}}>
            {/* Handle */}
            <div style={{display:"flex",justifyContent:"center",padding:"10px 0 4px"}}>
              <div style={{width:"36px",height:"4px",borderRadius:"2px",background:"var(--muted)",opacity:0.3}} />
            </div>
            {/* Header */}
            <div style={{padding:"8px 20px 16px",textAlign:"center"}}>
              <div style={{fontSize:"28px",marginBottom:"6px"}}>🤲</div>
              <div style={{fontSize:"18px",fontWeight:"800",color:"var(--gold)"}}>Invite Friends to Sirat</div>
              <div style={{fontSize:"12px",color:"var(--muted)",marginTop:"4px",lineHeight:1.5}}>Share the barakah. Generate a personal invite code and send it via WhatsApp.</div>
            </div>
            {/* Content */}
            <div style={{padding:"0 20px 24px",overflowY:"auto",flex:1}}>
              {/* Remaining count */}
              {userInviteRemaining !== null && (
                <div style={{textAlign:"center",marginBottom:"14px"}}>
                  <span style={{fontSize:"11px",fontWeight:"700",color:userInviteRemaining > 0 ? "var(--accent)" : "#f85149"}}>
                    {userInviteRemaining > 0 ? `${userInviteRemaining} invite${userInviteRemaining !== 1 ? "s" : ""} remaining` : "All invites used"}
                  </span>
                </div>
              )}
              {/* Generate button */}
              <button onClick={() => {
                if (!userId || userId === "guest") { alert("Please register first to invite friends."); return; }
                setUserInviteLoading(true);
                fetch(`${BACKEND}/api/user/generate-invite`, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({userId}) })
                  .then(r => r.json())
                  .then(d => {
                    if (d.ok && d.code) { setUserInviteCodes(prev => [d.code, ...prev]); if (d.remaining !== undefined) setUserInviteRemaining(d.remaining); }
                    else if (d.error) { alert(d.error); if (d.remaining !== undefined) setUserInviteRemaining(d.remaining); }
                  })
                  .catch(() => alert("Failed to generate code. Try again."))
                  .finally(() => setUserInviteLoading(false));
              }} disabled={userInviteLoading || !userInviteRemaining} style={{width:"100%",padding:"14px",borderRadius:"14px",border:"none",background:"linear-gradient(135deg,var(--gold),var(--gold2,#ff8f00))",color:darkMode?"#000":"#fff",fontSize:"14px",fontWeight:"800",cursor:"pointer",fontFamily:"inherit",opacity:(userInviteLoading||!userInviteRemaining)?0.5:1,marginBottom:(userInviteCodes.length > 0 || userInviteRemaining === 0) ? "16px" : "0"}}>
                {userInviteLoading ? "Generating..." : !userInviteRemaining ? "All Invites Used" : "Generate Invite Code"}
              </button>
              {userInviteRemaining === 0 && (
                <div style={{textAlign:"center",padding:"12px 16px",background:darkMode?"rgba(255,179,0,0.04)":"rgba(184,112,8,0.04)",border:`1px solid ${darkMode?"rgba(255,179,0,0.12)":"rgba(184,112,8,0.12)"}`,borderRadius:"14px",marginBottom:"16px"}}>
                  <div style={{fontSize:"13px",color:"var(--text)",fontWeight:"600",marginBottom:"6px"}}>You've used all your invite codes</div>
                  <div style={{fontSize:"12px",color:"var(--muted)",lineHeight:1.5,marginBottom:"12px"}}>Need more? Reach out to us and we'll sort you out In Shaa Allah.</div>
                  <div onClick={() => { setShowInviteSheet(false); setTab("settings"); setTimeout(() => { setToolView("feedback"); }, 100); }}
                    style={{display:"inline-flex",alignItems:"center",gap:"6px",padding:"10px 20px",background:"var(--accent)",color:"#fff",borderRadius:"10px",fontSize:"13px",fontWeight:"700",cursor:"pointer"}}>
                    {"\u{1F4AC}"} Contact Support
                  </div>
                </div>
              )}
              {/* Generated codes list */}
              {userInviteCodes.length > 0 && (
                <div style={{display:"flex",flexDirection:"column",gap:"8px"}}>
                  <div style={{fontSize:"10px",color:"var(--muted)",letterSpacing:"1px",textTransform:"uppercase",fontWeight:"700",opacity:0.6,marginBottom:"2px"}}>Latest code (tap to copy)</div>
                  {[userInviteCodes[0]].map((code, i) => {
                    ensureQrDataUrl(code);
                    const isCopied = copiedInviteIdx === i;
                    const deepLink = `${window.location.origin}?code=${code}`;
                    const senderName = userName || "A friend";
                    const waText = encodeURIComponent(`Assalamu Alaykum \u{1F319}\n\nI'd like to personally invite you to *Sirat \u2014 The Straight Path*.\n\nIt's a private Islamic app \u2014 prayer times, live masjid audio, Quran, Qibla & more. No ads, no noise. Just ibadah.\n\nSirat is invite-only and I thought of you. Here is your personal code:\n\n*${code}*\n\n\u{1F449} ${deepLink}\n\nFrom ${senderName} \u{1F932}`);
                    return (
                    <div key={code} style={{display:"flex",flexDirection:"column",gap:"8px"}}>
                      <div onClick={() => {
                        try { if (navigator.clipboard && navigator.clipboard.writeText) { navigator.clipboard.writeText(code); } else { const ta = document.createElement("textarea"); ta.value = code; ta.style.position = "fixed"; ta.style.opacity = "0"; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta); } } catch {}
                        setCopiedInviteIdx(i); setTimeout(() => setCopiedInviteIdx(prev => prev === i ? null : prev), 2000);
                      }} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 14px",background:isCopied?(darkMode?"rgba(0,201,167,0.1)":"rgba(0,158,135,0.08)"):(darkMode?"rgba(255,179,0,0.06)":"rgba(184,112,8,0.06)"),borderRadius:"12px",cursor:"pointer",border:`1px solid ${isCopied?(darkMode?"rgba(0,201,167,0.3)":"rgba(0,158,135,0.3)"):(darkMode?"rgba(255,179,0,0.1)":"rgba(184,112,8,0.1)")}`,transition:"all 0.2s"}}>
                        <span style={{fontSize:"15px",fontWeight:"800",color:isCopied?"var(--accent)":"var(--gold)",fontFamily:"monospace",letterSpacing:"1.5px",transition:"all 0.2s",flex:1}}>{code}</span>
                        <span style={{fontSize:"10px",color:isCopied?"var(--accent)":"var(--muted)",opacity:isCopied?1:0.6,fontWeight:isCopied?"700":"400"}}>{isCopied ? "\u2713 Copied!" : "tap to copy"}</span>
                      </div>
                      {qrDataUrls[code] && (
                      <div style={{textAlign:"center",padding:"14px 0 4px"}}>
                          <img src={qrDataUrls[code]} alt="QR invite" style={{width:200,height:200,borderRadius:"12px",border:"2px solid rgba(255,179,0,0.35)"}} />
                          <div style={{fontSize:"10px",color:"var(--muted)",marginTop:"6px",letterSpacing:"0.5px"}}>Hold up for friend to scan</div>
                        </div>
                      )}
                      <div onClick={() => { window.open(`https://wa.me/?text=${waText}`, '_blank', 'noopener'); }}
                        style={{display:"flex",alignItems:"center",justifyContent:"center",gap:"8px",padding:"11px 14px",background:"rgba(37,211,102,0.12)",border:"1px solid rgba(37,211,102,0.3)",borderRadius:"12px",cursor:"pointer"}}>
                        <span style={{fontSize:"17px"}}>{"\u{1F4AC}"}</span>
                        <span style={{fontSize:"13px",fontWeight:"700",color:"#25D366"}}>Send via WhatsApp</span>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Livemasjid About overlay */}
      {showLegalPage && (
        <div onClick={() => setShowLegalPage(null)} style={{position:"fixed",inset:0,zIndex:999,background:"rgba(0,0,0,0.7)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"16px"}}>
          <div onClick={e => e.stopPropagation()} style={{width:"100%",maxWidth:"480px",height:"85vh",background:"var(--bg)",borderRadius:"16px",overflow:"hidden",display:"flex",flexDirection:"column",border:"1px solid var(--border)"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 16px",borderBottom:"1px solid var(--border)",flexShrink:0}}>
              <button onClick={() => setShowLegalPage(null)} style={{background:"transparent",border:"none",color:"var(--accent)",fontSize:"14px",fontWeight:"700",cursor:"pointer",padding:"0",fontFamily:"inherit"}}>← Back</button>
              <span style={{fontSize:"14px",fontWeight:"700",color:"var(--text)"}}>{showLegalPage === "privacy" ? "Privacy Policy" : "Terms of Service"}</span>
              <div style={{width:"40px"}} />
            </div>
            <iframe src={showLegalPage === "privacy" ? "/privacy-policy.html" : "/terms.html"} title={showLegalPage === "privacy" ? "Privacy Policy" : "Terms of Service"} style={{flex:1,width:"100%",border:"none",background:"#fff"}} />
          </div>
        </div>
      )}
      {/* ── Ahzab 35 "Gem Verse" full-screen overlay ── */}
      {showAhzab35 && (
        <div
          onClick={ahzab35WakeControls}
          onTouchStart={e => { ahzab35SwipeRef.current = {x: e.touches[0].clientX, y: e.touches[0].clientY}; }}
          onTouchEnd={e => {
            const dx = e.changedTouches[0].clientX - ahzab35SwipeRef.current.x;
            const dy = Math.abs(e.changedTouches[0].clientY - ahzab35SwipeRef.current.y);
            if (dx > 80 && dx > dy * 1.2) { stopAhzab35(); setShowAhzab35(false); localStorage.removeItem("ahzab35LastOpen"); setTab("tools"); setToolView("grid"); }
          }}
          style={{position:"fixed",inset:0,zIndex:1200,background:darkMode?"#07090c":"#fdfbf0",overflow:"hidden"}}
        >
          {/* Layer 1 — always visible: breathing circle + Arabic text centred full-screen */}
          <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",pointerEvents:"none"}}>
            <div style={{position:"relative",width:"min(80vw,340px)",height:"min(80vw,340px)",display:"flex",alignItems:"center",justifyContent:"center"}}>
              <div style={{position:"absolute",inset:0,borderRadius:"50%",border:`1.5px solid ${darkMode?"rgba(255,179,0,0.35)":"rgba(184,112,8,0.3)"}`,animation:ahzab35Playing?"breathe 8s ease-in-out infinite":"none",boxShadow:ahzab35Playing?(darkMode?"0 0 60px rgba(255,179,0,0.14)":"0 0 40px rgba(184,112,8,0.1)"):""}}/>
              <div style={{position:"absolute",width:"75%",height:"75%",borderRadius:"50%",border:`1px solid ${darkMode?"rgba(255,179,0,0.15)":"rgba(184,112,8,0.15)"}`,animation:ahzab35Playing?"breathe 8s ease-in-out infinite 1s":"none"}}/>
              <div style={{position:"relative",zIndex:1,fontSize:"clamp(15px,4vw,20px)",fontFamily:"'Scheherazade New','Amiri',serif",color:darkMode?"rgba(255,179,0,0.95)":"rgba(140,85,0,0.95)",textAlign:"center",direction:"rtl",lineHeight:2.1,padding:"0 16%",textShadow:ahzab35Playing?(darkMode?"0 0 20px rgba(255,179,0,0.45)":"0 0 14px rgba(184,112,8,0.3)"):"none",transition:"text-shadow .6s"}}>
                {ahzab35TextRef.current?.arabic || "جارٍ التحميل…"}
              </div>
            </div>
          </div>

          {/* Layer 2 — fades: header, controls (anchored top + bottom so they never overlap the circle) */}
          <div style={{position:"absolute",inset:0,opacity:ahzab35ShowControls?1:0,pointerEvents:ahzab35ShowControls?"auto":"none",transition:"opacity 1.2s ease"}}>

            {/* Top — anchored to top of screen */}
            <div style={{position:"absolute",top:0,left:0,right:0,display:"flex",alignItems:"flex-start",justifyContent:"space-between",padding:"52px 20px 0"}}>
              <div style={{flex:1,textAlign:"center",paddingLeft:"36px"}}>
                <div style={{fontSize:"11px",fontWeight:"700",color:"var(--gold)",letterSpacing:"2px",textTransform:"uppercase"}}>✦  Ayah of Light  ✦</div>
                <div style={{fontSize:"11px",color:"var(--muted)",fontFamily:"'Scheherazade New','Amiri',serif",direction:"rtl",marginTop:"3px"}}>الأحزاب ٣٥</div>
                <div style={{fontSize:"11px",color:"rgba(255,179,0,0.55)",marginTop:"5px",fontStyle:"italic",letterSpacing:"0.4px"}}>for my friend Junaid Esoop</div>
              </div>
              <button onClick={e => { e.stopPropagation(); stopAhzab35(); setShowAhzab35(false); localStorage.removeItem("ahzab35LastOpen"); }}
                style={{background:"transparent",border:"none",fontSize:"22px",color:"var(--muted)",cursor:"pointer",padding:"0",flexShrink:0,lineHeight:1}}>×</button>
            </div>

            {/* Bottom — anchored to bottom of screen, never overlaps circle */}
            <div style={{position:"absolute",bottom:0,left:0,right:0,display:"flex",flexDirection:"column",alignItems:"center",gap:"12px",padding:"0 0 40px"}}>
              {/* Countdown */}
              {ahzab35SecondsLeft !== null && (
                <div style={{fontSize:"12px",fontWeight:"700",color:"var(--gold)",letterSpacing:"1px",opacity:0.75}}>
                  {String(Math.floor(ahzab35SecondsLeft/60)).padStart(2,"0")}:{String(ahzab35SecondsLeft%60).padStart(2,"0")}
                </div>
              )}

              {/* Duration pills */}
              <div style={{display:"flex",gap:"8px"}}>
                {[null,5,10,15,20].map(d => {
                  const active = ahzab35Duration === d;
                  return (
                    <button key={String(d)} onClick={e => { e.stopPropagation(); setAhzab35Duration(d); ahzab35DurationRef.current = d; if (ahzab35Playing) startAhzab35Countdown(d); }}
                      style={{padding:"5px 11px",borderRadius:"16px",border:`1px solid ${active?"var(--gold)":"var(--border)"}`,background:active?(darkMode?"rgba(255,179,0,0.15)":"rgba(184,112,8,0.1)"):"transparent",color:active?"var(--gold)":"var(--muted)",fontSize:"11px",fontWeight:active?"700":"400",cursor:"pointer",fontFamily:"inherit",transition:"all .2s"}}>
                      {d === null ? "∞" : `${d}m`}
                    </button>
                  );
                })}
              </div>

              {/* Reciter pills */}
              <div style={{overflowX:"auto",width:"100%",padding:"0 16px",WebkitOverflowScrolling:"touch"}}>
                <div style={{display:"flex",gap:"8px",width:"max-content",margin:"0 auto"}}>
                  {RECITERS.map(r => {
                    const active = r.id === ahzab35ReciterId;
                    return (
                      <button key={r.id} onClick={e => { e.stopPropagation(); switchAhzab35Reciter(r.id); }}
                        style={{padding:"5px 13px",borderRadius:"20px",border:`1px solid ${active?"var(--gold)":"var(--border)"}`,background:active?(darkMode?"rgba(255,179,0,0.15)":"rgba(184,112,8,0.1)"):"transparent",color:active?"var(--gold)":"var(--muted)",fontSize:"11px",fontWeight:active?"700":"400",cursor:"pointer",whiteSpace:"nowrap",transition:"all .2s",fontFamily:"inherit"}}>
                        {r.short}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Play / pause — SVG icons */}
              <button onClick={e => { e.stopPropagation(); ahzab35Playing ? pauseAhzab35() : playAhzab35(ahzab35ReciterId); }} disabled={ahzab35Loading}
                style={{width:"68px",height:"68px",borderRadius:"50%",background:"linear-gradient(145deg,#ffb300,#e09000)",border:"none",cursor:ahzab35Loading?"not-allowed":"pointer",boxShadow:ahzab35Playing?"0 0 32px rgba(255,179,0,0.55),0 0 14px rgba(255,179,0,0.3)":"0 4px 16px rgba(255,179,0,0.2)",transition:"box-shadow .4s",display:"flex",alignItems:"center",justifyContent:"center",color:"#07090c",opacity:ahzab35Loading?0.6:1}}>
                {ahzab35Playing
                  ? <svg width="18" height="22" viewBox="0 0 18 22" fill="currentColor"><rect x="0" y="0" width="6" height="22" rx="2"/><rect x="12" y="0" width="6" height="22" rx="2"/></svg>
                  : <svg width="18" height="22" viewBox="0 0 18 22" fill="currentColor" style={{marginLeft:"2px"}}><path d="M0 0 L18 11 L0 22 Z"/></svg>
                }
              </button>

              {/* Loop + Share */}
              <div style={{display:"flex",gap:"10px"}}>
                <button onClick={e => { e.stopPropagation(); setAhzab35Loop(l => !l); }}
                  style={{padding:"7px 16px",borderRadius:"20px",border:`1px solid ${ahzab35Loop?"var(--gold)":"var(--border)"}`,background:ahzab35Loop?(darkMode?"rgba(255,179,0,0.15)":"rgba(184,112,8,0.1)"):"transparent",color:ahzab35Loop?"var(--gold)":"var(--muted)",fontSize:"12px",fontWeight:ahzab35Loop?"700":"400",cursor:"pointer",fontFamily:"inherit",transition:"all .2s"}}>
                  {ahzab35Loop ? "Loop ON" : "Loop OFF"}
                </button>
                <button onClick={async e => { e.stopPropagation(); const t=`"${ahzab35TextRef.current?.translation}"\n\n— Surah Al-Ahzab, Ayah 35\n\n${ahzab35TextRef.current?.arabic}`; if(navigator.share){try{await navigator.share({text:t});}catch{}}else{navigator.clipboard?.writeText(t);} }}
                  style={{padding:"7px 16px",borderRadius:"20px",border:"1px solid var(--border)",background:"transparent",color:"var(--muted)",fontSize:"12px",cursor:"pointer",fontFamily:"inherit"}}>
                  Share
                </button>
              </div>
            </div>
          </div>

          {/* Session complete toast */}
          {ahzab35SessionDone && (
            <div style={{position:"absolute",bottom:"100px",left:"50%",transform:"translateX(-50%)",background:darkMode?"rgba(10,8,0,0.95)":"rgba(255,253,230,0.97)",border:"1px solid rgba(255,179,0,0.4)",borderRadius:"16px",padding:"14px 24px",textAlign:"center",zIndex:10,boxShadow:"0 8px 32px rgba(0,0,0,0.4)",animation:"fadeInOverlay 0.4s ease"}}>
              <div style={{fontSize:"20px",marginBottom:"4px"}}>✨</div>
              <div style={{fontSize:"14px",fontWeight:"700",color:"var(--gold)"}}>Session Complete</div>
              <div style={{fontSize:"11px",color:"var(--muted)",marginTop:"3px"}}>جَزَاكَ ٱللَّٰهُ خَيْرًا</div>
            </div>
          )}
        </div>
      )}

      {/* Splash screen — premium launch animation */}
      {showSplash && (
        <div className={`splash-screen${splashFading ? " fading" : ""}`}>
          <div className="splash-glow" />
          <div className="splash-pattern" />
          <div className="splash-features">
            {[
              { text:"Tasbeeh", top:"8%", left:"5%", dur:"5s", delay:"0.5s" },
              { text:"Quran", top:"15%", left:"72%", dur:"6s", delay:"1.2s" },
              { text:"Azkar", top:"28%", left:"12%", dur:"5.5s", delay:"2s" },
              { text:"Qibla", top:"22%", left:"80%", dur:"7s", delay:"0.8s" },
              { text:"Salah", top:"42%", left:"4%", dur:"6s", delay:"1.5s" },
              { text:"Dua", top:"52%", left:"82%", dur:"5s", delay:"3s" },
              { text:"Fasting", top:"60%", left:"8%", dur:"6.5s", delay:"0.3s" },
              { text:"Zakat", top:"68%", left:"78%", dur:"5.5s", delay:"2.5s" },
              { text:"Hajj", top:"75%", left:"15%", dur:"6s", delay:"1.8s" },
              { text:"Khatm", top:"82%", left:"75%", dur:"7s", delay:"0.6s" },
              { text:"99 Names", top:"35%", left:"85%", dur:"5.5s", delay:"3.5s" },
              { text:"Istikharah", top:"88%", left:"10%", dur:"6s", delay:"2.2s" },
              { text:"Muraqabah", top:"48%", left:"76%", dur:"5s", delay:"1s" },
              { text:"Tajweed", top:"90%", left:"70%", dur:"6.5s", delay:"4s" },
            ].map((f, i) => (
              <div key={i} className="splash-feat" style={{top:f.top, left:f.left, "--dur":f.dur, "--delay":f.delay}}>{f.text}</div>
            ))}
          </div>
          <div className="splash-content">
            <svg className="splash-logo" viewBox="0 0 44 44" width="100" height="100" fill="none" xmlns="http://www.w3.org/2000/svg">
              <ellipse className="splash-e1" cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.3" opacity="0.9"/>
              <ellipse className="splash-e2" cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.3" opacity="0.9" transform="rotate(60 22 22)"/>
              <ellipse className="splash-e3" cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.3" opacity="0.9" transform="rotate(120 22 22)"/>
            </svg>
            <div className="splash-title">Sirat</div>
            <div className="splash-tagline">THE STRAIGHT PATH</div>
            <div className="splash-line" />
            {userName && <div className="splash-greeting">Assalamu Alaykum, {userName}</div>}
            <button className="splash-skip" onClick={() => { setSplashFading(true); setTimeout(() => { setShowSplash(false); setSplashFading(false); sessionStorage.setItem("siratSplashShown","1"); }, 800); }}>Skip Intro</button>
          </div>
        </div>
      )}

      {/* Tips overlay */}
      {/* Onboarding ceremony — name reveal for new registrations */}
      {showOnboardingCeremony && (
        <div className={`ceremony-overlay${ceremonyFading ? " fading" : ""}`}>
          <div className="ceremony-glow" />
          <div className="ceremony-pattern" />
          <div className="ceremony-content">
            <div className="ceremony-name">{ceremonyNameRef.current}</div>
            <div className="ceremony-arabic">أهلاً وسهلاً يا {ceremonyNameRef.current}</div>
          </div>
        </div>
      )}

      {/* Dhikr After Salah overlay */}
      {showDhikr && (() => {
        const DHIKR_STEPS = [
          { arabic: "سُبْحَانَ ٱللَّٰهِ", translit: "SubhanAllah", english: "Glory be to Allah", target: 33, color: "var(--accent)" },
          { arabic: "ٱلْحَمْدُ لِلَّٰهِ", translit: "Alhamdulillah", english: "All praise is due to Allah", target: 33, color: "var(--gold)" },
          { arabic: "ٱللَّٰهُ أَكْبَرُ", translit: "Allahu Akbar", english: "Allah is the Greatest", target: 34, color: "#a06aff" },
        ];
        const isAyatulKursi = dhikrStep >= 3;
        const isDone = dhikrStep >= 4;
        const step = DHIKR_STEPS[dhikrStep] || DHIKR_STEPS[2];
        const progress = isAyatulKursi ? 1 : dhikrCount / step.target;
        const handleCount = () => {
          if (isAyatulKursi || isDone) return;
          // Haptic feedback
          if (navigator.vibrate) navigator.vibrate(15);
          const next = dhikrCount + 1;
          setDhikrCount(next);
          if (next >= step.target) {
            if (navigator.vibrate) navigator.vibrate([30, 50, 30]);
            setTimeout(() => { setDhikrStep(dhikrStep + 1); setDhikrCount(0); }, 600);
          }
        };
        // Next prayer for encouragement
        const prayerOrder = ["Fajr", "Dhuhr", "Asr", "Maghrib", "Isha"];
        const pIdx = prayerOrder.indexOf(dhikrPrayer);
        const nextPrayer = pIdx >= 0 && pIdx < 4 ? prayerOrder[pIdx + 1] : prayerOrder[0];
        return (
          <div style={{position:"fixed",inset:0,zIndex:1060,background:"rgba(7,9,12,0.97)",backdropFilter:"blur(20px)",WebkitBackdropFilter:"blur(20px)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"16px",animation:"fadeInOverlay 0.3s ease",userSelect:"none",WebkitUserSelect:"none",WebkitTapHighlightColor:"transparent",touchAction:"manipulation"}}
            onClick={!isAyatulKursi && !isDone ? handleCount : undefined}>
            {/* Close button */}
            <button onClick={(e) => { e.stopPropagation(); setShowDhikr(false); }} style={{position:"absolute",top:"max(16px,env(safe-area-inset-top))",right:"16px",background:"rgba(255,255,255,0.08)",border:"none",borderRadius:"50%",width:"44px",height:"44px",display:"flex",alignItems:"center",justifyContent:"center",color:"var(--muted)",fontSize:"20px",cursor:"pointer",fontFamily:"inherit",zIndex:2}}>✕</button>

            {isDone ? (
              /* ── Completion celebration ── */
              <div onClick={(e) => e.stopPropagation()} style={{textAlign:"center",animation:"fadeInOverlay 0.4s ease"}}>
                <div style={{fontSize:"64px",marginBottom:"16px",animation:"prayedGlow 2s ease infinite"}}>✨</div>
                <div style={{fontSize:"28px",fontFamily:"'Scheherazade New','Amiri',serif",color:"var(--gold)",marginBottom:"8px",direction:"rtl"}}>ما شاء الله</div>
                <div style={{fontSize:"18px",color:"var(--text)",fontWeight:"700",marginBottom:"6px"}}>Dhikr Complete!</div>
                <div style={{fontSize:"13px",color:"var(--muted)",marginBottom:"32px",lineHeight:1.6,maxWidth:"280px"}}>
                  You've completed your dhikr after {dhikrPrayer}.<br/>
                  May Allah accept it from you.
                </div>
                <div style={{background:"linear-gradient(135deg,rgba(255,179,0,0.1),rgba(255,179,0,0.05))",border:"1px solid rgba(255,179,0,0.2)",borderRadius:"16px",padding:"16px 24px",marginBottom:"28px",maxWidth:"280px"}}>
                  <div style={{fontSize:"12px",color:"var(--gold)",fontWeight:"700",letterSpacing:"0.5px",marginBottom:"4px"}}>UP NEXT</div>
                  <div style={{fontSize:"15px",color:"var(--text)",fontWeight:"600"}}>Continue this after {nextPrayer} too 🤲</div>
                </div>
                <button onClick={() => setShowDhikr(false)} style={{width:"100%",maxWidth:"280px",padding:"16px",background:"linear-gradient(135deg,var(--gold),rgba(255,179,0,0.8))",border:"none",borderRadius:"14px",color:"#07090c",fontSize:"16px",fontWeight:"800",cursor:"pointer",fontFamily:"inherit"}}>
                  Done
                </button>
              </div>
            ) : isAyatulKursi ? (
              <div onClick={(e) => e.stopPropagation()} style={{width:"100%",maxWidth:"380px",textAlign:"center"}}>
                <div style={{fontSize:"11px",color:"var(--muted)",letterSpacing:"1.5px",fontWeight:"600",textTransform:"uppercase",marginBottom:"8px"}}>Dhikr After {dhikrPrayer}</div>
                <div style={{display:"flex",justifyContent:"center",gap:"6px",marginBottom:"24px"}}>
                  {[0,1,2,3].map(i => (
                    <div key={i} style={{width:i===dhikrStep?"20px":"6px",height:"6px",borderRadius:"3px",background:i===dhikrStep?"var(--gold)":i<dhikrStep?"rgba(0,201,167,0.4)":"rgba(255,255,255,0.1)",transition:"all 0.3s"}}/>
                  ))}
                </div>
                <div style={{fontSize:"15px",color:"var(--gold)",fontWeight:"700",marginBottom:"16px"}}>Ayatul Kursi</div>
                <div style={{fontSize:"26px",fontFamily:"'Scheherazade New','Amiri',serif",color:"var(--text)",lineHeight:1.9,direction:"rtl",maxHeight:"45vh",overflowY:"auto",padding:"0 12px",marginBottom:"24px"}}>
                  ٱللَّٰهُ لَآ إِلَٰهَ إِلَّا هُوَ ٱلْحَىُّ ٱلْقَيُّومُ ۚ لَا تَأْخُذُهُۥ سِنَةٌ وَلَا نَوْمٌ ۚ لَّهُۥ مَا فِى ٱلسَّمَٰوَٰتِ وَمَا فِى ٱلْأَرْضِ ۗ مَن ذَا ٱلَّذِى يَشْفَعُ عِندَهُۥٓ إِلَّا بِإِذْنِهِۦ ۚ يَعْلَمُ مَا بَيْنَ أَيْدِيهِمْ وَمَا خَلْفَهُمْ ۖ وَلَا يُحِيطُونَ بِشَىْءٍ مِّنْ عِلْمِهِۦٓ إِلَّا بِمَا شَآءَ ۚ وَسِعَ كُرْسِيُّهُ ٱلسَّمَٰوَٰتِ وَٱلْأَرْضَ ۖ وَلَا يَـُٔودُهُۥ حِفْظُهُمَا ۚ وَهُوَ ٱلْعَلِىُّ ٱلْعَظِيمُ
                </div>
                <button onClick={() => { setDhikrStep(4); if (navigator.vibrate) navigator.vibrate([30, 50, 30, 50, 30]); }} style={{width:"100%",maxWidth:"320px",padding:"16px",background:"linear-gradient(135deg,var(--gold),rgba(255,179,0,0.8))",border:"none",borderRadius:"14px",color:"#07090c",fontSize:"16px",fontWeight:"800",cursor:"pointer",fontFamily:"inherit"}}>
                  I've Read It
                </button>
              </div>
            ) : (
              <>
                {/* Header */}
                <div style={{fontSize:"14px",color:"var(--muted)",letterSpacing:"2px",fontWeight:"700",textTransform:"uppercase",marginBottom:"6px"}}>Dhikr After {dhikrPrayer}</div>
                <div style={{display:"flex",justifyContent:"center",gap:"10px",marginBottom:"12px"}}>
                  {[0,1,2,3].map(i => (
                    <div key={i} style={{width:i===dhikrStep?"28px":"10px",height:"10px",borderRadius:"5px",background:i===dhikrStep?step.color:i<dhikrStep?"rgba(0,201,167,0.4)":"rgba(255,255,255,0.1)",transition:"all 0.3s"}}/>
                  ))}
                </div>

                {/* Arabic + transliteration */}
                <div style={{fontSize:"64px",fontFamily:"'Scheherazade New','Amiri',serif",color:step.color,lineHeight:1.3,marginBottom:"6px",direction:"rtl",transition:"color 0.3s"}}>{step.arabic}</div>
                <div style={{fontSize:"24px",color:"var(--text)",fontWeight:"700",marginBottom:"2px"}}>{step.translit}</div>
                <div style={{fontSize:"16px",color:"var(--muted)",marginBottom:"12px"}}>{step.english}</div>

                {/* Progress ring with count INSIDE */}
                <div style={{width:"min(320px, 75vw)",height:"min(320px, 75vw)",borderRadius:"50%",margin:"0 auto",position:"relative",background:`conic-gradient(${step.color} ${progress*360}deg, rgba(255,255,255,0.06) ${progress*360}deg)`,display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.12s",boxShadow:dhikrCount > 0 ? `0 0 ${Math.min(dhikrCount, 28)}px ${step.color}40` : "none"}}>
                  <div style={{width:"calc(min(320px, 75vw) - 32px)",height:"calc(min(320px, 75vw) - 32px)",borderRadius:"50%",background:darkMode?"#0a0d12":"#f0f5fa",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
                    <span style={{fontSize:"84px",fontWeight:"800",color:step.color,lineHeight:1,transition:"transform 0.1s"}} key={dhikrCount}>{dhikrCount}</span>
                    <span style={{fontSize:"22px",color:"var(--muted)",marginTop:"4px"}}>/ {step.target}</span>
                  </div>
                </div>

                <div style={{fontSize:"15px",color:"var(--muted)",marginTop:"10px",marginBottom:"10px",opacity:0.6}}>Tap anywhere to count</div>

                {/* Skip */}
                <button onClick={(e) => { e.stopPropagation(); setDhikrStep(dhikrStep + 1); setDhikrCount(0); }} style={{background:"transparent",border:"1px solid var(--border)",borderRadius:"14px",padding:"14px 40px",color:"var(--muted)",fontSize:"16px",fontWeight:"600",cursor:"pointer",fontFamily:"inherit"}}>
                  Skip to next
                </button>
              </>
            )}
          </div>
        );
      })()}

      {/* Permission onboarding flow */}
      {showPermFlow && (
        <PermissionFlow
          step={permFlowStep}
          steps={permSteps}
          onAllow={handlePermAllow}
          onSkip={handlePermSkip}
          darkMode={darkMode}
          confirmMsg={permConfirmMsg}
        />
      )}

      {/* What's New overlay */}
      {showWhatsNew && !showOnboardingTips && whatsNewData && (
        <div className="tips-overlay">
          <div style={{maxWidth:"380px",width:"100%",maxHeight:"80vh",overflowY:"auto",background:darkMode?"linear-gradient(160deg,#0c1210 0%,#07090c 60%,#0a0d12 100%)":"linear-gradient(160deg,#f8faf9 0%,#ffffff 60%,#f0f5f2 100%)",border:darkMode?"1px solid rgba(255,179,0,0.2)":"1px solid rgba(184,112,8,0.2)",borderRadius:"24px",padding:"32px 24px 24px",boxShadow:darkMode?"0 0 80px rgba(255,179,0,0.08), 0 0 40px rgba(0,201,167,0.06)":"0 0 60px rgba(184,112,8,0.1)"}}>
            <div style={{textAlign:"center",marginBottom:"20px"}}>
              <div style={{fontSize:"32px",marginBottom:"8px"}}>✨</div>
              <div style={{fontSize:"18px",fontWeight:"800",color:"var(--gold)",letterSpacing:"-0.3px"}}>{whatsNewData.title}</div>
              {whatsNewData.subtitle && <div style={{fontSize:"12px",color:"var(--muted)",marginTop:"4px",lineHeight:1.5}}>{whatsNewData.subtitle}</div>}
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:"10px"}}>
              {whatsNewData.items.map((item, i) => (
                <div key={i} style={{display:"flex",alignItems:"flex-start",gap:"12px",padding:"10px 12px",background:darkMode?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.02)",borderRadius:"12px",border:darkMode?"1px solid rgba(255,255,255,0.04)":"1px solid rgba(0,0,0,0.04)"}}>
                  <span style={{fontSize:"20px",flexShrink:0}}>{item.icon}</span>
                  <div style={{fontSize:"13px",color:"var(--text)",lineHeight:1.6}}>{item.text}</div>
                </div>
              ))}
            </div>
            <button onClick={() => { setShowWhatsNew(false); localStorage.setItem("whatsNewSeen", whatsNewData.id); }} style={{width:"100%",marginTop:"22px",padding:"14px",background:"linear-gradient(135deg,var(--gold),rgba(255,179,0,0.8))",border:"none",borderRadius:"14px",color:"#07090c",fontSize:"14px",fontWeight:"800",cursor:"pointer",fontFamily:"inherit"}}>Got it!</button>
          </div>
        </div>
      )}

      {siriDebugOpen && (
        <div className="tips-overlay" onClick={() => setSiriDebugOpen(false)} style={{padding:"24px 16px"}}>
          <div onClick={e => e.stopPropagation()} style={{maxWidth:"500px",width:"100%",maxHeight:"85vh",overflowY:"auto",background:darkMode?"#07090c":"#ffffff",border:darkMode?"1px solid rgba(0,201,167,0.3)":"1px solid rgba(0,158,135,0.3)",borderRadius:"16px",padding:"20px",fontFamily:"ui-monospace,SFMono-Regular,Menlo,monospace",fontSize:"11px",color:darkMode?"#e8f1ee":"#0a1512",lineHeight:1.5}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"16px"}}>
              <div style={{fontSize:"14px",fontWeight:"700",color:"var(--gold)",letterSpacing:"1px"}}>Siri / iCloud Debug</div>
              <button onClick={() => setSiriDebugOpen(false)} style={{padding:"6px 12px",background:"transparent",border:`1px solid ${darkMode?"rgba(255,179,0,0.4)":"rgba(184,112,8,0.4)"}`,borderRadius:"8px",color:"var(--gold)",fontSize:"12px",cursor:"pointer",fontFamily:"inherit"}}>Close</button>
            </div>
            {siriDebugData && (() => {
              const { siri, icloud, live, ts } = siriDebugData;
              const ok = (b) => b ? "✓" : "✗";
              const Row = ({ k, v }) => (
                <div style={{display:"flex",gap:"8px",padding:"4px 0",borderBottom:`1px solid ${darkMode?"rgba(255,255,255,0.05)":"rgba(0,0,0,0.05)"}`}}>
                  <span style={{color:"var(--muted)",minWidth:"140px",flexShrink:0}}>{k}</span>
                  <span style={{wordBreak:"break-all",flex:1}}>{typeof v === "object" ? JSON.stringify(v, null, 2) : String(v)}</span>
                </div>
              );
              return (
                <div>
                  <div style={{fontSize:"9px",color:"var(--muted)",marginBottom:"12px"}}>Snapshot at {ts}</div>

                  <div style={{fontSize:"12px",fontWeight:"700",color:"var(--accent)",margin:"8px 0",letterSpacing:"0.5px",textTransform:"uppercase"}}>SiriIntents Plugin</div>
                  <Row k="native" v={ok(siri.native)} />
                  <Row k="plugin present" v={ok(siri.pluginPresent)} />
                  <Row k="all plugins" v={(siri.pluginNames || []).join(", ") || "(none)"} />
                  {siri.error && <Row k="error" v={siri.error} />}
                  {siri.snapshot && (
                    <>
                      <Row k="appGroupId" v={siri.snapshot.appGroupId} />
                      <Row k="appGroupReachable" v={ok(siri.snapshot.appGroupReachable)} />
                      <Row k="prayerTimes" v={siri.snapshot.prayerTimes ?? "null"} />
                      <Row k="prayerTimesDate" v={siri.snapshot.prayerTimesDate ?? "null"} />
                      <Row k="userName" v={siri.snapshot.userName ?? "null"} />
                      <Row k="lat" v={siri.snapshot.lat ?? 0} />
                      <Row k="lng" v={siri.snapshot.lng ?? 0} />
                      <Row k="pendingAction" v={siri.snapshot.pendingAction ?? "null"} />
                    </>
                  )}

                  <div style={{fontSize:"12px",fontWeight:"700",color:"var(--accent)",margin:"16px 0 8px",letterSpacing:"0.5px",textTransform:"uppercase"}}>iCloud Plugin</div>
                  <Row k="native" v={ok(icloud.native)} />
                  <Row k="plugin present" v={ok(icloud.pluginPresent)} />
                  <Row k="iCloud available" v={ok(icloud.available)} />
                  {icloud.error && <Row k="error" v={icloud.error} />}
                  {icloud.values && (
                    <Row k="kv keys" v={Object.keys(icloud.values).length === 0 ? "(empty)" : Object.keys(icloud.values).join(", ")} />
                  )}
                  {icloud.values && (() => {
                    const HAYAH_KEYS = ["hayahPrefs","ghidhaProfile","calorieGoals","caloriesFavorites","calorieWeightLog","calorieFrequency","calorieLog","sirat-workouts","ghidhaMigrationV"];
                    const rows = HAYAH_KEYS.map(k => {
                      const v = icloud.values[k];
                      const kb = v ? (new TextEncoder().encode(v).length / 1024).toFixed(1) : null;
                      return { k, present: !!v, kb };
                    });
                    const totalKb = rows.reduce((s,r) => s + (r.kb ? parseFloat(r.kb) : 0), 0);
                    return (
                      <>
                        <div style={{fontSize:"12px",fontWeight:"700",color:"var(--gold)",margin:"14px 0 6px",letterSpacing:"0.5px",textTransform:"uppercase"}}>Hayah Data in iCloud</div>
                        {rows.map(r => (
                          <div key={r.k} style={{display:"flex",gap:"8px",padding:"3px 0",borderBottom:`1px solid ${darkMode?"rgba(255,255,255,0.05)":"rgba(0,0,0,0.05)"}`}}>
                            <span style={{color:r.present?"var(--accent)":"var(--danger)",minWidth:"18px"}}>{r.present ? "✓" : "✗"}</span>
                            <span style={{color:"var(--muted)",flex:1}}>{r.k}</span>
                            <span style={{color:r.present?"var(--text)":"var(--muted)"}}>{r.present ? `${r.kb} KB` : "—"}</span>
                          </div>
                        ))}
                        <div style={{display:"flex",justifyContent:"space-between",padding:"6px 0 0",fontSize:"11px",fontWeight:"700",color:"var(--gold)"}}>
                          <span>Total Hayah</span><span>{totalKb.toFixed(1)} KB / ~1024 KB limit</span>
                        </div>
                      </>
                    );
                  })()}

                  {live && (
                    <>
                      <div style={{fontSize:"12px",fontWeight:"700",color:"var(--accent)",margin:"16px 0 8px",letterSpacing:"0.5px",textTransform:"uppercase"}}>Live Activities</div>
                      <Row k="native" v={ok(live.native)} />
                      <Row k="plugin present" v={ok(live.pluginPresent)} />
                      <Row k="areActivitiesEnabled" v={ok(live.areActivitiesEnabled)} />
                      {"frequentPushesEnabled" in live && <Row k="frequentPushesEnabled" v={ok(live.frequentPushesEnabled)} />}
                      <Row k="active count" v={live.activeCount} />
                      {live.active && live.active.length > 0 && (
                        <Row k="active kinds" v={live.active.map(a => a.kind).join(", ")} />
                      )}
                      <Row k="last start attempt" v={live.lastStartAttempt || "(none)"} />
                      <Row k="last error" v={live.lastError || "(none)"} />
                      {live.bridgeError && <Row k="bridge error" v={live.bridgeError} />}
                    </>
                  )}

                  <div style={{display:"flex",gap:"8px",marginTop:"16px"}}>
                    <button onClick={async () => {
                      // Force-write current React prayerTimes + userName to App Group
                      if (prayerTimes) {
                        const now = new Date();
                        const dd = String(now.getDate()).padStart(2,"0");
                        const mm = String(now.getMonth()+1).padStart(2,"0");
                        const yyyy = now.getFullYear();
                        await siriWritePrayerTimes({ date: `${yyyy}-${mm}-${dd}`, timings: prayerTimes, lat: userLat || 0, lng: userLng || 0 });
                      }
                      if (userName) await siriWriteUserName(userName);
                      const siri2 = await siriGetDebugSnapshot();
                      const icloud2 = await getICloudDebugSnapshot();
                      setSiriDebugData({ siri: siri2, icloud: icloud2, ts: new Date().toLocaleString() });
                    }} style={{flex:1,padding:"10px",background:"var(--accent)",color:"#000",border:"none",borderRadius:"8px",fontSize:"12px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit"}}>Force-write & re-read</button>
                    <button onClick={async () => {
                      const siri2 = await siriGetDebugSnapshot();
                      const icloud2 = await getICloudDebugSnapshot();
                      setSiriDebugData({ siri: siri2, icloud: icloud2, ts: new Date().toLocaleString() });
                    }} style={{flex:1,padding:"10px",background:"transparent",color:"var(--gold)",border:"1px solid var(--gold)",borderRadius:"8px",fontSize:"12px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit"}}>Refresh</button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {showOnboardingTips && userId && (
        <div className="tips-overlay" onClick={() => { setShowOnboardingTips(false); localStorage.setItem("onboardingDone","1"); }}>
          <div onClick={e => e.stopPropagation()} style={{maxWidth:"360px",width:"100%",background:darkMode?"linear-gradient(160deg,#0a1512 0%,#07090c 60%,#0a0d12 100%)":"linear-gradient(160deg,#f0faf8 0%,#ffffff 60%,#e8f5f2 100%)",border:darkMode?"1px solid rgba(0,201,167,0.2)":"1px solid rgba(0,158,135,0.2)",borderRadius:"24px",padding:"32px 24px 24px",boxShadow:darkMode?"0 0 80px rgba(0,201,167,0.08), 0 0 40px rgba(255,179,0,0.06)":"0 0 60px rgba(0,158,135,0.1)"}}>
            <div style={{fontSize:"11px",fontWeight:"700",color:"var(--accent)",letterSpacing:"1.5px",textTransform:"uppercase",marginBottom:"18px",textAlign:"center"}}>Quick Tips</div>
            <div style={{display:"flex",flexDirection:"column",gap:"12px"}}>
              <div style={{display:"flex",alignItems:"flex-start",gap:"12px"}}>
                <span style={{fontSize:"20px",flexShrink:0}}>🕌</span>
                <div><div style={{fontSize:"13px",fontWeight:"700",color:"var(--text)"}}>Add a Masjid</div><div style={{fontSize:"11px",color:"var(--muted)",lineHeight:1.5,marginTop:"2px"}}>Tap <strong style={{color:"var(--accent)"}}>Add</strong> to save a masjid, then tap 🔔 to enable Azaan alerts</div></div>
              </div>
              <div style={{display:"flex",alignItems:"flex-start",gap:"12px"}}>
                <span style={{fontSize:"20px",flexShrink:0}}>🔔</span>
                <div><div style={{fontSize:"13px",fontWeight:"700",color:"var(--text)"}}>Enable Notifications</div><div style={{fontSize:"11px",color:"var(--muted)",lineHeight:1.5,marginTop:"2px"}}>Go to <strong style={{color:"var(--accent)"}}>Settings → Azaan Notifications</strong> to receive prayer alerts on your lock screen</div></div>
              </div>
              <div style={{display:"flex",alignItems:"flex-start",gap:"12px"}}>
                <span style={{fontSize:"20px",flexShrink:0}}>🤲</span>
                <div><div style={{fontSize:"13px",fontWeight:"700",color:"var(--text)"}}>Ibadah Tab</div><div style={{fontSize:"11px",color:"var(--muted)",lineHeight:1.5,marginTop:"2px"}}>Quran audio, Surah player, Duas & more — tap <strong style={{color:"var(--accent)"}}>🤲 Ibadah</strong> below</div></div>
              </div>
              <div style={{display:"flex",alignItems:"flex-start",gap:"12px"}}>
                <span style={{fontSize:"20px",flexShrink:0}}>✨</span>
                <div><div style={{fontSize:"13px",fontWeight:"700",color:"var(--text)"}}>Daily Verse</div><div style={{fontSize:"11px",color:"var(--muted)",lineHeight:1.5,marginTop:"2px"}}>Tap the <strong style={{color:"var(--accent)"}}>Sirat logo</strong> (top left) for a daily Quran verse</div></div>
              </div>
              <div style={{display:"flex",alignItems:"flex-start",gap:"12px"}}>
                <span style={{fontSize:"20px",flexShrink:0}}>🕐</span>
                <div><div style={{fontSize:"13px",fontWeight:"700",color:"var(--text)"}}>Salah Tracker</div><div style={{fontSize:"11px",color:"var(--muted)",lineHeight:1.5,marginTop:"2px"}}>Tap any <strong style={{color:"var(--accent)"}}>prayer time</strong> to mark it as prayed — track your streak & weekly progress</div></div>
              </div>
            </div>
            <button onClick={() => { setShowOnboardingTips(false); localStorage.setItem("onboardingDone","1"); }} style={{width:"100%",marginTop:"20px",padding:"14px",background:"var(--accent)",border:"none",borderRadius:"14px",color:"#07090c",fontSize:"14px",fontWeight:"800",cursor:"pointer",fontFamily:"inherit"}}>Got it!</button>
          </div>
        </div>
      )}

      {/* Payment success toast */}
      {showPaymentSuccess && (
        <>
          <div className="payment-toast-overlay" />
          <div className="payment-toast" style={{animation:"toastIn 0.5s cubic-bezier(0.22,1,0.36,1), toastGlow 3s ease-in-out 0.5s infinite"}}>
            {/* Background decorative orbs */}
            <div style={{position:"absolute",inset:0,overflow:"hidden",borderRadius:"28px",pointerEvents:"none"}}>
              <div style={{position:"absolute",top:"-30px",right:"-30px",width:"140px",height:"140px",borderRadius:"50%",background:"radial-gradient(circle,rgba(255,179,0,0.13) 0%,transparent 65%)",animation:"proOrbA 4s ease-in-out infinite"}}/>
              <div style={{position:"absolute",bottom:"-30px",left:"-30px",width:"120px",height:"120px",borderRadius:"50%",background:"radial-gradient(circle,rgba(0,201,167,0.1) 0%,transparent 65%)",animation:"proOrbB 5s ease-in-out infinite"}}/>
              <div style={{position:"absolute",top:"50%",left:"-20px",width:"80px",height:"80px",borderRadius:"50%",background:"radial-gradient(circle,rgba(255,179,0,0.07) 0%,transparent 70%)",animation:"proOrbA 6s ease-in-out infinite 1s"}}/>
            </div>
            {/* Floating sparkles */}
            {[{x:"15%",d:"0s"},{x:"75%",d:"0.5s"},{x:"45%",d:"1s"},{x:"85%",d:"1.5s"},{x:"30%",d:"2s"}].map((s,i) => (
              <span key={i} style={{position:"absolute",bottom:"60px",left:s.x,fontSize:"13px",animation:"sparkleFloat 2.5s ease-out infinite",animationDelay:s.d,pointerEvents:"none",zIndex:0}}>{i%2===0?"✨":"🌟"}</span>
            ))}
            {/* Main icon */}
            <div style={{fontSize:"60px",animation:"proIconFloat 2.5s ease-in-out infinite",position:"relative",zIndex:1,marginBottom:"-4px"}}>
              {proGrantedByAdmin ? "🌟" : "🌙"}
            </div>
            {/* Arabic blessing */}
            <div style={{fontSize:"15px",color:"rgba(255,179,0,0.8)",fontFamily:"Georgia,serif",direction:"rtl",letterSpacing:"1px",lineHeight:1.6,position:"relative",zIndex:1}}>
              {proGrantedByAdmin ? "بارك الله فيك" : "جَزَاكَ اللَّهُ خَيْرًا"}
            </div>
            {/* Title */}
            <div style={{fontSize:"20px",fontWeight:"800",color:"var(--text)",letterSpacing:"-0.4px",lineHeight:1.2,position:"relative",zIndex:1}}>
              {proGrantedByAdmin ? "You're a Sirat Supporter!" : "JazakAllah khayran!"}
            </div>
            {/* Pro status badge */}
            <div style={{width:"100%",padding:"12px 16px",background:"linear-gradient(135deg,rgba(0,201,167,0.1),rgba(0,201,167,0.06))",border:"1px solid rgba(0,201,167,0.3)",borderRadius:"16px",position:"relative",zIndex:1}}>
              <div style={{fontSize:"13px",fontWeight:"800",color:"var(--accent)",marginBottom:"3px"}}>✓ Sirat Pro — Active</div>
              <div style={{fontSize:"11px",color:"var(--muted)",lineHeight:1.5}}>
                {proGrantedByAdmin ? "You've been recognised as a valued supporter" : "All features unlocked · May Allah accept from us"}
              </div>
            </div>
            {/* Continue button */}
            <button
              onClick={() => setShowPaymentSuccess(false)}
              style={{marginTop:"4px",background:"linear-gradient(135deg,rgba(255,179,0,0.15),rgba(255,179,0,0.08))",border:"1px solid rgba(255,179,0,0.35)",borderRadius:"14px",color:"var(--gold)",fontSize:"14px",fontWeight:"700",padding:"11px 36px",cursor:"pointer",fontFamily:"inherit",letterSpacing:"0.3px",position:"relative",zIndex:1}}
            >
              Continue
            </button>
          </div>
        </>
      )}

      {/* Support thank-you toast */}
      {showSupportToast && (() => {
        const sup = remoteConfig.support || {};
        const thankYou = sup.thankYou || "JazakAllah khayran. May Allah accept it from you.";
        return (
          <>
            <div className="payment-toast-overlay" />
            <div className="payment-toast" style={{animation:"toastIn 0.5s cubic-bezier(0.22,1,0.36,1), toastGlow 3s ease-in-out 0.5s infinite"}}>
              <div style={{position:"absolute",inset:0,overflow:"hidden",borderRadius:"28px",pointerEvents:"none"}}>
                <div style={{position:"absolute",top:"-30px",right:"-30px",width:"140px",height:"140px",borderRadius:"50%",background:"radial-gradient(circle,rgba(255,179,0,0.13) 0%,transparent 65%)",animation:"proOrbA 4s ease-in-out infinite"}}/>
                <div style={{position:"absolute",bottom:"-30px",left:"-30px",width:"120px",height:"120px",borderRadius:"50%",background:"radial-gradient(circle,rgba(0,201,167,0.1) 0%,transparent 65%)",animation:"proOrbB 5s ease-in-out infinite"}}/>
              </div>
              {[{x:"15%",d:"0s"},{x:"75%",d:"0.5s"},{x:"45%",d:"1s"},{x:"85%",d:"1.5s"},{x:"30%",d:"2s"}].map((s,i) => (
                <span key={i} style={{position:"absolute",bottom:"60px",left:s.x,fontSize:"13px",animation:"sparkleFloat 2.5s ease-out infinite",animationDelay:s.d,pointerEvents:"none",zIndex:0}}>{i%2===0?"✨":"🌟"}</span>
              ))}
              <div style={{fontSize:"60px",animation:"proIconFloat 2.5s ease-in-out infinite",position:"relative",zIndex:1,marginBottom:"-4px"}}>🌙</div>
              <div style={{fontSize:"15px",color:"rgba(255,179,0,0.8)",fontFamily:"'Scheherazade New','Amiri',serif",direction:"rtl",letterSpacing:"1px",lineHeight:1.6,position:"relative",zIndex:1}}>بارك الله فيك</div>
              <div style={{fontSize:"13px",color:"rgba(255,179,0,0.6)",fontFamily:"'Scheherazade New','Amiri',serif",direction:"rtl",letterSpacing:"1px",lineHeight:1.4,position:"relative",zIndex:1,marginTop:"-2px"}}>جَزَاكَ اللهُ خَيْراً</div>
              <div style={{fontSize:"20px",fontWeight:"800",color:"var(--text)",letterSpacing:"-0.4px",lineHeight:1.2,position:"relative",zIndex:1}}>
                {supportToastUpgraded && supportToastTier?.name ? `You're now a ${supportToastTier.name} Supporter!` : "JazakAllah khayran!"}
              </div>
              {supportToastTier?.name && (
                <div style={{width:"100%",padding:"12px 16px",background:`linear-gradient(135deg,${supportToastTier.color}15,${supportToastTier.color}08)`,border:`1px solid ${supportToastTier.color}40`,borderRadius:"16px",position:"relative",zIndex:1}}>
                  <div style={{fontSize:"13px",fontWeight:"800",color:supportToastTier.color,marginBottom:"3px"}}>{supportToastTier.icon} {supportToastTier.name} Supporter</div>
                  <div style={{fontSize:"11px",color:"var(--muted)",lineHeight:1.5}}>{thankYou}</div>
                </div>
              )}
              {!supportToastTier?.name && (
                <div style={{fontSize:"13px",color:"var(--muted)",lineHeight:1.6,position:"relative",zIndex:1,textAlign:"center",maxWidth:"260px"}}>{thankYou}</div>
              )}
              <button onClick={() => setShowSupportToast(false)}
                style={{marginTop:"4px",background:"linear-gradient(135deg,rgba(255,179,0,0.15),rgba(255,179,0,0.08))",border:"1px solid rgba(255,179,0,0.35)",borderRadius:"14px",color:"var(--gold)",fontSize:"14px",fontWeight:"700",padding:"11px 36px",cursor:"pointer",fontFamily:"inherit",letterSpacing:"0.3px",position:"relative",zIndex:1}}>
                Continue
              </button>
            </div>
          </>
        );
      })()}

      {/* Radial fan quick actions menu — expands from logo */}
      {showFanMenu && (() => {
        const cx = window.innerWidth / 2;
        const cy = window.innerHeight / 2;
        const radius = 118;

        // ── Build pages: pinned first (if configured), then usage-sorted rest ──
        // NATIVE (TestFlight) builds use the Hayah-only option set; PWA keeps
        // the full set + pinned behaviour exactly as before.
        const hayahOnlyFan = !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
        const fanOptionSet = hayahOnlyFan ? HAYAH_FAN_OPTIONS : FAN_ALL_OPTIONS;
        const usage = (() => { try { return JSON.parse(localStorage.getItem("fanUsageCounts")||"{}"); } catch { return {}; } })();
        const usageSorted = [...fanOptionSet].sort((a, b) => (usage[b.id]||0) - (usage[a.id]||0));
        let fanPages;
        if (!hayahOnlyFan && fanPinnedIds && fanPinnedIds.length === 6) {
          const pinnedItems = fanPinnedIds.map(id => FAN_ALL_OPTIONS.find(o => o.id === id)).filter(Boolean);
          const restItems = usageSorted.filter(o => !fanPinnedIds.includes(o.id));
          const restPages = [];
          for (let i = 0; i < restItems.length; i += 6) restPages.push(restItems.slice(i, i + 6));
          fanPages = [pinnedItems, ...restPages];
        } else {
          fanPages = [];
          for (let i = 0; i < usageSorted.length; i += 6) fanPages.push(usageSorted.slice(i, i + 6));
        }
        const totalPages = fanPages.length;
        const curPage = fanPage % totalPages;
        const fanItems = fanPages[curPage] || [];

        // ── Stage booleans ──────────────────────────────────────────────────
        const showItems   = fanStage === 'open' || fanStage === 'unfold' || fanStage === 'switching';
        const isSwitching = fanStage === 'switching';
        const isOpen      = fanStage === 'open';
        // 'init': logo at src (no motion), 'flying': CSS transition to center
        const logoAtCenter = fanStage !== 'idle' && fanStage !== 'init' && fanStage !== 'closing';

        // ── Flying logo position ────────────────────────────────────────────
        const logoSrc = fanLogoSrcRef.current;
        const logoLeft = logoAtCenter ? cx : logoSrc.x;
        const logoTop  = logoAtCenter ? cy : logoSrc.y;

        // ── Particles ───────────────────────────────────────────────────────
        const t = fanParticleTick;
        const particles = (fanStage === 'burst' && t > 0) ? fanParticlesRef.current : [];

        return (
          <>
            {/* Backdrop — pointerEvents:none during closing so taps pass
                through to the tab bar immediately rather than hitting the
                backdrop and restarting the 320ms close timer indefinitely. */}
            <div onClick={closeFanMenu} style={{
              position:'fixed', inset:0, zIndex:998,
              background: logoAtCenter ? 'rgba(0,0,0,0.55)' : 'transparent',
              transition:'background 0.4s ease',
              pointerEvents: (fanStage === 'closing' || fanStage === 'idle') ? 'none' : 'auto',
            }}/>

            {/* Pulse rings */}
            {(fanStage === 'burst' || fanStage === 'unfold' || isOpen) && fanRingsRef.current.map((ring, i) => (
              <div key={i} style={{
                position:'fixed', left:cx, top:cy,
                borderRadius:'50%',
                border:'1.5px solid rgba(244,197,106,0.5)',
                zIndex:1002, pointerEvents:'none',
                animation:`fanPulseRing 850ms ease-out ${ring.delay}ms forwards`,
                transform:'translate(-50%,-50%)',
              }}/>
            ))}

            {/* Particles */}
            {particles.map((p, i) => {
              const et = 1 - Math.pow(1 - t, 3);
              const op = t < 0.15 ? t / 0.15 : 1 - (t - 0.15) / 0.85;
              return (
                <div key={i} style={{
                  position:'fixed',
                  left: cx + p.vx * et, top: cy + p.vy * et,
                  width: p.size, height: p.size, borderRadius:'50%',
                  background: p.color, opacity: Math.max(0, op),
                  zIndex:1003, pointerEvents:'none',
                  transform:'translate(-50%,-50%)',
                  boxShadow:`0 0 ${p.size * 2}px ${p.color}`,
                }}/>
              );
            })}

            {/* Comet trail — ghost logos during fly open and close */}
            {(fanStage === 'flying' || fanStage === 'closing') && fanFlyT > 0 && (() => {
              const ease = t => 1 - Math.pow(1 - t, 3);
              const lags     = [0.08, 0.16, 0.25, 0.35, 0.45];
              const opacities = [0.5,  0.35, 0.2,  0.1,  0.05];
              const isClosing = fanStage === 'closing';
              return lags.map((lag, i) => {
                const tg = Math.max(0, fanFlyT - lag);
                if (tg === 0) return null;
                const eg = ease(tg);
                const gx = isClosing ? cx + (logoSrc.x - cx) * eg : logoSrc.x + (cx - logoSrc.x) * eg;
                const gy = isClosing ? cy + (logoSrc.y - cy) * eg : logoSrc.y + (cy - logoSrc.y) * eg;
                const sz = Math.round(44 * (1 - i * 0.15));
                return (
                  <div key={i} style={{
                    position:'fixed', left:gx, top:gy,
                    width:sz, height:sz, borderRadius:'50%',
                    transform:'translate(-50%,-50%)',
                    background:'radial-gradient(circle, rgba(20,35,30,0.9), rgba(10,20,18,0.85))',
                    border:`1.5px solid rgba(244,197,106,${opacities[i] * 0.8})`,
                    boxShadow:`0 0 ${Math.round(sz * 0.4)}px rgba(244,197,106,${opacities[i] * 0.8})`,
                    opacity: opacities[i],
                    zIndex:1000, pointerEvents:'none',
                    display:'flex', alignItems:'center', justifyContent:'center',
                  }}>
                    <svg viewBox="0 0 44 44" width={Math.round(sz * 0.72)} height={Math.round(sz * 0.72)} fill="none">
                      <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85"/>
                      <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85" transform="rotate(60 22 22)"/>
                      <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85" transform="rotate(120 22 22)"/>
                    </svg>
                  </div>
                );
              });
            })()}

            {/* Flying / center logo */}
            <div
              onClick={e => { e.stopPropagation(); if (isOpen) nextFanPage(totalPages); }}
              style={{
                position:'fixed', zIndex:1001,
                cursor: isOpen ? 'pointer' : 'default',
                width:  logoAtCenter ? '88px' : '44px',
                height: logoAtCenter ? '88px' : '44px',
                borderRadius:'50%',
                background:'radial-gradient(circle, rgba(20,35,30,0.95), rgba(10,20,18,0.9))',
                border:`2px solid rgba(244,197,106,${logoAtCenter ? '0.4' : '0.25'})`,
                display:'flex', alignItems:'center', justifyContent:'center',
                boxShadow: logoAtCenter
                  ? '0 0 40px rgba(244,197,106,0.5), inset 0 0 20px rgba(244,197,106,0.1)'
                  : '0 4px 20px rgba(0,0,0,0.5)',
                left: logoLeft, top: logoTop,
                transform:'translate(-50%,-50%)',
                transition:[
                  'left 320ms cubic-bezier(0.22,1,0.36,1)',
                  'top 320ms cubic-bezier(0.22,1,0.36,1)',
                  'width 320ms cubic-bezier(0.22,1,0.36,1)',
                  'height 320ms cubic-bezier(0.22,1,0.36,1)',
                  'border-radius 550ms ease',
                  'box-shadow 400ms ease',
                  'border-color 400ms ease',
                ].join(', '),
                animation: isOpen ? 'fanCenterPulse 2.5s ease-in-out infinite' : 'none',
              }}
            >
              {logoAtCenter && (
                <div style={{
                  position:'absolute', inset:'-28px', borderRadius:'50%',
                  background:'radial-gradient(circle, rgba(244,197,106,0.3) 0%, transparent 70%)',
                  filter:'blur(14px)', pointerEvents:'none', zIndex:-1,
                }}/>
              )}
              <svg viewBox="0 0 44 44" width={logoAtCenter ? 50 : 36} height={logoAtCenter ? 50 : 36} fill="none">
                <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85"/>
                <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85" transform="rotate(60 22 22)"/>
                <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85" transform="rotate(120 22 22)"/>
              </svg>
            </div>

            {/* Icon orbit */}
            {showItems && (
              <div style={{position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)', zIndex:999, width:0, height:0}}>
                {fanItems.map((item, i) => {
                  const angle = (-90 + (360 / 6) * i) * Math.PI / 180;
                  const x = Math.cos(angle) * radius;
                  const y = Math.sin(angle) * radius;
                  const delay = i * 25;
                  const anim = isSwitching
                    ? `fanItemFadeOut 0.25s ease-in forwards`
                    : `fanItemOpen 0.22s cubic-bezier(0.34,1.56,0.64,1) ${delay}ms both`;
                  return (
                    <div
                      key={`${curPage}-${item.id}`}
                      onClick={() => {
                        if (fanTappedId) return;
                        hapticImpact("medium");
                        try { const c = JSON.parse(localStorage.getItem("fanUsageCounts")||"{}"); c[item.id] = (c[item.id]||0) + 1; localStorage.setItem("fanUsageCounts", JSON.stringify(c)); } catch {}
                        setFanTappedId(item.id);
                        setTimeout(() => { item.action(); closeFanMenu(); }, 280);
                      }}
                      style={{
                        position:'absolute', left:x, top:y,
                        transform: item.id === fanTappedId ? 'translate(-50%,-50%) scale(1.35)' : 'translate(-50%,-50%)',
                        transition: item.id === fanTappedId ? 'transform 0.15s cubic-bezier(0.34,1.56,0.64,1)' : undefined,
                        display:'flex', flexDirection:'column', alignItems:'center', gap:'7px',
                        cursor:'pointer',
                        animation: item.id === fanTappedId ? 'none' : anim,
                      }}
                    >
                      <div style={{
                        width:'66px', height:'66px', borderRadius:'18px',
                        background:'linear-gradient(160deg, rgba(30,46,40,0.9), rgba(14,22,20,0.9))',
                        border:'1px solid rgba(244,197,106,0.2)',
                        display:'flex', alignItems:'center', justifyContent:'center',
                        boxShadow: item.id === fanTappedId
                          ? '0 0 40px rgba(244,197,106,0.7), 0 8px 24px rgba(0,0,0,0.5), inset 0 1px 0 rgba(244,197,106,0.3)'
                          : '0 8px 24px rgba(0,0,0,0.5), inset 0 1px 0 rgba(244,197,106,0.12)',
                        transition:'box-shadow 0.12s ease',
                      }}>
                        {item.icon}
                      </div>
                      <span style={{fontSize:'10px', fontWeight:'700', color:'rgba(244,197,106,0.9)', letterSpacing:'0.3px', textShadow:'0 1px 6px rgba(0,0,0,0.9)', whiteSpace:'nowrap'}}>
                        {item.label}
                      </span>
                    </div>
                  );
                })}
                {/* Tap ripple ring */}
                {fanTappedId && (() => {
                  const ti = fanItems.findIndex(o => o.id === fanTappedId);
                  if (ti < 0) return null;
                  const ta = (-90 + (360 / 6) * ti) * Math.PI / 180;
                  return (
                    <div style={{
                      position:'absolute',
                      left: Math.cos(ta) * radius,
                      top: Math.sin(ta) * radius,
                      transform:'translate(-50%,-50%)',
                      borderRadius:'50%',
                      border:'2px solid rgba(244,197,106,0.6)',
                      animation:'fanTapRipple 0.38s ease-out forwards',
                      pointerEvents:'none', zIndex:1000,
                    }}/>
                  );
                })()}
              </div>
            )}

            {/* Title — Quick Access */}
            {isOpen && (
              <div style={{
                position:'fixed', top: cy - radius - 60, left:'50%', transform:'translateX(-50%)',
                zIndex:1001, pointerEvents:'none', textAlign:'center',
                animation:'fanItemFadeIn 0.35s ease both',
              }}>
                <div style={{fontSize:'11px', fontWeight:'800', color:'rgba(244,197,106,0.95)', letterSpacing:'3px', textTransform:'uppercase'}}>
                  Quick Access
                </div>
              </div>
            )}

            {/* Hint text + page dots */}
            {isOpen && (
              <div style={{
                position:'fixed', top: cy + radius + 72, left:'50%', transform:'translateX(-50%)',
                zIndex:1001, pointerEvents:'none', textAlign:'center',
                animation:'fanItemFadeIn 0.35s ease both',
              }}>
                <div style={{fontSize:'10px', fontWeight:'700', color:'rgba(244,197,106,0.6)', letterSpacing:'0.8px', textTransform:'uppercase'}}>
                  tap logo to cycle
                </div>
                <div style={{display:'flex', gap:'5px', justifyContent:'center', marginTop:'6px'}}>
                  {Array.from({length: totalPages}).map((_, i) => (
                    <div key={i} style={{
                      width: curPage === i ? '16px' : '5px', height:'5px', borderRadius:'3px',
                      background: curPage === i ? 'rgba(244,197,106,0.8)' : 'rgba(244,197,106,0.25)',
                      transition:'width 0.3s ease, background 0.3s ease',
                    }}/>
                  ))}
                </div>
              </div>
            )}
          </>
        );
      })()}

      {/* Hijri Calendar overlay — expands from hijri date; on Radio/Ibadah it starts right below the eyebrow and covers the title + logo */}
      {showHijriCalendar && (() => {
        const r = hijriRef.current?.getBoundingClientRect();
        const ox = r ? `${r.left + r.width / 2}px` : "80%";
        const oy = "0px";
        // On Radio / Ibadah grid the eyebrow sits at the top of the screen — anchor the calendar just below it.
        // On Masjids keep the original fixed offset so it drops below the full header.
        const anchorUnderEyebrow = (tab === "radio") || (tab === "tools" && toolView === "grid");
        const topVal = anchorUnderEyebrow && r
          ? `${Math.max(0, Math.round(r.bottom + 4))}px`
          : "calc(67px + env(safe-area-inset-top, 0px))";
        return (
          <div style={{position:"fixed",top:topVal,left:0,right:0,bottom:0,zIndex:44,margin:"0 auto",maxWidth:"480px",transformOrigin:`${ox} ${oy}`,animation: hijriCalClosing ? "hijriCalClose 0.5s ease-in forwards" : "hijriCalOpen 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards",background:"var(--bg)",overflowY:"auto",WebkitOverflowScrolling:"touch",paddingBottom:"calc(60px + env(safe-area-inset-bottom))"}}>
            <Lazy><IslamicCalendar darkMode={darkMode} /></Lazy>
          </div>
        );
      })()}

      {/* Daily Hadith full overlay */}
      {showHadithOverlay && remoteConfig?.dailyHadith && (() => {
        const h = remoteConfig.dailyHadith;
        return (
          <div onClick={() => setShowHadithOverlay(false)} style={{position:"fixed",inset:0,zIndex:1200,background:"rgba(0,0,0,0.75)",backdropFilter:"blur(10px)",WebkitBackdropFilter:"blur(10px)",display:"flex",alignItems:"center",justifyContent:"center",padding:"24px"}}>
            <div onClick={e => e.stopPropagation()} style={{background:"linear-gradient(160deg,#062e1a 0%,#041a0f 40%,#07090c 100%)",border:"1px solid rgba(255,179,0,0.25)",borderRadius:"24px",padding:"32px 28px",width:"100%",maxWidth:"440px",maxHeight:"85vh",overflowY:"auto",position:"relative",boxShadow:"0 24px 64px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,179,0,0.08) inset"}}>
              {/* Large crescent watermark */}
              <div style={{position:"absolute",top:-40,right:-40,fontSize:"180px",opacity:0.04,pointerEvents:"none",userSelect:"none",transform:"rotate(15deg)"}}>☪</div>
              {/* Subtle geometric pattern overlay */}
              <div style={{position:"absolute",inset:0,borderRadius:"24px",background:"repeating-linear-gradient(45deg,transparent,transparent 40px,rgba(255,179,0,0.015) 40px,rgba(255,179,0,0.015) 41px)",pointerEvents:"none"}}/>
              {/* Header */}
              <div style={{display:"flex",alignItems:"center",gap:"10px",marginBottom:"20px",position:"relative"}}>
                <span style={{fontSize:"22px"}}>📖</span>
                <span style={{fontSize:"14px",fontWeight:"800",color:"rgba(255,179,0,0.9)",letterSpacing:"2px",textTransform:"uppercase"}}>Daily Hadith</span>
              </div>
              {/* Gold divider */}
              <div style={{height:"1px",background:"linear-gradient(90deg,rgba(255,179,0,0.4),rgba(255,179,0,0.1),transparent)",marginBottom:"28px",position:"relative"}}/>
              {/* Arabic text — large, centered, gold */}
              {h.arabic && (
                <div style={{fontSize:"26px",color:"rgba(255,179,0,0.95)",textAlign:"center",lineHeight:2.0,marginBottom:"28px",fontFamily:"'Scheherazade New','Amiri',serif",direction:"rtl",padding:"0 4px"}}>
                  {h.arabic}
                </div>
              )}
              {/* English translation */}
              <div style={{fontSize:"17px",color:"rgba(255,255,255,0.88)",lineHeight:1.8,marginBottom:"20px",fontStyle:"italic",textAlign:"center",padding:"0 4px"}}>
                "{h.english}"
              </div>
              {/* Source */}
              <div style={{fontSize:"13px",color:"rgba(255,179,0,0.7)",fontWeight:"700",marginBottom:"32px",textAlign:"center"}}>— {h.source}</div>
              {/* Action buttons */}
              <div style={{display:"flex",gap:"10px",position:"relative"}}>
                <button
                  onClick={async () => {
                    try {
                      const blob = await generateShareCardBlob({ kind: "hadith", ar: h.arabic, en: h.english, ref: h.source });
                      const file = new File([blob], "daily-hadith.png", { type: "image/png" });
                      // Image-only share — the card visually contains everything; no caption.
                      if (navigator.canShare && navigator.canShare({ files: [file] })) {
                        await navigator.share({ files: [file] });
                      } else {
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url; a.download = "daily-hadith.png"; a.click();
                        setTimeout(() => URL.revokeObjectURL(url), 1000);
                      }
                    } catch (e) { if (e.name !== "AbortError") console.error("Share failed:", e); }
                  }}
                  style={{flex:1,padding:"14px",background:"linear-gradient(135deg,rgba(255,179,0,0.15),rgba(255,179,0,0.08))",border:"1px solid rgba(255,179,0,0.35)",borderRadius:"14px",color:"var(--gold)",fontSize:"14px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit"}}
                >
                  Share ↗
                </button>
                <button
                  onClick={() => setShowHadithOverlay(false)}
                  style={{flex:1,padding:"14px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:"14px",color:"rgba(255,255,255,0.7)",fontSize:"14px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit"}}
                >
                  Close
                </button>
              </div>
              {/* Branding */}
              <div style={{textAlign:"center",marginTop:"20px",fontSize:"10px",color:"rgba(255,255,255,0.15)",letterSpacing:"2px",textTransform:"uppercase",fontWeight:"700"}}>Sirat · The Straight Path</div>
            </div>
          </div>
        );
      })()}

      {/* Salah tracker toast */}
      {showSalahToast && salahToastData && (
        <>
          <div className="salah-toast-overlay" onClick={() => setShowSalahToast(false)} />
          <div className="salah-toast" onClick={() => setShowSalahToast(false)} style={{animation:"toastIn 0.5s cubic-bezier(0.22,1,0.36,1), toastGlow 3s ease-in-out 0.5s infinite"}}>
            <div style={{fontSize:"52px",lineHeight:1,animation:"proIconFloat 2.5s ease-in-out infinite"}}>{salahToastData.allDone ? "🌙" : "✅"}</div>
            {salahToastData.allDone && <div style={{fontSize:"15px",color:"rgba(255,179,0,0.8)",fontFamily:"Georgia,serif",direction:"rtl",letterSpacing:"1px"}}>ما شاء الله</div>}
            <div style={{fontSize:"18px",fontWeight:"800",color:"var(--gold)",letterSpacing:"-0.3px"}}>{salahToastData.allDone ? "All Prayers Complete" : "Prayer Logged"}</div>
            <div style={{fontSize:"13px",color:"var(--muted)",lineHeight:1.5}}>{salahToastData.allDone ? "You completed all 5 prayers today." : "Keep up the consistency!"}</div>
            {salahToastData.streak > 1 && (
              <div style={{padding:"10px 18px",background:"rgba(255,179,0,0.1)",border:"1px solid rgba(255,179,0,0.3)",borderRadius:"14px",fontSize:"15px",fontWeight:"800",color:"var(--gold)"}}>
                🔥 {salahToastData.streak} day streak
              </div>
            )}
            {salahToastData.streak === 1 && (
              <div style={{fontSize:"12px",color:"var(--muted)"}}>Keep it up tomorrow for a streak!</div>
            )}
          </div>
        </>
      )}

      {/* Coming Soon toast */}
      {comingSoonToast && (
        <div style={{position:"fixed",bottom:"100px",left:"50%",transform:"translateX(-50%)",zIndex:9999,background:darkMode?"rgba(10,8,0,0.95)":"rgba(255,253,230,0.97)",border:`1px solid ${darkMode?"rgba(255,179,0,0.35)":"rgba(184,112,8,0.3)"}`,borderRadius:"16px",padding:"16px 28px",textAlign:"center",boxShadow:darkMode?"0 8px 32px rgba(0,0,0,0.5)":"0 4px 20px rgba(0,0,0,0.12)",animation:"fadeInOverlay 0.4s ease",maxWidth:"320px",backdropFilter:"blur(12px)",WebkitBackdropFilter:"blur(12px)"}}>
          <div style={{fontSize:"24px",marginBottom:"6px"}}>🕌</div>
          <div style={{fontSize:"15px",fontWeight:"700",color:darkMode?"var(--gold)":"#b87008"}}>{comingSoonToast}</div>
          <div style={{fontSize:"12px",color:"var(--muted)",marginTop:"4px",lineHeight:1.5}}>This feature is coming soon, In Shaa Allah</div>
        </div>
      )}


      {showDonation && supportPageVisible && (() => {
        const sup = remoteConfig.support || {};
        const presets = sup.presets || [20, 50, 150];
        return (
          <>
            <div className="notif-sheet-backdrop" onClick={() => setShowDonation(false)} />
            <div className="notif-sheet">
              <div style={{width:"36px",height:"4px",borderRadius:"2px",background:"rgba(255,255,255,0.12)",margin:"0 auto 24px"}}/>
              <div style={{textAlign:"center",marginBottom:"24px"}}>
                <div style={{fontSize:"28px",marginBottom:"10px"}}>🌙</div>
                <div style={{fontSize:"19px",fontWeight:"800",color:"var(--text)",marginBottom:"8px"}}>{sup.promptHeading || "Support Sirat"}</div>
                <div style={{fontSize:"13px",color:"var(--muted)",lineHeight:1.6,maxWidth:"280px",margin:"0 auto"}}>
                  {sup.promptMessage || "Sirat is free for the Ummah. If you\u2019d like to support us, please do so below."}
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:presets.length<=3?`repeat(${presets.length},1fr)`:"repeat(3,1fr)",gap:"10px",marginBottom:"14px"}}>
                {presets.map(amt => (
                  <button key={amt} onClick={() => { setShowDonation(false); handlePaystack({ label: "Support", price: amt, days: 0 }); }}
                    style={{padding:"14px 8px",background:"var(--s3)",border:"1px solid rgba(255,179,0,0.4)",borderRadius:"12px",color:"var(--text)",cursor:"pointer",fontFamily:"inherit",textAlign:"center"}}>
                    <div style={{fontSize:"18px",fontWeight:"800",color:"var(--gold)"}}>R{amt}</div>
                  </button>
                ))}
              </div>
              <button onClick={() => { setShowDonation(false); setTab("tools"); setToolView("siratpro"); }}
                style={{width:"100%",padding:"11px",background:"transparent",border:"none",color:"var(--muted)",fontSize:"13px",cursor:"pointer",fontFamily:"inherit",marginBottom:"4px"}}>
                Choose your own amount →
              </button>
              <button onClick={() => setShowDonation(false)}
                style={{width:"100%",padding:"13px",background:"transparent",border:"1px solid var(--border)",borderRadius:"14px",color:"var(--muted)",fontSize:"15px",fontWeight:"600",cursor:"pointer",fontFamily:"inherit"}}>
                May Allah reward you
              </button>
            </div>
          </>
        );
      })()}

      {showDonation && !isSupportMode && (
        <>
          <div className="notif-sheet-backdrop" onClick={() => setShowDonation(false)} />
          <div className="notif-sheet">
            <div style={{width:"36px",height:"4px",borderRadius:"2px",background:"rgba(255,255,255,0.12)",margin:"0 auto 24px"}}/>
            <div style={{textAlign:"center",marginBottom:"24px"}}>
              <div style={{fontSize:"28px",marginBottom:"10px"}}>🔓</div>
              <div style={{fontSize:"19px",fontWeight:"800",color:"var(--text)",marginBottom:"8px"}}>Get Sirat Pro</div>
              <div style={{fontSize:"13px",color:"var(--muted)",lineHeight:1.6,maxWidth:"280px",margin:"0 auto"}}>
                Unlock all premium features — dark mode, Surahs, Quran, Azkar, 99 Names, Zakat Calculator and more.
              </div>
            </div>
            {paymentsEnabled && (() => {
              const donTiers = remoteConfig.proTiers || [
                { label: "Annual", price: remoteConfig.proPrice || 99, days: 365 },
                { label: "Full Access", price: remoteConfig.proLifetimePrice || 199, days: 0, badge: "BEST VALUE" }
              ];
              return (
                <div style={{display:"grid",gridTemplateColumns:donTiers.length===3?"1fr 1fr 1fr":"1fr 1fr",gap:"10px",marginBottom:"14px"}}>
                  {donTiers.map((t, i) => {
                    const isLt = t.days === 0;
                    return (
                      <button key={i} onClick={() => { setShowDonation(false); handlePaystack(t); }}
                        style={{padding:"14px 8px",background:"var(--s3)",border:`1px solid ${isLt?"rgba(255,179,0,0.4)":"var(--border)"}`,borderRadius:"12px",color:"var(--text)",cursor:"pointer",fontFamily:"inherit",textAlign:"center",position:"relative"}}>
                        {t.badge && <div style={{position:"absolute",top:"-9px",left:"50%",transform:"translateX(-50%)",background:"var(--gold)",color:"#07090c",fontSize:"9px",fontWeight:"800",padding:"2px 7px",borderRadius:"20px",whiteSpace:"nowrap"}}>{t.badge}</div>}
                        <div style={{fontSize:"18px",fontWeight:"800",color:isLt?"var(--gold)":"var(--accent)"}}>R{t.price}</div>
                        <div style={{fontSize:"11px",color:"var(--muted)",marginTop:"2px"}}>{tierLabel(t)}</div>
                      </button>
                    );
                  })}
                </div>
              );
            })()}
            <button onClick={() => { setShowDonation(false); setTab("tools"); setToolView("siratpro"); }}
              style={{width:"100%",padding:"11px",background:"transparent",border:"none",color:"var(--muted)",fontSize:"13px",cursor:"pointer",fontFamily:"inherit",marginBottom:"4px"}}>
              See everything included →
            </button>
            <button onClick={() => setShowDonation(false)}
              style={{width:"100%",padding:"13px",background:"transparent",border:"1px solid var(--border)",borderRadius:"14px",color:"var(--muted)",fontSize:"15px",fontWeight:"600",cursor:"pointer",fontFamily:"inherit"}}>
              Maybe later
            </button>
          </div>
        </>
      )}
      {/* Prayer detail sheet — triggered by tapping the Salaah hero dial glyph */}
      {prayerDetailFor && (() => {
        const info = PRAYER_INFO[prayerDetailFor] || {};
        const v = info.virtues && info.virtues.length > 0 ? info.virtues[virtueIdxFor(prayerDetailFor)] : null;
        const label = prayerDetailFor === "Sunrise" ? "Shuruq" : prayerDetailFor;
        const glyphMap = { Fajr:"🌙", Sunrise:"🌅", Dhuhr:"☀", Asr:"🌤", Maghrib:"🌆", Isha:"🌃" };
        const glyph = glyphMap[prayerDetailFor] || "🕌";
        const rawTime = prayerTimes?.[prayerDetailFor];
        const timeStr = rawTime ? rawTime.replace(/ \(.*\)/, "") : null;
        // Countdown to this prayer (if it's still upcoming today)
        let cd = null;
        if (rawTime) {
          const nowM = new Date().getHours()*60 + new Date().getMinutes();
          const target = to24min(rawTime);
          const diff = target - nowM;
          if (diff > 0) {
            const h = Math.floor(diff / 60); const m = diff % 60;
            cd = h > 0 ? `in ${h}h ${m}m` : `in ${m}m`;
          } else if (diff > -30) {
            cd = "now — tap to mark as prayed";
          }
        }
        const mono = '"JetBrains Mono",ui-monospace,SFMono-Regular,Menlo,monospace';
        const serif = '"Instrument Serif",Georgia,serif';
        const today = localDateStr();
        const todayLog = salahLog[today] || {};
        const isPrayed = TRACKABLE_PRAYERS.includes(prayerDetailFor) && !!todayLog[prayerDetailFor];
        const hasPassed = rawTime && to24min(rawTime) <= (new Date().getHours()*60 + new Date().getMinutes());
        return (
          <>
            <div onClick={() => setPrayerDetailFor(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:1200,backdropFilter:"blur(4px)"}} />
            <div className="notif-sheet" style={{zIndex:1201}}>
              <div style={{width:"36px",height:"4px",borderRadius:"2px",background:"rgba(255,255,255,0.12)",margin:"0 auto 18px"}}/>
              {/* Hero row */}
              <div style={{display:"flex",alignItems:"center",gap:"14px",marginBottom:"18px"}}>
                <div style={{width:"64px",height:"64px",borderRadius:"50%",flexShrink:0,
                  background:`radial-gradient(circle at 35% 35%,#e8c07c4a,rgba(4,26,21,0.8) 70%)`,
                  border:"1px solid rgba(232,192,124,0.3)",
                  display:"grid",placeItems:"center",fontSize:"28px",lineHeight:1,boxShadow:"0 3px 12px rgba(0,0,0,0.3)"}}>{glyph}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontFamily:mono,fontSize:"9.5px",letterSpacing:"2.5px",color:"var(--muted)",textTransform:"uppercase",marginBottom:"3px"}}>Prayer</div>
                  <div style={{fontFamily:serif,fontStyle:"italic",fontSize:"30px",color:"var(--text)",letterSpacing:"-0.02em",lineHeight:1}}>{label}</div>
                  {timeStr && <div style={{fontFamily:mono,fontSize:"11.5px",color:"var(--gold)",marginTop:"4px",letterSpacing:"0.3px"}}>{timeStr}{cd && <span style={{color:"var(--muted)",marginLeft:"8px"}}>· {cd}</span>}</div>}
                </div>
              </div>
              {/* Your relationship with THIS prayer — streak + this-month + 7-day dots */}
              {TRACKABLE_PRAYERS.includes(prayerDetailFor) && (() => {
                // Per-prayer streak: consecutive days this specific prayer has been marked
                // If today's prayer hasn't been prayed yet, start counting from yesterday
                // so the streak doesn't appear broken just because the time hasn't come.
                const toKey = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
                let perStreak = 0;
                const todayKey2 = localDateStr();
                const startOffset = salahLog[todayKey2]?.[prayerDetailFor] ? 0 : 1;
                for (let i = startOffset; i < 365; i++) {
                  const d = new Date(); d.setDate(d.getDate() - i);
                  if (salahLog[toKey(d)]?.[prayerDetailFor]) perStreak++;
                  else break;
                }
                // This month count + percent
                const now = new Date();
                const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
                const daysSoFar = Math.floor((now - monthStart) / 86400000) + 1;
                let monthCount = 0;
                Object.entries(salahLog).forEach(([k, day]) => {
                  const dd = new Date(k + "T00:00:00");
                  if (dd >= monthStart && dd <= now && day[prayerDetailFor]) monthCount++;
                });
                const pct = daysSoFar > 0 ? Math.round((monthCount / daysSoFar) * 100) : 0;
                // 7-day dot row
                const dots = [];
                for (let i = 6; i >= 0; i--) {
                  const d = new Date(); d.setDate(d.getDate() - i);
                  const k = toKey(d);
                  dots.push({
                    letter: ["S","M","T","W","T","F","S"][d.getDay()],
                    prayed: !!salahLog[k]?.[prayerDetailFor],
                    today: i === 0,
                  });
                }
                const monthName = now.toLocaleDateString(undefined, {month:"long"});
                return (
                  <div style={{padding:"14px 14px",marginBottom:"14px",borderRadius:"14px",
                    background: darkMode ? "linear-gradient(135deg,rgba(255,179,0,0.08),rgba(255,179,0,0.02))" : "linear-gradient(135deg,rgba(184,112,8,0.08),rgba(184,112,8,0.02))",
                    border: `1px solid ${darkMode ? "rgba(255,179,0,0.2)" : "rgba(184,112,8,0.2)"}`}}>
                    <div style={{display:"flex",alignItems:"center",gap:"14px",marginBottom:"10px"}}>
                      {/* Streak number */}
                      <div style={{flexShrink:0,textAlign:"center"}}>
                        <div style={{fontFamily:serif,fontStyle:"italic",fontSize:"36px",color:"var(--gold)",lineHeight:0.9}}>{perStreak}</div>
                        <div style={{fontFamily:mono,fontSize:"8.5px",letterSpacing:"1.5px",color:"var(--muted)",textTransform:"uppercase",marginTop:"4px"}}>{perStreak === 1 ? "Day" : "Days"} in a row</div>
                      </div>
                      {/* Month split */}
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontFamily:mono,fontSize:"9px",letterSpacing:"2px",color:"var(--muted)",textTransform:"uppercase",marginBottom:"3px"}}>{monthName}</div>
                        <div style={{display:"flex",alignItems:"baseline",gap:"4px"}}>
                          <span style={{fontFamily:serif,fontStyle:"italic",fontSize:"24px",color:"var(--text)",letterSpacing:"-0.02em",lineHeight:1}}>{monthCount}</span>
                          <span style={{fontFamily:mono,fontSize:"12px",color:"var(--muted)",letterSpacing:"0.5px"}}>/ {daysSoFar}</span>
                          <span style={{fontFamily:mono,fontSize:"10px",color:"var(--gold)",marginLeft:"auto",letterSpacing:"0.5px"}}>{pct}%</span>
                        </div>
                        {/* 7-day dots */}
                        <div style={{display:"flex",gap:"5px",marginTop:"8px"}}>
                          {dots.map((d, i) => (
                            <div key={i} style={{flex:1,textAlign:"center"}}>
                              <div style={{
                                height:"4px",borderRadius:"2px",
                                background: d.today
                                  ? "transparent"
                                  : d.prayed ? "var(--gold)" : (darkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"),
                                border: d.today ? `1px dashed ${darkMode ? "rgba(255,179,0,0.5)" : "rgba(184,112,8,0.5)"}` : "none",
                              }}/>
                              <div style={{fontFamily:mono,fontSize:"8.5px",color: d.today ? "var(--gold)" : (d.prayed ? "var(--text)" : "var(--muted)"),marginTop:"4px",fontWeight:700,letterSpacing:"0.8px",opacity: d.prayed || d.today ? 1 : 0.55}}>{d.letter}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    {/* Reset this prayer's streak — quiet footer link */}
                    {perStreak > 0 && (
                      <div style={{marginTop:"10px",paddingTop:"10px",borderTop:`1px dashed ${darkMode?"rgba(255,179,0,0.14)":"rgba(184,112,8,0.14)"}`,textAlign:"center"}}>
                        <button onClick={() => {
                          const label2 = prayerDetailFor === "Sunrise" ? "Shuruq" : prayerDetailFor;
                          if (!window.confirm(`Reset your ${label2} streak?\n\nThis clears all ${label2} entries from your salah log. Your other prayers stay intact. This cannot be undone.`)) return;
                          if (!window.confirm("Are you sure? Tap OK to confirm reset.")) return;
                          const pruned = {};
                          Object.entries(salahLog).forEach(([k, day]) => {
                            const rest = { ...day };
                            delete rest[prayerDetailFor];
                            if (Object.keys(rest).length > 0) pruned[k] = rest;
                          });
                          setSalahLog(pruned);
                          localStorage.setItem("salahLog", JSON.stringify(pruned));
                        }}
                          style={{background:"transparent",border:"none",cursor:"pointer",fontFamily:mono,fontSize:"10px",letterSpacing:"1.5px",color:"var(--muted)",textTransform:"uppercase",padding:"4px 10px"}}>
                          Reset {prayerDetailFor === "Sunrise" ? "Shuruq" : prayerDetailFor} streak
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}
              {/* Window + rakats */}
              {info.window && (
                <div style={{padding:"10px 12px",background:"rgba(232,192,124,0.06)",border:"1px solid rgba(232,192,124,0.18)",borderRadius:"10px",marginBottom:"12px"}}>
                  <div style={{fontFamily:mono,fontSize:"9px",letterSpacing:"2px",color:"var(--muted)",textTransform:"uppercase",marginBottom:"3px"}}>Window</div>
                  <div style={{fontSize:"13px",color:"var(--text)",letterSpacing:"0.1px"}}>{info.window}</div>
                </div>
              )}
              {info.rakats && (
                <div style={{display:"grid",gridTemplateColumns:`repeat(${info.rakats.length},1fr)`,gap:"8px",marginBottom:"14px"}}>
                  {info.rakats.map(([type, count]) => (
                    <div key={type} style={{padding:"10px 8px",background:"var(--s2)",border:"1px solid var(--border)",borderRadius:"10px",textAlign:"center"}}>
                      <div style={{fontFamily:serif,fontStyle:"italic",fontSize:"26px",color:"var(--gold)",lineHeight:1,marginBottom:"3px"}}>{count}</div>
                      <div style={{fontFamily:mono,fontSize:"8.5px",letterSpacing:"1.4px",color:"var(--muted)",textTransform:"uppercase"}}>{type}</div>
                    </div>
                  ))}
                </div>
              )}
              {/* Virtue / hadith */}
              {v && (
                <div style={{padding:"14px 14px",background:"linear-gradient(135deg,rgba(0,201,167,0.05),rgba(255,179,0,0.04))",border:"1px solid rgba(0,201,167,0.18)",borderRadius:"12px",marginBottom:"14px"}}>
                  <div style={{fontFamily:mono,fontSize:"9px",letterSpacing:"2px",color:"var(--accent)",textTransform:"uppercase",marginBottom:"6px"}}>Virtue</div>
                  <div style={{fontSize:"14px",color:"var(--text)",lineHeight:1.55,fontStyle:"italic",fontFamily:serif}}>"{v.text}"</div>
                  <div style={{fontFamily:mono,fontSize:"10px",color:"var(--muted)",marginTop:"8px",letterSpacing:"0.3px"}}>— {v.source}</div>
                </div>
              )}
              {/* Actions */}
              {TRACKABLE_PRAYERS.includes(prayerDetailFor) && hasPassed && (
                <button onClick={() => { toggleSalah(prayerDetailFor); setPrayerDetailFor(null); }}
                  style={{width:"100%",padding:"13px",background:isPrayed?"rgba(232,192,124,0.16)":"linear-gradient(135deg,#e8c07c,#b58648)",border:`1px solid ${isPrayed?"rgba(232,192,124,0.4)":"transparent"}`,borderRadius:"12px",color:isPrayed?"var(--gold)":"#1a1208",fontFamily:mono,fontSize:"11px",fontWeight:800,letterSpacing:"1.5px",textTransform:"uppercase",cursor:"pointer",marginBottom:"8px"}}>
                  {isPrayed ? "✓ Marked as prayed — tap to undo" : "Mark as prayed"}
                </button>
              )}
              <button onClick={() => setPrayerDetailFor(null)}
                style={{width:"100%",padding:"11px",background:"transparent",border:"1px solid var(--border)",borderRadius:"12px",color:"var(--muted)",fontSize:"13px",fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
                Close
              </button>
            </div>
          </>
        );
      })()}
      {/* Khatm auto-log prompt after surah playback */}
      {khatmLogPrompt && (
        <>
          <div onClick={() => setKhatmLogPrompt(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:1200,backdropFilter:"blur(4px)"}} />
          <div style={{position:"fixed",bottom:0,left:0,right:0,zIndex:1201,background:"var(--s1)",borderRadius:"20px 20px 0 0",padding:"24px 20px",paddingBottom:"calc(24px + env(safe-area-inset-bottom))",border:"1px solid var(--border)",borderBottom:"none"}}>
            <div style={{fontSize:"16px",fontWeight:"700",color:"var(--text)",marginBottom:"4px"}}>Log this reading for a Khatm?</div>
            <div style={{fontSize:"13px",color:"var(--muted)",marginBottom:"16px"}}>You just finished listening to {SURAH_INFO[khatmLogPrompt.surahKey]?.name || khatmLogPrompt.surahKey}</div>
            {khatmLogPrompt.khatms.map(k => (
              <button key={k._id} onClick={() => {
                fetch(`${BACKEND}/api/khatm/log`, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ userId, khatmId: k._id }) })
                  .then(r => r.json()).then(d => {
                    if (d.ok && navigator.vibrate) navigator.vibrate([40,60,40]);
                  }).catch(() => {});
                setKhatmLogPrompt(null);
              }} style={{width:"100%",padding:"14px",marginBottom:"8px",borderRadius:"14px",border:"none",background:"linear-gradient(135deg,var(--accent),#009e87)",color:"#fff",fontSize:"14px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>
                <div>Log for {k.marhoumName}</div>
                <div style={{fontSize:"11px",opacity:0.8,marginTop:"2px"}}>{k.totalRead}/{k.target} readings · {k.surahName}</div>
              </button>
            ))}
            <button onClick={() => setKhatmLogPrompt(null)} style={{width:"100%",padding:"12px",background:"transparent",border:"1px solid var(--border)",borderRadius:"14px",color:"var(--muted)",fontSize:"14px",cursor:"pointer",fontFamily:"inherit",marginTop:"4px"}}>
              No thanks
            </button>
          </div>
        </>
      )}
      <Toast />
      <HayahFastInsightToast {...hayahFastToast} />

      {/* ── Fan Quick Access Picker ─────────────────────────────────────── */}
      {fanEditingSlot !== null && (() => {
        const currentPinned = fanPinnedIds ? [...fanPinnedIds] : FAN_ALL_OPTIONS.slice(0,6).map(o=>o.id);
        return (
          <>
            <div onClick={() => setFanEditingSlot(null)} style={{position:"fixed",inset:0,zIndex:2100,background:"rgba(0,0,0,0.7)"}}/>
            <div style={{
              position:"fixed",bottom:0,left:0,right:0,zIndex:2101,
              maxHeight:"75vh",overflowY:"auto",WebkitOverflowScrolling:"touch",
              background:darkMode?"#0d1710":"#f5f0e8",
              borderRadius:"20px 20px 0 0",
              paddingBottom:`calc(20px + env(safe-area-inset-bottom))`,
            }}>
              {/* Header */}
              <div style={{position:"sticky",top:0,background:darkMode?"#0d1710":"#f5f0e8",zIndex:1,padding:"16px 20px 12px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:`1px solid ${darkMode?"rgba(244,197,106,0.12)":"rgba(184,112,8,0.2)"}`}}>
                <div>
                  <div style={{fontSize:"13px",fontWeight:"800",color:darkMode?"rgba(244,197,106,0.9)":"#8a5406",letterSpacing:"0.3px"}}>Choose Shortcut</div>
                  <div style={{fontSize:"11px",color:darkMode?"rgba(255,255,255,0.4)":"rgba(0,0,0,0.5)",marginTop:"2px"}}>Slot {fanEditingSlot + 1} of 6</div>
                </div>
                <button onClick={() => setFanEditingSlot(null)} style={{width:"30px",height:"30px",borderRadius:"50%",border:"none",background:darkMode?"rgba(244,197,106,0.12)":"rgba(184,112,8,0.18)",color:darkMode?"rgba(244,197,106,0.8)":"#8a5406",fontSize:"16px",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
              </div>
              {/* Grid of all options */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:"8px",padding:"14px"}}>
                {FAN_ALL_OPTIONS.map(item => {
                  const isSelected = currentPinned[fanEditingSlot] === item.id;
                  const isUsedElsewhere = currentPinned.includes(item.id) && !isSelected;
                  return (
                    <button key={item.id} onClick={() => {
                      hapticImpact("medium");
                      const newPinned = [...currentPinned];
                      newPinned[fanEditingSlot] = item.id;
                      setFanPinnedIds(newPinned);
                      localStorage.setItem("fanPinnedIds", JSON.stringify(newPinned));
                      setFanEditingSlot(null);
                    }} style={{
                      display:"flex",flexDirection:"column",alignItems:"center",gap:"6px",
                      padding:"12px 6px",borderRadius:"14px",
                      border:`1.5px solid ${isSelected?(darkMode?"rgba(244,197,106,0.6)":"rgba(184,112,8,0.6)"):isUsedElsewhere?(darkMode?"rgba(244,197,106,0.12)":"rgba(184,112,8,0.16)"):(darkMode?"rgba(244,197,106,0.15)":"rgba(184,112,8,0.2)")}`,
                      background:isSelected?(darkMode?"rgba(244,197,106,0.12)":"rgba(184,112,8,0.14)"):isUsedElsewhere?(darkMode?"rgba(255,255,255,0.02)":"rgba(0,0,0,0.03)"):"transparent",
                      cursor:"pointer",WebkitTapHighlightColor:"transparent",
                      opacity:isUsedElsewhere?0.4:1,
                    }}>
                      <div style={{width:"44px",height:"44px",borderRadius:"12px",background:darkMode?"rgba(244,197,106,0.08)":"rgba(184,112,8,0.14)",display:"flex",alignItems:"center",justifyContent:"center",filter:darkMode?"none":"brightness(0.5) saturate(1.4)"}}>
                        {item.icon}
                      </div>
                      <span style={{fontSize:"9.5px",fontWeight:"700",color:isSelected?(darkMode?"rgba(244,197,106,0.95)":"#6f4408"):(darkMode?"rgba(244,197,106,0.7)":"#8a5406"),letterSpacing:"0.2px",textAlign:"center",lineHeight:"1.2"}}>
                        {item.label}
                      </span>
                      {isSelected && <div style={{width:"6px",height:"6px",borderRadius:"50%",background:darkMode?"rgba(244,197,106,0.8)":"#8a5406"}}/>}
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        );
      })()}
      {showDebugFab && !debugScreenOpen && <DebugFAB onClick={() => setDebugScreenOpen(true)} />}
      <DebugScreen open={debugScreenOpen} onClose={() => setDebugScreenOpen(false)} />
    </>
  );
}
